/**
 * Generated by orval v8.22.0 🍺
 * Do not edit manually.
 * Gharbatai Finance API
 * Multi-branch investment management platform
 * OpenAPI spec version: 1.0
 */
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import { customMutator } from '../mutator.ts';
export interface LoginDto {
  email: string;
  password: string;
  totp?: string;
}

export interface AuthenticatedUserResponseDto {
  id: string;
  email: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
}

export interface AvailableContextResponseDto {
  membershipId: string;
  organizationId: string;
  organizationName: string;
  /** @nullable */
  branchId?: string | null;
  /** @nullable */
  branchName?: string | null;
}

export type LoginResponseDtoStatus = typeof LoginResponseDtoStatus[keyof typeof LoginResponseDtoStatus];


export const LoginResponseDtoStatus = {
  MFA_REQUIRED: 'MFA_REQUIRED',
  PASSWORD_CHANGE_REQUIRED: 'PASSWORD_CHANGE_REQUIRED',
  CONTEXT_REQUIRED: 'CONTEXT_REQUIRED',
  AUTHENTICATED: 'AUTHENTICATED',
} as const;

export interface LoginResponseDto {
  status: LoginResponseDtoStatus;
  accessToken?: string;
  user?: AuthenticatedUserResponseDto;
  challengeToken?: string;
  availableMethods?: string[];
  contextSelectionToken?: string;
  availableContexts?: AvailableContextResponseDto[];
  backupCodes?: string[];
  mfaEnrolled?: boolean;
}

export interface CreateOrganizationInvitationDto {
  email: string;
  roleIds?: string[];
}

export interface AcceptOrganizationInvitationDto {
  token: string;
  firstName: string;
  lastName: string;
  /** @minLength 8 */
  password: string;
}

export interface InitialPasswordChangeDto {
  challengeToken: string;
  newPassword: string;
}

export interface ChallengeTotpDto {
  code: string;
}

export interface ChallengeRecoveryCodeDto {
  code: string;
}

export interface SelectContextDto {
  contextSelectionToken: string;
  membershipId: string;
  branchId?: string;
}

export interface RefreshTokenDto {
  /** Service-to-service only. Browser clients must use the HttpOnly refresh cookie. */
  refreshToken?: string;
}

export interface AccessTokenResponseDto {
  accessToken: string;
}

export interface LogoutDto {
  /** Service-to-service only. Browser clients use the HttpOnly refresh cookie. */
  refreshToken?: string;
}

export interface SuccessResponseDto {
  success: boolean;
}

export interface RoleResponse {
  id: string;
  roleId: string;
  roleName: string;
  roleCode: string;
  branchId?: string;
  branchName?: string;
}

export interface MembershipResponse {
  id: string;
  organizationId: string;
  organizationName: string;
  isOwner: boolean;
  status: string;
  roles: RoleResponse[];
}

export type ProfileResponseMfa = { [key: string]: unknown };

/**
 * Authentication assurance level achieved.
 */
export type ProfileResponseAal = typeof ProfileResponseAal[keyof typeof ProfileResponseAal];


export const ProfileResponseAal = {
  AAL1: 'AAL1',
  AAL2: 'AAL2',
} as const;

export interface ProfileResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  status: string;
  /** Credential management mode: LOCAL for password-based accounts, SSO for identity-provider-managed accounts. */
  credentialManagementMode: string;
  mfa: ProfileResponseMfa;
  memberships: MembershipResponse[];
  /** Effective permission codes across all active memberships. */
  permissions: string[];
  /** Server-authoritative active membership ID from JWT claims. */
  activeMembershipId?: string;
  /** Server-authoritative active organization ID from JWT claims. */
  activeOrganizationId?: string;
  /** Server-authoritative active branch ID from JWT claims. */
  activeBranchId?: string;
  /** Resolved actor persona from the active session (e.g. USER, CLIENT). */
  persona?: string;
  /** Resolved scope kind from the active session (e.g. PLATFORM, ORGANIZATION, CLIENT). */
  scopeKind?: string;
  /** Authentication assurance level achieved. */
  aal?: ProfileResponseAal;
  /** Version of the active permission set. */
  permissionsVersion?: number;
  /** Version of the active entitlements set. */
  entitlementsVersion?: number;
  /** Whether the active session is an approved support delegation. */
  delegated?: boolean;
  /** Active support delegation identifier. */
  delegationId?: string;
  /** Server-authoritative active organization name. */
  activeOrganizationName?: string;
  /** Server-authoritative active branch name. */
  activeBranchName?: string;
}

export interface UpdateProfileDto {
  firstName?: string;
  lastName?: string;
  phone?: string;
}

/**
 * Versioned user preference bag.
 */
export type UpdatePreferencesDtoLocale = { [key: string]: unknown };

export interface UpdatePreferencesDto {
  /** Versioned user preference bag. */
  locale?: UpdatePreferencesDtoLocale;
  theme?: string;
  dateFormat?: string;
  timezone?: string;
  emailNotifications?: boolean;
  pushNotifications?: boolean;
}

export interface ChangePasswordRequestDto {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  totpCode?: string;
}

export interface ChangePasswordResponseDto {
  success: boolean;
  otherSessionsRevoked: number;
  currentSessionRotated: boolean;
}

export interface SessionListItemDto {
  id: string;
  /** @nullable */
  ipAddress: string | null;
  /** @nullable */
  deviceFingerprint: string | null;
  aal: string;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

export interface SessionListResponseDto {
  sessions: SessionListItemDto[];
}

export interface SwitchContextDto {
  membershipId: string;
  branchId?: string;
  clientAccountId?: string;
  portfolioAccountId?: string;
}

export interface BackupCodesResponseDto {
  secret: string;
  qrCode: string;
  backupCodes: string[];
}

export interface VerifyMfaDto {
  code: string;
}

export interface DisableMfaDto {
  code: string;
}

export interface RegenerateBackupCodesDto {
  /** Current TOTP or an unused recovery code. */
  code: string;
}

export interface RegeneratedBackupCodesResponseDto {
  backupCodes: string[];
}

export interface ForgotPasswordDto {
  email: string;
}

export interface ResetPasswordDto {
  token: string;
  password: string;
}

export type CreateOrgDtoModulesItem = typeof CreateOrgDtoModulesItem[keyof typeof CreateOrgDtoModulesItem];


export const CreateOrgDtoModulesItem = {
  INVESTMENT: 'INVESTMENT',
  ACCOUNTING: 'ACCOUNTING',
  TAX: 'TAX',
  RECONCILIATION: 'RECONCILIATION',
  CORPORATE_ACTIONS: 'CORPORATE_ACTIONS',
  REPORTING: 'REPORTING',
  AUTH: 'AUTH',
  DASHBOARD: 'DASHBOARD',
  SETTINGS: 'SETTINGS',
  ADMIN: 'ADMIN',
  PLATFORM: 'PLATFORM',
  NAVIGATION: 'NAVIGATION',
  DATA: 'DATA',
  INSTRUMENTS: 'INSTRUMENTS',
  IMPORT: 'IMPORT',
  FACTS: 'FACTS',
  HOLDINGS: 'HOLDINGS',
  REGULATORY: 'REGULATORY',
} as const;

export interface CreateOrgDto {
  code: string;
  name: string;
  legalName: string;
  jurisdiction: string;
  locale: string;
  timeZone: string;
  defaultBranchCode: string;
  defaultBranchName: string;
  modules: CreateOrgDtoModulesItem[];
  pan?: string;
  sebonRegistrationNumber?: string;
  vat?: string;
  baseCurrency?: string;
  /** Email for the org admin user to auto-create */
  adminEmail: string;
  /** Initial password for the auto-created org admin */
  adminPassword: string;
}

export interface PlatformAdministrativeContactDto {
  userId: string;
  email?: string;
}

export interface PlatformOrganizationViewDto {
  id: string;
  code: string;
  name: string;
  legalName?: string;
  status: string;
  jurisdiction: string;
  baseCurrency: string;
  subscription?: string;
  administrativeContacts: PlatformAdministrativeContactDto[];
  assignedModules: string[];
  branchCount?: number;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateOrgDto {
  expectedVersion: number;
  name?: string;
  legalName?: string;
  panNumber?: string;
  sebonRegistrationNumber?: string;
  vatNumber?: string;
  baseCurrency?: string;
  plan?: string;
  isActive?: boolean;
}

export type AssignInitialOrgAdminDtoMembershipStatus = typeof AssignInitialOrgAdminDtoMembershipStatus[keyof typeof AssignInitialOrgAdminDtoMembershipStatus];


export const AssignInitialOrgAdminDtoMembershipStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  SUSPENDED: 'SUSPENDED',
} as const;

export interface AssignInitialOrgAdminDto {
  userIdentityId: string;
  roleCode?: string;
  branchId?: string;
  membershipStatus?: AssignInitialOrgAdminDtoMembershipStatus;
}

export interface ModuleLicenseSurfaceDto {
  id: string;
  organizationId: string;
  module: string;
  maxBranches: number;
  maxUsers?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  graceEndsAt?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export type CreateLicenseDtoModule = typeof CreateLicenseDtoModule[keyof typeof CreateLicenseDtoModule];


export const CreateLicenseDtoModule = {
  INVESTMENT: 'INVESTMENT',
  ACCOUNTING: 'ACCOUNTING',
  TAX: 'TAX',
  RECONCILIATION: 'RECONCILIATION',
  CORPORATE_ACTIONS: 'CORPORATE_ACTIONS',
  REPORTING: 'REPORTING',
  AUTH: 'AUTH',
  DASHBOARD: 'DASHBOARD',
  SETTINGS: 'SETTINGS',
  ADMIN: 'ADMIN',
  PLATFORM: 'PLATFORM',
  NAVIGATION: 'NAVIGATION',
  DATA: 'DATA',
  INSTRUMENTS: 'INSTRUMENTS',
  IMPORT: 'IMPORT',
  FACTS: 'FACTS',
  HOLDINGS: 'HOLDINGS',
  REGULATORY: 'REGULATORY',
} as const;

export interface CreateLicenseDto {
  orgId: string;
  module: CreateLicenseDtoModule;
  maxBranches?: number;
  maxUsers?: number;
  effectiveFrom?: string;
  expiresAt?: string;
  /** End of the read-only historical access window. */
  graceEndsAt?: string;
}

export interface BranchSurfaceDto {
  id: string;
  organizationId: string;
  code: string;
  name: string;
  status: string;
  isDefault: boolean;
  entitlementsVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBranchDto {
  code: string;
  name: string;
  locale?: string;
  timeZone?: string;
  isDefault?: boolean;
  idempotencyKey?: string;
  type?: string;
}

export type UpdateBranchDtoStatus = typeof UpdateBranchDtoStatus[keyof typeof UpdateBranchDtoStatus];


export const UpdateBranchDtoStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  SUSPENDED: 'SUSPENDED',
} as const;

export interface UpdateBranchDto {
  expectedUpdatedAt: string;
  name?: string;
  status?: UpdateBranchDtoStatus;
}

export interface ConfigureOrganizationModuleDto { [key: string]: unknown }

export interface ClientAccountListSurfaceDto {
  id: string;
  code: string;
  fullName: string;
  email?: string;
  accountType: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClientAccountSurfaceDto {
  id: string;
  code: string;
  fullName: string;
  email?: string;
  phone?: string;
  panNumber?: string;
  accountType: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export type ClientSuitabilityProfileSurfaceDtoQuestionnaire = { [key: string]: unknown };

export type ClientSuitabilityProfileSurfaceDtoEvidence = { [key: string]: unknown };

export interface ClientSuitabilityProfileSurfaceDto {
  id: string;
  version: number;
  revision: number;
  status: string;
  effectiveStatus: string;
  score: number;
  riskProfile: string;
  investmentObjectives: string[];
  horizonMonths: number;
  liquidityNeed: string;
  lossCapacity: string;
  rationale: string;
  questionnaire: ClientSuitabilityProfileSurfaceDtoQuestionnaire;
  evidence: ClientSuitabilityProfileSurfaceDtoEvidence;
  assessedAt: string;
  expiresAt: string;
  materialChangeReason?: string;
  approvedAt?: string;
}

export type CreateClientSuitabilityProfileDtoQuestionnaire = { [key: string]: unknown };

export type CreateClientSuitabilityProfileDtoEvidence = { [key: string]: unknown };

export interface CreateClientSuitabilityProfileDto {
  questionnaire: CreateClientSuitabilityProfileDtoQuestionnaire;
  score: number;
  riskProfile: string;
  investmentObjectives: string[];
  horizonMonths: number;
  liquidityNeed: string;
  lossCapacity: string;
  rationale: string;
  evidence: CreateClientSuitabilityProfileDtoEvidence;
  assessedAt: string;
  expiresAt: string;
}

export interface ApproveClientSuitabilityProfileDto {
  expectedRevision: number;
}

export interface RecordClientSuitabilityMaterialChangeDto {
  expectedRevision: number;
  reason: string;
}

export interface CreateClientAccountDto {
  branchId?: string;
  code: string;
  fullName: string;
  email?: string;
  phone?: string;
  panNumber?: string;
  accountType?: string;
  notes?: string;
  idempotencyKey?: string;
}

export interface UpdateClientAccountDto {
  expectedUpdatedAt?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  panNumber?: string;
  accountType?: string;
  isActive?: boolean;
  notes?: string;
}

export type OrganizationClientCustodyAccountListSurfaceDtoClient = { [key: string]: unknown };

export interface OrganizationClientCustodyAccountListSurfaceDto {
  id: string;
  clientAccountId: string;
  client?: OrganizationClientCustodyAccountListSurfaceDtoClient;
  accountNumber: string;
  accountType: string;
  baseCurrency: string;
  /**
     * Masked BOID; raw identifier is never projected.
     * @nullable
     */
  boIdMasked: string | null;
  /** @nullable */
  dpCode: string | null;
  isDefault: boolean;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export type OrganizationClientCustodyAccountSurfaceDtoClient = { [key: string]: unknown };

export interface OrganizationClientCustodyAccountSurfaceDto {
  id: string;
  clientAccountId: string;
  client?: OrganizationClientCustodyAccountSurfaceDtoClient;
  accountNumber: string;
  accountType: string;
  baseCurrency: string;
  /**
     * Masked BOID; raw identifier is never projected.
     * @nullable
     */
  boIdMasked: string | null;
  /** @nullable */
  dpCode: string | null;
  isDefault: boolean;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export type CreateOrganizationClientCustodyAccountDtoAccountType = typeof CreateOrganizationClientCustodyAccountDtoAccountType[keyof typeof CreateOrganizationClientCustodyAccountDtoAccountType];


export const CreateOrganizationClientCustodyAccountDtoAccountType = {
  CUSTODY: 'CUSTODY',
  DEMAT: 'DEMAT',
  BROKERAGE: 'BROKERAGE',
} as const;

export interface CreateOrganizationClientCustodyAccountDto {
  clientAccountId: string;
  accountNumber: string;
  accountType: CreateOrganizationClientCustodyAccountDtoAccountType;
  baseCurrency: string;
  isDefault?: boolean;
  /** 16-digit CDSC BOID */
  boId?: string;
  /** 5 to 8 digit DP code */
  dpCode?: string;
  idempotencyKey?: string;
}

export type UpdateOrganizationClientCustodyAccountDtoAccountType = typeof UpdateOrganizationClientCustodyAccountDtoAccountType[keyof typeof UpdateOrganizationClientCustodyAccountDtoAccountType];


export const UpdateOrganizationClientCustodyAccountDtoAccountType = {
  CUSTODY: 'CUSTODY',
  DEMAT: 'DEMAT',
  BROKERAGE: 'BROKERAGE',
} as const;

export interface UpdateOrganizationClientCustodyAccountDto {
  expectedVersion: number;
  accountNumber?: string;
  accountType?: UpdateOrganizationClientCustodyAccountDtoAccountType;
  baseCurrency?: string;
  isDefault?: boolean;
  isActive?: boolean;
  /** 16-digit CDSC BOID */
  boId?: string;
  /** 5 to 8 digit DP code */
  dpCode?: string;
}

export type OrganizationClientCustodyAccountDocumentSurfaceDtoContentType = typeof OrganizationClientCustodyAccountDocumentSurfaceDtoContentType[keyof typeof OrganizationClientCustodyAccountDocumentSurfaceDtoContentType];


export const OrganizationClientCustodyAccountDocumentSurfaceDtoContentType = {
  'application/pdf': 'application/pdf',
  'image/jpeg': 'image/jpeg',
  'image/png': 'image/png',
} as const;

export interface OrganizationClientCustodyAccountDocumentSurfaceDto {
  id: string;
  documentType: string;
  originalFileName: string;
  contentType: OrganizationClientCustodyAccountDocumentSurfaceDtoContentType;
  byteSize: number;
  sha256: string;
  scanStatus: string;
  createdAt: string;
}

export type CreateOrganizationClientCustodyAccountDocumentDtoContentType = typeof CreateOrganizationClientCustodyAccountDocumentDtoContentType[keyof typeof CreateOrganizationClientCustodyAccountDocumentDtoContentType];


export const CreateOrganizationClientCustodyAccountDocumentDtoContentType = {
  'application/pdf': 'application/pdf',
  'image/jpeg': 'image/jpeg',
  'image/png': 'image/png',
} as const;

export interface CreateOrganizationClientCustodyAccountDocumentDto {
  documentType: string;
  storageKey: string;
  originalFileName: string;
  contentType: CreateOrganizationClientCustodyAccountDocumentDtoContentType;
  /** @maximum 10485760 */
  byteSize: number;
  /** Lowercase SHA-256 digest of the scanned object. */
  sha256: string;
  idempotencyKey?: string;
}

export interface OrganizationClientCustodyAccountSignatorySurfaceDto {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  designation?: string;
  createdAt: string;
}

export interface CreateOrganizationClientCustodyAccountSignatoryDto {
  name: string;
  email?: string;
  phone?: string;
  designation?: string;
  idempotencyKey?: string;
}

export type OrganizationManagedPortfolioSurfaceDtoClient = { [key: string]: unknown };

export type OrganizationManagedPortfolioSurfaceDtoCustodyAccount = { [key: string]: unknown };

export interface OrganizationManagedPortfolioSurfaceDto {
  id: string;
  clientAccountId: string;
  portfolioAccountId: string;
  name: string;
  baseCurrency: string;
  strategy: string;
  mandateStartDate: string;
  mandateEndDate?: string;
  status: string;
  version: number;
  client?: OrganizationManagedPortfolioSurfaceDtoClient;
  custodyAccount?: OrganizationManagedPortfolioSurfaceDtoCustodyAccount;
}

export type OrganizationManagedPortfolioMandateSurfaceDtoStatus = typeof OrganizationManagedPortfolioMandateSurfaceDtoStatus[keyof typeof OrganizationManagedPortfolioMandateSurfaceDtoStatus];


export const OrganizationManagedPortfolioMandateSurfaceDtoStatus = {
  DRAFT: 'DRAFT',
  APPROVED: 'APPROVED',
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
} as const;

export type OrganizationManagedPortfolioMandateSurfaceDtoFeeSchedule = { [key: string]: unknown };

export interface OrganizationManagedPortfolioMandateSurfaceDto {
  id: string;
  version: number;
  revision: number;
  status: OrganizationManagedPortfolioMandateSurfaceDtoStatus;
  effectiveStatus: string;
  objectives: string[];
  allowedAssets: string[];
  prohibitedAssets: string[];
  maxConcentrationPct: number;
  minimumLiquidityPct: number;
  permittedCurrencies: string[];
  benchmark?: string;
  riskProfile: string;
  feeSchedule: OrganizationManagedPortfolioMandateSurfaceDtoFeeSchedule;
  effectiveFrom: string;
  effectiveTo?: string;
  approvedAt?: string;
  suspendedAt?: string;
  suspensionReason?: string;
}

export type CreateOrganizationManagedPortfolioMandateDtoFeeSchedule = { [key: string]: unknown };

export interface CreateOrganizationManagedPortfolioMandateDto {
  objectives: string[];
  allowedAssets: string[];
  prohibitedAssets: string[];
  maxConcentrationPct: number;
  minimumLiquidityPct: number;
  permittedCurrencies: string[];
  benchmark?: string;
  riskProfile: string;
  feeSchedule: CreateOrganizationManagedPortfolioMandateDtoFeeSchedule;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface ApproveOrganizationManagedPortfolioMandateDto {
  expectedRevision: number;
}

export interface SuspendOrganizationManagedPortfolioMandateDto {
  expectedRevision: number;
  reason: string;
}

export interface AssessManagedPortfolioSuitabilityDto {
  suitabilityProfileId: string;
  rationale: string;
  asOfDate?: string;
}

export type ManagedPortfolioAllocationTargetDtoDimension = typeof ManagedPortfolioAllocationTargetDtoDimension[keyof typeof ManagedPortfolioAllocationTargetDtoDimension];


export const ManagedPortfolioAllocationTargetDtoDimension = {
  ASSET: 'ASSET',
  SECURITY: 'SECURITY',
  CURRENCY: 'CURRENCY',
} as const;

export interface ManagedPortfolioAllocationTargetDto {
  dimension: ManagedPortfolioAllocationTargetDtoDimension;
  key: string;
  targetPct: number;
  minimumPct?: number;
  maximumPct?: number;
}

export interface CreateManagedPortfolioAllocationTargetDto {
  targets: ManagedPortfolioAllocationTargetDto[];
}

export type DecideManagedPortfolioAllocationTargetDtoDecision = typeof DecideManagedPortfolioAllocationTargetDtoDecision[keyof typeof DecideManagedPortfolioAllocationTargetDtoDecision];


export const DecideManagedPortfolioAllocationTargetDtoDecision = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
} as const;

export interface DecideManagedPortfolioAllocationTargetDto {
  expectedRevision: number;
  decision: DecideManagedPortfolioAllocationTargetDtoDecision;
  reason?: string;
}

export interface CreateManagedPortfolioRebalanceSimulationDto {
  allocationTargetVersionId: string;
  taxAssumption: string;
  cashFlowAmount?: string;
}

export type DecideManagedPortfolioRebalanceSimulationDtoDecision = typeof DecideManagedPortfolioRebalanceSimulationDtoDecision[keyof typeof DecideManagedPortfolioRebalanceSimulationDtoDecision];


export const DecideManagedPortfolioRebalanceSimulationDtoDecision = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
} as const;

export interface DecideManagedPortfolioRebalanceSimulationDto {
  expectedVersion: number;
  decision: DecideManagedPortfolioRebalanceSimulationDtoDecision;
  reason: string;
}

export interface CreateOrganizationManagedPortfolioDto {
  clientAccountId: string;
  portfolioAccountId: string;
  name: string;
  baseCurrency: string;
  strategy: string;
  mandateStartDate: string;
  mandateEndDate?: string;
  idempotencyKey?: string;
}

export type UpdateOrganizationManagedPortfolioDtoStatus = typeof UpdateOrganizationManagedPortfolioDtoStatus[keyof typeof UpdateOrganizationManagedPortfolioDtoStatus];


export const UpdateOrganizationManagedPortfolioDtoStatus = {
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  CLOSED: 'CLOSED',
} as const;

export interface UpdateOrganizationManagedPortfolioDto {
  expectedVersion: number;
  name?: string;
  baseCurrency?: string;
  strategy?: string;
  mandateStartDate?: string;
  mandateEndDate?: string;
  status?: UpdateOrganizationManagedPortfolioDtoStatus;
}

export type ClientLifecycleTransitionDtoAction = typeof ClientLifecycleTransitionDtoAction[keyof typeof ClientLifecycleTransitionDtoAction];


export const ClientLifecycleTransitionDtoAction = {
  FREEZE: 'FREEZE',
  UNFREEZE: 'UNFREEZE',
  CLOSE: 'CLOSE',
} as const;

export interface ClientLifecycleTransitionDto {
  action: ClientLifecycleTransitionDtoAction;
  reason: string;
}

export interface BeneficialOwnerDto {
  fullName: string;
  identityReference?: string;
  ownershipPercent: number;
  pepIndicator?: boolean;
  sanctionsIndicator?: boolean;
  highRiskIndicator?: boolean;
}

export interface ConfigureClientDueDiligenceDto {
  jurisdiction: string;
  identityReference: string;
  beneficialOwners?: BeneficialOwnerDto[];
}

export interface ClientScreeningDto {
  provider: string;
  pepMatch?: boolean;
  sanctionsMatch?: boolean;
  highRiskMatch?: boolean;
  sourceReference?: string;
}

export type ClientDueDiligenceReviewDtoDecision = typeof ClientDueDiligenceReviewDtoDecision[keyof typeof ClientDueDiligenceReviewDtoDecision];


export const ClientDueDiligenceReviewDtoDecision = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  REQUIRES_REMEDIATION: 'REQUIRES_REMEDIATION',
} as const;

export interface ClientDueDiligenceReviewDto {
  decision: ClientDueDiligenceReviewDtoDecision;
  rationale?: string;
}

export type ClientDuplicateDecisionDtoDecision = typeof ClientDuplicateDecisionDtoDecision[keyof typeof ClientDuplicateDecisionDtoDecision];


export const ClientDuplicateDecisionDtoDecision = {
  CLEARED: 'CLEARED',
  CONFIRMED: 'CONFIRMED',
} as const;

export interface ClientDuplicateDecisionDto {
  decision: ClientDuplicateDecisionDtoDecision;
  rationale: string;
}

export interface ClientAccountDocumentSurfaceDto {
  id: string;
  documentType: string;
  documentUrl: string;
  verified?: boolean;
  expiresAt?: string;
}

export interface ClientAccountDocumentDto {
  documentType: string;
  documentUrl: string;
  verified?: boolean;
  expiresAt?: string;
}

export interface PortfolioAccountSurfaceDto {
  id: string;
  accountNumber?: string;
  boId?: string;
  dpCode?: string;
  ownerType: string;
  ownerClientAccountId?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePortfolioAccountDto {
  accountNumber?: string;
  boId?: string;
  dpCode?: string;
  ownerType?: string;
  ownerClientAccountId?: string;
  isDefault?: boolean;
}

export interface UpdatePortfolioAccountDto {
  accountNumber?: string;
  boId?: string;
  dpCode?: string;
  isDefault?: boolean;
  isActive?: boolean;
}

export interface PortfolioAccountHoldingSurfaceDto {
  instrumentSymbol: string;
  quantity: number;
  weightedAverageCost?: string;
  totalCost?: string;
}

export interface PortfolioAccountBalanceSurfaceDto {
  portfolioAccountId: string;
  holdings: PortfolioAccountHoldingSurfaceDto[];
}

export interface PortfolioAccountSignatorySurfaceDto {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  designation?: string;
}

export interface SignatoryDto {
  name: string;
  email?: string;
  phone?: string;
  designation?: string;
}

export type CreateCashMovementDtoMovementType = typeof CreateCashMovementDtoMovementType[keyof typeof CreateCashMovementDtoMovementType];


export const CreateCashMovementDtoMovementType = {
  DEPOSIT: 'DEPOSIT',
  WITHDRAWAL: 'WITHDRAWAL',
  TRANSFER: 'TRANSFER',
} as const;

export interface CreateCashMovementDto {
  movementType: CreateCashMovementDtoMovementType;
  amount: string;
  currency: string;
  businessDate: string;
  bankReference: string;
  reason: string;
  cashLedgerAccountId: string;
  counterpartyLedgerAccountId: string;
  destinationPortfolioAccountId?: string;
}

export type CashMovementDecisionDtoDecision = typeof CashMovementDecisionDtoDecision[keyof typeof CashMovementDecisionDtoDecision];


export const CashMovementDecisionDtoDecision = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  CANCEL: 'CANCEL',
} as const;

export interface CashMovementDecisionDto {
  decision: CashMovementDecisionDtoDecision;
  expectedVersion: number;
}

export interface CashMovementPostDto {
  expectedVersion: number;
  monetaryApprovalDecisionId?: string;
}

export type CreateAccountGroupDtoGroupType = typeof CreateAccountGroupDtoGroupType[keyof typeof CreateAccountGroupDtoGroupType];


export const CreateAccountGroupDtoGroupType = {
  BALANCE_SHEET: 'BALANCE_SHEET',
  PROFIT_AND_LOSS: 'PROFIT_AND_LOSS',
} as const;

export type CreateAccountGroupDtoAccountType = typeof CreateAccountGroupDtoAccountType[keyof typeof CreateAccountGroupDtoAccountType];


export const CreateAccountGroupDtoAccountType = {
  ASSET: 'ASSET',
  LIABILITY: 'LIABILITY',
  EQUITY: 'EQUITY',
  REVENUE: 'REVENUE',
  EXPENSE: 'EXPENSE',
} as const;

export type CreateAccountGroupDtoCashFlowClassification = typeof CreateAccountGroupDtoCashFlowClassification[keyof typeof CreateAccountGroupDtoCashFlowClassification];


export const CreateAccountGroupDtoCashFlowClassification = {
  OPERATING: 'OPERATING',
  INVESTING: 'INVESTING',
  FINANCING: 'FINANCING',
  OTHER: 'OTHER',
} as const;

export interface CreateAccountGroupDto {
  code: string;
  name: string;
  groupType: CreateAccountGroupDtoGroupType;
  accountType: CreateAccountGroupDtoAccountType;
  cashFlowClassification?: CreateAccountGroupDtoCashFlowClassification;
  parentId?: string;
  description?: string;
}

export interface RequestPerformanceFeeRestatementDto {
  /** @minimum 1 */
  expectedVersion: number;
  reason: string;
  openingSnapshotId: string;
  closingSnapshotId: string;
  externalFlowIds?: string[];
}

export interface FinalizePerformanceFeeRestatementDto {
  /** @minimum 1 */
  expectedRestatementVersion: number;
  /** @minimum 1 */
  expectedReplacementVersion: number;
}

export interface PostPerformanceFeeRestatementReversalDto {
  /** @minimum 1 */
  expectedRestatementVersion: number;
  /** @minimum 1 */
  expectedJournalVersion: number;
}

export type CreateLedgerAccountDtoAccountType = typeof CreateLedgerAccountDtoAccountType[keyof typeof CreateLedgerAccountDtoAccountType];


export const CreateLedgerAccountDtoAccountType = {
  ASSET: 'ASSET',
  LIABILITY: 'LIABILITY',
  EQUITY: 'EQUITY',
  REVENUE: 'REVENUE',
  EXPENSE: 'EXPENSE',
} as const;

export interface CreateLedgerAccountDto {
  accountGroupId: string;
  code: string;
  name: string;
  accountType: CreateLedgerAccountDtoAccountType;
  openingBalance?: string;
  description?: string;
  isCashEquivalent?: boolean;
}

export interface UpdateLedgerAccountDto {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface CreateFiscalYearDto {
  name: string;
  startDate: string;
  endDate: string;
}

export interface FiscalYearLifecycleDto {
  /** Current fiscal-year version. */
  expectedVersion: number;
  /** Auditable reason for closing or reopening the fiscal year. */
  reason: string;
  /** Required for reopening: durable reference to the approved correction window. */
  approvalReference?: string;
}

export interface OpeningCutoverLotDto {
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  quantity: string;
  totalCost: string;
  acquisitionDate: string;
}

export interface OpeningCutoverCashDto {
  clientAccountId: string;
  portfolioAccountId?: string;
  currency: string;
  amount: string;
}

export interface OpeningCutoverReceivableDto {
  clientAccountId: string;
  portfolioAccountId?: string;
  currency: string;
  amount: string;
  ledgerAccountId: string;
  reference: string;
}

export type JournalLineDtoDimensions = {[key: string]: string};

export interface JournalLineDto {
  ledgerAccountId: string;
  debit: string;
  credit: string;
  narration?: string;
  dimensions?: JournalLineDtoDimensions;
}

export interface PreviewOpeningCutoverDto {
  cutoverDate: string;
  lots: OpeningCutoverLotDto[];
  cash: OpeningCutoverCashDto[];
  receivables: OpeningCutoverReceivableDto[];
  journalLines: JournalLineDto[];
  supersedesBatchId?: string;
}

export interface CreateFiscalPeriodDto {
  fiscalYearId: string;
  name: string;
  bsYear: number;
  bsMonth: number;
  startDate: string;
  endDate: string;
}

export interface CreateJournalDto {
  fiscalYearId?: string;
  entryDate: string;
  reference?: string;
  narration?: string;
  lines: JournalLineDto[];
  sourceType?: string;
  sourceId?: string;
}

export interface JournalTransitionDto {
  /** @minimum 1 */
  expectedVersion: number;
}

export interface ReverseJournalDto {
  reversalDate: string;
  reason: string;
  /** @minimum 1 */
  expectedVersion: number;
}

export type CreateJournalTemplateDtoPolicy = { [key: string]: unknown };

export interface CreateJournalTemplateDto {
  templateKey: string;
  name: string;
  description?: string;
  /** @minimum 0 */
  expectedLatestVersion: number;
  policy: CreateJournalTemplateDtoPolicy;
}

export type CreateJournalScheduleDtoFrequency = typeof CreateJournalScheduleDtoFrequency[keyof typeof CreateJournalScheduleDtoFrequency];


export const CreateJournalScheduleDtoFrequency = {
  ONCE: 'ONCE',
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
} as const;

export type CreateJournalScheduleDtoDimensions = {[key: string]: string};

export interface CreateJournalScheduleDto {
  scheduleKey: string;
  templateId: string;
  /** @minimum 0 */
  expectedLatestVersion: number;
  effectiveFrom: string;
  effectiveTo?: string;
  frequency: CreateJournalScheduleDtoFrequency;
  /**
     * @minimum 1
     * @maximum 366
     */
  interval: number;
  /** @minimum 1 */
  autoReversalDelayDays?: number;
  dimensions: CreateJournalScheduleDtoDimensions;
  reference?: string;
  narration?: string;
}

export interface CancelJournalScheduleDto {
  /** @minimum 1 */
  expectedVersion: number;
  effectiveDate: string;
  reason: string;
}

export type PostingRuleLineDtoAmount = { [key: string]: unknown };

export interface PostingRuleLineDto {
  accountCode: string;
  side: string;
  amount: PostingRuleLineDtoAmount;
  currency?: string;
}

export type CreatePostingRuleDtoFilter = { [key: string]: unknown };

export interface CreatePostingRuleDto {
  name: string;
  description?: string;
  factTypes: string[];
  filter: CreatePostingRuleDtoFilter;
  lines: PostingRuleLineDto[];
  priority?: number;
}

export type PostingRuleTestFactDataDtoFactData = { [key: string]: unknown };

export interface PostingRuleTestFactDataDto {
  factData: PostingRuleTestFactDataDtoFactData;
}

export type AutoPostFromFactsDtoFactsItem = { [key: string]: unknown };

export interface AutoPostFromFactsDto {
  factType: string;
  facts: AutoPostFromFactsDtoFactsItem[];
}

export interface OpenComplianceBreachDto { [key: string]: unknown }

export interface RemediateComplianceBreachDto { [key: string]: unknown }

export interface WaiveComplianceBreachDto { [key: string]: unknown }

export interface OpenSuspiciousActivityCaseDto { [key: string]: unknown }

export interface CaseDecisionDto { [key: string]: unknown }

export interface ReportSuspiciousActivityCaseDto { [key: string]: unknown }

export interface RemediationDto {
  /** Remediation action code */
  action: string;
  /** Target route ID for remediation */
  routeId?: string;
}

/**
 * Public-facing availability state
 */
export type RouteAccessDecisionDtoAvailability = typeof RouteAccessDecisionDtoAvailability[keyof typeof RouteAccessDecisionDtoAvailability];


export const RouteAccessDecisionDtoAvailability = {
  AVAILABLE: 'AVAILABLE',
  READ_ONLY: 'READ_ONLY',
  LOCKED_TIER: 'LOCKED_TIER',
  LOCKED_PERMISSION: 'LOCKED_PERMISSION',
  FEATURE_DISABLED: 'FEATURE_DISABLED',
  DATA_UNAVAILABLE: 'DATA_UNAVAILABLE',
  HIDDEN: 'HIDDEN',
} as const;

/**
 * Internal authorization reason code (audit only)
 */
export type RouteAccessDecisionDtoInternalReason = typeof RouteAccessDecisionDtoInternalReason[keyof typeof RouteAccessDecisionDtoInternalReason];


export const RouteAccessDecisionDtoInternalReason = {
  AAL_INSUFFICIENT: 'AAL_INSUFFICIENT',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  PERMISSION_MISSING: 'PERMISSION_MISSING',
  ACTOR_TYPE_DENIED: 'ACTOR_TYPE_DENIED',
  ACTOR_INCOMPATIBLE: 'ACTOR_INCOMPATIBLE',
  SCOPE_DENIED: 'SCOPE_DENIED',
  SCOPE_INCOMPATIBLE: 'SCOPE_INCOMPATIBLE',
  CONTEXT_MISSING: 'CONTEXT_MISSING',
  CLIENT_SCOPE_REQUIRED: 'CLIENT_SCOPE_REQUIRED',
  CLIENT_FORCING_VIOLATION: 'CLIENT_FORCING_VIOLATION',
  OWNERSHIP_VIOLATION: 'OWNERSHIP_VIOLATION',
  ORGANIZATION_ISOLATION_VIOLATION: 'ORGANIZATION_ISOLATION_VIOLATION',
  PERSONA_INCOMPATIBLE: 'PERSONA_INCOMPATIBLE',
  PERSONA_POLICY_INVALID: 'PERSONA_POLICY_INVALID',
  AUTHENTICATION_REQUIRED: 'AUTHENTICATION_REQUIRED',
  DELEGATION_NOT_ELIGIBLE: 'DELEGATION_NOT_ELIGIBLE',
  DELEGATION_EXPIRED: 'DELEGATION_EXPIRED',
  DELEGATION_REQUIRED: 'DELEGATION_REQUIRED',
  DELEGATION_NOT_ALLOWED: 'DELEGATION_NOT_ALLOWED',
  WRITE_NOT_ALLOWED_IN_DELEGATION: 'WRITE_NOT_ALLOWED_IN_DELEGATION',
  RESOURCE_LIFECYCLE_RESTRICTED: 'RESOURCE_LIFECYCLE_RESTRICTED',
  ENTITLEMENT_MISSING: 'ENTITLEMENT_MISSING',
  MODULE_DISABLED: 'MODULE_DISABLED',
  MODULE_UNKNOWN: 'MODULE_UNKNOWN',
  OPERATION_UNKNOWN: 'OPERATION_UNKNOWN',
  OPERATION_MISSING: 'OPERATION_MISSING',
  PLANNED_FEATURE: 'PLANNED_FEATURE',
  MAINTENANCE_MODE: 'MAINTENANCE_MODE',
  TIER_LOCKED: 'TIER_LOCKED',
  DATA_UNAVAILABLE: 'DATA_UNAVAILABLE',
} as const;

export interface RouteAccessDecisionDto {
  /** Canonical route identifier */
  routeId: string;
  /** Public-facing availability state */
  availability: RouteAccessDecisionDtoAvailability;
  /** Operation ID associated with this route */
  operationId?: string;
  /** i18n key for disabled reason */
  disabledReasonKey?: string;
  /** Internal authorization reason code (audit only) */
  internalReason?: RouteAccessDecisionDtoInternalReason;
  /** Policy version that made this decision */
  policyVersion?: string;
  /** Whether decision was made via delegation */
  delegated?: boolean;
  /** Unique decision ID for traceability */
  decisionId?: string;
  /** Target route ID for navigation */
  targetRouteId?: string;
  /** Remediation action for denied access */
  remediation?: RemediationDto;
}

export type AuditLogSurfaceDtoOldValues = { [key: string]: unknown };

export type AuditLogSurfaceDtoNewValues = { [key: string]: unknown };

export interface AuditLogSurfaceDto {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  userId: string;
  userEmail: string;
  oldValues?: AuditLogSurfaceDtoOldValues;
  newValues?: AuditLogSurfaceDtoNewValues;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface AuditLogListDto {
  data: AuditLogSurfaceDto[];
  page: number;
  totalPages: number;
  total: number;
}

export interface CreateUserDto {
  idempotencyKey?: string;
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  roleIds?: string[];
  branchId?: string;
}

export interface UserSurfaceDto {
  /** Canonical membership id used for organization-scoped routes. */
  id: string;
  /** User identity id used only for identity-level security operations. */
  userIdentityId: string;
  /** Membership id (same as id) for explicit context. */
  membershipId: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  /** Membership status (ACTIVE or INACTIVE) */
  membershipStatus: string;
  mfaEnabled: boolean;
  orgId: string;
  branchId?: string;
  roles: string[];
  roleIds: string[];
  /** Membership authorization version for optimistic concurrency. */
  version: number;
  lastLoginAt?: string;
  createdAt: string;
}

export interface UpdateUserDto {
  /** Current membership authorization version. */
  expectedVersion?: number;
  firstName?: string;
  lastName?: string;
  /** Membership status: ACTIVE or INACTIVE */
  membershipStatus?: string;
  roleIds?: string[];
  /** Organization-scoped branch for role assignments. */
  branchId?: string;
  mfaEnabled?: boolean;
}

export type PermissionCatalogDtoAuthority = typeof PermissionCatalogDtoAuthority[keyof typeof PermissionCatalogDtoAuthority];


export const PermissionCatalogDtoAuthority = {
  PLATFORM: 'PLATFORM',
  ORGANIZATION: 'ORGANIZATION',
  CLIENT: 'CLIENT',
} as const;

export interface PermissionCatalogDto {
  id: string;
  /** Permission code e.g. access:org:admin */
  code: string;
  /** i18n key for label */
  labelKey: string;
  /** Safe human-readable permission label */
  label: string;
  /** Canonical permission domain */
  domain: string;
  /** Canonical UI grouping label */
  permissionGroup: string;
  authority: PermissionCatalogDtoAuthority;
  resourceScope: string;
  /** Module this permission belongs to */
  module: string;
  /** Resource being acted upon */
  resource: string;
  /** Action being performed */
  action: string;
  /** Scope kinds this permission applies to */
  scopeKinds: string[];
  /** Whether this permission can be delegated */
  delegable: boolean;
}

export interface UpdateOrganizationProfileDto {
  /** Current organization version for optimistic concurrency. */
  expectedVersion: number;
  name?: string;
  legalName?: string;
  /** ISO 4217 currency code. */
  baseCurrency?: string;
}

export interface UserTableRowsDto {
  rows: UserSurfaceDto[];
}

export interface UserTableDto {
  table: UserTableRowsDto;
}

export interface UserListSurfaceDto {
  data: UserTableDto;
  page: number;
  totalPages: number;
  total: number;
}

export interface RoleSurfaceDto {
  id: string;
  name: string;
  userTypeId: string;
  status: string;
  isSystem: boolean;
  version: number;
  permissions?: string[];
  createdAt: string;
}

export interface RoleTableRowsDto {
  rows: RoleSurfaceDto[];
}

export interface RoleTableDto {
  table: RoleTableRowsDto;
}

export interface RoleListSurfaceDto {
  data: RoleTableDto;
}

export interface CreateRoleDto {
  idempotencyKey?: string;
  name: string;
  code: string;
  permissionIds?: string[];
}

export interface UpdateRoleDto {
  /** Current role version for optimistic concurrency. */
  expectedVersion?: number;
  name?: string;
  permissionIds?: string[];
  status?: string;
}

export type InvitePlatformUserDtoRoleCode = typeof InvitePlatformUserDtoRoleCode[keyof typeof InvitePlatformUserDtoRoleCode];


export const InvitePlatformUserDtoRoleCode = {
  PLATFORM_ROOT_PERSONA: 'PLATFORM_ROOT_PERSONA',
  PLATFORM_ADMIN_PERSONA: 'PLATFORM_ADMIN_PERSONA',
  PLATFORM_USER_PERSONA: 'PLATFORM_USER_PERSONA',
} as const;

export interface InvitePlatformUserDto {
  email: string;
  roleCode: InvitePlatformUserDtoRoleCode;
}

export interface InstrumentSurfaceDto {
  id: string;
  symbol: string;
  instrumentName: string;
  securityName?: string;
  isin?: string;
  currency?: string;
  exchange?: string;
  sector?: string;
  instrumentType?: string;
  status: string;
  isShellEntry: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInstrumentDto {
  symbol: string;
  instrumentName: string;
  securityName?: string;
  isin?: string;
  currency?: string;
  exchange?: string;
  sector?: string;
  instrumentType?: string;
  status?: string;
  isShellEntry?: boolean;
}

export interface UpdateInstrumentDto {
  expectedVersion: number;
  /** Business-effective time for an immutable correction. */
  effectiveFrom?: string;
  /** Successor canonical symbol for a merger or reorganization. */
  successorSymbol?: string;
  instrumentName?: string;
  securityName?: string;
  isin?: string;
  currency?: string;
  exchange?: string;
  sector?: string;
  instrumentType?: string;
  status?: string;
  isShellEntry?: boolean;
}

export interface BulkUpsertInstrumentItemDto {
  symbol: string;
  instrumentName: string;
  securityName?: string;
  isin?: string;
  currency?: string;
  exchange?: string;
  sector?: string;
  instrumentType?: string;
  status?: string;
}

export type BulkUpsertInstrumentDtoMode = typeof BulkUpsertInstrumentDtoMode[keyof typeof BulkUpsertInstrumentDtoMode];


export const BulkUpsertInstrumentDtoMode = {
  PREVIEW: 'PREVIEW',
  COMMIT: 'COMMIT',
} as const;

export interface BulkUpsertInstrumentDto {
  items: BulkUpsertInstrumentItemDto[];
  mode?: BulkUpsertInstrumentDtoMode;
}

export type CreateInstrumentOverrideDtoFieldName = typeof CreateInstrumentOverrideDtoFieldName[keyof typeof CreateInstrumentOverrideDtoFieldName];


export const CreateInstrumentOverrideDtoFieldName = {
  PRICE_CLOSE: 'PRICE_CLOSE',
  FX_RATE: 'FX_RATE',
} as const;

export type CreateInstrumentOverrideDtoSourceState = typeof CreateInstrumentOverrideDtoSourceState[keyof typeof CreateInstrumentOverrideDtoSourceState];


export const CreateInstrumentOverrideDtoSourceState = {
  MISSING: 'MISSING',
  DISPUTED: 'DISPUTED',
} as const;

export interface CreateInstrumentOverrideDto {
  fieldName: CreateInstrumentOverrideDtoFieldName;
  overrideValue: string;
  sourceState: CreateInstrumentOverrideDtoSourceState;
  sourceObservationId?: string;
  baselineValue?: string;
  reason: string;
  evidenceReferences: string[];
  portfolioAccountId?: string;
  effectiveFrom: string;
  effectiveTo: string;
}

export interface UpdateInstrumentOverrideDto {
  expectedVersion: number;
  overrideValue: string;
  reason: string;
  evidenceReferences: string[];
  effectiveFrom: string;
  effectiveTo: string;
}

export interface DecideInstrumentOverrideDto {
  expectedVersion: number;
  reason: string;
}

export interface CreateMarketDataDto {
  instrumentSymbol?: string;
  priceType: string;
  price: string;
  currency?: string;
  tradedDate: string;
  source?: string;
}

export interface MarketDataDto {
  id: string;
  instrumentSymbol: string;
  priceType: string;
  price: string;
  currency: string;
  tradedDate: string;
  source?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateMarketDataDto {
  price?: string;
  currency?: string;
  tradedDate?: string;
  source?: string;
}

export interface BulkUpsertMarketDataItemDto {
  instrumentSymbol: string;
  priceType: string;
  price: string;
  currency?: string;
  tradedDate: string;
  source?: string;
}

export interface BulkUpsertMarketDataDto {
  items: BulkUpsertMarketDataItemDto[];
}

export interface SourceObservedChargesDto {
  commission?: string;
  dpCharges?: string;
  sebonFee?: string;
  otherCharges?: string;
  capitalGainTax?: string;
  sourceReference?: string;
}

export type CreateTransactionDtoTransactionType = typeof CreateTransactionDtoTransactionType[keyof typeof CreateTransactionDtoTransactionType];


export const CreateTransactionDtoTransactionType = {
  OPENING_BALANCE: 'OPENING_BALANCE',
  PURCHASE: 'PURCHASE',
  SALE: 'SALE',
  BONUS_SHARE: 'BONUS_SHARE',
  RIGHT_SHARE: 'RIGHT_SHARE',
  SPLIT: 'SPLIT',
  CONSOLIDATION: 'CONSOLIDATION',
  MERGER: 'MERGER',
  TRANSFER_IN: 'TRANSFER_IN',
  TRANSFER_OUT: 'TRANSFER_OUT',
  FEE: 'FEE',
  TAX: 'TAX',
} as const;

export interface CreateTransactionDto {
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol?: string;
  instrumentId?: string;
  transactionType: CreateTransactionDtoTransactionType;
  transactionDate: string;
  quantity: string;
  pricePerUnit?: string;
  sourceObservedCharges?: SourceObservedChargesDto;
  idempotencyKey?: string;
}

export interface TransactionSurfaceDto {
  id: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  transactionNumber?: string;
  transactionDate?: string;
  transactionType: string;
  quantity: number;
  pricePerUnit?: string;
  totalAmount?: string;
  commission?: string;
  dpCharges?: string;
  sebonFee?: string;
  otherCharges?: string;
  totalCost?: string;
  capitalGainTax?: string;
  status: string;
  version: number;
  postingVersion: number;
  postingStatus: string;
  postedAt?: string;
  idempotencyKey?: string;
  approvedBy?: string;
  approvedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface LifecycleCommandDto {
  expectedVersion?: number;
  idempotencyKey?: string;
  complianceWarningReason?: string;
  monetaryApprovalDecisionId?: string;
}

export interface ReasonedLifecycleCommandDto {
  expectedVersion?: number;
  idempotencyKey?: string;
  complianceWarningReason?: string;
  monetaryApprovalDecisionId?: string;
  reason: string;
}

export interface ReverseTransactionDto {
  expectedVersion?: number;
  idempotencyKey?: string;
  reason: string;
}

export interface RepairTransactionDto {
  expectedVersion?: number;
  idempotencyKey?: string;
  reason?: string;
  transactionDate?: string;
  quantity?: string;
  pricePerUnit?: string;
}

export interface TransactionLotSurfaceDto {
  id: string;
  transactionId: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  source: string;
  sourceId?: string;
  transactionDate?: string;
  purchasedQuantity: number;
  remainingQuantity: number;
  costPerUnit: string;
  totalLotCost: string;
  baseCostPerUnit: string;
  fxRate: string;
  fullyConsumed: boolean;
  voided: boolean;
  voidedBy?: string;
  voidedAt?: string;
  voidReason?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionDisposalSurfaceDto {
  id: string;
  saleTransactionId: string;
  sourceLotId: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  purchaseDate?: string;
  saleDate: string;
  quantity: number;
  saleRate: string;
  saleAmount: string;
  soldCostBasis: string;
  capitalGainTax: string;
  cgtRate: string;
  createdBy?: string;
  createdAt: string;
}

export type TransactionFactsSurfaceDtoPositionItem = { [key: string]: unknown };

export type TransactionFactsSurfaceDtoRealizedItem = { [key: string]: unknown };

export type TransactionFactsSurfaceDtoCgtItem = { [key: string]: unknown };

export interface TransactionFactsSurfaceDto {
  position?: TransactionFactsSurfaceDtoPositionItem[];
  realized?: TransactionFactsSurfaceDtoRealizedItem[];
  cgt?: TransactionFactsSurfaceDtoCgtItem[];
}

export type CorporateActionSurfaceDtoAudit = { [key: string]: unknown };

export type CorporateActionSurfaceDtoLineage = { [key: string]: unknown };

export interface CorporateActionSurfaceDto {
  id: string;
  instrumentSymbol: string;
  actionType: string;
  declarationDate?: string;
  bookCloseDate?: string;
  recordDate: string;
  exDate?: string;
  fiscalYear?: string;
  ratioNumerator?: string;
  ratioDenominator?: string;
  cashRate?: string;
  rightPrice?: string;
  replacementInstrumentSymbol?: string;
  sourceReference?: string;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  audit?: CorporateActionSurfaceDtoAudit;
  lineage?: CorporateActionSurfaceDtoLineage;
}

export interface CreateCorporateActionDto {
  instrumentSymbol: string;
  actionType: string;
  declarationDate?: string;
  bookCloseDate?: string;
  recordDate: string;
  exDate?: string;
  fiscalYear?: string;
  ratioNumerator?: string;
  ratioDenominator?: string;
  cashRate?: string;
  rightPrice?: string;
  replacementInstrumentSymbol?: string;
  sourceReference?: string;
}

export interface UpdateCorporateActionDto {
  declarationDate?: string;
  bookCloseDate?: string;
  recordDate?: string;
  exDate?: string;
  fiscalYear?: string;
  ratioNumerator?: string;
  ratioDenominator?: string;
  cashRate?: string;
  rightPrice?: string;
  replacementInstrumentSymbol?: string;
  sourceReference?: string;
  status?: string;
  cancellationReason?: string;
}

export interface CorporateActionEntitlementSurfaceDto {
  id: string;
  corporateActionId: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  eligibleQuantity: string;
  entitledQuantity?: string;
  cashAmount?: string;
  status: string;
  createdAt: string;
}

export interface ElectionDto {
  electionOption?: string;
}

export interface IncomeRecognitionDto {
  currency: string;
  receivableAccountCode: string;
  incomeAccountCode: string;
  withholdingTaxAccountCode: string;
  withholdingTaxRate: string;
}

export interface CashReceiptDto {
  receiptReference: string;
  receiptDate: string;
  receivedAmount: string;
  cashAccountCode: string;
  unappliedCashAccountCode?: string;
}

export interface PositionFactSurfaceDto {
  id: string;
  organizationId: string;
  branchId: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  source: string;
  sourceId: string;
  createdAt: string;
  updatedAt: string;
  asOfDate: string;
  quantity: string;
  averageCostPerUnit: string;
  totalCost: string;
  calculationVersion: string;
}

export interface ValuationFactSurfaceDto {
  id: string;
  organizationId: string;
  branchId: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  source: string;
  sourceId: string;
  createdAt: string;
  updatedAt: string;
  asOfDate: string;
  marketPrice?: string;
  marketValue?: string;
  totalCost?: string;
  unrealizedGainLoss?: string;
}

export interface MovementFactSurfaceDto {
  id: string;
  organizationId: string;
  branchId: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  source: string;
  sourceId: string;
  createdAt: string;
  updatedAt: string;
  yearMonth: string;
  openingQuantity: number;
  closingQuantity: number;
  purchases: number;
  sales: number;
  bonusShares: number;
  realizedGain?: string;
}

export interface RealizedGainLossFactSurfaceDto {
  id: string;
  organizationId: string;
  branchId: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  source: string;
  sourceId: string;
  createdAt: string;
  updatedAt: string;
  transactionId: string;
  saleDate: string;
  quantity: number;
  saleProceeds: string;
  costBasis: string;
  realizedGain: string;
}

export interface CgtFactSurfaceDto {
  id: string;
  organizationId: string;
  branchId: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  source: string;
  sourceId: string;
  createdAt: string;
  updatedAt: string;
  transactionId: string;
  saleDate: string;
  quantity: number;
  taxableGain: string;
  cgtRate: string;
  capitalGainTax: string;
  holdingPeriodDays: number;
}

export interface NfrsFactSurfaceDto {
  id: string;
  organizationId: string;
  branchId: string;
  clientAccountId: string;
  portfolioAccountId?: string;
  instrumentSymbol: string;
  source: string;
  sourceId: string;
  createdAt: string;
  updatedAt: string;
  asOfDate: string;
  standard: string;
  metric: string;
  amount: string;
}

export interface GenerateFactsDto {
  asOfDate: string;
  source?: string;
}

export interface FactGenerationResultDto {
  batchId: string;
  factCount: number;
  valuationFactCount?: number;
}

export interface FactGenerationBatchSurfaceDto {
  id: string;
  organizationId: string;
  branchId: string;
  eventType: string;
  sourceEntityType: string;
  sourceEntityId: string;
  status: string;
  factCount: number;
  generatedFactIds?: string;
  /** SHA-256 checksum of the generated fact lineage */
  lineageChecksum?: string;
  triggeredBy: string;
  completedAt?: string;
  startedAt: string;
  createdAt: string;
  updatedAt: string;
}

export type FactBatchLineageDtoIntegrity = { [key: string]: unknown };

export interface FactBatchLineageDto {
  batch: FactGenerationBatchSurfaceDto;
  facts: PositionFactSurfaceDto[];
  valuationFacts: ValuationFactSurfaceDto[];
  integrity: FactBatchLineageDtoIntegrity;
}

export interface CalculatePerformanceReturnDto { [key: string]: unknown }

export interface CalculateFixedIncomeValuationDto { [key: string]: unknown }

export interface ScheduleFixedIncomeLifecycleDto { [key: string]: unknown }

export interface TransitionFixedIncomeLifecycleDto { [key: string]: unknown }

export interface ProcessFixedIncomeDebtEventDto { [key: string]: unknown }

export interface RequestPerformanceReturnCorrectionDto { [key: string]: unknown }

export interface ApprovePerformanceReturnCorrectionDto { [key: string]: unknown }

export interface FinalizePerformanceReturnCorrectionDto { [key: string]: unknown }

export type ReportCatalogEntryDtoDescriptor = { [key: string]: unknown };

export type ReportCatalogEntryDtoParameterForm = { [key: string]: unknown };

export type ReportCatalogEntryDtoDefinitionForm = { [key: string]: unknown };

export type ReportCatalogEntryDtoRunForm = { [key: string]: unknown };

export interface ReportCatalogEntryDto {
  descriptor: ReportCatalogEntryDtoDescriptor;
  parameterForm: ReportCatalogEntryDtoParameterForm;
  definitionForm?: ReportCatalogEntryDtoDefinitionForm;
  runForm?: ReportCatalogEntryDtoRunForm;
  capabilities: string[];
}

/**
 * @nullable
 */
export type DashboardWidgetDataDtoData = { [key: string]: unknown } | null;

export type DashboardWidgetDataDtoState = typeof DashboardWidgetDataDtoState[keyof typeof DashboardWidgetDataDtoState];


export const DashboardWidgetDataDtoState = {
  CURRENT: 'CURRENT',
  STALE: 'STALE',
  PARTIAL: 'PARTIAL',
  EMPTY: 'EMPTY',
  UNAVAILABLE: 'UNAVAILABLE',
} as const;

export interface DashboardWidgetDataDto {
  id: string;
  /** @nullable */
  value: string | null;
  /** @nullable */
  data: DashboardWidgetDataDtoData;
  state: DashboardWidgetDataDtoState;
}

export type DashboardSummaryDtoDescriptor = { [key: string]: unknown };

export type DashboardSummaryDtoState = typeof DashboardSummaryDtoState[keyof typeof DashboardSummaryDtoState];


export const DashboardSummaryDtoState = {
  CURRENT: 'CURRENT',
  STALE: 'STALE',
  PARTIAL: 'PARTIAL',
  EMPTY: 'EMPTY',
  UNAVAILABLE: 'UNAVAILABLE',
} as const;

/**
 * Authoritative source book, lineage, freshness, publication, restatement, and calculation metadata for financial dashboards.
 */
export type DashboardSummaryDtoAuthoritativeMetadata = { [key: string]: unknown };

export interface DashboardSummaryDto {
  descriptor: DashboardSummaryDtoDescriptor;
  asOfDate: string;
  state: DashboardSummaryDtoState;
  /** @nullable */
  sourceAsOf: string | null;
  partialData: boolean;
  /** Authoritative source book, lineage, freshness, publication, restatement, and calculation metadata for financial dashboards. */
  authoritativeMetadata?: DashboardSummaryDtoAuthoritativeMetadata;
  widgets: DashboardWidgetDataDto[];
}

export type CreateImportJobDtoImportType = typeof CreateImportJobDtoImportType[keyof typeof CreateImportJobDtoImportType];


export const CreateImportJobDtoImportType = {
  INSTRUMENT: 'INSTRUMENT',
  TRANSACTION: 'TRANSACTION',
  MARKET_DATA: 'MARKET_DATA',
  CUSTODY_POSITION: 'CUSTODY_POSITION',
} as const;

export type CreateImportJobDtoRowsItem = { [key: string]: unknown };

export interface CreateImportJobDto {
  importType: CreateImportJobDtoImportType;
  sourceFileName?: string;
  sourceSheetName?: string;
  headers: string[];
  rows: CreateImportJobDtoRowsItem[];
  idempotencyKey?: string;
}

export interface ImportJobSurfaceDto {
  id: string;
  organizationId: string;
  branchId: string;
  sourceFileName?: string;
  sourceSheetName?: string;
  importType: string;
  status: string;
  totalRows: number;
  validRows: number;
  errorRows: number;
  committedRows: number;
  skippedRows: number;
  recoveryAttempts: number;
  idempotencyKey?: string;
  /** SHA-256 checksum of the normalized import content */
  contentChecksum?: string;
  createdBy?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ImportCreateJobResponseDto {
  job: ImportJobSurfaceDto;
  idempotent: boolean;
}

export interface CursorPageInfoDto {
  nextCursor?: string;
  hasNextPage: boolean;
}

export interface ImportJobsPageResponseDto {
  items: ImportJobSurfaceDto[];
  pageInfo: CursorPageInfoDto;
}

export type ImportRowSurfaceDtoRowData = { [key: string]: unknown };

export type ImportRowSurfaceDtoValidationErrors = { [key: string]: unknown };

export type ImportRowSurfaceDtoWarnings = { [key: string]: unknown };

export interface ImportRowSurfaceDto {
  id: string;
  jobId: string;
  rowNumber: number;
  status: string;
  rowData?: ImportRowSurfaceDtoRowData;
  validationErrors?: ImportRowSurfaceDtoValidationErrors;
  warnings?: ImportRowSurfaceDtoWarnings;
  createdEntityId?: string;
  createdEntityType?: string;
  errorCode?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ImportRowsPageResponseDto {
  items: ImportRowSurfaceDto[];
  pageInfo: CursorPageInfoDto;
}

export interface ImportValidationSummaryDto {
  code: string;
  message: string;
  count: number;
}

export interface ImportPreviewResponseDto {
  jobId: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  isValid: boolean;
  errorSummary: ImportValidationSummaryDto[];
}

export interface ImportCreatedEntityDto {
  rowNumber: number;
  entityType: string;
  entityId: string;
}

export interface ImportRowErrorDetailDto {
  rowNumber: number;
  code: string;
  message: string;
}

export interface ImportCommitResponseDto {
  jobId: string;
  committed: number;
  skipped: number;
  errors: number;
  idempotent: boolean;
  createdEntities: ImportCreatedEntityDto[];
  rowErrors: ImportRowErrorDetailDto[];
}

export interface ImportRecoveryResponseDto {
  jobId: string;
  recovered: number;
  failed: number;
  skipped: number;
  rowErrors: ImportRowErrorDetailDto[];
}

export type CreateStatementDtoCashAccountType = typeof CreateStatementDtoCashAccountType[keyof typeof CreateStatementDtoCashAccountType];


export const CreateStatementDtoCashAccountType = {
  CLIENT_OMNIBUS: 'CLIENT_OMNIBUS',
  HOUSE: 'HOUSE',
} as const;

export type CreateStatementDtoData = { [key: string]: unknown };

export interface CreateStatementDto {
  source: string;
  format: string;
  statementDate: string;
  entriesCount: number;
  totalAmount: string;
  externalAccountRef?: string;
  cashAccountType?: CreateStatementDtoCashAccountType;
  data: CreateStatementDtoData;
}

export type ExternalStatementSurfaceDtoData = { [key: string]: unknown };

export interface ExternalStatementSurfaceDto {
  statementId: string;
  branchId: string;
  externalCashAccountId?: string;
  source: string;
  format: string;
  statementDate: string;
  statementDateBs?: string;
  entriesCount: number;
  totalAmount: string;
  currency: string;
  data: ExternalStatementSurfaceDtoData;
  ingestedAt: string;
  ingestedAtBs?: string;
}

export type CreateSuspenseCashCaseDtoDirection = typeof CreateSuspenseCashCaseDtoDirection[keyof typeof CreateSuspenseCashCaseDtoDirection];


export const CreateSuspenseCashCaseDtoDirection = {
  RECEIPT: 'RECEIPT',
  PAYMENT: 'PAYMENT',
} as const;

export interface CreateSuspenseCashCaseDto {
  externalStatementId?: string;
  externalReference: string;
  direction: CreateSuspenseCashCaseDtoDirection;
  currency: string;
  amount: string;
  valueDate: string;
  narrative: string;
  candidateClientAccountIds?: string[];
}

export type RequestSuspenseCashResolutionDtoResolutionType = typeof RequestSuspenseCashResolutionDtoResolutionType[keyof typeof RequestSuspenseCashResolutionDtoResolutionType];


export const RequestSuspenseCashResolutionDtoResolutionType = {
  ALLOCATE: 'ALLOCATE',
  REFUND: 'REFUND',
} as const;

export interface RequestSuspenseCashResolutionDto {
  resolutionType: RequestSuspenseCashResolutionDtoResolutionType;
  expectedVersion: number;
  reason: string;
  evidence: string[];
  targetPortfolioAccountId?: string;
  cashLedgerAccountId: string;
  suspenseLedgerAccountId: string;
}

export interface SuspenseCashTransitionDto {
  expectedVersion: number;
  reason?: string;
  monetaryApprovalDecisionId?: string;
}

export type StartReconciliationDtoInternalLinesItem = { [key: string]: unknown };

export type StartReconciliationDtoFuzzyConfig = { [key: string]: unknown };

export interface StartReconciliationDto {
  reconciliationDate: string;
  runType: string;
  sources: string[];
  internalLines: StartReconciliationDtoInternalLinesItem[];
  externalStatementIds: string[];
  fuzzyConfig: StartReconciliationDtoFuzzyConfig;
}

export interface RunFinancialControlDto {
  cutoffDate: string;
}

export interface CreateFinancialControlRepairDto {
  evidenceReference: string;
  idempotencyKey: string;
}

export interface FinancialControlRepairDecisionDto {
  expectedVersion: number;
  comment?: string;
}

export type ReconciliationRunSurfaceDtoSources = { [key: string]: unknown };

export type ReconciliationRunSurfaceDtoSummary = { [key: string]: unknown };

export type ReconciliationRunSurfaceDtoAudit = { [key: string]: unknown };

export type ReconciliationRunSurfaceDtoLineage = { [key: string]: unknown };

export interface ReconciliationRunSurfaceDto {
  runId: string;
  branchId: string;
  reconciliationDate: string;
  reconciliationDateBs: string;
  runType: string;
  status: string;
  sources: ReconciliationRunSurfaceDtoSources;
  summary?: ReconciliationRunSurfaceDtoSummary;
  totalAccounts?: number;
  matchedAccounts?: number;
  breaksFound: number;
  segregationRatio?: string;
  startedAt: string;
  startedAtBs: string;
  completedAt?: string;
  completedAtBs?: string;
  errorDetails?: string;
  audit?: ReconciliationRunSurfaceDtoAudit;
  lineage?: ReconciliationRunSurfaceDtoLineage;
}

export interface ReconciliationBreakSurfaceDto {
  breakId: string;
  runId: string;
  branchId: string;
  clientAccount: string;
  internalBalance: string;
  externalBalance: string;
  difference: string;
  differencePct: string;
  currency: string;
  severity: string;
  status: string;
  sourceMismatch: string;
  agingBusinessDays: number;
  escalationTier: string;
  resolvedBy?: string;
  approvedBy?: string;
  detectedAt: string;
  detectedAtBs: string;
  resolvedAt?: string;
  resolvedAtBs?: string;
}

export interface BreakStatusUpdateDto {
  status: string;
}

export interface CreateAdjustmentDto {
  runId: string;
  debitAccount: string;
  creditAccount: string;
  amount: string;
  narrative: string;
  evidenceFiles: string[];
}

export interface ReconciliationAdjustmentSurfaceDto {
  adjustmentId: string;
  breakId: string;
  runId: string;
  branchId: string;
  debitAccount: string;
  creditAccount: string;
  amount: string;
  currency: string;
  narrative: string;
  status: string;
  makerId: string;
  makerAt: string;
}

export interface AdjustmentCommentDto {
  comment: string;
}

export type CreateReportDefinitionDtoReportType = typeof CreateReportDefinitionDtoReportType[keyof typeof CreateReportDefinitionDtoReportType];


export const CreateReportDefinitionDtoReportType = {
  SEBON_MONTHLY: 'SEBON_MONTHLY',
  IRD_CGT: 'IRD_CGT',
  NRB_QUARTERLY: 'NRB_QUARTERLY',
  FIU_AML_CTF: 'FIU_AML_CTF',
} as const;

export type CreateReportDefinitionDtoConfiguration = { [key: string]: unknown };

export interface CreateReportDefinitionDto {
  reportCode: string;
  reportName: string;
  reportType: CreateReportDefinitionDtoReportType;
  regulatoryBody?: string;
  configuration: CreateReportDefinitionDtoConfiguration;
}

export interface ReportDefinitionSurfaceDto {
  id: string;
  reportCode: string;
  reportName: string;
  reportType: string;
  regulatoryBody?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type GenerateReportDtoParameters = { [key: string]: unknown };

export interface GenerateReportDto {
  reportDefinitionId: string;
  parameters: GenerateReportDtoParameters;
  /** Completed report run superseded by this controlled restatement */
  restatementOfRunId?: string;
  /**
     * Controlled reason for restating a prior report run
     * @maxLength 500
     */
  restatementReason?: string;
}

export type ReportRunSurfaceDtoExecutionState = typeof ReportRunSurfaceDtoExecutionState[keyof typeof ReportRunSurfaceDtoExecutionState];


export const ReportRunSurfaceDtoExecutionState = {
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;

export type ReportRunSurfaceDtoParameters = { [key: string]: unknown };

export type ReportRunSurfaceDtoLineage = { [key: string]: unknown };

export type ReportRunSurfaceDtoValidationOutcome = { [key: string]: unknown };

export type ReportRunSurfaceDtoGovernancePolicy = { [key: string]: unknown };

export interface ReportRunSurfaceDto {
  id: string;
  reportDefinitionId: string;
  reportingPeriodStart?: string;
  reportingPeriodEnd?: string;
  status: string;
  executionState?: ReportRunSurfaceDtoExecutionState;
  /**
     * @minimum 0
     * @maximum 100
     */
  progressPercent: number;
  submittedAt?: string;
  acknowledgedAt?: string;
  startedAt?: string;
  completedAt?: string;
  /** @minimum 1 */
  generationVersion?: number;
  provenance?: string;
  parameters?: ReportRunSurfaceDtoParameters;
  lineage?: ReportRunSurfaceDtoLineage;
  validationOutcome?: ReportRunSurfaceDtoValidationOutcome;
  failureCode?: string;
  governancePolicy?: ReportRunSurfaceDtoGovernancePolicy;
  createdAt: string;
  updatedAt: string;
}

export type ReportArtifactSurfaceDtoClassification = typeof ReportArtifactSurfaceDtoClassification[keyof typeof ReportArtifactSurfaceDtoClassification];


export const ReportArtifactSurfaceDtoClassification = {
  CLIENT_VISIBLE: 'CLIENT_VISIBLE',
  FINANCIAL: 'FINANCIAL',
  REGULATORY_RESTRICTED: 'REGULATORY_RESTRICTED',
  INTERNAL_CONTROL: 'INTERNAL_CONTROL',
} as const;

export type ReportArtifactSurfaceDtoDownloadPolicy = { [key: string]: unknown };

export type ReportArtifactSurfaceDtoContentData = { [key: string]: unknown };

export interface ReportArtifactSurfaceDto {
  id: string;
  reportRunId: string;
  outputType: string;
  /** SHA-256 of canonical artifact content */
  contentHash: string;
  /** @minimum 0 */
  contentSizeBytes: number;
  classification: ReportArtifactSurfaceDtoClassification;
  retentionUntil: string;
  downloadPolicy: ReportArtifactSurfaceDtoDownloadPolicy;
  contentAvailable: boolean;
  contentData?: ReportArtifactSurfaceDtoContentData;
  createdAt: string;
  updatedAt: string;
}

export type ExportReportDtoFormat = typeof ExportReportDtoFormat[keyof typeof ExportReportDtoFormat];


export const ExportReportDtoFormat = {
  csv: 'csv',
  json: 'json',
} as const;

export interface ExportReportDto {
  format: ExportReportDtoFormat;
}

export type ReportValidationResultDtoChecksItem = { [key: string]: unknown };

export interface ReportValidationResultDto {
  runId: string;
  passed: boolean;
  checks: ReportValidationResultDtoChecksItem[];
  errors: string[];
}

export interface ReportSubmissionResultDto {
  runId: string;
  status: string;
  submittedAt: string;
  submittedAtBs?: string;
  reference: string;
}

export interface ReportRunLifecycleCommandDto {
  /**
     * Current report-run generation version.
     * @minimum 1
     */
  expectedVersion: number;
}

export type RecordTaxRemittanceDtoTaxType = typeof RecordTaxRemittanceDtoTaxType[keyof typeof RecordTaxRemittanceDtoTaxType];


export const RecordTaxRemittanceDtoTaxType = {
  CGT: 'CGT',
} as const;

export interface RecordTaxRemittanceDto {
  taxType: RecordTaxRemittanceDtoTaxType;
  paymentAmount: string;
  /** Signed liability adjustment. */
  adjustmentAmount?: string;
  paymentReference: string;
  paymentDate: string;
  currency?: string;
  /** Posted accounting journal supporting this payment. */
  journalEntryId: string;
  /** @minimum 0 */
  expectedVersion: number;
}

export interface ApiResponseMeta {
  timestamp: string;
  requestId: string;
  correlationId?: string;
}

export type ErrorResponseErrorMessageParams = { [key: string]: unknown };

export type ErrorResponseErrorDetailsAalRequired = typeof ErrorResponseErrorDetailsAalRequired[keyof typeof ErrorResponseErrorDetailsAalRequired];


export const ErrorResponseErrorDetailsAalRequired = {
  AAL1: 'AAL1',
  AAL2: 'AAL2',
  AAL3: 'AAL3',
} as const;

export type ErrorResponseErrorDetailsFieldErrorsItemMessageParams = { [key: string]: unknown };

export type ErrorResponseErrorDetailsFieldErrorsItem = {
  path: string;
  code: string;
  messageKey?: string;
  message?: string;
  messageParams?: ErrorResponseErrorDetailsFieldErrorsItemMessageParams;
};

export type ErrorResponseErrorDetailsImportRowErrorsItem = {
  row: number;
  messageKey: string;
};

export type ErrorResponseErrorDetailsBatchFailuresItem = {
  id?: string;
  messageKey?: string;
};

export type ErrorResponseErrorDetails = {
  retryable?: boolean;
  retryAfter?: number;
  currentVersion?: string;
  deniedOperation?: string;
  deniedScope?: string;
  mfaRequired?: boolean;
  aalRequired?: ErrorResponseErrorDetailsAalRequired;
  fieldErrors?: ErrorResponseErrorDetailsFieldErrorsItem[];
  importRowErrors?: ErrorResponseErrorDetailsImportRowErrorsItem[];
  batchFailures?: ErrorResponseErrorDetailsBatchFailuresItem[];
};

export type ErrorResponseError = {
  code: string;
  messageKey: string;
  message?: string;
  messageParams?: ErrorResponseErrorMessageParams;
  details?: ErrorResponseErrorDetails;
};

export interface ErrorResponse {
  error: ErrorResponseError;
  meta: ApiResponseMeta;
}

export type ValidationErrorResponseErrorMessageParams = { [key: string]: unknown };

export type ValidationErrorResponseErrorDetailsAalRequired = typeof ValidationErrorResponseErrorDetailsAalRequired[keyof typeof ValidationErrorResponseErrorDetailsAalRequired];


export const ValidationErrorResponseErrorDetailsAalRequired = {
  AAL1: 'AAL1',
  AAL2: 'AAL2',
  AAL3: 'AAL3',
} as const;

export type ValidationErrorResponseErrorDetailsFieldErrorsItemMessageParams = { [key: string]: unknown };

export type ValidationErrorResponseErrorDetailsFieldErrorsItem = {
  path: string;
  code: string;
  messageKey?: string;
  message?: string;
  messageParams?: ValidationErrorResponseErrorDetailsFieldErrorsItemMessageParams;
};

export type ValidationErrorResponseErrorDetailsImportRowErrorsItem = {
  row: number;
  messageKey: string;
};

export type ValidationErrorResponseErrorDetailsBatchFailuresItem = {
  id?: string;
  messageKey?: string;
};

export type ValidationErrorResponseErrorDetails = {
  retryable?: boolean;
  retryAfter?: number;
  currentVersion?: string;
  deniedOperation?: string;
  deniedScope?: string;
  mfaRequired?: boolean;
  aalRequired?: ValidationErrorResponseErrorDetailsAalRequired;
  fieldErrors?: ValidationErrorResponseErrorDetailsFieldErrorsItem[];
  importRowErrors?: ValidationErrorResponseErrorDetailsImportRowErrorsItem[];
  batchFailures?: ValidationErrorResponseErrorDetailsBatchFailuresItem[];
};

export type ValidationErrorResponseError = {
  code: string;
  messageKey: string;
  message?: string;
  messageParams?: ValidationErrorResponseErrorMessageParams;
  details?: ValidationErrorResponseErrorDetails;
};

export interface ValidationErrorResponse {
  error: ValidationErrorResponseError;
  meta: ApiResponseMeta;
}

export type UnauthorizedErrorResponseErrorMessageParams = { [key: string]: unknown };

export type UnauthorizedErrorResponseErrorDetailsAalRequired = typeof UnauthorizedErrorResponseErrorDetailsAalRequired[keyof typeof UnauthorizedErrorResponseErrorDetailsAalRequired];


export const UnauthorizedErrorResponseErrorDetailsAalRequired = {
  AAL1: 'AAL1',
  AAL2: 'AAL2',
  AAL3: 'AAL3',
} as const;

export type UnauthorizedErrorResponseErrorDetailsFieldErrorsItemMessageParams = { [key: string]: unknown };

export type UnauthorizedErrorResponseErrorDetailsFieldErrorsItem = {
  path: string;
  code: string;
  messageKey?: string;
  message?: string;
  messageParams?: UnauthorizedErrorResponseErrorDetailsFieldErrorsItemMessageParams;
};

export type UnauthorizedErrorResponseErrorDetailsImportRowErrorsItem = {
  row: number;
  messageKey: string;
};

export type UnauthorizedErrorResponseErrorDetailsBatchFailuresItem = {
  id?: string;
  messageKey?: string;
};

export type UnauthorizedErrorResponseErrorDetails = {
  retryable?: boolean;
  retryAfter?: number;
  currentVersion?: string;
  deniedOperation?: string;
  deniedScope?: string;
  mfaRequired?: boolean;
  aalRequired?: UnauthorizedErrorResponseErrorDetailsAalRequired;
  fieldErrors?: UnauthorizedErrorResponseErrorDetailsFieldErrorsItem[];
  importRowErrors?: UnauthorizedErrorResponseErrorDetailsImportRowErrorsItem[];
  batchFailures?: UnauthorizedErrorResponseErrorDetailsBatchFailuresItem[];
};

export type UnauthorizedErrorResponseError = {
  code: string;
  messageKey: string;
  message?: string;
  messageParams?: UnauthorizedErrorResponseErrorMessageParams;
  details?: UnauthorizedErrorResponseErrorDetails;
};

export interface UnauthorizedErrorResponse {
  error: UnauthorizedErrorResponseError;
  meta: ApiResponseMeta;
}

export type ForbiddenErrorResponseErrorMessageParams = { [key: string]: unknown };

export type ForbiddenErrorResponseErrorDetailsAalRequired = typeof ForbiddenErrorResponseErrorDetailsAalRequired[keyof typeof ForbiddenErrorResponseErrorDetailsAalRequired];


export const ForbiddenErrorResponseErrorDetailsAalRequired = {
  AAL1: 'AAL1',
  AAL2: 'AAL2',
  AAL3: 'AAL3',
} as const;

export type ForbiddenErrorResponseErrorDetailsFieldErrorsItemMessageParams = { [key: string]: unknown };

export type ForbiddenErrorResponseErrorDetailsFieldErrorsItem = {
  path: string;
  code: string;
  messageKey?: string;
  message?: string;
  messageParams?: ForbiddenErrorResponseErrorDetailsFieldErrorsItemMessageParams;
};

export type ForbiddenErrorResponseErrorDetailsImportRowErrorsItem = {
  row: number;
  messageKey: string;
};

export type ForbiddenErrorResponseErrorDetailsBatchFailuresItem = {
  id?: string;
  messageKey?: string;
};

export type ForbiddenErrorResponseErrorDetails = {
  retryable?: boolean;
  retryAfter?: number;
  currentVersion?: string;
  deniedOperation?: string;
  deniedScope?: string;
  mfaRequired?: boolean;
  aalRequired?: ForbiddenErrorResponseErrorDetailsAalRequired;
  fieldErrors?: ForbiddenErrorResponseErrorDetailsFieldErrorsItem[];
  importRowErrors?: ForbiddenErrorResponseErrorDetailsImportRowErrorsItem[];
  batchFailures?: ForbiddenErrorResponseErrorDetailsBatchFailuresItem[];
};

export type ForbiddenErrorResponseError = {
  code: string;
  messageKey: string;
  message?: string;
  messageParams?: ForbiddenErrorResponseErrorMessageParams;
  details?: ForbiddenErrorResponseErrorDetails;
};

export interface ForbiddenErrorResponse {
  error: ForbiddenErrorResponseError;
  meta: ApiResponseMeta;
}

export type NotFoundErrorResponseErrorMessageParams = { [key: string]: unknown };

export type NotFoundErrorResponseErrorDetailsAalRequired = typeof NotFoundErrorResponseErrorDetailsAalRequired[keyof typeof NotFoundErrorResponseErrorDetailsAalRequired];


export const NotFoundErrorResponseErrorDetailsAalRequired = {
  AAL1: 'AAL1',
  AAL2: 'AAL2',
  AAL3: 'AAL3',
} as const;

export type NotFoundErrorResponseErrorDetailsFieldErrorsItemMessageParams = { [key: string]: unknown };

export type NotFoundErrorResponseErrorDetailsFieldErrorsItem = {
  path: string;
  code: string;
  messageKey?: string;
  message?: string;
  messageParams?: NotFoundErrorResponseErrorDetailsFieldErrorsItemMessageParams;
};

export type NotFoundErrorResponseErrorDetailsImportRowErrorsItem = {
  row: number;
  messageKey: string;
};

export type NotFoundErrorResponseErrorDetailsBatchFailuresItem = {
  id?: string;
  messageKey?: string;
};

export type NotFoundErrorResponseErrorDetails = {
  retryable?: boolean;
  retryAfter?: number;
  currentVersion?: string;
  deniedOperation?: string;
  deniedScope?: string;
  mfaRequired?: boolean;
  aalRequired?: NotFoundErrorResponseErrorDetailsAalRequired;
  fieldErrors?: NotFoundErrorResponseErrorDetailsFieldErrorsItem[];
  importRowErrors?: NotFoundErrorResponseErrorDetailsImportRowErrorsItem[];
  batchFailures?: NotFoundErrorResponseErrorDetailsBatchFailuresItem[];
};

export type NotFoundErrorResponseError = {
  code: string;
  messageKey: string;
  message?: string;
  messageParams?: NotFoundErrorResponseErrorMessageParams;
  details?: NotFoundErrorResponseErrorDetails;
};

export interface NotFoundErrorResponse {
  error: NotFoundErrorResponseError;
  meta: ApiResponseMeta;
}

export type ConflictErrorResponseErrorMessageParams = { [key: string]: unknown };

export type ConflictErrorResponseErrorDetailsAalRequired = typeof ConflictErrorResponseErrorDetailsAalRequired[keyof typeof ConflictErrorResponseErrorDetailsAalRequired];


export const ConflictErrorResponseErrorDetailsAalRequired = {
  AAL1: 'AAL1',
  AAL2: 'AAL2',
  AAL3: 'AAL3',
} as const;

export type ConflictErrorResponseErrorDetailsFieldErrorsItemMessageParams = { [key: string]: unknown };

export type ConflictErrorResponseErrorDetailsFieldErrorsItem = {
  path: string;
  code: string;
  messageKey?: string;
  message?: string;
  messageParams?: ConflictErrorResponseErrorDetailsFieldErrorsItemMessageParams;
};

export type ConflictErrorResponseErrorDetailsImportRowErrorsItem = {
  row: number;
  messageKey: string;
};

export type ConflictErrorResponseErrorDetailsBatchFailuresItem = {
  id?: string;
  messageKey?: string;
};

export type ConflictErrorResponseErrorDetails = {
  retryable?: boolean;
  retryAfter?: number;
  currentVersion?: string;
  deniedOperation?: string;
  deniedScope?: string;
  mfaRequired?: boolean;
  aalRequired?: ConflictErrorResponseErrorDetailsAalRequired;
  fieldErrors?: ConflictErrorResponseErrorDetailsFieldErrorsItem[];
  importRowErrors?: ConflictErrorResponseErrorDetailsImportRowErrorsItem[];
  batchFailures?: ConflictErrorResponseErrorDetailsBatchFailuresItem[];
};

export type ConflictErrorResponseError = {
  code: string;
  messageKey: string;
  message?: string;
  messageParams?: ConflictErrorResponseErrorMessageParams;
  details?: ConflictErrorResponseErrorDetails;
};

export interface ConflictErrorResponse {
  error: ConflictErrorResponseError;
  meta: ApiResponseMeta;
}

export type InternalErrorResponseErrorMessageParams = { [key: string]: unknown };

export type InternalErrorResponseErrorDetailsAalRequired = typeof InternalErrorResponseErrorDetailsAalRequired[keyof typeof InternalErrorResponseErrorDetailsAalRequired];


export const InternalErrorResponseErrorDetailsAalRequired = {
  AAL1: 'AAL1',
  AAL2: 'AAL2',
  AAL3: 'AAL3',
} as const;

export type InternalErrorResponseErrorDetailsFieldErrorsItemMessageParams = { [key: string]: unknown };

export type InternalErrorResponseErrorDetailsFieldErrorsItem = {
  path: string;
  code: string;
  messageKey?: string;
  message?: string;
  messageParams?: InternalErrorResponseErrorDetailsFieldErrorsItemMessageParams;
};

export type InternalErrorResponseErrorDetailsImportRowErrorsItem = {
  row: number;
  messageKey: string;
};

export type InternalErrorResponseErrorDetailsBatchFailuresItem = {
  id?: string;
  messageKey?: string;
};

export type InternalErrorResponseErrorDetails = {
  retryable?: boolean;
  retryAfter?: number;
  currentVersion?: string;
  deniedOperation?: string;
  deniedScope?: string;
  mfaRequired?: boolean;
  aalRequired?: InternalErrorResponseErrorDetailsAalRequired;
  fieldErrors?: InternalErrorResponseErrorDetailsFieldErrorsItem[];
  importRowErrors?: InternalErrorResponseErrorDetailsImportRowErrorsItem[];
  batchFailures?: InternalErrorResponseErrorDetailsBatchFailuresItem[];
};

export type InternalErrorResponseError = {
  code: string;
  messageKey: string;
  message?: string;
  messageParams?: InternalErrorResponseErrorMessageParams;
  details?: InternalErrorResponseErrorDetails;
};

export interface InternalErrorResponse {
  error: InternalErrorResponseError;
  meta: ApiResponseMeta;
}

export type AuthLogin201 = {
  data: LoginResponseDto;
  meta: ApiResponseMeta;
};

export type OrganizationUsersCreate201Data = { [key: string]: unknown };

export type OrganizationUsersCreate201 = {
  data: OrganizationUsersCreate201Data;
  meta: ApiResponseMeta;
};

export type AuthInvitationAccept201Data = { [key: string]: unknown };

export type AuthInvitationAccept201 = {
  data: AuthInvitationAccept201Data;
  meta: ApiResponseMeta;
};

export type AuthPasswordChange201 = {
  data: LoginResponseDto;
  meta: ApiResponseMeta;
};

export type AuthChallengeTotp201 = {
  data: LoginResponseDto;
  meta: ApiResponseMeta;
};

export type AuthChallengeRecovery201 = {
  data: LoginResponseDto;
  meta: ApiResponseMeta;
};

export type AuthSelectContext201 = {
  data: LoginResponseDto;
  meta: ApiResponseMeta;
};

export type AuthDelegationEnter201 = {
  data: LoginResponseDto;
  meta: ApiResponseMeta;
};

export type AuthDelegationExit201 = {
  data: LoginResponseDto;
  meta: ApiResponseMeta;
};

export type AuthRefresh200 = {
  data: AccessTokenResponseDto;
  meta: ApiResponseMeta;
};

export type AuthLogout201 = {
  data: SuccessResponseDto;
  meta: ApiResponseMeta;
};

export type AuthReadProfile200 = {
  data: ProfileResponse;
  meta: ApiResponseMeta;
};

export type SettingsProfileUpdate200 = {
  data: ProfileResponse;
  meta: ApiResponseMeta;
};

export type SettingsPreferences200Data = { [key: string]: unknown };

export type SettingsPreferences200 = {
  data: SettingsPreferences200Data;
  meta: ApiResponseMeta;
};

export type SettingsPreferencesUpdate200Data = { [key: string]: unknown };

export type SettingsPreferencesUpdate200 = {
  data: SettingsPreferencesUpdate200Data;
  meta: ApiResponseMeta;
};

export type AuthPasswordChangePost200 = {
  data: ChangePasswordResponseDto;
  meta: ApiResponseMeta;
};

export type AuthSessionsManage200 = {
  data: SessionListResponseDto;
  meta: ApiResponseMeta;
};

export type AuthSessionsRevoke200 = {
  data: SuccessResponseDto;
  meta: ApiResponseMeta;
};

export type AuthSessionsRevokePost200 = {
  data: SuccessResponseDto;
  meta: ApiResponseMeta;
};

export type AuthSwitchContext200 = {
  data: AccessTokenResponseDto;
  meta: ApiResponseMeta;
};

export type AuthMfaEnroll201 = {
  data: BackupCodesResponseDto;
  meta: ApiResponseMeta;
};

export type AuthMfaVerify201 = {
  data: SuccessResponseDto;
  meta: ApiResponseMeta;
};

export type AuthMfaDisable201 = {
  data: SuccessResponseDto;
  meta: ApiResponseMeta;
};

export type AuthMfaBackupCodesRegenerate201 = {
  data: RegeneratedBackupCodesResponseDto;
  meta: ApiResponseMeta;
};

export type AuthPasswordForgot201 = {
  data: SuccessResponseDto;
  meta: ApiResponseMeta;
};

export type AuthPasswordReset201 = {
  data: SuccessResponseDto;
  meta: ApiResponseMeta;
};

export type PlatformOrganizationsList200 = {
  data: PlatformOrganizationViewDto[];
  meta: ApiResponseMeta;
};

export type PlatformOrganizationsRead200 = {
  data: PlatformOrganizationViewDto;
  meta: ApiResponseMeta;
};

export type PlatformLicenseList200 = {
  data: ModuleLicenseSurfaceDto[];
  meta: ApiResponseMeta;
};

export type PlatformModulesRead200 = {
  data: ModuleLicenseSurfaceDto[];
  meta: ApiResponseMeta;
};

export type OrganizationBranchesList200 = {
  data: BranchSurfaceDto[];
  meta: ApiResponseMeta;
};

export type OrganizationBranchesRead200 = {
  data: BranchSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationClientsList200 = {
  data: ClientAccountListSurfaceDto[];
  meta: ApiResponseMeta;
};

export type OrganizationClientsRead200 = {
  data: ClientAccountSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationClientsReadGet200 = {
  data: ClientSuitabilityProfileSurfaceDto[];
  meta: ApiResponseMeta;
};

export type OrganizationClientAccountsList200 = {
  data: OrganizationClientCustodyAccountListSurfaceDto[];
  meta: ApiResponseMeta;
};

export type OrganizationClientAccountsRead200 = {
  data: OrganizationClientCustodyAccountSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationClientAccountsDocumentsList200 = {
  data: OrganizationClientCustodyAccountDocumentSurfaceDto[];
  meta: ApiResponseMeta;
};

export type OrganizationClientAccountsDocumentsCreate201 = {
  data: OrganizationClientCustodyAccountDocumentSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationClientAccountsSignatoriesList200 = {
  data: OrganizationClientCustodyAccountSignatorySurfaceDto[];
  meta: ApiResponseMeta;
};

export type OrganizationClientAccountsSignatoriesCreate201 = {
  data: OrganizationClientCustodyAccountSignatorySurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationPortfoliosList200 = {
  data: OrganizationManagedPortfolioSurfaceDto[];
  meta: ApiResponseMeta;
};

export type OrganizationBenchmarkAssignmentsList200 = {
  data: OrganizationManagedPortfolioSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationPortfoliosRead200 = {
  data: OrganizationManagedPortfolioMandateSurfaceDto[];
  meta: ApiResponseMeta;
};

export type PlatformClientAccountList200 = {
  data: ClientAccountListSurfaceDto[];
  meta: ApiResponseMeta;
};

export type PlatformClientAccountGetGet200 = {
  data: ClientAccountSurfaceDto;
  meta: ApiResponseMeta;
};

export type PlatformClientAccountDocumentsList200 = {
  data: ClientAccountDocumentSurfaceDto[];
  meta: ApiResponseMeta;
};

export type OrganizationClientAccountsDocumentsRemoveDelete200 = {
  data: ClientAccountDocumentSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentPortfolioAccountList200 = {
  data: PortfolioAccountSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentPortfolioAccountGet200 = {
  data: PortfolioAccountSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentPortfolioAccountGetBalance200 = {
  data: PortfolioAccountBalanceSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentPortfolioAccountSignatoriesList200 = {
  data: PortfolioAccountSignatorySurfaceDto[];
  meta: ApiResponseMeta;
};

export type NavigationRoutesReadGet200 = {
  data: RouteAccessDecisionDto[];
  meta: ApiResponseMeta;
};

export type NavigationRouteReadGet7200 = {
  data: RouteAccessDecisionDto;
  meta: ApiResponseMeta;
};

export type OrganizationAuditListParams = {
limit?: string;
page?: string;
search?: string;
dateTo?: string;
dateFrom?: string;
action?: string;
entityType?: string;
};

export type OrganizationAuditList200 = {
  data: AuditLogListDto;
  meta: ApiResponseMeta;
};

export type OrganizationAuditExportParams = {
search?: string;
dateTo?: string;
dateFrom?: string;
action?: string;
entityType?: string;
};

export type OrganizationAuditExport200 = {
  data: string;
  meta: ApiResponseMeta;
};

export type OrganizationAuditRead200 = {
  data: AuditLogListDto;
  meta: ApiResponseMeta;
};

export type OrganizationUsersCreatePost201 = {
  data: UserSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationUsersRead200 = {
  data: UserSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationUsersUpdate200 = {
  data: UserSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationUsersDisable200Data = { [key: string]: unknown };

export type OrganizationUsersDisable200 = {
  data: OrganizationUsersDisable200Data;
  meta: ApiResponseMeta;
};

export type OrganizationPermissionsList200 = {
  data: PermissionCatalogDto[];
  meta: ApiResponseMeta;
};

export type OrganizationUsersUnlock200Data = { [key: string]: unknown };

export type OrganizationUsersUnlock200 = {
  data: OrganizationUsersUnlock200Data;
  meta: ApiResponseMeta;
};

export type OrganizationUsersForcePasswordReset200Data = { [key: string]: unknown };

export type OrganizationUsersForcePasswordReset200 = {
  data: OrganizationUsersForcePasswordReset200Data;
  meta: ApiResponseMeta;
};

export type OrganizationUsersMfaRequireEnrollment200Data = { [key: string]: unknown };

export type OrganizationUsersMfaRequireEnrollment200 = {
  data: OrganizationUsersMfaRequireEnrollment200Data;
  meta: ApiResponseMeta;
};

export type OrganizationUsersMfaResetEnrollment200Data = { [key: string]: unknown };

export type OrganizationUsersMfaResetEnrollment200 = {
  data: OrganizationUsersMfaResetEnrollment200Data;
  meta: ApiResponseMeta;
};

export type OrganizationUsersMfaDisable200Data = { [key: string]: unknown };

export type OrganizationUsersMfaDisable200 = {
  data: OrganizationUsersMfaDisable200Data;
  meta: ApiResponseMeta;
};

export type OrganizationUsersMfaRegenerateRecovery200Data = { [key: string]: unknown };

export type OrganizationUsersMfaRegenerateRecovery200 = {
  data: OrganizationUsersMfaRegenerateRecovery200Data;
  meta: ApiResponseMeta;
};

export type OrganizationLegalHoldPlace200Data = { [key: string]: unknown };

export type OrganizationLegalHoldPlace200 = {
  data: OrganizationLegalHoldPlace200Data;
  meta: ApiResponseMeta;
};

export type OrganizationLegalHoldList200DataItem = { [key: string]: unknown };

export type OrganizationLegalHoldList200 = {
  data: OrganizationLegalHoldList200DataItem[];
  meta: ApiResponseMeta;
};

export type OrganizationLegalHoldRemove200Data = { [key: string]: unknown };

export type OrganizationLegalHoldRemove200 = {
  data: OrganizationLegalHoldRemove200Data;
  meta: ApiResponseMeta;
};

export type OrganizationAdminDelegationCreate200Data = { [key: string]: unknown };

export type OrganizationAdminDelegationCreate200 = {
  data: OrganizationAdminDelegationCreate200Data;
  meta: ApiResponseMeta;
};

export type OrganizationAdminDelegationList200DataItem = { [key: string]: unknown };

export type OrganizationAdminDelegationList200 = {
  data: OrganizationAdminDelegationList200DataItem[];
  meta: ApiResponseMeta;
};

export type OrganizationAdminDelegationRevoke200Data = { [key: string]: unknown };

export type OrganizationAdminDelegationRevoke200 = {
  data: OrganizationAdminDelegationRevoke200Data;
  meta: ApiResponseMeta;
};

export type OrganizationProfileRead200Data = { [key: string]: unknown };

export type OrganizationProfileRead200 = {
  data: OrganizationProfileRead200Data;
  meta: ApiResponseMeta;
};

export type OrganizationProfileUpdate200Data = { [key: string]: unknown };

export type OrganizationProfileUpdate200 = {
  data: OrganizationProfileUpdate200Data;
  meta: ApiResponseMeta;
};

export type OrganizationUsersListParams = {
limit?: string;
page?: string;
search?: string;
branchId?: string;
status?: string;
};

export type OrganizationUsersList200 = {
  data: UserListSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationRolesListParams = {
limit?: string;
page?: string;
search?: string;
status?: string;
};

export type OrganizationRolesList200 = {
  data: RoleListSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationRolesRead200 = {
  data: RoleSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationRolesUpdate200 = {
  data: RoleSurfaceDto;
  meta: ApiResponseMeta;
};

export type OrganizationRolesDisable200Data = { [key: string]: unknown };

export type OrganizationRolesDisable200 = {
  data: OrganizationRolesDisable200Data;
  meta: ApiResponseMeta;
};

export type OrganizationRolesCreate200 = {
  data: RoleSurfaceDto;
  meta: ApiResponseMeta;
};

export type PlatformAuditExportParams = {
sortDirection?: PlatformAuditExportSortDirection;
sortField?: PlatformAuditExportSortField;
occurredTo?: string;
occurredFrom?: string;
search?: string;
branchId?: string;
organizationId?: string;
action?: string;
entityType?: string;
};

export type PlatformAuditExportSortDirection = typeof PlatformAuditExportSortDirection[keyof typeof PlatformAuditExportSortDirection];


export const PlatformAuditExportSortDirection = {
  asc: 'asc',
  desc: 'desc',
} as const;

export type PlatformAuditExportSortField = typeof PlatformAuditExportSortField[keyof typeof PlatformAuditExportSortField];


export const PlatformAuditExportSortField = {
  createdAt: 'createdAt',
  entityType: 'entityType',
  action: 'action',
} as const;

export type PlatformAuditExport200 = {
  data: string;
  meta: ApiResponseMeta;
};

export type PlatformUsersCreate201Data = { [key: string]: unknown };

export type PlatformUsersCreate201 = {
  data: PlatformUsersCreate201Data;
  meta: ApiResponseMeta;
};

export type InvestmentInstrumentList200 = {
  data: InstrumentSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentInstrumentGet200 = {
  data: InstrumentSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentInstrumentOverrideList200 = {
  data: InstrumentSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentMarketDataList200 = {
  data: MarketDataDto[];
  meta: ApiResponseMeta;
};

export type InvestmentMarketDataListGet200Data = { [key: string]: unknown };

export type InvestmentMarketDataListGet200 = {
  data: InvestmentMarketDataListGet200Data;
  meta: ApiResponseMeta;
};

export type InvestmentMarketDataGet200 = {
  data: MarketDataDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionCreate201 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionList200 = {
  data: TransactionSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentTransactionSubmit200 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionApprovePost200 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionReject200 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionWithdraw200 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionPostPost200 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionReversePost200 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionRepair200 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionRepost200 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionExport200 = {
  data: string;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionGet200 = {
  data: TransactionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentTransactionLots200 = {
  data: TransactionLotSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentTransactionDisposals200 = {
  data: TransactionDisposalSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentTransactionFacts200 = {
  data: TransactionFactsSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentCorporateActionList200 = {
  data: CorporateActionSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentCorporateActionGet200 = {
  data: CorporateActionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentCorporateActionUpdate200 = {
  data: CorporateActionSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentCorporateActionEntitlementsList200 = {
  data: CorporateActionEntitlementSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentCorporateActionExDateClassify200Data = { [key: string]: unknown };

export type InvestmentCorporateActionExDateClassify200 = {
  data: InvestmentCorporateActionExDateClassify200Data;
  meta: ApiResponseMeta;
};

export type InvestmentCorporateActionExDateVerify200Data = { [key: string]: unknown };

export type InvestmentCorporateActionExDateVerify200 = {
  data: InvestmentCorporateActionExDateVerify200Data;
  meta: ApiResponseMeta;
};

export type InvestmentFactPositionParams = {
instrument?: string;
account?: string;
asOfDate?: string;
};

export type InvestmentFactPosition200 = {
  data: PositionFactSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentFactValuationParams = {
instrument?: string;
asOfDate?: string;
};

export type InvestmentFactValuation200 = {
  data: ValuationFactSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentFactMovementParams = {
account?: string;
monthYearKey?: string;
};

export type InvestmentFactMovement200 = {
  data: MovementFactSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentFactRealizedParams = {
instrument?: string;
account?: string;
};

export type InvestmentFactRealized200 = {
  data: RealizedGainLossFactSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentFactCgtParams = {
instrument?: string;
account?: string;
};

export type InvestmentFactCgt200 = {
  data: CgtFactSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentFactNfrsParams = {
instrument?: string;
account?: string;
};

export type InvestmentFactNfrs200 = {
  data: NfrsFactSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentFactCreate201 = {
  data: FactGenerationResultDto;
  meta: ApiResponseMeta;
};

export type InvestmentFactBatchesList200 = {
  data: FactGenerationBatchSurfaceDto[];
  meta: ApiResponseMeta;
};

export type InvestmentFactBatchGet200 = {
  data: FactGenerationBatchSurfaceDto;
  meta: ApiResponseMeta;
};

export type InvestmentFactBatchLineage200 = {
  data: FactBatchLineageDto;
  meta: ApiResponseMeta;
};

export type ReportsView200 = {
  data: ReportCatalogEntryDto[];
  meta: ApiResponseMeta;
};

export type DashboardView200 = {
  data: DashboardSummaryDto;
  meta: ApiResponseMeta;
};

export type ClientDashboardRead200 = {
  data: DashboardSummaryDto;
  meta: ApiResponseMeta;
};

export type PlatformDashboardRead200 = {
  data: DashboardSummaryDto;
  meta: ApiResponseMeta;
};

export type InvestmentPerformanceExport200 = {
  data: string;
  meta: ApiResponseMeta;
};

export type ImportUpload201 = {
  data: ImportCreateJobResponseDto;
  meta: ApiResponseMeta;
};

export type ImportList200 = {
  data: ImportJobsPageResponseDto;
  meta: ApiResponseMeta;
};

export type ImportGet200 = {
  data: ImportJobSurfaceDto;
  meta: ApiResponseMeta;
};

export type ImportRowsList200 = {
  data: ImportRowsPageResponseDto;
  meta: ApiResponseMeta;
};

export type ImportValidate200 = {
  data: ImportPreviewResponseDto;
  meta: ApiResponseMeta;
};

export type ImportCommit200 = {
  data: ImportCommitResponseDto;
  meta: ApiResponseMeta;
};

export type ImportRecover200 = {
  data: ImportRecoveryResponseDto;
  meta: ApiResponseMeta;
};

export type ImportCancel200 = {
  data: ImportJobSurfaceDto;
  meta: ApiResponseMeta;
};

export type ImportReconcile200Data = { [key: string]: unknown };

export type ImportReconcile200 = {
  data: ImportReconcile200Data;
  meta: ApiResponseMeta;
};

export type ReconciliationStatementList200 = {
  data: ExternalStatementSurfaceDto[];
  meta: ApiResponseMeta;
};

export type ReconciliationSuspenseCashList200DataItem = { [key: string]: unknown };

export type ReconciliationSuspenseCashList200 = {
  data: ReconciliationSuspenseCashList200DataItem[];
  meta: ApiResponseMeta;
};

export type ReconciliationStatementGet200 = {
  data: ExternalStatementSurfaceDto;
  meta: ApiResponseMeta;
};

export type ReconciliationRunList200 = {
  data: ReconciliationRunSurfaceDto[];
  meta: ApiResponseMeta;
};

export type ReconciliationRunListGet200DataItem = { [key: string]: unknown };

export type ReconciliationRunListGet200 = {
  data: ReconciliationRunListGet200DataItem[];
  meta: ApiResponseMeta;
};

export type ReconciliationRunGet200Data = { [key: string]: unknown };

export type ReconciliationRunGet200 = {
  data: ReconciliationRunGet200Data;
  meta: ApiResponseMeta;
};

export type ReconciliationRunGetGet200 = {
  data: ReconciliationRunSurfaceDto;
  meta: ApiResponseMeta;
};

export type ReconciliationRunBreaksList200 = {
  data: ReconciliationBreakSurfaceDto[];
  meta: ApiResponseMeta;
};

export type ReconciliationRunMatchesList200 = {
  data: ReconciliationBreakSurfaceDto[];
  meta: ApiResponseMeta;
};

export type ReconciliationBreakGet200 = {
  data: ReconciliationBreakSurfaceDto;
  meta: ApiResponseMeta;
};

export type ReconciliationAdjustmentList200 = {
  data: ReconciliationAdjustmentSurfaceDto[];
  meta: ApiResponseMeta;
};

export type RegulatoryReportDefinitionList200 = {
  data: ReportDefinitionSurfaceDto[];
  meta: ApiResponseMeta;
};

export type RegulatoryReportDefinitionGet200 = {
  data: ReportDefinitionSurfaceDto;
  meta: ApiResponseMeta;
};

export type RegulatoryReportDefinitionUpdate200 = {
  data: ReportDefinitionSurfaceDto;
  meta: ApiResponseMeta;
};

export type RegulatoryReportDefinitionDisable200 = {
  data: ReportDefinitionSurfaceDto;
  meta: ApiResponseMeta;
};

export type RegulatoryReportRunGenerate201 = {
  data: ReportRunSurfaceDto;
  meta: ApiResponseMeta;
};

export type RegulatoryReportRunList200 = {
  data: ReportRunSurfaceDto[];
  meta: ApiResponseMeta;
};

export type RegulatorySubmissionList200 = {
  data: ReportRunSurfaceDto[];
  meta: ApiResponseMeta;
};

export type RegulatoryReportRunGet200 = {
  data: ReportRunSurfaceDto;
  meta: ApiResponseMeta;
};

export type RegulatoryReportRunArtifactGet200 = {
  data: ReportArtifactSurfaceDto;
  meta: ApiResponseMeta;
};

export type RegulatoryReportRunValidate201 = {
  data: ReportValidationResultDto;
  meta: ApiResponseMeta;
};

export type RegulatoryReportRunSubmit201 = {
  data: ReportSubmissionResultDto;
  meta: ApiResponseMeta;
};

export type RegulatorySubmissionAcknowledge200 = {
  data: ReportRunSurfaceDto;
  meta: ApiResponseMeta;
};

export type RegulatoryReportRunCancel200 = {
  data: ReportRunSurfaceDto;
  meta: ApiResponseMeta;
};

export type RegulatoryReportRunRetry201 = {
  data: ReportRunSurfaceDto;
  meta: ApiResponseMeta;
};

export type RegulatoryTaxRemittanceList200DataItem = { [key: string]: unknown };

export type RegulatoryTaxRemittanceList200 = {
  data: RegulatoryTaxRemittanceList200DataItem[];
  meta: ApiResponseMeta;
};

export type RegulatoryTaxRemittanceRecord201Data = { [key: string]: unknown };

export type RegulatoryTaxRemittanceRecord201 = {
  data: RegulatoryTaxRemittanceRecord201Data;
  meta: ApiResponseMeta;
};

export type AccountingGovernanceRecordBodyRestatement = {
  supersedesResultId: unknown;
  restatementReason: unknown;
};

export type AccountingGovernanceRecordBody = {
  subjectType: string;
  subjectId: string;
  eventTime: string;
  assetClass: string;
  classification: string;
  measurementBasis: string;
  /** @maxItems 128 */
  judgmentEvidenceIds: string[];
  preparedBy: string;
  restatement?: AccountingGovernanceRecordBodyRestatement;
};

export type AccountingGovernanceRecordPostBodyRestatement = {
  supersedesResultId: unknown;
  restatementReason: unknown;
};

export type AccountingGovernanceRecordPostBody = {
  subjectType: string;
  subjectId: string;
  modelId: string;
  modelVersion: string;
  /** @maxItems 128 */
  inputEvidenceIds: string[];
  riskStageBefore: string;
  riskStageAfter: string;
  completenessCheckId: string;
  journalEntryId: string;
  exposure: string;
  lossRate: string;
  eventTime: string;
  preparedBy: string;
  restatement?: AccountingGovernanceRecordPostBodyRestatement;
};

export type AccountingGovernanceRecordPost1BodyRestatement = {
  supersedesResultId: unknown;
  restatementReason: unknown;
};

export type AccountingGovernanceRecordPost1Body = {
  subjectType: string;
  subjectId: string;
  hierarchy: string;
  /** @maxItems 128 */
  observableInputEvidenceIds: string[];
  /** @maxItems 128 */
  unobservableInputEvidenceIds: string[];
  uncertaintyDisclosure: string;
  eventTime: string;
  preparedBy: string;
  restatement?: AccountingGovernanceRecordPost1BodyRestatement;
};

export type AccountingGovernanceRecordPost2BodyRestatement = {
  supersedesResultId: unknown;
  restatementReason: unknown;
};

export type AccountingGovernanceRecordPost2Body = {
  subjectType: string;
  subjectId: string;
  disclosureVersion: string;
  periodEnd: string;
  /** @maxItems 128 */
  evidenceIds: string[];
  preparedBy: string;
  restatement?: AccountingGovernanceRecordPost2BodyRestatement;
};

export type ComplianceGovernanceRecordBody = {
  subjectId: string;
  relatedPartyId: string;
  relationshipType: string;
  /** @maxItems 128 */
  sourceIds: string[];
  declaredAt: string;
};

export type ComplianceGovernanceApproveBody = {
  actorId: string;
  conflictId: string;
  recusedAt: string;
  /** @maxItems 128 */
  reasonSourceIds: string[];
  declarationId: string;
  businessEntityType: string;
  businessEntityId: string;
  decision: string;
  reason: string;
  materialityAmount?: string;
  currency?: string;
  supersedesDecisionId?: string;
};

export type ComplianceGovernanceRecordPostBody = {
  listSourceId: string;
  snapshotHash: string;
  retrievedAt: string;
};

export type ComplianceGovernanceApprovePostBody = {
  snapshotId: string;
  subjectId: string;
  disposition: string;
  /** @maxItems 128 */
  evidenceIds: string[];
  decidedAt: string;
  subjectType: string;
  alertType: string;
  explanation: string;
  supersedesDecisionId?: string;
};

export type ComplianceGovernanceRecordPost1BodyReport = {
  reportType: unknown;
  caseId: unknown;
  retentionSourceIds: unknown;
  correctionOfReportId?: unknown;
};

export type ComplianceGovernanceRecordPost1Body = {
  report: ComplianceGovernanceRecordPost1BodyReport;
};

export type ComplianceGovernanceApprovePatch1Body = {
  expectedVersion: number;
  action: string;
  reference?: string;
};

export type ClientsGovernanceRecordBody = {
  agreementId: string;
  clientId: string;
  templateId: string;
  version: string;
  effectiveFrom: string;
  /** @maxItems 128 */
  sourceIds: string[];
  portfolioAccountId?: string;
  expiresAt?: string;
  supersedesAgreementId?: string;
};

export type ClientsGovernanceRecordPostBody = {
  agreementId: string;
  deliveryId: string;
  deliveredAt: string;
  artifactHash: string;
  expectedVersion: number;
};

export type ClientsGovernanceRecordPost1Body = {
  agreementId: string;
  decision: string;
  decidedAt: string;
  /** @maxItems 128 */
  requiredConsentIds: string[];
  /** @maxItems 128 */
  optionalConsentIds: string[];
  decidedBy: string;
  expectedVersion: number;
  expiresAt?: string;
};

export type ClientsGovernanceRecordPost2BodyNewAgreement = {
  agreementId: unknown;
  clientId: unknown;
  templateId: unknown;
  version: unknown;
  effectiveFrom: unknown;
  sourceIds: unknown;
  portfolioAccountId?: unknown;
  expiresAt?: unknown;
  supersedesAgreementId?: unknown;
};

export type ClientsGovernanceRecordPost2Body = {
  agreementId: string;
  supersedesVersion: string;
  effectiveFrom: string;
  /** @maxItems 128 */
  withdrawnConsentIds: string[];
  expectedVersion: number;
  newAgreement: ClientsGovernanceRecordPost2BodyNewAgreement;
};

export type ClientsGovernanceRecordPost3BodyRestatement = {
  supersedesRunId: unknown;
  reason: unknown;
};

export type ClientsGovernanceRecordPost3Body = {
  runId: string;
  statementVersion: string;
  artifactHash: string;
  inputFingerprint: string;
  reportRunId: string;
  clientId: string;
  periodStart: string;
  periodEnd: string;
  portfolioAccountId?: string;
  restatement?: ClientsGovernanceRecordPost3BodyRestatement;
};

export type ClientsGovernanceRecordPost4BodyPeriodsItem = { [key: string]: unknown };

export type ClientsGovernanceRecordPost4Body = {
  scheduleId: string;
  scheduleKey: string;
  expectedLatestVersion: number;
  clientId: string;
  portfolioAccountId?: string;
  generationVersion: number;
  /**
     * @minItems 1
     * @maxItems 120
     */
  periods: ClientsGovernanceRecordPost4BodyPeriodsItem[];
};

export type ClientsGovernanceRecordPost5Body = {
  expectedRevision: number;
};

export type ClientsGovernanceRecordPost6Body = {
  expectedRevision: number;
  reason: string;
};

export type ClientsGovernanceRecordPost7Body = {
  reportRunId: string;
  clientId: string;
  portfolioAccountId?: string;
  periodStart: string;
  periodEnd: string;
  cutoffAt: string;
  generationVersion: number;
};

export type ClientsGovernanceRecordPost8Body = {
  statementRunId: string;
  attemptId: string;
  status: string;
  attemptedAt: string;
  artifactHash: string;
  channel: string;
  remediationId?: string;
  reasonCode?: string;
};

export type ClientsGovernanceRecordPost9Body = {
  complaintId: string;
  classification: string;
  acknowledgedAt: string;
  ownerId: string;
  slaPolicyId: string;
  /** @maxItems 128 */
  evidenceIds: string[];
  clientId: string;
};

export type ClientsGovernanceRecordPost10BodyPayload = { [key: string]: unknown };

export type ClientsGovernanceRecordPost10Body = {
  type: string;
  payload: ClientsGovernanceRecordPost10BodyPayload;
  expectedVersion: number;
};

export type PrivacyRightsManageBody = {
  requestId: string;
  requestType: string;
  requesterUserIdentityId: string;
  subjectUserIdentityId: string;
  receivedAt: string;
  identityEvidenceHash: string;
  identityEvidenceType: string;
  /** @maxItems 128 */
  requestedCategoryIds: string[];
  idempotencyKey: string;
};

export type PrivacyRightsManagePostBody = {
  requestId: string;
  decision: string;
  decidedAt: string;
  reason: string;
  /** @maxItems 128 */
  retainedCategoryIds: string[];
  /** @maxItems 128 */
  legalHoldIds: string[];
  /** @maxItems 128 */
  correctionLineageIds: string[];
  expectedVersion: number;
  /** @maxItems 128 */
  optionalPurposeIds?: string[];
};

export type IntegrationsGovernanceManageBodyCredential = {
  fingerprint: unknown;
  algorithm: unknown;
  verificationKey: unknown;
  validFrom: unknown;
};

export type IntegrationsGovernanceManageBody = {
  connectionCode: string;
  providerType: string;
  credential: IntegrationsGovernanceManageBodyCredential;
};

export type IntegrationsGovernanceManagePostBodyCredential = {
  fingerprint: unknown;
  algorithm: unknown;
  verificationKey: unknown;
  validFrom: unknown;
};

export type IntegrationsGovernanceManagePostBody = {
  expectedVersion: number;
  credential: IntegrationsGovernanceManagePostBodyCredential;
};

export type IntegrationsGovernanceManagePost1Body = {
  expectedVersion: number;
};

export type IntegrationsGovernanceManagePost2Body = {
  connectionId: string;
  messageId: string;
  streamId: string;
  sequence: string;
  occurredAt: string;
  keyFingerprint: string;
  signatureAlgorithm: string;
  payloadHash: string;
  signature: string;
};

export type IntegrationsGovernanceManagePost3Body = {
  committedEntityType: string;
  committedEntityId: string;
};

export type MonetaryApprovalManageBody = {
  userIdentityId: string;
  tierId: string;
  /** @maxItems 128 */
  actionIds: string[];
  maximumBaseAmount: string;
  effectiveFrom: string;
  reason: string;
  effectiveTo?: string;
  delegatedFromId?: string;
};

export type MonetaryApprovalManagePostBody = {
  expectedVersion: number;
};

export type MonetaryApprovalManagePost1Body = {
  expectedVersion: number;
  reason: string;
};

export type MonetaryApprovalDecideBody = {
  actionId: string;
  businessIdentity: string;
  amount: string;
  currency: string;
  baseAmount: string;
  fxObservationId: string;
  fxObservedAt: string;
  actorId: string;
  /** @maxItems 128 */
  relatedBusinessIdentityIds: string[];
  eventTime: string;
};

export type RiskAnalyticsRunBody = {
  portfolioAccountId: string;
  cutoffAt: string;
  supersedesRunId?: string;
  restatementReason?: string;
};

export type RiskAnalyticsResolveBody = {
  expectedVersion: number;
  action: string;
  reason: string;
  resolutionRunId?: string;
};

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type authLoginResponse201 = {
  data: AuthLogin201
  status: 201
}

export type authLoginResponseSuccess = (authLoginResponse201) & {
  headers: Headers;
};
;

export type authLoginResponse = (authLoginResponseSuccess)

export const getAuthLoginUrl = () => {




  return `/api/auth/login`
}

export const authLogin = async (loginDto: LoginDto, options?: RequestInit): Promise<authLoginResponse> => {

  return customMutator<authLoginResponse>(getAuthLoginUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(loginDto)
  }
);}





export const getAuthLoginMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLogin>>, TError,{data: LoginDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authLogin>>, TError,{data: LoginDto}, TContext> => {

const mutationKey = ['authLogin'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authLogin>>, {data: LoginDto}> = (props) => {
          const {data} = props ?? {};

          return  authLogin(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthLoginMutationResult = NonNullable<Awaited<ReturnType<typeof authLogin>>>
    export type AuthLoginMutationBody = LoginDto
    export type AuthLoginMutationError = unknown

    export const useAuthLogin = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLogin>>, TError,{data: LoginDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authLogin>>,
        TError,
        {data: LoginDto},
        TContext
      > => {
      return useMutation(getAuthLoginMutationOptions(options), queryClient);
    }

export type organizationUsersCreateResponse201 = {
  data: OrganizationUsersCreate201
  status: 201
}

export type organizationUsersCreateResponseSuccess = (organizationUsersCreateResponse201) & {
  headers: Headers;
};
;

export type organizationUsersCreateResponse = (organizationUsersCreateResponseSuccess)

export const getOrganizationUsersCreateUrl = () => {




  return `/api/auth/invitations`
}

export const organizationUsersCreate = async (createOrganizationInvitationDto: CreateOrganizationInvitationDto, options?: RequestInit): Promise<organizationUsersCreateResponse> => {

  return customMutator<organizationUsersCreateResponse>(getOrganizationUsersCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createOrganizationInvitationDto)
  }
);}





export const getOrganizationUsersCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersCreate>>, TError,{data: CreateOrganizationInvitationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersCreate>>, TError,{data: CreateOrganizationInvitationDto}, TContext> => {

const mutationKey = ['organizationUsersCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersCreate>>, {data: CreateOrganizationInvitationDto}> = (props) => {
          const {data} = props ?? {};

          return  organizationUsersCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersCreate>>>
    export type OrganizationUsersCreateMutationBody = CreateOrganizationInvitationDto
    export type OrganizationUsersCreateMutationError = unknown

    export const useOrganizationUsersCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersCreate>>, TError,{data: CreateOrganizationInvitationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersCreate>>,
        TError,
        {data: CreateOrganizationInvitationDto},
        TContext
      > => {
      return useMutation(getOrganizationUsersCreateMutationOptions(options), queryClient);
    }

export type authInvitationAcceptResponse201 = {
  data: AuthInvitationAccept201
  status: 201
}

export type authInvitationAcceptResponseSuccess = (authInvitationAcceptResponse201) & {
  headers: Headers;
};
;

export type authInvitationAcceptResponse = (authInvitationAcceptResponseSuccess)

export const getAuthInvitationAcceptUrl = () => {




  return `/api/auth/invitations/accept`
}

export const authInvitationAccept = async (acceptOrganizationInvitationDto: AcceptOrganizationInvitationDto, options?: RequestInit): Promise<authInvitationAcceptResponse> => {

  return customMutator<authInvitationAcceptResponse>(getAuthInvitationAcceptUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(acceptOrganizationInvitationDto)
  }
);}





export const getAuthInvitationAcceptMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authInvitationAccept>>, TError,{data: AcceptOrganizationInvitationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authInvitationAccept>>, TError,{data: AcceptOrganizationInvitationDto}, TContext> => {

const mutationKey = ['authInvitationAccept'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authInvitationAccept>>, {data: AcceptOrganizationInvitationDto}> = (props) => {
          const {data} = props ?? {};

          return  authInvitationAccept(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthInvitationAcceptMutationResult = NonNullable<Awaited<ReturnType<typeof authInvitationAccept>>>
    export type AuthInvitationAcceptMutationBody = AcceptOrganizationInvitationDto
    export type AuthInvitationAcceptMutationError = unknown

    export const useAuthInvitationAccept = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authInvitationAccept>>, TError,{data: AcceptOrganizationInvitationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authInvitationAccept>>,
        TError,
        {data: AcceptOrganizationInvitationDto},
        TContext
      > => {
      return useMutation(getAuthInvitationAcceptMutationOptions(options), queryClient);
    }

export type authPasswordChangeResponse201 = {
  data: AuthPasswordChange201
  status: 201
}

export type authPasswordChangeResponseSuccess = (authPasswordChangeResponse201) & {
  headers: Headers;
};
;

export type authPasswordChangeResponse = (authPasswordChangeResponseSuccess)

export const getAuthPasswordChangeUrl = () => {




  return `/api/auth/me/password/initial`
}

export const authPasswordChange = async (initialPasswordChangeDto: InitialPasswordChangeDto, options?: RequestInit): Promise<authPasswordChangeResponse> => {

  return customMutator<authPasswordChangeResponse>(getAuthPasswordChangeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(initialPasswordChangeDto)
  }
);}





export const getAuthPasswordChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authPasswordChange>>, TError,{data: InitialPasswordChangeDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authPasswordChange>>, TError,{data: InitialPasswordChangeDto}, TContext> => {

const mutationKey = ['authPasswordChange'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authPasswordChange>>, {data: InitialPasswordChangeDto}> = (props) => {
          const {data} = props ?? {};

          return  authPasswordChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthPasswordChangeMutationResult = NonNullable<Awaited<ReturnType<typeof authPasswordChange>>>
    export type AuthPasswordChangeMutationBody = InitialPasswordChangeDto
    export type AuthPasswordChangeMutationError = unknown

    export const useAuthPasswordChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authPasswordChange>>, TError,{data: InitialPasswordChangeDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authPasswordChange>>,
        TError,
        {data: InitialPasswordChangeDto},
        TContext
      > => {
      return useMutation(getAuthPasswordChangeMutationOptions(options), queryClient);
    }

export type authChallengeTotpResponse201 = {
  data: AuthChallengeTotp201
  status: 201
}

export type authChallengeTotpResponseSuccess = (authChallengeTotpResponse201) & {
  headers: Headers;
};
;

export type authChallengeTotpResponse = (authChallengeTotpResponseSuccess)

export const getAuthChallengeTotpUrl = (challengeToken: string,) => {




  return `/api/auth/challenges/${challengeToken}/totp`
}

export const authChallengeTotp = async (challengeToken: string,
    challengeTotpDto: ChallengeTotpDto, options?: RequestInit): Promise<authChallengeTotpResponse> => {

  return customMutator<authChallengeTotpResponse>(getAuthChallengeTotpUrl(challengeToken),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(challengeTotpDto)
  }
);}





export const getAuthChallengeTotpMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChallengeTotp>>, TError,{challengeToken: string;data: ChallengeTotpDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authChallengeTotp>>, TError,{challengeToken: string;data: ChallengeTotpDto}, TContext> => {

const mutationKey = ['authChallengeTotp'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authChallengeTotp>>, {challengeToken: string;data: ChallengeTotpDto}> = (props) => {
          const {challengeToken,data} = props ?? {};

          return  authChallengeTotp(challengeToken,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthChallengeTotpMutationResult = NonNullable<Awaited<ReturnType<typeof authChallengeTotp>>>
    export type AuthChallengeTotpMutationBody = ChallengeTotpDto
    export type AuthChallengeTotpMutationError = unknown

    export const useAuthChallengeTotp = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChallengeTotp>>, TError,{challengeToken: string;data: ChallengeTotpDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authChallengeTotp>>,
        TError,
        {challengeToken: string;data: ChallengeTotpDto},
        TContext
      > => {
      return useMutation(getAuthChallengeTotpMutationOptions(options), queryClient);
    }

export type authChallengeRecoveryResponse201 = {
  data: AuthChallengeRecovery201
  status: 201
}

export type authChallengeRecoveryResponseSuccess = (authChallengeRecoveryResponse201) & {
  headers: Headers;
};
;

export type authChallengeRecoveryResponse = (authChallengeRecoveryResponseSuccess)

export const getAuthChallengeRecoveryUrl = (challengeToken: string,) => {




  return `/api/auth/challenges/${challengeToken}/recovery-code`
}

export const authChallengeRecovery = async (challengeToken: string,
    challengeRecoveryCodeDto: ChallengeRecoveryCodeDto, options?: RequestInit): Promise<authChallengeRecoveryResponse> => {

  return customMutator<authChallengeRecoveryResponse>(getAuthChallengeRecoveryUrl(challengeToken),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(challengeRecoveryCodeDto)
  }
);}





export const getAuthChallengeRecoveryMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChallengeRecovery>>, TError,{challengeToken: string;data: ChallengeRecoveryCodeDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authChallengeRecovery>>, TError,{challengeToken: string;data: ChallengeRecoveryCodeDto}, TContext> => {

const mutationKey = ['authChallengeRecovery'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authChallengeRecovery>>, {challengeToken: string;data: ChallengeRecoveryCodeDto}> = (props) => {
          const {challengeToken,data} = props ?? {};

          return  authChallengeRecovery(challengeToken,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthChallengeRecoveryMutationResult = NonNullable<Awaited<ReturnType<typeof authChallengeRecovery>>>
    export type AuthChallengeRecoveryMutationBody = ChallengeRecoveryCodeDto
    export type AuthChallengeRecoveryMutationError = unknown

    export const useAuthChallengeRecovery = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChallengeRecovery>>, TError,{challengeToken: string;data: ChallengeRecoveryCodeDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authChallengeRecovery>>,
        TError,
        {challengeToken: string;data: ChallengeRecoveryCodeDto},
        TContext
      > => {
      return useMutation(getAuthChallengeRecoveryMutationOptions(options), queryClient);
    }

export type authSelectContextResponse201 = {
  data: AuthSelectContext201
  status: 201
}

export type authSelectContextResponseSuccess = (authSelectContextResponse201) & {
  headers: Headers;
};
;

export type authSelectContextResponse = (authSelectContextResponseSuccess)

export const getAuthSelectContextUrl = () => {




  return `/api/auth/context`
}

export const authSelectContext = async (selectContextDto: SelectContextDto, options?: RequestInit): Promise<authSelectContextResponse> => {

  return customMutator<authSelectContextResponse>(getAuthSelectContextUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(selectContextDto)
  }
);}





export const getAuthSelectContextMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authSelectContext>>, TError,{data: SelectContextDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authSelectContext>>, TError,{data: SelectContextDto}, TContext> => {

const mutationKey = ['authSelectContext'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authSelectContext>>, {data: SelectContextDto}> = (props) => {
          const {data} = props ?? {};

          return  authSelectContext(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthSelectContextMutationResult = NonNullable<Awaited<ReturnType<typeof authSelectContext>>>
    export type AuthSelectContextMutationBody = SelectContextDto
    export type AuthSelectContextMutationError = unknown

    export const useAuthSelectContext = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authSelectContext>>, TError,{data: SelectContextDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authSelectContext>>,
        TError,
        {data: SelectContextDto},
        TContext
      > => {
      return useMutation(getAuthSelectContextMutationOptions(options), queryClient);
    }

export type authDelegationEnterResponse201 = {
  data: AuthDelegationEnter201
  status: 201
}

export type authDelegationEnterResponseSuccess = (authDelegationEnterResponse201) & {
  headers: Headers;
};
;

export type authDelegationEnterResponse = (authDelegationEnterResponseSuccess)

export const getAuthDelegationEnterUrl = (delegationId: string,) => {




  return `/api/auth/delegations/${delegationId}/enter`
}

export const authDelegationEnter = async (delegationId: string, options?: RequestInit): Promise<authDelegationEnterResponse> => {

  return customMutator<authDelegationEnterResponse>(getAuthDelegationEnterUrl(delegationId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAuthDelegationEnterMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authDelegationEnter>>, TError,{delegationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authDelegationEnter>>, TError,{delegationId: string}, TContext> => {

const mutationKey = ['authDelegationEnter'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authDelegationEnter>>, {delegationId: string}> = (props) => {
          const {delegationId} = props ?? {};

          return  authDelegationEnter(delegationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthDelegationEnterMutationResult = NonNullable<Awaited<ReturnType<typeof authDelegationEnter>>>

    export type AuthDelegationEnterMutationError = unknown

    export const useAuthDelegationEnter = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authDelegationEnter>>, TError,{delegationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authDelegationEnter>>,
        TError,
        {delegationId: string},
        TContext
      > => {
      return useMutation(getAuthDelegationEnterMutationOptions(options), queryClient);
    }

export type authDelegationExitResponse201 = {
  data: AuthDelegationExit201
  status: 201
}

export type authDelegationExitResponseSuccess = (authDelegationExitResponse201) & {
  headers: Headers;
};
;

export type authDelegationExitResponse = (authDelegationExitResponseSuccess)

export const getAuthDelegationExitUrl = () => {




  return `/api/auth/delegations/exit`
}

export const authDelegationExit = async ( options?: RequestInit): Promise<authDelegationExitResponse> => {

  return customMutator<authDelegationExitResponse>(getAuthDelegationExitUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAuthDelegationExitMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authDelegationExit>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authDelegationExit>>, TError,void, TContext> => {

const mutationKey = ['authDelegationExit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authDelegationExit>>, void> = () => {


          return  authDelegationExit(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthDelegationExitMutationResult = NonNullable<Awaited<ReturnType<typeof authDelegationExit>>>

    export type AuthDelegationExitMutationError = unknown

    export const useAuthDelegationExit = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authDelegationExit>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authDelegationExit>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAuthDelegationExitMutationOptions(options), queryClient);
    }

export type authRefreshResponse200 = {
  data: AuthRefresh200
  status: 200
}

export type authRefreshResponseSuccess = (authRefreshResponse200) & {
  headers: Headers;
};
;

export type authRefreshResponse = (authRefreshResponseSuccess)

export const getAuthRefreshUrl = () => {




  return `/api/auth/refresh`
}

export const authRefresh = async (refreshTokenDto: RefreshTokenDto, options?: RequestInit): Promise<authRefreshResponse> => {

  return customMutator<authRefreshResponse>(getAuthRefreshUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(refreshTokenDto)
  }
);}





export const getAuthRefreshMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRefresh>>, TError,{data: RefreshTokenDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authRefresh>>, TError,{data: RefreshTokenDto}, TContext> => {

const mutationKey = ['authRefresh'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authRefresh>>, {data: RefreshTokenDto}> = (props) => {
          const {data} = props ?? {};

          return  authRefresh(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthRefreshMutationResult = NonNullable<Awaited<ReturnType<typeof authRefresh>>>
    export type AuthRefreshMutationBody = RefreshTokenDto
    export type AuthRefreshMutationError = unknown

    export const useAuthRefresh = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRefresh>>, TError,{data: RefreshTokenDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authRefresh>>,
        TError,
        {data: RefreshTokenDto},
        TContext
      > => {
      return useMutation(getAuthRefreshMutationOptions(options), queryClient);
    }

export type authLogoutResponse201 = {
  data: AuthLogout201
  status: 201
}

export type authLogoutResponseSuccess = (authLogoutResponse201) & {
  headers: Headers;
};
;

export type authLogoutResponse = (authLogoutResponseSuccess)

export const getAuthLogoutUrl = () => {




  return `/api/auth/logout`
}

export const authLogout = async (logoutDto: LogoutDto, options?: RequestInit): Promise<authLogoutResponse> => {

  return customMutator<authLogoutResponse>(getAuthLogoutUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(logoutDto)
  }
);}





export const getAuthLogoutMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLogout>>, TError,{data: LogoutDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authLogout>>, TError,{data: LogoutDto}, TContext> => {

const mutationKey = ['authLogout'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authLogout>>, {data: LogoutDto}> = (props) => {
          const {data} = props ?? {};

          return  authLogout(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthLogoutMutationResult = NonNullable<Awaited<ReturnType<typeof authLogout>>>
    export type AuthLogoutMutationBody = LogoutDto
    export type AuthLogoutMutationError = unknown

    export const useAuthLogout = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLogout>>, TError,{data: LogoutDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authLogout>>,
        TError,
        {data: LogoutDto},
        TContext
      > => {
      return useMutation(getAuthLogoutMutationOptions(options), queryClient);
    }

export type authReadProfileResponse200 = {
  data: AuthReadProfile200
  status: 200
}

export type authReadProfileResponseSuccess = (authReadProfileResponse200) & {
  headers: Headers;
};
;

export type authReadProfileResponse = (authReadProfileResponseSuccess)

export const getAuthReadProfileUrl = () => {




  return `/api/auth/me`
}

export const authReadProfile = async ( options?: RequestInit): Promise<authReadProfileResponse> => {

  return customMutator<authReadProfileResponse>(getAuthReadProfileUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAuthReadProfileQueryKey = () => {
    return [
    `/api/auth/me`
    ] as const;
    }


export const getAuthReadProfileQueryOptions = <TData = Awaited<ReturnType<typeof authReadProfile>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authReadProfile>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAuthReadProfileQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof authReadProfile>>> = ({ signal }) => authReadProfile({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof authReadProfile>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AuthReadProfileQueryResult = NonNullable<Awaited<ReturnType<typeof authReadProfile>>>
export type AuthReadProfileQueryError = unknown


export function useAuthReadProfile<TData = Awaited<ReturnType<typeof authReadProfile>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof authReadProfile>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof authReadProfile>>,
          TError,
          Awaited<ReturnType<typeof authReadProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAuthReadProfile<TData = Awaited<ReturnType<typeof authReadProfile>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authReadProfile>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof authReadProfile>>,
          TError,
          Awaited<ReturnType<typeof authReadProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAuthReadProfile<TData = Awaited<ReturnType<typeof authReadProfile>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authReadProfile>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAuthReadProfile<TData = Awaited<ReturnType<typeof authReadProfile>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authReadProfile>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAuthReadProfileQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type settingsProfileUpdateResponse200 = {
  data: SettingsProfileUpdate200
  status: 200
}

export type settingsProfileUpdateResponseSuccess = (settingsProfileUpdateResponse200) & {
  headers: Headers;
};
;

export type settingsProfileUpdateResponse = (settingsProfileUpdateResponseSuccess)

export const getSettingsProfileUpdateUrl = () => {




  return `/api/auth/me`
}

export const settingsProfileUpdate = async (updateProfileDto: UpdateProfileDto, options?: RequestInit): Promise<settingsProfileUpdateResponse> => {

  return customMutator<settingsProfileUpdateResponse>(getSettingsProfileUpdateUrl(),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateProfileDto)
  }
);}





export const getSettingsProfileUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settingsProfileUpdate>>, TError,{data: UpdateProfileDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof settingsProfileUpdate>>, TError,{data: UpdateProfileDto}, TContext> => {

const mutationKey = ['settingsProfileUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof settingsProfileUpdate>>, {data: UpdateProfileDto}> = (props) => {
          const {data} = props ?? {};

          return  settingsProfileUpdate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SettingsProfileUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof settingsProfileUpdate>>>
    export type SettingsProfileUpdateMutationBody = UpdateProfileDto
    export type SettingsProfileUpdateMutationError = unknown

    export const useSettingsProfileUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settingsProfileUpdate>>, TError,{data: UpdateProfileDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof settingsProfileUpdate>>,
        TError,
        {data: UpdateProfileDto},
        TContext
      > => {
      return useMutation(getSettingsProfileUpdateMutationOptions(options), queryClient);
    }

export type settingsPreferencesResponse200 = {
  data: SettingsPreferences200
  status: 200
}

export type settingsPreferencesResponseSuccess = (settingsPreferencesResponse200) & {
  headers: Headers;
};
;

export type settingsPreferencesResponse = (settingsPreferencesResponseSuccess)

export const getSettingsPreferencesUrl = () => {




  return `/api/auth/me/preferences`
}

export const settingsPreferences = async ( options?: RequestInit): Promise<settingsPreferencesResponse> => {

  return customMutator<settingsPreferencesResponse>(getSettingsPreferencesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSettingsPreferencesQueryKey = () => {
    return [
    `/api/auth/me/preferences`
    ] as const;
    }


export const getSettingsPreferencesQueryOptions = <TData = Awaited<ReturnType<typeof settingsPreferences>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settingsPreferences>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSettingsPreferencesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof settingsPreferences>>> = ({ signal }) => settingsPreferences({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof settingsPreferences>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SettingsPreferencesQueryResult = NonNullable<Awaited<ReturnType<typeof settingsPreferences>>>
export type SettingsPreferencesQueryError = unknown


export function useSettingsPreferences<TData = Awaited<ReturnType<typeof settingsPreferences>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof settingsPreferences>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof settingsPreferences>>,
          TError,
          Awaited<ReturnType<typeof settingsPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettingsPreferences<TData = Awaited<ReturnType<typeof settingsPreferences>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settingsPreferences>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof settingsPreferences>>,
          TError,
          Awaited<ReturnType<typeof settingsPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettingsPreferences<TData = Awaited<ReturnType<typeof settingsPreferences>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settingsPreferences>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useSettingsPreferences<TData = Awaited<ReturnType<typeof settingsPreferences>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settingsPreferences>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSettingsPreferencesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type settingsPreferencesUpdateResponse200 = {
  data: SettingsPreferencesUpdate200
  status: 200
}

export type settingsPreferencesUpdateResponseSuccess = (settingsPreferencesUpdateResponse200) & {
  headers: Headers;
};
;

export type settingsPreferencesUpdateResponse = (settingsPreferencesUpdateResponseSuccess)

export const getSettingsPreferencesUpdateUrl = () => {




  return `/api/auth/me/preferences`
}

export const settingsPreferencesUpdate = async (updatePreferencesDto: UpdatePreferencesDto, options?: RequestInit): Promise<settingsPreferencesUpdateResponse> => {

  return customMutator<settingsPreferencesUpdateResponse>(getSettingsPreferencesUpdateUrl(),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updatePreferencesDto)
  }
);}





export const getSettingsPreferencesUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settingsPreferencesUpdate>>, TError,{data: UpdatePreferencesDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof settingsPreferencesUpdate>>, TError,{data: UpdatePreferencesDto}, TContext> => {

const mutationKey = ['settingsPreferencesUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof settingsPreferencesUpdate>>, {data: UpdatePreferencesDto}> = (props) => {
          const {data} = props ?? {};

          return  settingsPreferencesUpdate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SettingsPreferencesUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof settingsPreferencesUpdate>>>
    export type SettingsPreferencesUpdateMutationBody = UpdatePreferencesDto
    export type SettingsPreferencesUpdateMutationError = unknown

    export const useSettingsPreferencesUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settingsPreferencesUpdate>>, TError,{data: UpdatePreferencesDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof settingsPreferencesUpdate>>,
        TError,
        {data: UpdatePreferencesDto},
        TContext
      > => {
      return useMutation(getSettingsPreferencesUpdateMutationOptions(options), queryClient);
    }

export type authPasswordChangePostResponse200 = {
  data: AuthPasswordChangePost200
  status: 200
}

export type authPasswordChangePostResponseSuccess = (authPasswordChangePostResponse200) & {
  headers: Headers;
};
;

export type authPasswordChangePostResponse = (authPasswordChangePostResponseSuccess)

export const getAuthPasswordChangePostUrl = () => {




  return `/api/auth/me/password`
}

export const authPasswordChangePost = async (changePasswordRequestDto: ChangePasswordRequestDto, options?: RequestInit): Promise<authPasswordChangePostResponse> => {

  return customMutator<authPasswordChangePostResponse>(getAuthPasswordChangePostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(changePasswordRequestDto)
  }
);}





export const getAuthPasswordChangePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authPasswordChangePost>>, TError,{data: ChangePasswordRequestDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authPasswordChangePost>>, TError,{data: ChangePasswordRequestDto}, TContext> => {

const mutationKey = ['authPasswordChangePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authPasswordChangePost>>, {data: ChangePasswordRequestDto}> = (props) => {
          const {data} = props ?? {};

          return  authPasswordChangePost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthPasswordChangePostMutationResult = NonNullable<Awaited<ReturnType<typeof authPasswordChangePost>>>
    export type AuthPasswordChangePostMutationBody = ChangePasswordRequestDto
    export type AuthPasswordChangePostMutationError = unknown

    export const useAuthPasswordChangePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authPasswordChangePost>>, TError,{data: ChangePasswordRequestDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authPasswordChangePost>>,
        TError,
        {data: ChangePasswordRequestDto},
        TContext
      > => {
      return useMutation(getAuthPasswordChangePostMutationOptions(options), queryClient);
    }

export type authSessionsManageResponse200 = {
  data: AuthSessionsManage200
  status: 200
}

export type authSessionsManageResponseSuccess = (authSessionsManageResponse200) & {
  headers: Headers;
};
;

export type authSessionsManageResponse = (authSessionsManageResponseSuccess)

export const getAuthSessionsManageUrl = () => {




  return `/api/auth/me/sessions`
}

export const authSessionsManage = async ( options?: RequestInit): Promise<authSessionsManageResponse> => {

  return customMutator<authSessionsManageResponse>(getAuthSessionsManageUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAuthSessionsManageQueryKey = () => {
    return [
    `/api/auth/me/sessions`
    ] as const;
    }


export const getAuthSessionsManageQueryOptions = <TData = Awaited<ReturnType<typeof authSessionsManage>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authSessionsManage>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAuthSessionsManageQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof authSessionsManage>>> = ({ signal }) => authSessionsManage({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof authSessionsManage>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AuthSessionsManageQueryResult = NonNullable<Awaited<ReturnType<typeof authSessionsManage>>>
export type AuthSessionsManageQueryError = unknown


export function useAuthSessionsManage<TData = Awaited<ReturnType<typeof authSessionsManage>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof authSessionsManage>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof authSessionsManage>>,
          TError,
          Awaited<ReturnType<typeof authSessionsManage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAuthSessionsManage<TData = Awaited<ReturnType<typeof authSessionsManage>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authSessionsManage>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof authSessionsManage>>,
          TError,
          Awaited<ReturnType<typeof authSessionsManage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAuthSessionsManage<TData = Awaited<ReturnType<typeof authSessionsManage>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authSessionsManage>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAuthSessionsManage<TData = Awaited<ReturnType<typeof authSessionsManage>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authSessionsManage>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAuthSessionsManageQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type authSessionsRevokeResponse200 = {
  data: AuthSessionsRevoke200
  status: 200
}

export type authSessionsRevokeResponseSuccess = (authSessionsRevokeResponse200) & {
  headers: Headers;
};
;

export type authSessionsRevokeResponse = (authSessionsRevokeResponseSuccess)

export const getAuthSessionsRevokeUrl = (sessionId: string,) => {




  return `/api/auth/me/sessions/${sessionId}/revoke`
}

export const authSessionsRevoke = async (sessionId: string, options?: RequestInit): Promise<authSessionsRevokeResponse> => {

  return customMutator<authSessionsRevokeResponse>(getAuthSessionsRevokeUrl(sessionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAuthSessionsRevokeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authSessionsRevoke>>, TError,{sessionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authSessionsRevoke>>, TError,{sessionId: string}, TContext> => {

const mutationKey = ['authSessionsRevoke'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authSessionsRevoke>>, {sessionId: string}> = (props) => {
          const {sessionId} = props ?? {};

          return  authSessionsRevoke(sessionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthSessionsRevokeMutationResult = NonNullable<Awaited<ReturnType<typeof authSessionsRevoke>>>

    export type AuthSessionsRevokeMutationError = unknown

    export const useAuthSessionsRevoke = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authSessionsRevoke>>, TError,{sessionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authSessionsRevoke>>,
        TError,
        {sessionId: string},
        TContext
      > => {
      return useMutation(getAuthSessionsRevokeMutationOptions(options), queryClient);
    }

export type authSessionsRevokePostResponse200 = {
  data: AuthSessionsRevokePost200
  status: 200
}

export type authSessionsRevokePostResponseSuccess = (authSessionsRevokePostResponse200) & {
  headers: Headers;
};
;

export type authSessionsRevokePostResponse = (authSessionsRevokePostResponseSuccess)

export const getAuthSessionsRevokePostUrl = () => {




  return `/api/auth/me/sessions/revoke-others`
}

export const authSessionsRevokePost = async ( options?: RequestInit): Promise<authSessionsRevokePostResponse> => {

  return customMutator<authSessionsRevokePostResponse>(getAuthSessionsRevokePostUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAuthSessionsRevokePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authSessionsRevokePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authSessionsRevokePost>>, TError,void, TContext> => {

const mutationKey = ['authSessionsRevokePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authSessionsRevokePost>>, void> = () => {


          return  authSessionsRevokePost(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthSessionsRevokePostMutationResult = NonNullable<Awaited<ReturnType<typeof authSessionsRevokePost>>>

    export type AuthSessionsRevokePostMutationError = unknown

    export const useAuthSessionsRevokePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authSessionsRevokePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authSessionsRevokePost>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAuthSessionsRevokePostMutationOptions(options), queryClient);
    }

export type authSwitchContextResponse200 = {
  data: AuthSwitchContext200
  status: 200
}

export type authSwitchContextResponseSuccess = (authSwitchContextResponse200) & {
  headers: Headers;
};
;

export type authSwitchContextResponse = (authSwitchContextResponseSuccess)

export const getAuthSwitchContextUrl = () => {




  return `/api/auth/context/switch`
}

export const authSwitchContext = async (switchContextDto: SwitchContextDto, options?: RequestInit): Promise<authSwitchContextResponse> => {

  return customMutator<authSwitchContextResponse>(getAuthSwitchContextUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(switchContextDto)
  }
);}





export const getAuthSwitchContextMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authSwitchContext>>, TError,{data: SwitchContextDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authSwitchContext>>, TError,{data: SwitchContextDto}, TContext> => {

const mutationKey = ['authSwitchContext'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authSwitchContext>>, {data: SwitchContextDto}> = (props) => {
          const {data} = props ?? {};

          return  authSwitchContext(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthSwitchContextMutationResult = NonNullable<Awaited<ReturnType<typeof authSwitchContext>>>
    export type AuthSwitchContextMutationBody = SwitchContextDto
    export type AuthSwitchContextMutationError = unknown

    export const useAuthSwitchContext = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authSwitchContext>>, TError,{data: SwitchContextDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authSwitchContext>>,
        TError,
        {data: SwitchContextDto},
        TContext
      > => {
      return useMutation(getAuthSwitchContextMutationOptions(options), queryClient);
    }

export type authMfaEnrollResponse201 = {
  data: AuthMfaEnroll201
  status: 201
}

export type authMfaEnrollResponseSuccess = (authMfaEnrollResponse201) & {
  headers: Headers;
};
;

export type authMfaEnrollResponse = (authMfaEnrollResponseSuccess)

export const getAuthMfaEnrollUrl = () => {




  return `/api/auth/mfa/enroll`
}

export const authMfaEnroll = async ( options?: RequestInit): Promise<authMfaEnrollResponse> => {

  return customMutator<authMfaEnrollResponse>(getAuthMfaEnrollUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAuthMfaEnrollMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authMfaEnroll>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authMfaEnroll>>, TError,void, TContext> => {

const mutationKey = ['authMfaEnroll'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authMfaEnroll>>, void> = () => {


          return  authMfaEnroll(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthMfaEnrollMutationResult = NonNullable<Awaited<ReturnType<typeof authMfaEnroll>>>

    export type AuthMfaEnrollMutationError = unknown

    export const useAuthMfaEnroll = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authMfaEnroll>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authMfaEnroll>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAuthMfaEnrollMutationOptions(options), queryClient);
    }

export type authMfaVerifyResponse201 = {
  data: AuthMfaVerify201
  status: 201
}

export type authMfaVerifyResponseSuccess = (authMfaVerifyResponse201) & {
  headers: Headers;
};
;

export type authMfaVerifyResponse = (authMfaVerifyResponseSuccess)

export const getAuthMfaVerifyUrl = () => {




  return `/api/auth/mfa/verify`
}

export const authMfaVerify = async (verifyMfaDto: VerifyMfaDto, options?: RequestInit): Promise<authMfaVerifyResponse> => {

  return customMutator<authMfaVerifyResponse>(getAuthMfaVerifyUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(verifyMfaDto)
  }
);}





export const getAuthMfaVerifyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authMfaVerify>>, TError,{data: VerifyMfaDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authMfaVerify>>, TError,{data: VerifyMfaDto}, TContext> => {

const mutationKey = ['authMfaVerify'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authMfaVerify>>, {data: VerifyMfaDto}> = (props) => {
          const {data} = props ?? {};

          return  authMfaVerify(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthMfaVerifyMutationResult = NonNullable<Awaited<ReturnType<typeof authMfaVerify>>>
    export type AuthMfaVerifyMutationBody = VerifyMfaDto
    export type AuthMfaVerifyMutationError = unknown

    export const useAuthMfaVerify = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authMfaVerify>>, TError,{data: VerifyMfaDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authMfaVerify>>,
        TError,
        {data: VerifyMfaDto},
        TContext
      > => {
      return useMutation(getAuthMfaVerifyMutationOptions(options), queryClient);
    }

export type authMfaDisableResponse201 = {
  data: AuthMfaDisable201
  status: 201
}

export type authMfaDisableResponseSuccess = (authMfaDisableResponse201) & {
  headers: Headers;
};
;

export type authMfaDisableResponse = (authMfaDisableResponseSuccess)

export const getAuthMfaDisableUrl = () => {




  return `/api/auth/mfa/disable`
}

export const authMfaDisable = async (disableMfaDto: DisableMfaDto, options?: RequestInit): Promise<authMfaDisableResponse> => {

  return customMutator<authMfaDisableResponse>(getAuthMfaDisableUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(disableMfaDto)
  }
);}





export const getAuthMfaDisableMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authMfaDisable>>, TError,{data: DisableMfaDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authMfaDisable>>, TError,{data: DisableMfaDto}, TContext> => {

const mutationKey = ['authMfaDisable'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authMfaDisable>>, {data: DisableMfaDto}> = (props) => {
          const {data} = props ?? {};

          return  authMfaDisable(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthMfaDisableMutationResult = NonNullable<Awaited<ReturnType<typeof authMfaDisable>>>
    export type AuthMfaDisableMutationBody = DisableMfaDto
    export type AuthMfaDisableMutationError = unknown

    export const useAuthMfaDisable = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authMfaDisable>>, TError,{data: DisableMfaDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authMfaDisable>>,
        TError,
        {data: DisableMfaDto},
        TContext
      > => {
      return useMutation(getAuthMfaDisableMutationOptions(options), queryClient);
    }

export type authMfaBackupCodesRegenerateResponse201 = {
  data: AuthMfaBackupCodesRegenerate201
  status: 201
}

export type authMfaBackupCodesRegenerateResponseSuccess = (authMfaBackupCodesRegenerateResponse201) & {
  headers: Headers;
};
;

export type authMfaBackupCodesRegenerateResponse = (authMfaBackupCodesRegenerateResponseSuccess)

export const getAuthMfaBackupCodesRegenerateUrl = () => {




  return `/api/auth/mfa/backup-codes`
}

export const authMfaBackupCodesRegenerate = async (regenerateBackupCodesDto: RegenerateBackupCodesDto, options?: RequestInit): Promise<authMfaBackupCodesRegenerateResponse> => {

  return customMutator<authMfaBackupCodesRegenerateResponse>(getAuthMfaBackupCodesRegenerateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(regenerateBackupCodesDto)
  }
);}





export const getAuthMfaBackupCodesRegenerateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authMfaBackupCodesRegenerate>>, TError,{data: RegenerateBackupCodesDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authMfaBackupCodesRegenerate>>, TError,{data: RegenerateBackupCodesDto}, TContext> => {

const mutationKey = ['authMfaBackupCodesRegenerate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authMfaBackupCodesRegenerate>>, {data: RegenerateBackupCodesDto}> = (props) => {
          const {data} = props ?? {};

          return  authMfaBackupCodesRegenerate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthMfaBackupCodesRegenerateMutationResult = NonNullable<Awaited<ReturnType<typeof authMfaBackupCodesRegenerate>>>
    export type AuthMfaBackupCodesRegenerateMutationBody = RegenerateBackupCodesDto
    export type AuthMfaBackupCodesRegenerateMutationError = unknown

    export const useAuthMfaBackupCodesRegenerate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authMfaBackupCodesRegenerate>>, TError,{data: RegenerateBackupCodesDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authMfaBackupCodesRegenerate>>,
        TError,
        {data: RegenerateBackupCodesDto},
        TContext
      > => {
      return useMutation(getAuthMfaBackupCodesRegenerateMutationOptions(options), queryClient);
    }

export type authPasswordForgotResponse201 = {
  data: AuthPasswordForgot201
  status: 201
}

export type authPasswordForgotResponseSuccess = (authPasswordForgotResponse201) & {
  headers: Headers;
};
;

export type authPasswordForgotResponse = (authPasswordForgotResponseSuccess)

export const getAuthPasswordForgotUrl = () => {




  return `/api/auth/password/forgot`
}

export const authPasswordForgot = async (forgotPasswordDto: ForgotPasswordDto, options?: RequestInit): Promise<authPasswordForgotResponse> => {

  return customMutator<authPasswordForgotResponse>(getAuthPasswordForgotUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(forgotPasswordDto)
  }
);}





export const getAuthPasswordForgotMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authPasswordForgot>>, TError,{data: ForgotPasswordDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authPasswordForgot>>, TError,{data: ForgotPasswordDto}, TContext> => {

const mutationKey = ['authPasswordForgot'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authPasswordForgot>>, {data: ForgotPasswordDto}> = (props) => {
          const {data} = props ?? {};

          return  authPasswordForgot(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthPasswordForgotMutationResult = NonNullable<Awaited<ReturnType<typeof authPasswordForgot>>>
    export type AuthPasswordForgotMutationBody = ForgotPasswordDto
    export type AuthPasswordForgotMutationError = unknown

    export const useAuthPasswordForgot = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authPasswordForgot>>, TError,{data: ForgotPasswordDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authPasswordForgot>>,
        TError,
        {data: ForgotPasswordDto},
        TContext
      > => {
      return useMutation(getAuthPasswordForgotMutationOptions(options), queryClient);
    }

export type authPasswordResetResponse201 = {
  data: AuthPasswordReset201
  status: 201
}

export type authPasswordResetResponseSuccess = (authPasswordResetResponse201) & {
  headers: Headers;
};
;

export type authPasswordResetResponse = (authPasswordResetResponseSuccess)

export const getAuthPasswordResetUrl = () => {




  return `/api/auth/password/reset`
}

export const authPasswordReset = async (resetPasswordDto: ResetPasswordDto, options?: RequestInit): Promise<authPasswordResetResponse> => {

  return customMutator<authPasswordResetResponse>(getAuthPasswordResetUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(resetPasswordDto)
  }
);}





export const getAuthPasswordResetMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authPasswordReset>>, TError,{data: ResetPasswordDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof authPasswordReset>>, TError,{data: ResetPasswordDto}, TContext> => {

const mutationKey = ['authPasswordReset'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authPasswordReset>>, {data: ResetPasswordDto}> = (props) => {
          const {data} = props ?? {};

          return  authPasswordReset(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthPasswordResetMutationResult = NonNullable<Awaited<ReturnType<typeof authPasswordReset>>>
    export type AuthPasswordResetMutationBody = ResetPasswordDto
    export type AuthPasswordResetMutationError = unknown

    export const useAuthPasswordReset = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authPasswordReset>>, TError,{data: ResetPasswordDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authPasswordReset>>,
        TError,
        {data: ResetPasswordDto},
        TContext
      > => {
      return useMutation(getAuthPasswordResetMutationOptions(options), queryClient);
    }

export type platformSupportDelegationsListResponse200 = {
  data: void
  status: 200
}

export type platformSupportDelegationsListResponseSuccess = (platformSupportDelegationsListResponse200) & {
  headers: Headers;
};
;

export type platformSupportDelegationsListResponse = (platformSupportDelegationsListResponseSuccess)

export const getPlatformSupportDelegationsListUrl = () => {




  return `/api/platform/support-delegations`
}

export const platformSupportDelegationsList = async ( options?: RequestInit): Promise<platformSupportDelegationsListResponse> => {

  return customMutator<platformSupportDelegationsListResponse>(getPlatformSupportDelegationsListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformSupportDelegationsListQueryKey = () => {
    return [
    `/api/platform/support-delegations`
    ] as const;
    }


export const getPlatformSupportDelegationsListQueryOptions = <TData = Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformSupportDelegationsListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformSupportDelegationsList>>> = ({ signal }) => platformSupportDelegationsList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformSupportDelegationsListQueryResult = NonNullable<Awaited<ReturnType<typeof platformSupportDelegationsList>>>
export type PlatformSupportDelegationsListQueryError = unknown


export function usePlatformSupportDelegationsList<TData = Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformSupportDelegationsList>>,
          TError,
          Awaited<ReturnType<typeof platformSupportDelegationsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformSupportDelegationsList<TData = Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformSupportDelegationsList>>,
          TError,
          Awaited<ReturnType<typeof platformSupportDelegationsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformSupportDelegationsList<TData = Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformSupportDelegationsList<TData = Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformSupportDelegationsListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformSupportDelegationsCreateResponse201 = {
  data: void
  status: 201
}

export type platformSupportDelegationsCreateResponseSuccess = (platformSupportDelegationsCreateResponse201) & {
  headers: Headers;
};
;

export type platformSupportDelegationsCreateResponse = (platformSupportDelegationsCreateResponseSuccess)

export const getPlatformSupportDelegationsCreateUrl = () => {




  return `/api/platform/support-delegations`
}

export const platformSupportDelegationsCreate = async ( options?: RequestInit): Promise<platformSupportDelegationsCreateResponse> => {

  return customMutator<platformSupportDelegationsCreateResponse>(getPlatformSupportDelegationsCreateUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSupportDelegationsCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsCreate>>, TError,void, TContext> => {

const mutationKey = ['platformSupportDelegationsCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSupportDelegationsCreate>>, void> = () => {


          return  platformSupportDelegationsCreate(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSupportDelegationsCreateMutationResult = NonNullable<Awaited<ReturnType<typeof platformSupportDelegationsCreate>>>

    export type PlatformSupportDelegationsCreateMutationError = unknown

    export const usePlatformSupportDelegationsCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSupportDelegationsCreate>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformSupportDelegationsCreateMutationOptions(options), queryClient);
    }

export type platformSupportDelegationsReadResponse200 = {
  data: void
  status: 200
}

export type platformSupportDelegationsReadResponseSuccess = (platformSupportDelegationsReadResponse200) & {
  headers: Headers;
};
;

export type platformSupportDelegationsReadResponse = (platformSupportDelegationsReadResponseSuccess)

export const getPlatformSupportDelegationsReadUrl = (delegationId: string,) => {




  return `/api/platform/support-delegations/${delegationId}`
}

export const platformSupportDelegationsRead = async (delegationId: string, options?: RequestInit): Promise<platformSupportDelegationsReadResponse> => {

  return customMutator<platformSupportDelegationsReadResponse>(getPlatformSupportDelegationsReadUrl(delegationId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformSupportDelegationsReadQueryKey = (delegationId: string,) => {
    return [
    `/api/platform/support-delegations/${delegationId}`
    ] as const;
    }


export const getPlatformSupportDelegationsReadQueryOptions = <TData = Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError = unknown>(delegationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformSupportDelegationsReadQueryKey(delegationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformSupportDelegationsRead>>> = ({ signal }) => platformSupportDelegationsRead(delegationId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: delegationId !== null && delegationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformSupportDelegationsReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformSupportDelegationsRead>>>
export type PlatformSupportDelegationsReadQueryError = unknown


export function usePlatformSupportDelegationsRead<TData = Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError = unknown>(
 delegationId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformSupportDelegationsRead>>,
          TError,
          Awaited<ReturnType<typeof platformSupportDelegationsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformSupportDelegationsRead<TData = Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError = unknown>(
 delegationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformSupportDelegationsRead>>,
          TError,
          Awaited<ReturnType<typeof platformSupportDelegationsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformSupportDelegationsRead<TData = Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError = unknown>(
 delegationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformSupportDelegationsRead<TData = Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError = unknown>(
 delegationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSupportDelegationsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformSupportDelegationsReadQueryOptions(delegationId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformSupportDelegationsApproveResponse201 = {
  data: void
  status: 201
}

export type platformSupportDelegationsApproveResponseSuccess = (platformSupportDelegationsApproveResponse201) & {
  headers: Headers;
};
;

export type platformSupportDelegationsApproveResponse = (platformSupportDelegationsApproveResponseSuccess)

export const getPlatformSupportDelegationsApproveUrl = (delegationId: string,) => {




  return `/api/platform/support-delegations/${delegationId}/decision`
}

export const platformSupportDelegationsApprove = async (delegationId: string, options?: RequestInit): Promise<platformSupportDelegationsApproveResponse> => {

  return customMutator<platformSupportDelegationsApproveResponse>(getPlatformSupportDelegationsApproveUrl(delegationId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSupportDelegationsApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsApprove>>, TError,{delegationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsApprove>>, TError,{delegationId: string}, TContext> => {

const mutationKey = ['platformSupportDelegationsApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSupportDelegationsApprove>>, {delegationId: string}> = (props) => {
          const {delegationId} = props ?? {};

          return  platformSupportDelegationsApprove(delegationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSupportDelegationsApproveMutationResult = NonNullable<Awaited<ReturnType<typeof platformSupportDelegationsApprove>>>

    export type PlatformSupportDelegationsApproveMutationError = unknown

    export const usePlatformSupportDelegationsApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsApprove>>, TError,{delegationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSupportDelegationsApprove>>,
        TError,
        {delegationId: string},
        TContext
      > => {
      return useMutation(getPlatformSupportDelegationsApproveMutationOptions(options), queryClient);
    }

export type platformSupportDelegationsRevokeResponse201 = {
  data: void
  status: 201
}

export type platformSupportDelegationsRevokeResponseSuccess = (platformSupportDelegationsRevokeResponse201) & {
  headers: Headers;
};
;

export type platformSupportDelegationsRevokeResponse = (platformSupportDelegationsRevokeResponseSuccess)

export const getPlatformSupportDelegationsRevokeUrl = (delegationId: string,) => {




  return `/api/platform/support-delegations/${delegationId}/revoke`
}

export const platformSupportDelegationsRevoke = async (delegationId: string, options?: RequestInit): Promise<platformSupportDelegationsRevokeResponse> => {

  return customMutator<platformSupportDelegationsRevokeResponse>(getPlatformSupportDelegationsRevokeUrl(delegationId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSupportDelegationsRevokeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsRevoke>>, TError,{delegationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsRevoke>>, TError,{delegationId: string}, TContext> => {

const mutationKey = ['platformSupportDelegationsRevoke'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSupportDelegationsRevoke>>, {delegationId: string}> = (props) => {
          const {delegationId} = props ?? {};

          return  platformSupportDelegationsRevoke(delegationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSupportDelegationsRevokeMutationResult = NonNullable<Awaited<ReturnType<typeof platformSupportDelegationsRevoke>>>

    export type PlatformSupportDelegationsRevokeMutationError = unknown

    export const usePlatformSupportDelegationsRevoke = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsRevoke>>, TError,{delegationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSupportDelegationsRevoke>>,
        TError,
        {delegationId: string},
        TContext
      > => {
      return useMutation(getPlatformSupportDelegationsRevokeMutationOptions(options), queryClient);
    }

export type platformSupportDelegationsApprovePostResponse201 = {
  data: void
  status: 201
}

export type platformSupportDelegationsApprovePostResponseSuccess = (platformSupportDelegationsApprovePostResponse201) & {
  headers: Headers;
};
;

export type platformSupportDelegationsApprovePostResponse = (platformSupportDelegationsApprovePostResponseSuccess)

export const getPlatformSupportDelegationsApprovePostUrl = (delegationId: string,) => {




  return `/api/platform/support-delegations/${delegationId}/emergency-review`
}

export const platformSupportDelegationsApprovePost = async (delegationId: string, options?: RequestInit): Promise<platformSupportDelegationsApprovePostResponse> => {

  return customMutator<platformSupportDelegationsApprovePostResponse>(getPlatformSupportDelegationsApprovePostUrl(delegationId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSupportDelegationsApprovePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsApprovePost>>, TError,{delegationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsApprovePost>>, TError,{delegationId: string}, TContext> => {

const mutationKey = ['platformSupportDelegationsApprovePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSupportDelegationsApprovePost>>, {delegationId: string}> = (props) => {
          const {delegationId} = props ?? {};

          return  platformSupportDelegationsApprovePost(delegationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSupportDelegationsApprovePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformSupportDelegationsApprovePost>>>

    export type PlatformSupportDelegationsApprovePostMutationError = unknown

    export const usePlatformSupportDelegationsApprovePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSupportDelegationsApprovePost>>, TError,{delegationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSupportDelegationsApprovePost>>,
        TError,
        {delegationId: string},
        TContext
      > => {
      return useMutation(getPlatformSupportDelegationsApprovePostMutationOptions(options), queryClient);
    }

export type platformOrganizationsCreateResponse200 = {
  data: void
  status: 200
}

export type platformOrganizationsCreateResponse201 = {
  data: void
  status: 201
}

export type platformOrganizationsCreateResponseSuccess = (platformOrganizationsCreateResponse200 | platformOrganizationsCreateResponse201) & {
  headers: Headers;
};
;

export type platformOrganizationsCreateResponse = (platformOrganizationsCreateResponseSuccess)

export const getPlatformOrganizationsCreateUrl = () => {




  return `/api/platform/organizations`
}

export const platformOrganizationsCreate = async (createOrgDto: CreateOrgDto, options?: RequestInit): Promise<platformOrganizationsCreateResponse> => {

  return customMutator<platformOrganizationsCreateResponse>(getPlatformOrganizationsCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createOrgDto)
  }
);}





export const getPlatformOrganizationsCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationsCreate>>, TError,{data: CreateOrgDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationsCreate>>, TError,{data: CreateOrgDto}, TContext> => {

const mutationKey = ['platformOrganizationsCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationsCreate>>, {data: CreateOrgDto}> = (props) => {
          const {data} = props ?? {};

          return  platformOrganizationsCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationsCreateMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationsCreate>>>
    export type PlatformOrganizationsCreateMutationBody = CreateOrgDto
    export type PlatformOrganizationsCreateMutationError = unknown

    export const usePlatformOrganizationsCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationsCreate>>, TError,{data: CreateOrgDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationsCreate>>,
        TError,
        {data: CreateOrgDto},
        TContext
      > => {
      return useMutation(getPlatformOrganizationsCreateMutationOptions(options), queryClient);
    }

export type platformOrganizationsListResponse200 = {
  data: PlatformOrganizationsList200
  status: 200
}

export type platformOrganizationsListResponseSuccess = (platformOrganizationsListResponse200) & {
  headers: Headers;
};
;

export type platformOrganizationsListResponse = (platformOrganizationsListResponseSuccess)

export const getPlatformOrganizationsListUrl = () => {




  return `/api/platform/organizations`
}

export const platformOrganizationsList = async ( options?: RequestInit): Promise<platformOrganizationsListResponse> => {

  return customMutator<platformOrganizationsListResponse>(getPlatformOrganizationsListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformOrganizationsListQueryKey = () => {
    return [
    `/api/platform/organizations`
    ] as const;
    }


export const getPlatformOrganizationsListQueryOptions = <TData = Awaited<ReturnType<typeof platformOrganizationsList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformOrganizationsListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformOrganizationsList>>> = ({ signal }) => platformOrganizationsList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformOrganizationsListQueryResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationsList>>>
export type PlatformOrganizationsListQueryError = unknown


export function usePlatformOrganizationsList<TData = Awaited<ReturnType<typeof platformOrganizationsList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationsList>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationsList<TData = Awaited<ReturnType<typeof platformOrganizationsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationsList>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationsList<TData = Awaited<ReturnType<typeof platformOrganizationsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformOrganizationsList<TData = Awaited<ReturnType<typeof platformOrganizationsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformOrganizationsListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformOrganizationsReadResponse200 = {
  data: PlatformOrganizationsRead200
  status: 200
}

export type platformOrganizationsReadResponseSuccess = (platformOrganizationsReadResponse200) & {
  headers: Headers;
};
;

export type platformOrganizationsReadResponse = (platformOrganizationsReadResponseSuccess)

export const getPlatformOrganizationsReadUrl = (id: string,) => {




  return `/api/platform/organizations/${id}`
}

export const platformOrganizationsRead = async (id: string, options?: RequestInit): Promise<platformOrganizationsReadResponse> => {

  return customMutator<platformOrganizationsReadResponse>(getPlatformOrganizationsReadUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformOrganizationsReadQueryKey = (id: string,) => {
    return [
    `/api/platform/organizations/${id}`
    ] as const;
    }


export const getPlatformOrganizationsReadQueryOptions = <TData = Awaited<ReturnType<typeof platformOrganizationsRead>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformOrganizationsReadQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformOrganizationsRead>>> = ({ signal }) => platformOrganizationsRead(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformOrganizationsReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationsRead>>>
export type PlatformOrganizationsReadQueryError = unknown


export function usePlatformOrganizationsRead<TData = Awaited<ReturnType<typeof platformOrganizationsRead>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationsRead>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationsRead<TData = Awaited<ReturnType<typeof platformOrganizationsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationsRead>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationsRead<TData = Awaited<ReturnType<typeof platformOrganizationsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformOrganizationsRead<TData = Awaited<ReturnType<typeof platformOrganizationsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformOrganizationsReadQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformOrganizationsUpdateResponse200 = {
  data: void
  status: 200
}

export type platformOrganizationsUpdateResponseSuccess = (platformOrganizationsUpdateResponse200) & {
  headers: Headers;
};
;

export type platformOrganizationsUpdateResponse = (platformOrganizationsUpdateResponseSuccess)

export const getPlatformOrganizationsUpdateUrl = (id: string,) => {




  return `/api/platform/organizations/${id}`
}

export const platformOrganizationsUpdate = async (id: string,
    updateOrgDto: UpdateOrgDto, options?: RequestInit): Promise<platformOrganizationsUpdateResponse> => {

  return customMutator<platformOrganizationsUpdateResponse>(getPlatformOrganizationsUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateOrgDto)
  }
);}





export const getPlatformOrganizationsUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationsUpdate>>, TError,{id: string;data: UpdateOrgDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationsUpdate>>, TError,{id: string;data: UpdateOrgDto}, TContext> => {

const mutationKey = ['platformOrganizationsUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationsUpdate>>, {id: string;data: UpdateOrgDto}> = (props) => {
          const {id,data} = props ?? {};

          return  platformOrganizationsUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationsUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationsUpdate>>>
    export type PlatformOrganizationsUpdateMutationBody = UpdateOrgDto
    export type PlatformOrganizationsUpdateMutationError = unknown

    export const usePlatformOrganizationsUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationsUpdate>>, TError,{id: string;data: UpdateOrgDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationsUpdate>>,
        TError,
        {id: string;data: UpdateOrgDto},
        TContext
      > => {
      return useMutation(getPlatformOrganizationsUpdateMutationOptions(options), queryClient);
    }

export type accessMembershipManageResponse200 = {
  data: void
  status: 200
}

export type accessMembershipManageResponse201 = {
  data: void
  status: 201
}

export type accessMembershipManageResponseSuccess = (accessMembershipManageResponse200 | accessMembershipManageResponse201) & {
  headers: Headers;
};
;

export type accessMembershipManageResponse = (accessMembershipManageResponseSuccess)

export const getAccessMembershipManageUrl = (id: string,) => {




  return `/api/platform/organizations/${id}/admins/initial`
}

export const accessMembershipManage = async (id: string,
    assignInitialOrgAdminDto: AssignInitialOrgAdminDto, options?: RequestInit): Promise<accessMembershipManageResponse> => {

  return customMutator<accessMembershipManageResponse>(getAccessMembershipManageUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(assignInitialOrgAdminDto)
  }
);}





export const getAccessMembershipManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accessMembershipManage>>, TError,{id: string;data: AssignInitialOrgAdminDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accessMembershipManage>>, TError,{id: string;data: AssignInitialOrgAdminDto}, TContext> => {

const mutationKey = ['accessMembershipManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accessMembershipManage>>, {id: string;data: AssignInitialOrgAdminDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accessMembershipManage(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccessMembershipManageMutationResult = NonNullable<Awaited<ReturnType<typeof accessMembershipManage>>>
    export type AccessMembershipManageMutationBody = AssignInitialOrgAdminDto
    export type AccessMembershipManageMutationError = unknown

    export const useAccessMembershipManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accessMembershipManage>>, TError,{id: string;data: AssignInitialOrgAdminDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accessMembershipManage>>,
        TError,
        {id: string;data: AssignInitialOrgAdminDto},
        TContext
      > => {
      return useMutation(getAccessMembershipManageMutationOptions(options), queryClient);
    }

export type platformLicenseListResponse200 = {
  data: PlatformLicenseList200
  status: 200
}

export type platformLicenseListResponseSuccess = (platformLicenseListResponse200) & {
  headers: Headers;
};
;

export type platformLicenseListResponse = (platformLicenseListResponseSuccess)

export const getPlatformLicenseListUrl = () => {




  return `/api/platform/module-licenses`
}

export const platformLicenseList = async ( options?: RequestInit): Promise<platformLicenseListResponse> => {

  return customMutator<platformLicenseListResponse>(getPlatformLicenseListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformLicenseListQueryKey = () => {
    return [
    `/api/platform/module-licenses`
    ] as const;
    }


export const getPlatformLicenseListQueryOptions = <TData = Awaited<ReturnType<typeof platformLicenseList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformLicenseList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformLicenseListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformLicenseList>>> = ({ signal }) => platformLicenseList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformLicenseList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformLicenseListQueryResult = NonNullable<Awaited<ReturnType<typeof platformLicenseList>>>
export type PlatformLicenseListQueryError = unknown


export function usePlatformLicenseList<TData = Awaited<ReturnType<typeof platformLicenseList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformLicenseList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformLicenseList>>,
          TError,
          Awaited<ReturnType<typeof platformLicenseList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformLicenseList<TData = Awaited<ReturnType<typeof platformLicenseList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformLicenseList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformLicenseList>>,
          TError,
          Awaited<ReturnType<typeof platformLicenseList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformLicenseList<TData = Awaited<ReturnType<typeof platformLicenseList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformLicenseList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformLicenseList<TData = Awaited<ReturnType<typeof platformLicenseList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformLicenseList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformLicenseListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformLicenseCreateResponse200 = {
  data: void
  status: 200
}

export type platformLicenseCreateResponse201 = {
  data: void
  status: 201
}

export type platformLicenseCreateResponseSuccess = (platformLicenseCreateResponse200 | platformLicenseCreateResponse201) & {
  headers: Headers;
};
;

export type platformLicenseCreateResponse = (platformLicenseCreateResponseSuccess)

export const getPlatformLicenseCreateUrl = () => {




  return `/api/platform/module-licenses`
}

export const platformLicenseCreate = async (createLicenseDto: CreateLicenseDto, options?: RequestInit): Promise<platformLicenseCreateResponse> => {

  return customMutator<platformLicenseCreateResponse>(getPlatformLicenseCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createLicenseDto)
  }
);}





export const getPlatformLicenseCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformLicenseCreate>>, TError,{data: CreateLicenseDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformLicenseCreate>>, TError,{data: CreateLicenseDto}, TContext> => {

const mutationKey = ['platformLicenseCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformLicenseCreate>>, {data: CreateLicenseDto}> = (props) => {
          const {data} = props ?? {};

          return  platformLicenseCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformLicenseCreateMutationResult = NonNullable<Awaited<ReturnType<typeof platformLicenseCreate>>>
    export type PlatformLicenseCreateMutationBody = CreateLicenseDto
    export type PlatformLicenseCreateMutationError = unknown

    export const usePlatformLicenseCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformLicenseCreate>>, TError,{data: CreateLicenseDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformLicenseCreate>>,
        TError,
        {data: CreateLicenseDto},
        TContext
      > => {
      return useMutation(getPlatformLicenseCreateMutationOptions(options), queryClient);
    }

export type platformModulesReadResponse200 = {
  data: PlatformModulesRead200
  status: 200
}

export type platformModulesReadResponseSuccess = (platformModulesReadResponse200) & {
  headers: Headers;
};
;

export type platformModulesReadResponse = (platformModulesReadResponseSuccess)

export const getPlatformModulesReadUrl = (organizationId: string,) => {




  return `/api/platform/organizations/${organizationId}/module-assignments`
}

export const platformModulesRead = async (organizationId: string, options?: RequestInit): Promise<platformModulesReadResponse> => {

  return customMutator<platformModulesReadResponse>(getPlatformModulesReadUrl(organizationId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformModulesReadQueryKey = (organizationId: string,) => {
    return [
    `/api/platform/organizations/${organizationId}/module-assignments`
    ] as const;
    }


export const getPlatformModulesReadQueryOptions = <TData = Awaited<ReturnType<typeof platformModulesRead>>, TError = unknown>(organizationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformModulesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformModulesReadQueryKey(organizationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformModulesRead>>> = ({ signal }) => platformModulesRead(organizationId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: organizationId !== null && organizationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformModulesRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformModulesReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformModulesRead>>>
export type PlatformModulesReadQueryError = unknown


export function usePlatformModulesRead<TData = Awaited<ReturnType<typeof platformModulesRead>>, TError = unknown>(
 organizationId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformModulesRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformModulesRead>>,
          TError,
          Awaited<ReturnType<typeof platformModulesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformModulesRead<TData = Awaited<ReturnType<typeof platformModulesRead>>, TError = unknown>(
 organizationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformModulesRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformModulesRead>>,
          TError,
          Awaited<ReturnType<typeof platformModulesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformModulesRead<TData = Awaited<ReturnType<typeof platformModulesRead>>, TError = unknown>(
 organizationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformModulesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformModulesRead<TData = Awaited<ReturnType<typeof platformModulesRead>>, TError = unknown>(
 organizationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformModulesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformModulesReadQueryOptions(organizationId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformModulesAssignToOrganizationResponse200 = {
  data: void
  status: 200
}

export type platformModulesAssignToOrganizationResponse201 = {
  data: void
  status: 201
}

export type platformModulesAssignToOrganizationResponseSuccess = (platformModulesAssignToOrganizationResponse200 | platformModulesAssignToOrganizationResponse201) & {
  headers: Headers;
};
;

export type platformModulesAssignToOrganizationResponse = (platformModulesAssignToOrganizationResponseSuccess)

export const getPlatformModulesAssignToOrganizationUrl = (organizationId: string,
    module: string,) => {




  return `/api/platform/organizations/${organizationId}/module-assignments/${module}`
}

export const platformModulesAssignToOrganization = async (organizationId: string,
    module: string, options?: RequestInit): Promise<platformModulesAssignToOrganizationResponse> => {

  return customMutator<platformModulesAssignToOrganizationResponse>(getPlatformModulesAssignToOrganizationUrl(organizationId,module),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformModulesAssignToOrganizationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformModulesAssignToOrganization>>, TError,{organizationId: string;module: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformModulesAssignToOrganization>>, TError,{organizationId: string;module: string}, TContext> => {

const mutationKey = ['platformModulesAssignToOrganization'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformModulesAssignToOrganization>>, {organizationId: string;module: string}> = (props) => {
          const {organizationId,module} = props ?? {};

          return  platformModulesAssignToOrganization(organizationId,module,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformModulesAssignToOrganizationMutationResult = NonNullable<Awaited<ReturnType<typeof platformModulesAssignToOrganization>>>

    export type PlatformModulesAssignToOrganizationMutationError = unknown

    export const usePlatformModulesAssignToOrganization = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformModulesAssignToOrganization>>, TError,{organizationId: string;module: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformModulesAssignToOrganization>>,
        TError,
        {organizationId: string;module: string},
        TContext
      > => {
      return useMutation(getPlatformModulesAssignToOrganizationMutationOptions(options), queryClient);
    }

export type platformModulesRevokeFromOrganizationResponse200 = {
  data: void
  status: 200
}

export type platformModulesRevokeFromOrganizationResponse201 = {
  data: void
  status: 201
}

export type platformModulesRevokeFromOrganizationResponseSuccess = (platformModulesRevokeFromOrganizationResponse200 | platformModulesRevokeFromOrganizationResponse201) & {
  headers: Headers;
};
;

export type platformModulesRevokeFromOrganizationResponse = (platformModulesRevokeFromOrganizationResponseSuccess)

export const getPlatformModulesRevokeFromOrganizationUrl = (organizationId: string,
    module: string,) => {




  return `/api/platform/organizations/${organizationId}/module-assignments/${module}/revoke`
}

export const platformModulesRevokeFromOrganization = async (organizationId: string,
    module: string, options?: RequestInit): Promise<platformModulesRevokeFromOrganizationResponse> => {

  return customMutator<platformModulesRevokeFromOrganizationResponse>(getPlatformModulesRevokeFromOrganizationUrl(organizationId,module),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformModulesRevokeFromOrganizationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformModulesRevokeFromOrganization>>, TError,{organizationId: string;module: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformModulesRevokeFromOrganization>>, TError,{organizationId: string;module: string}, TContext> => {

const mutationKey = ['platformModulesRevokeFromOrganization'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformModulesRevokeFromOrganization>>, {organizationId: string;module: string}> = (props) => {
          const {organizationId,module} = props ?? {};

          return  platformModulesRevokeFromOrganization(organizationId,module,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformModulesRevokeFromOrganizationMutationResult = NonNullable<Awaited<ReturnType<typeof platformModulesRevokeFromOrganization>>>

    export type PlatformModulesRevokeFromOrganizationMutationError = unknown

    export const usePlatformModulesRevokeFromOrganization = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformModulesRevokeFromOrganization>>, TError,{organizationId: string;module: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformModulesRevokeFromOrganization>>,
        TError,
        {organizationId: string;module: string},
        TContext
      > => {
      return useMutation(getPlatformModulesRevokeFromOrganizationMutationOptions(options), queryClient);
    }

export type organizationBranchesListResponse200 = {
  data: OrganizationBranchesList200
  status: 200
}

export type organizationBranchesListResponseSuccess = (organizationBranchesListResponse200) & {
  headers: Headers;
};
;

export type organizationBranchesListResponse = (organizationBranchesListResponseSuccess)

export const getOrganizationBranchesListUrl = () => {




  return `/api/organization/branches`
}

export const organizationBranchesList = async ( options?: RequestInit): Promise<organizationBranchesListResponse> => {

  return customMutator<organizationBranchesListResponse>(getOrganizationBranchesListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationBranchesListQueryKey = () => {
    return [
    `/api/organization/branches`
    ] as const;
    }


export const getOrganizationBranchesListQueryOptions = <TData = Awaited<ReturnType<typeof organizationBranchesList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationBranchesListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationBranchesList>>> = ({ signal }) => organizationBranchesList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationBranchesListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationBranchesList>>>
export type OrganizationBranchesListQueryError = unknown


export function useOrganizationBranchesList<TData = Awaited<ReturnType<typeof organizationBranchesList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationBranchesList>>,
          TError,
          Awaited<ReturnType<typeof organizationBranchesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationBranchesList<TData = Awaited<ReturnType<typeof organizationBranchesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationBranchesList>>,
          TError,
          Awaited<ReturnType<typeof organizationBranchesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationBranchesList<TData = Awaited<ReturnType<typeof organizationBranchesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationBranchesList<TData = Awaited<ReturnType<typeof organizationBranchesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationBranchesListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationBranchesCreateResponse200 = {
  data: void
  status: 200
}

export type organizationBranchesCreateResponse201 = {
  data: void
  status: 201
}

export type organizationBranchesCreateResponseSuccess = (organizationBranchesCreateResponse200 | organizationBranchesCreateResponse201) & {
  headers: Headers;
};
;

export type organizationBranchesCreateResponse = (organizationBranchesCreateResponseSuccess)

export const getOrganizationBranchesCreateUrl = () => {




  return `/api/organization/branches`
}

export const organizationBranchesCreate = async (createBranchDto: CreateBranchDto, options?: RequestInit): Promise<organizationBranchesCreateResponse> => {

  return customMutator<organizationBranchesCreateResponse>(getOrganizationBranchesCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createBranchDto)
  }
);}





export const getOrganizationBranchesCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBranchesCreate>>, TError,{data: CreateBranchDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationBranchesCreate>>, TError,{data: CreateBranchDto}, TContext> => {

const mutationKey = ['organizationBranchesCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationBranchesCreate>>, {data: CreateBranchDto}> = (props) => {
          const {data} = props ?? {};

          return  organizationBranchesCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationBranchesCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationBranchesCreate>>>
    export type OrganizationBranchesCreateMutationBody = CreateBranchDto
    export type OrganizationBranchesCreateMutationError = unknown

    export const useOrganizationBranchesCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBranchesCreate>>, TError,{data: CreateBranchDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationBranchesCreate>>,
        TError,
        {data: CreateBranchDto},
        TContext
      > => {
      return useMutation(getOrganizationBranchesCreateMutationOptions(options), queryClient);
    }

export type organizationBranchesReadResponse200 = {
  data: OrganizationBranchesRead200
  status: 200
}

export type organizationBranchesReadResponseSuccess = (organizationBranchesReadResponse200) & {
  headers: Headers;
};
;

export type organizationBranchesReadResponse = (organizationBranchesReadResponseSuccess)

export const getOrganizationBranchesReadUrl = (branchId: string,) => {




  return `/api/organization/branches/${branchId}`
}

export const organizationBranchesRead = async (branchId: string, options?: RequestInit): Promise<organizationBranchesReadResponse> => {

  return customMutator<organizationBranchesReadResponse>(getOrganizationBranchesReadUrl(branchId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationBranchesReadQueryKey = (branchId: string,) => {
    return [
    `/api/organization/branches/${branchId}`
    ] as const;
    }


export const getOrganizationBranchesReadQueryOptions = <TData = Awaited<ReturnType<typeof organizationBranchesRead>>, TError = unknown>(branchId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationBranchesReadQueryKey(branchId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationBranchesRead>>> = ({ signal }) => organizationBranchesRead(branchId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: branchId !== null && branchId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationBranchesReadQueryResult = NonNullable<Awaited<ReturnType<typeof organizationBranchesRead>>>
export type OrganizationBranchesReadQueryError = unknown


export function useOrganizationBranchesRead<TData = Awaited<ReturnType<typeof organizationBranchesRead>>, TError = unknown>(
 branchId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationBranchesRead>>,
          TError,
          Awaited<ReturnType<typeof organizationBranchesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationBranchesRead<TData = Awaited<ReturnType<typeof organizationBranchesRead>>, TError = unknown>(
 branchId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationBranchesRead>>,
          TError,
          Awaited<ReturnType<typeof organizationBranchesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationBranchesRead<TData = Awaited<ReturnType<typeof organizationBranchesRead>>, TError = unknown>(
 branchId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationBranchesRead<TData = Awaited<ReturnType<typeof organizationBranchesRead>>, TError = unknown>(
 branchId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBranchesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationBranchesReadQueryOptions(branchId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationBranchesUpdateResponse200 = {
  data: void
  status: 200
}

export type organizationBranchesUpdateResponseSuccess = (organizationBranchesUpdateResponse200) & {
  headers: Headers;
};
;

export type organizationBranchesUpdateResponse = (organizationBranchesUpdateResponseSuccess)

export const getOrganizationBranchesUpdateUrl = (branchId: string,) => {




  return `/api/organization/branches/${branchId}`
}

export const organizationBranchesUpdate = async (branchId: string,
    updateBranchDto: UpdateBranchDto, options?: RequestInit): Promise<organizationBranchesUpdateResponse> => {

  return customMutator<organizationBranchesUpdateResponse>(getOrganizationBranchesUpdateUrl(branchId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateBranchDto)
  }
);}





export const getOrganizationBranchesUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBranchesUpdate>>, TError,{branchId: string;data: UpdateBranchDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationBranchesUpdate>>, TError,{branchId: string;data: UpdateBranchDto}, TContext> => {

const mutationKey = ['organizationBranchesUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationBranchesUpdate>>, {branchId: string;data: UpdateBranchDto}> = (props) => {
          const {branchId,data} = props ?? {};

          return  organizationBranchesUpdate(branchId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationBranchesUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationBranchesUpdate>>>
    export type OrganizationBranchesUpdateMutationBody = UpdateBranchDto
    export type OrganizationBranchesUpdateMutationError = unknown

    export const useOrganizationBranchesUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBranchesUpdate>>, TError,{branchId: string;data: UpdateBranchDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationBranchesUpdate>>,
        TError,
        {branchId: string;data: UpdateBranchDto},
        TContext
      > => {
      return useMutation(getOrganizationBranchesUpdateMutationOptions(options), queryClient);
    }

export type organizationBranchesDisableResponse200 = {
  data: void
  status: 200
}

export type organizationBranchesDisableResponseSuccess = (organizationBranchesDisableResponse200) & {
  headers: Headers;
};
;

export type organizationBranchesDisableResponse = (organizationBranchesDisableResponseSuccess)

export const getOrganizationBranchesDisableUrl = (branchId: string,) => {




  return `/api/organization/branches/${branchId}`
}

export const organizationBranchesDisable = async (branchId: string, options?: RequestInit): Promise<organizationBranchesDisableResponse> => {

  return customMutator<organizationBranchesDisableResponse>(getOrganizationBranchesDisableUrl(branchId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getOrganizationBranchesDisableMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBranchesDisable>>, TError,{branchId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationBranchesDisable>>, TError,{branchId: string}, TContext> => {

const mutationKey = ['organizationBranchesDisable'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationBranchesDisable>>, {branchId: string}> = (props) => {
          const {branchId} = props ?? {};

          return  organizationBranchesDisable(branchId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationBranchesDisableMutationResult = NonNullable<Awaited<ReturnType<typeof organizationBranchesDisable>>>

    export type OrganizationBranchesDisableMutationError = unknown

    export const useOrganizationBranchesDisable = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBranchesDisable>>, TError,{branchId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationBranchesDisable>>,
        TError,
        {branchId: string},
        TContext
      > => {
      return useMutation(getOrganizationBranchesDisableMutationOptions(options), queryClient);
    }

export type organizationModulesListAssignedResponse200 = {
  data: void
  status: 200
}

export type organizationModulesListAssignedResponseSuccess = (organizationModulesListAssignedResponse200) & {
  headers: Headers;
};
;

export type organizationModulesListAssignedResponse = (organizationModulesListAssignedResponseSuccess)

export const getOrganizationModulesListAssignedUrl = () => {




  return `/api/organization/modules`
}

export const organizationModulesListAssigned = async ( options?: RequestInit): Promise<organizationModulesListAssignedResponse> => {

  return customMutator<organizationModulesListAssignedResponse>(getOrganizationModulesListAssignedUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationModulesListAssignedQueryKey = () => {
    return [
    `/api/organization/modules`
    ] as const;
    }


export const getOrganizationModulesListAssignedQueryOptions = <TData = Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationModulesListAssignedQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationModulesListAssigned>>> = ({ signal }) => organizationModulesListAssigned({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationModulesListAssignedQueryResult = NonNullable<Awaited<ReturnType<typeof organizationModulesListAssigned>>>
export type OrganizationModulesListAssignedQueryError = unknown


export function useOrganizationModulesListAssigned<TData = Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationModulesListAssigned>>,
          TError,
          Awaited<ReturnType<typeof organizationModulesListAssigned>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationModulesListAssigned<TData = Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationModulesListAssigned>>,
          TError,
          Awaited<ReturnType<typeof organizationModulesListAssigned>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationModulesListAssigned<TData = Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationModulesListAssigned<TData = Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssigned>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationModulesListAssignedQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationModulesListAssignedGetResponse200 = {
  data: void
  status: 200
}

export type organizationModulesListAssignedGetResponseSuccess = (organizationModulesListAssignedGetResponse200) & {
  headers: Headers;
};
;

export type organizationModulesListAssignedGetResponse = (organizationModulesListAssignedGetResponseSuccess)

export const getOrganizationModulesListAssignedGetUrl = () => {




  return `/api/organization/modules/options`
}

export const organizationModulesListAssignedGet = async ( options?: RequestInit): Promise<organizationModulesListAssignedGetResponse> => {

  return customMutator<organizationModulesListAssignedGetResponse>(getOrganizationModulesListAssignedGetUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationModulesListAssignedGetQueryKey = () => {
    return [
    `/api/organization/modules/options`
    ] as const;
    }


export const getOrganizationModulesListAssignedGetQueryOptions = <TData = Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationModulesListAssignedGetQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationModulesListAssignedGet>>> = ({ signal }) => organizationModulesListAssignedGet({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationModulesListAssignedGetQueryResult = NonNullable<Awaited<ReturnType<typeof organizationModulesListAssignedGet>>>
export type OrganizationModulesListAssignedGetQueryError = unknown


export function useOrganizationModulesListAssignedGet<TData = Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationModulesListAssignedGet>>,
          TError,
          Awaited<ReturnType<typeof organizationModulesListAssignedGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationModulesListAssignedGet<TData = Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationModulesListAssignedGet>>,
          TError,
          Awaited<ReturnType<typeof organizationModulesListAssignedGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationModulesListAssignedGet<TData = Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationModulesListAssignedGet<TData = Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationModulesListAssignedGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationModulesListAssignedGetQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationModulesEnableResponse200 = {
  data: void
  status: 200
}

export type organizationModulesEnableResponse201 = {
  data: void
  status: 201
}

export type organizationModulesEnableResponseSuccess = (organizationModulesEnableResponse200 | organizationModulesEnableResponse201) & {
  headers: Headers;
};
;

export type organizationModulesEnableResponse = (organizationModulesEnableResponseSuccess)

export const getOrganizationModulesEnableUrl = (module: string,) => {




  return `/api/organization/modules/${module}/enable`
}

export const organizationModulesEnable = async (module: string, options?: RequestInit): Promise<organizationModulesEnableResponse> => {

  return customMutator<organizationModulesEnableResponse>(getOrganizationModulesEnableUrl(module),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationModulesEnableMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationModulesEnable>>, TError,{module: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationModulesEnable>>, TError,{module: string}, TContext> => {

const mutationKey = ['organizationModulesEnable'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationModulesEnable>>, {module: string}> = (props) => {
          const {module} = props ?? {};

          return  organizationModulesEnable(module,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationModulesEnableMutationResult = NonNullable<Awaited<ReturnType<typeof organizationModulesEnable>>>

    export type OrganizationModulesEnableMutationError = unknown

    export const useOrganizationModulesEnable = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationModulesEnable>>, TError,{module: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationModulesEnable>>,
        TError,
        {module: string},
        TContext
      > => {
      return useMutation(getOrganizationModulesEnableMutationOptions(options), queryClient);
    }

export type organizationModulesDisableResponse200 = {
  data: void
  status: 200
}

export type organizationModulesDisableResponse201 = {
  data: void
  status: 201
}

export type organizationModulesDisableResponseSuccess = (organizationModulesDisableResponse200 | organizationModulesDisableResponse201) & {
  headers: Headers;
};
;

export type organizationModulesDisableResponse = (organizationModulesDisableResponseSuccess)

export const getOrganizationModulesDisableUrl = (module: string,) => {




  return `/api/organization/modules/${module}/disable`
}

export const organizationModulesDisable = async (module: string, options?: RequestInit): Promise<organizationModulesDisableResponse> => {

  return customMutator<organizationModulesDisableResponse>(getOrganizationModulesDisableUrl(module),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationModulesDisableMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationModulesDisable>>, TError,{module: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationModulesDisable>>, TError,{module: string}, TContext> => {

const mutationKey = ['organizationModulesDisable'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationModulesDisable>>, {module: string}> = (props) => {
          const {module} = props ?? {};

          return  organizationModulesDisable(module,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationModulesDisableMutationResult = NonNullable<Awaited<ReturnType<typeof organizationModulesDisable>>>

    export type OrganizationModulesDisableMutationError = unknown

    export const useOrganizationModulesDisable = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationModulesDisable>>, TError,{module: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationModulesDisable>>,
        TError,
        {module: string},
        TContext
      > => {
      return useMutation(getOrganizationModulesDisableMutationOptions(options), queryClient);
    }

export type organizationModulesConfigureResponse200 = {
  data: void
  status: 200
}

export type organizationModulesConfigureResponseSuccess = (organizationModulesConfigureResponse200) & {
  headers: Headers;
};
;

export type organizationModulesConfigureResponse = (organizationModulesConfigureResponseSuccess)

export const getOrganizationModulesConfigureUrl = (module: string,) => {




  return `/api/organization/modules/${module}/configuration`
}

export const organizationModulesConfigure = async (module: string,
    configureOrganizationModuleDto: ConfigureOrganizationModuleDto, options?: RequestInit): Promise<organizationModulesConfigureResponse> => {

  return customMutator<organizationModulesConfigureResponse>(getOrganizationModulesConfigureUrl(module),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(configureOrganizationModuleDto)
  }
);}





export const getOrganizationModulesConfigureMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationModulesConfigure>>, TError,{module: string;data: ConfigureOrganizationModuleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationModulesConfigure>>, TError,{module: string;data: ConfigureOrganizationModuleDto}, TContext> => {

const mutationKey = ['organizationModulesConfigure'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationModulesConfigure>>, {module: string;data: ConfigureOrganizationModuleDto}> = (props) => {
          const {module,data} = props ?? {};

          return  organizationModulesConfigure(module,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationModulesConfigureMutationResult = NonNullable<Awaited<ReturnType<typeof organizationModulesConfigure>>>
    export type OrganizationModulesConfigureMutationBody = ConfigureOrganizationModuleDto
    export type OrganizationModulesConfigureMutationError = unknown

    export const useOrganizationModulesConfigure = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationModulesConfigure>>, TError,{module: string;data: ConfigureOrganizationModuleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationModulesConfigure>>,
        TError,
        {module: string;data: ConfigureOrganizationModuleDto},
        TContext
      > => {
      return useMutation(getOrganizationModulesConfigureMutationOptions(options), queryClient);
    }

export type organizationClientsListResponse200 = {
  data: OrganizationClientsList200
  status: 200
}

export type organizationClientsListResponseSuccess = (organizationClientsListResponse200) & {
  headers: Headers;
};
;

export type organizationClientsListResponse = (organizationClientsListResponseSuccess)

export const getOrganizationClientsListUrl = () => {




  return `/api/organization/clients`
}

export const organizationClientsList = async ( options?: RequestInit): Promise<organizationClientsListResponse> => {

  return customMutator<organizationClientsListResponse>(getOrganizationClientsListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationClientsListQueryKey = () => {
    return [
    `/api/organization/clients`
    ] as const;
    }


export const getOrganizationClientsListQueryOptions = <TData = Awaited<ReturnType<typeof organizationClientsList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationClientsListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationClientsList>>> = ({ signal }) => organizationClientsList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationClientsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationClientsListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationClientsList>>>
export type OrganizationClientsListQueryError = unknown


export function useOrganizationClientsList<TData = Awaited<ReturnType<typeof organizationClientsList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientsList>>,
          TError,
          Awaited<ReturnType<typeof organizationClientsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientsList<TData = Awaited<ReturnType<typeof organizationClientsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientsList>>,
          TError,
          Awaited<ReturnType<typeof organizationClientsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientsList<TData = Awaited<ReturnType<typeof organizationClientsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationClientsList<TData = Awaited<ReturnType<typeof organizationClientsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationClientsListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationClientsCreateResponse200 = {
  data: void
  status: 200
}

export type organizationClientsCreateResponse201 = {
  data: void
  status: 201
}

export type organizationClientsCreateResponseSuccess = (organizationClientsCreateResponse200 | organizationClientsCreateResponse201) & {
  headers: Headers;
};
;

export type organizationClientsCreateResponse = (organizationClientsCreateResponseSuccess)

export const getOrganizationClientsCreateUrl = () => {




  return `/api/organization/clients`
}

export const organizationClientsCreate = async (createClientAccountDto: CreateClientAccountDto, options?: RequestInit): Promise<organizationClientsCreateResponse> => {

  return customMutator<organizationClientsCreateResponse>(getOrganizationClientsCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createClientAccountDto)
  }
);}





export const getOrganizationClientsCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsCreate>>, TError,{data: CreateClientAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientsCreate>>, TError,{data: CreateClientAccountDto}, TContext> => {

const mutationKey = ['organizationClientsCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientsCreate>>, {data: CreateClientAccountDto}> = (props) => {
          const {data} = props ?? {};

          return  organizationClientsCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientsCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientsCreate>>>
    export type OrganizationClientsCreateMutationBody = CreateClientAccountDto
    export type OrganizationClientsCreateMutationError = unknown

    export const useOrganizationClientsCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsCreate>>, TError,{data: CreateClientAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientsCreate>>,
        TError,
        {data: CreateClientAccountDto},
        TContext
      > => {
      return useMutation(getOrganizationClientsCreateMutationOptions(options), queryClient);
    }

export type organizationClientsReadResponse200 = {
  data: OrganizationClientsRead200
  status: 200
}

export type organizationClientsReadResponseSuccess = (organizationClientsReadResponse200) & {
  headers: Headers;
};
;

export type organizationClientsReadResponse = (organizationClientsReadResponseSuccess)

export const getOrganizationClientsReadUrl = (id: string,) => {




  return `/api/organization/clients/${id}`
}

export const organizationClientsRead = async (id: string, options?: RequestInit): Promise<organizationClientsReadResponse> => {

  return customMutator<organizationClientsReadResponse>(getOrganizationClientsReadUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationClientsReadQueryKey = (id: string,) => {
    return [
    `/api/organization/clients/${id}`
    ] as const;
    }


export const getOrganizationClientsReadQueryOptions = <TData = Awaited<ReturnType<typeof organizationClientsRead>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationClientsReadQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationClientsRead>>> = ({ signal }) => organizationClientsRead(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationClientsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationClientsReadQueryResult = NonNullable<Awaited<ReturnType<typeof organizationClientsRead>>>
export type OrganizationClientsReadQueryError = unknown


export function useOrganizationClientsRead<TData = Awaited<ReturnType<typeof organizationClientsRead>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientsRead>>,
          TError,
          Awaited<ReturnType<typeof organizationClientsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientsRead<TData = Awaited<ReturnType<typeof organizationClientsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientsRead>>,
          TError,
          Awaited<ReturnType<typeof organizationClientsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientsRead<TData = Awaited<ReturnType<typeof organizationClientsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationClientsRead<TData = Awaited<ReturnType<typeof organizationClientsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationClientsReadQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationClientsUpdateResponse200 = {
  data: void
  status: 200
}

export type organizationClientsUpdateResponseSuccess = (organizationClientsUpdateResponse200) & {
  headers: Headers;
};
;

export type organizationClientsUpdateResponse = (organizationClientsUpdateResponseSuccess)

export const getOrganizationClientsUpdateUrl = (id: string,) => {




  return `/api/organization/clients/${id}`
}

export const organizationClientsUpdate = async (id: string,
    updateClientAccountDto: UpdateClientAccountDto, options?: RequestInit): Promise<organizationClientsUpdateResponse> => {

  return customMutator<organizationClientsUpdateResponse>(getOrganizationClientsUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateClientAccountDto)
  }
);}





export const getOrganizationClientsUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdate>>, TError,{id: string;data: UpdateClientAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdate>>, TError,{id: string;data: UpdateClientAccountDto}, TContext> => {

const mutationKey = ['organizationClientsUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientsUpdate>>, {id: string;data: UpdateClientAccountDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationClientsUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientsUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientsUpdate>>>
    export type OrganizationClientsUpdateMutationBody = UpdateClientAccountDto
    export type OrganizationClientsUpdateMutationError = unknown

    export const useOrganizationClientsUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdate>>, TError,{id: string;data: UpdateClientAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientsUpdate>>,
        TError,
        {id: string;data: UpdateClientAccountDto},
        TContext
      > => {
      return useMutation(getOrganizationClientsUpdateMutationOptions(options), queryClient);
    }

export type organizationClientsReadGetResponse200 = {
  data: OrganizationClientsReadGet200
  status: 200
}

export type organizationClientsReadGetResponseSuccess = (organizationClientsReadGetResponse200) & {
  headers: Headers;
};
;

export type organizationClientsReadGetResponse = (organizationClientsReadGetResponseSuccess)

export const getOrganizationClientsReadGetUrl = (id: string,) => {




  return `/api/organization/clients/${id}/suitability`
}

export const organizationClientsReadGet = async (id: string, options?: RequestInit): Promise<organizationClientsReadGetResponse> => {

  return customMutator<organizationClientsReadGetResponse>(getOrganizationClientsReadGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationClientsReadGetQueryKey = (id: string,) => {
    return [
    `/api/organization/clients/${id}/suitability`
    ] as const;
    }


export const getOrganizationClientsReadGetQueryOptions = <TData = Awaited<ReturnType<typeof organizationClientsReadGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationClientsReadGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationClientsReadGet>>> = ({ signal }) => organizationClientsReadGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationClientsReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationClientsReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof organizationClientsReadGet>>>
export type OrganizationClientsReadGetQueryError = unknown


export function useOrganizationClientsReadGet<TData = Awaited<ReturnType<typeof organizationClientsReadGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientsReadGet>>,
          TError,
          Awaited<ReturnType<typeof organizationClientsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientsReadGet<TData = Awaited<ReturnType<typeof organizationClientsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientsReadGet>>,
          TError,
          Awaited<ReturnType<typeof organizationClientsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientsReadGet<TData = Awaited<ReturnType<typeof organizationClientsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationClientsReadGet<TData = Awaited<ReturnType<typeof organizationClientsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationClientsReadGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationClientsUpdatePostResponse200 = {
  data: void
  status: 200
}

export type organizationClientsUpdatePostResponse201 = {
  data: void
  status: 201
}

export type organizationClientsUpdatePostResponseSuccess = (organizationClientsUpdatePostResponse200 | organizationClientsUpdatePostResponse201) & {
  headers: Headers;
};
;

export type organizationClientsUpdatePostResponse = (organizationClientsUpdatePostResponseSuccess)

export const getOrganizationClientsUpdatePostUrl = (id: string,) => {




  return `/api/organization/clients/${id}/suitability`
}

export const organizationClientsUpdatePost = async (id: string,
    createClientSuitabilityProfileDto: CreateClientSuitabilityProfileDto, options?: RequestInit): Promise<organizationClientsUpdatePostResponse> => {

  return customMutator<organizationClientsUpdatePostResponse>(getOrganizationClientsUpdatePostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createClientSuitabilityProfileDto)
  }
);}





export const getOrganizationClientsUpdatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdatePost>>, TError,{id: string;data: CreateClientSuitabilityProfileDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdatePost>>, TError,{id: string;data: CreateClientSuitabilityProfileDto}, TContext> => {

const mutationKey = ['organizationClientsUpdatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientsUpdatePost>>, {id: string;data: CreateClientSuitabilityProfileDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationClientsUpdatePost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientsUpdatePostMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientsUpdatePost>>>
    export type OrganizationClientsUpdatePostMutationBody = CreateClientSuitabilityProfileDto
    export type OrganizationClientsUpdatePostMutationError = unknown

    export const useOrganizationClientsUpdatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdatePost>>, TError,{id: string;data: CreateClientSuitabilityProfileDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientsUpdatePost>>,
        TError,
        {id: string;data: CreateClientSuitabilityProfileDto},
        TContext
      > => {
      return useMutation(getOrganizationClientsUpdatePostMutationOptions(options), queryClient);
    }

export type organizationClientsUpdatePost1Response200 = {
  data: void
  status: 200
}

export type organizationClientsUpdatePost1Response201 = {
  data: void
  status: 201
}

export type organizationClientsUpdatePost1ResponseSuccess = (organizationClientsUpdatePost1Response200 | organizationClientsUpdatePost1Response201) & {
  headers: Headers;
};
;

export type organizationClientsUpdatePost1Response = (organizationClientsUpdatePost1ResponseSuccess)

export const getOrganizationClientsUpdatePost1Url = (id: string,
    profileId: string,) => {




  return `/api/organization/clients/${id}/suitability/${profileId}/approve`
}

export const organizationClientsUpdatePost1 = async (id: string,
    profileId: string,
    approveClientSuitabilityProfileDto: ApproveClientSuitabilityProfileDto, options?: RequestInit): Promise<organizationClientsUpdatePost1Response> => {

  return customMutator<organizationClientsUpdatePost1Response>(getOrganizationClientsUpdatePost1Url(id,profileId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(approveClientSuitabilityProfileDto)
  }
);}





export const getOrganizationClientsUpdatePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdatePost1>>, TError,{id: string;profileId: string;data: ApproveClientSuitabilityProfileDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdatePost1>>, TError,{id: string;profileId: string;data: ApproveClientSuitabilityProfileDto}, TContext> => {

const mutationKey = ['organizationClientsUpdatePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientsUpdatePost1>>, {id: string;profileId: string;data: ApproveClientSuitabilityProfileDto}> = (props) => {
          const {id,profileId,data} = props ?? {};

          return  organizationClientsUpdatePost1(id,profileId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientsUpdatePost1MutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientsUpdatePost1>>>
    export type OrganizationClientsUpdatePost1MutationBody = ApproveClientSuitabilityProfileDto
    export type OrganizationClientsUpdatePost1MutationError = unknown

    export const useOrganizationClientsUpdatePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdatePost1>>, TError,{id: string;profileId: string;data: ApproveClientSuitabilityProfileDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientsUpdatePost1>>,
        TError,
        {id: string;profileId: string;data: ApproveClientSuitabilityProfileDto},
        TContext
      > => {
      return useMutation(getOrganizationClientsUpdatePost1MutationOptions(options), queryClient);
    }

export type organizationClientsUpdatePost2Response200 = {
  data: void
  status: 200
}

export type organizationClientsUpdatePost2Response201 = {
  data: void
  status: 201
}

export type organizationClientsUpdatePost2ResponseSuccess = (organizationClientsUpdatePost2Response200 | organizationClientsUpdatePost2Response201) & {
  headers: Headers;
};
;

export type organizationClientsUpdatePost2Response = (organizationClientsUpdatePost2ResponseSuccess)

export const getOrganizationClientsUpdatePost2Url = (id: string,
    profileId: string,) => {




  return `/api/organization/clients/${id}/suitability/${profileId}/material-change`
}

export const organizationClientsUpdatePost2 = async (id: string,
    profileId: string,
    recordClientSuitabilityMaterialChangeDto: RecordClientSuitabilityMaterialChangeDto, options?: RequestInit): Promise<organizationClientsUpdatePost2Response> => {

  return customMutator<organizationClientsUpdatePost2Response>(getOrganizationClientsUpdatePost2Url(id,profileId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(recordClientSuitabilityMaterialChangeDto)
  }
);}





export const getOrganizationClientsUpdatePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdatePost2>>, TError,{id: string;profileId: string;data: RecordClientSuitabilityMaterialChangeDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdatePost2>>, TError,{id: string;profileId: string;data: RecordClientSuitabilityMaterialChangeDto}, TContext> => {

const mutationKey = ['organizationClientsUpdatePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientsUpdatePost2>>, {id: string;profileId: string;data: RecordClientSuitabilityMaterialChangeDto}> = (props) => {
          const {id,profileId,data} = props ?? {};

          return  organizationClientsUpdatePost2(id,profileId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientsUpdatePost2MutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientsUpdatePost2>>>
    export type OrganizationClientsUpdatePost2MutationBody = RecordClientSuitabilityMaterialChangeDto
    export type OrganizationClientsUpdatePost2MutationError = unknown

    export const useOrganizationClientsUpdatePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsUpdatePost2>>, TError,{id: string;profileId: string;data: RecordClientSuitabilityMaterialChangeDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientsUpdatePost2>>,
        TError,
        {id: string;profileId: string;data: RecordClientSuitabilityMaterialChangeDto},
        TContext
      > => {
      return useMutation(getOrganizationClientsUpdatePost2MutationOptions(options), queryClient);
    }

export type organizationClientsDisableResponse200 = {
  data: void
  status: 200
}

export type organizationClientsDisableResponse201 = {
  data: void
  status: 201
}

export type organizationClientsDisableResponseSuccess = (organizationClientsDisableResponse200 | organizationClientsDisableResponse201) & {
  headers: Headers;
};
;

export type organizationClientsDisableResponse = (organizationClientsDisableResponseSuccess)

export const getOrganizationClientsDisableUrl = (id: string,) => {




  return `/api/organization/clients/${id}/disable`
}

export const organizationClientsDisable = async (id: string, options?: RequestInit): Promise<organizationClientsDisableResponse> => {

  return customMutator<organizationClientsDisableResponse>(getOrganizationClientsDisableUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationClientsDisableMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientsDisable>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationClientsDisable'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientsDisable>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationClientsDisable(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientsDisableMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientsDisable>>>

    export type OrganizationClientsDisableMutationError = unknown

    export const useOrganizationClientsDisable = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientsDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientsDisable>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationClientsDisableMutationOptions(options), queryClient);
    }

export type organizationClientAccountsListResponse200 = {
  data: OrganizationClientAccountsList200
  status: 200
}

export type organizationClientAccountsListResponseSuccess = (organizationClientAccountsListResponse200) & {
  headers: Headers;
};
;

export type organizationClientAccountsListResponse = (organizationClientAccountsListResponseSuccess)

export const getOrganizationClientAccountsListUrl = () => {




  return `/api/organization/client-accounts`
}

export const organizationClientAccountsList = async ( options?: RequestInit): Promise<organizationClientAccountsListResponse> => {

  return customMutator<organizationClientAccountsListResponse>(getOrganizationClientAccountsListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationClientAccountsListQueryKey = () => {
    return [
    `/api/organization/client-accounts`
    ] as const;
    }


export const getOrganizationClientAccountsListQueryOptions = <TData = Awaited<ReturnType<typeof organizationClientAccountsList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationClientAccountsListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationClientAccountsList>>> = ({ signal }) => organizationClientAccountsList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationClientAccountsListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsList>>>
export type OrganizationClientAccountsListQueryError = unknown


export function useOrganizationClientAccountsList<TData = Awaited<ReturnType<typeof organizationClientAccountsList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientAccountsList>>,
          TError,
          Awaited<ReturnType<typeof organizationClientAccountsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientAccountsList<TData = Awaited<ReturnType<typeof organizationClientAccountsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientAccountsList>>,
          TError,
          Awaited<ReturnType<typeof organizationClientAccountsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientAccountsList<TData = Awaited<ReturnType<typeof organizationClientAccountsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationClientAccountsList<TData = Awaited<ReturnType<typeof organizationClientAccountsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationClientAccountsListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationClientAccountsCreateResponse200 = {
  data: void
  status: 200
}

export type organizationClientAccountsCreateResponse201 = {
  data: void
  status: 201
}

export type organizationClientAccountsCreateResponseSuccess = (organizationClientAccountsCreateResponse200 | organizationClientAccountsCreateResponse201) & {
  headers: Headers;
};
;

export type organizationClientAccountsCreateResponse = (organizationClientAccountsCreateResponseSuccess)

export const getOrganizationClientAccountsCreateUrl = () => {




  return `/api/organization/client-accounts`
}

export const organizationClientAccountsCreate = async (createOrganizationClientCustodyAccountDto: CreateOrganizationClientCustodyAccountDto, options?: RequestInit): Promise<organizationClientAccountsCreateResponse> => {

  return customMutator<organizationClientAccountsCreateResponse>(getOrganizationClientAccountsCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createOrganizationClientCustodyAccountDto)
  }
);}





export const getOrganizationClientAccountsCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsCreate>>, TError,{data: CreateOrganizationClientCustodyAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsCreate>>, TError,{data: CreateOrganizationClientCustodyAccountDto}, TContext> => {

const mutationKey = ['organizationClientAccountsCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientAccountsCreate>>, {data: CreateOrganizationClientCustodyAccountDto}> = (props) => {
          const {data} = props ?? {};

          return  organizationClientAccountsCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientAccountsCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsCreate>>>
    export type OrganizationClientAccountsCreateMutationBody = CreateOrganizationClientCustodyAccountDto
    export type OrganizationClientAccountsCreateMutationError = unknown

    export const useOrganizationClientAccountsCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsCreate>>, TError,{data: CreateOrganizationClientCustodyAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientAccountsCreate>>,
        TError,
        {data: CreateOrganizationClientCustodyAccountDto},
        TContext
      > => {
      return useMutation(getOrganizationClientAccountsCreateMutationOptions(options), queryClient);
    }

export type organizationClientAccountsReadResponse200 = {
  data: OrganizationClientAccountsRead200
  status: 200
}

export type organizationClientAccountsReadResponseSuccess = (organizationClientAccountsReadResponse200) & {
  headers: Headers;
};
;

export type organizationClientAccountsReadResponse = (organizationClientAccountsReadResponseSuccess)

export const getOrganizationClientAccountsReadUrl = (id: string,) => {




  return `/api/organization/client-accounts/${id}`
}

export const organizationClientAccountsRead = async (id: string, options?: RequestInit): Promise<organizationClientAccountsReadResponse> => {

  return customMutator<organizationClientAccountsReadResponse>(getOrganizationClientAccountsReadUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationClientAccountsReadQueryKey = (id: string,) => {
    return [
    `/api/organization/client-accounts/${id}`
    ] as const;
    }


export const getOrganizationClientAccountsReadQueryOptions = <TData = Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationClientAccountsReadQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationClientAccountsRead>>> = ({ signal }) => organizationClientAccountsRead(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationClientAccountsReadQueryResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsRead>>>
export type OrganizationClientAccountsReadQueryError = unknown


export function useOrganizationClientAccountsRead<TData = Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientAccountsRead>>,
          TError,
          Awaited<ReturnType<typeof organizationClientAccountsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientAccountsRead<TData = Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientAccountsRead>>,
          TError,
          Awaited<ReturnType<typeof organizationClientAccountsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientAccountsRead<TData = Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationClientAccountsRead<TData = Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationClientAccountsReadQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationClientAccountsUpdateResponse200 = {
  data: void
  status: 200
}

export type organizationClientAccountsUpdateResponseSuccess = (organizationClientAccountsUpdateResponse200) & {
  headers: Headers;
};
;

export type organizationClientAccountsUpdateResponse = (organizationClientAccountsUpdateResponseSuccess)

export const getOrganizationClientAccountsUpdateUrl = (id: string,) => {




  return `/api/organization/client-accounts/${id}`
}

export const organizationClientAccountsUpdate = async (id: string,
    updateOrganizationClientCustodyAccountDto: UpdateOrganizationClientCustodyAccountDto, options?: RequestInit): Promise<organizationClientAccountsUpdateResponse> => {

  return customMutator<organizationClientAccountsUpdateResponse>(getOrganizationClientAccountsUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateOrganizationClientCustodyAccountDto)
  }
);}





export const getOrganizationClientAccountsUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsUpdate>>, TError,{id: string;data: UpdateOrganizationClientCustodyAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsUpdate>>, TError,{id: string;data: UpdateOrganizationClientCustodyAccountDto}, TContext> => {

const mutationKey = ['organizationClientAccountsUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientAccountsUpdate>>, {id: string;data: UpdateOrganizationClientCustodyAccountDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationClientAccountsUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientAccountsUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsUpdate>>>
    export type OrganizationClientAccountsUpdateMutationBody = UpdateOrganizationClientCustodyAccountDto
    export type OrganizationClientAccountsUpdateMutationError = unknown

    export const useOrganizationClientAccountsUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsUpdate>>, TError,{id: string;data: UpdateOrganizationClientCustodyAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientAccountsUpdate>>,
        TError,
        {id: string;data: UpdateOrganizationClientCustodyAccountDto},
        TContext
      > => {
      return useMutation(getOrganizationClientAccountsUpdateMutationOptions(options), queryClient);
    }

export type organizationClientAccountsDocumentsListResponse200 = {
  data: OrganizationClientAccountsDocumentsList200
  status: 200
}

export type organizationClientAccountsDocumentsListResponseSuccess = (organizationClientAccountsDocumentsListResponse200) & {
  headers: Headers;
};
;

export type organizationClientAccountsDocumentsListResponse = (organizationClientAccountsDocumentsListResponseSuccess)

export const getOrganizationClientAccountsDocumentsListUrl = (id: string,) => {




  return `/api/organization/client-accounts/${id}/documents`
}

export const organizationClientAccountsDocumentsList = async (id: string, options?: RequestInit): Promise<organizationClientAccountsDocumentsListResponse> => {

  return customMutator<organizationClientAccountsDocumentsListResponse>(getOrganizationClientAccountsDocumentsListUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationClientAccountsDocumentsListQueryKey = (id: string,) => {
    return [
    `/api/organization/client-accounts/${id}/documents`
    ] as const;
    }


export const getOrganizationClientAccountsDocumentsListQueryOptions = <TData = Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationClientAccountsDocumentsListQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>> = ({ signal }) => organizationClientAccountsDocumentsList(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationClientAccountsDocumentsListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>>
export type OrganizationClientAccountsDocumentsListQueryError = unknown


export function useOrganizationClientAccountsDocumentsList<TData = Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>,
          TError,
          Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientAccountsDocumentsList<TData = Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>,
          TError,
          Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientAccountsDocumentsList<TData = Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationClientAccountsDocumentsList<TData = Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationClientAccountsDocumentsListQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationClientAccountsDocumentsCreateResponse200 = {
  data: void
  status: 200
}

export type organizationClientAccountsDocumentsCreateResponse201 = {
  data: OrganizationClientAccountsDocumentsCreate201
  status: 201
}

export type organizationClientAccountsDocumentsCreateResponseSuccess = (organizationClientAccountsDocumentsCreateResponse200 | organizationClientAccountsDocumentsCreateResponse201) & {
  headers: Headers;
};
;

export type organizationClientAccountsDocumentsCreateResponse = (organizationClientAccountsDocumentsCreateResponseSuccess)

export const getOrganizationClientAccountsDocumentsCreateUrl = (id: string,) => {




  return `/api/organization/client-accounts/${id}/documents`
}

export const organizationClientAccountsDocumentsCreate = async (id: string,
    createOrganizationClientCustodyAccountDocumentDto: CreateOrganizationClientCustodyAccountDocumentDto, options?: RequestInit): Promise<organizationClientAccountsDocumentsCreateResponse> => {

  return customMutator<organizationClientAccountsDocumentsCreateResponse>(getOrganizationClientAccountsDocumentsCreateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createOrganizationClientCustodyAccountDocumentDto)
  }
);}





export const getOrganizationClientAccountsDocumentsCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsCreate>>, TError,{id: string;data: CreateOrganizationClientCustodyAccountDocumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsCreate>>, TError,{id: string;data: CreateOrganizationClientCustodyAccountDocumentDto}, TContext> => {

const mutationKey = ['organizationClientAccountsDocumentsCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientAccountsDocumentsCreate>>, {id: string;data: CreateOrganizationClientCustodyAccountDocumentDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationClientAccountsDocumentsCreate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientAccountsDocumentsCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsDocumentsCreate>>>
    export type OrganizationClientAccountsDocumentsCreateMutationBody = CreateOrganizationClientCustodyAccountDocumentDto
    export type OrganizationClientAccountsDocumentsCreateMutationError = unknown

    export const useOrganizationClientAccountsDocumentsCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsCreate>>, TError,{id: string;data: CreateOrganizationClientCustodyAccountDocumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientAccountsDocumentsCreate>>,
        TError,
        {id: string;data: CreateOrganizationClientCustodyAccountDocumentDto},
        TContext
      > => {
      return useMutation(getOrganizationClientAccountsDocumentsCreateMutationOptions(options), queryClient);
    }

export type organizationClientAccountsDocumentsRemoveResponse200 = {
  data: void
  status: 200
}

export type organizationClientAccountsDocumentsRemoveResponseSuccess = (organizationClientAccountsDocumentsRemoveResponse200) & {
  headers: Headers;
};
;

export type organizationClientAccountsDocumentsRemoveResponse = (organizationClientAccountsDocumentsRemoveResponseSuccess)

export const getOrganizationClientAccountsDocumentsRemoveUrl = (id: string,
    documentId: string,) => {




  return `/api/organization/client-accounts/${id}/documents/${documentId}`
}

export const organizationClientAccountsDocumentsRemove = async (id: string,
    documentId: string, options?: RequestInit): Promise<organizationClientAccountsDocumentsRemoveResponse> => {

  return customMutator<organizationClientAccountsDocumentsRemoveResponse>(getOrganizationClientAccountsDocumentsRemoveUrl(id,documentId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getOrganizationClientAccountsDocumentsRemoveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemove>>, TError,{id: string;documentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemove>>, TError,{id: string;documentId: string}, TContext> => {

const mutationKey = ['organizationClientAccountsDocumentsRemove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemove>>, {id: string;documentId: string}> = (props) => {
          const {id,documentId} = props ?? {};

          return  organizationClientAccountsDocumentsRemove(id,documentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientAccountsDocumentsRemoveMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemove>>>

    export type OrganizationClientAccountsDocumentsRemoveMutationError = unknown

    export const useOrganizationClientAccountsDocumentsRemove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemove>>, TError,{id: string;documentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemove>>,
        TError,
        {id: string;documentId: string},
        TContext
      > => {
      return useMutation(getOrganizationClientAccountsDocumentsRemoveMutationOptions(options), queryClient);
    }

export type organizationClientAccountsSignatoriesListResponse200 = {
  data: OrganizationClientAccountsSignatoriesList200
  status: 200
}

export type organizationClientAccountsSignatoriesListResponseSuccess = (organizationClientAccountsSignatoriesListResponse200) & {
  headers: Headers;
};
;

export type organizationClientAccountsSignatoriesListResponse = (organizationClientAccountsSignatoriesListResponseSuccess)

export const getOrganizationClientAccountsSignatoriesListUrl = (id: string,) => {




  return `/api/organization/client-accounts/${id}/signatories`
}

export const organizationClientAccountsSignatoriesList = async (id: string, options?: RequestInit): Promise<organizationClientAccountsSignatoriesListResponse> => {

  return customMutator<organizationClientAccountsSignatoriesListResponse>(getOrganizationClientAccountsSignatoriesListUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationClientAccountsSignatoriesListQueryKey = (id: string,) => {
    return [
    `/api/organization/client-accounts/${id}/signatories`
    ] as const;
    }


export const getOrganizationClientAccountsSignatoriesListQueryOptions = <TData = Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationClientAccountsSignatoriesListQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>> = ({ signal }) => organizationClientAccountsSignatoriesList(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationClientAccountsSignatoriesListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>>
export type OrganizationClientAccountsSignatoriesListQueryError = unknown


export function useOrganizationClientAccountsSignatoriesList<TData = Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>,
          TError,
          Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientAccountsSignatoriesList<TData = Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>,
          TError,
          Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationClientAccountsSignatoriesList<TData = Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationClientAccountsSignatoriesList<TData = Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationClientAccountsSignatoriesListQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationClientAccountsSignatoriesCreateResponse200 = {
  data: void
  status: 200
}

export type organizationClientAccountsSignatoriesCreateResponse201 = {
  data: OrganizationClientAccountsSignatoriesCreate201
  status: 201
}

export type organizationClientAccountsSignatoriesCreateResponseSuccess = (organizationClientAccountsSignatoriesCreateResponse200 | organizationClientAccountsSignatoriesCreateResponse201) & {
  headers: Headers;
};
;

export type organizationClientAccountsSignatoriesCreateResponse = (organizationClientAccountsSignatoriesCreateResponseSuccess)

export const getOrganizationClientAccountsSignatoriesCreateUrl = (id: string,) => {




  return `/api/organization/client-accounts/${id}/signatories`
}

export const organizationClientAccountsSignatoriesCreate = async (id: string,
    createOrganizationClientCustodyAccountSignatoryDto: CreateOrganizationClientCustodyAccountSignatoryDto, options?: RequestInit): Promise<organizationClientAccountsSignatoriesCreateResponse> => {

  return customMutator<organizationClientAccountsSignatoriesCreateResponse>(getOrganizationClientAccountsSignatoriesCreateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createOrganizationClientCustodyAccountSignatoryDto)
  }
);}





export const getOrganizationClientAccountsSignatoriesCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesCreate>>, TError,{id: string;data: CreateOrganizationClientCustodyAccountSignatoryDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesCreate>>, TError,{id: string;data: CreateOrganizationClientCustodyAccountSignatoryDto}, TContext> => {

const mutationKey = ['organizationClientAccountsSignatoriesCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesCreate>>, {id: string;data: CreateOrganizationClientCustodyAccountSignatoryDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationClientAccountsSignatoriesCreate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientAccountsSignatoriesCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesCreate>>>
    export type OrganizationClientAccountsSignatoriesCreateMutationBody = CreateOrganizationClientCustodyAccountSignatoryDto
    export type OrganizationClientAccountsSignatoriesCreateMutationError = unknown

    export const useOrganizationClientAccountsSignatoriesCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesCreate>>, TError,{id: string;data: CreateOrganizationClientCustodyAccountSignatoryDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientAccountsSignatoriesCreate>>,
        TError,
        {id: string;data: CreateOrganizationClientCustodyAccountSignatoryDto},
        TContext
      > => {
      return useMutation(getOrganizationClientAccountsSignatoriesCreateMutationOptions(options), queryClient);
    }

export type organizationClientAccountsSignatoriesRemoveResponse200 = {
  data: void
  status: 200
}

export type organizationClientAccountsSignatoriesRemoveResponseSuccess = (organizationClientAccountsSignatoriesRemoveResponse200) & {
  headers: Headers;
};
;

export type organizationClientAccountsSignatoriesRemoveResponse = (organizationClientAccountsSignatoriesRemoveResponseSuccess)

export const getOrganizationClientAccountsSignatoriesRemoveUrl = (id: string,
    signatoryId: string,) => {




  return `/api/organization/client-accounts/${id}/signatories/${signatoryId}`
}

export const organizationClientAccountsSignatoriesRemove = async (id: string,
    signatoryId: string, options?: RequestInit): Promise<organizationClientAccountsSignatoriesRemoveResponse> => {

  return customMutator<organizationClientAccountsSignatoriesRemoveResponse>(getOrganizationClientAccountsSignatoriesRemoveUrl(id,signatoryId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getOrganizationClientAccountsSignatoriesRemoveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesRemove>>, TError,{id: string;signatoryId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesRemove>>, TError,{id: string;signatoryId: string}, TContext> => {

const mutationKey = ['organizationClientAccountsSignatoriesRemove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesRemove>>, {id: string;signatoryId: string}> = (props) => {
          const {id,signatoryId} = props ?? {};

          return  organizationClientAccountsSignatoriesRemove(id,signatoryId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientAccountsSignatoriesRemoveMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesRemove>>>

    export type OrganizationClientAccountsSignatoriesRemoveMutationError = unknown

    export const useOrganizationClientAccountsSignatoriesRemove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsSignatoriesRemove>>, TError,{id: string;signatoryId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientAccountsSignatoriesRemove>>,
        TError,
        {id: string;signatoryId: string},
        TContext
      > => {
      return useMutation(getOrganizationClientAccountsSignatoriesRemoveMutationOptions(options), queryClient);
    }

export type organizationPortfoliosListResponse200 = {
  data: OrganizationPortfoliosList200
  status: 200
}

export type organizationPortfoliosListResponseSuccess = (organizationPortfoliosListResponse200) & {
  headers: Headers;
};
;

export type organizationPortfoliosListResponse = (organizationPortfoliosListResponseSuccess)

export const getOrganizationPortfoliosListUrl = () => {




  return `/api/organization/portfolios`
}

export const organizationPortfoliosList = async ( options?: RequestInit): Promise<organizationPortfoliosListResponse> => {

  return customMutator<organizationPortfoliosListResponse>(getOrganizationPortfoliosListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationPortfoliosListQueryKey = () => {
    return [
    `/api/organization/portfolios`
    ] as const;
    }


export const getOrganizationPortfoliosListQueryOptions = <TData = Awaited<ReturnType<typeof organizationPortfoliosList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationPortfoliosListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationPortfoliosList>>> = ({ signal }) => organizationPortfoliosList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationPortfoliosListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosList>>>
export type OrganizationPortfoliosListQueryError = unknown


export function useOrganizationPortfoliosList<TData = Awaited<ReturnType<typeof organizationPortfoliosList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosList>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosList<TData = Awaited<ReturnType<typeof organizationPortfoliosList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosList>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosList<TData = Awaited<ReturnType<typeof organizationPortfoliosList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationPortfoliosList<TData = Awaited<ReturnType<typeof organizationPortfoliosList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationPortfoliosListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationPortfoliosCreateResponse200 = {
  data: void
  status: 200
}

export type organizationPortfoliosCreateResponse201 = {
  data: void
  status: 201
}

export type organizationPortfoliosCreateResponseSuccess = (organizationPortfoliosCreateResponse200 | organizationPortfoliosCreateResponse201) & {
  headers: Headers;
};
;

export type organizationPortfoliosCreateResponse = (organizationPortfoliosCreateResponseSuccess)

export const getOrganizationPortfoliosCreateUrl = () => {




  return `/api/organization/portfolios`
}

export const organizationPortfoliosCreate = async (createOrganizationManagedPortfolioDto: CreateOrganizationManagedPortfolioDto, options?: RequestInit): Promise<organizationPortfoliosCreateResponse> => {

  return customMutator<organizationPortfoliosCreateResponse>(getOrganizationPortfoliosCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createOrganizationManagedPortfolioDto)
  }
);}





export const getOrganizationPortfoliosCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreate>>, TError,{data: CreateOrganizationManagedPortfolioDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreate>>, TError,{data: CreateOrganizationManagedPortfolioDto}, TContext> => {

const mutationKey = ['organizationPortfoliosCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosCreate>>, {data: CreateOrganizationManagedPortfolioDto}> = (props) => {
          const {data} = props ?? {};

          return  organizationPortfoliosCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosCreate>>>
    export type OrganizationPortfoliosCreateMutationBody = CreateOrganizationManagedPortfolioDto
    export type OrganizationPortfoliosCreateMutationError = unknown

    export const useOrganizationPortfoliosCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreate>>, TError,{data: CreateOrganizationManagedPortfolioDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosCreate>>,
        TError,
        {data: CreateOrganizationManagedPortfolioDto},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosCreateMutationOptions(options), queryClient);
    }

export type organizationBenchmarkAssignmentsListResponse200 = {
  data: OrganizationBenchmarkAssignmentsList200
  status: 200
}

export type organizationBenchmarkAssignmentsListResponseSuccess = (organizationBenchmarkAssignmentsListResponse200) & {
  headers: Headers;
};
;

export type organizationBenchmarkAssignmentsListResponse = (organizationBenchmarkAssignmentsListResponseSuccess)

export const getOrganizationBenchmarkAssignmentsListUrl = (id: string,) => {




  return `/api/organization/portfolios/${id}`
}

export const organizationBenchmarkAssignmentsList = async (id: string, options?: RequestInit): Promise<organizationBenchmarkAssignmentsListResponse> => {

  return customMutator<organizationBenchmarkAssignmentsListResponse>(getOrganizationBenchmarkAssignmentsListUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationBenchmarkAssignmentsListQueryKey = (id: string,) => {
    return [
    `/api/organization/portfolios/${id}`
    ] as const;
    }


export const getOrganizationBenchmarkAssignmentsListQueryOptions = <TData = Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationBenchmarkAssignmentsListQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>> = ({ signal }) => organizationBenchmarkAssignmentsList(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationBenchmarkAssignmentsListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>>
export type OrganizationBenchmarkAssignmentsListQueryError = unknown


export function useOrganizationBenchmarkAssignmentsList<TData = Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>,
          TError,
          Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationBenchmarkAssignmentsList<TData = Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>,
          TError,
          Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationBenchmarkAssignmentsList<TData = Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationBenchmarkAssignmentsList<TData = Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationBenchmarkAssignmentsListQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationPortfoliosUpdateResponse200 = {
  data: void
  status: 200
}

export type organizationPortfoliosUpdateResponseSuccess = (organizationPortfoliosUpdateResponse200) & {
  headers: Headers;
};
;

export type organizationPortfoliosUpdateResponse = (organizationPortfoliosUpdateResponseSuccess)

export const getOrganizationPortfoliosUpdateUrl = (id: string,) => {




  return `/api/organization/portfolios/${id}`
}

export const organizationPortfoliosUpdate = async (id: string,
    updateOrganizationManagedPortfolioDto: UpdateOrganizationManagedPortfolioDto, options?: RequestInit): Promise<organizationPortfoliosUpdateResponse> => {

  return customMutator<organizationPortfoliosUpdateResponse>(getOrganizationPortfoliosUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateOrganizationManagedPortfolioDto)
  }
);}





export const getOrganizationPortfoliosUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdate>>, TError,{id: string;data: UpdateOrganizationManagedPortfolioDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdate>>, TError,{id: string;data: UpdateOrganizationManagedPortfolioDto}, TContext> => {

const mutationKey = ['organizationPortfoliosUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosUpdate>>, {id: string;data: UpdateOrganizationManagedPortfolioDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationPortfoliosUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosUpdate>>>
    export type OrganizationPortfoliosUpdateMutationBody = UpdateOrganizationManagedPortfolioDto
    export type OrganizationPortfoliosUpdateMutationError = unknown

    export const useOrganizationPortfoliosUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdate>>, TError,{id: string;data: UpdateOrganizationManagedPortfolioDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosUpdate>>,
        TError,
        {id: string;data: UpdateOrganizationManagedPortfolioDto},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosUpdateMutationOptions(options), queryClient);
    }

export type organizationPortfoliosReadResponse200 = {
  data: OrganizationPortfoliosRead200
  status: 200
}

export type organizationPortfoliosReadResponseSuccess = (organizationPortfoliosReadResponse200) & {
  headers: Headers;
};
;

export type organizationPortfoliosReadResponse = (organizationPortfoliosReadResponseSuccess)

export const getOrganizationPortfoliosReadUrl = (id: string,) => {




  return `/api/organization/portfolios/${id}/mandates`
}

export const organizationPortfoliosRead = async (id: string, options?: RequestInit): Promise<organizationPortfoliosReadResponse> => {

  return customMutator<organizationPortfoliosReadResponse>(getOrganizationPortfoliosReadUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationPortfoliosReadQueryKey = (id: string,) => {
    return [
    `/api/organization/portfolios/${id}/mandates`
    ] as const;
    }


export const getOrganizationPortfoliosReadQueryOptions = <TData = Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationPortfoliosReadQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationPortfoliosRead>>> = ({ signal }) => organizationPortfoliosRead(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationPortfoliosReadQueryResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosRead>>>
export type OrganizationPortfoliosReadQueryError = unknown


export function useOrganizationPortfoliosRead<TData = Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosRead>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosRead<TData = Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosRead>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosRead<TData = Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationPortfoliosRead<TData = Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationPortfoliosReadQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationBenchmarkAssignmentsCreateResponse200 = {
  data: void
  status: 200
}

export type organizationBenchmarkAssignmentsCreateResponse201 = {
  data: void
  status: 201
}

export type organizationBenchmarkAssignmentsCreateResponseSuccess = (organizationBenchmarkAssignmentsCreateResponse200 | organizationBenchmarkAssignmentsCreateResponse201) & {
  headers: Headers;
};
;

export type organizationBenchmarkAssignmentsCreateResponse = (organizationBenchmarkAssignmentsCreateResponseSuccess)

export const getOrganizationBenchmarkAssignmentsCreateUrl = (id: string,) => {




  return `/api/organization/portfolios/${id}/mandates`
}

export const organizationBenchmarkAssignmentsCreate = async (id: string,
    createOrganizationManagedPortfolioMandateDto: CreateOrganizationManagedPortfolioMandateDto, options?: RequestInit): Promise<organizationBenchmarkAssignmentsCreateResponse> => {

  return customMutator<organizationBenchmarkAssignmentsCreateResponse>(getOrganizationBenchmarkAssignmentsCreateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createOrganizationManagedPortfolioMandateDto)
  }
);}





export const getOrganizationBenchmarkAssignmentsCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsCreate>>, TError,{id: string;data: CreateOrganizationManagedPortfolioMandateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsCreate>>, TError,{id: string;data: CreateOrganizationManagedPortfolioMandateDto}, TContext> => {

const mutationKey = ['organizationBenchmarkAssignmentsCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsCreate>>, {id: string;data: CreateOrganizationManagedPortfolioMandateDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationBenchmarkAssignmentsCreate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationBenchmarkAssignmentsCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsCreate>>>
    export type OrganizationBenchmarkAssignmentsCreateMutationBody = CreateOrganizationManagedPortfolioMandateDto
    export type OrganizationBenchmarkAssignmentsCreateMutationError = unknown

    export const useOrganizationBenchmarkAssignmentsCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsCreate>>, TError,{id: string;data: CreateOrganizationManagedPortfolioMandateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationBenchmarkAssignmentsCreate>>,
        TError,
        {id: string;data: CreateOrganizationManagedPortfolioMandateDto},
        TContext
      > => {
      return useMutation(getOrganizationBenchmarkAssignmentsCreateMutationOptions(options), queryClient);
    }

export type organizationBenchmarkAssignmentsApproveResponse200 = {
  data: void
  status: 200
}

export type organizationBenchmarkAssignmentsApproveResponse201 = {
  data: void
  status: 201
}

export type organizationBenchmarkAssignmentsApproveResponseSuccess = (organizationBenchmarkAssignmentsApproveResponse200 | organizationBenchmarkAssignmentsApproveResponse201) & {
  headers: Headers;
};
;

export type organizationBenchmarkAssignmentsApproveResponse = (organizationBenchmarkAssignmentsApproveResponseSuccess)

export const getOrganizationBenchmarkAssignmentsApproveUrl = (id: string,
    mandateId: string,) => {




  return `/api/organization/portfolios/${id}/mandates/${mandateId}/approve`
}

export const organizationBenchmarkAssignmentsApprove = async (id: string,
    mandateId: string,
    approveOrganizationManagedPortfolioMandateDto: ApproveOrganizationManagedPortfolioMandateDto, options?: RequestInit): Promise<organizationBenchmarkAssignmentsApproveResponse> => {

  return customMutator<organizationBenchmarkAssignmentsApproveResponse>(getOrganizationBenchmarkAssignmentsApproveUrl(id,mandateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(approveOrganizationManagedPortfolioMandateDto)
  }
);}





export const getOrganizationBenchmarkAssignmentsApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsApprove>>, TError,{id: string;mandateId: string;data: ApproveOrganizationManagedPortfolioMandateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsApprove>>, TError,{id: string;mandateId: string;data: ApproveOrganizationManagedPortfolioMandateDto}, TContext> => {

const mutationKey = ['organizationBenchmarkAssignmentsApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsApprove>>, {id: string;mandateId: string;data: ApproveOrganizationManagedPortfolioMandateDto}> = (props) => {
          const {id,mandateId,data} = props ?? {};

          return  organizationBenchmarkAssignmentsApprove(id,mandateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationBenchmarkAssignmentsApproveMutationResult = NonNullable<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsApprove>>>
    export type OrganizationBenchmarkAssignmentsApproveMutationBody = ApproveOrganizationManagedPortfolioMandateDto
    export type OrganizationBenchmarkAssignmentsApproveMutationError = unknown

    export const useOrganizationBenchmarkAssignmentsApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationBenchmarkAssignmentsApprove>>, TError,{id: string;mandateId: string;data: ApproveOrganizationManagedPortfolioMandateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationBenchmarkAssignmentsApprove>>,
        TError,
        {id: string;mandateId: string;data: ApproveOrganizationManagedPortfolioMandateDto},
        TContext
      > => {
      return useMutation(getOrganizationBenchmarkAssignmentsApproveMutationOptions(options), queryClient);
    }

export type organizationPortfoliosUpdatePostResponse200 = {
  data: void
  status: 200
}

export type organizationPortfoliosUpdatePostResponse201 = {
  data: void
  status: 201
}

export type organizationPortfoliosUpdatePostResponseSuccess = (organizationPortfoliosUpdatePostResponse200 | organizationPortfoliosUpdatePostResponse201) & {
  headers: Headers;
};
;

export type organizationPortfoliosUpdatePostResponse = (organizationPortfoliosUpdatePostResponseSuccess)

export const getOrganizationPortfoliosUpdatePostUrl = (id: string,
    mandateId: string,) => {




  return `/api/organization/portfolios/${id}/mandates/${mandateId}/suspend`
}

export const organizationPortfoliosUpdatePost = async (id: string,
    mandateId: string,
    suspendOrganizationManagedPortfolioMandateDto: SuspendOrganizationManagedPortfolioMandateDto, options?: RequestInit): Promise<organizationPortfoliosUpdatePostResponse> => {

  return customMutator<organizationPortfoliosUpdatePostResponse>(getOrganizationPortfoliosUpdatePostUrl(id,mandateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(suspendOrganizationManagedPortfolioMandateDto)
  }
);}





export const getOrganizationPortfoliosUpdatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost>>, TError,{id: string;mandateId: string;data: SuspendOrganizationManagedPortfolioMandateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost>>, TError,{id: string;mandateId: string;data: SuspendOrganizationManagedPortfolioMandateDto}, TContext> => {

const mutationKey = ['organizationPortfoliosUpdatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost>>, {id: string;mandateId: string;data: SuspendOrganizationManagedPortfolioMandateDto}> = (props) => {
          const {id,mandateId,data} = props ?? {};

          return  organizationPortfoliosUpdatePost(id,mandateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosUpdatePostMutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost>>>
    export type OrganizationPortfoliosUpdatePostMutationBody = SuspendOrganizationManagedPortfolioMandateDto
    export type OrganizationPortfoliosUpdatePostMutationError = unknown

    export const useOrganizationPortfoliosUpdatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost>>, TError,{id: string;mandateId: string;data: SuspendOrganizationManagedPortfolioMandateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosUpdatePost>>,
        TError,
        {id: string;mandateId: string;data: SuspendOrganizationManagedPortfolioMandateDto},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosUpdatePostMutationOptions(options), queryClient);
    }

export type organizationPortfoliosReadGetResponse200 = {
  data: void
  status: 200
}

export type organizationPortfoliosReadGetResponseSuccess = (organizationPortfoliosReadGetResponse200) & {
  headers: Headers;
};
;

export type organizationPortfoliosReadGetResponse = (organizationPortfoliosReadGetResponseSuccess)

export const getOrganizationPortfoliosReadGetUrl = (id: string,) => {




  return `/api/organization/portfolios/${id}/mandates/report`
}

export const organizationPortfoliosReadGet = async (id: string, options?: RequestInit): Promise<organizationPortfoliosReadGetResponse> => {

  return customMutator<organizationPortfoliosReadGetResponse>(getOrganizationPortfoliosReadGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationPortfoliosReadGetQueryKey = (id: string,) => {
    return [
    `/api/organization/portfolios/${id}/mandates/report`
    ] as const;
    }


export const getOrganizationPortfoliosReadGetQueryOptions = <TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationPortfoliosReadGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationPortfoliosReadGet>>> = ({ signal }) => organizationPortfoliosReadGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationPortfoliosReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosReadGet>>>
export type OrganizationPortfoliosReadGetQueryError = unknown


export function useOrganizationPortfoliosReadGet<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosReadGet>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosReadGet<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosReadGet>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosReadGet<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationPortfoliosReadGet<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationPortfoliosReadGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationPortfoliosReadGet1Response200 = {
  data: void
  status: 200
}

export type organizationPortfoliosReadGet1ResponseSuccess = (organizationPortfoliosReadGet1Response200) & {
  headers: Headers;
};
;

export type organizationPortfoliosReadGet1Response = (organizationPortfoliosReadGet1ResponseSuccess)

export const getOrganizationPortfoliosReadGet1Url = (id: string,) => {




  return `/api/organization/portfolios/${id}/benchmark-assignments`
}

export const organizationPortfoliosReadGet1 = async (id: string, options?: RequestInit): Promise<organizationPortfoliosReadGet1Response> => {

  return customMutator<organizationPortfoliosReadGet1Response>(getOrganizationPortfoliosReadGet1Url(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationPortfoliosReadGet1QueryKey = (id: string,) => {
    return [
    `/api/organization/portfolios/${id}/benchmark-assignments`
    ] as const;
    }


export const getOrganizationPortfoliosReadGet1QueryOptions = <TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationPortfoliosReadGet1QueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>> = ({ signal }) => organizationPortfoliosReadGet1(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationPortfoliosReadGet1QueryResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>>
export type OrganizationPortfoliosReadGet1QueryError = unknown


export function useOrganizationPortfoliosReadGet1<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosReadGet1<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosReadGet1<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationPortfoliosReadGet1<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationPortfoliosReadGet1QueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationPortfoliosCreatePostResponse200 = {
  data: void
  status: 200
}

export type organizationPortfoliosCreatePostResponse201 = {
  data: void
  status: 201
}

export type organizationPortfoliosCreatePostResponseSuccess = (organizationPortfoliosCreatePostResponse200 | organizationPortfoliosCreatePostResponse201) & {
  headers: Headers;
};
;

export type organizationPortfoliosCreatePostResponse = (organizationPortfoliosCreatePostResponseSuccess)

export const getOrganizationPortfoliosCreatePostUrl = (id: string,) => {




  return `/api/organization/portfolios/${id}/benchmark-assignments`
}

export const organizationPortfoliosCreatePost = async (id: string, options?: RequestInit): Promise<organizationPortfoliosCreatePostResponse> => {

  return customMutator<organizationPortfoliosCreatePostResponse>(getOrganizationPortfoliosCreatePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationPortfoliosCreatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreatePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreatePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationPortfoliosCreatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosCreatePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationPortfoliosCreatePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosCreatePostMutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosCreatePost>>>

    export type OrganizationPortfoliosCreatePostMutationError = unknown

    export const useOrganizationPortfoliosCreatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreatePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosCreatePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosCreatePostMutationOptions(options), queryClient);
    }

export type organizationPortfoliosUpdatePost1Response200 = {
  data: void
  status: 200
}

export type organizationPortfoliosUpdatePost1Response201 = {
  data: void
  status: 201
}

export type organizationPortfoliosUpdatePost1ResponseSuccess = (organizationPortfoliosUpdatePost1Response200 | organizationPortfoliosUpdatePost1Response201) & {
  headers: Headers;
};
;

export type organizationPortfoliosUpdatePost1Response = (organizationPortfoliosUpdatePost1ResponseSuccess)

export const getOrganizationPortfoliosUpdatePost1Url = (id: string,
    assignmentId: string,) => {




  return `/api/organization/portfolios/${id}/benchmark-assignments/${assignmentId}/approve`
}

export const organizationPortfoliosUpdatePost1 = async (id: string,
    assignmentId: string, options?: RequestInit): Promise<organizationPortfoliosUpdatePost1Response> => {

  return customMutator<organizationPortfoliosUpdatePost1Response>(getOrganizationPortfoliosUpdatePost1Url(id,assignmentId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationPortfoliosUpdatePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost1>>, TError,{id: string;assignmentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost1>>, TError,{id: string;assignmentId: string}, TContext> => {

const mutationKey = ['organizationPortfoliosUpdatePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost1>>, {id: string;assignmentId: string}> = (props) => {
          const {id,assignmentId} = props ?? {};

          return  organizationPortfoliosUpdatePost1(id,assignmentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosUpdatePost1MutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost1>>>

    export type OrganizationPortfoliosUpdatePost1MutationError = unknown

    export const useOrganizationPortfoliosUpdatePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost1>>, TError,{id: string;assignmentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosUpdatePost1>>,
        TError,
        {id: string;assignmentId: string},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosUpdatePost1MutationOptions(options), queryClient);
    }

export type organizationPortfoliosUpdatePost2Response200 = {
  data: void
  status: 200
}

export type organizationPortfoliosUpdatePost2Response201 = {
  data: void
  status: 201
}

export type organizationPortfoliosUpdatePost2ResponseSuccess = (organizationPortfoliosUpdatePost2Response200 | organizationPortfoliosUpdatePost2Response201) & {
  headers: Headers;
};
;

export type organizationPortfoliosUpdatePost2Response = (organizationPortfoliosUpdatePost2ResponseSuccess)

export const getOrganizationPortfoliosUpdatePost2Url = (id: string,) => {




  return `/api/organization/portfolios/${id}/suitability`
}

export const organizationPortfoliosUpdatePost2 = async (id: string,
    assessManagedPortfolioSuitabilityDto: AssessManagedPortfolioSuitabilityDto, options?: RequestInit): Promise<organizationPortfoliosUpdatePost2Response> => {

  return customMutator<organizationPortfoliosUpdatePost2Response>(getOrganizationPortfoliosUpdatePost2Url(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(assessManagedPortfolioSuitabilityDto)
  }
);}





export const getOrganizationPortfoliosUpdatePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost2>>, TError,{id: string;data: AssessManagedPortfolioSuitabilityDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost2>>, TError,{id: string;data: AssessManagedPortfolioSuitabilityDto}, TContext> => {

const mutationKey = ['organizationPortfoliosUpdatePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost2>>, {id: string;data: AssessManagedPortfolioSuitabilityDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationPortfoliosUpdatePost2(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosUpdatePost2MutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost2>>>
    export type OrganizationPortfoliosUpdatePost2MutationBody = AssessManagedPortfolioSuitabilityDto
    export type OrganizationPortfoliosUpdatePost2MutationError = unknown

    export const useOrganizationPortfoliosUpdatePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost2>>, TError,{id: string;data: AssessManagedPortfolioSuitabilityDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosUpdatePost2>>,
        TError,
        {id: string;data: AssessManagedPortfolioSuitabilityDto},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosUpdatePost2MutationOptions(options), queryClient);
    }

export type organizationPortfoliosReadGet2Response200 = {
  data: void
  status: 200
}

export type organizationPortfoliosReadGet2ResponseSuccess = (organizationPortfoliosReadGet2Response200) & {
  headers: Headers;
};
;

export type organizationPortfoliosReadGet2Response = (organizationPortfoliosReadGet2ResponseSuccess)

export const getOrganizationPortfoliosReadGet2Url = (id: string,) => {




  return `/api/organization/portfolios/${id}/allocation-targets`
}

export const organizationPortfoliosReadGet2 = async (id: string, options?: RequestInit): Promise<organizationPortfoliosReadGet2Response> => {

  return customMutator<organizationPortfoliosReadGet2Response>(getOrganizationPortfoliosReadGet2Url(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationPortfoliosReadGet2QueryKey = (id: string,) => {
    return [
    `/api/organization/portfolios/${id}/allocation-targets`
    ] as const;
    }


export const getOrganizationPortfoliosReadGet2QueryOptions = <TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationPortfoliosReadGet2QueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>> = ({ signal }) => organizationPortfoliosReadGet2(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationPortfoliosReadGet2QueryResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>>
export type OrganizationPortfoliosReadGet2QueryError = unknown


export function useOrganizationPortfoliosReadGet2<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosReadGet2<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosReadGet2<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationPortfoliosReadGet2<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet2>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationPortfoliosReadGet2QueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationPortfoliosCreatePost1Response200 = {
  data: void
  status: 200
}

export type organizationPortfoliosCreatePost1Response201 = {
  data: void
  status: 201
}

export type organizationPortfoliosCreatePost1ResponseSuccess = (organizationPortfoliosCreatePost1Response200 | organizationPortfoliosCreatePost1Response201) & {
  headers: Headers;
};
;

export type organizationPortfoliosCreatePost1Response = (organizationPortfoliosCreatePost1ResponseSuccess)

export const getOrganizationPortfoliosCreatePost1Url = (id: string,) => {




  return `/api/organization/portfolios/${id}/allocation-targets`
}

export const organizationPortfoliosCreatePost1 = async (id: string,
    createManagedPortfolioAllocationTargetDto: CreateManagedPortfolioAllocationTargetDto, options?: RequestInit): Promise<organizationPortfoliosCreatePost1Response> => {

  return customMutator<organizationPortfoliosCreatePost1Response>(getOrganizationPortfoliosCreatePost1Url(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createManagedPortfolioAllocationTargetDto)
  }
);}





export const getOrganizationPortfoliosCreatePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreatePost1>>, TError,{id: string;data: CreateManagedPortfolioAllocationTargetDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreatePost1>>, TError,{id: string;data: CreateManagedPortfolioAllocationTargetDto}, TContext> => {

const mutationKey = ['organizationPortfoliosCreatePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosCreatePost1>>, {id: string;data: CreateManagedPortfolioAllocationTargetDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationPortfoliosCreatePost1(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosCreatePost1MutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosCreatePost1>>>
    export type OrganizationPortfoliosCreatePost1MutationBody = CreateManagedPortfolioAllocationTargetDto
    export type OrganizationPortfoliosCreatePost1MutationError = unknown

    export const useOrganizationPortfoliosCreatePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreatePost1>>, TError,{id: string;data: CreateManagedPortfolioAllocationTargetDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosCreatePost1>>,
        TError,
        {id: string;data: CreateManagedPortfolioAllocationTargetDto},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosCreatePost1MutationOptions(options), queryClient);
    }

export type organizationPortfoliosUpdatePost3Response200 = {
  data: void
  status: 200
}

export type organizationPortfoliosUpdatePost3Response201 = {
  data: void
  status: 201
}

export type organizationPortfoliosUpdatePost3ResponseSuccess = (organizationPortfoliosUpdatePost3Response200 | organizationPortfoliosUpdatePost3Response201) & {
  headers: Headers;
};
;

export type organizationPortfoliosUpdatePost3Response = (organizationPortfoliosUpdatePost3ResponseSuccess)

export const getOrganizationPortfoliosUpdatePost3Url = (id: string,
    targetId: string,) => {




  return `/api/organization/portfolios/${id}/allocation-targets/${targetId}/decision`
}

export const organizationPortfoliosUpdatePost3 = async (id: string,
    targetId: string,
    decideManagedPortfolioAllocationTargetDto: DecideManagedPortfolioAllocationTargetDto, options?: RequestInit): Promise<organizationPortfoliosUpdatePost3Response> => {

  return customMutator<organizationPortfoliosUpdatePost3Response>(getOrganizationPortfoliosUpdatePost3Url(id,targetId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(decideManagedPortfolioAllocationTargetDto)
  }
);}





export const getOrganizationPortfoliosUpdatePost3MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost3>>, TError,{id: string;targetId: string;data: DecideManagedPortfolioAllocationTargetDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost3>>, TError,{id: string;targetId: string;data: DecideManagedPortfolioAllocationTargetDto}, TContext> => {

const mutationKey = ['organizationPortfoliosUpdatePost3'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost3>>, {id: string;targetId: string;data: DecideManagedPortfolioAllocationTargetDto}> = (props) => {
          const {id,targetId,data} = props ?? {};

          return  organizationPortfoliosUpdatePost3(id,targetId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosUpdatePost3MutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost3>>>
    export type OrganizationPortfoliosUpdatePost3MutationBody = DecideManagedPortfolioAllocationTargetDto
    export type OrganizationPortfoliosUpdatePost3MutationError = unknown

    export const useOrganizationPortfoliosUpdatePost3 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost3>>, TError,{id: string;targetId: string;data: DecideManagedPortfolioAllocationTargetDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosUpdatePost3>>,
        TError,
        {id: string;targetId: string;data: DecideManagedPortfolioAllocationTargetDto},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosUpdatePost3MutationOptions(options), queryClient);
    }

export type organizationPortfoliosCreatePost2Response200 = {
  data: void
  status: 200
}

export type organizationPortfoliosCreatePost2Response201 = {
  data: void
  status: 201
}

export type organizationPortfoliosCreatePost2ResponseSuccess = (organizationPortfoliosCreatePost2Response200 | organizationPortfoliosCreatePost2Response201) & {
  headers: Headers;
};
;

export type organizationPortfoliosCreatePost2Response = (organizationPortfoliosCreatePost2ResponseSuccess)

export const getOrganizationPortfoliosCreatePost2Url = (id: string,) => {




  return `/api/organization/portfolios/${id}/rebalancing/simulations`
}

export const organizationPortfoliosCreatePost2 = async (id: string,
    createManagedPortfolioRebalanceSimulationDto: CreateManagedPortfolioRebalanceSimulationDto, options?: RequestInit): Promise<organizationPortfoliosCreatePost2Response> => {

  return customMutator<organizationPortfoliosCreatePost2Response>(getOrganizationPortfoliosCreatePost2Url(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createManagedPortfolioRebalanceSimulationDto)
  }
);}





export const getOrganizationPortfoliosCreatePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreatePost2>>, TError,{id: string;data: CreateManagedPortfolioRebalanceSimulationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreatePost2>>, TError,{id: string;data: CreateManagedPortfolioRebalanceSimulationDto}, TContext> => {

const mutationKey = ['organizationPortfoliosCreatePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosCreatePost2>>, {id: string;data: CreateManagedPortfolioRebalanceSimulationDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationPortfoliosCreatePost2(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosCreatePost2MutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosCreatePost2>>>
    export type OrganizationPortfoliosCreatePost2MutationBody = CreateManagedPortfolioRebalanceSimulationDto
    export type OrganizationPortfoliosCreatePost2MutationError = unknown

    export const useOrganizationPortfoliosCreatePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosCreatePost2>>, TError,{id: string;data: CreateManagedPortfolioRebalanceSimulationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosCreatePost2>>,
        TError,
        {id: string;data: CreateManagedPortfolioRebalanceSimulationDto},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosCreatePost2MutationOptions(options), queryClient);
    }

export type organizationPortfoliosReadGet3Response200 = {
  data: void
  status: 200
}

export type organizationPortfoliosReadGet3ResponseSuccess = (organizationPortfoliosReadGet3Response200) & {
  headers: Headers;
};
;

export type organizationPortfoliosReadGet3Response = (organizationPortfoliosReadGet3ResponseSuccess)

export const getOrganizationPortfoliosReadGet3Url = (id: string,
    simulationId: string,) => {




  return `/api/organization/portfolios/${id}/rebalancing/simulations/${simulationId}`
}

export const organizationPortfoliosReadGet3 = async (id: string,
    simulationId: string, options?: RequestInit): Promise<organizationPortfoliosReadGet3Response> => {

  return customMutator<organizationPortfoliosReadGet3Response>(getOrganizationPortfoliosReadGet3Url(id,simulationId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationPortfoliosReadGet3QueryKey = (id: string,
    simulationId: string,) => {
    return [
    `/api/organization/portfolios/${id}/rebalancing/simulations/${simulationId}`
    ] as const;
    }


export const getOrganizationPortfoliosReadGet3QueryOptions = <TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError = unknown>(id: string,
    simulationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationPortfoliosReadGet3QueryKey(id,simulationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>> = ({ signal }) => organizationPortfoliosReadGet3(id,simulationId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && simulationId !== null && simulationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationPortfoliosReadGet3QueryResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>>
export type OrganizationPortfoliosReadGet3QueryError = unknown


export function useOrganizationPortfoliosReadGet3<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError = unknown>(
 id: string,
    simulationId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosReadGet3<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError = unknown>(
 id: string,
    simulationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>,
          TError,
          Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPortfoliosReadGet3<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError = unknown>(
 id: string,
    simulationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationPortfoliosReadGet3<TData = Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError = unknown>(
 id: string,
    simulationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPortfoliosReadGet3>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationPortfoliosReadGet3QueryOptions(id,simulationId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationPortfoliosUpdatePost4Response200 = {
  data: void
  status: 200
}

export type organizationPortfoliosUpdatePost4Response201 = {
  data: void
  status: 201
}

export type organizationPortfoliosUpdatePost4ResponseSuccess = (organizationPortfoliosUpdatePost4Response200 | organizationPortfoliosUpdatePost4Response201) & {
  headers: Headers;
};
;

export type organizationPortfoliosUpdatePost4Response = (organizationPortfoliosUpdatePost4ResponseSuccess)

export const getOrganizationPortfoliosUpdatePost4Url = (id: string,
    simulationId: string,) => {




  return `/api/organization/portfolios/${id}/rebalancing/simulations/${simulationId}/decision`
}

export const organizationPortfoliosUpdatePost4 = async (id: string,
    simulationId: string,
    decideManagedPortfolioRebalanceSimulationDto: DecideManagedPortfolioRebalanceSimulationDto, options?: RequestInit): Promise<organizationPortfoliosUpdatePost4Response> => {

  return customMutator<organizationPortfoliosUpdatePost4Response>(getOrganizationPortfoliosUpdatePost4Url(id,simulationId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(decideManagedPortfolioRebalanceSimulationDto)
  }
);}





export const getOrganizationPortfoliosUpdatePost4MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost4>>, TError,{id: string;simulationId: string;data: DecideManagedPortfolioRebalanceSimulationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost4>>, TError,{id: string;simulationId: string;data: DecideManagedPortfolioRebalanceSimulationDto}, TContext> => {

const mutationKey = ['organizationPortfoliosUpdatePost4'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost4>>, {id: string;simulationId: string;data: DecideManagedPortfolioRebalanceSimulationDto}> = (props) => {
          const {id,simulationId,data} = props ?? {};

          return  organizationPortfoliosUpdatePost4(id,simulationId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationPortfoliosUpdatePost4MutationResult = NonNullable<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost4>>>
    export type OrganizationPortfoliosUpdatePost4MutationBody = DecideManagedPortfolioRebalanceSimulationDto
    export type OrganizationPortfoliosUpdatePost4MutationError = unknown

    export const useOrganizationPortfoliosUpdatePost4 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationPortfoliosUpdatePost4>>, TError,{id: string;simulationId: string;data: DecideManagedPortfolioRebalanceSimulationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationPortfoliosUpdatePost4>>,
        TError,
        {id: string;simulationId: string;data: DecideManagedPortfolioRebalanceSimulationDto},
        TContext
      > => {
      return useMutation(getOrganizationPortfoliosUpdatePost4MutationOptions(options), queryClient);
    }

export type platformClientAccountListResponse200 = {
  data: PlatformClientAccountList200
  status: 200
}

export type platformClientAccountListResponseSuccess = (platformClientAccountListResponse200) & {
  headers: Headers;
};
;

export type platformClientAccountListResponse = (platformClientAccountListResponseSuccess)

export const getPlatformClientAccountListUrl = () => {




  return `/api/parties`
}

export const platformClientAccountList = async ( options?: RequestInit): Promise<platformClientAccountListResponse> => {

  return customMutator<platformClientAccountListResponse>(getPlatformClientAccountListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformClientAccountListQueryKey = () => {
    return [
    `/api/parties`
    ] as const;
    }


export const getPlatformClientAccountListQueryOptions = <TData = Awaited<ReturnType<typeof platformClientAccountList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformClientAccountListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformClientAccountList>>> = ({ signal }) => platformClientAccountList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformClientAccountListQueryResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountList>>>
export type PlatformClientAccountListQueryError = unknown


export function usePlatformClientAccountList<TData = Awaited<ReturnType<typeof platformClientAccountList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformClientAccountList>>,
          TError,
          Awaited<ReturnType<typeof platformClientAccountList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformClientAccountList<TData = Awaited<ReturnType<typeof platformClientAccountList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformClientAccountList>>,
          TError,
          Awaited<ReturnType<typeof platformClientAccountList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformClientAccountList<TData = Awaited<ReturnType<typeof platformClientAccountList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformClientAccountList<TData = Awaited<ReturnType<typeof platformClientAccountList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformClientAccountListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformClientAccountCreateResponse200 = {
  data: void
  status: 200
}

export type platformClientAccountCreateResponse201 = {
  data: void
  status: 201
}

export type platformClientAccountCreateResponseSuccess = (platformClientAccountCreateResponse200 | platformClientAccountCreateResponse201) & {
  headers: Headers;
};
;

export type platformClientAccountCreateResponse = (platformClientAccountCreateResponseSuccess)

export const getPlatformClientAccountCreateUrl = () => {




  return `/api/parties`
}

export const platformClientAccountCreate = async (createClientAccountDto: CreateClientAccountDto, options?: RequestInit): Promise<platformClientAccountCreateResponse> => {

  return customMutator<platformClientAccountCreateResponse>(getPlatformClientAccountCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createClientAccountDto)
  }
);}





export const getPlatformClientAccountCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountCreate>>, TError,{data: CreateClientAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountCreate>>, TError,{data: CreateClientAccountDto}, TContext> => {

const mutationKey = ['platformClientAccountCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformClientAccountCreate>>, {data: CreateClientAccountDto}> = (props) => {
          const {data} = props ?? {};

          return  platformClientAccountCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformClientAccountCreateMutationResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountCreate>>>
    export type PlatformClientAccountCreateMutationBody = CreateClientAccountDto
    export type PlatformClientAccountCreateMutationError = unknown

    export const usePlatformClientAccountCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountCreate>>, TError,{data: CreateClientAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformClientAccountCreate>>,
        TError,
        {data: CreateClientAccountDto},
        TContext
      > => {
      return useMutation(getPlatformClientAccountCreateMutationOptions(options), queryClient);
    }

export type platformClientAccountUpdateResponse200 = {
  data: void
  status: 200
}

export type platformClientAccountUpdateResponseSuccess = (platformClientAccountUpdateResponse200) & {
  headers: Headers;
};
;

export type platformClientAccountUpdateResponse = (platformClientAccountUpdateResponseSuccess)

export const getPlatformClientAccountUpdateUrl = (id: string,) => {




  return `/api/parties/${id}/lifecycle`
}

export const platformClientAccountUpdate = async (id: string,
    clientLifecycleTransitionDto: ClientLifecycleTransitionDto, options?: RequestInit): Promise<platformClientAccountUpdateResponse> => {

  return customMutator<platformClientAccountUpdateResponse>(getPlatformClientAccountUpdateUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientLifecycleTransitionDto)
  }
);}





export const getPlatformClientAccountUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountUpdate>>, TError,{id: string;data: ClientLifecycleTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountUpdate>>, TError,{id: string;data: ClientLifecycleTransitionDto}, TContext> => {

const mutationKey = ['platformClientAccountUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformClientAccountUpdate>>, {id: string;data: ClientLifecycleTransitionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  platformClientAccountUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformClientAccountUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountUpdate>>>
    export type PlatformClientAccountUpdateMutationBody = ClientLifecycleTransitionDto
    export type PlatformClientAccountUpdateMutationError = unknown

    export const usePlatformClientAccountUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountUpdate>>, TError,{id: string;data: ClientLifecycleTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformClientAccountUpdate>>,
        TError,
        {id: string;data: ClientLifecycleTransitionDto},
        TContext
      > => {
      return useMutation(getPlatformClientAccountUpdateMutationOptions(options), queryClient);
    }

export type platformClientAccountGetResponse200 = {
  data: void
  status: 200
}

export type platformClientAccountGetResponseSuccess = (platformClientAccountGetResponse200) & {
  headers: Headers;
};
;

export type platformClientAccountGetResponse = (platformClientAccountGetResponseSuccess)

export const getPlatformClientAccountGetUrl = (id: string,) => {




  return `/api/parties/${id}/due-diligence`
}

export const platformClientAccountGet = async (id: string, options?: RequestInit): Promise<platformClientAccountGetResponse> => {

  return customMutator<platformClientAccountGetResponse>(getPlatformClientAccountGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformClientAccountGetQueryKey = (id: string,) => {
    return [
    `/api/parties/${id}/due-diligence`
    ] as const;
    }


export const getPlatformClientAccountGetQueryOptions = <TData = Awaited<ReturnType<typeof platformClientAccountGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformClientAccountGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformClientAccountGet>>> = ({ signal }) => platformClientAccountGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformClientAccountGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountGet>>>
export type PlatformClientAccountGetQueryError = unknown


export function usePlatformClientAccountGet<TData = Awaited<ReturnType<typeof platformClientAccountGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformClientAccountGet>>,
          TError,
          Awaited<ReturnType<typeof platformClientAccountGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformClientAccountGet<TData = Awaited<ReturnType<typeof platformClientAccountGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformClientAccountGet>>,
          TError,
          Awaited<ReturnType<typeof platformClientAccountGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformClientAccountGet<TData = Awaited<ReturnType<typeof platformClientAccountGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformClientAccountGet<TData = Awaited<ReturnType<typeof platformClientAccountGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformClientAccountGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformClientAccountUpdatePutResponse200 = {
  data: void
  status: 200
}

export type platformClientAccountUpdatePutResponseSuccess = (platformClientAccountUpdatePutResponse200) & {
  headers: Headers;
};
;

export type platformClientAccountUpdatePutResponse = (platformClientAccountUpdatePutResponseSuccess)

export const getPlatformClientAccountUpdatePutUrl = (id: string,) => {




  return `/api/parties/${id}/due-diligence`
}

export const platformClientAccountUpdatePut = async (id: string,
    configureClientDueDiligenceDto: ConfigureClientDueDiligenceDto, options?: RequestInit): Promise<platformClientAccountUpdatePutResponse> => {

  return customMutator<platformClientAccountUpdatePutResponse>(getPlatformClientAccountUpdatePutUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(configureClientDueDiligenceDto)
  }
);}





export const getPlatformClientAccountUpdatePutMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountUpdatePut>>, TError,{id: string;data: ConfigureClientDueDiligenceDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountUpdatePut>>, TError,{id: string;data: ConfigureClientDueDiligenceDto}, TContext> => {

const mutationKey = ['platformClientAccountUpdatePut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformClientAccountUpdatePut>>, {id: string;data: ConfigureClientDueDiligenceDto}> = (props) => {
          const {id,data} = props ?? {};

          return  platformClientAccountUpdatePut(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformClientAccountUpdatePutMutationResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountUpdatePut>>>
    export type PlatformClientAccountUpdatePutMutationBody = ConfigureClientDueDiligenceDto
    export type PlatformClientAccountUpdatePutMutationError = unknown

    export const usePlatformClientAccountUpdatePut = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountUpdatePut>>, TError,{id: string;data: ConfigureClientDueDiligenceDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformClientAccountUpdatePut>>,
        TError,
        {id: string;data: ConfigureClientDueDiligenceDto},
        TContext
      > => {
      return useMutation(getPlatformClientAccountUpdatePutMutationOptions(options), queryClient);
    }

export type platformClientAccountDocumentCreateResponse200 = {
  data: void
  status: 200
}

export type platformClientAccountDocumentCreateResponse201 = {
  data: void
  status: 201
}

export type platformClientAccountDocumentCreateResponseSuccess = (platformClientAccountDocumentCreateResponse200 | platformClientAccountDocumentCreateResponse201) & {
  headers: Headers;
};
;

export type platformClientAccountDocumentCreateResponse = (platformClientAccountDocumentCreateResponseSuccess)

export const getPlatformClientAccountDocumentCreateUrl = (id: string,) => {




  return `/api/parties/${id}/due-diligence/screen`
}

export const platformClientAccountDocumentCreate = async (id: string,
    clientScreeningDto: ClientScreeningDto, options?: RequestInit): Promise<platformClientAccountDocumentCreateResponse> => {

  return customMutator<platformClientAccountDocumentCreateResponse>(getPlatformClientAccountDocumentCreateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientScreeningDto)
  }
);}





export const getPlatformClientAccountDocumentCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreate>>, TError,{id: string;data: ClientScreeningDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreate>>, TError,{id: string;data: ClientScreeningDto}, TContext> => {

const mutationKey = ['platformClientAccountDocumentCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformClientAccountDocumentCreate>>, {id: string;data: ClientScreeningDto}> = (props) => {
          const {id,data} = props ?? {};

          return  platformClientAccountDocumentCreate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformClientAccountDocumentCreateMutationResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountDocumentCreate>>>
    export type PlatformClientAccountDocumentCreateMutationBody = ClientScreeningDto
    export type PlatformClientAccountDocumentCreateMutationError = unknown

    export const usePlatformClientAccountDocumentCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreate>>, TError,{id: string;data: ClientScreeningDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformClientAccountDocumentCreate>>,
        TError,
        {id: string;data: ClientScreeningDto},
        TContext
      > => {
      return useMutation(getPlatformClientAccountDocumentCreateMutationOptions(options), queryClient);
    }

export type platformClientAccountDocumentCreatePostResponse200 = {
  data: void
  status: 200
}

export type platformClientAccountDocumentCreatePostResponseSuccess = (platformClientAccountDocumentCreatePostResponse200) & {
  headers: Headers;
};
;

export type platformClientAccountDocumentCreatePostResponse = (platformClientAccountDocumentCreatePostResponseSuccess)

export const getPlatformClientAccountDocumentCreatePostUrl = (id: string,) => {




  return `/api/parties/${id}/due-diligence/submit`
}

export const platformClientAccountDocumentCreatePost = async (id: string, options?: RequestInit): Promise<platformClientAccountDocumentCreatePostResponse> => {

  return customMutator<platformClientAccountDocumentCreatePostResponse>(getPlatformClientAccountDocumentCreatePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformClientAccountDocumentCreatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformClientAccountDocumentCreatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformClientAccountDocumentCreatePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformClientAccountDocumentCreatePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost>>>

    export type PlatformClientAccountDocumentCreatePostMutationError = unknown

    export const usePlatformClientAccountDocumentCreatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformClientAccountDocumentCreatePostMutationOptions(options), queryClient);
    }

export type platformClientAccountDocumentCreatePost1Response200 = {
  data: void
  status: 200
}

export type platformClientAccountDocumentCreatePost1ResponseSuccess = (platformClientAccountDocumentCreatePost1Response200) & {
  headers: Headers;
};
;

export type platformClientAccountDocumentCreatePost1Response = (platformClientAccountDocumentCreatePost1ResponseSuccess)

export const getPlatformClientAccountDocumentCreatePost1Url = (id: string,) => {




  return `/api/parties/${id}/due-diligence/review`
}

export const platformClientAccountDocumentCreatePost1 = async (id: string,
    clientDueDiligenceReviewDto: ClientDueDiligenceReviewDto, options?: RequestInit): Promise<platformClientAccountDocumentCreatePost1Response> => {

  return customMutator<platformClientAccountDocumentCreatePost1Response>(getPlatformClientAccountDocumentCreatePost1Url(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientDueDiligenceReviewDto)
  }
);}





export const getPlatformClientAccountDocumentCreatePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost1>>, TError,{id: string;data: ClientDueDiligenceReviewDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost1>>, TError,{id: string;data: ClientDueDiligenceReviewDto}, TContext> => {

const mutationKey = ['platformClientAccountDocumentCreatePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost1>>, {id: string;data: ClientDueDiligenceReviewDto}> = (props) => {
          const {id,data} = props ?? {};

          return  platformClientAccountDocumentCreatePost1(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformClientAccountDocumentCreatePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost1>>>
    export type PlatformClientAccountDocumentCreatePost1MutationBody = ClientDueDiligenceReviewDto
    export type PlatformClientAccountDocumentCreatePost1MutationError = unknown

    export const usePlatformClientAccountDocumentCreatePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost1>>, TError,{id: string;data: ClientDueDiligenceReviewDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost1>>,
        TError,
        {id: string;data: ClientDueDiligenceReviewDto},
        TContext
      > => {
      return useMutation(getPlatformClientAccountDocumentCreatePost1MutationOptions(options), queryClient);
    }

export type platformClientAccountDocumentCreatePost2Response200 = {
  data: void
  status: 200
}

export type platformClientAccountDocumentCreatePost2ResponseSuccess = (platformClientAccountDocumentCreatePost2Response200) & {
  headers: Headers;
};
;

export type platformClientAccountDocumentCreatePost2Response = (platformClientAccountDocumentCreatePost2ResponseSuccess)

export const getPlatformClientAccountDocumentCreatePost2Url = (id: string,
    investigationId: string,) => {




  return `/api/parties/${id}/due-diligence/duplicates/${investigationId}/decision`
}

export const platformClientAccountDocumentCreatePost2 = async (id: string,
    investigationId: string,
    clientDuplicateDecisionDto: ClientDuplicateDecisionDto, options?: RequestInit): Promise<platformClientAccountDocumentCreatePost2Response> => {

  return customMutator<platformClientAccountDocumentCreatePost2Response>(getPlatformClientAccountDocumentCreatePost2Url(id,investigationId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientDuplicateDecisionDto)
  }
);}





export const getPlatformClientAccountDocumentCreatePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost2>>, TError,{id: string;investigationId: string;data: ClientDuplicateDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost2>>, TError,{id: string;investigationId: string;data: ClientDuplicateDecisionDto}, TContext> => {

const mutationKey = ['platformClientAccountDocumentCreatePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost2>>, {id: string;investigationId: string;data: ClientDuplicateDecisionDto}> = (props) => {
          const {id,investigationId,data} = props ?? {};

          return  platformClientAccountDocumentCreatePost2(id,investigationId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformClientAccountDocumentCreatePost2MutationResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost2>>>
    export type PlatformClientAccountDocumentCreatePost2MutationBody = ClientDuplicateDecisionDto
    export type PlatformClientAccountDocumentCreatePost2MutationError = unknown

    export const usePlatformClientAccountDocumentCreatePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost2>>, TError,{id: string;investigationId: string;data: ClientDuplicateDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost2>>,
        TError,
        {id: string;investigationId: string;data: ClientDuplicateDecisionDto},
        TContext
      > => {
      return useMutation(getPlatformClientAccountDocumentCreatePost2MutationOptions(options), queryClient);
    }

export type platformClientAccountGetGetResponse200 = {
  data: PlatformClientAccountGetGet200
  status: 200
}

export type platformClientAccountGetGetResponseSuccess = (platformClientAccountGetGetResponse200) & {
  headers: Headers;
};
;

export type platformClientAccountGetGetResponse = (platformClientAccountGetGetResponseSuccess)

export const getPlatformClientAccountGetGetUrl = (id: string,) => {




  return `/api/parties/${id}`
}

export const platformClientAccountGetGet = async (id: string, options?: RequestInit): Promise<platformClientAccountGetGetResponse> => {

  return customMutator<platformClientAccountGetGetResponse>(getPlatformClientAccountGetGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformClientAccountGetGetQueryKey = (id: string,) => {
    return [
    `/api/parties/${id}`
    ] as const;
    }


export const getPlatformClientAccountGetGetQueryOptions = <TData = Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformClientAccountGetGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformClientAccountGetGet>>> = ({ signal }) => platformClientAccountGetGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformClientAccountGetGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountGetGet>>>
export type PlatformClientAccountGetGetQueryError = unknown


export function usePlatformClientAccountGetGet<TData = Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformClientAccountGetGet>>,
          TError,
          Awaited<ReturnType<typeof platformClientAccountGetGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformClientAccountGetGet<TData = Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformClientAccountGetGet>>,
          TError,
          Awaited<ReturnType<typeof platformClientAccountGetGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformClientAccountGetGet<TData = Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformClientAccountGetGet<TData = Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountGetGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformClientAccountGetGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformClientAccountUpdatePatch1Response200 = {
  data: void
  status: 200
}

export type platformClientAccountUpdatePatch1ResponseSuccess = (platformClientAccountUpdatePatch1Response200) & {
  headers: Headers;
};
;

export type platformClientAccountUpdatePatch1Response = (platformClientAccountUpdatePatch1ResponseSuccess)

export const getPlatformClientAccountUpdatePatch1Url = (id: string,) => {




  return `/api/parties/${id}`
}

export const platformClientAccountUpdatePatch1 = async (id: string,
    updateClientAccountDto: UpdateClientAccountDto, options?: RequestInit): Promise<platformClientAccountUpdatePatch1Response> => {

  return customMutator<platformClientAccountUpdatePatch1Response>(getPlatformClientAccountUpdatePatch1Url(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateClientAccountDto)
  }
);}





export const getPlatformClientAccountUpdatePatch1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountUpdatePatch1>>, TError,{id: string;data: UpdateClientAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountUpdatePatch1>>, TError,{id: string;data: UpdateClientAccountDto}, TContext> => {

const mutationKey = ['platformClientAccountUpdatePatch1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformClientAccountUpdatePatch1>>, {id: string;data: UpdateClientAccountDto}> = (props) => {
          const {id,data} = props ?? {};

          return  platformClientAccountUpdatePatch1(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformClientAccountUpdatePatch1MutationResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountUpdatePatch1>>>
    export type PlatformClientAccountUpdatePatch1MutationBody = UpdateClientAccountDto
    export type PlatformClientAccountUpdatePatch1MutationError = unknown

    export const usePlatformClientAccountUpdatePatch1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountUpdatePatch1>>, TError,{id: string;data: UpdateClientAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformClientAccountUpdatePatch1>>,
        TError,
        {id: string;data: UpdateClientAccountDto},
        TContext
      > => {
      return useMutation(getPlatformClientAccountUpdatePatch1MutationOptions(options), queryClient);
    }

export type platformClientAccountDocumentsListResponse200 = {
  data: PlatformClientAccountDocumentsList200
  status: 200
}

export type platformClientAccountDocumentsListResponseSuccess = (platformClientAccountDocumentsListResponse200) & {
  headers: Headers;
};
;

export type platformClientAccountDocumentsListResponse = (platformClientAccountDocumentsListResponseSuccess)

export const getPlatformClientAccountDocumentsListUrl = (id: string,) => {




  return `/api/parties/${id}/documents`
}

export const platformClientAccountDocumentsList = async (id: string, options?: RequestInit): Promise<platformClientAccountDocumentsListResponse> => {

  return customMutator<platformClientAccountDocumentsListResponse>(getPlatformClientAccountDocumentsListUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformClientAccountDocumentsListQueryKey = (id: string,) => {
    return [
    `/api/parties/${id}/documents`
    ] as const;
    }


export const getPlatformClientAccountDocumentsListQueryOptions = <TData = Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformClientAccountDocumentsListQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformClientAccountDocumentsList>>> = ({ signal }) => platformClientAccountDocumentsList(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformClientAccountDocumentsListQueryResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountDocumentsList>>>
export type PlatformClientAccountDocumentsListQueryError = unknown


export function usePlatformClientAccountDocumentsList<TData = Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformClientAccountDocumentsList>>,
          TError,
          Awaited<ReturnType<typeof platformClientAccountDocumentsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformClientAccountDocumentsList<TData = Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformClientAccountDocumentsList>>,
          TError,
          Awaited<ReturnType<typeof platformClientAccountDocumentsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformClientAccountDocumentsList<TData = Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformClientAccountDocumentsList<TData = Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformClientAccountDocumentsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformClientAccountDocumentsListQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformClientAccountDocumentCreatePost3Response200 = {
  data: void
  status: 200
}

export type platformClientAccountDocumentCreatePost3Response201 = {
  data: void
  status: 201
}

export type platformClientAccountDocumentCreatePost3ResponseSuccess = (platformClientAccountDocumentCreatePost3Response200 | platformClientAccountDocumentCreatePost3Response201) & {
  headers: Headers;
};
;

export type platformClientAccountDocumentCreatePost3Response = (platformClientAccountDocumentCreatePost3ResponseSuccess)

export const getPlatformClientAccountDocumentCreatePost3Url = (id: string,) => {




  return `/api/parties/${id}/documents`
}

export const platformClientAccountDocumentCreatePost3 = async (id: string,
    clientAccountDocumentDto: ClientAccountDocumentDto, options?: RequestInit): Promise<platformClientAccountDocumentCreatePost3Response> => {

  return customMutator<platformClientAccountDocumentCreatePost3Response>(getPlatformClientAccountDocumentCreatePost3Url(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientAccountDocumentDto)
  }
);}





export const getPlatformClientAccountDocumentCreatePost3MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost3>>, TError,{id: string;data: ClientAccountDocumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost3>>, TError,{id: string;data: ClientAccountDocumentDto}, TContext> => {

const mutationKey = ['platformClientAccountDocumentCreatePost3'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost3>>, {id: string;data: ClientAccountDocumentDto}> = (props) => {
          const {id,data} = props ?? {};

          return  platformClientAccountDocumentCreatePost3(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformClientAccountDocumentCreatePost3MutationResult = NonNullable<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost3>>>
    export type PlatformClientAccountDocumentCreatePost3MutationBody = ClientAccountDocumentDto
    export type PlatformClientAccountDocumentCreatePost3MutationError = unknown

    export const usePlatformClientAccountDocumentCreatePost3 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost3>>, TError,{id: string;data: ClientAccountDocumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformClientAccountDocumentCreatePost3>>,
        TError,
        {id: string;data: ClientAccountDocumentDto},
        TContext
      > => {
      return useMutation(getPlatformClientAccountDocumentCreatePost3MutationOptions(options), queryClient);
    }

export type organizationClientAccountsDocumentsRemoveDeleteResponse200 = {
  data: OrganizationClientAccountsDocumentsRemoveDelete200
  status: 200
}

export type organizationClientAccountsDocumentsRemoveDeleteResponseSuccess = (organizationClientAccountsDocumentsRemoveDeleteResponse200) & {
  headers: Headers;
};
;

export type organizationClientAccountsDocumentsRemoveDeleteResponse = (organizationClientAccountsDocumentsRemoveDeleteResponseSuccess)

export const getOrganizationClientAccountsDocumentsRemoveDeleteUrl = (id: string,
    documentId: string,) => {




  return `/api/parties/${id}/documents/${documentId}`
}

export const organizationClientAccountsDocumentsRemoveDelete = async (id: string,
    documentId: string, options?: RequestInit): Promise<organizationClientAccountsDocumentsRemoveDeleteResponse> => {

  return customMutator<organizationClientAccountsDocumentsRemoveDeleteResponse>(getOrganizationClientAccountsDocumentsRemoveDeleteUrl(id,documentId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getOrganizationClientAccountsDocumentsRemoveDeleteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemoveDelete>>, TError,{id: string;documentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemoveDelete>>, TError,{id: string;documentId: string}, TContext> => {

const mutationKey = ['organizationClientAccountsDocumentsRemoveDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemoveDelete>>, {id: string;documentId: string}> = (props) => {
          const {id,documentId} = props ?? {};

          return  organizationClientAccountsDocumentsRemoveDelete(id,documentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationClientAccountsDocumentsRemoveDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemoveDelete>>>

    export type OrganizationClientAccountsDocumentsRemoveDeleteMutationError = unknown

    export const useOrganizationClientAccountsDocumentsRemoveDelete = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemoveDelete>>, TError,{id: string;documentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationClientAccountsDocumentsRemoveDelete>>,
        TError,
        {id: string;documentId: string},
        TContext
      > => {
      return useMutation(getOrganizationClientAccountsDocumentsRemoveDeleteMutationOptions(options), queryClient);
    }

export type investmentPortfolioAccountListResponse200 = {
  data: InvestmentPortfolioAccountList200
  status: 200
}

export type investmentPortfolioAccountListResponseSuccess = (investmentPortfolioAccountListResponse200) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountListResponse = (investmentPortfolioAccountListResponseSuccess)

export const getInvestmentPortfolioAccountListUrl = () => {




  return `/api/accounts`
}

export const investmentPortfolioAccountList = async ( options?: RequestInit): Promise<investmentPortfolioAccountListResponse> => {

  return customMutator<investmentPortfolioAccountListResponse>(getInvestmentPortfolioAccountListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentPortfolioAccountListQueryKey = () => {
    return [
    `/api/accounts`
    ] as const;
    }


export const getInvestmentPortfolioAccountListQueryOptions = <TData = Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentPortfolioAccountListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentPortfolioAccountList>>> = ({ signal }) => investmentPortfolioAccountList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentPortfolioAccountListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountList>>>
export type InvestmentPortfolioAccountListQueryError = unknown


export function useInvestmentPortfolioAccountList<TData = Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountList>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountList<TData = Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountList>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountList<TData = Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentPortfolioAccountList<TData = Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentPortfolioAccountListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPortfolioAccountCreateResponse200 = {
  data: void
  status: 200
}

export type investmentPortfolioAccountCreateResponse201 = {
  data: void
  status: 201
}

export type investmentPortfolioAccountCreateResponseSuccess = (investmentPortfolioAccountCreateResponse200 | investmentPortfolioAccountCreateResponse201) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountCreateResponse = (investmentPortfolioAccountCreateResponseSuccess)

export const getInvestmentPortfolioAccountCreateUrl = () => {




  return `/api/accounts`
}

export const investmentPortfolioAccountCreate = async (createPortfolioAccountDto: CreatePortfolioAccountDto, options?: RequestInit): Promise<investmentPortfolioAccountCreateResponse> => {

  return customMutator<investmentPortfolioAccountCreateResponse>(getInvestmentPortfolioAccountCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createPortfolioAccountDto)
  }
);}





export const getInvestmentPortfolioAccountCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountCreate>>, TError,{data: CreatePortfolioAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountCreate>>, TError,{data: CreatePortfolioAccountDto}, TContext> => {

const mutationKey = ['investmentPortfolioAccountCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPortfolioAccountCreate>>, {data: CreatePortfolioAccountDto}> = (props) => {
          const {data} = props ?? {};

          return  investmentPortfolioAccountCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPortfolioAccountCreateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountCreate>>>
    export type InvestmentPortfolioAccountCreateMutationBody = CreatePortfolioAccountDto
    export type InvestmentPortfolioAccountCreateMutationError = unknown

    export const useInvestmentPortfolioAccountCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountCreate>>, TError,{data: CreatePortfolioAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPortfolioAccountCreate>>,
        TError,
        {data: CreatePortfolioAccountDto},
        TContext
      > => {
      return useMutation(getInvestmentPortfolioAccountCreateMutationOptions(options), queryClient);
    }

export type investmentPortfolioAccountGetResponse200 = {
  data: InvestmentPortfolioAccountGet200
  status: 200
}

export type investmentPortfolioAccountGetResponseSuccess = (investmentPortfolioAccountGetResponse200) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountGetResponse = (investmentPortfolioAccountGetResponseSuccess)

export const getInvestmentPortfolioAccountGetUrl = (id: string,) => {




  return `/api/accounts/${id}`
}

export const investmentPortfolioAccountGet = async (id: string, options?: RequestInit): Promise<investmentPortfolioAccountGetResponse> => {

  return customMutator<investmentPortfolioAccountGetResponse>(getInvestmentPortfolioAccountGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentPortfolioAccountGetQueryKey = (id: string,) => {
    return [
    `/api/accounts/${id}`
    ] as const;
    }


export const getInvestmentPortfolioAccountGetQueryOptions = <TData = Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentPortfolioAccountGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentPortfolioAccountGet>>> = ({ signal }) => investmentPortfolioAccountGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentPortfolioAccountGetQueryResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountGet>>>
export type InvestmentPortfolioAccountGetQueryError = unknown


export function useInvestmentPortfolioAccountGet<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountGet>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountGet<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountGet>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountGet<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentPortfolioAccountGet<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentPortfolioAccountGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPortfolioAccountUpdateResponse200 = {
  data: void
  status: 200
}

export type investmentPortfolioAccountUpdateResponseSuccess = (investmentPortfolioAccountUpdateResponse200) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountUpdateResponse = (investmentPortfolioAccountUpdateResponseSuccess)

export const getInvestmentPortfolioAccountUpdateUrl = (id: string,) => {




  return `/api/accounts/${id}`
}

export const investmentPortfolioAccountUpdate = async (id: string,
    updatePortfolioAccountDto: UpdatePortfolioAccountDto, options?: RequestInit): Promise<investmentPortfolioAccountUpdateResponse> => {

  return customMutator<investmentPortfolioAccountUpdateResponse>(getInvestmentPortfolioAccountUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updatePortfolioAccountDto)
  }
);}





export const getInvestmentPortfolioAccountUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountUpdate>>, TError,{id: string;data: UpdatePortfolioAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountUpdate>>, TError,{id: string;data: UpdatePortfolioAccountDto}, TContext> => {

const mutationKey = ['investmentPortfolioAccountUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPortfolioAccountUpdate>>, {id: string;data: UpdatePortfolioAccountDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentPortfolioAccountUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPortfolioAccountUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountUpdate>>>
    export type InvestmentPortfolioAccountUpdateMutationBody = UpdatePortfolioAccountDto
    export type InvestmentPortfolioAccountUpdateMutationError = unknown

    export const useInvestmentPortfolioAccountUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountUpdate>>, TError,{id: string;data: UpdatePortfolioAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPortfolioAccountUpdate>>,
        TError,
        {id: string;data: UpdatePortfolioAccountDto},
        TContext
      > => {
      return useMutation(getInvestmentPortfolioAccountUpdateMutationOptions(options), queryClient);
    }

export type investmentPortfolioAccountGetBalanceResponse200 = {
  data: InvestmentPortfolioAccountGetBalance200
  status: 200
}

export type investmentPortfolioAccountGetBalanceResponseSuccess = (investmentPortfolioAccountGetBalanceResponse200) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountGetBalanceResponse = (investmentPortfolioAccountGetBalanceResponseSuccess)

export const getInvestmentPortfolioAccountGetBalanceUrl = (id: string,) => {




  return `/api/accounts/${id}/balance`
}

export const investmentPortfolioAccountGetBalance = async (id: string, options?: RequestInit): Promise<investmentPortfolioAccountGetBalanceResponse> => {

  return customMutator<investmentPortfolioAccountGetBalanceResponse>(getInvestmentPortfolioAccountGetBalanceUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentPortfolioAccountGetBalanceQueryKey = (id: string,) => {
    return [
    `/api/accounts/${id}/balance`
    ] as const;
    }


export const getInvestmentPortfolioAccountGetBalanceQueryOptions = <TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentPortfolioAccountGetBalanceQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>> = ({ signal }) => investmentPortfolioAccountGetBalance(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentPortfolioAccountGetBalanceQueryResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>>
export type InvestmentPortfolioAccountGetBalanceQueryError = unknown


export function useInvestmentPortfolioAccountGetBalance<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountGetBalance<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountGetBalance<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentPortfolioAccountGetBalance<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetBalance>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentPortfolioAccountGetBalanceQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPortfolioAccountSignatoriesListResponse200 = {
  data: InvestmentPortfolioAccountSignatoriesList200
  status: 200
}

export type investmentPortfolioAccountSignatoriesListResponseSuccess = (investmentPortfolioAccountSignatoriesListResponse200) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountSignatoriesListResponse = (investmentPortfolioAccountSignatoriesListResponseSuccess)

export const getInvestmentPortfolioAccountSignatoriesListUrl = (id: string,) => {




  return `/api/accounts/${id}/signatories`
}

export const investmentPortfolioAccountSignatoriesList = async (id: string, options?: RequestInit): Promise<investmentPortfolioAccountSignatoriesListResponse> => {

  return customMutator<investmentPortfolioAccountSignatoriesListResponse>(getInvestmentPortfolioAccountSignatoriesListUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentPortfolioAccountSignatoriesListQueryKey = (id: string,) => {
    return [
    `/api/accounts/${id}/signatories`
    ] as const;
    }


export const getInvestmentPortfolioAccountSignatoriesListQueryOptions = <TData = Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentPortfolioAccountSignatoriesListQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>> = ({ signal }) => investmentPortfolioAccountSignatoriesList(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentPortfolioAccountSignatoriesListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>>
export type InvestmentPortfolioAccountSignatoriesListQueryError = unknown


export function useInvestmentPortfolioAccountSignatoriesList<TData = Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountSignatoriesList<TData = Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountSignatoriesList<TData = Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentPortfolioAccountSignatoriesList<TData = Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoriesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentPortfolioAccountSignatoriesListQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPortfolioAccountSignatoryCreateResponse200 = {
  data: void
  status: 200
}

export type investmentPortfolioAccountSignatoryCreateResponse201 = {
  data: void
  status: 201
}

export type investmentPortfolioAccountSignatoryCreateResponseSuccess = (investmentPortfolioAccountSignatoryCreateResponse200 | investmentPortfolioAccountSignatoryCreateResponse201) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountSignatoryCreateResponse = (investmentPortfolioAccountSignatoryCreateResponseSuccess)

export const getInvestmentPortfolioAccountSignatoryCreateUrl = (id: string,) => {




  return `/api/accounts/${id}/signatories`
}

export const investmentPortfolioAccountSignatoryCreate = async (id: string,
    signatoryDto: SignatoryDto, options?: RequestInit): Promise<investmentPortfolioAccountSignatoryCreateResponse> => {

  return customMutator<investmentPortfolioAccountSignatoryCreateResponse>(getInvestmentPortfolioAccountSignatoryCreateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(signatoryDto)
  }
);}





export const getInvestmentPortfolioAccountSignatoryCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryCreate>>, TError,{id: string;data: SignatoryDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryCreate>>, TError,{id: string;data: SignatoryDto}, TContext> => {

const mutationKey = ['investmentPortfolioAccountSignatoryCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryCreate>>, {id: string;data: SignatoryDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentPortfolioAccountSignatoryCreate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPortfolioAccountSignatoryCreateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryCreate>>>
    export type InvestmentPortfolioAccountSignatoryCreateMutationBody = SignatoryDto
    export type InvestmentPortfolioAccountSignatoryCreateMutationError = unknown

    export const useInvestmentPortfolioAccountSignatoryCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryCreate>>, TError,{id: string;data: SignatoryDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryCreate>>,
        TError,
        {id: string;data: SignatoryDto},
        TContext
      > => {
      return useMutation(getInvestmentPortfolioAccountSignatoryCreateMutationOptions(options), queryClient);
    }

export type investmentPortfolioAccountSignatoryDeleteResponse200 = {
  data: void
  status: 200
}

export type investmentPortfolioAccountSignatoryDeleteResponseSuccess = (investmentPortfolioAccountSignatoryDeleteResponse200) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountSignatoryDeleteResponse = (investmentPortfolioAccountSignatoryDeleteResponseSuccess)

export const getInvestmentPortfolioAccountSignatoryDeleteUrl = (id: string,
    signatoryId: string,) => {




  return `/api/accounts/${id}/signatories/${signatoryId}`
}

export const investmentPortfolioAccountSignatoryDelete = async (id: string,
    signatoryId: string, options?: RequestInit): Promise<investmentPortfolioAccountSignatoryDeleteResponse> => {

  return customMutator<investmentPortfolioAccountSignatoryDeleteResponse>(getInvestmentPortfolioAccountSignatoryDeleteUrl(id,signatoryId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getInvestmentPortfolioAccountSignatoryDeleteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryDelete>>, TError,{id: string;signatoryId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryDelete>>, TError,{id: string;signatoryId: string}, TContext> => {

const mutationKey = ['investmentPortfolioAccountSignatoryDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryDelete>>, {id: string;signatoryId: string}> = (props) => {
          const {id,signatoryId} = props ?? {};

          return  investmentPortfolioAccountSignatoryDelete(id,signatoryId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPortfolioAccountSignatoryDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryDelete>>>

    export type InvestmentPortfolioAccountSignatoryDeleteMutationError = unknown

    export const useInvestmentPortfolioAccountSignatoryDelete = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryDelete>>, TError,{id: string;signatoryId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPortfolioAccountSignatoryDelete>>,
        TError,
        {id: string;signatoryId: string},
        TContext
      > => {
      return useMutation(getInvestmentPortfolioAccountSignatoryDeleteMutationOptions(options), queryClient);
    }

export type investmentPortfolioAccountGetGetResponse200 = {
  data: void
  status: 200
}

export type investmentPortfolioAccountGetGetResponseSuccess = (investmentPortfolioAccountGetGetResponse200) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountGetGetResponse = (investmentPortfolioAccountGetGetResponseSuccess)

export const getInvestmentPortfolioAccountGetGetUrl = (id: string,) => {




  return `/api/accounts/${id}/cash-movements`
}

export const investmentPortfolioAccountGetGet = async (id: string, options?: RequestInit): Promise<investmentPortfolioAccountGetGetResponse> => {

  return customMutator<investmentPortfolioAccountGetGetResponse>(getInvestmentPortfolioAccountGetGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentPortfolioAccountGetGetQueryKey = (id: string,) => {
    return [
    `/api/accounts/${id}/cash-movements`
    ] as const;
    }


export const getInvestmentPortfolioAccountGetGetQueryOptions = <TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentPortfolioAccountGetGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>> = ({ signal }) => investmentPortfolioAccountGetGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentPortfolioAccountGetGetQueryResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>>
export type InvestmentPortfolioAccountGetGetQueryError = unknown


export function useInvestmentPortfolioAccountGetGet<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountGetGet<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>,
          TError,
          Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPortfolioAccountGetGet<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentPortfolioAccountGetGet<TData = Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPortfolioAccountGetGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentPortfolioAccountGetGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPortfolioAccountCreatePostResponse200 = {
  data: void
  status: 200
}

export type investmentPortfolioAccountCreatePostResponse201 = {
  data: void
  status: 201
}

export type investmentPortfolioAccountCreatePostResponseSuccess = (investmentPortfolioAccountCreatePostResponse200 | investmentPortfolioAccountCreatePostResponse201) & {
  headers: Headers;
};
;

export type investmentPortfolioAccountCreatePostResponse = (investmentPortfolioAccountCreatePostResponseSuccess)

export const getInvestmentPortfolioAccountCreatePostUrl = (id: string,) => {




  return `/api/accounts/${id}/cash-movements`
}

export const investmentPortfolioAccountCreatePost = async (id: string,
    createCashMovementDto: CreateCashMovementDto, options?: RequestInit): Promise<investmentPortfolioAccountCreatePostResponse> => {

  return customMutator<investmentPortfolioAccountCreatePostResponse>(getInvestmentPortfolioAccountCreatePostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createCashMovementDto)
  }
);}





export const getInvestmentPortfolioAccountCreatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountCreatePost>>, TError,{id: string;data: CreateCashMovementDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountCreatePost>>, TError,{id: string;data: CreateCashMovementDto}, TContext> => {

const mutationKey = ['investmentPortfolioAccountCreatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPortfolioAccountCreatePost>>, {id: string;data: CreateCashMovementDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentPortfolioAccountCreatePost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPortfolioAccountCreatePostMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPortfolioAccountCreatePost>>>
    export type InvestmentPortfolioAccountCreatePostMutationBody = CreateCashMovementDto
    export type InvestmentPortfolioAccountCreatePostMutationError = unknown

    export const useInvestmentPortfolioAccountCreatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPortfolioAccountCreatePost>>, TError,{id: string;data: CreateCashMovementDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPortfolioAccountCreatePost>>,
        TError,
        {id: string;data: CreateCashMovementDto},
        TContext
      > => {
      return useMutation(getInvestmentPortfolioAccountCreatePostMutationOptions(options), queryClient);
    }

export type investmentTransactionApproveResponse200 = {
  data: void
  status: 200
}

export type investmentTransactionApproveResponse201 = {
  data: void
  status: 201
}

export type investmentTransactionApproveResponseSuccess = (investmentTransactionApproveResponse200 | investmentTransactionApproveResponse201) & {
  headers: Headers;
};
;

export type investmentTransactionApproveResponse = (investmentTransactionApproveResponseSuccess)

export const getInvestmentTransactionApproveUrl = (id: string,
    movementId: string,) => {




  return `/api/accounts/${id}/cash-movements/${movementId}/decision`
}

export const investmentTransactionApprove = async (id: string,
    movementId: string,
    cashMovementDecisionDto: CashMovementDecisionDto, options?: RequestInit): Promise<investmentTransactionApproveResponse> => {

  return customMutator<investmentTransactionApproveResponse>(getInvestmentTransactionApproveUrl(id,movementId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(cashMovementDecisionDto)
  }
);}





export const getInvestmentTransactionApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionApprove>>, TError,{id: string;movementId: string;data: CashMovementDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionApprove>>, TError,{id: string;movementId: string;data: CashMovementDecisionDto}, TContext> => {

const mutationKey = ['investmentTransactionApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionApprove>>, {id: string;movementId: string;data: CashMovementDecisionDto}> = (props) => {
          const {id,movementId,data} = props ?? {};

          return  investmentTransactionApprove(id,movementId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionApproveMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionApprove>>>
    export type InvestmentTransactionApproveMutationBody = CashMovementDecisionDto
    export type InvestmentTransactionApproveMutationError = unknown

    export const useInvestmentTransactionApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionApprove>>, TError,{id: string;movementId: string;data: CashMovementDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionApprove>>,
        TError,
        {id: string;movementId: string;data: CashMovementDecisionDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionApproveMutationOptions(options), queryClient);
    }

export type investmentTransactionPostResponse200 = {
  data: void
  status: 200
}

export type investmentTransactionPostResponse201 = {
  data: void
  status: 201
}

export type investmentTransactionPostResponseSuccess = (investmentTransactionPostResponse200 | investmentTransactionPostResponse201) & {
  headers: Headers;
};
;

export type investmentTransactionPostResponse = (investmentTransactionPostResponseSuccess)

export const getInvestmentTransactionPostUrl = (id: string,
    movementId: string,) => {




  return `/api/accounts/${id}/cash-movements/${movementId}/post`
}

export const investmentTransactionPost = async (id: string,
    movementId: string,
    cashMovementPostDto: CashMovementPostDto, options?: RequestInit): Promise<investmentTransactionPostResponse> => {

  return customMutator<investmentTransactionPostResponse>(getInvestmentTransactionPostUrl(id,movementId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(cashMovementPostDto)
  }
);}





export const getInvestmentTransactionPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionPost>>, TError,{id: string;movementId: string;data: CashMovementPostDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionPost>>, TError,{id: string;movementId: string;data: CashMovementPostDto}, TContext> => {

const mutationKey = ['investmentTransactionPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionPost>>, {id: string;movementId: string;data: CashMovementPostDto}> = (props) => {
          const {id,movementId,data} = props ?? {};

          return  investmentTransactionPost(id,movementId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionPostMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionPost>>>
    export type InvestmentTransactionPostMutationBody = CashMovementPostDto
    export type InvestmentTransactionPostMutationError = unknown

    export const useInvestmentTransactionPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionPost>>, TError,{id: string;movementId: string;data: CashMovementPostDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionPost>>,
        TError,
        {id: string;movementId: string;data: CashMovementPostDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionPostMutationOptions(options), queryClient);
    }

export type investmentTransactionReverseResponse200 = {
  data: void
  status: 200
}

export type investmentTransactionReverseResponse201 = {
  data: void
  status: 201
}

export type investmentTransactionReverseResponseSuccess = (investmentTransactionReverseResponse200 | investmentTransactionReverseResponse201) & {
  headers: Headers;
};
;

export type investmentTransactionReverseResponse = (investmentTransactionReverseResponseSuccess)

export const getInvestmentTransactionReverseUrl = (id: string,
    movementId: string,) => {




  return `/api/accounts/${id}/cash-movements/${movementId}/reverse`
}

export const investmentTransactionReverse = async (id: string,
    movementId: string,
    cashMovementPostDto: CashMovementPostDto, options?: RequestInit): Promise<investmentTransactionReverseResponse> => {

  return customMutator<investmentTransactionReverseResponse>(getInvestmentTransactionReverseUrl(id,movementId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(cashMovementPostDto)
  }
);}





export const getInvestmentTransactionReverseMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionReverse>>, TError,{id: string;movementId: string;data: CashMovementPostDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionReverse>>, TError,{id: string;movementId: string;data: CashMovementPostDto}, TContext> => {

const mutationKey = ['investmentTransactionReverse'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionReverse>>, {id: string;movementId: string;data: CashMovementPostDto}> = (props) => {
          const {id,movementId,data} = props ?? {};

          return  investmentTransactionReverse(id,movementId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionReverseMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionReverse>>>
    export type InvestmentTransactionReverseMutationBody = CashMovementPostDto
    export type InvestmentTransactionReverseMutationError = unknown

    export const useInvestmentTransactionReverse = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionReverse>>, TError,{id: string;movementId: string;data: CashMovementPostDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionReverse>>,
        TError,
        {id: string;movementId: string;data: CashMovementPostDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionReverseMutationOptions(options), queryClient);
    }

export type accountingCoaCreateGroupResponse200 = {
  data: void
  status: 200
}

export type accountingCoaCreateGroupResponse201 = {
  data: void
  status: 201
}

export type accountingCoaCreateGroupResponseSuccess = (accountingCoaCreateGroupResponse200 | accountingCoaCreateGroupResponse201) & {
  headers: Headers;
};
;

export type accountingCoaCreateGroupResponse = (accountingCoaCreateGroupResponseSuccess)

export const getAccountingCoaCreateGroupUrl = () => {




  return `/api/accounting/coa/groups`
}

export const accountingCoaCreateGroup = async (createAccountGroupDto: CreateAccountGroupDto, options?: RequestInit): Promise<accountingCoaCreateGroupResponse> => {

  return customMutator<accountingCoaCreateGroupResponse>(getAccountingCoaCreateGroupUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createAccountGroupDto)
  }
);}





export const getAccountingCoaCreateGroupMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingCoaCreateGroup>>, TError,{data: CreateAccountGroupDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingCoaCreateGroup>>, TError,{data: CreateAccountGroupDto}, TContext> => {

const mutationKey = ['accountingCoaCreateGroup'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingCoaCreateGroup>>, {data: CreateAccountGroupDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingCoaCreateGroup(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingCoaCreateGroupMutationResult = NonNullable<Awaited<ReturnType<typeof accountingCoaCreateGroup>>>
    export type AccountingCoaCreateGroupMutationBody = CreateAccountGroupDto
    export type AccountingCoaCreateGroupMutationError = unknown

    export const useAccountingCoaCreateGroup = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingCoaCreateGroup>>, TError,{data: CreateAccountGroupDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingCoaCreateGroup>>,
        TError,
        {data: CreateAccountGroupDto},
        TContext
      > => {
      return useMutation(getAccountingCoaCreateGroupMutationOptions(options), queryClient);
    }

export type accountingCoaGetGroupsResponse200 = {
  data: void
  status: 200
}

export type accountingCoaGetGroupsResponseSuccess = (accountingCoaGetGroupsResponse200) & {
  headers: Headers;
};
;

export type accountingCoaGetGroupsResponse = (accountingCoaGetGroupsResponseSuccess)

export const getAccountingCoaGetGroupsUrl = () => {




  return `/api/accounting/coa/groups`
}

export const accountingCoaGetGroups = async ( options?: RequestInit): Promise<accountingCoaGetGroupsResponse> => {

  return customMutator<accountingCoaGetGroupsResponse>(getAccountingCoaGetGroupsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingCoaGetGroupsQueryKey = () => {
    return [
    `/api/accounting/coa/groups`
    ] as const;
    }


export const getAccountingCoaGetGroupsQueryOptions = <TData = Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingCoaGetGroupsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingCoaGetGroups>>> = ({ signal }) => accountingCoaGetGroups({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingCoaGetGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof accountingCoaGetGroups>>>
export type AccountingCoaGetGroupsQueryError = unknown


export function useAccountingCoaGetGroups<TData = Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingCoaGetGroups>>,
          TError,
          Awaited<ReturnType<typeof accountingCoaGetGroups>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingCoaGetGroups<TData = Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingCoaGetGroups>>,
          TError,
          Awaited<ReturnType<typeof accountingCoaGetGroups>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingCoaGetGroups<TData = Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingCoaGetGroups<TData = Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetGroups>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingCoaGetGroupsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingManagementFeeListResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeListResponseSuccess = (accountingManagementFeeListResponse200) & {
  headers: Headers;
};
;

export type accountingManagementFeeListResponse = (accountingManagementFeeListResponseSuccess)

export const getAccountingManagementFeeListUrl = () => {




  return `/api/accounting/management-fees`
}

export const accountingManagementFeeList = async ( options?: RequestInit): Promise<accountingManagementFeeListResponse> => {

  return customMutator<accountingManagementFeeListResponse>(getAccountingManagementFeeListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingManagementFeeListQueryKey = () => {
    return [
    `/api/accounting/management-fees`
    ] as const;
    }


export const getAccountingManagementFeeListQueryOptions = <TData = Awaited<ReturnType<typeof accountingManagementFeeList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingManagementFeeListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingManagementFeeList>>> = ({ signal }) => accountingManagementFeeList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingManagementFeeListQueryResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeList>>>
export type AccountingManagementFeeListQueryError = unknown


export function useAccountingManagementFeeList<TData = Awaited<ReturnType<typeof accountingManagementFeeList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingManagementFeeList>>,
          TError,
          Awaited<ReturnType<typeof accountingManagementFeeList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingManagementFeeList<TData = Awaited<ReturnType<typeof accountingManagementFeeList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingManagementFeeList>>,
          TError,
          Awaited<ReturnType<typeof accountingManagementFeeList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingManagementFeeList<TData = Awaited<ReturnType<typeof accountingManagementFeeList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingManagementFeeList<TData = Awaited<ReturnType<typeof accountingManagementFeeList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingManagementFeeListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingPerformanceFeeListResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeListResponseSuccess = (accountingPerformanceFeeListResponse200) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeListResponse = (accountingPerformanceFeeListResponseSuccess)

export const getAccountingPerformanceFeeListUrl = () => {




  return `/api/accounting/performance-fees`
}

export const accountingPerformanceFeeList = async ( options?: RequestInit): Promise<accountingPerformanceFeeListResponse> => {

  return customMutator<accountingPerformanceFeeListResponse>(getAccountingPerformanceFeeListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingPerformanceFeeListQueryKey = () => {
    return [
    `/api/accounting/performance-fees`
    ] as const;
    }


export const getAccountingPerformanceFeeListQueryOptions = <TData = Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingPerformanceFeeListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingPerformanceFeeList>>> = ({ signal }) => accountingPerformanceFeeList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingPerformanceFeeListQueryResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeList>>>
export type AccountingPerformanceFeeListQueryError = unknown


export function useAccountingPerformanceFeeList<TData = Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingPerformanceFeeList>>,
          TError,
          Awaited<ReturnType<typeof accountingPerformanceFeeList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingPerformanceFeeList<TData = Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingPerformanceFeeList>>,
          TError,
          Awaited<ReturnType<typeof accountingPerformanceFeeList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingPerformanceFeeList<TData = Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingPerformanceFeeList<TData = Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingPerformanceFeeListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingPerformanceFeeCalculateResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeCalculateResponseSuccess = (accountingPerformanceFeeCalculateResponse200) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeCalculateResponse = (accountingPerformanceFeeCalculateResponseSuccess)

export const getAccountingPerformanceFeeCalculateUrl = () => {




  return `/api/accounting/performance-fees/calculate-eligibility`
}

export const accountingPerformanceFeeCalculate = async ( options?: RequestInit): Promise<accountingPerformanceFeeCalculateResponse> => {

  return customMutator<accountingPerformanceFeeCalculateResponse>(getAccountingPerformanceFeeCalculateUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingPerformanceFeeCalculateQueryKey = () => {
    return [
    `/api/accounting/performance-fees/calculate-eligibility`
    ] as const;
    }


export const getAccountingPerformanceFeeCalculateQueryOptions = <TData = Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingPerformanceFeeCalculateQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>> = ({ signal }) => accountingPerformanceFeeCalculate({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingPerformanceFeeCalculateQueryResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>>
export type AccountingPerformanceFeeCalculateQueryError = unknown


export function useAccountingPerformanceFeeCalculate<TData = Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>,
          TError,
          Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingPerformanceFeeCalculate<TData = Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>,
          TError,
          Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingPerformanceFeeCalculate<TData = Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingPerformanceFeeCalculate<TData = Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculate>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingPerformanceFeeCalculateQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingPerformanceFeeCalculatePostResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeCalculatePostResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeCalculatePostResponseSuccess = (accountingPerformanceFeeCalculatePostResponse200 | accountingPerformanceFeeCalculatePostResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeCalculatePostResponse = (accountingPerformanceFeeCalculatePostResponseSuccess)

export const getAccountingPerformanceFeeCalculatePostUrl = () => {




  return `/api/accounting/performance-fees/calculate`
}

export const accountingPerformanceFeeCalculatePost = async ( options?: RequestInit): Promise<accountingPerformanceFeeCalculatePostResponse> => {

  return customMutator<accountingPerformanceFeeCalculatePostResponse>(getAccountingPerformanceFeeCalculatePostUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeeCalculatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost>>, TError,void, TContext> => {

const mutationKey = ['accountingPerformanceFeeCalculatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost>>, void> = () => {


          return  accountingPerformanceFeeCalculatePost(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeCalculatePostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost>>>

    export type AccountingPerformanceFeeCalculatePostMutationError = unknown

    export const useAccountingPerformanceFeeCalculatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeCalculatePostMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeApproveResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeApproveResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeApproveResponseSuccess = (accountingPerformanceFeeApproveResponse200 | accountingPerformanceFeeApproveResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeApproveResponse = (accountingPerformanceFeeApproveResponseSuccess)

export const getAccountingPerformanceFeeApproveUrl = (id: string,) => {




  return `/api/accounting/performance-fees/${id}/approve`
}

export const accountingPerformanceFeeApprove = async (id: string, options?: RequestInit): Promise<accountingPerformanceFeeApproveResponse> => {

  return customMutator<accountingPerformanceFeeApproveResponse>(getAccountingPerformanceFeeApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeeApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingPerformanceFeeApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingPerformanceFeeApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeApproveMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeApprove>>>

    export type AccountingPerformanceFeeApproveMutationError = unknown

    export const useAccountingPerformanceFeeApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeApproveMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeCrystallizeResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeCrystallizeResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeCrystallizeResponseSuccess = (accountingPerformanceFeeCrystallizeResponse200 | accountingPerformanceFeeCrystallizeResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeCrystallizeResponse = (accountingPerformanceFeeCrystallizeResponseSuccess)

export const getAccountingPerformanceFeeCrystallizeUrl = (id: string,) => {




  return `/api/accounting/performance-fees/${id}/crystallize`
}

export const accountingPerformanceFeeCrystallize = async (id: string, options?: RequestInit): Promise<accountingPerformanceFeeCrystallizeResponse> => {

  return customMutator<accountingPerformanceFeeCrystallizeResponse>(getAccountingPerformanceFeeCrystallizeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeeCrystallizeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCrystallize>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCrystallize>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingPerformanceFeeCrystallize'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeCrystallize>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingPerformanceFeeCrystallize(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeCrystallizeMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeCrystallize>>>

    export type AccountingPerformanceFeeCrystallizeMutationError = unknown

    export const useAccountingPerformanceFeeCrystallize = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCrystallize>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeCrystallize>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeCrystallizeMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeRestateResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeRestateResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeRestateResponseSuccess = (accountingPerformanceFeeRestateResponse200 | accountingPerformanceFeeRestateResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeRestateResponse = (accountingPerformanceFeeRestateResponseSuccess)

export const getAccountingPerformanceFeeRestateUrl = (id: string,) => {




  return `/api/accounting/performance-fees/${id}/restatements`
}

export const accountingPerformanceFeeRestate = async (id: string,
    requestPerformanceFeeRestatementDto: RequestPerformanceFeeRestatementDto, options?: RequestInit): Promise<accountingPerformanceFeeRestateResponse> => {

  return customMutator<accountingPerformanceFeeRestateResponse>(getAccountingPerformanceFeeRestateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(requestPerformanceFeeRestatementDto)
  }
);}





export const getAccountingPerformanceFeeRestateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestate>>, TError,{id: string;data: RequestPerformanceFeeRestatementDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestate>>, TError,{id: string;data: RequestPerformanceFeeRestatementDto}, TContext> => {

const mutationKey = ['accountingPerformanceFeeRestate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeRestate>>, {id: string;data: RequestPerformanceFeeRestatementDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingPerformanceFeeRestate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeRestateMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeRestate>>>
    export type AccountingPerformanceFeeRestateMutationBody = RequestPerformanceFeeRestatementDto
    export type AccountingPerformanceFeeRestateMutationError = unknown

    export const useAccountingPerformanceFeeRestate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestate>>, TError,{id: string;data: RequestPerformanceFeeRestatementDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeRestate>>,
        TError,
        {id: string;data: RequestPerformanceFeeRestatementDto},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeRestateMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeRestatePostResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeRestatePostResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeRestatePostResponseSuccess = (accountingPerformanceFeeRestatePostResponse200 | accountingPerformanceFeeRestatePostResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeRestatePostResponse = (accountingPerformanceFeeRestatePostResponseSuccess)

export const getAccountingPerformanceFeeRestatePostUrl = (id: string,) => {




  return `/api/accounting/performance-fee-restatements/${id}/approve`
}

export const accountingPerformanceFeeRestatePost = async (id: string, options?: RequestInit): Promise<accountingPerformanceFeeRestatePostResponse> => {

  return customMutator<accountingPerformanceFeeRestatePostResponse>(getAccountingPerformanceFeeRestatePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeeRestatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingPerformanceFeeRestatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingPerformanceFeeRestatePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeRestatePostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost>>>

    export type AccountingPerformanceFeeRestatePostMutationError = unknown

    export const useAccountingPerformanceFeeRestatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeRestatePostMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeRestatePost1Response200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeRestatePost1Response201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeRestatePost1ResponseSuccess = (accountingPerformanceFeeRestatePost1Response200 | accountingPerformanceFeeRestatePost1Response201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeRestatePost1Response = (accountingPerformanceFeeRestatePost1ResponseSuccess)

export const getAccountingPerformanceFeeRestatePost1Url = (id: string,) => {




  return `/api/accounting/performance-fee-restatements/${id}/finalize`
}

export const accountingPerformanceFeeRestatePost1 = async (id: string,
    finalizePerformanceFeeRestatementDto: FinalizePerformanceFeeRestatementDto, options?: RequestInit): Promise<accountingPerformanceFeeRestatePost1Response> => {

  return customMutator<accountingPerformanceFeeRestatePost1Response>(getAccountingPerformanceFeeRestatePost1Url(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(finalizePerformanceFeeRestatementDto)
  }
);}





export const getAccountingPerformanceFeeRestatePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost1>>, TError,{id: string;data: FinalizePerformanceFeeRestatementDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost1>>, TError,{id: string;data: FinalizePerformanceFeeRestatementDto}, TContext> => {

const mutationKey = ['accountingPerformanceFeeRestatePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost1>>, {id: string;data: FinalizePerformanceFeeRestatementDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingPerformanceFeeRestatePost1(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeRestatePost1MutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost1>>>
    export type AccountingPerformanceFeeRestatePost1MutationBody = FinalizePerformanceFeeRestatementDto
    export type AccountingPerformanceFeeRestatePost1MutationError = unknown

    export const useAccountingPerformanceFeeRestatePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost1>>, TError,{id: string;data: FinalizePerformanceFeeRestatementDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeRestatePost1>>,
        TError,
        {id: string;data: FinalizePerformanceFeeRestatementDto},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeRestatePost1MutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeRestatementReversalPostResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeRestatementReversalPostResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeRestatementReversalPostResponseSuccess = (accountingPerformanceFeeRestatementReversalPostResponse200 | accountingPerformanceFeeRestatementReversalPostResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeRestatementReversalPostResponse = (accountingPerformanceFeeRestatementReversalPostResponseSuccess)

export const getAccountingPerformanceFeeRestatementReversalPostUrl = (id: string,) => {




  return `/api/accounting/performance-fee-restatements/${id}/reversal/post`
}

export const accountingPerformanceFeeRestatementReversalPost = async (id: string,
    postPerformanceFeeRestatementReversalDto: PostPerformanceFeeRestatementReversalDto, options?: RequestInit): Promise<accountingPerformanceFeeRestatementReversalPostResponse> => {

  return customMutator<accountingPerformanceFeeRestatementReversalPostResponse>(getAccountingPerformanceFeeRestatementReversalPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(postPerformanceFeeRestatementReversalDto)
  }
);}





export const getAccountingPerformanceFeeRestatementReversalPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestatementReversalPost>>, TError,{id: string;data: PostPerformanceFeeRestatementReversalDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestatementReversalPost>>, TError,{id: string;data: PostPerformanceFeeRestatementReversalDto}, TContext> => {

const mutationKey = ['accountingPerformanceFeeRestatementReversalPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeRestatementReversalPost>>, {id: string;data: PostPerformanceFeeRestatementReversalDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingPerformanceFeeRestatementReversalPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeRestatementReversalPostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeRestatementReversalPost>>>
    export type AccountingPerformanceFeeRestatementReversalPostMutationBody = PostPerformanceFeeRestatementReversalDto
    export type AccountingPerformanceFeeRestatementReversalPostMutationError = unknown

    export const useAccountingPerformanceFeeRestatementReversalPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeRestatementReversalPost>>, TError,{id: string;data: PostPerformanceFeeRestatementReversalDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeRestatementReversalPost>>,
        TError,
        {id: string;data: PostPerformanceFeeRestatementReversalDto},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeRestatementReversalPostMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeCalculatePost1Response200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeCalculatePost1Response201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeCalculatePost1ResponseSuccess = (accountingPerformanceFeeCalculatePost1Response200 | accountingPerformanceFeeCalculatePost1Response201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeCalculatePost1Response = (accountingPerformanceFeeCalculatePost1ResponseSuccess)

export const getAccountingPerformanceFeeCalculatePost1Url = () => {




  return `/api/accounting/performance-fee-posting-profiles`
}

export const accountingPerformanceFeeCalculatePost1 = async ( options?: RequestInit): Promise<accountingPerformanceFeeCalculatePost1Response> => {

  return customMutator<accountingPerformanceFeeCalculatePost1Response>(getAccountingPerformanceFeeCalculatePost1Url(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeeCalculatePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost1>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost1>>, TError,void, TContext> => {

const mutationKey = ['accountingPerformanceFeeCalculatePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost1>>, void> = () => {


          return  accountingPerformanceFeeCalculatePost1(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeCalculatePost1MutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost1>>>

    export type AccountingPerformanceFeeCalculatePost1MutationError = unknown

    export const useAccountingPerformanceFeeCalculatePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost1>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeCalculatePost1>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeCalculatePost1MutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeApprovePostResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeApprovePostResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeApprovePostResponseSuccess = (accountingPerformanceFeeApprovePostResponse200 | accountingPerformanceFeeApprovePostResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeApprovePostResponse = (accountingPerformanceFeeApprovePostResponseSuccess)

export const getAccountingPerformanceFeeApprovePostUrl = (id: string,) => {




  return `/api/accounting/performance-fee-posting-profiles/${id}/approve`
}

export const accountingPerformanceFeeApprovePost = async (id: string, options?: RequestInit): Promise<accountingPerformanceFeeApprovePostResponse> => {

  return customMutator<accountingPerformanceFeeApprovePostResponse>(getAccountingPerformanceFeeApprovePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeeApprovePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeApprovePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeApprovePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingPerformanceFeeApprovePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeApprovePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingPerformanceFeeApprovePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeApprovePostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeApprovePost>>>

    export type AccountingPerformanceFeeApprovePostMutationError = unknown

    export const useAccountingPerformanceFeeApprovePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeApprovePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeApprovePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeApprovePostMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeInvoiceResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeInvoiceResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeInvoiceResponseSuccess = (accountingPerformanceFeeInvoiceResponse200 | accountingPerformanceFeeInvoiceResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeInvoiceResponse = (accountingPerformanceFeeInvoiceResponseSuccess)

export const getAccountingPerformanceFeeInvoiceUrl = (id: string,) => {




  return `/api/accounting/performance-fees/${id}/invoice`
}

export const accountingPerformanceFeeInvoice = async (id: string, options?: RequestInit): Promise<accountingPerformanceFeeInvoiceResponse> => {

  return customMutator<accountingPerformanceFeeInvoiceResponse>(getAccountingPerformanceFeeInvoiceUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeeInvoiceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeInvoice>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeInvoice>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingPerformanceFeeInvoice'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeInvoice>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingPerformanceFeeInvoice(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeInvoiceMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeInvoice>>>

    export type AccountingPerformanceFeeInvoiceMutationError = unknown

    export const useAccountingPerformanceFeeInvoice = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeInvoice>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeInvoice>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeInvoiceMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeeInvoicePostResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeeInvoicePostResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeeInvoicePostResponseSuccess = (accountingPerformanceFeeInvoicePostResponse200 | accountingPerformanceFeeInvoicePostResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeeInvoicePostResponse = (accountingPerformanceFeeInvoicePostResponseSuccess)

export const getAccountingPerformanceFeeInvoicePostUrl = (id: string,) => {




  return `/api/accounting/performance-fee-invoices/${id}/post`
}

export const accountingPerformanceFeeInvoicePost = async (id: string, options?: RequestInit): Promise<accountingPerformanceFeeInvoicePostResponse> => {

  return customMutator<accountingPerformanceFeeInvoicePostResponse>(getAccountingPerformanceFeeInvoicePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeeInvoicePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeInvoicePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeInvoicePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingPerformanceFeeInvoicePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeeInvoicePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingPerformanceFeeInvoicePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeeInvoicePostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeeInvoicePost>>>

    export type AccountingPerformanceFeeInvoicePostMutationError = unknown

    export const useAccountingPerformanceFeeInvoicePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeeInvoicePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeeInvoicePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeeInvoicePostMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeePayResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeePayResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeePayResponseSuccess = (accountingPerformanceFeePayResponse200 | accountingPerformanceFeePayResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeePayResponse = (accountingPerformanceFeePayResponseSuccess)

export const getAccountingPerformanceFeePayUrl = (id: string,) => {




  return `/api/accounting/performance-fee-invoices/${id}/pay`
}

export const accountingPerformanceFeePay = async (id: string, options?: RequestInit): Promise<accountingPerformanceFeePayResponse> => {

  return customMutator<accountingPerformanceFeePayResponse>(getAccountingPerformanceFeePayUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeePayMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeePay>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeePay>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingPerformanceFeePay'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeePay>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingPerformanceFeePay(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeePayMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeePay>>>

    export type AccountingPerformanceFeePayMutationError = unknown

    export const useAccountingPerformanceFeePay = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeePay>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeePay>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeePayMutationOptions(options), queryClient);
    }

export type accountingPerformanceFeePaymentPostResponse200 = {
  data: void
  status: 200
}

export type accountingPerformanceFeePaymentPostResponse201 = {
  data: void
  status: 201
}

export type accountingPerformanceFeePaymentPostResponseSuccess = (accountingPerformanceFeePaymentPostResponse200 | accountingPerformanceFeePaymentPostResponse201) & {
  headers: Headers;
};
;

export type accountingPerformanceFeePaymentPostResponse = (accountingPerformanceFeePaymentPostResponseSuccess)

export const getAccountingPerformanceFeePaymentPostUrl = (id: string,) => {




  return `/api/accounting/performance-fee-payments/${id}/post`
}

export const accountingPerformanceFeePaymentPost = async (id: string, options?: RequestInit): Promise<accountingPerformanceFeePaymentPostResponse> => {

  return customMutator<accountingPerformanceFeePaymentPostResponse>(getAccountingPerformanceFeePaymentPostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingPerformanceFeePaymentPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeePaymentPost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeePaymentPost>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingPerformanceFeePaymentPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPerformanceFeePaymentPost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingPerformanceFeePaymentPost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPerformanceFeePaymentPostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPerformanceFeePaymentPost>>>

    export type AccountingPerformanceFeePaymentPostMutationError = unknown

    export const useAccountingPerformanceFeePaymentPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPerformanceFeePaymentPost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPerformanceFeePaymentPost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingPerformanceFeePaymentPostMutationOptions(options), queryClient);
    }

export type accountingManagementFeeCalculateResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeCalculateResponse201 = {
  data: void
  status: 201
}

export type accountingManagementFeeCalculateResponseSuccess = (accountingManagementFeeCalculateResponse200 | accountingManagementFeeCalculateResponse201) & {
  headers: Headers;
};
;

export type accountingManagementFeeCalculateResponse = (accountingManagementFeeCalculateResponseSuccess)

export const getAccountingManagementFeeCalculateUrl = () => {




  return `/api/accounting/management-fee-posting-profiles`
}

export const accountingManagementFeeCalculate = async ( options?: RequestInit): Promise<accountingManagementFeeCalculateResponse> => {

  return customMutator<accountingManagementFeeCalculateResponse>(getAccountingManagementFeeCalculateUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingManagementFeeCalculateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculate>>, TError,void, TContext> => {

const mutationKey = ['accountingManagementFeeCalculate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingManagementFeeCalculate>>, void> = () => {


          return  accountingManagementFeeCalculate(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingManagementFeeCalculateMutationResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeCalculate>>>

    export type AccountingManagementFeeCalculateMutationError = unknown

    export const useAccountingManagementFeeCalculate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingManagementFeeCalculate>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAccountingManagementFeeCalculateMutationOptions(options), queryClient);
    }

export type accountingManagementFeeApproveResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeApproveResponse201 = {
  data: void
  status: 201
}

export type accountingManagementFeeApproveResponseSuccess = (accountingManagementFeeApproveResponse200 | accountingManagementFeeApproveResponse201) & {
  headers: Headers;
};
;

export type accountingManagementFeeApproveResponse = (accountingManagementFeeApproveResponseSuccess)

export const getAccountingManagementFeeApproveUrl = (id: string,) => {




  return `/api/accounting/management-fee-posting-profiles/${id}/approve`
}

export const accountingManagementFeeApprove = async (id: string, options?: RequestInit): Promise<accountingManagementFeeApproveResponse> => {

  return customMutator<accountingManagementFeeApproveResponse>(getAccountingManagementFeeApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingManagementFeeApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingManagementFeeApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingManagementFeeApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingManagementFeeApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingManagementFeeApproveMutationResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeApprove>>>

    export type AccountingManagementFeeApproveMutationError = unknown

    export const useAccountingManagementFeeApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingManagementFeeApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingManagementFeeApproveMutationOptions(options), queryClient);
    }

export type accountingManagementFeeCalculateGetResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeCalculateGetResponseSuccess = (accountingManagementFeeCalculateGetResponse200) & {
  headers: Headers;
};
;

export type accountingManagementFeeCalculateGetResponse = (accountingManagementFeeCalculateGetResponseSuccess)

export const getAccountingManagementFeeCalculateGetUrl = () => {




  return `/api/accounting/management-fees/calculate-eligibility`
}

export const accountingManagementFeeCalculateGet = async ( options?: RequestInit): Promise<accountingManagementFeeCalculateGetResponse> => {

  return customMutator<accountingManagementFeeCalculateGetResponse>(getAccountingManagementFeeCalculateGetUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingManagementFeeCalculateGetQueryKey = () => {
    return [
    `/api/accounting/management-fees/calculate-eligibility`
    ] as const;
    }


export const getAccountingManagementFeeCalculateGetQueryOptions = <TData = Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingManagementFeeCalculateGetQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>> = ({ signal }) => accountingManagementFeeCalculateGet({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingManagementFeeCalculateGetQueryResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>>
export type AccountingManagementFeeCalculateGetQueryError = unknown


export function useAccountingManagementFeeCalculateGet<TData = Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>,
          TError,
          Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingManagementFeeCalculateGet<TData = Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>,
          TError,
          Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingManagementFeeCalculateGet<TData = Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingManagementFeeCalculateGet<TData = Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculateGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingManagementFeeCalculateGetQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingManagementFeeCalculatePost1Response200 = {
  data: void
  status: 200
}

export type accountingManagementFeeCalculatePost1Response201 = {
  data: void
  status: 201
}

export type accountingManagementFeeCalculatePost1ResponseSuccess = (accountingManagementFeeCalculatePost1Response200 | accountingManagementFeeCalculatePost1Response201) & {
  headers: Headers;
};
;

export type accountingManagementFeeCalculatePost1Response = (accountingManagementFeeCalculatePost1ResponseSuccess)

export const getAccountingManagementFeeCalculatePost1Url = () => {




  return `/api/accounting/management-fees/calculate`
}

export const accountingManagementFeeCalculatePost1 = async ( options?: RequestInit): Promise<accountingManagementFeeCalculatePost1Response> => {

  return customMutator<accountingManagementFeeCalculatePost1Response>(getAccountingManagementFeeCalculatePost1Url(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingManagementFeeCalculatePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculatePost1>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculatePost1>>, TError,void, TContext> => {

const mutationKey = ['accountingManagementFeeCalculatePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingManagementFeeCalculatePost1>>, void> = () => {


          return  accountingManagementFeeCalculatePost1(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingManagementFeeCalculatePost1MutationResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeCalculatePost1>>>

    export type AccountingManagementFeeCalculatePost1MutationError = unknown

    export const useAccountingManagementFeeCalculatePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeCalculatePost1>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingManagementFeeCalculatePost1>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAccountingManagementFeeCalculatePost1MutationOptions(options), queryClient);
    }

export type accountingManagementFeeGetResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeGetResponseSuccess = (accountingManagementFeeGetResponse200) & {
  headers: Headers;
};
;

export type accountingManagementFeeGetResponse = (accountingManagementFeeGetResponseSuccess)

export const getAccountingManagementFeeGetUrl = (id: string,) => {




  return `/api/accounting/management-fees/${id}`
}

export const accountingManagementFeeGet = async (id: string, options?: RequestInit): Promise<accountingManagementFeeGetResponse> => {

  return customMutator<accountingManagementFeeGetResponse>(getAccountingManagementFeeGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingManagementFeeGetQueryKey = (id: string,) => {
    return [
    `/api/accounting/management-fees/${id}`
    ] as const;
    }


export const getAccountingManagementFeeGetQueryOptions = <TData = Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingManagementFeeGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingManagementFeeGet>>> = ({ signal }) => accountingManagementFeeGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingManagementFeeGetQueryResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeGet>>>
export type AccountingManagementFeeGetQueryError = unknown


export function useAccountingManagementFeeGet<TData = Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingManagementFeeGet>>,
          TError,
          Awaited<ReturnType<typeof accountingManagementFeeGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingManagementFeeGet<TData = Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingManagementFeeGet>>,
          TError,
          Awaited<ReturnType<typeof accountingManagementFeeGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingManagementFeeGet<TData = Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingManagementFeeGet<TData = Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingManagementFeeGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingManagementFeeGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingManagementFeeApprovePostResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeApprovePostResponse201 = {
  data: void
  status: 201
}

export type accountingManagementFeeApprovePostResponseSuccess = (accountingManagementFeeApprovePostResponse200 | accountingManagementFeeApprovePostResponse201) & {
  headers: Headers;
};
;

export type accountingManagementFeeApprovePostResponse = (accountingManagementFeeApprovePostResponseSuccess)

export const getAccountingManagementFeeApprovePostUrl = (id: string,) => {




  return `/api/accounting/management-fees/${id}/approve`
}

export const accountingManagementFeeApprovePost = async (id: string, options?: RequestInit): Promise<accountingManagementFeeApprovePostResponse> => {

  return customMutator<accountingManagementFeeApprovePostResponse>(getAccountingManagementFeeApprovePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingManagementFeeApprovePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeApprovePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeApprovePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingManagementFeeApprovePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingManagementFeeApprovePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingManagementFeeApprovePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingManagementFeeApprovePostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeApprovePost>>>

    export type AccountingManagementFeeApprovePostMutationError = unknown

    export const useAccountingManagementFeeApprovePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeApprovePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingManagementFeeApprovePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingManagementFeeApprovePostMutationOptions(options), queryClient);
    }

export type accountingManagementFeeWaiveResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeWaiveResponse201 = {
  data: void
  status: 201
}

export type accountingManagementFeeWaiveResponseSuccess = (accountingManagementFeeWaiveResponse200 | accountingManagementFeeWaiveResponse201) & {
  headers: Headers;
};
;

export type accountingManagementFeeWaiveResponse = (accountingManagementFeeWaiveResponseSuccess)

export const getAccountingManagementFeeWaiveUrl = (id: string,) => {




  return `/api/accounting/management-fees/${id}/waive`
}

export const accountingManagementFeeWaive = async (id: string, options?: RequestInit): Promise<accountingManagementFeeWaiveResponse> => {

  return customMutator<accountingManagementFeeWaiveResponse>(getAccountingManagementFeeWaiveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingManagementFeeWaiveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeWaive>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeWaive>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingManagementFeeWaive'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingManagementFeeWaive>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingManagementFeeWaive(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingManagementFeeWaiveMutationResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeWaive>>>

    export type AccountingManagementFeeWaiveMutationError = unknown

    export const useAccountingManagementFeeWaive = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeWaive>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingManagementFeeWaive>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingManagementFeeWaiveMutationOptions(options), queryClient);
    }

export type accountingManagementFeeInvoiceResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeInvoiceResponse201 = {
  data: void
  status: 201
}

export type accountingManagementFeeInvoiceResponseSuccess = (accountingManagementFeeInvoiceResponse200 | accountingManagementFeeInvoiceResponse201) & {
  headers: Headers;
};
;

export type accountingManagementFeeInvoiceResponse = (accountingManagementFeeInvoiceResponseSuccess)

export const getAccountingManagementFeeInvoiceUrl = (id: string,) => {




  return `/api/accounting/management-fees/${id}/invoice`
}

export const accountingManagementFeeInvoice = async (id: string, options?: RequestInit): Promise<accountingManagementFeeInvoiceResponse> => {

  return customMutator<accountingManagementFeeInvoiceResponse>(getAccountingManagementFeeInvoiceUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingManagementFeeInvoiceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeInvoice>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeInvoice>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingManagementFeeInvoice'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingManagementFeeInvoice>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingManagementFeeInvoice(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingManagementFeeInvoiceMutationResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeInvoice>>>

    export type AccountingManagementFeeInvoiceMutationError = unknown

    export const useAccountingManagementFeeInvoice = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeInvoice>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingManagementFeeInvoice>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingManagementFeeInvoiceMutationOptions(options), queryClient);
    }

export type accountingManagementFeeReverseResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeReverseResponse201 = {
  data: void
  status: 201
}

export type accountingManagementFeeReverseResponseSuccess = (accountingManagementFeeReverseResponse200 | accountingManagementFeeReverseResponse201) & {
  headers: Headers;
};
;

export type accountingManagementFeeReverseResponse = (accountingManagementFeeReverseResponseSuccess)

export const getAccountingManagementFeeReverseUrl = (id: string,) => {




  return `/api/accounting/management-fees/${id}/reverse`
}

export const accountingManagementFeeReverse = async (id: string, options?: RequestInit): Promise<accountingManagementFeeReverseResponse> => {

  return customMutator<accountingManagementFeeReverseResponse>(getAccountingManagementFeeReverseUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingManagementFeeReverseMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeReverse>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeReverse>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingManagementFeeReverse'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingManagementFeeReverse>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingManagementFeeReverse(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingManagementFeeReverseMutationResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeReverse>>>

    export type AccountingManagementFeeReverseMutationError = unknown

    export const useAccountingManagementFeeReverse = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeReverse>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingManagementFeeReverse>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingManagementFeeReverseMutationOptions(options), queryClient);
    }

export type accountingManagementFeePayResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeePayResponse201 = {
  data: void
  status: 201
}

export type accountingManagementFeePayResponseSuccess = (accountingManagementFeePayResponse200 | accountingManagementFeePayResponse201) & {
  headers: Headers;
};
;

export type accountingManagementFeePayResponse = (accountingManagementFeePayResponseSuccess)

export const getAccountingManagementFeePayUrl = (id: string,) => {




  return `/api/accounting/management-fee-invoices/${id}/pay`
}

export const accountingManagementFeePay = async (id: string, options?: RequestInit): Promise<accountingManagementFeePayResponse> => {

  return customMutator<accountingManagementFeePayResponse>(getAccountingManagementFeePayUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingManagementFeePayMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeePay>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeePay>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingManagementFeePay'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingManagementFeePay>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingManagementFeePay(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingManagementFeePayMutationResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeePay>>>

    export type AccountingManagementFeePayMutationError = unknown

    export const useAccountingManagementFeePay = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeePay>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingManagementFeePay>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingManagementFeePayMutationOptions(options), queryClient);
    }

export type accountingManagementFeeCreditResponse200 = {
  data: void
  status: 200
}

export type accountingManagementFeeCreditResponse201 = {
  data: void
  status: 201
}

export type accountingManagementFeeCreditResponseSuccess = (accountingManagementFeeCreditResponse200 | accountingManagementFeeCreditResponse201) & {
  headers: Headers;
};
;

export type accountingManagementFeeCreditResponse = (accountingManagementFeeCreditResponseSuccess)

export const getAccountingManagementFeeCreditUrl = (id: string,) => {




  return `/api/accounting/management-fee-invoices/${id}/credit`
}

export const accountingManagementFeeCredit = async (id: string, options?: RequestInit): Promise<accountingManagementFeeCreditResponse> => {

  return customMutator<accountingManagementFeeCreditResponse>(getAccountingManagementFeeCreditUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingManagementFeeCreditMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeCredit>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeCredit>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingManagementFeeCredit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingManagementFeeCredit>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingManagementFeeCredit(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingManagementFeeCreditMutationResult = NonNullable<Awaited<ReturnType<typeof accountingManagementFeeCredit>>>

    export type AccountingManagementFeeCreditMutationError = unknown

    export const useAccountingManagementFeeCredit = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingManagementFeeCredit>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingManagementFeeCredit>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingManagementFeeCreditMutationOptions(options), queryClient);
    }

export type accountingCoaCreateAccountResponse200 = {
  data: void
  status: 200
}

export type accountingCoaCreateAccountResponse201 = {
  data: void
  status: 201
}

export type accountingCoaCreateAccountResponseSuccess = (accountingCoaCreateAccountResponse200 | accountingCoaCreateAccountResponse201) & {
  headers: Headers;
};
;

export type accountingCoaCreateAccountResponse = (accountingCoaCreateAccountResponseSuccess)

export const getAccountingCoaCreateAccountUrl = () => {




  return `/api/accounting/coa/accounts`
}

export const accountingCoaCreateAccount = async (createLedgerAccountDto: CreateLedgerAccountDto, options?: RequestInit): Promise<accountingCoaCreateAccountResponse> => {

  return customMutator<accountingCoaCreateAccountResponse>(getAccountingCoaCreateAccountUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createLedgerAccountDto)
  }
);}





export const getAccountingCoaCreateAccountMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingCoaCreateAccount>>, TError,{data: CreateLedgerAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingCoaCreateAccount>>, TError,{data: CreateLedgerAccountDto}, TContext> => {

const mutationKey = ['accountingCoaCreateAccount'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingCoaCreateAccount>>, {data: CreateLedgerAccountDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingCoaCreateAccount(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingCoaCreateAccountMutationResult = NonNullable<Awaited<ReturnType<typeof accountingCoaCreateAccount>>>
    export type AccountingCoaCreateAccountMutationBody = CreateLedgerAccountDto
    export type AccountingCoaCreateAccountMutationError = unknown

    export const useAccountingCoaCreateAccount = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingCoaCreateAccount>>, TError,{data: CreateLedgerAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingCoaCreateAccount>>,
        TError,
        {data: CreateLedgerAccountDto},
        TContext
      > => {
      return useMutation(getAccountingCoaCreateAccountMutationOptions(options), queryClient);
    }

export type accountingCoaGetAccountsResponse200 = {
  data: void
  status: 200
}

export type accountingCoaGetAccountsResponseSuccess = (accountingCoaGetAccountsResponse200) & {
  headers: Headers;
};
;

export type accountingCoaGetAccountsResponse = (accountingCoaGetAccountsResponseSuccess)

export const getAccountingCoaGetAccountsUrl = () => {




  return `/api/accounting/coa/accounts`
}

export const accountingCoaGetAccounts = async ( options?: RequestInit): Promise<accountingCoaGetAccountsResponse> => {

  return customMutator<accountingCoaGetAccountsResponse>(getAccountingCoaGetAccountsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingCoaGetAccountsQueryKey = () => {
    return [
    `/api/accounting/coa/accounts`
    ] as const;
    }


export const getAccountingCoaGetAccountsQueryOptions = <TData = Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingCoaGetAccountsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingCoaGetAccounts>>> = ({ signal }) => accountingCoaGetAccounts({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingCoaGetAccountsQueryResult = NonNullable<Awaited<ReturnType<typeof accountingCoaGetAccounts>>>
export type AccountingCoaGetAccountsQueryError = unknown


export function useAccountingCoaGetAccounts<TData = Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingCoaGetAccounts>>,
          TError,
          Awaited<ReturnType<typeof accountingCoaGetAccounts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingCoaGetAccounts<TData = Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingCoaGetAccounts>>,
          TError,
          Awaited<ReturnType<typeof accountingCoaGetAccounts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingCoaGetAccounts<TData = Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingCoaGetAccounts<TData = Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccounts>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingCoaGetAccountsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingCoaGetAccountResponse200 = {
  data: void
  status: 200
}

export type accountingCoaGetAccountResponseSuccess = (accountingCoaGetAccountResponse200) & {
  headers: Headers;
};
;

export type accountingCoaGetAccountResponse = (accountingCoaGetAccountResponseSuccess)

export const getAccountingCoaGetAccountUrl = (id: string,) => {




  return `/api/accounting/coa/accounts/${id}`
}

export const accountingCoaGetAccount = async (id: string, options?: RequestInit): Promise<accountingCoaGetAccountResponse> => {

  return customMutator<accountingCoaGetAccountResponse>(getAccountingCoaGetAccountUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingCoaGetAccountQueryKey = (id: string,) => {
    return [
    `/api/accounting/coa/accounts/${id}`
    ] as const;
    }


export const getAccountingCoaGetAccountQueryOptions = <TData = Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingCoaGetAccountQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingCoaGetAccount>>> = ({ signal }) => accountingCoaGetAccount(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingCoaGetAccountQueryResult = NonNullable<Awaited<ReturnType<typeof accountingCoaGetAccount>>>
export type AccountingCoaGetAccountQueryError = unknown


export function useAccountingCoaGetAccount<TData = Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingCoaGetAccount>>,
          TError,
          Awaited<ReturnType<typeof accountingCoaGetAccount>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingCoaGetAccount<TData = Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingCoaGetAccount>>,
          TError,
          Awaited<ReturnType<typeof accountingCoaGetAccount>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingCoaGetAccount<TData = Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingCoaGetAccount<TData = Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccount>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingCoaGetAccountQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingCoaUpdateAccountResponse200 = {
  data: void
  status: 200
}

export type accountingCoaUpdateAccountResponseSuccess = (accountingCoaUpdateAccountResponse200) & {
  headers: Headers;
};
;

export type accountingCoaUpdateAccountResponse = (accountingCoaUpdateAccountResponseSuccess)

export const getAccountingCoaUpdateAccountUrl = (id: string,) => {




  return `/api/accounting/coa/accounts/${id}`
}

export const accountingCoaUpdateAccount = async (id: string,
    updateLedgerAccountDto: UpdateLedgerAccountDto, options?: RequestInit): Promise<accountingCoaUpdateAccountResponse> => {

  return customMutator<accountingCoaUpdateAccountResponse>(getAccountingCoaUpdateAccountUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateLedgerAccountDto)
  }
);}





export const getAccountingCoaUpdateAccountMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingCoaUpdateAccount>>, TError,{id: string;data: UpdateLedgerAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingCoaUpdateAccount>>, TError,{id: string;data: UpdateLedgerAccountDto}, TContext> => {

const mutationKey = ['accountingCoaUpdateAccount'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingCoaUpdateAccount>>, {id: string;data: UpdateLedgerAccountDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingCoaUpdateAccount(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingCoaUpdateAccountMutationResult = NonNullable<Awaited<ReturnType<typeof accountingCoaUpdateAccount>>>
    export type AccountingCoaUpdateAccountMutationBody = UpdateLedgerAccountDto
    export type AccountingCoaUpdateAccountMutationError = unknown

    export const useAccountingCoaUpdateAccount = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingCoaUpdateAccount>>, TError,{id: string;data: UpdateLedgerAccountDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingCoaUpdateAccount>>,
        TError,
        {id: string;data: UpdateLedgerAccountDto},
        TContext
      > => {
      return useMutation(getAccountingCoaUpdateAccountMutationOptions(options), queryClient);
    }

export type accountingCoaGetAccountBalanceResponse200 = {
  data: void
  status: 200
}

export type accountingCoaGetAccountBalanceResponseSuccess = (accountingCoaGetAccountBalanceResponse200) & {
  headers: Headers;
};
;

export type accountingCoaGetAccountBalanceResponse = (accountingCoaGetAccountBalanceResponseSuccess)

export const getAccountingCoaGetAccountBalanceUrl = (id: string,) => {




  return `/api/accounting/coa/accounts/${id}/balance`
}

export const accountingCoaGetAccountBalance = async (id: string, options?: RequestInit): Promise<accountingCoaGetAccountBalanceResponse> => {

  return customMutator<accountingCoaGetAccountBalanceResponse>(getAccountingCoaGetAccountBalanceUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingCoaGetAccountBalanceQueryKey = (id: string,) => {
    return [
    `/api/accounting/coa/accounts/${id}/balance`
    ] as const;
    }


export const getAccountingCoaGetAccountBalanceQueryOptions = <TData = Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingCoaGetAccountBalanceQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>> = ({ signal }) => accountingCoaGetAccountBalance(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingCoaGetAccountBalanceQueryResult = NonNullable<Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>>
export type AccountingCoaGetAccountBalanceQueryError = unknown


export function useAccountingCoaGetAccountBalance<TData = Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>,
          TError,
          Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingCoaGetAccountBalance<TData = Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>,
          TError,
          Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingCoaGetAccountBalance<TData = Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingCoaGetAccountBalance<TData = Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingCoaGetAccountBalance>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingCoaGetAccountBalanceQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingFiscalYearCreateResponse200 = {
  data: void
  status: 200
}

export type accountingFiscalYearCreateResponse201 = {
  data: void
  status: 201
}

export type accountingFiscalYearCreateResponseSuccess = (accountingFiscalYearCreateResponse200 | accountingFiscalYearCreateResponse201) & {
  headers: Headers;
};
;

export type accountingFiscalYearCreateResponse = (accountingFiscalYearCreateResponseSuccess)

export const getAccountingFiscalYearCreateUrl = () => {




  return `/api/accounting/fiscal-years`
}

export const accountingFiscalYearCreate = async (createFiscalYearDto: CreateFiscalYearDto, options?: RequestInit): Promise<accountingFiscalYearCreateResponse> => {

  return customMutator<accountingFiscalYearCreateResponse>(getAccountingFiscalYearCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createFiscalYearDto)
  }
);}





export const getAccountingFiscalYearCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalYearCreate>>, TError,{data: CreateFiscalYearDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalYearCreate>>, TError,{data: CreateFiscalYearDto}, TContext> => {

const mutationKey = ['accountingFiscalYearCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingFiscalYearCreate>>, {data: CreateFiscalYearDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingFiscalYearCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingFiscalYearCreateMutationResult = NonNullable<Awaited<ReturnType<typeof accountingFiscalYearCreate>>>
    export type AccountingFiscalYearCreateMutationBody = CreateFiscalYearDto
    export type AccountingFiscalYearCreateMutationError = unknown

    export const useAccountingFiscalYearCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalYearCreate>>, TError,{data: CreateFiscalYearDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingFiscalYearCreate>>,
        TError,
        {data: CreateFiscalYearDto},
        TContext
      > => {
      return useMutation(getAccountingFiscalYearCreateMutationOptions(options), queryClient);
    }

export type accountingFiscalYearListResponse200 = {
  data: void
  status: 200
}

export type accountingFiscalYearListResponseSuccess = (accountingFiscalYearListResponse200) & {
  headers: Headers;
};
;

export type accountingFiscalYearListResponse = (accountingFiscalYearListResponseSuccess)

export const getAccountingFiscalYearListUrl = () => {




  return `/api/accounting/fiscal-years`
}

export const accountingFiscalYearList = async ( options?: RequestInit): Promise<accountingFiscalYearListResponse> => {

  return customMutator<accountingFiscalYearListResponse>(getAccountingFiscalYearListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingFiscalYearListQueryKey = () => {
    return [
    `/api/accounting/fiscal-years`
    ] as const;
    }


export const getAccountingFiscalYearListQueryOptions = <TData = Awaited<ReturnType<typeof accountingFiscalYearList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalYearList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingFiscalYearListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingFiscalYearList>>> = ({ signal }) => accountingFiscalYearList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalYearList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingFiscalYearListQueryResult = NonNullable<Awaited<ReturnType<typeof accountingFiscalYearList>>>
export type AccountingFiscalYearListQueryError = unknown


export function useAccountingFiscalYearList<TData = Awaited<ReturnType<typeof accountingFiscalYearList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalYearList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingFiscalYearList>>,
          TError,
          Awaited<ReturnType<typeof accountingFiscalYearList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingFiscalYearList<TData = Awaited<ReturnType<typeof accountingFiscalYearList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalYearList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingFiscalYearList>>,
          TError,
          Awaited<ReturnType<typeof accountingFiscalYearList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingFiscalYearList<TData = Awaited<ReturnType<typeof accountingFiscalYearList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalYearList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingFiscalYearList<TData = Awaited<ReturnType<typeof accountingFiscalYearList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalYearList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingFiscalYearListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingFiscalYearCloseResponse200 = {
  data: void
  status: 200
}

export type accountingFiscalYearCloseResponse201 = {
  data: void
  status: 201
}

export type accountingFiscalYearCloseResponseSuccess = (accountingFiscalYearCloseResponse200 | accountingFiscalYearCloseResponse201) & {
  headers: Headers;
};
;

export type accountingFiscalYearCloseResponse = (accountingFiscalYearCloseResponseSuccess)

export const getAccountingFiscalYearCloseUrl = (id: string,) => {




  return `/api/accounting/fiscal-years/${id}/close`
}

export const accountingFiscalYearClose = async (id: string,
    fiscalYearLifecycleDto: FiscalYearLifecycleDto, options?: RequestInit): Promise<accountingFiscalYearCloseResponse> => {

  return customMutator<accountingFiscalYearCloseResponse>(getAccountingFiscalYearCloseUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(fiscalYearLifecycleDto)
  }
);}





export const getAccountingFiscalYearCloseMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalYearClose>>, TError,{id: string;data: FiscalYearLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalYearClose>>, TError,{id: string;data: FiscalYearLifecycleDto}, TContext> => {

const mutationKey = ['accountingFiscalYearClose'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingFiscalYearClose>>, {id: string;data: FiscalYearLifecycleDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingFiscalYearClose(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingFiscalYearCloseMutationResult = NonNullable<Awaited<ReturnType<typeof accountingFiscalYearClose>>>
    export type AccountingFiscalYearCloseMutationBody = FiscalYearLifecycleDto
    export type AccountingFiscalYearCloseMutationError = unknown

    export const useAccountingFiscalYearClose = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalYearClose>>, TError,{id: string;data: FiscalYearLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingFiscalYearClose>>,
        TError,
        {id: string;data: FiscalYearLifecycleDto},
        TContext
      > => {
      return useMutation(getAccountingFiscalYearCloseMutationOptions(options), queryClient);
    }

export type accountingFiscalYearReopenResponse200 = {
  data: void
  status: 200
}

export type accountingFiscalYearReopenResponseSuccess = (accountingFiscalYearReopenResponse200) & {
  headers: Headers;
};
;

export type accountingFiscalYearReopenResponse = (accountingFiscalYearReopenResponseSuccess)

export const getAccountingFiscalYearReopenUrl = (id: string,) => {




  return `/api/accounting/fiscal-years/${id}/reopen`
}

export const accountingFiscalYearReopen = async (id: string,
    fiscalYearLifecycleDto: FiscalYearLifecycleDto, options?: RequestInit): Promise<accountingFiscalYearReopenResponse> => {

  return customMutator<accountingFiscalYearReopenResponse>(getAccountingFiscalYearReopenUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(fiscalYearLifecycleDto)
  }
);}





export const getAccountingFiscalYearReopenMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalYearReopen>>, TError,{id: string;data: FiscalYearLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalYearReopen>>, TError,{id: string;data: FiscalYearLifecycleDto}, TContext> => {

const mutationKey = ['accountingFiscalYearReopen'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingFiscalYearReopen>>, {id: string;data: FiscalYearLifecycleDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingFiscalYearReopen(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingFiscalYearReopenMutationResult = NonNullable<Awaited<ReturnType<typeof accountingFiscalYearReopen>>>
    export type AccountingFiscalYearReopenMutationBody = FiscalYearLifecycleDto
    export type AccountingFiscalYearReopenMutationError = unknown

    export const useAccountingFiscalYearReopen = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalYearReopen>>, TError,{id: string;data: FiscalYearLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingFiscalYearReopen>>,
        TError,
        {id: string;data: FiscalYearLifecycleDto},
        TContext
      > => {
      return useMutation(getAccountingFiscalYearReopenMutationOptions(options), queryClient);
    }

export type accountingOpeningCutoverPreviewResponse200 = {
  data: void
  status: 200
}

export type accountingOpeningCutoverPreviewResponse201 = {
  data: void
  status: 201
}

export type accountingOpeningCutoverPreviewResponseSuccess = (accountingOpeningCutoverPreviewResponse200 | accountingOpeningCutoverPreviewResponse201) & {
  headers: Headers;
};
;

export type accountingOpeningCutoverPreviewResponse = (accountingOpeningCutoverPreviewResponseSuccess)

export const getAccountingOpeningCutoverPreviewUrl = () => {




  return `/api/accounting/opening-cutovers/preview`
}

export const accountingOpeningCutoverPreview = async (previewOpeningCutoverDto: PreviewOpeningCutoverDto, options?: RequestInit): Promise<accountingOpeningCutoverPreviewResponse> => {

  return customMutator<accountingOpeningCutoverPreviewResponse>(getAccountingOpeningCutoverPreviewUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(previewOpeningCutoverDto)
  }
);}





export const getAccountingOpeningCutoverPreviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingOpeningCutoverPreview>>, TError,{data: PreviewOpeningCutoverDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingOpeningCutoverPreview>>, TError,{data: PreviewOpeningCutoverDto}, TContext> => {

const mutationKey = ['accountingOpeningCutoverPreview'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingOpeningCutoverPreview>>, {data: PreviewOpeningCutoverDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingOpeningCutoverPreview(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingOpeningCutoverPreviewMutationResult = NonNullable<Awaited<ReturnType<typeof accountingOpeningCutoverPreview>>>
    export type AccountingOpeningCutoverPreviewMutationBody = PreviewOpeningCutoverDto
    export type AccountingOpeningCutoverPreviewMutationError = unknown

    export const useAccountingOpeningCutoverPreview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingOpeningCutoverPreview>>, TError,{data: PreviewOpeningCutoverDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingOpeningCutoverPreview>>,
        TError,
        {data: PreviewOpeningCutoverDto},
        TContext
      > => {
      return useMutation(getAccountingOpeningCutoverPreviewMutationOptions(options), queryClient);
    }

export type accountingOpeningCutoverApproveResponse200 = {
  data: void
  status: 200
}

export type accountingOpeningCutoverApproveResponse201 = {
  data: void
  status: 201
}

export type accountingOpeningCutoverApproveResponseSuccess = (accountingOpeningCutoverApproveResponse200 | accountingOpeningCutoverApproveResponse201) & {
  headers: Headers;
};
;

export type accountingOpeningCutoverApproveResponse = (accountingOpeningCutoverApproveResponseSuccess)

export const getAccountingOpeningCutoverApproveUrl = (id: string,) => {




  return `/api/accounting/opening-cutovers/${id}/approve`
}

export const accountingOpeningCutoverApprove = async (id: string, options?: RequestInit): Promise<accountingOpeningCutoverApproveResponse> => {

  return customMutator<accountingOpeningCutoverApproveResponse>(getAccountingOpeningCutoverApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingOpeningCutoverApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingOpeningCutoverApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingOpeningCutoverApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingOpeningCutoverApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingOpeningCutoverApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingOpeningCutoverApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingOpeningCutoverApproveMutationResult = NonNullable<Awaited<ReturnType<typeof accountingOpeningCutoverApprove>>>

    export type AccountingOpeningCutoverApproveMutationError = unknown

    export const useAccountingOpeningCutoverApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingOpeningCutoverApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingOpeningCutoverApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingOpeningCutoverApproveMutationOptions(options), queryClient);
    }

export type accountingOpeningCutoverGetResponse200 = {
  data: void
  status: 200
}

export type accountingOpeningCutoverGetResponseSuccess = (accountingOpeningCutoverGetResponse200) & {
  headers: Headers;
};
;

export type accountingOpeningCutoverGetResponse = (accountingOpeningCutoverGetResponseSuccess)

export const getAccountingOpeningCutoverGetUrl = (id: string,) => {




  return `/api/accounting/opening-cutovers/${id}`
}

export const accountingOpeningCutoverGet = async (id: string, options?: RequestInit): Promise<accountingOpeningCutoverGetResponse> => {

  return customMutator<accountingOpeningCutoverGetResponse>(getAccountingOpeningCutoverGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingOpeningCutoverGetQueryKey = (id: string,) => {
    return [
    `/api/accounting/opening-cutovers/${id}`
    ] as const;
    }


export const getAccountingOpeningCutoverGetQueryOptions = <TData = Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingOpeningCutoverGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingOpeningCutoverGet>>> = ({ signal }) => accountingOpeningCutoverGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingOpeningCutoverGetQueryResult = NonNullable<Awaited<ReturnType<typeof accountingOpeningCutoverGet>>>
export type AccountingOpeningCutoverGetQueryError = unknown


export function useAccountingOpeningCutoverGet<TData = Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingOpeningCutoverGet>>,
          TError,
          Awaited<ReturnType<typeof accountingOpeningCutoverGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingOpeningCutoverGet<TData = Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingOpeningCutoverGet>>,
          TError,
          Awaited<ReturnType<typeof accountingOpeningCutoverGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingOpeningCutoverGet<TData = Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingOpeningCutoverGet<TData = Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingOpeningCutoverGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingOpeningCutoverGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingFiscalPeriodCreateResponse200 = {
  data: void
  status: 200
}

export type accountingFiscalPeriodCreateResponse201 = {
  data: void
  status: 201
}

export type accountingFiscalPeriodCreateResponseSuccess = (accountingFiscalPeriodCreateResponse200 | accountingFiscalPeriodCreateResponse201) & {
  headers: Headers;
};
;

export type accountingFiscalPeriodCreateResponse = (accountingFiscalPeriodCreateResponseSuccess)

export const getAccountingFiscalPeriodCreateUrl = () => {




  return `/api/accounting/fiscal-periods`
}

export const accountingFiscalPeriodCreate = async (createFiscalPeriodDto: CreateFiscalPeriodDto, options?: RequestInit): Promise<accountingFiscalPeriodCreateResponse> => {

  return customMutator<accountingFiscalPeriodCreateResponse>(getAccountingFiscalPeriodCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createFiscalPeriodDto)
  }
);}





export const getAccountingFiscalPeriodCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalPeriodCreate>>, TError,{data: CreateFiscalPeriodDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalPeriodCreate>>, TError,{data: CreateFiscalPeriodDto}, TContext> => {

const mutationKey = ['accountingFiscalPeriodCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingFiscalPeriodCreate>>, {data: CreateFiscalPeriodDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingFiscalPeriodCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingFiscalPeriodCreateMutationResult = NonNullable<Awaited<ReturnType<typeof accountingFiscalPeriodCreate>>>
    export type AccountingFiscalPeriodCreateMutationBody = CreateFiscalPeriodDto
    export type AccountingFiscalPeriodCreateMutationError = unknown

    export const useAccountingFiscalPeriodCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalPeriodCreate>>, TError,{data: CreateFiscalPeriodDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingFiscalPeriodCreate>>,
        TError,
        {data: CreateFiscalPeriodDto},
        TContext
      > => {
      return useMutation(getAccountingFiscalPeriodCreateMutationOptions(options), queryClient);
    }

export type accountingFiscalPeriodListResponse200 = {
  data: void
  status: 200
}

export type accountingFiscalPeriodListResponseSuccess = (accountingFiscalPeriodListResponse200) & {
  headers: Headers;
};
;

export type accountingFiscalPeriodListResponse = (accountingFiscalPeriodListResponseSuccess)

export const getAccountingFiscalPeriodListUrl = () => {




  return `/api/accounting/fiscal-periods`
}

export const accountingFiscalPeriodList = async ( options?: RequestInit): Promise<accountingFiscalPeriodListResponse> => {

  return customMutator<accountingFiscalPeriodListResponse>(getAccountingFiscalPeriodListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingFiscalPeriodListQueryKey = () => {
    return [
    `/api/accounting/fiscal-periods`
    ] as const;
    }


export const getAccountingFiscalPeriodListQueryOptions = <TData = Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingFiscalPeriodListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingFiscalPeriodList>>> = ({ signal }) => accountingFiscalPeriodList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingFiscalPeriodListQueryResult = NonNullable<Awaited<ReturnType<typeof accountingFiscalPeriodList>>>
export type AccountingFiscalPeriodListQueryError = unknown


export function useAccountingFiscalPeriodList<TData = Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingFiscalPeriodList>>,
          TError,
          Awaited<ReturnType<typeof accountingFiscalPeriodList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingFiscalPeriodList<TData = Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingFiscalPeriodList>>,
          TError,
          Awaited<ReturnType<typeof accountingFiscalPeriodList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingFiscalPeriodList<TData = Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingFiscalPeriodList<TData = Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingFiscalPeriodList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingFiscalPeriodListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingFiscalPeriodLockResponse200 = {
  data: void
  status: 200
}

export type accountingFiscalPeriodLockResponse201 = {
  data: void
  status: 201
}

export type accountingFiscalPeriodLockResponseSuccess = (accountingFiscalPeriodLockResponse200 | accountingFiscalPeriodLockResponse201) & {
  headers: Headers;
};
;

export type accountingFiscalPeriodLockResponse = (accountingFiscalPeriodLockResponseSuccess)

export const getAccountingFiscalPeriodLockUrl = (id: string,) => {




  return `/api/accounting/fiscal-periods/${id}/lock`
}

export const accountingFiscalPeriodLock = async (id: string, options?: RequestInit): Promise<accountingFiscalPeriodLockResponse> => {

  return customMutator<accountingFiscalPeriodLockResponse>(getAccountingFiscalPeriodLockUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingFiscalPeriodLockMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalPeriodLock>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalPeriodLock>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingFiscalPeriodLock'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingFiscalPeriodLock>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingFiscalPeriodLock(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingFiscalPeriodLockMutationResult = NonNullable<Awaited<ReturnType<typeof accountingFiscalPeriodLock>>>

    export type AccountingFiscalPeriodLockMutationError = unknown

    export const useAccountingFiscalPeriodLock = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalPeriodLock>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingFiscalPeriodLock>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingFiscalPeriodLockMutationOptions(options), queryClient);
    }

export type accountingFiscalPeriodUnlockResponse200 = {
  data: void
  status: 200
}

export type accountingFiscalPeriodUnlockResponseSuccess = (accountingFiscalPeriodUnlockResponse200) & {
  headers: Headers;
};
;

export type accountingFiscalPeriodUnlockResponse = (accountingFiscalPeriodUnlockResponseSuccess)

export const getAccountingFiscalPeriodUnlockUrl = (id: string,) => {




  return `/api/accounting/fiscal-periods/${id}/unlock`
}

export const accountingFiscalPeriodUnlock = async (id: string, options?: RequestInit): Promise<accountingFiscalPeriodUnlockResponse> => {

  return customMutator<accountingFiscalPeriodUnlockResponse>(getAccountingFiscalPeriodUnlockUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingFiscalPeriodUnlockMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalPeriodUnlock>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalPeriodUnlock>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingFiscalPeriodUnlock'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingFiscalPeriodUnlock>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingFiscalPeriodUnlock(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingFiscalPeriodUnlockMutationResult = NonNullable<Awaited<ReturnType<typeof accountingFiscalPeriodUnlock>>>

    export type AccountingFiscalPeriodUnlockMutationError = unknown

    export const useAccountingFiscalPeriodUnlock = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingFiscalPeriodUnlock>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingFiscalPeriodUnlock>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingFiscalPeriodUnlockMutationOptions(options), queryClient);
    }

export type accountingJournalCreateResponse200 = {
  data: void
  status: 200
}

export type accountingJournalCreateResponse201 = {
  data: void
  status: 201
}

export type accountingJournalCreateResponseSuccess = (accountingJournalCreateResponse200 | accountingJournalCreateResponse201) & {
  headers: Headers;
};
;

export type accountingJournalCreateResponse = (accountingJournalCreateResponseSuccess)

export const getAccountingJournalCreateUrl = () => {




  return `/api/accounting/journal-entries`
}

export const accountingJournalCreate = async (createJournalDto: CreateJournalDto, options?: RequestInit): Promise<accountingJournalCreateResponse> => {

  return customMutator<accountingJournalCreateResponse>(getAccountingJournalCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createJournalDto)
  }
);}





export const getAccountingJournalCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalCreate>>, TError,{data: CreateJournalDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingJournalCreate>>, TError,{data: CreateJournalDto}, TContext> => {

const mutationKey = ['accountingJournalCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingJournalCreate>>, {data: CreateJournalDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingJournalCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingJournalCreateMutationResult = NonNullable<Awaited<ReturnType<typeof accountingJournalCreate>>>
    export type AccountingJournalCreateMutationBody = CreateJournalDto
    export type AccountingJournalCreateMutationError = unknown

    export const useAccountingJournalCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalCreate>>, TError,{data: CreateJournalDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingJournalCreate>>,
        TError,
        {data: CreateJournalDto},
        TContext
      > => {
      return useMutation(getAccountingJournalCreateMutationOptions(options), queryClient);
    }

export type accountingJournalListResponse200 = {
  data: void
  status: 200
}

export type accountingJournalListResponseSuccess = (accountingJournalListResponse200) & {
  headers: Headers;
};
;

export type accountingJournalListResponse = (accountingJournalListResponseSuccess)

export const getAccountingJournalListUrl = () => {




  return `/api/accounting/journal-entries`
}

export const accountingJournalList = async ( options?: RequestInit): Promise<accountingJournalListResponse> => {

  return customMutator<accountingJournalListResponse>(getAccountingJournalListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingJournalListQueryKey = () => {
    return [
    `/api/accounting/journal-entries`
    ] as const;
    }


export const getAccountingJournalListQueryOptions = <TData = Awaited<ReturnType<typeof accountingJournalList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingJournalListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingJournalList>>> = ({ signal }) => accountingJournalList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingJournalList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingJournalListQueryResult = NonNullable<Awaited<ReturnType<typeof accountingJournalList>>>
export type AccountingJournalListQueryError = unknown


export function useAccountingJournalList<TData = Awaited<ReturnType<typeof accountingJournalList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingJournalList>>,
          TError,
          Awaited<ReturnType<typeof accountingJournalList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingJournalList<TData = Awaited<ReturnType<typeof accountingJournalList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingJournalList>>,
          TError,
          Awaited<ReturnType<typeof accountingJournalList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingJournalList<TData = Awaited<ReturnType<typeof accountingJournalList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingJournalList<TData = Awaited<ReturnType<typeof accountingJournalList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingJournalListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingJournalGetResponse200 = {
  data: void
  status: 200
}

export type accountingJournalGetResponseSuccess = (accountingJournalGetResponse200) & {
  headers: Headers;
};
;

export type accountingJournalGetResponse = (accountingJournalGetResponseSuccess)

export const getAccountingJournalGetUrl = (id: string,) => {




  return `/api/accounting/journal-entries/${id}`
}

export const accountingJournalGet = async (id: string, options?: RequestInit): Promise<accountingJournalGetResponse> => {

  return customMutator<accountingJournalGetResponse>(getAccountingJournalGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingJournalGetQueryKey = (id: string,) => {
    return [
    `/api/accounting/journal-entries/${id}`
    ] as const;
    }


export const getAccountingJournalGetQueryOptions = <TData = Awaited<ReturnType<typeof accountingJournalGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingJournalGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingJournalGet>>> = ({ signal }) => accountingJournalGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingJournalGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingJournalGetQueryResult = NonNullable<Awaited<ReturnType<typeof accountingJournalGet>>>
export type AccountingJournalGetQueryError = unknown


export function useAccountingJournalGet<TData = Awaited<ReturnType<typeof accountingJournalGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingJournalGet>>,
          TError,
          Awaited<ReturnType<typeof accountingJournalGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingJournalGet<TData = Awaited<ReturnType<typeof accountingJournalGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingJournalGet>>,
          TError,
          Awaited<ReturnType<typeof accountingJournalGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingJournalGet<TData = Awaited<ReturnType<typeof accountingJournalGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingJournalGet<TData = Awaited<ReturnType<typeof accountingJournalGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingJournalGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingJournalPostResponse200 = {
  data: void
  status: 200
}

export type accountingJournalPostResponse201 = {
  data: void
  status: 201
}

export type accountingJournalPostResponseSuccess = (accountingJournalPostResponse200 | accountingJournalPostResponse201) & {
  headers: Headers;
};
;

export type accountingJournalPostResponse = (accountingJournalPostResponseSuccess)

export const getAccountingJournalPostUrl = (id: string,) => {




  return `/api/accounting/journal-entries/${id}/post`
}

export const accountingJournalPost = async (id: string,
    journalTransitionDto: JournalTransitionDto, options?: RequestInit): Promise<accountingJournalPostResponse> => {

  return customMutator<accountingJournalPostResponse>(getAccountingJournalPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(journalTransitionDto)
  }
);}





export const getAccountingJournalPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalPost>>, TError,{id: string;data: JournalTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingJournalPost>>, TError,{id: string;data: JournalTransitionDto}, TContext> => {

const mutationKey = ['accountingJournalPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingJournalPost>>, {id: string;data: JournalTransitionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingJournalPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingJournalPostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingJournalPost>>>
    export type AccountingJournalPostMutationBody = JournalTransitionDto
    export type AccountingJournalPostMutationError = unknown

    export const useAccountingJournalPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalPost>>, TError,{id: string;data: JournalTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingJournalPost>>,
        TError,
        {id: string;data: JournalTransitionDto},
        TContext
      > => {
      return useMutation(getAccountingJournalPostMutationOptions(options), queryClient);
    }

export type accountingJournalReverseResponse200 = {
  data: void
  status: 200
}

export type accountingJournalReverseResponse201 = {
  data: void
  status: 201
}

export type accountingJournalReverseResponseSuccess = (accountingJournalReverseResponse200 | accountingJournalReverseResponse201) & {
  headers: Headers;
};
;

export type accountingJournalReverseResponse = (accountingJournalReverseResponseSuccess)

export const getAccountingJournalReverseUrl = (id: string,) => {




  return `/api/accounting/journal-entries/${id}/reverse`
}

export const accountingJournalReverse = async (id: string,
    reverseJournalDto: ReverseJournalDto, options?: RequestInit): Promise<accountingJournalReverseResponse> => {

  return customMutator<accountingJournalReverseResponse>(getAccountingJournalReverseUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(reverseJournalDto)
  }
);}





export const getAccountingJournalReverseMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalReverse>>, TError,{id: string;data: ReverseJournalDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingJournalReverse>>, TError,{id: string;data: ReverseJournalDto}, TContext> => {

const mutationKey = ['accountingJournalReverse'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingJournalReverse>>, {id: string;data: ReverseJournalDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingJournalReverse(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingJournalReverseMutationResult = NonNullable<Awaited<ReturnType<typeof accountingJournalReverse>>>
    export type AccountingJournalReverseMutationBody = ReverseJournalDto
    export type AccountingJournalReverseMutationError = unknown

    export const useAccountingJournalReverse = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalReverse>>, TError,{id: string;data: ReverseJournalDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingJournalReverse>>,
        TError,
        {id: string;data: ReverseJournalDto},
        TContext
      > => {
      return useMutation(getAccountingJournalReverseMutationOptions(options), queryClient);
    }

export type accountingJournalTemplateViewResponse200 = {
  data: void
  status: 200
}

export type accountingJournalTemplateViewResponseSuccess = (accountingJournalTemplateViewResponse200) & {
  headers: Headers;
};
;

export type accountingJournalTemplateViewResponse = (accountingJournalTemplateViewResponseSuccess)

export const getAccountingJournalTemplateViewUrl = () => {




  return `/api/accounting/journal-templates`
}

export const accountingJournalTemplateView = async ( options?: RequestInit): Promise<accountingJournalTemplateViewResponse> => {

  return customMutator<accountingJournalTemplateViewResponse>(getAccountingJournalTemplateViewUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingJournalTemplateViewQueryKey = () => {
    return [
    `/api/accounting/journal-templates`
    ] as const;
    }


export const getAccountingJournalTemplateViewQueryOptions = <TData = Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingJournalTemplateViewQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingJournalTemplateView>>> = ({ signal }) => accountingJournalTemplateView({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingJournalTemplateViewQueryResult = NonNullable<Awaited<ReturnType<typeof accountingJournalTemplateView>>>
export type AccountingJournalTemplateViewQueryError = unknown


export function useAccountingJournalTemplateView<TData = Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingJournalTemplateView>>,
          TError,
          Awaited<ReturnType<typeof accountingJournalTemplateView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingJournalTemplateView<TData = Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingJournalTemplateView>>,
          TError,
          Awaited<ReturnType<typeof accountingJournalTemplateView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingJournalTemplateView<TData = Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingJournalTemplateView<TData = Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalTemplateView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingJournalTemplateViewQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingJournalTemplateCreateResponse200 = {
  data: void
  status: 200
}

export type accountingJournalTemplateCreateResponse201 = {
  data: void
  status: 201
}

export type accountingJournalTemplateCreateResponseSuccess = (accountingJournalTemplateCreateResponse200 | accountingJournalTemplateCreateResponse201) & {
  headers: Headers;
};
;

export type accountingJournalTemplateCreateResponse = (accountingJournalTemplateCreateResponseSuccess)

export const getAccountingJournalTemplateCreateUrl = () => {




  return `/api/accounting/journal-templates`
}

export const accountingJournalTemplateCreate = async (createJournalTemplateDto: CreateJournalTemplateDto, options?: RequestInit): Promise<accountingJournalTemplateCreateResponse> => {

  return customMutator<accountingJournalTemplateCreateResponse>(getAccountingJournalTemplateCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createJournalTemplateDto)
  }
);}





export const getAccountingJournalTemplateCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalTemplateCreate>>, TError,{data: CreateJournalTemplateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingJournalTemplateCreate>>, TError,{data: CreateJournalTemplateDto}, TContext> => {

const mutationKey = ['accountingJournalTemplateCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingJournalTemplateCreate>>, {data: CreateJournalTemplateDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingJournalTemplateCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingJournalTemplateCreateMutationResult = NonNullable<Awaited<ReturnType<typeof accountingJournalTemplateCreate>>>
    export type AccountingJournalTemplateCreateMutationBody = CreateJournalTemplateDto
    export type AccountingJournalTemplateCreateMutationError = unknown

    export const useAccountingJournalTemplateCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalTemplateCreate>>, TError,{data: CreateJournalTemplateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingJournalTemplateCreate>>,
        TError,
        {data: CreateJournalTemplateDto},
        TContext
      > => {
      return useMutation(getAccountingJournalTemplateCreateMutationOptions(options), queryClient);
    }

export type accountingJournalTemplateApproveResponse200 = {
  data: void
  status: 200
}

export type accountingJournalTemplateApproveResponse201 = {
  data: void
  status: 201
}

export type accountingJournalTemplateApproveResponseSuccess = (accountingJournalTemplateApproveResponse200 | accountingJournalTemplateApproveResponse201) & {
  headers: Headers;
};
;

export type accountingJournalTemplateApproveResponse = (accountingJournalTemplateApproveResponseSuccess)

export const getAccountingJournalTemplateApproveUrl = (id: string,) => {




  return `/api/accounting/journal-templates/${id}/approve`
}

export const accountingJournalTemplateApprove = async (id: string, options?: RequestInit): Promise<accountingJournalTemplateApproveResponse> => {

  return customMutator<accountingJournalTemplateApproveResponse>(getAccountingJournalTemplateApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingJournalTemplateApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalTemplateApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingJournalTemplateApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingJournalTemplateApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingJournalTemplateApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingJournalTemplateApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingJournalTemplateApproveMutationResult = NonNullable<Awaited<ReturnType<typeof accountingJournalTemplateApprove>>>

    export type AccountingJournalTemplateApproveMutationError = unknown

    export const useAccountingJournalTemplateApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalTemplateApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingJournalTemplateApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingJournalTemplateApproveMutationOptions(options), queryClient);
    }

export type accountingJournalScheduleViewResponse200 = {
  data: void
  status: 200
}

export type accountingJournalScheduleViewResponseSuccess = (accountingJournalScheduleViewResponse200) & {
  headers: Headers;
};
;

export type accountingJournalScheduleViewResponse = (accountingJournalScheduleViewResponseSuccess)

export const getAccountingJournalScheduleViewUrl = () => {




  return `/api/accounting/journal-schedules`
}

export const accountingJournalScheduleView = async ( options?: RequestInit): Promise<accountingJournalScheduleViewResponse> => {

  return customMutator<accountingJournalScheduleViewResponse>(getAccountingJournalScheduleViewUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingJournalScheduleViewQueryKey = () => {
    return [
    `/api/accounting/journal-schedules`
    ] as const;
    }


export const getAccountingJournalScheduleViewQueryOptions = <TData = Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingJournalScheduleViewQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingJournalScheduleView>>> = ({ signal }) => accountingJournalScheduleView({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingJournalScheduleViewQueryResult = NonNullable<Awaited<ReturnType<typeof accountingJournalScheduleView>>>
export type AccountingJournalScheduleViewQueryError = unknown


export function useAccountingJournalScheduleView<TData = Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingJournalScheduleView>>,
          TError,
          Awaited<ReturnType<typeof accountingJournalScheduleView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingJournalScheduleView<TData = Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingJournalScheduleView>>,
          TError,
          Awaited<ReturnType<typeof accountingJournalScheduleView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingJournalScheduleView<TData = Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingJournalScheduleView<TData = Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingJournalScheduleView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingJournalScheduleViewQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingJournalScheduleCreateResponse200 = {
  data: void
  status: 200
}

export type accountingJournalScheduleCreateResponse201 = {
  data: void
  status: 201
}

export type accountingJournalScheduleCreateResponseSuccess = (accountingJournalScheduleCreateResponse200 | accountingJournalScheduleCreateResponse201) & {
  headers: Headers;
};
;

export type accountingJournalScheduleCreateResponse = (accountingJournalScheduleCreateResponseSuccess)

export const getAccountingJournalScheduleCreateUrl = () => {




  return `/api/accounting/journal-schedules`
}

export const accountingJournalScheduleCreate = async (createJournalScheduleDto: CreateJournalScheduleDto, options?: RequestInit): Promise<accountingJournalScheduleCreateResponse> => {

  return customMutator<accountingJournalScheduleCreateResponse>(getAccountingJournalScheduleCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createJournalScheduleDto)
  }
);}





export const getAccountingJournalScheduleCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleCreate>>, TError,{data: CreateJournalScheduleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleCreate>>, TError,{data: CreateJournalScheduleDto}, TContext> => {

const mutationKey = ['accountingJournalScheduleCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingJournalScheduleCreate>>, {data: CreateJournalScheduleDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingJournalScheduleCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingJournalScheduleCreateMutationResult = NonNullable<Awaited<ReturnType<typeof accountingJournalScheduleCreate>>>
    export type AccountingJournalScheduleCreateMutationBody = CreateJournalScheduleDto
    export type AccountingJournalScheduleCreateMutationError = unknown

    export const useAccountingJournalScheduleCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleCreate>>, TError,{data: CreateJournalScheduleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingJournalScheduleCreate>>,
        TError,
        {data: CreateJournalScheduleDto},
        TContext
      > => {
      return useMutation(getAccountingJournalScheduleCreateMutationOptions(options), queryClient);
    }

export type accountingJournalScheduleAmendResponse200 = {
  data: void
  status: 200
}

export type accountingJournalScheduleAmendResponseSuccess = (accountingJournalScheduleAmendResponse200) & {
  headers: Headers;
};
;

export type accountingJournalScheduleAmendResponse = (accountingJournalScheduleAmendResponseSuccess)

export const getAccountingJournalScheduleAmendUrl = (id: string,) => {




  return `/api/accounting/journal-schedules/${id}/amend`
}

export const accountingJournalScheduleAmend = async (id: string,
    createJournalScheduleDto: CreateJournalScheduleDto, options?: RequestInit): Promise<accountingJournalScheduleAmendResponse> => {

  return customMutator<accountingJournalScheduleAmendResponse>(getAccountingJournalScheduleAmendUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createJournalScheduleDto)
  }
);}





export const getAccountingJournalScheduleAmendMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleAmend>>, TError,{id: string;data: CreateJournalScheduleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleAmend>>, TError,{id: string;data: CreateJournalScheduleDto}, TContext> => {

const mutationKey = ['accountingJournalScheduleAmend'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingJournalScheduleAmend>>, {id: string;data: CreateJournalScheduleDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingJournalScheduleAmend(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingJournalScheduleAmendMutationResult = NonNullable<Awaited<ReturnType<typeof accountingJournalScheduleAmend>>>
    export type AccountingJournalScheduleAmendMutationBody = CreateJournalScheduleDto
    export type AccountingJournalScheduleAmendMutationError = unknown

    export const useAccountingJournalScheduleAmend = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleAmend>>, TError,{id: string;data: CreateJournalScheduleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingJournalScheduleAmend>>,
        TError,
        {id: string;data: CreateJournalScheduleDto},
        TContext
      > => {
      return useMutation(getAccountingJournalScheduleAmendMutationOptions(options), queryClient);
    }

export type accountingJournalScheduleApproveResponse200 = {
  data: void
  status: 200
}

export type accountingJournalScheduleApproveResponse201 = {
  data: void
  status: 201
}

export type accountingJournalScheduleApproveResponseSuccess = (accountingJournalScheduleApproveResponse200 | accountingJournalScheduleApproveResponse201) & {
  headers: Headers;
};
;

export type accountingJournalScheduleApproveResponse = (accountingJournalScheduleApproveResponseSuccess)

export const getAccountingJournalScheduleApproveUrl = (id: string,) => {




  return `/api/accounting/journal-schedules/${id}/approve`
}

export const accountingJournalScheduleApprove = async (id: string, options?: RequestInit): Promise<accountingJournalScheduleApproveResponse> => {

  return customMutator<accountingJournalScheduleApproveResponse>(getAccountingJournalScheduleApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingJournalScheduleApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['accountingJournalScheduleApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingJournalScheduleApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  accountingJournalScheduleApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingJournalScheduleApproveMutationResult = NonNullable<Awaited<ReturnType<typeof accountingJournalScheduleApprove>>>

    export type AccountingJournalScheduleApproveMutationError = unknown

    export const useAccountingJournalScheduleApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingJournalScheduleApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getAccountingJournalScheduleApproveMutationOptions(options), queryClient);
    }

export type accountingJournalScheduleCancelResponse200 = {
  data: void
  status: 200
}

export type accountingJournalScheduleCancelResponse201 = {
  data: void
  status: 201
}

export type accountingJournalScheduleCancelResponseSuccess = (accountingJournalScheduleCancelResponse200 | accountingJournalScheduleCancelResponse201) & {
  headers: Headers;
};
;

export type accountingJournalScheduleCancelResponse = (accountingJournalScheduleCancelResponseSuccess)

export const getAccountingJournalScheduleCancelUrl = (id: string,) => {




  return `/api/accounting/journal-schedules/${id}/cancel`
}

export const accountingJournalScheduleCancel = async (id: string,
    cancelJournalScheduleDto: CancelJournalScheduleDto, options?: RequestInit): Promise<accountingJournalScheduleCancelResponse> => {

  return customMutator<accountingJournalScheduleCancelResponse>(getAccountingJournalScheduleCancelUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(cancelJournalScheduleDto)
  }
);}





export const getAccountingJournalScheduleCancelMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleCancel>>, TError,{id: string;data: CancelJournalScheduleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleCancel>>, TError,{id: string;data: CancelJournalScheduleDto}, TContext> => {

const mutationKey = ['accountingJournalScheduleCancel'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingJournalScheduleCancel>>, {id: string;data: CancelJournalScheduleDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingJournalScheduleCancel(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingJournalScheduleCancelMutationResult = NonNullable<Awaited<ReturnType<typeof accountingJournalScheduleCancel>>>
    export type AccountingJournalScheduleCancelMutationBody = CancelJournalScheduleDto
    export type AccountingJournalScheduleCancelMutationError = unknown

    export const useAccountingJournalScheduleCancel = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingJournalScheduleCancel>>, TError,{id: string;data: CancelJournalScheduleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingJournalScheduleCancel>>,
        TError,
        {id: string;data: CancelJournalScheduleDto},
        TContext
      > => {
      return useMutation(getAccountingJournalScheduleCancelMutationOptions(options), queryClient);
    }

export type accountingPostingRuleCreateResponse200 = {
  data: void
  status: 200
}

export type accountingPostingRuleCreateResponse201 = {
  data: void
  status: 201
}

export type accountingPostingRuleCreateResponseSuccess = (accountingPostingRuleCreateResponse200 | accountingPostingRuleCreateResponse201) & {
  headers: Headers;
};
;

export type accountingPostingRuleCreateResponse = (accountingPostingRuleCreateResponseSuccess)

export const getAccountingPostingRuleCreateUrl = () => {




  return `/api/accounting/posting-rules`
}

export const accountingPostingRuleCreate = async (createPostingRuleDto: CreatePostingRuleDto, options?: RequestInit): Promise<accountingPostingRuleCreateResponse> => {

  return customMutator<accountingPostingRuleCreateResponse>(getAccountingPostingRuleCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createPostingRuleDto)
  }
);}





export const getAccountingPostingRuleCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPostingRuleCreate>>, TError,{data: CreatePostingRuleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPostingRuleCreate>>, TError,{data: CreatePostingRuleDto}, TContext> => {

const mutationKey = ['accountingPostingRuleCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPostingRuleCreate>>, {data: CreatePostingRuleDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingPostingRuleCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPostingRuleCreateMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPostingRuleCreate>>>
    export type AccountingPostingRuleCreateMutationBody = CreatePostingRuleDto
    export type AccountingPostingRuleCreateMutationError = unknown

    export const useAccountingPostingRuleCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPostingRuleCreate>>, TError,{data: CreatePostingRuleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPostingRuleCreate>>,
        TError,
        {data: CreatePostingRuleDto},
        TContext
      > => {
      return useMutation(getAccountingPostingRuleCreateMutationOptions(options), queryClient);
    }

export type accountingPostingRuleListResponse200 = {
  data: void
  status: 200
}

export type accountingPostingRuleListResponseSuccess = (accountingPostingRuleListResponse200) & {
  headers: Headers;
};
;

export type accountingPostingRuleListResponse = (accountingPostingRuleListResponseSuccess)

export const getAccountingPostingRuleListUrl = () => {




  return `/api/accounting/posting-rules`
}

export const accountingPostingRuleList = async ( options?: RequestInit): Promise<accountingPostingRuleListResponse> => {

  return customMutator<accountingPostingRuleListResponse>(getAccountingPostingRuleListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingPostingRuleListQueryKey = () => {
    return [
    `/api/accounting/posting-rules`
    ] as const;
    }


export const getAccountingPostingRuleListQueryOptions = <TData = Awaited<ReturnType<typeof accountingPostingRuleList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPostingRuleList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingPostingRuleListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingPostingRuleList>>> = ({ signal }) => accountingPostingRuleList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingPostingRuleList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingPostingRuleListQueryResult = NonNullable<Awaited<ReturnType<typeof accountingPostingRuleList>>>
export type AccountingPostingRuleListQueryError = unknown


export function useAccountingPostingRuleList<TData = Awaited<ReturnType<typeof accountingPostingRuleList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPostingRuleList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingPostingRuleList>>,
          TError,
          Awaited<ReturnType<typeof accountingPostingRuleList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingPostingRuleList<TData = Awaited<ReturnType<typeof accountingPostingRuleList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPostingRuleList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingPostingRuleList>>,
          TError,
          Awaited<ReturnType<typeof accountingPostingRuleList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingPostingRuleList<TData = Awaited<ReturnType<typeof accountingPostingRuleList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPostingRuleList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingPostingRuleList<TData = Awaited<ReturnType<typeof accountingPostingRuleList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingPostingRuleList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingPostingRuleListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingPostingRuleUpdateResponse200 = {
  data: void
  status: 200
}

export type accountingPostingRuleUpdateResponseSuccess = (accountingPostingRuleUpdateResponse200) & {
  headers: Headers;
};
;

export type accountingPostingRuleUpdateResponse = (accountingPostingRuleUpdateResponseSuccess)

export const getAccountingPostingRuleUpdateUrl = (id: string,) => {




  return `/api/accounting/posting-rules/${id}`
}

export const accountingPostingRuleUpdate = async (id: string,
    createPostingRuleDto: CreatePostingRuleDto, options?: RequestInit): Promise<accountingPostingRuleUpdateResponse> => {

  return customMutator<accountingPostingRuleUpdateResponse>(getAccountingPostingRuleUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createPostingRuleDto)
  }
);}





export const getAccountingPostingRuleUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPostingRuleUpdate>>, TError,{id: string;data: CreatePostingRuleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPostingRuleUpdate>>, TError,{id: string;data: CreatePostingRuleDto}, TContext> => {

const mutationKey = ['accountingPostingRuleUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPostingRuleUpdate>>, {id: string;data: CreatePostingRuleDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingPostingRuleUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPostingRuleUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPostingRuleUpdate>>>
    export type AccountingPostingRuleUpdateMutationBody = CreatePostingRuleDto
    export type AccountingPostingRuleUpdateMutationError = unknown

    export const useAccountingPostingRuleUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPostingRuleUpdate>>, TError,{id: string;data: CreatePostingRuleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPostingRuleUpdate>>,
        TError,
        {id: string;data: CreatePostingRuleDto},
        TContext
      > => {
      return useMutation(getAccountingPostingRuleUpdateMutationOptions(options), queryClient);
    }

export type accountingPostingRuleTestResponse200 = {
  data: void
  status: 200
}

export type accountingPostingRuleTestResponseSuccess = (accountingPostingRuleTestResponse200) & {
  headers: Headers;
};
;

export type accountingPostingRuleTestResponse = (accountingPostingRuleTestResponseSuccess)

export const getAccountingPostingRuleTestUrl = (id: string,) => {




  return `/api/accounting/posting-rules/${id}/test`
}

export const accountingPostingRuleTest = async (id: string,
    postingRuleTestFactDataDto: PostingRuleTestFactDataDto, options?: RequestInit): Promise<accountingPostingRuleTestResponse> => {

  return customMutator<accountingPostingRuleTestResponse>(getAccountingPostingRuleTestUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(postingRuleTestFactDataDto)
  }
);}





export const getAccountingPostingRuleTestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPostingRuleTest>>, TError,{id: string;data: PostingRuleTestFactDataDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingPostingRuleTest>>, TError,{id: string;data: PostingRuleTestFactDataDto}, TContext> => {

const mutationKey = ['accountingPostingRuleTest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingPostingRuleTest>>, {id: string;data: PostingRuleTestFactDataDto}> = (props) => {
          const {id,data} = props ?? {};

          return  accountingPostingRuleTest(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingPostingRuleTestMutationResult = NonNullable<Awaited<ReturnType<typeof accountingPostingRuleTest>>>
    export type AccountingPostingRuleTestMutationBody = PostingRuleTestFactDataDto
    export type AccountingPostingRuleTestMutationError = unknown

    export const useAccountingPostingRuleTest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingPostingRuleTest>>, TError,{id: string;data: PostingRuleTestFactDataDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingPostingRuleTest>>,
        TError,
        {id: string;data: PostingRuleTestFactDataDto},
        TContext
      > => {
      return useMutation(getAccountingPostingRuleTestMutationOptions(options), queryClient);
    }

export type accountingAutoPostResponse200 = {
  data: void
  status: 200
}

export type accountingAutoPostResponse201 = {
  data: void
  status: 201
}

export type accountingAutoPostResponseSuccess = (accountingAutoPostResponse200 | accountingAutoPostResponse201) & {
  headers: Headers;
};
;

export type accountingAutoPostResponse = (accountingAutoPostResponseSuccess)

export const getAccountingAutoPostUrl = () => {




  return `/api/accounting/auto-post`
}

export const accountingAutoPost = async (autoPostFromFactsDto: AutoPostFromFactsDto, options?: RequestInit): Promise<accountingAutoPostResponse> => {

  return customMutator<accountingAutoPostResponse>(getAccountingAutoPostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(autoPostFromFactsDto)
  }
);}





export const getAccountingAutoPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingAutoPost>>, TError,{data: AutoPostFromFactsDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingAutoPost>>, TError,{data: AutoPostFromFactsDto}, TContext> => {

const mutationKey = ['accountingAutoPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingAutoPost>>, {data: AutoPostFromFactsDto}> = (props) => {
          const {data} = props ?? {};

          return  accountingAutoPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingAutoPostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingAutoPost>>>
    export type AccountingAutoPostMutationBody = AutoPostFromFactsDto
    export type AccountingAutoPostMutationError = unknown

    export const useAccountingAutoPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingAutoPost>>, TError,{data: AutoPostFromFactsDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingAutoPost>>,
        TError,
        {data: AutoPostFromFactsDto},
        TContext
      > => {
      return useMutation(getAccountingAutoPostMutationOptions(options), queryClient);
    }

export type accountingLedgerTrialBalanceResponse200 = {
  data: void
  status: 200
}

export type accountingLedgerTrialBalanceResponseSuccess = (accountingLedgerTrialBalanceResponse200) & {
  headers: Headers;
};
;

export type accountingLedgerTrialBalanceResponse = (accountingLedgerTrialBalanceResponseSuccess)

export const getAccountingLedgerTrialBalanceUrl = () => {




  return `/api/accounting/trial-balance`
}

export const accountingLedgerTrialBalance = async ( options?: RequestInit): Promise<accountingLedgerTrialBalanceResponse> => {

  return customMutator<accountingLedgerTrialBalanceResponse>(getAccountingLedgerTrialBalanceUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingLedgerTrialBalanceQueryKey = () => {
    return [
    `/api/accounting/trial-balance`
    ] as const;
    }


export const getAccountingLedgerTrialBalanceQueryOptions = <TData = Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingLedgerTrialBalanceQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingLedgerTrialBalance>>> = ({ signal }) => accountingLedgerTrialBalance({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingLedgerTrialBalanceQueryResult = NonNullable<Awaited<ReturnType<typeof accountingLedgerTrialBalance>>>
export type AccountingLedgerTrialBalanceQueryError = unknown


export function useAccountingLedgerTrialBalance<TData = Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingLedgerTrialBalance>>,
          TError,
          Awaited<ReturnType<typeof accountingLedgerTrialBalance>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingLedgerTrialBalance<TData = Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingLedgerTrialBalance>>,
          TError,
          Awaited<ReturnType<typeof accountingLedgerTrialBalance>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingLedgerTrialBalance<TData = Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingLedgerTrialBalance<TData = Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalance>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingLedgerTrialBalanceQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingLedgerTrialBalanceGetResponse200 = {
  data: void
  status: 200
}

export type accountingLedgerTrialBalanceGetResponseSuccess = (accountingLedgerTrialBalanceGetResponse200) & {
  headers: Headers;
};
;

export type accountingLedgerTrialBalanceGetResponse = (accountingLedgerTrialBalanceGetResponseSuccess)

export const getAccountingLedgerTrialBalanceGetUrl = () => {




  return `/api/accounting/financial-statements`
}

export const accountingLedgerTrialBalanceGet = async ( options?: RequestInit): Promise<accountingLedgerTrialBalanceGetResponse> => {

  return customMutator<accountingLedgerTrialBalanceGetResponse>(getAccountingLedgerTrialBalanceGetUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingLedgerTrialBalanceGetQueryKey = () => {
    return [
    `/api/accounting/financial-statements`
    ] as const;
    }


export const getAccountingLedgerTrialBalanceGetQueryOptions = <TData = Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingLedgerTrialBalanceGetQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>> = ({ signal }) => accountingLedgerTrialBalanceGet({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingLedgerTrialBalanceGetQueryResult = NonNullable<Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>>
export type AccountingLedgerTrialBalanceGetQueryError = unknown


export function useAccountingLedgerTrialBalanceGet<TData = Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>,
          TError,
          Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingLedgerTrialBalanceGet<TData = Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>,
          TError,
          Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingLedgerTrialBalanceGet<TData = Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingLedgerTrialBalanceGet<TData = Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerTrialBalanceGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingLedgerTrialBalanceGetQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingLedgerGeneralLedgerResponse200 = {
  data: void
  status: 200
}

export type accountingLedgerGeneralLedgerResponseSuccess = (accountingLedgerGeneralLedgerResponse200) & {
  headers: Headers;
};
;

export type accountingLedgerGeneralLedgerResponse = (accountingLedgerGeneralLedgerResponseSuccess)

export const getAccountingLedgerGeneralLedgerUrl = () => {




  return `/api/accounting/general-ledger`
}

export const accountingLedgerGeneralLedger = async ( options?: RequestInit): Promise<accountingLedgerGeneralLedgerResponse> => {

  return customMutator<accountingLedgerGeneralLedgerResponse>(getAccountingLedgerGeneralLedgerUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingLedgerGeneralLedgerQueryKey = () => {
    return [
    `/api/accounting/general-ledger`
    ] as const;
    }


export const getAccountingLedgerGeneralLedgerQueryOptions = <TData = Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingLedgerGeneralLedgerQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>> = ({ signal }) => accountingLedgerGeneralLedger({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingLedgerGeneralLedgerQueryResult = NonNullable<Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>>
export type AccountingLedgerGeneralLedgerQueryError = unknown


export function useAccountingLedgerGeneralLedger<TData = Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>,
          TError,
          Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingLedgerGeneralLedger<TData = Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>,
          TError,
          Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingLedgerGeneralLedger<TData = Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingLedgerGeneralLedger<TData = Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingLedgerGeneralLedger>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingLedgerGeneralLedgerQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingLedgerRebuildBalancesResponse200 = {
  data: void
  status: 200
}

export type accountingLedgerRebuildBalancesResponse201 = {
  data: void
  status: 201
}

export type accountingLedgerRebuildBalancesResponseSuccess = (accountingLedgerRebuildBalancesResponse200 | accountingLedgerRebuildBalancesResponse201) & {
  headers: Headers;
};
;

export type accountingLedgerRebuildBalancesResponse = (accountingLedgerRebuildBalancesResponseSuccess)

export const getAccountingLedgerRebuildBalancesUrl = () => {




  return `/api/accounting/rebuild-balances`
}

export const accountingLedgerRebuildBalances = async ( options?: RequestInit): Promise<accountingLedgerRebuildBalancesResponse> => {

  return customMutator<accountingLedgerRebuildBalancesResponse>(getAccountingLedgerRebuildBalancesUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccountingLedgerRebuildBalancesMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingLedgerRebuildBalances>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingLedgerRebuildBalances>>, TError,void, TContext> => {

const mutationKey = ['accountingLedgerRebuildBalances'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingLedgerRebuildBalances>>, void> = () => {


          return  accountingLedgerRebuildBalances(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingLedgerRebuildBalancesMutationResult = NonNullable<Awaited<ReturnType<typeof accountingLedgerRebuildBalances>>>

    export type AccountingLedgerRebuildBalancesMutationError = unknown

    export const useAccountingLedgerRebuildBalances = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingLedgerRebuildBalances>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingLedgerRebuildBalances>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAccountingLedgerRebuildBalancesMutationOptions(options), queryClient);
    }

export type complianceBreachesListResponse200 = {
  data: void
  status: 200
}

export type complianceBreachesListResponseSuccess = (complianceBreachesListResponse200) & {
  headers: Headers;
};
;

export type complianceBreachesListResponse = (complianceBreachesListResponseSuccess)

export const getComplianceBreachesListUrl = () => {




  return `/api/compliance/breaches`
}

export const complianceBreachesList = async ( options?: RequestInit): Promise<complianceBreachesListResponse> => {

  return customMutator<complianceBreachesListResponse>(getComplianceBreachesListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getComplianceBreachesListQueryKey = () => {
    return [
    `/api/compliance/breaches`
    ] as const;
    }


export const getComplianceBreachesListQueryOptions = <TData = Awaited<ReturnType<typeof complianceBreachesList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceBreachesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getComplianceBreachesListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof complianceBreachesList>>> = ({ signal }) => complianceBreachesList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof complianceBreachesList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ComplianceBreachesListQueryResult = NonNullable<Awaited<ReturnType<typeof complianceBreachesList>>>
export type ComplianceBreachesListQueryError = unknown


export function useComplianceBreachesList<TData = Awaited<ReturnType<typeof complianceBreachesList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceBreachesList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof complianceBreachesList>>,
          TError,
          Awaited<ReturnType<typeof complianceBreachesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useComplianceBreachesList<TData = Awaited<ReturnType<typeof complianceBreachesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceBreachesList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof complianceBreachesList>>,
          TError,
          Awaited<ReturnType<typeof complianceBreachesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useComplianceBreachesList<TData = Awaited<ReturnType<typeof complianceBreachesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceBreachesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useComplianceBreachesList<TData = Awaited<ReturnType<typeof complianceBreachesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceBreachesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getComplianceBreachesListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type complianceBreachesOpenResponse201 = {
  data: void
  status: 201
}

export type complianceBreachesOpenResponseSuccess = (complianceBreachesOpenResponse201) & {
  headers: Headers;
};
;

export type complianceBreachesOpenResponse = (complianceBreachesOpenResponseSuccess)

export const getComplianceBreachesOpenUrl = () => {




  return `/api/compliance/breaches`
}

export const complianceBreachesOpen = async (openComplianceBreachDto: OpenComplianceBreachDto, options?: RequestInit): Promise<complianceBreachesOpenResponse> => {

  return customMutator<complianceBreachesOpenResponse>(getComplianceBreachesOpenUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(openComplianceBreachDto)
  }
);}





export const getComplianceBreachesOpenMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceBreachesOpen>>, TError,{data: OpenComplianceBreachDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceBreachesOpen>>, TError,{data: OpenComplianceBreachDto}, TContext> => {

const mutationKey = ['complianceBreachesOpen'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceBreachesOpen>>, {data: OpenComplianceBreachDto}> = (props) => {
          const {data} = props ?? {};

          return  complianceBreachesOpen(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceBreachesOpenMutationResult = NonNullable<Awaited<ReturnType<typeof complianceBreachesOpen>>>
    export type ComplianceBreachesOpenMutationBody = OpenComplianceBreachDto
    export type ComplianceBreachesOpenMutationError = unknown

    export const useComplianceBreachesOpen = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceBreachesOpen>>, TError,{data: OpenComplianceBreachDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceBreachesOpen>>,
        TError,
        {data: OpenComplianceBreachDto},
        TContext
      > => {
      return useMutation(getComplianceBreachesOpenMutationOptions(options), queryClient);
    }

export type complianceBreachesRemediateResponse201 = {
  data: void
  status: 201
}

export type complianceBreachesRemediateResponseSuccess = (complianceBreachesRemediateResponse201) & {
  headers: Headers;
};
;

export type complianceBreachesRemediateResponse = (complianceBreachesRemediateResponseSuccess)

export const getComplianceBreachesRemediateUrl = (id: string,) => {




  return `/api/compliance/breaches/${id}/remediate`
}

export const complianceBreachesRemediate = async (id: string,
    remediateComplianceBreachDto: RemediateComplianceBreachDto, options?: RequestInit): Promise<complianceBreachesRemediateResponse> => {

  return customMutator<complianceBreachesRemediateResponse>(getComplianceBreachesRemediateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(remediateComplianceBreachDto)
  }
);}





export const getComplianceBreachesRemediateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceBreachesRemediate>>, TError,{id: string;data: RemediateComplianceBreachDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceBreachesRemediate>>, TError,{id: string;data: RemediateComplianceBreachDto}, TContext> => {

const mutationKey = ['complianceBreachesRemediate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceBreachesRemediate>>, {id: string;data: RemediateComplianceBreachDto}> = (props) => {
          const {id,data} = props ?? {};

          return  complianceBreachesRemediate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceBreachesRemediateMutationResult = NonNullable<Awaited<ReturnType<typeof complianceBreachesRemediate>>>
    export type ComplianceBreachesRemediateMutationBody = RemediateComplianceBreachDto
    export type ComplianceBreachesRemediateMutationError = unknown

    export const useComplianceBreachesRemediate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceBreachesRemediate>>, TError,{id: string;data: RemediateComplianceBreachDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceBreachesRemediate>>,
        TError,
        {id: string;data: RemediateComplianceBreachDto},
        TContext
      > => {
      return useMutation(getComplianceBreachesRemediateMutationOptions(options), queryClient);
    }

export type complianceBreachesWaiveResponse201 = {
  data: void
  status: 201
}

export type complianceBreachesWaiveResponseSuccess = (complianceBreachesWaiveResponse201) & {
  headers: Headers;
};
;

export type complianceBreachesWaiveResponse = (complianceBreachesWaiveResponseSuccess)

export const getComplianceBreachesWaiveUrl = (id: string,) => {




  return `/api/compliance/breaches/${id}/waive`
}

export const complianceBreachesWaive = async (id: string,
    waiveComplianceBreachDto: WaiveComplianceBreachDto, options?: RequestInit): Promise<complianceBreachesWaiveResponse> => {

  return customMutator<complianceBreachesWaiveResponse>(getComplianceBreachesWaiveUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(waiveComplianceBreachDto)
  }
);}





export const getComplianceBreachesWaiveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceBreachesWaive>>, TError,{id: string;data: WaiveComplianceBreachDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceBreachesWaive>>, TError,{id: string;data: WaiveComplianceBreachDto}, TContext> => {

const mutationKey = ['complianceBreachesWaive'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceBreachesWaive>>, {id: string;data: WaiveComplianceBreachDto}> = (props) => {
          const {id,data} = props ?? {};

          return  complianceBreachesWaive(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceBreachesWaiveMutationResult = NonNullable<Awaited<ReturnType<typeof complianceBreachesWaive>>>
    export type ComplianceBreachesWaiveMutationBody = WaiveComplianceBreachDto
    export type ComplianceBreachesWaiveMutationError = unknown

    export const useComplianceBreachesWaive = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceBreachesWaive>>, TError,{id: string;data: WaiveComplianceBreachDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceBreachesWaive>>,
        TError,
        {id: string;data: WaiveComplianceBreachDto},
        TContext
      > => {
      return useMutation(getComplianceBreachesWaiveMutationOptions(options), queryClient);
    }

export type complianceAmlCasesListResponse200 = {
  data: void
  status: 200
}

export type complianceAmlCasesListResponseSuccess = (complianceAmlCasesListResponse200) & {
  headers: Headers;
};
;

export type complianceAmlCasesListResponse = (complianceAmlCasesListResponseSuccess)

export const getComplianceAmlCasesListUrl = () => {




  return `/api/compliance/aml-cases`
}

export const complianceAmlCasesList = async ( options?: RequestInit): Promise<complianceAmlCasesListResponse> => {

  return customMutator<complianceAmlCasesListResponse>(getComplianceAmlCasesListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getComplianceAmlCasesListQueryKey = () => {
    return [
    `/api/compliance/aml-cases`
    ] as const;
    }


export const getComplianceAmlCasesListQueryOptions = <TData = Awaited<ReturnType<typeof complianceAmlCasesList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getComplianceAmlCasesListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof complianceAmlCasesList>>> = ({ signal }) => complianceAmlCasesList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ComplianceAmlCasesListQueryResult = NonNullable<Awaited<ReturnType<typeof complianceAmlCasesList>>>
export type ComplianceAmlCasesListQueryError = unknown


export function useComplianceAmlCasesList<TData = Awaited<ReturnType<typeof complianceAmlCasesList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof complianceAmlCasesList>>,
          TError,
          Awaited<ReturnType<typeof complianceAmlCasesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useComplianceAmlCasesList<TData = Awaited<ReturnType<typeof complianceAmlCasesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof complianceAmlCasesList>>,
          TError,
          Awaited<ReturnType<typeof complianceAmlCasesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useComplianceAmlCasesList<TData = Awaited<ReturnType<typeof complianceAmlCasesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useComplianceAmlCasesList<TData = Awaited<ReturnType<typeof complianceAmlCasesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getComplianceAmlCasesListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type complianceAmlCasesOpenResponse201 = {
  data: void
  status: 201
}

export type complianceAmlCasesOpenResponseSuccess = (complianceAmlCasesOpenResponse201) & {
  headers: Headers;
};
;

export type complianceAmlCasesOpenResponse = (complianceAmlCasesOpenResponseSuccess)

export const getComplianceAmlCasesOpenUrl = () => {




  return `/api/compliance/aml-cases`
}

export const complianceAmlCasesOpen = async (openSuspiciousActivityCaseDto: OpenSuspiciousActivityCaseDto, options?: RequestInit): Promise<complianceAmlCasesOpenResponse> => {

  return customMutator<complianceAmlCasesOpenResponse>(getComplianceAmlCasesOpenUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(openSuspiciousActivityCaseDto)
  }
);}





export const getComplianceAmlCasesOpenMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesOpen>>, TError,{data: OpenSuspiciousActivityCaseDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesOpen>>, TError,{data: OpenSuspiciousActivityCaseDto}, TContext> => {

const mutationKey = ['complianceAmlCasesOpen'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceAmlCasesOpen>>, {data: OpenSuspiciousActivityCaseDto}> = (props) => {
          const {data} = props ?? {};

          return  complianceAmlCasesOpen(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceAmlCasesOpenMutationResult = NonNullable<Awaited<ReturnType<typeof complianceAmlCasesOpen>>>
    export type ComplianceAmlCasesOpenMutationBody = OpenSuspiciousActivityCaseDto
    export type ComplianceAmlCasesOpenMutationError = unknown

    export const useComplianceAmlCasesOpen = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesOpen>>, TError,{data: OpenSuspiciousActivityCaseDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceAmlCasesOpen>>,
        TError,
        {data: OpenSuspiciousActivityCaseDto},
        TContext
      > => {
      return useMutation(getComplianceAmlCasesOpenMutationOptions(options), queryClient);
    }

export type complianceAmlCasesGetResponse200 = {
  data: void
  status: 200
}

export type complianceAmlCasesGetResponseSuccess = (complianceAmlCasesGetResponse200) & {
  headers: Headers;
};
;

export type complianceAmlCasesGetResponse = (complianceAmlCasesGetResponseSuccess)

export const getComplianceAmlCasesGetUrl = (id: string,) => {




  return `/api/compliance/aml-cases/${id}`
}

export const complianceAmlCasesGet = async (id: string, options?: RequestInit): Promise<complianceAmlCasesGetResponse> => {

  return customMutator<complianceAmlCasesGetResponse>(getComplianceAmlCasesGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getComplianceAmlCasesGetQueryKey = (id: string,) => {
    return [
    `/api/compliance/aml-cases/${id}`
    ] as const;
    }


export const getComplianceAmlCasesGetQueryOptions = <TData = Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getComplianceAmlCasesGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof complianceAmlCasesGet>>> = ({ signal }) => complianceAmlCasesGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ComplianceAmlCasesGetQueryResult = NonNullable<Awaited<ReturnType<typeof complianceAmlCasesGet>>>
export type ComplianceAmlCasesGetQueryError = unknown


export function useComplianceAmlCasesGet<TData = Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof complianceAmlCasesGet>>,
          TError,
          Awaited<ReturnType<typeof complianceAmlCasesGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useComplianceAmlCasesGet<TData = Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof complianceAmlCasesGet>>,
          TError,
          Awaited<ReturnType<typeof complianceAmlCasesGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useComplianceAmlCasesGet<TData = Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useComplianceAmlCasesGet<TData = Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceAmlCasesGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getComplianceAmlCasesGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type complianceAmlCasesReviewResponse201 = {
  data: void
  status: 201
}

export type complianceAmlCasesReviewResponseSuccess = (complianceAmlCasesReviewResponse201) & {
  headers: Headers;
};
;

export type complianceAmlCasesReviewResponse = (complianceAmlCasesReviewResponseSuccess)

export const getComplianceAmlCasesReviewUrl = (id: string,) => {




  return `/api/compliance/aml-cases/${id}/review`
}

export const complianceAmlCasesReview = async (id: string,
    caseDecisionDto: CaseDecisionDto, options?: RequestInit): Promise<complianceAmlCasesReviewResponse> => {

  return customMutator<complianceAmlCasesReviewResponse>(getComplianceAmlCasesReviewUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(caseDecisionDto)
  }
);}





export const getComplianceAmlCasesReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesReview>>, TError,{id: string;data: CaseDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesReview>>, TError,{id: string;data: CaseDecisionDto}, TContext> => {

const mutationKey = ['complianceAmlCasesReview'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceAmlCasesReview>>, {id: string;data: CaseDecisionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  complianceAmlCasesReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceAmlCasesReviewMutationResult = NonNullable<Awaited<ReturnType<typeof complianceAmlCasesReview>>>
    export type ComplianceAmlCasesReviewMutationBody = CaseDecisionDto
    export type ComplianceAmlCasesReviewMutationError = unknown

    export const useComplianceAmlCasesReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesReview>>, TError,{id: string;data: CaseDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceAmlCasesReview>>,
        TError,
        {id: string;data: CaseDecisionDto},
        TContext
      > => {
      return useMutation(getComplianceAmlCasesReviewMutationOptions(options), queryClient);
    }

export type complianceAmlCasesReportResponse201 = {
  data: void
  status: 201
}

export type complianceAmlCasesReportResponseSuccess = (complianceAmlCasesReportResponse201) & {
  headers: Headers;
};
;

export type complianceAmlCasesReportResponse = (complianceAmlCasesReportResponseSuccess)

export const getComplianceAmlCasesReportUrl = (id: string,) => {




  return `/api/compliance/aml-cases/${id}/report`
}

export const complianceAmlCasesReport = async (id: string,
    reportSuspiciousActivityCaseDto: ReportSuspiciousActivityCaseDto, options?: RequestInit): Promise<complianceAmlCasesReportResponse> => {

  return customMutator<complianceAmlCasesReportResponse>(getComplianceAmlCasesReportUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(reportSuspiciousActivityCaseDto)
  }
);}





export const getComplianceAmlCasesReportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesReport>>, TError,{id: string;data: ReportSuspiciousActivityCaseDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesReport>>, TError,{id: string;data: ReportSuspiciousActivityCaseDto}, TContext> => {

const mutationKey = ['complianceAmlCasesReport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceAmlCasesReport>>, {id: string;data: ReportSuspiciousActivityCaseDto}> = (props) => {
          const {id,data} = props ?? {};

          return  complianceAmlCasesReport(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceAmlCasesReportMutationResult = NonNullable<Awaited<ReturnType<typeof complianceAmlCasesReport>>>
    export type ComplianceAmlCasesReportMutationBody = ReportSuspiciousActivityCaseDto
    export type ComplianceAmlCasesReportMutationError = unknown

    export const useComplianceAmlCasesReport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesReport>>, TError,{id: string;data: ReportSuspiciousActivityCaseDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceAmlCasesReport>>,
        TError,
        {id: string;data: ReportSuspiciousActivityCaseDto},
        TContext
      > => {
      return useMutation(getComplianceAmlCasesReportMutationOptions(options), queryClient);
    }

export type complianceAmlCasesCloseResponse201 = {
  data: void
  status: 201
}

export type complianceAmlCasesCloseResponseSuccess = (complianceAmlCasesCloseResponse201) & {
  headers: Headers;
};
;

export type complianceAmlCasesCloseResponse = (complianceAmlCasesCloseResponseSuccess)

export const getComplianceAmlCasesCloseUrl = (id: string,) => {




  return `/api/compliance/aml-cases/${id}/close`
}

export const complianceAmlCasesClose = async (id: string,
    caseDecisionDto: CaseDecisionDto, options?: RequestInit): Promise<complianceAmlCasesCloseResponse> => {

  return customMutator<complianceAmlCasesCloseResponse>(getComplianceAmlCasesCloseUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(caseDecisionDto)
  }
);}





export const getComplianceAmlCasesCloseMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesClose>>, TError,{id: string;data: CaseDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesClose>>, TError,{id: string;data: CaseDecisionDto}, TContext> => {

const mutationKey = ['complianceAmlCasesClose'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceAmlCasesClose>>, {id: string;data: CaseDecisionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  complianceAmlCasesClose(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceAmlCasesCloseMutationResult = NonNullable<Awaited<ReturnType<typeof complianceAmlCasesClose>>>
    export type ComplianceAmlCasesCloseMutationBody = CaseDecisionDto
    export type ComplianceAmlCasesCloseMutationError = unknown

    export const useComplianceAmlCasesClose = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceAmlCasesClose>>, TError,{id: string;data: CaseDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceAmlCasesClose>>,
        TError,
        {id: string;data: CaseDecisionDto},
        TContext
      > => {
      return useMutation(getComplianceAmlCasesCloseMutationOptions(options), queryClient);
    }

export type platformHealthCheckResponse200 = {
  data: void
  status: 200
}

export type platformHealthCheckResponseSuccess = (platformHealthCheckResponse200) & {
  headers: Headers;
};
;

export type platformHealthCheckResponse = (platformHealthCheckResponseSuccess)

export const getPlatformHealthCheckUrl = () => {




  return `/api/health`
}

export const platformHealthCheck = async ( options?: RequestInit): Promise<platformHealthCheckResponse> => {

  return customMutator<platformHealthCheckResponse>(getPlatformHealthCheckUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformHealthCheckQueryKey = () => {
    return [
    `/api/health`
    ] as const;
    }


export const getPlatformHealthCheckQueryOptions = <TData = Awaited<ReturnType<typeof platformHealthCheck>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthCheck>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformHealthCheckQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformHealthCheck>>> = ({ signal }) => platformHealthCheck({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformHealthCheck>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformHealthCheckQueryResult = NonNullable<Awaited<ReturnType<typeof platformHealthCheck>>>
export type PlatformHealthCheckQueryError = unknown


export function usePlatformHealthCheck<TData = Awaited<ReturnType<typeof platformHealthCheck>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthCheck>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformHealthCheck>>,
          TError,
          Awaited<ReturnType<typeof platformHealthCheck>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformHealthCheck<TData = Awaited<ReturnType<typeof platformHealthCheck>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthCheck>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformHealthCheck>>,
          TError,
          Awaited<ReturnType<typeof platformHealthCheck>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformHealthCheck<TData = Awaited<ReturnType<typeof platformHealthCheck>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthCheck>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformHealthCheck<TData = Awaited<ReturnType<typeof platformHealthCheck>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthCheck>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformHealthCheckQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformHealthReadinessResponse200 = {
  data: void
  status: 200
}

export type platformHealthReadinessResponseSuccess = (platformHealthReadinessResponse200) & {
  headers: Headers;
};
;

export type platformHealthReadinessResponse = (platformHealthReadinessResponseSuccess)

export const getPlatformHealthReadinessUrl = () => {




  return `/api/health/ready`
}

export const platformHealthReadiness = async ( options?: RequestInit): Promise<platformHealthReadinessResponse> => {

  return customMutator<platformHealthReadinessResponse>(getPlatformHealthReadinessUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformHealthReadinessQueryKey = () => {
    return [
    `/api/health/ready`
    ] as const;
    }


export const getPlatformHealthReadinessQueryOptions = <TData = Awaited<ReturnType<typeof platformHealthReadiness>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthReadiness>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformHealthReadinessQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformHealthReadiness>>> = ({ signal }) => platformHealthReadiness({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformHealthReadiness>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformHealthReadinessQueryResult = NonNullable<Awaited<ReturnType<typeof platformHealthReadiness>>>
export type PlatformHealthReadinessQueryError = unknown


export function usePlatformHealthReadiness<TData = Awaited<ReturnType<typeof platformHealthReadiness>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthReadiness>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformHealthReadiness>>,
          TError,
          Awaited<ReturnType<typeof platformHealthReadiness>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformHealthReadiness<TData = Awaited<ReturnType<typeof platformHealthReadiness>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthReadiness>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformHealthReadiness>>,
          TError,
          Awaited<ReturnType<typeof platformHealthReadiness>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformHealthReadiness<TData = Awaited<ReturnType<typeof platformHealthReadiness>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthReadiness>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformHealthReadiness<TData = Awaited<ReturnType<typeof platformHealthReadiness>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthReadiness>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformHealthReadinessQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformHealthLivenessResponse200 = {
  data: void
  status: 200
}

export type platformHealthLivenessResponseSuccess = (platformHealthLivenessResponse200) & {
  headers: Headers;
};
;

export type platformHealthLivenessResponse = (platformHealthLivenessResponseSuccess)

export const getPlatformHealthLivenessUrl = () => {




  return `/api/health/live`
}

export const platformHealthLiveness = async ( options?: RequestInit): Promise<platformHealthLivenessResponse> => {

  return customMutator<platformHealthLivenessResponse>(getPlatformHealthLivenessUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformHealthLivenessQueryKey = () => {
    return [
    `/api/health/live`
    ] as const;
    }


export const getPlatformHealthLivenessQueryOptions = <TData = Awaited<ReturnType<typeof platformHealthLiveness>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthLiveness>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformHealthLivenessQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformHealthLiveness>>> = ({ signal }) => platformHealthLiveness({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformHealthLiveness>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformHealthLivenessQueryResult = NonNullable<Awaited<ReturnType<typeof platformHealthLiveness>>>
export type PlatformHealthLivenessQueryError = unknown


export function usePlatformHealthLiveness<TData = Awaited<ReturnType<typeof platformHealthLiveness>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthLiveness>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformHealthLiveness>>,
          TError,
          Awaited<ReturnType<typeof platformHealthLiveness>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformHealthLiveness<TData = Awaited<ReturnType<typeof platformHealthLiveness>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthLiveness>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformHealthLiveness>>,
          TError,
          Awaited<ReturnType<typeof platformHealthLiveness>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformHealthLiveness<TData = Awaited<ReturnType<typeof platformHealthLiveness>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthLiveness>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformHealthLiveness<TData = Awaited<ReturnType<typeof platformHealthLiveness>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformHealthLiveness>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformHealthLivenessQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationShellReadResponse200 = {
  data: void
  status: 200
}

export type navigationShellReadResponseSuccess = (navigationShellReadResponse200) & {
  headers: Headers;
};
;

export type navigationShellReadResponse = (navigationShellReadResponseSuccess)

export const getNavigationShellReadUrl = () => {




  return `/api/experience/shell`
}

export const navigationShellRead = async ( options?: RequestInit): Promise<navigationShellReadResponse> => {

  return customMutator<navigationShellReadResponse>(getNavigationShellReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationShellReadQueryKey = () => {
    return [
    `/api/experience/shell`
    ] as const;
    }


export const getNavigationShellReadQueryOptions = <TData = Awaited<ReturnType<typeof navigationShellRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationShellReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationShellRead>>> = ({ signal }) => navigationShellRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationShellRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationShellReadQueryResult = NonNullable<Awaited<ReturnType<typeof navigationShellRead>>>
export type NavigationShellReadQueryError = unknown


export function useNavigationShellRead<TData = Awaited<ReturnType<typeof navigationShellRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationShellRead>>,
          TError,
          Awaited<ReturnType<typeof navigationShellRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationShellRead<TData = Awaited<ReturnType<typeof navigationShellRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationShellRead>>,
          TError,
          Awaited<ReturnType<typeof navigationShellRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationShellRead<TData = Awaited<ReturnType<typeof navigationShellRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationShellRead<TData = Awaited<ReturnType<typeof navigationShellRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationShellReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRoutesReadResponse200 = {
  data: void
  status: 200
}

export type navigationRoutesReadResponseSuccess = (navigationRoutesReadResponse200) & {
  headers: Headers;
};
;

export type navigationRoutesReadResponse = (navigationRoutesReadResponseSuccess)

export const getNavigationRoutesReadUrl = () => {




  return `/api/experience/routes`
}

export const navigationRoutesRead = async ( options?: RequestInit): Promise<navigationRoutesReadResponse> => {

  return customMutator<navigationRoutesReadResponse>(getNavigationRoutesReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRoutesReadQueryKey = () => {
    return [
    `/api/experience/routes`
    ] as const;
    }


export const getNavigationRoutesReadQueryOptions = <TData = Awaited<ReturnType<typeof navigationRoutesRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRoutesReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRoutesRead>>> = ({ signal }) => navigationRoutesRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRoutesReadQueryResult = NonNullable<Awaited<ReturnType<typeof navigationRoutesRead>>>
export type NavigationRoutesReadQueryError = unknown


export function useNavigationRoutesRead<TData = Awaited<ReturnType<typeof navigationRoutesRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRoutesRead>>,
          TError,
          Awaited<ReturnType<typeof navigationRoutesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRoutesRead<TData = Awaited<ReturnType<typeof navigationRoutesRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRoutesRead>>,
          TError,
          Awaited<ReturnType<typeof navigationRoutesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRoutesRead<TData = Awaited<ReturnType<typeof navigationRoutesRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRoutesRead<TData = Awaited<ReturnType<typeof navigationRoutesRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRoutesReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRouteReadResponse200 = {
  data: void
  status: 200
}

export type navigationRouteReadResponseSuccess = (navigationRouteReadResponse200) & {
  headers: Headers;
};
;

export type navigationRouteReadResponse = (navigationRouteReadResponseSuccess)

export const getNavigationRouteReadUrl = () => {




  return `/api/experience/financial-capabilities`
}

export const navigationRouteRead = async ( options?: RequestInit): Promise<navigationRouteReadResponse> => {

  return customMutator<navigationRouteReadResponse>(getNavigationRouteReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRouteReadQueryKey = () => {
    return [
    `/api/experience/financial-capabilities`
    ] as const;
    }


export const getNavigationRouteReadQueryOptions = <TData = Awaited<ReturnType<typeof navigationRouteRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRouteReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRouteRead>>> = ({ signal }) => navigationRouteRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRouteRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRouteReadQueryResult = NonNullable<Awaited<ReturnType<typeof navigationRouteRead>>>
export type NavigationRouteReadQueryError = unknown


export function useNavigationRouteRead<TData = Awaited<ReturnType<typeof navigationRouteRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteRead>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteRead<TData = Awaited<ReturnType<typeof navigationRouteRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteRead>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteRead<TData = Awaited<ReturnType<typeof navigationRouteRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRouteRead<TData = Awaited<ReturnType<typeof navigationRouteRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRouteReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationHubReadResponse200 = {
  data: void
  status: 200
}

export type navigationHubReadResponseSuccess = (navigationHubReadResponse200) & {
  headers: Headers;
};
;

export type navigationHubReadResponse = (navigationHubReadResponseSuccess)

export const getNavigationHubReadUrl = (hubId: string,) => {




  return `/api/experience/hubs/${hubId}`
}

export const navigationHubRead = async (hubId: string, options?: RequestInit): Promise<navigationHubReadResponse> => {

  return customMutator<navigationHubReadResponse>(getNavigationHubReadUrl(hubId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationHubReadQueryKey = (hubId: string,) => {
    return [
    `/api/experience/hubs/${hubId}`
    ] as const;
    }


export const getNavigationHubReadQueryOptions = <TData = Awaited<ReturnType<typeof navigationHubRead>>, TError = unknown>(hubId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationHubReadQueryKey(hubId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationHubRead>>> = ({ signal }) => navigationHubRead(hubId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: hubId !== null && hubId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationHubRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationHubReadQueryResult = NonNullable<Awaited<ReturnType<typeof navigationHubRead>>>
export type NavigationHubReadQueryError = unknown


export function useNavigationHubRead<TData = Awaited<ReturnType<typeof navigationHubRead>>, TError = unknown>(
 hubId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationHubRead>>,
          TError,
          Awaited<ReturnType<typeof navigationHubRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationHubRead<TData = Awaited<ReturnType<typeof navigationHubRead>>, TError = unknown>(
 hubId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationHubRead>>,
          TError,
          Awaited<ReturnType<typeof navigationHubRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationHubRead<TData = Awaited<ReturnType<typeof navigationHubRead>>, TError = unknown>(
 hubId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationHubRead<TData = Awaited<ReturnType<typeof navigationHubRead>>, TError = unknown>(
 hubId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationHubReadQueryOptions(hubId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRouteReadGetResponse200 = {
  data: void
  status: 200
}

export type navigationRouteReadGetResponseSuccess = (navigationRouteReadGetResponse200) & {
  headers: Headers;
};
;

export type navigationRouteReadGetResponse = (navigationRouteReadGetResponseSuccess)

export const getNavigationRouteReadGetUrl = (resourceId: string,) => {




  return `/api/experience/resources/${resourceId}`
}

export const navigationRouteReadGet = async (resourceId: string, options?: RequestInit): Promise<navigationRouteReadGetResponse> => {

  return customMutator<navigationRouteReadGetResponse>(getNavigationRouteReadGetUrl(resourceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRouteReadGetQueryKey = (resourceId: string,) => {
    return [
    `/api/experience/resources/${resourceId}`
    ] as const;
    }


export const getNavigationRouteReadGetQueryOptions = <TData = Awaited<ReturnType<typeof navigationRouteReadGet>>, TError = unknown>(resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRouteReadGetQueryKey(resourceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRouteReadGet>>> = ({ signal }) => navigationRouteReadGet(resourceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: resourceId !== null && resourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRouteReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof navigationRouteReadGet>>>
export type NavigationRouteReadGetQueryError = unknown


export function useNavigationRouteReadGet<TData = Awaited<ReturnType<typeof navigationRouteReadGet>>, TError = unknown>(
 resourceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet<TData = Awaited<ReturnType<typeof navigationRouteReadGet>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet<TData = Awaited<ReturnType<typeof navigationRouteReadGet>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRouteReadGet<TData = Awaited<ReturnType<typeof navigationRouteReadGet>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRouteReadGetQueryOptions(resourceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRouteReadGet1Response200 = {
  data: void
  status: 200
}

export type navigationRouteReadGet1ResponseSuccess = (navigationRouteReadGet1Response200) & {
  headers: Headers;
};
;

export type navigationRouteReadGet1Response = (navigationRouteReadGet1ResponseSuccess)

export const getNavigationRouteReadGet1Url = (resourceId: string,) => {




  return `/api/experience/resources/${resourceId}/table`
}

export const navigationRouteReadGet1 = async (resourceId: string, options?: RequestInit): Promise<navigationRouteReadGet1Response> => {

  return customMutator<navigationRouteReadGet1Response>(getNavigationRouteReadGet1Url(resourceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRouteReadGet1QueryKey = (resourceId: string,) => {
    return [
    `/api/experience/resources/${resourceId}/table`
    ] as const;
    }


export const getNavigationRouteReadGet1QueryOptions = <TData = Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError = unknown>(resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRouteReadGet1QueryKey(resourceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRouteReadGet1>>> = ({ signal }) => navigationRouteReadGet1(resourceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: resourceId !== null && resourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRouteReadGet1QueryResult = NonNullable<Awaited<ReturnType<typeof navigationRouteReadGet1>>>
export type NavigationRouteReadGet1QueryError = unknown


export function useNavigationRouteReadGet1<TData = Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError = unknown>(
 resourceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet1>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet1<TData = Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet1>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet1<TData = Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRouteReadGet1<TData = Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRouteReadGet1QueryOptions(resourceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRouteReadGet2Response200 = {
  data: void
  status: 200
}

export type navigationRouteReadGet2ResponseSuccess = (navigationRouteReadGet2Response200) & {
  headers: Headers;
};
;

export type navigationRouteReadGet2Response = (navigationRouteReadGet2ResponseSuccess)

export const getNavigationRouteReadGet2Url = (resourceId: string,
    mode: 'create' | 'edit' | 'view',) => {




  return `/api/experience/resources/${resourceId}/form/${mode}`
}

export const navigationRouteReadGet2 = async (resourceId: string,
    mode: 'create' | 'edit' | 'view', options?: RequestInit): Promise<navigationRouteReadGet2Response> => {

  return customMutator<navigationRouteReadGet2Response>(getNavigationRouteReadGet2Url(resourceId,mode),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRouteReadGet2QueryKey = (resourceId: string,
    mode: 'create' | 'edit' | 'view',) => {
    return [
    `/api/experience/resources/${resourceId}/form/${mode}`
    ] as const;
    }


export const getNavigationRouteReadGet2QueryOptions = <TData = Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError = unknown>(resourceId: string,
    mode: 'create' | 'edit' | 'view', options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRouteReadGet2QueryKey(resourceId,mode);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRouteReadGet2>>> = ({ signal }) => navigationRouteReadGet2(resourceId,mode, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: resourceId !== null && resourceId !== undefined && mode !== null && mode !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRouteReadGet2QueryResult = NonNullable<Awaited<ReturnType<typeof navigationRouteReadGet2>>>
export type NavigationRouteReadGet2QueryError = unknown


export function useNavigationRouteReadGet2<TData = Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError = unknown>(
 resourceId: string,
    mode: 'create' | 'edit' | 'view', options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet2>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet2>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet2<TData = Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError = unknown>(
 resourceId: string,
    mode: 'create' | 'edit' | 'view', options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet2>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet2>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet2<TData = Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError = unknown>(
 resourceId: string,
    mode: 'create' | 'edit' | 'view', options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRouteReadGet2<TData = Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError = unknown>(
 resourceId: string,
    mode: 'create' | 'edit' | 'view', options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet2>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRouteReadGet2QueryOptions(resourceId,mode,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRouteReadGet3Response200 = {
  data: void
  status: 200
}

export type navigationRouteReadGet3ResponseSuccess = (navigationRouteReadGet3Response200) & {
  headers: Headers;
};
;

export type navigationRouteReadGet3Response = (navigationRouteReadGet3ResponseSuccess)

export const getNavigationRouteReadGet3Url = () => {




  return `/api/experience/reports`
}

export const navigationRouteReadGet3 = async ( options?: RequestInit): Promise<navigationRouteReadGet3Response> => {

  return customMutator<navigationRouteReadGet3Response>(getNavigationRouteReadGet3Url(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRouteReadGet3QueryKey = () => {
    return [
    `/api/experience/reports`
    ] as const;
    }


export const getNavigationRouteReadGet3QueryOptions = <TData = Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRouteReadGet3QueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRouteReadGet3>>> = ({ signal }) => navigationRouteReadGet3({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRouteReadGet3QueryResult = NonNullable<Awaited<ReturnType<typeof navigationRouteReadGet3>>>
export type NavigationRouteReadGet3QueryError = unknown


export function useNavigationRouteReadGet3<TData = Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet3>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet3>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet3<TData = Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet3>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet3>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet3<TData = Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRouteReadGet3<TData = Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet3>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRouteReadGet3QueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRouteReadGet4Response200 = {
  data: void
  status: 200
}

export type navigationRouteReadGet4ResponseSuccess = (navigationRouteReadGet4Response200) & {
  headers: Headers;
};
;

export type navigationRouteReadGet4Response = (navigationRouteReadGet4ResponseSuccess)

export const getNavigationRouteReadGet4Url = (reportId: string,) => {




  return `/api/experience/reports/${reportId}`
}

export const navigationRouteReadGet4 = async (reportId: string, options?: RequestInit): Promise<navigationRouteReadGet4Response> => {

  return customMutator<navigationRouteReadGet4Response>(getNavigationRouteReadGet4Url(reportId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRouteReadGet4QueryKey = (reportId: string,) => {
    return [
    `/api/experience/reports/${reportId}`
    ] as const;
    }


export const getNavigationRouteReadGet4QueryOptions = <TData = Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError = unknown>(reportId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRouteReadGet4QueryKey(reportId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRouteReadGet4>>> = ({ signal }) => navigationRouteReadGet4(reportId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: reportId !== null && reportId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRouteReadGet4QueryResult = NonNullable<Awaited<ReturnType<typeof navigationRouteReadGet4>>>
export type NavigationRouteReadGet4QueryError = unknown


export function useNavigationRouteReadGet4<TData = Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError = unknown>(
 reportId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet4>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet4>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet4<TData = Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError = unknown>(
 reportId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet4>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet4>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet4<TData = Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError = unknown>(
 reportId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRouteReadGet4<TData = Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError = unknown>(
 reportId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet4>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRouteReadGet4QueryOptions(reportId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRouteReadGet5Response200 = {
  data: void
  status: 200
}

export type navigationRouteReadGet5ResponseSuccess = (navigationRouteReadGet5Response200) & {
  headers: Headers;
};
;

export type navigationRouteReadGet5Response = (navigationRouteReadGet5ResponseSuccess)

export const getNavigationRouteReadGet5Url = (reportId: string,) => {




  return `/api/experience/reports/${reportId}/data`
}

export const navigationRouteReadGet5 = async (reportId: string, options?: RequestInit): Promise<navigationRouteReadGet5Response> => {

  return customMutator<navigationRouteReadGet5Response>(getNavigationRouteReadGet5Url(reportId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRouteReadGet5QueryKey = (reportId: string,) => {
    return [
    `/api/experience/reports/${reportId}/data`
    ] as const;
    }


export const getNavigationRouteReadGet5QueryOptions = <TData = Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError = unknown>(reportId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRouteReadGet5QueryKey(reportId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRouteReadGet5>>> = ({ signal }) => navigationRouteReadGet5(reportId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: reportId !== null && reportId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRouteReadGet5QueryResult = NonNullable<Awaited<ReturnType<typeof navigationRouteReadGet5>>>
export type NavigationRouteReadGet5QueryError = unknown


export function useNavigationRouteReadGet5<TData = Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError = unknown>(
 reportId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet5>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet5>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet5<TData = Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError = unknown>(
 reportId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet5>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet5>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet5<TData = Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError = unknown>(
 reportId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRouteReadGet5<TData = Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError = unknown>(
 reportId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet5>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRouteReadGet5QueryOptions(reportId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRouteReadGet6Response200 = {
  data: void
  status: 200
}

export type navigationRouteReadGet6ResponseSuccess = (navigationRouteReadGet6Response200) & {
  headers: Headers;
};
;

export type navigationRouteReadGet6Response = (navigationRouteReadGet6ResponseSuccess)

export const getNavigationRouteReadGet6Url = (dashboardId: string,) => {




  return `/api/experience/dashboards/${dashboardId}`
}

export const navigationRouteReadGet6 = async (dashboardId: string, options?: RequestInit): Promise<navigationRouteReadGet6Response> => {

  return customMutator<navigationRouteReadGet6Response>(getNavigationRouteReadGet6Url(dashboardId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRouteReadGet6QueryKey = (dashboardId: string,) => {
    return [
    `/api/experience/dashboards/${dashboardId}`
    ] as const;
    }


export const getNavigationRouteReadGet6QueryOptions = <TData = Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError = unknown>(dashboardId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRouteReadGet6QueryKey(dashboardId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRouteReadGet6>>> = ({ signal }) => navigationRouteReadGet6(dashboardId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: dashboardId !== null && dashboardId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRouteReadGet6QueryResult = NonNullable<Awaited<ReturnType<typeof navigationRouteReadGet6>>>
export type NavigationRouteReadGet6QueryError = unknown


export function useNavigationRouteReadGet6<TData = Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError = unknown>(
 dashboardId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet6>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet6>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet6<TData = Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError = unknown>(
 dashboardId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet6>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet6>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet6<TData = Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError = unknown>(
 dashboardId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRouteReadGet6<TData = Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError = unknown>(
 dashboardId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet6>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRouteReadGet6QueryOptions(dashboardId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type genericResourceControllerList0Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerList0ResponseSuccess = (genericResourceControllerList0Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerList0Response = (genericResourceControllerList0ResponseSuccess)

export const getGenericResourceControllerList0Url = (resourceId: string,) => {




  return `/api/resource/${resourceId}`
}

export const genericResourceControllerList0 = async (resourceId: string, options?: RequestInit): Promise<genericResourceControllerList0Response> => {

  return customMutator<genericResourceControllerList0Response>(getGenericResourceControllerList0Url(resourceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGenericResourceControllerList0QueryKey = (resourceId: string,) => {
    return [
    `/api/resource/${resourceId}`
    ] as const;
    }


export const getGenericResourceControllerList0QueryOptions = <TData = Awaited<ReturnType<typeof genericResourceControllerList0>>, TError = unknown>(resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList0>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGenericResourceControllerList0QueryKey(resourceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof genericResourceControllerList0>>> = ({ signal }) => genericResourceControllerList0(resourceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: resourceId !== null && resourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList0>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GenericResourceControllerList0QueryResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerList0>>>
export type GenericResourceControllerList0QueryError = unknown


export function useGenericResourceControllerList0<TData = Awaited<ReturnType<typeof genericResourceControllerList0>>, TError = unknown>(
 resourceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList0>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof genericResourceControllerList0>>,
          TError,
          Awaited<ReturnType<typeof genericResourceControllerList0>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGenericResourceControllerList0<TData = Awaited<ReturnType<typeof genericResourceControllerList0>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList0>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof genericResourceControllerList0>>,
          TError,
          Awaited<ReturnType<typeof genericResourceControllerList0>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGenericResourceControllerList0<TData = Awaited<ReturnType<typeof genericResourceControllerList0>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList0>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGenericResourceControllerList0<TData = Awaited<ReturnType<typeof genericResourceControllerList0>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList0>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGenericResourceControllerList0QueryOptions(resourceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type genericResourceControllerCreate0Response201 = {
  data: void
  status: 201
}

export type genericResourceControllerCreate0ResponseSuccess = (genericResourceControllerCreate0Response201) & {
  headers: Headers;
};
;

export type genericResourceControllerCreate0Response = (genericResourceControllerCreate0ResponseSuccess)

export const getGenericResourceControllerCreate0Url = (resourceId: string,) => {




  return `/api/resource/${resourceId}`
}

export const genericResourceControllerCreate0 = async (resourceId: string, options?: RequestInit): Promise<genericResourceControllerCreate0Response> => {

  return customMutator<genericResourceControllerCreate0Response>(getGenericResourceControllerCreate0Url(resourceId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenericResourceControllerCreate0MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerCreate0>>, TError,{resourceId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerCreate0>>, TError,{resourceId: string}, TContext> => {

const mutationKey = ['genericResourceControllerCreate0'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerCreate0>>, {resourceId: string}> = (props) => {
          const {resourceId} = props ?? {};

          return  genericResourceControllerCreate0(resourceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerCreate0MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerCreate0>>>

    export type GenericResourceControllerCreate0MutationError = unknown

    export const useGenericResourceControllerCreate0 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerCreate0>>, TError,{resourceId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerCreate0>>,
        TError,
        {resourceId: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerCreate0MutationOptions(options), queryClient);
    }

export type genericResourceControllerList1Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerList1ResponseSuccess = (genericResourceControllerList1Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerList1Response = (genericResourceControllerList1ResponseSuccess)

export const getGenericResourceControllerList1Url = (resourceId: string,) => {




  return `/api/resources/${resourceId}`
}

export const genericResourceControllerList1 = async (resourceId: string, options?: RequestInit): Promise<genericResourceControllerList1Response> => {

  return customMutator<genericResourceControllerList1Response>(getGenericResourceControllerList1Url(resourceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGenericResourceControllerList1QueryKey = (resourceId: string,) => {
    return [
    `/api/resources/${resourceId}`
    ] as const;
    }


export const getGenericResourceControllerList1QueryOptions = <TData = Awaited<ReturnType<typeof genericResourceControllerList1>>, TError = unknown>(resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGenericResourceControllerList1QueryKey(resourceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof genericResourceControllerList1>>> = ({ signal }) => genericResourceControllerList1(resourceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: resourceId !== null && resourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList1>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GenericResourceControllerList1QueryResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerList1>>>
export type GenericResourceControllerList1QueryError = unknown


export function useGenericResourceControllerList1<TData = Awaited<ReturnType<typeof genericResourceControllerList1>>, TError = unknown>(
 resourceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList1>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof genericResourceControllerList1>>,
          TError,
          Awaited<ReturnType<typeof genericResourceControllerList1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGenericResourceControllerList1<TData = Awaited<ReturnType<typeof genericResourceControllerList1>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList1>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof genericResourceControllerList1>>,
          TError,
          Awaited<ReturnType<typeof genericResourceControllerList1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGenericResourceControllerList1<TData = Awaited<ReturnType<typeof genericResourceControllerList1>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGenericResourceControllerList1<TData = Awaited<ReturnType<typeof genericResourceControllerList1>>, TError = unknown>(
 resourceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerList1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGenericResourceControllerList1QueryOptions(resourceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type genericResourceControllerCreate1Response201 = {
  data: void
  status: 201
}

export type genericResourceControllerCreate1ResponseSuccess = (genericResourceControllerCreate1Response201) & {
  headers: Headers;
};
;

export type genericResourceControllerCreate1Response = (genericResourceControllerCreate1ResponseSuccess)

export const getGenericResourceControllerCreate1Url = (resourceId: string,) => {




  return `/api/resources/${resourceId}`
}

export const genericResourceControllerCreate1 = async (resourceId: string, options?: RequestInit): Promise<genericResourceControllerCreate1Response> => {

  return customMutator<genericResourceControllerCreate1Response>(getGenericResourceControllerCreate1Url(resourceId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenericResourceControllerCreate1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerCreate1>>, TError,{resourceId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerCreate1>>, TError,{resourceId: string}, TContext> => {

const mutationKey = ['genericResourceControllerCreate1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerCreate1>>, {resourceId: string}> = (props) => {
          const {resourceId} = props ?? {};

          return  genericResourceControllerCreate1(resourceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerCreate1MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerCreate1>>>

    export type GenericResourceControllerCreate1MutationError = unknown

    export const useGenericResourceControllerCreate1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerCreate1>>, TError,{resourceId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerCreate1>>,
        TError,
        {resourceId: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerCreate1MutationOptions(options), queryClient);
    }

export type genericResourceControllerRead0Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerRead0ResponseSuccess = (genericResourceControllerRead0Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerRead0Response = (genericResourceControllerRead0ResponseSuccess)

export const getGenericResourceControllerRead0Url = (resourceId: string,
    id: string,) => {




  return `/api/resource/${resourceId}/${id}`
}

export const genericResourceControllerRead0 = async (resourceId: string,
    id: string, options?: RequestInit): Promise<genericResourceControllerRead0Response> => {

  return customMutator<genericResourceControllerRead0Response>(getGenericResourceControllerRead0Url(resourceId,id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGenericResourceControllerRead0QueryKey = (resourceId: string,
    id: string,) => {
    return [
    `/api/resource/${resourceId}/${id}`
    ] as const;
    }


export const getGenericResourceControllerRead0QueryOptions = <TData = Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError = unknown>(resourceId: string,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGenericResourceControllerRead0QueryKey(resourceId,id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof genericResourceControllerRead0>>> = ({ signal }) => genericResourceControllerRead0(resourceId,id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: resourceId !== null && resourceId !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GenericResourceControllerRead0QueryResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerRead0>>>
export type GenericResourceControllerRead0QueryError = unknown


export function useGenericResourceControllerRead0<TData = Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError = unknown>(
 resourceId: string,
    id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof genericResourceControllerRead0>>,
          TError,
          Awaited<ReturnType<typeof genericResourceControllerRead0>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGenericResourceControllerRead0<TData = Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError = unknown>(
 resourceId: string,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof genericResourceControllerRead0>>,
          TError,
          Awaited<ReturnType<typeof genericResourceControllerRead0>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGenericResourceControllerRead0<TData = Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError = unknown>(
 resourceId: string,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGenericResourceControllerRead0<TData = Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError = unknown>(
 resourceId: string,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead0>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGenericResourceControllerRead0QueryOptions(resourceId,id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type genericResourceControllerUpdate0Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerUpdate0ResponseSuccess = (genericResourceControllerUpdate0Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerUpdate0Response = (genericResourceControllerUpdate0ResponseSuccess)

export const getGenericResourceControllerUpdate0Url = (resourceId: string,
    id: string,) => {




  return `/api/resource/${resourceId}/${id}`
}

export const genericResourceControllerUpdate0 = async (resourceId: string,
    id: string, options?: RequestInit): Promise<genericResourceControllerUpdate0Response> => {

  return customMutator<genericResourceControllerUpdate0Response>(getGenericResourceControllerUpdate0Url(resourceId,id),
  {
    ...options,
    method: 'PATCH'


  }
);}





export const getGenericResourceControllerUpdate0MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerUpdate0>>, TError,{resourceId: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerUpdate0>>, TError,{resourceId: string;id: string}, TContext> => {

const mutationKey = ['genericResourceControllerUpdate0'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerUpdate0>>, {resourceId: string;id: string}> = (props) => {
          const {resourceId,id} = props ?? {};

          return  genericResourceControllerUpdate0(resourceId,id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerUpdate0MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerUpdate0>>>

    export type GenericResourceControllerUpdate0MutationError = unknown

    export const useGenericResourceControllerUpdate0 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerUpdate0>>, TError,{resourceId: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerUpdate0>>,
        TError,
        {resourceId: string;id: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerUpdate0MutationOptions(options), queryClient);
    }

export type genericResourceControllerDelete0Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerDelete0ResponseSuccess = (genericResourceControllerDelete0Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerDelete0Response = (genericResourceControllerDelete0ResponseSuccess)

export const getGenericResourceControllerDelete0Url = (resourceId: string,
    id: string,) => {




  return `/api/resource/${resourceId}/${id}`
}

export const genericResourceControllerDelete0 = async (resourceId: string,
    id: string, options?: RequestInit): Promise<genericResourceControllerDelete0Response> => {

  return customMutator<genericResourceControllerDelete0Response>(getGenericResourceControllerDelete0Url(resourceId,id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getGenericResourceControllerDelete0MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerDelete0>>, TError,{resourceId: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerDelete0>>, TError,{resourceId: string;id: string}, TContext> => {

const mutationKey = ['genericResourceControllerDelete0'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerDelete0>>, {resourceId: string;id: string}> = (props) => {
          const {resourceId,id} = props ?? {};

          return  genericResourceControllerDelete0(resourceId,id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerDelete0MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerDelete0>>>

    export type GenericResourceControllerDelete0MutationError = unknown

    export const useGenericResourceControllerDelete0 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerDelete0>>, TError,{resourceId: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerDelete0>>,
        TError,
        {resourceId: string;id: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerDelete0MutationOptions(options), queryClient);
    }

export type genericResourceControllerRead1Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerRead1ResponseSuccess = (genericResourceControllerRead1Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerRead1Response = (genericResourceControllerRead1ResponseSuccess)

export const getGenericResourceControllerRead1Url = (resourceId: string,
    id: string,) => {




  return `/api/resources/${resourceId}/${id}`
}

export const genericResourceControllerRead1 = async (resourceId: string,
    id: string, options?: RequestInit): Promise<genericResourceControllerRead1Response> => {

  return customMutator<genericResourceControllerRead1Response>(getGenericResourceControllerRead1Url(resourceId,id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGenericResourceControllerRead1QueryKey = (resourceId: string,
    id: string,) => {
    return [
    `/api/resources/${resourceId}/${id}`
    ] as const;
    }


export const getGenericResourceControllerRead1QueryOptions = <TData = Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError = unknown>(resourceId: string,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGenericResourceControllerRead1QueryKey(resourceId,id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof genericResourceControllerRead1>>> = ({ signal }) => genericResourceControllerRead1(resourceId,id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: resourceId !== null && resourceId !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GenericResourceControllerRead1QueryResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerRead1>>>
export type GenericResourceControllerRead1QueryError = unknown


export function useGenericResourceControllerRead1<TData = Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError = unknown>(
 resourceId: string,
    id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof genericResourceControllerRead1>>,
          TError,
          Awaited<ReturnType<typeof genericResourceControllerRead1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGenericResourceControllerRead1<TData = Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError = unknown>(
 resourceId: string,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof genericResourceControllerRead1>>,
          TError,
          Awaited<ReturnType<typeof genericResourceControllerRead1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGenericResourceControllerRead1<TData = Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError = unknown>(
 resourceId: string,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGenericResourceControllerRead1<TData = Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError = unknown>(
 resourceId: string,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof genericResourceControllerRead1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGenericResourceControllerRead1QueryOptions(resourceId,id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type genericResourceControllerUpdate1Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerUpdate1ResponseSuccess = (genericResourceControllerUpdate1Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerUpdate1Response = (genericResourceControllerUpdate1ResponseSuccess)

export const getGenericResourceControllerUpdate1Url = (resourceId: string,
    id: string,) => {




  return `/api/resources/${resourceId}/${id}`
}

export const genericResourceControllerUpdate1 = async (resourceId: string,
    id: string, options?: RequestInit): Promise<genericResourceControllerUpdate1Response> => {

  return customMutator<genericResourceControllerUpdate1Response>(getGenericResourceControllerUpdate1Url(resourceId,id),
  {
    ...options,
    method: 'PATCH'


  }
);}





export const getGenericResourceControllerUpdate1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerUpdate1>>, TError,{resourceId: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerUpdate1>>, TError,{resourceId: string;id: string}, TContext> => {

const mutationKey = ['genericResourceControllerUpdate1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerUpdate1>>, {resourceId: string;id: string}> = (props) => {
          const {resourceId,id} = props ?? {};

          return  genericResourceControllerUpdate1(resourceId,id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerUpdate1MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerUpdate1>>>

    export type GenericResourceControllerUpdate1MutationError = unknown

    export const useGenericResourceControllerUpdate1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerUpdate1>>, TError,{resourceId: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerUpdate1>>,
        TError,
        {resourceId: string;id: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerUpdate1MutationOptions(options), queryClient);
    }

export type genericResourceControllerDelete1Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerDelete1ResponseSuccess = (genericResourceControllerDelete1Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerDelete1Response = (genericResourceControllerDelete1ResponseSuccess)

export const getGenericResourceControllerDelete1Url = (resourceId: string,
    id: string,) => {




  return `/api/resources/${resourceId}/${id}`
}

export const genericResourceControllerDelete1 = async (resourceId: string,
    id: string, options?: RequestInit): Promise<genericResourceControllerDelete1Response> => {

  return customMutator<genericResourceControllerDelete1Response>(getGenericResourceControllerDelete1Url(resourceId,id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getGenericResourceControllerDelete1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerDelete1>>, TError,{resourceId: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerDelete1>>, TError,{resourceId: string;id: string}, TContext> => {

const mutationKey = ['genericResourceControllerDelete1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerDelete1>>, {resourceId: string;id: string}> = (props) => {
          const {resourceId,id} = props ?? {};

          return  genericResourceControllerDelete1(resourceId,id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerDelete1MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerDelete1>>>

    export type GenericResourceControllerDelete1MutationError = unknown

    export const useGenericResourceControllerDelete1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerDelete1>>, TError,{resourceId: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerDelete1>>,
        TError,
        {resourceId: string;id: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerDelete1MutationOptions(options), queryClient);
    }

export type genericResourceControllerAction0Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerAction0ResponseSuccess = (genericResourceControllerAction0Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerAction0Response = (genericResourceControllerAction0ResponseSuccess)

export const getGenericResourceControllerAction0Url = (resourceId: string,
    actionId: string,) => {




  return `/api/resource/${resourceId}/actions/${actionId}`
}

export const genericResourceControllerAction0 = async (resourceId: string,
    actionId: string, options?: RequestInit): Promise<genericResourceControllerAction0Response> => {

  return customMutator<genericResourceControllerAction0Response>(getGenericResourceControllerAction0Url(resourceId,actionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenericResourceControllerAction0MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerAction0>>, TError,{resourceId: string;actionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerAction0>>, TError,{resourceId: string;actionId: string}, TContext> => {

const mutationKey = ['genericResourceControllerAction0'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerAction0>>, {resourceId: string;actionId: string}> = (props) => {
          const {resourceId,actionId} = props ?? {};

          return  genericResourceControllerAction0(resourceId,actionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerAction0MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerAction0>>>

    export type GenericResourceControllerAction0MutationError = unknown

    export const useGenericResourceControllerAction0 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerAction0>>, TError,{resourceId: string;actionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerAction0>>,
        TError,
        {resourceId: string;actionId: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerAction0MutationOptions(options), queryClient);
    }

export type genericResourceControllerAction1Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerAction1ResponseSuccess = (genericResourceControllerAction1Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerAction1Response = (genericResourceControllerAction1ResponseSuccess)

export const getGenericResourceControllerAction1Url = (resourceId: string,
    actionId: string,) => {




  return `/api/resources/${resourceId}/actions/${actionId}`
}

export const genericResourceControllerAction1 = async (resourceId: string,
    actionId: string, options?: RequestInit): Promise<genericResourceControllerAction1Response> => {

  return customMutator<genericResourceControllerAction1Response>(getGenericResourceControllerAction1Url(resourceId,actionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenericResourceControllerAction1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerAction1>>, TError,{resourceId: string;actionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerAction1>>, TError,{resourceId: string;actionId: string}, TContext> => {

const mutationKey = ['genericResourceControllerAction1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerAction1>>, {resourceId: string;actionId: string}> = (props) => {
          const {resourceId,actionId} = props ?? {};

          return  genericResourceControllerAction1(resourceId,actionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerAction1MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerAction1>>>

    export type GenericResourceControllerAction1MutationError = unknown

    export const useGenericResourceControllerAction1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerAction1>>, TError,{resourceId: string;actionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerAction1>>,
        TError,
        {resourceId: string;actionId: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerAction1MutationOptions(options), queryClient);
    }

export type genericResourceControllerBulkAction0Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerBulkAction0ResponseSuccess = (genericResourceControllerBulkAction0Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerBulkAction0Response = (genericResourceControllerBulkAction0ResponseSuccess)

export const getGenericResourceControllerBulkAction0Url = (resourceId: string,
    actionId: string,) => {




  return `/api/resource/${resourceId}/bulk-actions/${actionId}`
}

export const genericResourceControllerBulkAction0 = async (resourceId: string,
    actionId: string, options?: RequestInit): Promise<genericResourceControllerBulkAction0Response> => {

  return customMutator<genericResourceControllerBulkAction0Response>(getGenericResourceControllerBulkAction0Url(resourceId,actionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenericResourceControllerBulkAction0MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerBulkAction0>>, TError,{resourceId: string;actionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerBulkAction0>>, TError,{resourceId: string;actionId: string}, TContext> => {

const mutationKey = ['genericResourceControllerBulkAction0'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerBulkAction0>>, {resourceId: string;actionId: string}> = (props) => {
          const {resourceId,actionId} = props ?? {};

          return  genericResourceControllerBulkAction0(resourceId,actionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerBulkAction0MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerBulkAction0>>>

    export type GenericResourceControllerBulkAction0MutationError = unknown

    export const useGenericResourceControllerBulkAction0 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerBulkAction0>>, TError,{resourceId: string;actionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerBulkAction0>>,
        TError,
        {resourceId: string;actionId: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerBulkAction0MutationOptions(options), queryClient);
    }

export type genericResourceControllerBulkAction1Response200 = {
  data: void
  status: 200
}

export type genericResourceControllerBulkAction1ResponseSuccess = (genericResourceControllerBulkAction1Response200) & {
  headers: Headers;
};
;

export type genericResourceControllerBulkAction1Response = (genericResourceControllerBulkAction1ResponseSuccess)

export const getGenericResourceControllerBulkAction1Url = (resourceId: string,
    actionId: string,) => {




  return `/api/resources/${resourceId}/bulk-actions/${actionId}`
}

export const genericResourceControllerBulkAction1 = async (resourceId: string,
    actionId: string, options?: RequestInit): Promise<genericResourceControllerBulkAction1Response> => {

  return customMutator<genericResourceControllerBulkAction1Response>(getGenericResourceControllerBulkAction1Url(resourceId,actionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenericResourceControllerBulkAction1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerBulkAction1>>, TError,{resourceId: string;actionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerBulkAction1>>, TError,{resourceId: string;actionId: string}, TContext> => {

const mutationKey = ['genericResourceControllerBulkAction1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof genericResourceControllerBulkAction1>>, {resourceId: string;actionId: string}> = (props) => {
          const {resourceId,actionId} = props ?? {};

          return  genericResourceControllerBulkAction1(resourceId,actionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenericResourceControllerBulkAction1MutationResult = NonNullable<Awaited<ReturnType<typeof genericResourceControllerBulkAction1>>>

    export type GenericResourceControllerBulkAction1MutationError = unknown

    export const useGenericResourceControllerBulkAction1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof genericResourceControllerBulkAction1>>, TError,{resourceId: string;actionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof genericResourceControllerBulkAction1>>,
        TError,
        {resourceId: string;actionId: string},
        TContext
      > => {
      return useMutation(getGenericResourceControllerBulkAction1MutationOptions(options), queryClient);
    }

export type navigationShellReadGetResponse200 = {
  data: void
  status: 200
}

export type navigationShellReadGetResponseSuccess = (navigationShellReadGetResponse200) & {
  headers: Headers;
};
;

export type navigationShellReadGetResponse = (navigationShellReadGetResponseSuccess)

export const getNavigationShellReadGetUrl = () => {




  return `/api/navigation`
}

export const navigationShellReadGet = async ( options?: RequestInit): Promise<navigationShellReadGetResponse> => {

  return customMutator<navigationShellReadGetResponse>(getNavigationShellReadGetUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationShellReadGetQueryKey = () => {
    return [
    `/api/navigation`
    ] as const;
    }


export const getNavigationShellReadGetQueryOptions = <TData = Awaited<ReturnType<typeof navigationShellReadGet>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationShellReadGetQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationShellReadGet>>> = ({ signal }) => navigationShellReadGet({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationShellReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationShellReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof navigationShellReadGet>>>
export type NavigationShellReadGetQueryError = unknown


export function useNavigationShellReadGet<TData = Awaited<ReturnType<typeof navigationShellReadGet>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationShellReadGet>>,
          TError,
          Awaited<ReturnType<typeof navigationShellReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationShellReadGet<TData = Awaited<ReturnType<typeof navigationShellReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationShellReadGet>>,
          TError,
          Awaited<ReturnType<typeof navigationShellReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationShellReadGet<TData = Awaited<ReturnType<typeof navigationShellReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationShellReadGet<TData = Awaited<ReturnType<typeof navigationShellReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationShellReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationShellReadGetQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRoutesReadGetResponse200 = {
  data: NavigationRoutesReadGet200
  status: 200
}

export type navigationRoutesReadGetResponseSuccess = (navigationRoutesReadGetResponse200) & {
  headers: Headers;
};
;

export type navigationRoutesReadGetResponse = (navigationRoutesReadGetResponseSuccess)

export const getNavigationRoutesReadGetUrl = () => {




  return `/api/navigation/routes`
}

export const navigationRoutesReadGet = async ( options?: RequestInit): Promise<navigationRoutesReadGetResponse> => {

  return customMutator<navigationRoutesReadGetResponse>(getNavigationRoutesReadGetUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRoutesReadGetQueryKey = () => {
    return [
    `/api/navigation/routes`
    ] as const;
    }


export const getNavigationRoutesReadGetQueryOptions = <TData = Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRoutesReadGetQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRoutesReadGet>>> = ({ signal }) => navigationRoutesReadGet({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRoutesReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof navigationRoutesReadGet>>>
export type NavigationRoutesReadGetQueryError = unknown


export function useNavigationRoutesReadGet<TData = Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRoutesReadGet>>,
          TError,
          Awaited<ReturnType<typeof navigationRoutesReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRoutesReadGet<TData = Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRoutesReadGet>>,
          TError,
          Awaited<ReturnType<typeof navigationRoutesReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRoutesReadGet<TData = Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRoutesReadGet<TData = Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRoutesReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRoutesReadGetQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationRouteReadGet7Response200 = {
  data: NavigationRouteReadGet7200
  status: 200
}

export type navigationRouteReadGet7ResponseSuccess = (navigationRouteReadGet7Response200) & {
  headers: Headers;
};
;

export type navigationRouteReadGet7Response = (navigationRouteReadGet7ResponseSuccess)

export const getNavigationRouteReadGet7Url = (routeId: string,) => {




  return `/api/navigation/route/${routeId}`
}

export const navigationRouteReadGet7 = async (routeId: string, options?: RequestInit): Promise<navigationRouteReadGet7Response> => {

  return customMutator<navigationRouteReadGet7Response>(getNavigationRouteReadGet7Url(routeId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationRouteReadGet7QueryKey = (routeId: string,) => {
    return [
    `/api/navigation/route/${routeId}`
    ] as const;
    }


export const getNavigationRouteReadGet7QueryOptions = <TData = Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError = unknown>(routeId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationRouteReadGet7QueryKey(routeId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationRouteReadGet7>>> = ({ signal }) => navigationRouteReadGet7(routeId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: routeId !== null && routeId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationRouteReadGet7QueryResult = NonNullable<Awaited<ReturnType<typeof navigationRouteReadGet7>>>
export type NavigationRouteReadGet7QueryError = unknown


export function useNavigationRouteReadGet7<TData = Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError = unknown>(
 routeId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet7>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet7>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet7<TData = Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError = unknown>(
 routeId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationRouteReadGet7>>,
          TError,
          Awaited<ReturnType<typeof navigationRouteReadGet7>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationRouteReadGet7<TData = Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError = unknown>(
 routeId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationRouteReadGet7<TData = Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError = unknown>(
 routeId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationRouteReadGet7>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationRouteReadGet7QueryOptions(routeId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type navigationHubReadGetResponse200 = {
  data: void
  status: 200
}

export type navigationHubReadGetResponseSuccess = (navigationHubReadGetResponse200) & {
  headers: Headers;
};
;

export type navigationHubReadGetResponse = (navigationHubReadGetResponseSuccess)

export const getNavigationHubReadGetUrl = (hub: string,) => {




  return `/api/navigation/hub/${hub}`
}

export const navigationHubReadGet = async (hub: string, options?: RequestInit): Promise<navigationHubReadGetResponse> => {

  return customMutator<navigationHubReadGetResponse>(getNavigationHubReadGetUrl(hub),
  {
    ...options,
    method: 'GET'


  }
);}





export const getNavigationHubReadGetQueryKey = (hub: string,) => {
    return [
    `/api/navigation/hub/${hub}`
    ] as const;
    }


export const getNavigationHubReadGetQueryOptions = <TData = Awaited<ReturnType<typeof navigationHubReadGet>>, TError = unknown>(hub: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNavigationHubReadGetQueryKey(hub);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof navigationHubReadGet>>> = ({ signal }) => navigationHubReadGet(hub, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: hub !== null && hub !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof navigationHubReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NavigationHubReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof navigationHubReadGet>>>
export type NavigationHubReadGetQueryError = unknown


export function useNavigationHubReadGet<TData = Awaited<ReturnType<typeof navigationHubReadGet>>, TError = unknown>(
 hub: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationHubReadGet>>,
          TError,
          Awaited<ReturnType<typeof navigationHubReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationHubReadGet<TData = Awaited<ReturnType<typeof navigationHubReadGet>>, TError = unknown>(
 hub: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigationHubReadGet>>,
          TError,
          Awaited<ReturnType<typeof navigationHubReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNavigationHubReadGet<TData = Awaited<ReturnType<typeof navigationHubReadGet>>, TError = unknown>(
 hub: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useNavigationHubReadGet<TData = Awaited<ReturnType<typeof navigationHubReadGet>>, TError = unknown>(
 hub: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof navigationHubReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNavigationHubReadGetQueryOptions(hub,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationAuditListResponse200 = {
  data: OrganizationAuditList200
  status: 200
}

export type organizationAuditListResponseSuccess = (organizationAuditListResponse200) & {
  headers: Headers;
};
;

export type organizationAuditListResponse = (organizationAuditListResponseSuccess)

export const getOrganizationAuditListUrl = (params?: OrganizationAuditListParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/audit-log?${stringifiedParams}` : `/api/admin/audit-log`
}

export const organizationAuditList = async (params?: OrganizationAuditListParams, options?: RequestInit): Promise<organizationAuditListResponse> => {

  return customMutator<organizationAuditListResponse>(getOrganizationAuditListUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationAuditListQueryKey = (params?: OrganizationAuditListParams,) => {
    return [
    `/api/admin/audit-log`, ...(params ? [params] : [])
    ] as const;
    }


export const getOrganizationAuditListQueryOptions = <TData = Awaited<ReturnType<typeof organizationAuditList>>, TError = unknown>(params?: OrganizationAuditListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationAuditListQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationAuditList>>> = ({ signal }) => organizationAuditList(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationAuditList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationAuditListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationAuditList>>>
export type OrganizationAuditListQueryError = unknown


export function useOrganizationAuditList<TData = Awaited<ReturnType<typeof organizationAuditList>>, TError = unknown>(
 params: undefined |  OrganizationAuditListParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAuditList>>,
          TError,
          Awaited<ReturnType<typeof organizationAuditList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAuditList<TData = Awaited<ReturnType<typeof organizationAuditList>>, TError = unknown>(
 params?: OrganizationAuditListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAuditList>>,
          TError,
          Awaited<ReturnType<typeof organizationAuditList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAuditList<TData = Awaited<ReturnType<typeof organizationAuditList>>, TError = unknown>(
 params?: OrganizationAuditListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationAuditList<TData = Awaited<ReturnType<typeof organizationAuditList>>, TError = unknown>(
 params?: OrganizationAuditListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationAuditListQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationAuditExportResponse200 = {
  data: OrganizationAuditExport200
  status: 200
}

export type organizationAuditExportResponseSuccess = (organizationAuditExportResponse200) & {
  headers: Headers;
};
;

export type organizationAuditExportResponse = (organizationAuditExportResponseSuccess)

export const getOrganizationAuditExportUrl = (params?: OrganizationAuditExportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/audit-log/export?${stringifiedParams}` : `/api/admin/audit-log/export`
}

export const organizationAuditExport = async (params?: OrganizationAuditExportParams, options?: RequestInit): Promise<organizationAuditExportResponse> => {

  return customMutator<organizationAuditExportResponse>(getOrganizationAuditExportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationAuditExportQueryKey = (params?: OrganizationAuditExportParams,) => {
    return [
    `/api/admin/audit-log/export`, ...(params ? [params] : [])
    ] as const;
    }


export const getOrganizationAuditExportQueryOptions = <TData = Awaited<ReturnType<typeof organizationAuditExport>>, TError = unknown>(params?: OrganizationAuditExportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditExport>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationAuditExportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationAuditExport>>> = ({ signal }) => organizationAuditExport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationAuditExport>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationAuditExportQueryResult = NonNullable<Awaited<ReturnType<typeof organizationAuditExport>>>
export type OrganizationAuditExportQueryError = unknown


export function useOrganizationAuditExport<TData = Awaited<ReturnType<typeof organizationAuditExport>>, TError = unknown>(
 params: undefined |  OrganizationAuditExportParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditExport>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAuditExport>>,
          TError,
          Awaited<ReturnType<typeof organizationAuditExport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAuditExport<TData = Awaited<ReturnType<typeof organizationAuditExport>>, TError = unknown>(
 params?: OrganizationAuditExportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditExport>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAuditExport>>,
          TError,
          Awaited<ReturnType<typeof organizationAuditExport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAuditExport<TData = Awaited<ReturnType<typeof organizationAuditExport>>, TError = unknown>(
 params?: OrganizationAuditExportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditExport>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationAuditExport<TData = Awaited<ReturnType<typeof organizationAuditExport>>, TError = unknown>(
 params?: OrganizationAuditExportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditExport>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationAuditExportQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationAuditReadResponse200 = {
  data: OrganizationAuditRead200
  status: 200
}

export type organizationAuditReadResponseSuccess = (organizationAuditReadResponse200) & {
  headers: Headers;
};
;

export type organizationAuditReadResponse = (organizationAuditReadResponseSuccess)

export const getOrganizationAuditReadUrl = (id: string,) => {




  return `/api/admin/audit-log/${id}`
}

export const organizationAuditRead = async (id: string, options?: RequestInit): Promise<organizationAuditReadResponse> => {

  return customMutator<organizationAuditReadResponse>(getOrganizationAuditReadUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationAuditReadQueryKey = (id: string,) => {
    return [
    `/api/admin/audit-log/${id}`
    ] as const;
    }


export const getOrganizationAuditReadQueryOptions = <TData = Awaited<ReturnType<typeof organizationAuditRead>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationAuditReadQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationAuditRead>>> = ({ signal }) => organizationAuditRead(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationAuditRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationAuditReadQueryResult = NonNullable<Awaited<ReturnType<typeof organizationAuditRead>>>
export type OrganizationAuditReadQueryError = unknown


export function useOrganizationAuditRead<TData = Awaited<ReturnType<typeof organizationAuditRead>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAuditRead>>,
          TError,
          Awaited<ReturnType<typeof organizationAuditRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAuditRead<TData = Awaited<ReturnType<typeof organizationAuditRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAuditRead>>,
          TError,
          Awaited<ReturnType<typeof organizationAuditRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAuditRead<TData = Awaited<ReturnType<typeof organizationAuditRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationAuditRead<TData = Awaited<ReturnType<typeof organizationAuditRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAuditRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationAuditReadQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationUsersCreatePostResponse200 = {
  data: void
  status: 200
}

export type organizationUsersCreatePostResponse201 = {
  data: OrganizationUsersCreatePost201
  status: 201
}

export type organizationUsersCreatePostResponseSuccess = (organizationUsersCreatePostResponse200 | organizationUsersCreatePostResponse201) & {
  headers: Headers;
};
;

export type organizationUsersCreatePostResponse = (organizationUsersCreatePostResponseSuccess)

export const getOrganizationUsersCreatePostUrl = () => {




  return `/api/admin/users`
}

export const organizationUsersCreatePost = async (createUserDto: CreateUserDto, options?: RequestInit): Promise<organizationUsersCreatePostResponse> => {

  return customMutator<organizationUsersCreatePostResponse>(getOrganizationUsersCreatePostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createUserDto)
  }
);}





export const getOrganizationUsersCreatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersCreatePost>>, TError,{data: CreateUserDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersCreatePost>>, TError,{data: CreateUserDto}, TContext> => {

const mutationKey = ['organizationUsersCreatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersCreatePost>>, {data: CreateUserDto}> = (props) => {
          const {data} = props ?? {};

          return  organizationUsersCreatePost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersCreatePostMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersCreatePost>>>
    export type OrganizationUsersCreatePostMutationBody = CreateUserDto
    export type OrganizationUsersCreatePostMutationError = unknown

    export const useOrganizationUsersCreatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersCreatePost>>, TError,{data: CreateUserDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersCreatePost>>,
        TError,
        {data: CreateUserDto},
        TContext
      > => {
      return useMutation(getOrganizationUsersCreatePostMutationOptions(options), queryClient);
    }

export type organizationUsersReadResponse200 = {
  data: OrganizationUsersRead200
  status: 200
}

export type organizationUsersReadResponseSuccess = (organizationUsersReadResponse200) & {
  headers: Headers;
};
;

export type organizationUsersReadResponse = (organizationUsersReadResponseSuccess)

export const getOrganizationUsersReadUrl = (id: string,) => {




  return `/api/admin/users/${id}`
}

export const organizationUsersRead = async (id: string, options?: RequestInit): Promise<organizationUsersReadResponse> => {

  return customMutator<organizationUsersReadResponse>(getOrganizationUsersReadUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationUsersReadQueryKey = (id: string,) => {
    return [
    `/api/admin/users/${id}`
    ] as const;
    }


export const getOrganizationUsersReadQueryOptions = <TData = Awaited<ReturnType<typeof organizationUsersRead>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationUsersReadQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationUsersRead>>> = ({ signal }) => organizationUsersRead(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationUsersRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationUsersReadQueryResult = NonNullable<Awaited<ReturnType<typeof organizationUsersRead>>>
export type OrganizationUsersReadQueryError = unknown


export function useOrganizationUsersRead<TData = Awaited<ReturnType<typeof organizationUsersRead>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationUsersRead>>,
          TError,
          Awaited<ReturnType<typeof organizationUsersRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationUsersRead<TData = Awaited<ReturnType<typeof organizationUsersRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationUsersRead>>,
          TError,
          Awaited<ReturnType<typeof organizationUsersRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationUsersRead<TData = Awaited<ReturnType<typeof organizationUsersRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationUsersRead<TData = Awaited<ReturnType<typeof organizationUsersRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationUsersReadQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationUsersUpdateResponse200 = {
  data: OrganizationUsersUpdate200
  status: 200
}

export type organizationUsersUpdateResponseSuccess = (organizationUsersUpdateResponse200) & {
  headers: Headers;
};
;

export type organizationUsersUpdateResponse = (organizationUsersUpdateResponseSuccess)

export const getOrganizationUsersUpdateUrl = (id: string,) => {




  return `/api/admin/users/${id}`
}

export const organizationUsersUpdate = async (id: string,
    updateUserDto: UpdateUserDto, options?: RequestInit): Promise<organizationUsersUpdateResponse> => {

  return customMutator<organizationUsersUpdateResponse>(getOrganizationUsersUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateUserDto)
  }
);}





export const getOrganizationUsersUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersUpdate>>, TError,{id: string;data: UpdateUserDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersUpdate>>, TError,{id: string;data: UpdateUserDto}, TContext> => {

const mutationKey = ['organizationUsersUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersUpdate>>, {id: string;data: UpdateUserDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationUsersUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersUpdate>>>
    export type OrganizationUsersUpdateMutationBody = UpdateUserDto
    export type OrganizationUsersUpdateMutationError = unknown

    export const useOrganizationUsersUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersUpdate>>, TError,{id: string;data: UpdateUserDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersUpdate>>,
        TError,
        {id: string;data: UpdateUserDto},
        TContext
      > => {
      return useMutation(getOrganizationUsersUpdateMutationOptions(options), queryClient);
    }

export type organizationUsersDisableResponse200 = {
  data: OrganizationUsersDisable200
  status: 200
}

export type organizationUsersDisableResponseSuccess = (organizationUsersDisableResponse200) & {
  headers: Headers;
};
;

export type organizationUsersDisableResponse = (organizationUsersDisableResponseSuccess)

export const getOrganizationUsersDisableUrl = (id: string,) => {




  return `/api/admin/users/${id}`
}

export const organizationUsersDisable = async (id: string, options?: RequestInit): Promise<organizationUsersDisableResponse> => {

  return customMutator<organizationUsersDisableResponse>(getOrganizationUsersDisableUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getOrganizationUsersDisableMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersDisable>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationUsersDisable'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersDisable>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationUsersDisable(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersDisableMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersDisable>>>

    export type OrganizationUsersDisableMutationError = unknown

    export const useOrganizationUsersDisable = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersDisable>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationUsersDisableMutationOptions(options), queryClient);
    }

export type organizationPermissionsListResponse200 = {
  data: OrganizationPermissionsList200
  status: 200
}

export type organizationPermissionsListResponseSuccess = (organizationPermissionsListResponse200) & {
  headers: Headers;
};
;

export type organizationPermissionsListResponse = (organizationPermissionsListResponseSuccess)

export const getOrganizationPermissionsListUrl = () => {




  return `/api/admin/permissions`
}

export const organizationPermissionsList = async ( options?: RequestInit): Promise<organizationPermissionsListResponse> => {

  return customMutator<organizationPermissionsListResponse>(getOrganizationPermissionsListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationPermissionsListQueryKey = () => {
    return [
    `/api/admin/permissions`
    ] as const;
    }


export const getOrganizationPermissionsListQueryOptions = <TData = Awaited<ReturnType<typeof organizationPermissionsList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPermissionsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationPermissionsListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationPermissionsList>>> = ({ signal }) => organizationPermissionsList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationPermissionsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationPermissionsListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationPermissionsList>>>
export type OrganizationPermissionsListQueryError = unknown


export function useOrganizationPermissionsList<TData = Awaited<ReturnType<typeof organizationPermissionsList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPermissionsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPermissionsList>>,
          TError,
          Awaited<ReturnType<typeof organizationPermissionsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPermissionsList<TData = Awaited<ReturnType<typeof organizationPermissionsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPermissionsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationPermissionsList>>,
          TError,
          Awaited<ReturnType<typeof organizationPermissionsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationPermissionsList<TData = Awaited<ReturnType<typeof organizationPermissionsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPermissionsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationPermissionsList<TData = Awaited<ReturnType<typeof organizationPermissionsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationPermissionsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationPermissionsListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationUsersUnlockResponse200 = {
  data: OrganizationUsersUnlock200
  status: 200
}

export type organizationUsersUnlockResponseSuccess = (organizationUsersUnlockResponse200) & {
  headers: Headers;
};
;

export type organizationUsersUnlockResponse = (organizationUsersUnlockResponseSuccess)

export const getOrganizationUsersUnlockUrl = (id: string,) => {




  return `/api/admin/users/${id}/unlock`
}

export const organizationUsersUnlock = async (id: string, options?: RequestInit): Promise<organizationUsersUnlockResponse> => {

  return customMutator<organizationUsersUnlockResponse>(getOrganizationUsersUnlockUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationUsersUnlockMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersUnlock>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersUnlock>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationUsersUnlock'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersUnlock>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationUsersUnlock(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersUnlockMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersUnlock>>>

    export type OrganizationUsersUnlockMutationError = unknown

    export const useOrganizationUsersUnlock = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersUnlock>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersUnlock>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationUsersUnlockMutationOptions(options), queryClient);
    }

export type organizationUsersForcePasswordResetResponse200 = {
  data: OrganizationUsersForcePasswordReset200
  status: 200
}

export type organizationUsersForcePasswordResetResponseSuccess = (organizationUsersForcePasswordResetResponse200) & {
  headers: Headers;
};
;

export type organizationUsersForcePasswordResetResponse = (organizationUsersForcePasswordResetResponseSuccess)

export const getOrganizationUsersForcePasswordResetUrl = (id: string,) => {




  return `/api/admin/users/${id}/force-password-reset`
}

export const organizationUsersForcePasswordReset = async (id: string, options?: RequestInit): Promise<organizationUsersForcePasswordResetResponse> => {

  return customMutator<organizationUsersForcePasswordResetResponse>(getOrganizationUsersForcePasswordResetUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationUsersForcePasswordResetMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersForcePasswordReset>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersForcePasswordReset>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationUsersForcePasswordReset'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersForcePasswordReset>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationUsersForcePasswordReset(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersForcePasswordResetMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersForcePasswordReset>>>

    export type OrganizationUsersForcePasswordResetMutationError = unknown

    export const useOrganizationUsersForcePasswordReset = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersForcePasswordReset>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersForcePasswordReset>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationUsersForcePasswordResetMutationOptions(options), queryClient);
    }

export type organizationUsersMfaRequireEnrollmentResponse200 = {
  data: OrganizationUsersMfaRequireEnrollment200
  status: 200
}

export type organizationUsersMfaRequireEnrollmentResponseSuccess = (organizationUsersMfaRequireEnrollmentResponse200) & {
  headers: Headers;
};
;

export type organizationUsersMfaRequireEnrollmentResponse = (organizationUsersMfaRequireEnrollmentResponseSuccess)

export const getOrganizationUsersMfaRequireEnrollmentUrl = (id: string,) => {




  return `/api/admin/users/${id}/mfa/require-enrollment`
}

export const organizationUsersMfaRequireEnrollment = async (id: string, options?: RequestInit): Promise<organizationUsersMfaRequireEnrollmentResponse> => {

  return customMutator<organizationUsersMfaRequireEnrollmentResponse>(getOrganizationUsersMfaRequireEnrollmentUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationUsersMfaRequireEnrollmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaRequireEnrollment>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaRequireEnrollment>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationUsersMfaRequireEnrollment'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersMfaRequireEnrollment>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationUsersMfaRequireEnrollment(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersMfaRequireEnrollmentMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersMfaRequireEnrollment>>>

    export type OrganizationUsersMfaRequireEnrollmentMutationError = unknown

    export const useOrganizationUsersMfaRequireEnrollment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaRequireEnrollment>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersMfaRequireEnrollment>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationUsersMfaRequireEnrollmentMutationOptions(options), queryClient);
    }

export type organizationUsersMfaResetEnrollmentResponse200 = {
  data: OrganizationUsersMfaResetEnrollment200
  status: 200
}

export type organizationUsersMfaResetEnrollmentResponseSuccess = (organizationUsersMfaResetEnrollmentResponse200) & {
  headers: Headers;
};
;

export type organizationUsersMfaResetEnrollmentResponse = (organizationUsersMfaResetEnrollmentResponseSuccess)

export const getOrganizationUsersMfaResetEnrollmentUrl = (id: string,) => {




  return `/api/admin/users/${id}/mfa/reset-enrollment`
}

export const organizationUsersMfaResetEnrollment = async (id: string, options?: RequestInit): Promise<organizationUsersMfaResetEnrollmentResponse> => {

  return customMutator<organizationUsersMfaResetEnrollmentResponse>(getOrganizationUsersMfaResetEnrollmentUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationUsersMfaResetEnrollmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaResetEnrollment>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaResetEnrollment>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationUsersMfaResetEnrollment'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersMfaResetEnrollment>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationUsersMfaResetEnrollment(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersMfaResetEnrollmentMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersMfaResetEnrollment>>>

    export type OrganizationUsersMfaResetEnrollmentMutationError = unknown

    export const useOrganizationUsersMfaResetEnrollment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaResetEnrollment>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersMfaResetEnrollment>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationUsersMfaResetEnrollmentMutationOptions(options), queryClient);
    }

export type organizationUsersMfaDisableResponse200 = {
  data: OrganizationUsersMfaDisable200
  status: 200
}

export type organizationUsersMfaDisableResponseSuccess = (organizationUsersMfaDisableResponse200) & {
  headers: Headers;
};
;

export type organizationUsersMfaDisableResponse = (organizationUsersMfaDisableResponseSuccess)

export const getOrganizationUsersMfaDisableUrl = (id: string,) => {




  return `/api/admin/users/${id}/mfa/disable`
}

export const organizationUsersMfaDisable = async (id: string, options?: RequestInit): Promise<organizationUsersMfaDisableResponse> => {

  return customMutator<organizationUsersMfaDisableResponse>(getOrganizationUsersMfaDisableUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationUsersMfaDisableMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaDisable>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationUsersMfaDisable'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersMfaDisable>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationUsersMfaDisable(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersMfaDisableMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersMfaDisable>>>

    export type OrganizationUsersMfaDisableMutationError = unknown

    export const useOrganizationUsersMfaDisable = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersMfaDisable>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationUsersMfaDisableMutationOptions(options), queryClient);
    }

export type organizationUsersMfaRegenerateRecoveryResponse200 = {
  data: OrganizationUsersMfaRegenerateRecovery200
  status: 200
}

export type organizationUsersMfaRegenerateRecoveryResponseSuccess = (organizationUsersMfaRegenerateRecoveryResponse200) & {
  headers: Headers;
};
;

export type organizationUsersMfaRegenerateRecoveryResponse = (organizationUsersMfaRegenerateRecoveryResponseSuccess)

export const getOrganizationUsersMfaRegenerateRecoveryUrl = (id: string,) => {




  return `/api/admin/users/${id}/mfa/regenerate-recovery`
}

export const organizationUsersMfaRegenerateRecovery = async (id: string, options?: RequestInit): Promise<organizationUsersMfaRegenerateRecoveryResponse> => {

  return customMutator<organizationUsersMfaRegenerateRecoveryResponse>(getOrganizationUsersMfaRegenerateRecoveryUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationUsersMfaRegenerateRecoveryMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaRegenerateRecovery>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaRegenerateRecovery>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationUsersMfaRegenerateRecovery'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationUsersMfaRegenerateRecovery>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationUsersMfaRegenerateRecovery(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationUsersMfaRegenerateRecoveryMutationResult = NonNullable<Awaited<ReturnType<typeof organizationUsersMfaRegenerateRecovery>>>

    export type OrganizationUsersMfaRegenerateRecoveryMutationError = unknown

    export const useOrganizationUsersMfaRegenerateRecovery = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationUsersMfaRegenerateRecovery>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationUsersMfaRegenerateRecovery>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationUsersMfaRegenerateRecoveryMutationOptions(options), queryClient);
    }

export type organizationLegalHoldPlaceResponse200 = {
  data: OrganizationLegalHoldPlace200
  status: 200
}

export type organizationLegalHoldPlaceResponseSuccess = (organizationLegalHoldPlaceResponse200) & {
  headers: Headers;
};
;

export type organizationLegalHoldPlaceResponse = (organizationLegalHoldPlaceResponseSuccess)

export const getOrganizationLegalHoldPlaceUrl = () => {




  return `/api/admin/legal-holds`
}

export const organizationLegalHoldPlace = async ( options?: RequestInit): Promise<organizationLegalHoldPlaceResponse> => {

  return customMutator<organizationLegalHoldPlaceResponse>(getOrganizationLegalHoldPlaceUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationLegalHoldPlaceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationLegalHoldPlace>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationLegalHoldPlace>>, TError,void, TContext> => {

const mutationKey = ['organizationLegalHoldPlace'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationLegalHoldPlace>>, void> = () => {


          return  organizationLegalHoldPlace(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationLegalHoldPlaceMutationResult = NonNullable<Awaited<ReturnType<typeof organizationLegalHoldPlace>>>

    export type OrganizationLegalHoldPlaceMutationError = unknown

    export const useOrganizationLegalHoldPlace = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationLegalHoldPlace>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationLegalHoldPlace>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getOrganizationLegalHoldPlaceMutationOptions(options), queryClient);
    }

export type organizationLegalHoldListResponse200 = {
  data: OrganizationLegalHoldList200
  status: 200
}

export type organizationLegalHoldListResponseSuccess = (organizationLegalHoldListResponse200) & {
  headers: Headers;
};
;

export type organizationLegalHoldListResponse = (organizationLegalHoldListResponseSuccess)

export const getOrganizationLegalHoldListUrl = () => {




  return `/api/admin/legal-holds`
}

export const organizationLegalHoldList = async ( options?: RequestInit): Promise<organizationLegalHoldListResponse> => {

  return customMutator<organizationLegalHoldListResponse>(getOrganizationLegalHoldListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationLegalHoldListQueryKey = () => {
    return [
    `/api/admin/legal-holds`
    ] as const;
    }


export const getOrganizationLegalHoldListQueryOptions = <TData = Awaited<ReturnType<typeof organizationLegalHoldList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationLegalHoldList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationLegalHoldListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationLegalHoldList>>> = ({ signal }) => organizationLegalHoldList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationLegalHoldList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationLegalHoldListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationLegalHoldList>>>
export type OrganizationLegalHoldListQueryError = unknown


export function useOrganizationLegalHoldList<TData = Awaited<ReturnType<typeof organizationLegalHoldList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationLegalHoldList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationLegalHoldList>>,
          TError,
          Awaited<ReturnType<typeof organizationLegalHoldList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationLegalHoldList<TData = Awaited<ReturnType<typeof organizationLegalHoldList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationLegalHoldList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationLegalHoldList>>,
          TError,
          Awaited<ReturnType<typeof organizationLegalHoldList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationLegalHoldList<TData = Awaited<ReturnType<typeof organizationLegalHoldList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationLegalHoldList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationLegalHoldList<TData = Awaited<ReturnType<typeof organizationLegalHoldList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationLegalHoldList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationLegalHoldListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationLegalHoldRemoveResponse200 = {
  data: OrganizationLegalHoldRemove200
  status: 200
}

export type organizationLegalHoldRemoveResponseSuccess = (organizationLegalHoldRemoveResponse200) & {
  headers: Headers;
};
;

export type organizationLegalHoldRemoveResponse = (organizationLegalHoldRemoveResponseSuccess)

export const getOrganizationLegalHoldRemoveUrl = (id: string,) => {




  return `/api/admin/legal-holds/${id}`
}

export const organizationLegalHoldRemove = async (id: string, options?: RequestInit): Promise<organizationLegalHoldRemoveResponse> => {

  return customMutator<organizationLegalHoldRemoveResponse>(getOrganizationLegalHoldRemoveUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getOrganizationLegalHoldRemoveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationLegalHoldRemove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationLegalHoldRemove>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationLegalHoldRemove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationLegalHoldRemove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationLegalHoldRemove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationLegalHoldRemoveMutationResult = NonNullable<Awaited<ReturnType<typeof organizationLegalHoldRemove>>>

    export type OrganizationLegalHoldRemoveMutationError = unknown

    export const useOrganizationLegalHoldRemove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationLegalHoldRemove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationLegalHoldRemove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationLegalHoldRemoveMutationOptions(options), queryClient);
    }

export type organizationAdminDelegationCreateResponse200 = {
  data: OrganizationAdminDelegationCreate200
  status: 200
}

export type organizationAdminDelegationCreateResponseSuccess = (organizationAdminDelegationCreateResponse200) & {
  headers: Headers;
};
;

export type organizationAdminDelegationCreateResponse = (organizationAdminDelegationCreateResponseSuccess)

export const getOrganizationAdminDelegationCreateUrl = () => {




  return `/api/admin/admin-delegations`
}

export const organizationAdminDelegationCreate = async ( options?: RequestInit): Promise<organizationAdminDelegationCreateResponse> => {

  return customMutator<organizationAdminDelegationCreateResponse>(getOrganizationAdminDelegationCreateUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getOrganizationAdminDelegationCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationAdminDelegationCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationAdminDelegationCreate>>, TError,void, TContext> => {

const mutationKey = ['organizationAdminDelegationCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationAdminDelegationCreate>>, void> = () => {


          return  organizationAdminDelegationCreate(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationAdminDelegationCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationAdminDelegationCreate>>>

    export type OrganizationAdminDelegationCreateMutationError = unknown

    export const useOrganizationAdminDelegationCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationAdminDelegationCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationAdminDelegationCreate>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getOrganizationAdminDelegationCreateMutationOptions(options), queryClient);
    }

export type organizationAdminDelegationListResponse200 = {
  data: OrganizationAdminDelegationList200
  status: 200
}

export type organizationAdminDelegationListResponseSuccess = (organizationAdminDelegationListResponse200) & {
  headers: Headers;
};
;

export type organizationAdminDelegationListResponse = (organizationAdminDelegationListResponseSuccess)

export const getOrganizationAdminDelegationListUrl = () => {




  return `/api/admin/admin-delegations`
}

export const organizationAdminDelegationList = async ( options?: RequestInit): Promise<organizationAdminDelegationListResponse> => {

  return customMutator<organizationAdminDelegationListResponse>(getOrganizationAdminDelegationListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationAdminDelegationListQueryKey = () => {
    return [
    `/api/admin/admin-delegations`
    ] as const;
    }


export const getOrganizationAdminDelegationListQueryOptions = <TData = Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationAdminDelegationListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationAdminDelegationList>>> = ({ signal }) => organizationAdminDelegationList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationAdminDelegationListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationAdminDelegationList>>>
export type OrganizationAdminDelegationListQueryError = unknown


export function useOrganizationAdminDelegationList<TData = Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAdminDelegationList>>,
          TError,
          Awaited<ReturnType<typeof organizationAdminDelegationList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAdminDelegationList<TData = Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAdminDelegationList>>,
          TError,
          Awaited<ReturnType<typeof organizationAdminDelegationList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAdminDelegationList<TData = Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationAdminDelegationList<TData = Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAdminDelegationList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationAdminDelegationListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationAdminDelegationRevokeResponse200 = {
  data: OrganizationAdminDelegationRevoke200
  status: 200
}

export type organizationAdminDelegationRevokeResponseSuccess = (organizationAdminDelegationRevokeResponse200) & {
  headers: Headers;
};
;

export type organizationAdminDelegationRevokeResponse = (organizationAdminDelegationRevokeResponseSuccess)

export const getOrganizationAdminDelegationRevokeUrl = (id: string,) => {




  return `/api/admin/admin-delegations/${id}`
}

export const organizationAdminDelegationRevoke = async (id: string, options?: RequestInit): Promise<organizationAdminDelegationRevokeResponse> => {

  return customMutator<organizationAdminDelegationRevokeResponse>(getOrganizationAdminDelegationRevokeUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getOrganizationAdminDelegationRevokeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationAdminDelegationRevoke>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationAdminDelegationRevoke>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationAdminDelegationRevoke'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationAdminDelegationRevoke>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationAdminDelegationRevoke(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationAdminDelegationRevokeMutationResult = NonNullable<Awaited<ReturnType<typeof organizationAdminDelegationRevoke>>>

    export type OrganizationAdminDelegationRevokeMutationError = unknown

    export const useOrganizationAdminDelegationRevoke = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationAdminDelegationRevoke>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationAdminDelegationRevoke>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationAdminDelegationRevokeMutationOptions(options), queryClient);
    }

export type organizationProfileReadResponse200 = {
  data: OrganizationProfileRead200
  status: 200
}

export type organizationProfileReadResponseSuccess = (organizationProfileReadResponse200) & {
  headers: Headers;
};
;

export type organizationProfileReadResponse = (organizationProfileReadResponseSuccess)

export const getOrganizationProfileReadUrl = () => {




  return `/api/admin/organization/profile`
}

export const organizationProfileRead = async ( options?: RequestInit): Promise<organizationProfileReadResponse> => {

  return customMutator<organizationProfileReadResponse>(getOrganizationProfileReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationProfileReadQueryKey = () => {
    return [
    `/api/admin/organization/profile`
    ] as const;
    }


export const getOrganizationProfileReadQueryOptions = <TData = Awaited<ReturnType<typeof organizationProfileRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationProfileRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationProfileReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationProfileRead>>> = ({ signal }) => organizationProfileRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationProfileRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationProfileReadQueryResult = NonNullable<Awaited<ReturnType<typeof organizationProfileRead>>>
export type OrganizationProfileReadQueryError = unknown


export function useOrganizationProfileRead<TData = Awaited<ReturnType<typeof organizationProfileRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationProfileRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationProfileRead>>,
          TError,
          Awaited<ReturnType<typeof organizationProfileRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationProfileRead<TData = Awaited<ReturnType<typeof organizationProfileRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationProfileRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationProfileRead>>,
          TError,
          Awaited<ReturnType<typeof organizationProfileRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationProfileRead<TData = Awaited<ReturnType<typeof organizationProfileRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationProfileRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationProfileRead<TData = Awaited<ReturnType<typeof organizationProfileRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationProfileRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationProfileReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationProfileUpdateResponse200 = {
  data: OrganizationProfileUpdate200
  status: 200
}

export type organizationProfileUpdateResponseSuccess = (organizationProfileUpdateResponse200) & {
  headers: Headers;
};
;

export type organizationProfileUpdateResponse = (organizationProfileUpdateResponseSuccess)

export const getOrganizationProfileUpdateUrl = () => {




  return `/api/admin/organization/profile`
}

export const organizationProfileUpdate = async (updateOrganizationProfileDto: UpdateOrganizationProfileDto, options?: RequestInit): Promise<organizationProfileUpdateResponse> => {

  return customMutator<organizationProfileUpdateResponse>(getOrganizationProfileUpdateUrl(),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateOrganizationProfileDto)
  }
);}





export const getOrganizationProfileUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationProfileUpdate>>, TError,{data: UpdateOrganizationProfileDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationProfileUpdate>>, TError,{data: UpdateOrganizationProfileDto}, TContext> => {

const mutationKey = ['organizationProfileUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationProfileUpdate>>, {data: UpdateOrganizationProfileDto}> = (props) => {
          const {data} = props ?? {};

          return  organizationProfileUpdate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationProfileUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationProfileUpdate>>>
    export type OrganizationProfileUpdateMutationBody = UpdateOrganizationProfileDto
    export type OrganizationProfileUpdateMutationError = unknown

    export const useOrganizationProfileUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationProfileUpdate>>, TError,{data: UpdateOrganizationProfileDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationProfileUpdate>>,
        TError,
        {data: UpdateOrganizationProfileDto},
        TContext
      > => {
      return useMutation(getOrganizationProfileUpdateMutationOptions(options), queryClient);
    }

export type organizationUsersListResponse200 = {
  data: OrganizationUsersList200
  status: 200
}

export type organizationUsersListResponseSuccess = (organizationUsersListResponse200) & {
  headers: Headers;
};
;

export type organizationUsersListResponse = (organizationUsersListResponseSuccess)

export const getOrganizationUsersListUrl = (params?: OrganizationUsersListParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/users/surface?${stringifiedParams}` : `/api/users/surface`
}

export const organizationUsersList = async (params?: OrganizationUsersListParams, options?: RequestInit): Promise<organizationUsersListResponse> => {

  return customMutator<organizationUsersListResponse>(getOrganizationUsersListUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationUsersListQueryKey = (params?: OrganizationUsersListParams,) => {
    return [
    `/api/users/surface`, ...(params ? [params] : [])
    ] as const;
    }


export const getOrganizationUsersListQueryOptions = <TData = Awaited<ReturnType<typeof organizationUsersList>>, TError = unknown>(params?: OrganizationUsersListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationUsersListQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationUsersList>>> = ({ signal }) => organizationUsersList(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationUsersList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationUsersListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationUsersList>>>
export type OrganizationUsersListQueryError = unknown


export function useOrganizationUsersList<TData = Awaited<ReturnType<typeof organizationUsersList>>, TError = unknown>(
 params: undefined |  OrganizationUsersListParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationUsersList>>,
          TError,
          Awaited<ReturnType<typeof organizationUsersList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationUsersList<TData = Awaited<ReturnType<typeof organizationUsersList>>, TError = unknown>(
 params?: OrganizationUsersListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationUsersList>>,
          TError,
          Awaited<ReturnType<typeof organizationUsersList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationUsersList<TData = Awaited<ReturnType<typeof organizationUsersList>>, TError = unknown>(
 params?: OrganizationUsersListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationUsersList<TData = Awaited<ReturnType<typeof organizationUsersList>>, TError = unknown>(
 params?: OrganizationUsersListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationUsersList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationUsersListQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationRolesListResponse200 = {
  data: OrganizationRolesList200
  status: 200
}

export type organizationRolesListResponseSuccess = (organizationRolesListResponse200) & {
  headers: Headers;
};
;

export type organizationRolesListResponse = (organizationRolesListResponseSuccess)

export const getOrganizationRolesListUrl = (params?: OrganizationRolesListParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/roles/surface?${stringifiedParams}` : `/api/roles/surface`
}

export const organizationRolesList = async (params?: OrganizationRolesListParams, options?: RequestInit): Promise<organizationRolesListResponse> => {

  return customMutator<organizationRolesListResponse>(getOrganizationRolesListUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationRolesListQueryKey = (params?: OrganizationRolesListParams,) => {
    return [
    `/api/roles/surface`, ...(params ? [params] : [])
    ] as const;
    }


export const getOrganizationRolesListQueryOptions = <TData = Awaited<ReturnType<typeof organizationRolesList>>, TError = unknown>(params?: OrganizationRolesListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationRolesListQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationRolesList>>> = ({ signal }) => organizationRolesList(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationRolesList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationRolesListQueryResult = NonNullable<Awaited<ReturnType<typeof organizationRolesList>>>
export type OrganizationRolesListQueryError = unknown


export function useOrganizationRolesList<TData = Awaited<ReturnType<typeof organizationRolesList>>, TError = unknown>(
 params: undefined |  OrganizationRolesListParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationRolesList>>,
          TError,
          Awaited<ReturnType<typeof organizationRolesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationRolesList<TData = Awaited<ReturnType<typeof organizationRolesList>>, TError = unknown>(
 params?: OrganizationRolesListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationRolesList>>,
          TError,
          Awaited<ReturnType<typeof organizationRolesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationRolesList<TData = Awaited<ReturnType<typeof organizationRolesList>>, TError = unknown>(
 params?: OrganizationRolesListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationRolesList<TData = Awaited<ReturnType<typeof organizationRolesList>>, TError = unknown>(
 params?: OrganizationRolesListParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationRolesListQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationRolesReadResponse200 = {
  data: OrganizationRolesRead200
  status: 200
}

export type organizationRolesReadResponseSuccess = (organizationRolesReadResponse200) & {
  headers: Headers;
};
;

export type organizationRolesReadResponse = (organizationRolesReadResponseSuccess)

export const getOrganizationRolesReadUrl = (id: string,) => {




  return `/api/roles/${id}`
}

export const organizationRolesRead = async (id: string, options?: RequestInit): Promise<organizationRolesReadResponse> => {

  return customMutator<organizationRolesReadResponse>(getOrganizationRolesReadUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationRolesReadQueryKey = (id: string,) => {
    return [
    `/api/roles/${id}`
    ] as const;
    }


export const getOrganizationRolesReadQueryOptions = <TData = Awaited<ReturnType<typeof organizationRolesRead>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationRolesReadQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationRolesRead>>> = ({ signal }) => organizationRolesRead(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationRolesRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationRolesReadQueryResult = NonNullable<Awaited<ReturnType<typeof organizationRolesRead>>>
export type OrganizationRolesReadQueryError = unknown


export function useOrganizationRolesRead<TData = Awaited<ReturnType<typeof organizationRolesRead>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationRolesRead>>,
          TError,
          Awaited<ReturnType<typeof organizationRolesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationRolesRead<TData = Awaited<ReturnType<typeof organizationRolesRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationRolesRead>>,
          TError,
          Awaited<ReturnType<typeof organizationRolesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationRolesRead<TData = Awaited<ReturnType<typeof organizationRolesRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationRolesRead<TData = Awaited<ReturnType<typeof organizationRolesRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationRolesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationRolesReadQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationRolesUpdateResponse200 = {
  data: OrganizationRolesUpdate200
  status: 200
}

export type organizationRolesUpdateResponseSuccess = (organizationRolesUpdateResponse200) & {
  headers: Headers;
};
;

export type organizationRolesUpdateResponse = (organizationRolesUpdateResponseSuccess)

export const getOrganizationRolesUpdateUrl = (id: string,) => {




  return `/api/roles/${id}`
}

export const organizationRolesUpdate = async (id: string,
    updateRoleDto: UpdateRoleDto, options?: RequestInit): Promise<organizationRolesUpdateResponse> => {

  return customMutator<organizationRolesUpdateResponse>(getOrganizationRolesUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateRoleDto)
  }
);}





export const getOrganizationRolesUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationRolesUpdate>>, TError,{id: string;data: UpdateRoleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationRolesUpdate>>, TError,{id: string;data: UpdateRoleDto}, TContext> => {

const mutationKey = ['organizationRolesUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationRolesUpdate>>, {id: string;data: UpdateRoleDto}> = (props) => {
          const {id,data} = props ?? {};

          return  organizationRolesUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationRolesUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationRolesUpdate>>>
    export type OrganizationRolesUpdateMutationBody = UpdateRoleDto
    export type OrganizationRolesUpdateMutationError = unknown

    export const useOrganizationRolesUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationRolesUpdate>>, TError,{id: string;data: UpdateRoleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationRolesUpdate>>,
        TError,
        {id: string;data: UpdateRoleDto},
        TContext
      > => {
      return useMutation(getOrganizationRolesUpdateMutationOptions(options), queryClient);
    }

export type organizationRolesDisableResponse200 = {
  data: OrganizationRolesDisable200
  status: 200
}

export type organizationRolesDisableResponseSuccess = (organizationRolesDisableResponse200) & {
  headers: Headers;
};
;

export type organizationRolesDisableResponse = (organizationRolesDisableResponseSuccess)

export const getOrganizationRolesDisableUrl = (id: string,) => {




  return `/api/roles/${id}`
}

export const organizationRolesDisable = async (id: string, options?: RequestInit): Promise<organizationRolesDisableResponse> => {

  return customMutator<organizationRolesDisableResponse>(getOrganizationRolesDisableUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getOrganizationRolesDisableMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationRolesDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationRolesDisable>>, TError,{id: string}, TContext> => {

const mutationKey = ['organizationRolesDisable'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationRolesDisable>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  organizationRolesDisable(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationRolesDisableMutationResult = NonNullable<Awaited<ReturnType<typeof organizationRolesDisable>>>

    export type OrganizationRolesDisableMutationError = unknown

    export const useOrganizationRolesDisable = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationRolesDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationRolesDisable>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getOrganizationRolesDisableMutationOptions(options), queryClient);
    }

export type organizationRolesCreateResponse200 = {
  data: OrganizationRolesCreate200
  status: 200
}

export type organizationRolesCreateResponseSuccess = (organizationRolesCreateResponse200) & {
  headers: Headers;
};
;

export type organizationRolesCreateResponse = (organizationRolesCreateResponseSuccess)

export const getOrganizationRolesCreateUrl = () => {




  return `/api/roles`
}

export const organizationRolesCreate = async (createRoleDto: CreateRoleDto, options?: RequestInit): Promise<organizationRolesCreateResponse> => {

  return customMutator<organizationRolesCreateResponse>(getOrganizationRolesCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createRoleDto)
  }
);}





export const getOrganizationRolesCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationRolesCreate>>, TError,{data: CreateRoleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof organizationRolesCreate>>, TError,{data: CreateRoleDto}, TContext> => {

const mutationKey = ['organizationRolesCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof organizationRolesCreate>>, {data: CreateRoleDto}> = (props) => {
          const {data} = props ?? {};

          return  organizationRolesCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OrganizationRolesCreateMutationResult = NonNullable<Awaited<ReturnType<typeof organizationRolesCreate>>>
    export type OrganizationRolesCreateMutationBody = CreateRoleDto
    export type OrganizationRolesCreateMutationError = unknown

    export const useOrganizationRolesCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof organizationRolesCreate>>, TError,{data: CreateRoleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof organizationRolesCreate>>,
        TError,
        {data: CreateRoleDto},
        TContext
      > => {
      return useMutation(getOrganizationRolesCreateMutationOptions(options), queryClient);
    }

export type platformAuditExportResponse200 = {
  data: PlatformAuditExport200
  status: 200
}

export type platformAuditExportResponse401 = {
  data: void
  status: 401
}

export type platformAuditExportResponse403 = {
  data: void
  status: 403
}

export type platformAuditExportResponseSuccess = (platformAuditExportResponse200) & {
  headers: Headers;
};
export type platformAuditExportResponseError = (platformAuditExportResponse401 | platformAuditExportResponse403) & {
  headers: Headers;
};

export type platformAuditExportResponse = (platformAuditExportResponseSuccess | platformAuditExportResponseError)

export const getPlatformAuditExportUrl = (params?: PlatformAuditExportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/audit/export?${stringifiedParams}` : `/api/platform/audit/export`
}

export const platformAuditExport = async (params?: PlatformAuditExportParams, options?: RequestInit): Promise<platformAuditExportResponse> => {

  return customMutator<platformAuditExportResponse>(getPlatformAuditExportUrl(params),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformAuditExportMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformAuditExport>>, TError,{params?: PlatformAuditExportParams}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformAuditExport>>, TError,{params?: PlatformAuditExportParams}, TContext> => {

const mutationKey = ['platformAuditExport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformAuditExport>>, {params?: PlatformAuditExportParams}> = (props) => {
          const {params} = props ?? {};

          return  platformAuditExport(params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformAuditExportMutationResult = NonNullable<Awaited<ReturnType<typeof platformAuditExport>>>

    export type PlatformAuditExportMutationError = void

    export const usePlatformAuditExport = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformAuditExport>>, TError,{params?: PlatformAuditExportParams}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformAuditExport>>,
        TError,
        {params?: PlatformAuditExportParams},
        TContext
      > => {
      return useMutation(getPlatformAuditExportMutationOptions(options), queryClient);
    }

export type platformOrganizationsOffboardingExportResponse201 = {
  data: void
  status: 201
}

export type platformOrganizationsOffboardingExportResponseSuccess = (platformOrganizationsOffboardingExportResponse201) & {
  headers: Headers;
};
;

export type platformOrganizationsOffboardingExportResponse = (platformOrganizationsOffboardingExportResponseSuccess)

export const getPlatformOrganizationsOffboardingExportUrl = (targetOrganizationId: string,) => {




  return `/api/platform/organizations/${targetOrganizationId}/offboarding-export`
}

export const platformOrganizationsOffboardingExport = async (targetOrganizationId: string, options?: RequestInit): Promise<platformOrganizationsOffboardingExportResponse> => {

  return customMutator<platformOrganizationsOffboardingExportResponse>(getPlatformOrganizationsOffboardingExportUrl(targetOrganizationId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationsOffboardingExportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationsOffboardingExport>>, TError,{targetOrganizationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationsOffboardingExport>>, TError,{targetOrganizationId: string}, TContext> => {

const mutationKey = ['platformOrganizationsOffboardingExport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationsOffboardingExport>>, {targetOrganizationId: string}> = (props) => {
          const {targetOrganizationId} = props ?? {};

          return  platformOrganizationsOffboardingExport(targetOrganizationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationsOffboardingExportMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationsOffboardingExport>>>

    export type PlatformOrganizationsOffboardingExportMutationError = unknown

    export const usePlatformOrganizationsOffboardingExport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationsOffboardingExport>>, TError,{targetOrganizationId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationsOffboardingExport>>,
        TError,
        {targetOrganizationId: string},
        TContext
      > => {
      return useMutation(getPlatformOrganizationsOffboardingExportMutationOptions(options), queryClient);
    }

export type platformUsersCreateResponse201 = {
  data: PlatformUsersCreate201
  status: 201
}

export type platformUsersCreateResponseSuccess = (platformUsersCreateResponse201) & {
  headers: Headers;
};
;

export type platformUsersCreateResponse = (platformUsersCreateResponseSuccess)

export const getPlatformUsersCreateUrl = () => {




  return `/api/platform/users/invitations`
}

export const platformUsersCreate = async (invitePlatformUserDto: InvitePlatformUserDto, options?: RequestInit): Promise<platformUsersCreateResponse> => {

  return customMutator<platformUsersCreateResponse>(getPlatformUsersCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(invitePlatformUserDto)
  }
);}





export const getPlatformUsersCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformUsersCreate>>, TError,{data: InvitePlatformUserDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformUsersCreate>>, TError,{data: InvitePlatformUserDto}, TContext> => {

const mutationKey = ['platformUsersCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformUsersCreate>>, {data: InvitePlatformUserDto}> = (props) => {
          const {data} = props ?? {};

          return  platformUsersCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformUsersCreateMutationResult = NonNullable<Awaited<ReturnType<typeof platformUsersCreate>>>
    export type PlatformUsersCreateMutationBody = InvitePlatformUserDto
    export type PlatformUsersCreateMutationError = unknown

    export const usePlatformUsersCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformUsersCreate>>, TError,{data: InvitePlatformUserDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformUsersCreate>>,
        TError,
        {data: InvitePlatformUserDto},
        TContext
      > => {
      return useMutation(getPlatformUsersCreateMutationOptions(options), queryClient);
    }

export type platformDataResidencyReadResponse200 = {
  data: void
  status: 200
}

export type platformDataResidencyReadResponseSuccess = (platformDataResidencyReadResponse200) & {
  headers: Headers;
};
;

export type platformDataResidencyReadResponse = (platformDataResidencyReadResponseSuccess)

export const getPlatformDataResidencyReadUrl = () => {




  return `/api/platform/data-residency/policy`
}

export const platformDataResidencyRead = async ( options?: RequestInit): Promise<platformDataResidencyReadResponse> => {

  return customMutator<platformDataResidencyReadResponse>(getPlatformDataResidencyReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformDataResidencyReadQueryKey = () => {
    return [
    `/api/platform/data-residency/policy`
    ] as const;
    }


export const getPlatformDataResidencyReadQueryOptions = <TData = Awaited<ReturnType<typeof platformDataResidencyRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDataResidencyRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformDataResidencyReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformDataResidencyRead>>> = ({ signal }) => platformDataResidencyRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformDataResidencyRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformDataResidencyReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformDataResidencyRead>>>
export type PlatformDataResidencyReadQueryError = unknown


export function usePlatformDataResidencyRead<TData = Awaited<ReturnType<typeof platformDataResidencyRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDataResidencyRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformDataResidencyRead>>,
          TError,
          Awaited<ReturnType<typeof platformDataResidencyRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformDataResidencyRead<TData = Awaited<ReturnType<typeof platformDataResidencyRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDataResidencyRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformDataResidencyRead>>,
          TError,
          Awaited<ReturnType<typeof platformDataResidencyRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformDataResidencyRead<TData = Awaited<ReturnType<typeof platformDataResidencyRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDataResidencyRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformDataResidencyRead<TData = Awaited<ReturnType<typeof platformDataResidencyRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDataResidencyRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformDataResidencyReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformDataResidencyPolicyUpdateResponse200 = {
  data: void
  status: 200
}

export type platformDataResidencyPolicyUpdateResponseSuccess = (platformDataResidencyPolicyUpdateResponse200) & {
  headers: Headers;
};
;

export type platformDataResidencyPolicyUpdateResponse = (platformDataResidencyPolicyUpdateResponseSuccess)

export const getPlatformDataResidencyPolicyUpdateUrl = () => {




  return `/api/platform/data-residency/policy`
}

export const platformDataResidencyPolicyUpdate = async ( options?: RequestInit): Promise<platformDataResidencyPolicyUpdateResponse> => {

  return customMutator<platformDataResidencyPolicyUpdateResponse>(getPlatformDataResidencyPolicyUpdateUrl(),
  {
    ...options,
    method: 'PUT'


  }
);}





export const getPlatformDataResidencyPolicyUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformDataResidencyPolicyUpdate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformDataResidencyPolicyUpdate>>, TError,void, TContext> => {

const mutationKey = ['platformDataResidencyPolicyUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformDataResidencyPolicyUpdate>>, void> = () => {


          return  platformDataResidencyPolicyUpdate(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformDataResidencyPolicyUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof platformDataResidencyPolicyUpdate>>>

    export type PlatformDataResidencyPolicyUpdateMutationError = unknown

    export const usePlatformDataResidencyPolicyUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformDataResidencyPolicyUpdate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformDataResidencyPolicyUpdate>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformDataResidencyPolicyUpdateMutationOptions(options), queryClient);
    }

export type platformDataResidencyExceptionApproveResponse201 = {
  data: void
  status: 201
}

export type platformDataResidencyExceptionApproveResponseSuccess = (platformDataResidencyExceptionApproveResponse201) & {
  headers: Headers;
};
;

export type platformDataResidencyExceptionApproveResponse = (platformDataResidencyExceptionApproveResponseSuccess)

export const getPlatformDataResidencyExceptionApproveUrl = () => {




  return `/api/platform/data-residency/exceptions`
}

export const platformDataResidencyExceptionApprove = async ( options?: RequestInit): Promise<platformDataResidencyExceptionApproveResponse> => {

  return customMutator<platformDataResidencyExceptionApproveResponse>(getPlatformDataResidencyExceptionApproveUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformDataResidencyExceptionApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformDataResidencyExceptionApprove>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformDataResidencyExceptionApprove>>, TError,void, TContext> => {

const mutationKey = ['platformDataResidencyExceptionApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformDataResidencyExceptionApprove>>, void> = () => {


          return  platformDataResidencyExceptionApprove(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformDataResidencyExceptionApproveMutationResult = NonNullable<Awaited<ReturnType<typeof platformDataResidencyExceptionApprove>>>

    export type PlatformDataResidencyExceptionApproveMutationError = unknown

    export const usePlatformDataResidencyExceptionApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformDataResidencyExceptionApprove>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformDataResidencyExceptionApprove>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformDataResidencyExceptionApproveMutationOptions(options), queryClient);
    }

export type platformDataResidencyTransferEvaluateResponse201 = {
  data: void
  status: 201
}

export type platformDataResidencyTransferEvaluateResponseSuccess = (platformDataResidencyTransferEvaluateResponse201) & {
  headers: Headers;
};
;

export type platformDataResidencyTransferEvaluateResponse = (platformDataResidencyTransferEvaluateResponseSuccess)

export const getPlatformDataResidencyTransferEvaluateUrl = () => {




  return `/api/platform/data-residency/transfers/evaluate`
}

export const platformDataResidencyTransferEvaluate = async ( options?: RequestInit): Promise<platformDataResidencyTransferEvaluateResponse> => {

  return customMutator<platformDataResidencyTransferEvaluateResponse>(getPlatformDataResidencyTransferEvaluateUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformDataResidencyTransferEvaluateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformDataResidencyTransferEvaluate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformDataResidencyTransferEvaluate>>, TError,void, TContext> => {

const mutationKey = ['platformDataResidencyTransferEvaluate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformDataResidencyTransferEvaluate>>, void> = () => {


          return  platformDataResidencyTransferEvaluate(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformDataResidencyTransferEvaluateMutationResult = NonNullable<Awaited<ReturnType<typeof platformDataResidencyTransferEvaluate>>>

    export type PlatformDataResidencyTransferEvaluateMutationError = unknown

    export const usePlatformDataResidencyTransferEvaluate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformDataResidencyTransferEvaluate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformDataResidencyTransferEvaluate>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformDataResidencyTransferEvaluateMutationOptions(options), queryClient);
    }

export type platformReportsSnapshotCreateResponse201 = {
  data: void
  status: 201
}

export type platformReportsSnapshotCreateResponseSuccess = (platformReportsSnapshotCreateResponse201) & {
  headers: Headers;
};
;

export type platformReportsSnapshotCreateResponse = (platformReportsSnapshotCreateResponseSuccess)

export const getPlatformReportsSnapshotCreateUrl = () => {




  return `/api/platform/reports/reproducibility/snapshots`
}

export const platformReportsSnapshotCreate = async ( options?: RequestInit): Promise<platformReportsSnapshotCreateResponse> => {

  return customMutator<platformReportsSnapshotCreateResponse>(getPlatformReportsSnapshotCreateUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformReportsSnapshotCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformReportsSnapshotCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformReportsSnapshotCreate>>, TError,void, TContext> => {

const mutationKey = ['platformReportsSnapshotCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformReportsSnapshotCreate>>, void> = () => {


          return  platformReportsSnapshotCreate(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformReportsSnapshotCreateMutationResult = NonNullable<Awaited<ReturnType<typeof platformReportsSnapshotCreate>>>

    export type PlatformReportsSnapshotCreateMutationError = unknown

    export const usePlatformReportsSnapshotCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformReportsSnapshotCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformReportsSnapshotCreate>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformReportsSnapshotCreateMutationOptions(options), queryClient);
    }

export type platformReportsReadResponse200 = {
  data: void
  status: 200
}

export type platformReportsReadResponseSuccess = (platformReportsReadResponse200) & {
  headers: Headers;
};
;

export type platformReportsReadResponse = (platformReportsReadResponseSuccess)

export const getPlatformReportsReadUrl = (id: string,) => {




  return `/api/platform/reports/reproducibility/runs/${id}/reproduce`
}

export const platformReportsRead = async (id: string, options?: RequestInit): Promise<platformReportsReadResponse> => {

  return customMutator<platformReportsReadResponse>(getPlatformReportsReadUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformReportsReadQueryKey = (id: string,) => {
    return [
    `/api/platform/reports/reproducibility/runs/${id}/reproduce`
    ] as const;
    }


export const getPlatformReportsReadQueryOptions = <TData = Awaited<ReturnType<typeof platformReportsRead>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReportsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformReportsReadQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformReportsRead>>> = ({ signal }) => platformReportsRead(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformReportsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformReportsReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformReportsRead>>>
export type PlatformReportsReadQueryError = unknown


export function usePlatformReportsRead<TData = Awaited<ReturnType<typeof platformReportsRead>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReportsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformReportsRead>>,
          TError,
          Awaited<ReturnType<typeof platformReportsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformReportsRead<TData = Awaited<ReturnType<typeof platformReportsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReportsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformReportsRead>>,
          TError,
          Awaited<ReturnType<typeof platformReportsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformReportsRead<TData = Awaited<ReturnType<typeof platformReportsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReportsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformReportsRead<TData = Awaited<ReturnType<typeof platformReportsRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReportsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformReportsReadQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformReportsRestatementCreateResponse201 = {
  data: void
  status: 201
}

export type platformReportsRestatementCreateResponseSuccess = (platformReportsRestatementCreateResponse201) & {
  headers: Headers;
};
;

export type platformReportsRestatementCreateResponse = (platformReportsRestatementCreateResponseSuccess)

export const getPlatformReportsRestatementCreateUrl = () => {




  return `/api/platform/reports/reproducibility/restatements`
}

export const platformReportsRestatementCreate = async ( options?: RequestInit): Promise<platformReportsRestatementCreateResponse> => {

  return customMutator<platformReportsRestatementCreateResponse>(getPlatformReportsRestatementCreateUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformReportsRestatementCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformReportsRestatementCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformReportsRestatementCreate>>, TError,void, TContext> => {

const mutationKey = ['platformReportsRestatementCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformReportsRestatementCreate>>, void> = () => {


          return  platformReportsRestatementCreate(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformReportsRestatementCreateMutationResult = NonNullable<Awaited<ReturnType<typeof platformReportsRestatementCreate>>>

    export type PlatformReportsRestatementCreateMutationError = unknown

    export const usePlatformReportsRestatementCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformReportsRestatementCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformReportsRestatementCreate>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformReportsRestatementCreateMutationOptions(options), queryClient);
    }

export type platformConfigurationRolloutManageResponse201 = {
  data: void
  status: 201
}

export type platformConfigurationRolloutManageResponseSuccess = (platformConfigurationRolloutManageResponse201) & {
  headers: Headers;
};
;

export type platformConfigurationRolloutManageResponse = (platformConfigurationRolloutManageResponseSuccess)

export const getPlatformConfigurationRolloutManageUrl = () => {




  return `/api/platform/configuration-rollouts/preview`
}

export const platformConfigurationRolloutManage = async ( options?: RequestInit): Promise<platformConfigurationRolloutManageResponse> => {

  return customMutator<platformConfigurationRolloutManageResponse>(getPlatformConfigurationRolloutManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformConfigurationRolloutManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformConfigurationRolloutManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformConfigurationRolloutManage>>, TError,void, TContext> => {

const mutationKey = ['platformConfigurationRolloutManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformConfigurationRolloutManage>>, void> = () => {


          return  platformConfigurationRolloutManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformConfigurationRolloutManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformConfigurationRolloutManage>>>

    export type PlatformConfigurationRolloutManageMutationError = unknown

    export const usePlatformConfigurationRolloutManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformConfigurationRolloutManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformConfigurationRolloutManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformConfigurationRolloutManageMutationOptions(options), queryClient);
    }

export type platformConfigurationRolloutManagePostResponse201 = {
  data: void
  status: 201
}

export type platformConfigurationRolloutManagePostResponseSuccess = (platformConfigurationRolloutManagePostResponse201) & {
  headers: Headers;
};
;

export type platformConfigurationRolloutManagePostResponse = (platformConfigurationRolloutManagePostResponseSuccess)

export const getPlatformConfigurationRolloutManagePostUrl = () => {




  return `/api/platform/configuration-rollouts`
}

export const platformConfigurationRolloutManagePost = async ( options?: RequestInit): Promise<platformConfigurationRolloutManagePostResponse> => {

  return customMutator<platformConfigurationRolloutManagePostResponse>(getPlatformConfigurationRolloutManagePostUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformConfigurationRolloutManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformConfigurationRolloutManagePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformConfigurationRolloutManagePost>>, TError,void, TContext> => {

const mutationKey = ['platformConfigurationRolloutManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformConfigurationRolloutManagePost>>, void> = () => {


          return  platformConfigurationRolloutManagePost(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformConfigurationRolloutManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformConfigurationRolloutManagePost>>>

    export type PlatformConfigurationRolloutManagePostMutationError = unknown

    export const usePlatformConfigurationRolloutManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformConfigurationRolloutManagePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformConfigurationRolloutManagePost>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformConfigurationRolloutManagePostMutationOptions(options), queryClient);
    }

export type platformJurisdictionPackagesReadResponse200 = {
  data: void
  status: 200
}

export type platformJurisdictionPackagesReadResponseSuccess = (platformJurisdictionPackagesReadResponse200) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesReadResponse = (platformJurisdictionPackagesReadResponseSuccess)

export const getPlatformJurisdictionPackagesReadUrl = () => {




  return `/api/platform/jurisdiction-packages`
}

export const platformJurisdictionPackagesRead = async ( options?: RequestInit): Promise<platformJurisdictionPackagesReadResponse> => {

  return customMutator<platformJurisdictionPackagesReadResponse>(getPlatformJurisdictionPackagesReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformJurisdictionPackagesReadQueryKey = () => {
    return [
    `/api/platform/jurisdiction-packages`
    ] as const;
    }


export const getPlatformJurisdictionPackagesReadQueryOptions = <TData = Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformJurisdictionPackagesReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>> = ({ signal }) => platformJurisdictionPackagesRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformJurisdictionPackagesReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>>
export type PlatformJurisdictionPackagesReadQueryError = unknown


export function usePlatformJurisdictionPackagesRead<TData = Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>,
          TError,
          Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformJurisdictionPackagesRead<TData = Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>,
          TError,
          Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformJurisdictionPackagesRead<TData = Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformJurisdictionPackagesRead<TData = Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformJurisdictionPackagesReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformJurisdictionPackagesManageResponse201 = {
  data: void
  status: 201
}

export type platformJurisdictionPackagesManageResponseSuccess = (platformJurisdictionPackagesManageResponse201) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesManageResponse = (platformJurisdictionPackagesManageResponseSuccess)

export const getPlatformJurisdictionPackagesManageUrl = () => {




  return `/api/platform/jurisdiction-packages`
}

export const platformJurisdictionPackagesManage = async ( options?: RequestInit): Promise<platformJurisdictionPackagesManageResponse> => {

  return customMutator<platformJurisdictionPackagesManageResponse>(getPlatformJurisdictionPackagesManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformJurisdictionPackagesManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManage>>, TError,void, TContext> => {

const mutationKey = ['platformJurisdictionPackagesManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesManage>>, void> = () => {


          return  platformJurisdictionPackagesManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformJurisdictionPackagesManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesManage>>>

    export type PlatformJurisdictionPackagesManageMutationError = unknown

    export const usePlatformJurisdictionPackagesManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformJurisdictionPackagesManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformJurisdictionPackagesManageMutationOptions(options), queryClient);
    }

export type platformJurisdictionPackagesReadGetResponse200 = {
  data: void
  status: 200
}

export type platformJurisdictionPackagesReadGetResponseSuccess = (platformJurisdictionPackagesReadGetResponse200) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesReadGetResponse = (platformJurisdictionPackagesReadGetResponseSuccess)

export const getPlatformJurisdictionPackagesReadGetUrl = (targetOrganizationId: string,) => {




  return `/api/platform/jurisdiction-packages/applicable/${targetOrganizationId}`
}

export const platformJurisdictionPackagesReadGet = async (targetOrganizationId: string, options?: RequestInit): Promise<platformJurisdictionPackagesReadGetResponse> => {

  return customMutator<platformJurisdictionPackagesReadGetResponse>(getPlatformJurisdictionPackagesReadGetUrl(targetOrganizationId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformJurisdictionPackagesReadGetQueryKey = (targetOrganizationId: string,) => {
    return [
    `/api/platform/jurisdiction-packages/applicable/${targetOrganizationId}`
    ] as const;
    }


export const getPlatformJurisdictionPackagesReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError = unknown>(targetOrganizationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformJurisdictionPackagesReadGetQueryKey(targetOrganizationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>> = ({ signal }) => platformJurisdictionPackagesReadGet(targetOrganizationId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: targetOrganizationId !== null && targetOrganizationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformJurisdictionPackagesReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>>
export type PlatformJurisdictionPackagesReadGetQueryError = unknown


export function usePlatformJurisdictionPackagesReadGet<TData = Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError = unknown>(
 targetOrganizationId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformJurisdictionPackagesReadGet<TData = Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError = unknown>(
 targetOrganizationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformJurisdictionPackagesReadGet<TData = Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError = unknown>(
 targetOrganizationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformJurisdictionPackagesReadGet<TData = Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError = unknown>(
 targetOrganizationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformJurisdictionPackagesReadGetQueryOptions(targetOrganizationId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformJurisdictionPackagesManagePostResponse201 = {
  data: void
  status: 201
}

export type platformJurisdictionPackagesManagePostResponseSuccess = (platformJurisdictionPackagesManagePostResponse201) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesManagePostResponse = (platformJurisdictionPackagesManagePostResponseSuccess)

export const getPlatformJurisdictionPackagesManagePostUrl = (id: string,) => {




  return `/api/platform/jurisdiction-packages/${id}/sources`
}

export const platformJurisdictionPackagesManagePost = async (id: string, options?: RequestInit): Promise<platformJurisdictionPackagesManagePostResponse> => {

  return customMutator<platformJurisdictionPackagesManagePostResponse>(getPlatformJurisdictionPackagesManagePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformJurisdictionPackagesManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformJurisdictionPackagesManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformJurisdictionPackagesManagePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformJurisdictionPackagesManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost>>>

    export type PlatformJurisdictionPackagesManagePostMutationError = unknown

    export const usePlatformJurisdictionPackagesManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformJurisdictionPackagesManagePostMutationOptions(options), queryClient);
    }

export type platformJurisdictionPackagesManagePost1Response201 = {
  data: void
  status: 201
}

export type platformJurisdictionPackagesManagePost1ResponseSuccess = (platformJurisdictionPackagesManagePost1Response201) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesManagePost1Response = (platformJurisdictionPackagesManagePost1ResponseSuccess)

export const getPlatformJurisdictionPackagesManagePost1Url = (sourceDocumentId: string,) => {




  return `/api/platform/jurisdiction-packages/sources/${sourceDocumentId}/reviews`
}

export const platformJurisdictionPackagesManagePost1 = async (sourceDocumentId: string, options?: RequestInit): Promise<platformJurisdictionPackagesManagePost1Response> => {

  return customMutator<platformJurisdictionPackagesManagePost1Response>(getPlatformJurisdictionPackagesManagePost1Url(sourceDocumentId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformJurisdictionPackagesManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost1>>, TError,{sourceDocumentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost1>>, TError,{sourceDocumentId: string}, TContext> => {

const mutationKey = ['platformJurisdictionPackagesManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost1>>, {sourceDocumentId: string}> = (props) => {
          const {sourceDocumentId} = props ?? {};

          return  platformJurisdictionPackagesManagePost1(sourceDocumentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformJurisdictionPackagesManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost1>>>

    export type PlatformJurisdictionPackagesManagePost1MutationError = unknown

    export const usePlatformJurisdictionPackagesManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost1>>, TError,{sourceDocumentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost1>>,
        TError,
        {sourceDocumentId: string},
        TContext
      > => {
      return useMutation(getPlatformJurisdictionPackagesManagePost1MutationOptions(options), queryClient);
    }

export type platformJurisdictionPackagesManagePost2Response201 = {
  data: void
  status: 201
}

export type platformJurisdictionPackagesManagePost2ResponseSuccess = (platformJurisdictionPackagesManagePost2Response201) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesManagePost2Response = (platformJurisdictionPackagesManagePost2ResponseSuccess)

export const getPlatformJurisdictionPackagesManagePost2Url = (id: string,) => {




  return `/api/platform/jurisdiction-packages/${id}/submit`
}

export const platformJurisdictionPackagesManagePost2 = async (id: string, options?: RequestInit): Promise<platformJurisdictionPackagesManagePost2Response> => {

  return customMutator<platformJurisdictionPackagesManagePost2Response>(getPlatformJurisdictionPackagesManagePost2Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformJurisdictionPackagesManagePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost2>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformJurisdictionPackagesManagePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost2>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformJurisdictionPackagesManagePost2(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformJurisdictionPackagesManagePost2MutationResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost2>>>

    export type PlatformJurisdictionPackagesManagePost2MutationError = unknown

    export const usePlatformJurisdictionPackagesManagePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost2>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformJurisdictionPackagesManagePost2MutationOptions(options), queryClient);
    }

export type platformJurisdictionPackagesManagePost3Response201 = {
  data: void
  status: 201
}

export type platformJurisdictionPackagesManagePost3ResponseSuccess = (platformJurisdictionPackagesManagePost3Response201) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesManagePost3Response = (platformJurisdictionPackagesManagePost3ResponseSuccess)

export const getPlatformJurisdictionPackagesManagePost3Url = (id: string,) => {




  return `/api/platform/jurisdiction-packages/${id}/approve`
}

export const platformJurisdictionPackagesManagePost3 = async (id: string, options?: RequestInit): Promise<platformJurisdictionPackagesManagePost3Response> => {

  return customMutator<platformJurisdictionPackagesManagePost3Response>(getPlatformJurisdictionPackagesManagePost3Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformJurisdictionPackagesManagePost3MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost3>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost3>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformJurisdictionPackagesManagePost3'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost3>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformJurisdictionPackagesManagePost3(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformJurisdictionPackagesManagePost3MutationResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost3>>>

    export type PlatformJurisdictionPackagesManagePost3MutationError = unknown

    export const usePlatformJurisdictionPackagesManagePost3 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost3>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost3>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformJurisdictionPackagesManagePost3MutationOptions(options), queryClient);
    }

export type platformJurisdictionPackagesManagePost4Response201 = {
  data: void
  status: 201
}

export type platformJurisdictionPackagesManagePost4ResponseSuccess = (platformJurisdictionPackagesManagePost4Response201) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesManagePost4Response = (platformJurisdictionPackagesManagePost4ResponseSuccess)

export const getPlatformJurisdictionPackagesManagePost4Url = (id: string,) => {




  return `/api/platform/jurisdiction-packages/${id}/activate`
}

export const platformJurisdictionPackagesManagePost4 = async (id: string, options?: RequestInit): Promise<platformJurisdictionPackagesManagePost4Response> => {

  return customMutator<platformJurisdictionPackagesManagePost4Response>(getPlatformJurisdictionPackagesManagePost4Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformJurisdictionPackagesManagePost4MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost4>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost4>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformJurisdictionPackagesManagePost4'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost4>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformJurisdictionPackagesManagePost4(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformJurisdictionPackagesManagePost4MutationResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost4>>>

    export type PlatformJurisdictionPackagesManagePost4MutationError = unknown

    export const usePlatformJurisdictionPackagesManagePost4 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost4>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost4>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformJurisdictionPackagesManagePost4MutationOptions(options), queryClient);
    }

export type platformJurisdictionPackagesManagePost5Response201 = {
  data: void
  status: 201
}

export type platformJurisdictionPackagesManagePost5ResponseSuccess = (platformJurisdictionPackagesManagePost5Response201) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesManagePost5Response = (platformJurisdictionPackagesManagePost5ResponseSuccess)

export const getPlatformJurisdictionPackagesManagePost5Url = (id: string,) => {




  return `/api/platform/jurisdiction-packages/${id}/assignments`
}

export const platformJurisdictionPackagesManagePost5 = async (id: string, options?: RequestInit): Promise<platformJurisdictionPackagesManagePost5Response> => {

  return customMutator<platformJurisdictionPackagesManagePost5Response>(getPlatformJurisdictionPackagesManagePost5Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformJurisdictionPackagesManagePost5MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost5>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost5>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformJurisdictionPackagesManagePost5'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost5>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformJurisdictionPackagesManagePost5(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformJurisdictionPackagesManagePost5MutationResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost5>>>

    export type PlatformJurisdictionPackagesManagePost5MutationError = unknown

    export const usePlatformJurisdictionPackagesManagePost5 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost5>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost5>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformJurisdictionPackagesManagePost5MutationOptions(options), queryClient);
    }

export type platformJurisdictionPackagesManagePost6Response201 = {
  data: void
  status: 201
}

export type platformJurisdictionPackagesManagePost6ResponseSuccess = (platformJurisdictionPackagesManagePost6Response201) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesManagePost6Response = (platformJurisdictionPackagesManagePost6ResponseSuccess)

export const getPlatformJurisdictionPackagesManagePost6Url = (id: string,) => {




  return `/api/platform/jurisdiction-packages/${id}/rollback`
}

export const platformJurisdictionPackagesManagePost6 = async (id: string, options?: RequestInit): Promise<platformJurisdictionPackagesManagePost6Response> => {

  return customMutator<platformJurisdictionPackagesManagePost6Response>(getPlatformJurisdictionPackagesManagePost6Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformJurisdictionPackagesManagePost6MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost6>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost6>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformJurisdictionPackagesManagePost6'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost6>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformJurisdictionPackagesManagePost6(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformJurisdictionPackagesManagePost6MutationResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost6>>>

    export type PlatformJurisdictionPackagesManagePost6MutationError = unknown

    export const usePlatformJurisdictionPackagesManagePost6 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost6>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost6>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformJurisdictionPackagesManagePost6MutationOptions(options), queryClient);
    }

export type platformJurisdictionPackagesManagePost7Response201 = {
  data: void
  status: 201
}

export type platformJurisdictionPackagesManagePost7ResponseSuccess = (platformJurisdictionPackagesManagePost7Response201) & {
  headers: Headers;
};
;

export type platformJurisdictionPackagesManagePost7Response = (platformJurisdictionPackagesManagePost7ResponseSuccess)

export const getPlatformJurisdictionPackagesManagePost7Url = (id: string,) => {




  return `/api/platform/jurisdiction-packages/${id}/retire`
}

export const platformJurisdictionPackagesManagePost7 = async (id: string, options?: RequestInit): Promise<platformJurisdictionPackagesManagePost7Response> => {

  return customMutator<platformJurisdictionPackagesManagePost7Response>(getPlatformJurisdictionPackagesManagePost7Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformJurisdictionPackagesManagePost7MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost7>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost7>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformJurisdictionPackagesManagePost7'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost7>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformJurisdictionPackagesManagePost7(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformJurisdictionPackagesManagePost7MutationResult = NonNullable<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost7>>>

    export type PlatformJurisdictionPackagesManagePost7MutationError = unknown

    export const usePlatformJurisdictionPackagesManagePost7 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost7>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformJurisdictionPackagesManagePost7>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformJurisdictionPackagesManagePost7MutationOptions(options), queryClient);
    }

export type platformReferenceMasterReadResponse200 = {
  data: void
  status: 200
}

export type platformReferenceMasterReadResponseSuccess = (platformReferenceMasterReadResponse200) & {
  headers: Headers;
};
;

export type platformReferenceMasterReadResponse = (platformReferenceMasterReadResponseSuccess)

export const getPlatformReferenceMasterReadUrl = () => {




  return `/api/platform/reference-master`
}

export const platformReferenceMasterRead = async ( options?: RequestInit): Promise<platformReferenceMasterReadResponse> => {

  return customMutator<platformReferenceMasterReadResponse>(getPlatformReferenceMasterReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformReferenceMasterReadQueryKey = () => {
    return [
    `/api/platform/reference-master`
    ] as const;
    }


export const getPlatformReferenceMasterReadQueryOptions = <TData = Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformReferenceMasterReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformReferenceMasterRead>>> = ({ signal }) => platformReferenceMasterRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformReferenceMasterReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformReferenceMasterRead>>>
export type PlatformReferenceMasterReadQueryError = unknown


export function usePlatformReferenceMasterRead<TData = Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformReferenceMasterRead>>,
          TError,
          Awaited<ReturnType<typeof platformReferenceMasterRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformReferenceMasterRead<TData = Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformReferenceMasterRead>>,
          TError,
          Awaited<ReturnType<typeof platformReferenceMasterRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformReferenceMasterRead<TData = Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformReferenceMasterRead<TData = Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformReferenceMasterReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformReferenceMasterManageResponse201 = {
  data: void
  status: 201
}

export type platformReferenceMasterManageResponseSuccess = (platformReferenceMasterManageResponse201) & {
  headers: Headers;
};
;

export type platformReferenceMasterManageResponse = (platformReferenceMasterManageResponseSuccess)

export const getPlatformReferenceMasterManageUrl = () => {




  return `/api/platform/reference-master`
}

export const platformReferenceMasterManage = async ( options?: RequestInit): Promise<platformReferenceMasterManageResponse> => {

  return customMutator<platformReferenceMasterManageResponse>(getPlatformReferenceMasterManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformReferenceMasterManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformReferenceMasterManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformReferenceMasterManage>>, TError,void, TContext> => {

const mutationKey = ['platformReferenceMasterManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformReferenceMasterManage>>, void> = () => {


          return  platformReferenceMasterManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformReferenceMasterManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformReferenceMasterManage>>>

    export type PlatformReferenceMasterManageMutationError = unknown

    export const usePlatformReferenceMasterManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformReferenceMasterManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformReferenceMasterManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformReferenceMasterManageMutationOptions(options), queryClient);
    }

export type platformReferenceMasterReadGetResponse200 = {
  data: void
  status: 200
}

export type platformReferenceMasterReadGetResponseSuccess = (platformReferenceMasterReadGetResponse200) & {
  headers: Headers;
};
;

export type platformReferenceMasterReadGetResponse = (platformReferenceMasterReadGetResponseSuccess)

export const getPlatformReferenceMasterReadGetUrl = (family: string,
    referenceKey: string,) => {




  return `/api/platform/reference-master/${family}/${referenceKey}`
}

export const platformReferenceMasterReadGet = async (family: string,
    referenceKey: string, options?: RequestInit): Promise<platformReferenceMasterReadGetResponse> => {

  return customMutator<platformReferenceMasterReadGetResponse>(getPlatformReferenceMasterReadGetUrl(family,referenceKey),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformReferenceMasterReadGetQueryKey = (family: string,
    referenceKey: string,) => {
    return [
    `/api/platform/reference-master/${family}/${referenceKey}`
    ] as const;
    }


export const getPlatformReferenceMasterReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError = unknown>(family: string,
    referenceKey: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformReferenceMasterReadGetQueryKey(family,referenceKey);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformReferenceMasterReadGet>>> = ({ signal }) => platformReferenceMasterReadGet(family,referenceKey, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: family !== null && family !== undefined && referenceKey !== null && referenceKey !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformReferenceMasterReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformReferenceMasterReadGet>>>
export type PlatformReferenceMasterReadGetQueryError = unknown


export function usePlatformReferenceMasterReadGet<TData = Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError = unknown>(
 family: string,
    referenceKey: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformReferenceMasterReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformReferenceMasterReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformReferenceMasterReadGet<TData = Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError = unknown>(
 family: string,
    referenceKey: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformReferenceMasterReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformReferenceMasterReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformReferenceMasterReadGet<TData = Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError = unknown>(
 family: string,
    referenceKey: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformReferenceMasterReadGet<TData = Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError = unknown>(
 family: string,
    referenceKey: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformReferenceMasterReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformReferenceMasterReadGetQueryOptions(family,referenceKey,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformReferenceMasterManagePostResponse201 = {
  data: void
  status: 201
}

export type platformReferenceMasterManagePostResponseSuccess = (platformReferenceMasterManagePostResponse201) & {
  headers: Headers;
};
;

export type platformReferenceMasterManagePostResponse = (platformReferenceMasterManagePostResponseSuccess)

export const getPlatformReferenceMasterManagePostUrl = (id: string,) => {




  return `/api/platform/reference-master/${id}/retire`
}

export const platformReferenceMasterManagePost = async (id: string, options?: RequestInit): Promise<platformReferenceMasterManagePostResponse> => {

  return customMutator<platformReferenceMasterManagePostResponse>(getPlatformReferenceMasterManagePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformReferenceMasterManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformReferenceMasterManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformReferenceMasterManagePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformReferenceMasterManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformReferenceMasterManagePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformReferenceMasterManagePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformReferenceMasterManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformReferenceMasterManagePost>>>

    export type PlatformReferenceMasterManagePostMutationError = unknown

    export const usePlatformReferenceMasterManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformReferenceMasterManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformReferenceMasterManagePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformReferenceMasterManagePostMutationOptions(options), queryClient);
    }

export type platformOrganizationPortabilityReadResponse200 = {
  data: void
  status: 200
}

export type platformOrganizationPortabilityReadResponseSuccess = (platformOrganizationPortabilityReadResponse200) & {
  headers: Headers;
};
;

export type platformOrganizationPortabilityReadResponse = (platformOrganizationPortabilityReadResponseSuccess)

export const getPlatformOrganizationPortabilityReadUrl = (id: string,) => {




  return `/api/platform/organization-portability/${id}`
}

export const platformOrganizationPortabilityRead = async (id: string, options?: RequestInit): Promise<platformOrganizationPortabilityReadResponse> => {

  return customMutator<platformOrganizationPortabilityReadResponse>(getPlatformOrganizationPortabilityReadUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformOrganizationPortabilityReadQueryKey = (id: string,) => {
    return [
    `/api/platform/organization-portability/${id}`
    ] as const;
    }


export const getPlatformOrganizationPortabilityReadQueryOptions = <TData = Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformOrganizationPortabilityReadQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>> = ({ signal }) => platformOrganizationPortabilityRead(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformOrganizationPortabilityReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>>
export type PlatformOrganizationPortabilityReadQueryError = unknown


export function usePlatformOrganizationPortabilityRead<TData = Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationPortabilityRead<TData = Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationPortabilityRead<TData = Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformOrganizationPortabilityRead<TData = Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformOrganizationPortabilityReadQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformOrganizationPortabilityRequestResponse201 = {
  data: void
  status: 201
}

export type platformOrganizationPortabilityRequestResponseSuccess = (platformOrganizationPortabilityRequestResponse201) & {
  headers: Headers;
};
;

export type platformOrganizationPortabilityRequestResponse = (platformOrganizationPortabilityRequestResponseSuccess)

export const getPlatformOrganizationPortabilityRequestUrl = () => {




  return `/api/platform/organization-portability`
}

export const platformOrganizationPortabilityRequest = async ( options?: RequestInit): Promise<platformOrganizationPortabilityRequestResponse> => {

  return customMutator<platformOrganizationPortabilityRequestResponse>(getPlatformOrganizationPortabilityRequestUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationPortabilityRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequest>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequest>>, TError,void, TContext> => {

const mutationKey = ['platformOrganizationPortabilityRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationPortabilityRequest>>, void> = () => {


          return  platformOrganizationPortabilityRequest(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationPortabilityRequestMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationPortabilityRequest>>>

    export type PlatformOrganizationPortabilityRequestMutationError = unknown

    export const usePlatformOrganizationPortabilityRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequest>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationPortabilityRequest>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformOrganizationPortabilityRequestMutationOptions(options), queryClient);
    }

export type platformOrganizationPortabilityApproveResponse201 = {
  data: void
  status: 201
}

export type platformOrganizationPortabilityApproveResponseSuccess = (platformOrganizationPortabilityApproveResponse201) & {
  headers: Headers;
};
;

export type platformOrganizationPortabilityApproveResponse = (platformOrganizationPortabilityApproveResponseSuccess)

export const getPlatformOrganizationPortabilityApproveUrl = (id: string,) => {




  return `/api/platform/organization-portability/${id}/approvals`
}

export const platformOrganizationPortabilityApprove = async (id: string, options?: RequestInit): Promise<platformOrganizationPortabilityApproveResponse> => {

  return customMutator<platformOrganizationPortabilityApproveResponse>(getPlatformOrganizationPortabilityApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationPortabilityApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformOrganizationPortabilityApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationPortabilityApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformOrganizationPortabilityApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationPortabilityApproveMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationPortabilityApprove>>>

    export type PlatformOrganizationPortabilityApproveMutationError = unknown

    export const usePlatformOrganizationPortabilityApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationPortabilityApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformOrganizationPortabilityApproveMutationOptions(options), queryClient);
    }

export type platformOrganizationPortabilityRequestPostResponse201 = {
  data: void
  status: 201
}

export type platformOrganizationPortabilityRequestPostResponseSuccess = (platformOrganizationPortabilityRequestPostResponse201) & {
  headers: Headers;
};
;

export type platformOrganizationPortabilityRequestPostResponse = (platformOrganizationPortabilityRequestPostResponseSuccess)

export const getPlatformOrganizationPortabilityRequestPostUrl = (id: string,) => {




  return `/api/platform/organization-portability/${id}/transfers`
}

export const platformOrganizationPortabilityRequestPost = async (id: string, options?: RequestInit): Promise<platformOrganizationPortabilityRequestPostResponse> => {

  return customMutator<platformOrganizationPortabilityRequestPostResponse>(getPlatformOrganizationPortabilityRequestPostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationPortabilityRequestPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformOrganizationPortabilityRequestPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformOrganizationPortabilityRequestPost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationPortabilityRequestPostMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost>>>

    export type PlatformOrganizationPortabilityRequestPostMutationError = unknown

    export const usePlatformOrganizationPortabilityRequestPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformOrganizationPortabilityRequestPostMutationOptions(options), queryClient);
    }

export type platformOrganizationPortabilityRequestPost1Response201 = {
  data: void
  status: 201
}

export type platformOrganizationPortabilityRequestPost1ResponseSuccess = (platformOrganizationPortabilityRequestPost1Response201) & {
  headers: Headers;
};
;

export type platformOrganizationPortabilityRequestPost1Response = (platformOrganizationPortabilityRequestPost1ResponseSuccess)

export const getPlatformOrganizationPortabilityRequestPost1Url = (id: string,) => {




  return `/api/platform/organization-portability/${id}/acknowledgments`
}

export const platformOrganizationPortabilityRequestPost1 = async (id: string, options?: RequestInit): Promise<platformOrganizationPortabilityRequestPost1Response> => {

  return customMutator<platformOrganizationPortabilityRequestPost1Response>(getPlatformOrganizationPortabilityRequestPost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationPortabilityRequestPost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformOrganizationPortabilityRequestPost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformOrganizationPortabilityRequestPost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationPortabilityRequestPost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost1>>>

    export type PlatformOrganizationPortabilityRequestPost1MutationError = unknown

    export const usePlatformOrganizationPortabilityRequestPost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformOrganizationPortabilityRequestPost1MutationOptions(options), queryClient);
    }

export type platformOrganizationPortabilityRequestPost2Response201 = {
  data: void
  status: 201
}

export type platformOrganizationPortabilityRequestPost2ResponseSuccess = (platformOrganizationPortabilityRequestPost2Response201) & {
  headers: Headers;
};
;

export type platformOrganizationPortabilityRequestPost2Response = (platformOrganizationPortabilityRequestPost2ResponseSuccess)

export const getPlatformOrganizationPortabilityRequestPost2Url = (id: string,) => {




  return `/api/platform/organization-portability/${id}/import-validations`
}

export const platformOrganizationPortabilityRequestPost2 = async (id: string, options?: RequestInit): Promise<platformOrganizationPortabilityRequestPost2Response> => {

  return customMutator<platformOrganizationPortabilityRequestPost2Response>(getPlatformOrganizationPortabilityRequestPost2Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationPortabilityRequestPost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost2>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformOrganizationPortabilityRequestPost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost2>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformOrganizationPortabilityRequestPost2(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationPortabilityRequestPost2MutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost2>>>

    export type PlatformOrganizationPortabilityRequestPost2MutationError = unknown

    export const usePlatformOrganizationPortabilityRequestPost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationPortabilityRequestPost2>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformOrganizationPortabilityRequestPost2MutationOptions(options), queryClient);
    }

export type platformOrganizationPortabilityPurgeResponse201 = {
  data: void
  status: 201
}

export type platformOrganizationPortabilityPurgeResponseSuccess = (platformOrganizationPortabilityPurgeResponse201) & {
  headers: Headers;
};
;

export type platformOrganizationPortabilityPurgeResponse = (platformOrganizationPortabilityPurgeResponseSuccess)

export const getPlatformOrganizationPortabilityPurgeUrl = (id: string,) => {




  return `/api/platform/organization-portability/${id}/purges`
}

export const platformOrganizationPortabilityPurge = async (id: string, options?: RequestInit): Promise<platformOrganizationPortabilityPurgeResponse> => {

  return customMutator<platformOrganizationPortabilityPurgeResponse>(getPlatformOrganizationPortabilityPurgeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationPortabilityPurgeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityPurge>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityPurge>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformOrganizationPortabilityPurge'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationPortabilityPurge>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformOrganizationPortabilityPurge(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationPortabilityPurgeMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationPortabilityPurge>>>

    export type PlatformOrganizationPortabilityPurgeMutationError = unknown

    export const usePlatformOrganizationPortabilityPurge = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationPortabilityPurge>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationPortabilityPurge>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformOrganizationPortabilityPurgeMutationOptions(options), queryClient);
    }

export type platformNotificationGovernanceReadResponse200 = {
  data: void
  status: 200
}

export type platformNotificationGovernanceReadResponseSuccess = (platformNotificationGovernanceReadResponse200) & {
  headers: Headers;
};
;

export type platformNotificationGovernanceReadResponse = (platformNotificationGovernanceReadResponseSuccess)

export const getPlatformNotificationGovernanceReadUrl = () => {




  return `/api/platform/notification-governance`
}

export const platformNotificationGovernanceRead = async ( options?: RequestInit): Promise<platformNotificationGovernanceReadResponse> => {

  return customMutator<platformNotificationGovernanceReadResponse>(getPlatformNotificationGovernanceReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformNotificationGovernanceReadQueryKey = () => {
    return [
    `/api/platform/notification-governance`
    ] as const;
    }


export const getPlatformNotificationGovernanceReadQueryOptions = <TData = Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformNotificationGovernanceReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformNotificationGovernanceRead>>> = ({ signal }) => platformNotificationGovernanceRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformNotificationGovernanceReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformNotificationGovernanceRead>>>
export type PlatformNotificationGovernanceReadQueryError = unknown


export function usePlatformNotificationGovernanceRead<TData = Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformNotificationGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof platformNotificationGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformNotificationGovernanceRead<TData = Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformNotificationGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof platformNotificationGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformNotificationGovernanceRead<TData = Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformNotificationGovernanceRead<TData = Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformNotificationGovernanceReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformNotificationGovernanceManageResponse201 = {
  data: void
  status: 201
}

export type platformNotificationGovernanceManageResponseSuccess = (platformNotificationGovernanceManageResponse201) & {
  headers: Headers;
};
;

export type platformNotificationGovernanceManageResponse = (platformNotificationGovernanceManageResponseSuccess)

export const getPlatformNotificationGovernanceManageUrl = () => {




  return `/api/platform/notification-governance/templates`
}

export const platformNotificationGovernanceManage = async ( options?: RequestInit): Promise<platformNotificationGovernanceManageResponse> => {

  return customMutator<platformNotificationGovernanceManageResponse>(getPlatformNotificationGovernanceManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformNotificationGovernanceManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManage>>, TError,void, TContext> => {

const mutationKey = ['platformNotificationGovernanceManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformNotificationGovernanceManage>>, void> = () => {


          return  platformNotificationGovernanceManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformNotificationGovernanceManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformNotificationGovernanceManage>>>

    export type PlatformNotificationGovernanceManageMutationError = unknown

    export const usePlatformNotificationGovernanceManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformNotificationGovernanceManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformNotificationGovernanceManageMutationOptions(options), queryClient);
    }

export type platformNotificationGovernanceApproveResponse201 = {
  data: void
  status: 201
}

export type platformNotificationGovernanceApproveResponseSuccess = (platformNotificationGovernanceApproveResponse201) & {
  headers: Headers;
};
;

export type platformNotificationGovernanceApproveResponse = (platformNotificationGovernanceApproveResponseSuccess)

export const getPlatformNotificationGovernanceApproveUrl = (id: string,) => {




  return `/api/platform/notification-governance/templates/${id}/approvals`
}

export const platformNotificationGovernanceApprove = async (id: string, options?: RequestInit): Promise<platformNotificationGovernanceApproveResponse> => {

  return customMutator<platformNotificationGovernanceApproveResponse>(getPlatformNotificationGovernanceApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformNotificationGovernanceApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformNotificationGovernanceApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformNotificationGovernanceApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformNotificationGovernanceApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformNotificationGovernanceApproveMutationResult = NonNullable<Awaited<ReturnType<typeof platformNotificationGovernanceApprove>>>

    export type PlatformNotificationGovernanceApproveMutationError = unknown

    export const usePlatformNotificationGovernanceApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformNotificationGovernanceApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformNotificationGovernanceApproveMutationOptions(options), queryClient);
    }

export type platformNotificationGovernanceReadGetResponse200 = {
  data: void
  status: 200
}

export type platformNotificationGovernanceReadGetResponseSuccess = (platformNotificationGovernanceReadGetResponse200) & {
  headers: Headers;
};
;

export type platformNotificationGovernanceReadGetResponse = (platformNotificationGovernanceReadGetResponseSuccess)

export const getPlatformNotificationGovernanceReadGetUrl = (id: string,) => {




  return `/api/platform/notification-governance/templates/${id}/preview`
}

export const platformNotificationGovernanceReadGet = async (id: string, options?: RequestInit): Promise<platformNotificationGovernanceReadGetResponse> => {

  return customMutator<platformNotificationGovernanceReadGetResponse>(getPlatformNotificationGovernanceReadGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformNotificationGovernanceReadGetQueryKey = (id: string,) => {
    return [
    `/api/platform/notification-governance/templates/${id}/preview`
    ] as const;
    }


export const getPlatformNotificationGovernanceReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformNotificationGovernanceReadGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>> = ({ signal }) => platformNotificationGovernanceReadGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformNotificationGovernanceReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>>
export type PlatformNotificationGovernanceReadGetQueryError = unknown


export function usePlatformNotificationGovernanceReadGet<TData = Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformNotificationGovernanceReadGet<TData = Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformNotificationGovernanceReadGet<TData = Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformNotificationGovernanceReadGet<TData = Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformNotificationGovernanceReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformNotificationGovernanceReadGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformNotificationGovernanceManagePostResponse201 = {
  data: void
  status: 201
}

export type platformNotificationGovernanceManagePostResponseSuccess = (platformNotificationGovernanceManagePostResponse201) & {
  headers: Headers;
};
;

export type platformNotificationGovernanceManagePostResponse = (platformNotificationGovernanceManagePostResponseSuccess)

export const getPlatformNotificationGovernanceManagePostUrl = () => {




  return `/api/platform/notification-governance/recipient-policies`
}

export const platformNotificationGovernanceManagePost = async ( options?: RequestInit): Promise<platformNotificationGovernanceManagePostResponse> => {

  return customMutator<platformNotificationGovernanceManagePostResponse>(getPlatformNotificationGovernanceManagePostUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformNotificationGovernanceManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost>>, TError,void, TContext> => {

const mutationKey = ['platformNotificationGovernanceManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost>>, void> = () => {


          return  platformNotificationGovernanceManagePost(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformNotificationGovernanceManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost>>>

    export type PlatformNotificationGovernanceManagePostMutationError = unknown

    export const usePlatformNotificationGovernanceManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformNotificationGovernanceManagePost>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformNotificationGovernanceManagePostMutationOptions(options), queryClient);
    }

export type platformNotificationGovernanceApprovePostResponse201 = {
  data: void
  status: 201
}

export type platformNotificationGovernanceApprovePostResponseSuccess = (platformNotificationGovernanceApprovePostResponse201) & {
  headers: Headers;
};
;

export type platformNotificationGovernanceApprovePostResponse = (platformNotificationGovernanceApprovePostResponseSuccess)

export const getPlatformNotificationGovernanceApprovePostUrl = (id: string,) => {




  return `/api/platform/notification-governance/recipient-policies/${id}/approvals`
}

export const platformNotificationGovernanceApprovePost = async (id: string, options?: RequestInit): Promise<platformNotificationGovernanceApprovePostResponse> => {

  return customMutator<platformNotificationGovernanceApprovePostResponse>(getPlatformNotificationGovernanceApprovePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformNotificationGovernanceApprovePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceApprovePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceApprovePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformNotificationGovernanceApprovePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformNotificationGovernanceApprovePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformNotificationGovernanceApprovePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformNotificationGovernanceApprovePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformNotificationGovernanceApprovePost>>>

    export type PlatformNotificationGovernanceApprovePostMutationError = unknown

    export const usePlatformNotificationGovernanceApprovePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceApprovePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformNotificationGovernanceApprovePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformNotificationGovernanceApprovePostMutationOptions(options), queryClient);
    }

export type platformNotificationGovernanceManagePost1Response201 = {
  data: void
  status: 201
}

export type platformNotificationGovernanceManagePost1ResponseSuccess = (platformNotificationGovernanceManagePost1Response201) & {
  headers: Headers;
};
;

export type platformNotificationGovernanceManagePost1Response = (platformNotificationGovernanceManagePost1ResponseSuccess)

export const getPlatformNotificationGovernanceManagePost1Url = () => {




  return `/api/platform/notification-governance/deliveries`
}

export const platformNotificationGovernanceManagePost1 = async ( options?: RequestInit): Promise<platformNotificationGovernanceManagePost1Response> => {

  return customMutator<platformNotificationGovernanceManagePost1Response>(getPlatformNotificationGovernanceManagePost1Url(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformNotificationGovernanceManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost1>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost1>>, TError,void, TContext> => {

const mutationKey = ['platformNotificationGovernanceManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost1>>, void> = () => {


          return  platformNotificationGovernanceManagePost1(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformNotificationGovernanceManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost1>>>

    export type PlatformNotificationGovernanceManagePost1MutationError = unknown

    export const usePlatformNotificationGovernanceManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost1>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformNotificationGovernanceManagePost1>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformNotificationGovernanceManagePost1MutationOptions(options), queryClient);
    }

export type platformNotificationGovernanceManagePost2Response201 = {
  data: void
  status: 201
}

export type platformNotificationGovernanceManagePost2ResponseSuccess = (platformNotificationGovernanceManagePost2Response201) & {
  headers: Headers;
};
;

export type platformNotificationGovernanceManagePost2Response = (platformNotificationGovernanceManagePost2ResponseSuccess)

export const getPlatformNotificationGovernanceManagePost2Url = (id: string,) => {




  return `/api/platform/notification-governance/deliveries/${id}/suppressions`
}

export const platformNotificationGovernanceManagePost2 = async (id: string, options?: RequestInit): Promise<platformNotificationGovernanceManagePost2Response> => {

  return customMutator<platformNotificationGovernanceManagePost2Response>(getPlatformNotificationGovernanceManagePost2Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformNotificationGovernanceManagePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost2>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformNotificationGovernanceManagePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost2>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformNotificationGovernanceManagePost2(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformNotificationGovernanceManagePost2MutationResult = NonNullable<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost2>>>

    export type PlatformNotificationGovernanceManagePost2MutationError = unknown

    export const usePlatformNotificationGovernanceManagePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformNotificationGovernanceManagePost2>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformNotificationGovernanceManagePost2MutationOptions(options), queryClient);
    }

export type platformNotificationGovernanceManagePost3Response201 = {
  data: void
  status: 201
}

export type platformNotificationGovernanceManagePost3ResponseSuccess = (platformNotificationGovernanceManagePost3Response201) & {
  headers: Headers;
};
;

export type platformNotificationGovernanceManagePost3Response = (platformNotificationGovernanceManagePost3ResponseSuccess)

export const getPlatformNotificationGovernanceManagePost3Url = (id: string,) => {




  return `/api/platform/notification-governance/deliveries/${id}/escalations`
}

export const platformNotificationGovernanceManagePost3 = async (id: string, options?: RequestInit): Promise<platformNotificationGovernanceManagePost3Response> => {

  return customMutator<platformNotificationGovernanceManagePost3Response>(getPlatformNotificationGovernanceManagePost3Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformNotificationGovernanceManagePost3MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost3>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost3>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformNotificationGovernanceManagePost3'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost3>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformNotificationGovernanceManagePost3(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformNotificationGovernanceManagePost3MutationResult = NonNullable<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost3>>>

    export type PlatformNotificationGovernanceManagePost3MutationError = unknown

    export const usePlatformNotificationGovernanceManagePost3 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformNotificationGovernanceManagePost3>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformNotificationGovernanceManagePost3>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformNotificationGovernanceManagePost3MutationOptions(options), queryClient);
    }

export type investmentInstrumentListResponse200 = {
  data: InvestmentInstrumentList200
  status: 200
}

export type investmentInstrumentListResponseSuccess = (investmentInstrumentListResponse200) & {
  headers: Headers;
};
;

export type investmentInstrumentListResponse = (investmentInstrumentListResponseSuccess)

export const getInvestmentInstrumentListUrl = () => {




  return `/api/instruments`
}

export const investmentInstrumentList = async ( options?: RequestInit): Promise<investmentInstrumentListResponse> => {

  return customMutator<investmentInstrumentListResponse>(getInvestmentInstrumentListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentInstrumentListQueryKey = () => {
    return [
    `/api/instruments`
    ] as const;
    }


export const getInvestmentInstrumentListQueryOptions = <TData = Awaited<ReturnType<typeof investmentInstrumentList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentInstrumentListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentInstrumentList>>> = ({ signal }) => investmentInstrumentList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentInstrumentListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentList>>>
export type InvestmentInstrumentListQueryError = unknown


export function useInvestmentInstrumentList<TData = Awaited<ReturnType<typeof investmentInstrumentList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentInstrumentList>>,
          TError,
          Awaited<ReturnType<typeof investmentInstrumentList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentInstrumentList<TData = Awaited<ReturnType<typeof investmentInstrumentList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentInstrumentList>>,
          TError,
          Awaited<ReturnType<typeof investmentInstrumentList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentInstrumentList<TData = Awaited<ReturnType<typeof investmentInstrumentList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentInstrumentList<TData = Awaited<ReturnType<typeof investmentInstrumentList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentInstrumentListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentInstrumentCreateResponse200 = {
  data: void
  status: 200
}

export type investmentInstrumentCreateResponse201 = {
  data: void
  status: 201
}

export type investmentInstrumentCreateResponseSuccess = (investmentInstrumentCreateResponse200 | investmentInstrumentCreateResponse201) & {
  headers: Headers;
};
;

export type investmentInstrumentCreateResponse = (investmentInstrumentCreateResponseSuccess)

export const getInvestmentInstrumentCreateUrl = () => {




  return `/api/instruments`
}

export const investmentInstrumentCreate = async (createInstrumentDto: CreateInstrumentDto, options?: RequestInit): Promise<investmentInstrumentCreateResponse> => {

  return customMutator<investmentInstrumentCreateResponse>(getInvestmentInstrumentCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createInstrumentDto)
  }
);}





export const getInvestmentInstrumentCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentCreate>>, TError,{data: CreateInstrumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentCreate>>, TError,{data: CreateInstrumentDto}, TContext> => {

const mutationKey = ['investmentInstrumentCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentInstrumentCreate>>, {data: CreateInstrumentDto}> = (props) => {
          const {data} = props ?? {};

          return  investmentInstrumentCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentInstrumentCreateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentCreate>>>
    export type InvestmentInstrumentCreateMutationBody = CreateInstrumentDto
    export type InvestmentInstrumentCreateMutationError = unknown

    export const useInvestmentInstrumentCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentCreate>>, TError,{data: CreateInstrumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentInstrumentCreate>>,
        TError,
        {data: CreateInstrumentDto},
        TContext
      > => {
      return useMutation(getInvestmentInstrumentCreateMutationOptions(options), queryClient);
    }

export type investmentInstrumentGetResponse200 = {
  data: InvestmentInstrumentGet200
  status: 200
}

export type investmentInstrumentGetResponseSuccess = (investmentInstrumentGetResponse200) & {
  headers: Headers;
};
;

export type investmentInstrumentGetResponse = (investmentInstrumentGetResponseSuccess)

export const getInvestmentInstrumentGetUrl = (symbol: string,) => {




  return `/api/instruments/${symbol}`
}

export const investmentInstrumentGet = async (symbol: string, options?: RequestInit): Promise<investmentInstrumentGetResponse> => {

  return customMutator<investmentInstrumentGetResponse>(getInvestmentInstrumentGetUrl(symbol),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentInstrumentGetQueryKey = (symbol: string,) => {
    return [
    `/api/instruments/${symbol}`
    ] as const;
    }


export const getInvestmentInstrumentGetQueryOptions = <TData = Awaited<ReturnType<typeof investmentInstrumentGet>>, TError = unknown>(symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentInstrumentGetQueryKey(symbol);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentInstrumentGet>>> = ({ signal }) => investmentInstrumentGet(symbol, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: symbol !== null && symbol !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentInstrumentGetQueryResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentGet>>>
export type InvestmentInstrumentGetQueryError = unknown


export function useInvestmentInstrumentGet<TData = Awaited<ReturnType<typeof investmentInstrumentGet>>, TError = unknown>(
 symbol: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentInstrumentGet>>,
          TError,
          Awaited<ReturnType<typeof investmentInstrumentGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentInstrumentGet<TData = Awaited<ReturnType<typeof investmentInstrumentGet>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentInstrumentGet>>,
          TError,
          Awaited<ReturnType<typeof investmentInstrumentGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentInstrumentGet<TData = Awaited<ReturnType<typeof investmentInstrumentGet>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentInstrumentGet<TData = Awaited<ReturnType<typeof investmentInstrumentGet>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentInstrumentGetQueryOptions(symbol,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentInstrumentUpdateResponse200 = {
  data: void
  status: 200
}

export type investmentInstrumentUpdateResponseSuccess = (investmentInstrumentUpdateResponse200) & {
  headers: Headers;
};
;

export type investmentInstrumentUpdateResponse = (investmentInstrumentUpdateResponseSuccess)

export const getInvestmentInstrumentUpdateUrl = (symbol: string,) => {




  return `/api/instruments/${symbol}`
}

export const investmentInstrumentUpdate = async (symbol: string,
    updateInstrumentDto: UpdateInstrumentDto, options?: RequestInit): Promise<investmentInstrumentUpdateResponse> => {

  return customMutator<investmentInstrumentUpdateResponse>(getInvestmentInstrumentUpdateUrl(symbol),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateInstrumentDto)
  }
);}





export const getInvestmentInstrumentUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentUpdate>>, TError,{symbol: string;data: UpdateInstrumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentUpdate>>, TError,{symbol: string;data: UpdateInstrumentDto}, TContext> => {

const mutationKey = ['investmentInstrumentUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentInstrumentUpdate>>, {symbol: string;data: UpdateInstrumentDto}> = (props) => {
          const {symbol,data} = props ?? {};

          return  investmentInstrumentUpdate(symbol,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentInstrumentUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentUpdate>>>
    export type InvestmentInstrumentUpdateMutationBody = UpdateInstrumentDto
    export type InvestmentInstrumentUpdateMutationError = unknown

    export const useInvestmentInstrumentUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentUpdate>>, TError,{symbol: string;data: UpdateInstrumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentInstrumentUpdate>>,
        TError,
        {symbol: string;data: UpdateInstrumentDto},
        TContext
      > => {
      return useMutation(getInvestmentInstrumentUpdateMutationOptions(options), queryClient);
    }

export type investmentInstrumentDeleteResponse200 = {
  data: void
  status: 200
}

export type investmentInstrumentDeleteResponseSuccess = (investmentInstrumentDeleteResponse200) & {
  headers: Headers;
};
;

export type investmentInstrumentDeleteResponse = (investmentInstrumentDeleteResponseSuccess)

export const getInvestmentInstrumentDeleteUrl = (symbol: string,) => {




  return `/api/instruments/${symbol}`
}

export const investmentInstrumentDelete = async (symbol: string, options?: RequestInit): Promise<investmentInstrumentDeleteResponse> => {

  return customMutator<investmentInstrumentDeleteResponse>(getInvestmentInstrumentDeleteUrl(symbol),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getInvestmentInstrumentDeleteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentDelete>>, TError,{symbol: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentDelete>>, TError,{symbol: string}, TContext> => {

const mutationKey = ['investmentInstrumentDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentInstrumentDelete>>, {symbol: string}> = (props) => {
          const {symbol} = props ?? {};

          return  investmentInstrumentDelete(symbol,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentInstrumentDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentDelete>>>

    export type InvestmentInstrumentDeleteMutationError = unknown

    export const useInvestmentInstrumentDelete = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentDelete>>, TError,{symbol: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentInstrumentDelete>>,
        TError,
        {symbol: string},
        TContext
      > => {
      return useMutation(getInvestmentInstrumentDeleteMutationOptions(options), queryClient);
    }

export type investmentInstrumentBulkUpsertResponse200 = {
  data: void
  status: 200
}

export type investmentInstrumentBulkUpsertResponseSuccess = (investmentInstrumentBulkUpsertResponse200) & {
  headers: Headers;
};
;

export type investmentInstrumentBulkUpsertResponse = (investmentInstrumentBulkUpsertResponseSuccess)

export const getInvestmentInstrumentBulkUpsertUrl = () => {




  return `/api/instruments/bulk-upsert`
}

export const investmentInstrumentBulkUpsert = async (bulkUpsertInstrumentDto: BulkUpsertInstrumentDto, options?: RequestInit): Promise<investmentInstrumentBulkUpsertResponse> => {

  return customMutator<investmentInstrumentBulkUpsertResponse>(getInvestmentInstrumentBulkUpsertUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(bulkUpsertInstrumentDto)
  }
);}





export const getInvestmentInstrumentBulkUpsertMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentBulkUpsert>>, TError,{data: BulkUpsertInstrumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentBulkUpsert>>, TError,{data: BulkUpsertInstrumentDto}, TContext> => {

const mutationKey = ['investmentInstrumentBulkUpsert'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentInstrumentBulkUpsert>>, {data: BulkUpsertInstrumentDto}> = (props) => {
          const {data} = props ?? {};

          return  investmentInstrumentBulkUpsert(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentInstrumentBulkUpsertMutationResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentBulkUpsert>>>
    export type InvestmentInstrumentBulkUpsertMutationBody = BulkUpsertInstrumentDto
    export type InvestmentInstrumentBulkUpsertMutationError = unknown

    export const useInvestmentInstrumentBulkUpsert = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentBulkUpsert>>, TError,{data: BulkUpsertInstrumentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentInstrumentBulkUpsert>>,
        TError,
        {data: BulkUpsertInstrumentDto},
        TContext
      > => {
      return useMutation(getInvestmentInstrumentBulkUpsertMutationOptions(options), queryClient);
    }

export type investmentInstrumentOverrideCreateResponse200 = {
  data: void
  status: 200
}

export type investmentInstrumentOverrideCreateResponse201 = {
  data: void
  status: 201
}

export type investmentInstrumentOverrideCreateResponseSuccess = (investmentInstrumentOverrideCreateResponse200 | investmentInstrumentOverrideCreateResponse201) & {
  headers: Headers;
};
;

export type investmentInstrumentOverrideCreateResponse = (investmentInstrumentOverrideCreateResponseSuccess)

export const getInvestmentInstrumentOverrideCreateUrl = (symbol: string,) => {




  return `/api/instruments/${symbol}/override`
}

export const investmentInstrumentOverrideCreate = async (symbol: string,
    createInstrumentOverrideDto: CreateInstrumentOverrideDto, options?: RequestInit): Promise<investmentInstrumentOverrideCreateResponse> => {

  return customMutator<investmentInstrumentOverrideCreateResponse>(getInvestmentInstrumentOverrideCreateUrl(symbol),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createInstrumentOverrideDto)
  }
);}





export const getInvestmentInstrumentOverrideCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideCreate>>, TError,{symbol: string;data: CreateInstrumentOverrideDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideCreate>>, TError,{symbol: string;data: CreateInstrumentOverrideDto}, TContext> => {

const mutationKey = ['investmentInstrumentOverrideCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentInstrumentOverrideCreate>>, {symbol: string;data: CreateInstrumentOverrideDto}> = (props) => {
          const {symbol,data} = props ?? {};

          return  investmentInstrumentOverrideCreate(symbol,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentInstrumentOverrideCreateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentOverrideCreate>>>
    export type InvestmentInstrumentOverrideCreateMutationBody = CreateInstrumentOverrideDto
    export type InvestmentInstrumentOverrideCreateMutationError = unknown

    export const useInvestmentInstrumentOverrideCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideCreate>>, TError,{symbol: string;data: CreateInstrumentOverrideDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentInstrumentOverrideCreate>>,
        TError,
        {symbol: string;data: CreateInstrumentOverrideDto},
        TContext
      > => {
      return useMutation(getInvestmentInstrumentOverrideCreateMutationOptions(options), queryClient);
    }

export type investmentInstrumentOverrideListResponse200 = {
  data: InvestmentInstrumentOverrideList200
  status: 200
}

export type investmentInstrumentOverrideListResponseSuccess = (investmentInstrumentOverrideListResponse200) & {
  headers: Headers;
};
;

export type investmentInstrumentOverrideListResponse = (investmentInstrumentOverrideListResponseSuccess)

export const getInvestmentInstrumentOverrideListUrl = (symbol: string,) => {




  return `/api/instruments/${symbol}/override`
}

export const investmentInstrumentOverrideList = async (symbol: string, options?: RequestInit): Promise<investmentInstrumentOverrideListResponse> => {

  return customMutator<investmentInstrumentOverrideListResponse>(getInvestmentInstrumentOverrideListUrl(symbol),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentInstrumentOverrideListQueryKey = (symbol: string,) => {
    return [
    `/api/instruments/${symbol}/override`
    ] as const;
    }


export const getInvestmentInstrumentOverrideListQueryOptions = <TData = Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError = unknown>(symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentInstrumentOverrideListQueryKey(symbol);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentInstrumentOverrideList>>> = ({ signal }) => investmentInstrumentOverrideList(symbol, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: symbol !== null && symbol !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentInstrumentOverrideListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentOverrideList>>>
export type InvestmentInstrumentOverrideListQueryError = unknown


export function useInvestmentInstrumentOverrideList<TData = Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError = unknown>(
 symbol: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentInstrumentOverrideList>>,
          TError,
          Awaited<ReturnType<typeof investmentInstrumentOverrideList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentInstrumentOverrideList<TData = Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentInstrumentOverrideList>>,
          TError,
          Awaited<ReturnType<typeof investmentInstrumentOverrideList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentInstrumentOverrideList<TData = Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentInstrumentOverrideList<TData = Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentInstrumentOverrideListQueryOptions(symbol,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentInstrumentOverrideUpdateResponse200 = {
  data: void
  status: 200
}

export type investmentInstrumentOverrideUpdateResponseSuccess = (investmentInstrumentOverrideUpdateResponse200) & {
  headers: Headers;
};
;

export type investmentInstrumentOverrideUpdateResponse = (investmentInstrumentOverrideUpdateResponseSuccess)

export const getInvestmentInstrumentOverrideUpdateUrl = (symbol: string,
    id: string,) => {




  return `/api/instruments/${symbol}/override/${id}`
}

export const investmentInstrumentOverrideUpdate = async (symbol: string,
    id: string,
    updateInstrumentOverrideDto: UpdateInstrumentOverrideDto, options?: RequestInit): Promise<investmentInstrumentOverrideUpdateResponse> => {

  return customMutator<investmentInstrumentOverrideUpdateResponse>(getInvestmentInstrumentOverrideUpdateUrl(symbol,id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateInstrumentOverrideDto)
  }
);}





export const getInvestmentInstrumentOverrideUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdate>>, TError,{symbol: string;id: string;data: UpdateInstrumentOverrideDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdate>>, TError,{symbol: string;id: string;data: UpdateInstrumentOverrideDto}, TContext> => {

const mutationKey = ['investmentInstrumentOverrideUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdate>>, {symbol: string;id: string;data: UpdateInstrumentOverrideDto}> = (props) => {
          const {symbol,id,data} = props ?? {};

          return  investmentInstrumentOverrideUpdate(symbol,id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentInstrumentOverrideUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdate>>>
    export type InvestmentInstrumentOverrideUpdateMutationBody = UpdateInstrumentOverrideDto
    export type InvestmentInstrumentOverrideUpdateMutationError = unknown

    export const useInvestmentInstrumentOverrideUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdate>>, TError,{symbol: string;id: string;data: UpdateInstrumentOverrideDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentInstrumentOverrideUpdate>>,
        TError,
        {symbol: string;id: string;data: UpdateInstrumentOverrideDto},
        TContext
      > => {
      return useMutation(getInvestmentInstrumentOverrideUpdateMutationOptions(options), queryClient);
    }

export type investmentInstrumentOverrideDeleteResponse200 = {
  data: void
  status: 200
}

export type investmentInstrumentOverrideDeleteResponseSuccess = (investmentInstrumentOverrideDeleteResponse200) & {
  headers: Headers;
};
;

export type investmentInstrumentOverrideDeleteResponse = (investmentInstrumentOverrideDeleteResponseSuccess)

export const getInvestmentInstrumentOverrideDeleteUrl = (symbol: string,
    id: string,) => {




  return `/api/instruments/${symbol}/override/${id}`
}

export const investmentInstrumentOverrideDelete = async (symbol: string,
    id: string, options?: RequestInit): Promise<investmentInstrumentOverrideDeleteResponse> => {

  return customMutator<investmentInstrumentOverrideDeleteResponse>(getInvestmentInstrumentOverrideDeleteUrl(symbol,id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getInvestmentInstrumentOverrideDeleteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideDelete>>, TError,{symbol: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideDelete>>, TError,{symbol: string;id: string}, TContext> => {

const mutationKey = ['investmentInstrumentOverrideDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentInstrumentOverrideDelete>>, {symbol: string;id: string}> = (props) => {
          const {symbol,id} = props ?? {};

          return  investmentInstrumentOverrideDelete(symbol,id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentInstrumentOverrideDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentOverrideDelete>>>

    export type InvestmentInstrumentOverrideDeleteMutationError = unknown

    export const useInvestmentInstrumentOverrideDelete = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideDelete>>, TError,{symbol: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentInstrumentOverrideDelete>>,
        TError,
        {symbol: string;id: string},
        TContext
      > => {
      return useMutation(getInvestmentInstrumentOverrideDeleteMutationOptions(options), queryClient);
    }

export type investmentInstrumentOverrideUpdatePostResponse200 = {
  data: void
  status: 200
}

export type investmentInstrumentOverrideUpdatePostResponseSuccess = (investmentInstrumentOverrideUpdatePostResponse200) & {
  headers: Headers;
};
;

export type investmentInstrumentOverrideUpdatePostResponse = (investmentInstrumentOverrideUpdatePostResponseSuccess)

export const getInvestmentInstrumentOverrideUpdatePostUrl = (symbol: string,
    id: string,) => {




  return `/api/instruments/${symbol}/override/${id}/approve`
}

export const investmentInstrumentOverrideUpdatePost = async (symbol: string,
    id: string,
    decideInstrumentOverrideDto: DecideInstrumentOverrideDto, options?: RequestInit): Promise<investmentInstrumentOverrideUpdatePostResponse> => {

  return customMutator<investmentInstrumentOverrideUpdatePostResponse>(getInvestmentInstrumentOverrideUpdatePostUrl(symbol,id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(decideInstrumentOverrideDto)
  }
);}





export const getInvestmentInstrumentOverrideUpdatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost>>, TError,{symbol: string;id: string;data: DecideInstrumentOverrideDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost>>, TError,{symbol: string;id: string;data: DecideInstrumentOverrideDto}, TContext> => {

const mutationKey = ['investmentInstrumentOverrideUpdatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost>>, {symbol: string;id: string;data: DecideInstrumentOverrideDto}> = (props) => {
          const {symbol,id,data} = props ?? {};

          return  investmentInstrumentOverrideUpdatePost(symbol,id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentInstrumentOverrideUpdatePostMutationResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost>>>
    export type InvestmentInstrumentOverrideUpdatePostMutationBody = DecideInstrumentOverrideDto
    export type InvestmentInstrumentOverrideUpdatePostMutationError = unknown

    export const useInvestmentInstrumentOverrideUpdatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost>>, TError,{symbol: string;id: string;data: DecideInstrumentOverrideDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost>>,
        TError,
        {symbol: string;id: string;data: DecideInstrumentOverrideDto},
        TContext
      > => {
      return useMutation(getInvestmentInstrumentOverrideUpdatePostMutationOptions(options), queryClient);
    }

export type investmentInstrumentOverrideUpdatePost1Response200 = {
  data: void
  status: 200
}

export type investmentInstrumentOverrideUpdatePost1ResponseSuccess = (investmentInstrumentOverrideUpdatePost1Response200) & {
  headers: Headers;
};
;

export type investmentInstrumentOverrideUpdatePost1Response = (investmentInstrumentOverrideUpdatePost1ResponseSuccess)

export const getInvestmentInstrumentOverrideUpdatePost1Url = (symbol: string,
    id: string,) => {




  return `/api/instruments/${symbol}/override/${id}/revoke`
}

export const investmentInstrumentOverrideUpdatePost1 = async (symbol: string,
    id: string,
    decideInstrumentOverrideDto: DecideInstrumentOverrideDto, options?: RequestInit): Promise<investmentInstrumentOverrideUpdatePost1Response> => {

  return customMutator<investmentInstrumentOverrideUpdatePost1Response>(getInvestmentInstrumentOverrideUpdatePost1Url(symbol,id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(decideInstrumentOverrideDto)
  }
);}





export const getInvestmentInstrumentOverrideUpdatePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost1>>, TError,{symbol: string;id: string;data: DecideInstrumentOverrideDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost1>>, TError,{symbol: string;id: string;data: DecideInstrumentOverrideDto}, TContext> => {

const mutationKey = ['investmentInstrumentOverrideUpdatePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost1>>, {symbol: string;id: string;data: DecideInstrumentOverrideDto}> = (props) => {
          const {symbol,id,data} = props ?? {};

          return  investmentInstrumentOverrideUpdatePost1(symbol,id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentInstrumentOverrideUpdatePost1MutationResult = NonNullable<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost1>>>
    export type InvestmentInstrumentOverrideUpdatePost1MutationBody = DecideInstrumentOverrideDto
    export type InvestmentInstrumentOverrideUpdatePost1MutationError = unknown

    export const useInvestmentInstrumentOverrideUpdatePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost1>>, TError,{symbol: string;id: string;data: DecideInstrumentOverrideDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentInstrumentOverrideUpdatePost1>>,
        TError,
        {symbol: string;id: string;data: DecideInstrumentOverrideDto},
        TContext
      > => {
      return useMutation(getInvestmentInstrumentOverrideUpdatePost1MutationOptions(options), queryClient);
    }

export type investmentMarketDataCreateResponse200 = {
  data: void
  status: 200
}

export type investmentMarketDataCreateResponse201 = {
  data: void
  status: 201
}

export type investmentMarketDataCreateResponseSuccess = (investmentMarketDataCreateResponse200 | investmentMarketDataCreateResponse201) & {
  headers: Headers;
};
;

export type investmentMarketDataCreateResponse = (investmentMarketDataCreateResponseSuccess)

export const getInvestmentMarketDataCreateUrl = (symbol: string,) => {




  return `/api/instruments/${symbol}/market-data`
}

export const investmentMarketDataCreate = async (symbol: string,
    createMarketDataDto: CreateMarketDataDto, options?: RequestInit): Promise<investmentMarketDataCreateResponse> => {

  return customMutator<investmentMarketDataCreateResponse>(getInvestmentMarketDataCreateUrl(symbol),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createMarketDataDto)
  }
);}





export const getInvestmentMarketDataCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataCreate>>, TError,{symbol: string;data: CreateMarketDataDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataCreate>>, TError,{symbol: string;data: CreateMarketDataDto}, TContext> => {

const mutationKey = ['investmentMarketDataCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentMarketDataCreate>>, {symbol: string;data: CreateMarketDataDto}> = (props) => {
          const {symbol,data} = props ?? {};

          return  investmentMarketDataCreate(symbol,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentMarketDataCreateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentMarketDataCreate>>>
    export type InvestmentMarketDataCreateMutationBody = CreateMarketDataDto
    export type InvestmentMarketDataCreateMutationError = unknown

    export const useInvestmentMarketDataCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataCreate>>, TError,{symbol: string;data: CreateMarketDataDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentMarketDataCreate>>,
        TError,
        {symbol: string;data: CreateMarketDataDto},
        TContext
      > => {
      return useMutation(getInvestmentMarketDataCreateMutationOptions(options), queryClient);
    }

export type investmentMarketDataListResponse200 = {
  data: InvestmentMarketDataList200
  status: 200
}

export type investmentMarketDataListResponseSuccess = (investmentMarketDataListResponse200) & {
  headers: Headers;
};
;

export type investmentMarketDataListResponse = (investmentMarketDataListResponseSuccess)

export const getInvestmentMarketDataListUrl = (symbol: string,) => {




  return `/api/instruments/${symbol}/market-data`
}

export const investmentMarketDataList = async (symbol: string, options?: RequestInit): Promise<investmentMarketDataListResponse> => {

  return customMutator<investmentMarketDataListResponse>(getInvestmentMarketDataListUrl(symbol),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentMarketDataListQueryKey = (symbol: string,) => {
    return [
    `/api/instruments/${symbol}/market-data`
    ] as const;
    }


export const getInvestmentMarketDataListQueryOptions = <TData = Awaited<ReturnType<typeof investmentMarketDataList>>, TError = unknown>(symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentMarketDataListQueryKey(symbol);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentMarketDataList>>> = ({ signal }) => investmentMarketDataList(symbol, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: symbol !== null && symbol !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentMarketDataListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentMarketDataList>>>
export type InvestmentMarketDataListQueryError = unknown


export function useInvestmentMarketDataList<TData = Awaited<ReturnType<typeof investmentMarketDataList>>, TError = unknown>(
 symbol: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentMarketDataList>>,
          TError,
          Awaited<ReturnType<typeof investmentMarketDataList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentMarketDataList<TData = Awaited<ReturnType<typeof investmentMarketDataList>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentMarketDataList>>,
          TError,
          Awaited<ReturnType<typeof investmentMarketDataList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentMarketDataList<TData = Awaited<ReturnType<typeof investmentMarketDataList>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentMarketDataList<TData = Awaited<ReturnType<typeof investmentMarketDataList>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentMarketDataListQueryOptions(symbol,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentMarketDataListGetResponse200 = {
  data: InvestmentMarketDataListGet200
  status: 200
}

export type investmentMarketDataListGetResponseSuccess = (investmentMarketDataListGetResponse200) & {
  headers: Headers;
};
;

export type investmentMarketDataListGetResponse = (investmentMarketDataListGetResponseSuccess)

export const getInvestmentMarketDataListGetUrl = (symbol: string,) => {




  return `/api/instruments/${symbol}/market-data/resolved`
}

export const investmentMarketDataListGet = async (symbol: string, options?: RequestInit): Promise<investmentMarketDataListGetResponse> => {

  return customMutator<investmentMarketDataListGetResponse>(getInvestmentMarketDataListGetUrl(symbol),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentMarketDataListGetQueryKey = (symbol: string,) => {
    return [
    `/api/instruments/${symbol}/market-data/resolved`
    ] as const;
    }


export const getInvestmentMarketDataListGetQueryOptions = <TData = Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError = unknown>(symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentMarketDataListGetQueryKey(symbol);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentMarketDataListGet>>> = ({ signal }) => investmentMarketDataListGet(symbol, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: symbol !== null && symbol !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentMarketDataListGetQueryResult = NonNullable<Awaited<ReturnType<typeof investmentMarketDataListGet>>>
export type InvestmentMarketDataListGetQueryError = unknown


export function useInvestmentMarketDataListGet<TData = Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError = unknown>(
 symbol: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentMarketDataListGet>>,
          TError,
          Awaited<ReturnType<typeof investmentMarketDataListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentMarketDataListGet<TData = Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentMarketDataListGet>>,
          TError,
          Awaited<ReturnType<typeof investmentMarketDataListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentMarketDataListGet<TData = Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentMarketDataListGet<TData = Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError = unknown>(
 symbol: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataListGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentMarketDataListGetQueryOptions(symbol,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentMarketDataGetResponse200 = {
  data: InvestmentMarketDataGet200
  status: 200
}

export type investmentMarketDataGetResponseSuccess = (investmentMarketDataGetResponse200) & {
  headers: Headers;
};
;

export type investmentMarketDataGetResponse = (investmentMarketDataGetResponseSuccess)

export const getInvestmentMarketDataGetUrl = (id: string,) => {




  return `/api/instruments/market-data/${id}`
}

export const investmentMarketDataGet = async (id: string, options?: RequestInit): Promise<investmentMarketDataGetResponse> => {

  return customMutator<investmentMarketDataGetResponse>(getInvestmentMarketDataGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentMarketDataGetQueryKey = (id: string,) => {
    return [
    `/api/instruments/market-data/${id}`
    ] as const;
    }


export const getInvestmentMarketDataGetQueryOptions = <TData = Awaited<ReturnType<typeof investmentMarketDataGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentMarketDataGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentMarketDataGet>>> = ({ signal }) => investmentMarketDataGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentMarketDataGetQueryResult = NonNullable<Awaited<ReturnType<typeof investmentMarketDataGet>>>
export type InvestmentMarketDataGetQueryError = unknown


export function useInvestmentMarketDataGet<TData = Awaited<ReturnType<typeof investmentMarketDataGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentMarketDataGet>>,
          TError,
          Awaited<ReturnType<typeof investmentMarketDataGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentMarketDataGet<TData = Awaited<ReturnType<typeof investmentMarketDataGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentMarketDataGet>>,
          TError,
          Awaited<ReturnType<typeof investmentMarketDataGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentMarketDataGet<TData = Awaited<ReturnType<typeof investmentMarketDataGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentMarketDataGet<TData = Awaited<ReturnType<typeof investmentMarketDataGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentMarketDataGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentMarketDataGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentMarketDataUpdateResponse200 = {
  data: void
  status: 200
}

export type investmentMarketDataUpdateResponseSuccess = (investmentMarketDataUpdateResponse200) & {
  headers: Headers;
};
;

export type investmentMarketDataUpdateResponse = (investmentMarketDataUpdateResponseSuccess)

export const getInvestmentMarketDataUpdateUrl = (id: string,) => {




  return `/api/instruments/market-data/${id}`
}

export const investmentMarketDataUpdate = async (id: string,
    updateMarketDataDto: UpdateMarketDataDto, options?: RequestInit): Promise<investmentMarketDataUpdateResponse> => {

  return customMutator<investmentMarketDataUpdateResponse>(getInvestmentMarketDataUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateMarketDataDto)
  }
);}





export const getInvestmentMarketDataUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataUpdate>>, TError,{id: string;data: UpdateMarketDataDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataUpdate>>, TError,{id: string;data: UpdateMarketDataDto}, TContext> => {

const mutationKey = ['investmentMarketDataUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentMarketDataUpdate>>, {id: string;data: UpdateMarketDataDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentMarketDataUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentMarketDataUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentMarketDataUpdate>>>
    export type InvestmentMarketDataUpdateMutationBody = UpdateMarketDataDto
    export type InvestmentMarketDataUpdateMutationError = unknown

    export const useInvestmentMarketDataUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataUpdate>>, TError,{id: string;data: UpdateMarketDataDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentMarketDataUpdate>>,
        TError,
        {id: string;data: UpdateMarketDataDto},
        TContext
      > => {
      return useMutation(getInvestmentMarketDataUpdateMutationOptions(options), queryClient);
    }

export type investmentMarketDataDeleteResponse200 = {
  data: void
  status: 200
}

export type investmentMarketDataDeleteResponseSuccess = (investmentMarketDataDeleteResponse200) & {
  headers: Headers;
};
;

export type investmentMarketDataDeleteResponse = (investmentMarketDataDeleteResponseSuccess)

export const getInvestmentMarketDataDeleteUrl = (id: string,) => {




  return `/api/instruments/market-data/${id}`
}

export const investmentMarketDataDelete = async (id: string, options?: RequestInit): Promise<investmentMarketDataDeleteResponse> => {

  return customMutator<investmentMarketDataDeleteResponse>(getInvestmentMarketDataDeleteUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getInvestmentMarketDataDeleteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataDelete>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataDelete>>, TError,{id: string}, TContext> => {

const mutationKey = ['investmentMarketDataDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentMarketDataDelete>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  investmentMarketDataDelete(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentMarketDataDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof investmentMarketDataDelete>>>

    export type InvestmentMarketDataDeleteMutationError = unknown

    export const useInvestmentMarketDataDelete = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataDelete>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentMarketDataDelete>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getInvestmentMarketDataDeleteMutationOptions(options), queryClient);
    }

export type investmentMarketDataBulkUpsertResponse200 = {
  data: void
  status: 200
}

export type investmentMarketDataBulkUpsertResponseSuccess = (investmentMarketDataBulkUpsertResponse200) & {
  headers: Headers;
};
;

export type investmentMarketDataBulkUpsertResponse = (investmentMarketDataBulkUpsertResponseSuccess)

export const getInvestmentMarketDataBulkUpsertUrl = () => {




  return `/api/instruments/market-data/bulk-upsert`
}

export const investmentMarketDataBulkUpsert = async (bulkUpsertMarketDataDto: BulkUpsertMarketDataDto, options?: RequestInit): Promise<investmentMarketDataBulkUpsertResponse> => {

  return customMutator<investmentMarketDataBulkUpsertResponse>(getInvestmentMarketDataBulkUpsertUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(bulkUpsertMarketDataDto)
  }
);}





export const getInvestmentMarketDataBulkUpsertMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataBulkUpsert>>, TError,{data: BulkUpsertMarketDataDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataBulkUpsert>>, TError,{data: BulkUpsertMarketDataDto}, TContext> => {

const mutationKey = ['investmentMarketDataBulkUpsert'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentMarketDataBulkUpsert>>, {data: BulkUpsertMarketDataDto}> = (props) => {
          const {data} = props ?? {};

          return  investmentMarketDataBulkUpsert(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentMarketDataBulkUpsertMutationResult = NonNullable<Awaited<ReturnType<typeof investmentMarketDataBulkUpsert>>>
    export type InvestmentMarketDataBulkUpsertMutationBody = BulkUpsertMarketDataDto
    export type InvestmentMarketDataBulkUpsertMutationError = unknown

    export const useInvestmentMarketDataBulkUpsert = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentMarketDataBulkUpsert>>, TError,{data: BulkUpsertMarketDataDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentMarketDataBulkUpsert>>,
        TError,
        {data: BulkUpsertMarketDataDto},
        TContext
      > => {
      return useMutation(getInvestmentMarketDataBulkUpsertMutationOptions(options), queryClient);
    }

export type investmentTransactionCreateResponse201 = {
  data: InvestmentTransactionCreate201
  status: 201
}

export type investmentTransactionCreateResponseSuccess = (investmentTransactionCreateResponse201) & {
  headers: Headers;
};
;

export type investmentTransactionCreateResponse = (investmentTransactionCreateResponseSuccess)

export const getInvestmentTransactionCreateUrl = () => {




  return `/api/transactions`
}

export const investmentTransactionCreate = async (createTransactionDto: CreateTransactionDto, options?: RequestInit): Promise<investmentTransactionCreateResponse> => {

  return customMutator<investmentTransactionCreateResponse>(getInvestmentTransactionCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createTransactionDto)
  }
);}





export const getInvestmentTransactionCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionCreate>>, TError,{data: CreateTransactionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionCreate>>, TError,{data: CreateTransactionDto}, TContext> => {

const mutationKey = ['investmentTransactionCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionCreate>>, {data: CreateTransactionDto}> = (props) => {
          const {data} = props ?? {};

          return  investmentTransactionCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionCreateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionCreate>>>
    export type InvestmentTransactionCreateMutationBody = CreateTransactionDto
    export type InvestmentTransactionCreateMutationError = unknown

    export const useInvestmentTransactionCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionCreate>>, TError,{data: CreateTransactionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionCreate>>,
        TError,
        {data: CreateTransactionDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionCreateMutationOptions(options), queryClient);
    }

export type investmentTransactionListResponse200 = {
  data: InvestmentTransactionList200
  status: 200
}

export type investmentTransactionListResponseSuccess = (investmentTransactionListResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionListResponse = (investmentTransactionListResponseSuccess)

export const getInvestmentTransactionListUrl = () => {




  return `/api/transactions`
}

export const investmentTransactionList = async ( options?: RequestInit): Promise<investmentTransactionListResponse> => {

  return customMutator<investmentTransactionListResponse>(getInvestmentTransactionListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentTransactionListQueryKey = () => {
    return [
    `/api/transactions`
    ] as const;
    }


export const getInvestmentTransactionListQueryOptions = <TData = Awaited<ReturnType<typeof investmentTransactionList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentTransactionListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentTransactionList>>> = ({ signal }) => investmentTransactionList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentTransactionListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionList>>>
export type InvestmentTransactionListQueryError = unknown


export function useInvestmentTransactionList<TData = Awaited<ReturnType<typeof investmentTransactionList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionList>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionList<TData = Awaited<ReturnType<typeof investmentTransactionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionList>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionList<TData = Awaited<ReturnType<typeof investmentTransactionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentTransactionList<TData = Awaited<ReturnType<typeof investmentTransactionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentTransactionListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentTransactionSubmitResponse200 = {
  data: InvestmentTransactionSubmit200
  status: 200
}

export type investmentTransactionSubmitResponseSuccess = (investmentTransactionSubmitResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionSubmitResponse = (investmentTransactionSubmitResponseSuccess)

export const getInvestmentTransactionSubmitUrl = (id: string,) => {




  return `/api/transactions/${id}/submit`
}

export const investmentTransactionSubmit = async (id: string,
    lifecycleCommandDto?: LifecycleCommandDto, options?: RequestInit): Promise<investmentTransactionSubmitResponse> => {

  return customMutator<investmentTransactionSubmitResponse>(getInvestmentTransactionSubmitUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(lifecycleCommandDto)
  }
);}





export const getInvestmentTransactionSubmitMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionSubmit>>, TError,{id: string;data?: LifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionSubmit>>, TError,{id: string;data?: LifecycleCommandDto}, TContext> => {

const mutationKey = ['investmentTransactionSubmit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionSubmit>>, {id: string;data?: LifecycleCommandDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentTransactionSubmit(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionSubmitMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionSubmit>>>
    export type InvestmentTransactionSubmitMutationBody = LifecycleCommandDto | undefined
    export type InvestmentTransactionSubmitMutationError = unknown

    export const useInvestmentTransactionSubmit = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionSubmit>>, TError,{id: string;data?: LifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionSubmit>>,
        TError,
        {id: string;data?: LifecycleCommandDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionSubmitMutationOptions(options), queryClient);
    }

export type investmentTransactionApprovePostResponse200 = {
  data: InvestmentTransactionApprovePost200
  status: 200
}

export type investmentTransactionApprovePostResponseSuccess = (investmentTransactionApprovePostResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionApprovePostResponse = (investmentTransactionApprovePostResponseSuccess)

export const getInvestmentTransactionApprovePostUrl = (id: string,) => {




  return `/api/transactions/${id}/approve`
}

export const investmentTransactionApprovePost = async (id: string,
    lifecycleCommandDto?: LifecycleCommandDto, options?: RequestInit): Promise<investmentTransactionApprovePostResponse> => {

  return customMutator<investmentTransactionApprovePostResponse>(getInvestmentTransactionApprovePostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(lifecycleCommandDto)
  }
);}





export const getInvestmentTransactionApprovePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionApprovePost>>, TError,{id: string;data?: LifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionApprovePost>>, TError,{id: string;data?: LifecycleCommandDto}, TContext> => {

const mutationKey = ['investmentTransactionApprovePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionApprovePost>>, {id: string;data?: LifecycleCommandDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentTransactionApprovePost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionApprovePostMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionApprovePost>>>
    export type InvestmentTransactionApprovePostMutationBody = LifecycleCommandDto | undefined
    export type InvestmentTransactionApprovePostMutationError = unknown

    export const useInvestmentTransactionApprovePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionApprovePost>>, TError,{id: string;data?: LifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionApprovePost>>,
        TError,
        {id: string;data?: LifecycleCommandDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionApprovePostMutationOptions(options), queryClient);
    }

export type investmentTransactionRejectResponse200 = {
  data: InvestmentTransactionReject200
  status: 200
}

export type investmentTransactionRejectResponseSuccess = (investmentTransactionRejectResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionRejectResponse = (investmentTransactionRejectResponseSuccess)

export const getInvestmentTransactionRejectUrl = (id: string,) => {




  return `/api/transactions/${id}/reject`
}

export const investmentTransactionReject = async (id: string,
    reasonedLifecycleCommandDto: ReasonedLifecycleCommandDto, options?: RequestInit): Promise<investmentTransactionRejectResponse> => {

  return customMutator<investmentTransactionRejectResponse>(getInvestmentTransactionRejectUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(reasonedLifecycleCommandDto)
  }
);}





export const getInvestmentTransactionRejectMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionReject>>, TError,{id: string;data: ReasonedLifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionReject>>, TError,{id: string;data: ReasonedLifecycleCommandDto}, TContext> => {

const mutationKey = ['investmentTransactionReject'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionReject>>, {id: string;data: ReasonedLifecycleCommandDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentTransactionReject(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionRejectMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionReject>>>
    export type InvestmentTransactionRejectMutationBody = ReasonedLifecycleCommandDto
    export type InvestmentTransactionRejectMutationError = unknown

    export const useInvestmentTransactionReject = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionReject>>, TError,{id: string;data: ReasonedLifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionReject>>,
        TError,
        {id: string;data: ReasonedLifecycleCommandDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionRejectMutationOptions(options), queryClient);
    }

export type investmentTransactionWithdrawResponse200 = {
  data: InvestmentTransactionWithdraw200
  status: 200
}

export type investmentTransactionWithdrawResponseSuccess = (investmentTransactionWithdrawResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionWithdrawResponse = (investmentTransactionWithdrawResponseSuccess)

export const getInvestmentTransactionWithdrawUrl = (id: string,) => {




  return `/api/transactions/${id}/withdraw`
}

export const investmentTransactionWithdraw = async (id: string,
    reasonedLifecycleCommandDto: ReasonedLifecycleCommandDto, options?: RequestInit): Promise<investmentTransactionWithdrawResponse> => {

  return customMutator<investmentTransactionWithdrawResponse>(getInvestmentTransactionWithdrawUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(reasonedLifecycleCommandDto)
  }
);}





export const getInvestmentTransactionWithdrawMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionWithdraw>>, TError,{id: string;data: ReasonedLifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionWithdraw>>, TError,{id: string;data: ReasonedLifecycleCommandDto}, TContext> => {

const mutationKey = ['investmentTransactionWithdraw'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionWithdraw>>, {id: string;data: ReasonedLifecycleCommandDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentTransactionWithdraw(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionWithdrawMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionWithdraw>>>
    export type InvestmentTransactionWithdrawMutationBody = ReasonedLifecycleCommandDto
    export type InvestmentTransactionWithdrawMutationError = unknown

    export const useInvestmentTransactionWithdraw = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionWithdraw>>, TError,{id: string;data: ReasonedLifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionWithdraw>>,
        TError,
        {id: string;data: ReasonedLifecycleCommandDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionWithdrawMutationOptions(options), queryClient);
    }

export type investmentTransactionPostPostResponse200 = {
  data: InvestmentTransactionPostPost200
  status: 200
}

export type investmentTransactionPostPostResponseSuccess = (investmentTransactionPostPostResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionPostPostResponse = (investmentTransactionPostPostResponseSuccess)

export const getInvestmentTransactionPostPostUrl = (id: string,) => {




  return `/api/transactions/${id}/post`
}

export const investmentTransactionPostPost = async (id: string,
    lifecycleCommandDto?: LifecycleCommandDto, options?: RequestInit): Promise<investmentTransactionPostPostResponse> => {

  return customMutator<investmentTransactionPostPostResponse>(getInvestmentTransactionPostPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(lifecycleCommandDto)
  }
);}





export const getInvestmentTransactionPostPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionPostPost>>, TError,{id: string;data?: LifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionPostPost>>, TError,{id: string;data?: LifecycleCommandDto}, TContext> => {

const mutationKey = ['investmentTransactionPostPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionPostPost>>, {id: string;data?: LifecycleCommandDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentTransactionPostPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionPostPostMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionPostPost>>>
    export type InvestmentTransactionPostPostMutationBody = LifecycleCommandDto | undefined
    export type InvestmentTransactionPostPostMutationError = unknown

    export const useInvestmentTransactionPostPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionPostPost>>, TError,{id: string;data?: LifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionPostPost>>,
        TError,
        {id: string;data?: LifecycleCommandDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionPostPostMutationOptions(options), queryClient);
    }

export type investmentTransactionReversePostResponse200 = {
  data: InvestmentTransactionReversePost200
  status: 200
}

export type investmentTransactionReversePostResponseSuccess = (investmentTransactionReversePostResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionReversePostResponse = (investmentTransactionReversePostResponseSuccess)

export const getInvestmentTransactionReversePostUrl = (id: string,) => {




  return `/api/transactions/${id}/reverse`
}

export const investmentTransactionReversePost = async (id: string,
    reverseTransactionDto: ReverseTransactionDto, options?: RequestInit): Promise<investmentTransactionReversePostResponse> => {

  return customMutator<investmentTransactionReversePostResponse>(getInvestmentTransactionReversePostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(reverseTransactionDto)
  }
);}





export const getInvestmentTransactionReversePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionReversePost>>, TError,{id: string;data: ReverseTransactionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionReversePost>>, TError,{id: string;data: ReverseTransactionDto}, TContext> => {

const mutationKey = ['investmentTransactionReversePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionReversePost>>, {id: string;data: ReverseTransactionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentTransactionReversePost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionReversePostMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionReversePost>>>
    export type InvestmentTransactionReversePostMutationBody = ReverseTransactionDto
    export type InvestmentTransactionReversePostMutationError = unknown

    export const useInvestmentTransactionReversePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionReversePost>>, TError,{id: string;data: ReverseTransactionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionReversePost>>,
        TError,
        {id: string;data: ReverseTransactionDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionReversePostMutationOptions(options), queryClient);
    }

export type investmentTransactionRepairResponse200 = {
  data: InvestmentTransactionRepair200
  status: 200
}

export type investmentTransactionRepairResponseSuccess = (investmentTransactionRepairResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionRepairResponse = (investmentTransactionRepairResponseSuccess)

export const getInvestmentTransactionRepairUrl = (id: string,) => {




  return `/api/transactions/${id}/repair`
}

export const investmentTransactionRepair = async (id: string,
    repairTransactionDto: RepairTransactionDto, options?: RequestInit): Promise<investmentTransactionRepairResponse> => {

  return customMutator<investmentTransactionRepairResponse>(getInvestmentTransactionRepairUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(repairTransactionDto)
  }
);}





export const getInvestmentTransactionRepairMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionRepair>>, TError,{id: string;data: RepairTransactionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionRepair>>, TError,{id: string;data: RepairTransactionDto}, TContext> => {

const mutationKey = ['investmentTransactionRepair'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionRepair>>, {id: string;data: RepairTransactionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentTransactionRepair(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionRepairMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionRepair>>>
    export type InvestmentTransactionRepairMutationBody = RepairTransactionDto
    export type InvestmentTransactionRepairMutationError = unknown

    export const useInvestmentTransactionRepair = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionRepair>>, TError,{id: string;data: RepairTransactionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionRepair>>,
        TError,
        {id: string;data: RepairTransactionDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionRepairMutationOptions(options), queryClient);
    }

export type investmentTransactionRepostResponse200 = {
  data: InvestmentTransactionRepost200
  status: 200
}

export type investmentTransactionRepostResponseSuccess = (investmentTransactionRepostResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionRepostResponse = (investmentTransactionRepostResponseSuccess)

export const getInvestmentTransactionRepostUrl = (id: string,) => {




  return `/api/transactions/${id}/repost`
}

export const investmentTransactionRepost = async (id: string,
    lifecycleCommandDto?: LifecycleCommandDto, options?: RequestInit): Promise<investmentTransactionRepostResponse> => {

  return customMutator<investmentTransactionRepostResponse>(getInvestmentTransactionRepostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(lifecycleCommandDto)
  }
);}





export const getInvestmentTransactionRepostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionRepost>>, TError,{id: string;data?: LifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionRepost>>, TError,{id: string;data?: LifecycleCommandDto}, TContext> => {

const mutationKey = ['investmentTransactionRepost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionRepost>>, {id: string;data?: LifecycleCommandDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentTransactionRepost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionRepostMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionRepost>>>
    export type InvestmentTransactionRepostMutationBody = LifecycleCommandDto | undefined
    export type InvestmentTransactionRepostMutationError = unknown

    export const useInvestmentTransactionRepost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionRepost>>, TError,{id: string;data?: LifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionRepost>>,
        TError,
        {id: string;data?: LifecycleCommandDto},
        TContext
      > => {
      return useMutation(getInvestmentTransactionRepostMutationOptions(options), queryClient);
    }

export type investmentTransactionExportResponse200 = {
  data: InvestmentTransactionExport200
  status: 200
}

export type investmentTransactionExportResponseSuccess = (investmentTransactionExportResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionExportResponse = (investmentTransactionExportResponseSuccess)

export const getInvestmentTransactionExportUrl = () => {




  return `/api/transactions/export`
}

export const investmentTransactionExport = async ( options?: RequestInit): Promise<investmentTransactionExportResponse> => {

  return customMutator<investmentTransactionExportResponse>(getInvestmentTransactionExportUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getInvestmentTransactionExportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionExport>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionExport>>, TError,void, TContext> => {

const mutationKey = ['investmentTransactionExport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentTransactionExport>>, void> = () => {


          return  investmentTransactionExport(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentTransactionExportMutationResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionExport>>>

    export type InvestmentTransactionExportMutationError = unknown

    export const useInvestmentTransactionExport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentTransactionExport>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentTransactionExport>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getInvestmentTransactionExportMutationOptions(options), queryClient);
    }

export type investmentTransactionGetResponse200 = {
  data: InvestmentTransactionGet200
  status: 200
}

export type investmentTransactionGetResponseSuccess = (investmentTransactionGetResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionGetResponse = (investmentTransactionGetResponseSuccess)

export const getInvestmentTransactionGetUrl = (id: string,) => {




  return `/api/transactions/${id}`
}

export const investmentTransactionGet = async (id: string, options?: RequestInit): Promise<investmentTransactionGetResponse> => {

  return customMutator<investmentTransactionGetResponse>(getInvestmentTransactionGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentTransactionGetQueryKey = (id: string,) => {
    return [
    `/api/transactions/${id}`
    ] as const;
    }


export const getInvestmentTransactionGetQueryOptions = <TData = Awaited<ReturnType<typeof investmentTransactionGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentTransactionGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentTransactionGet>>> = ({ signal }) => investmentTransactionGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentTransactionGetQueryResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionGet>>>
export type InvestmentTransactionGetQueryError = unknown


export function useInvestmentTransactionGet<TData = Awaited<ReturnType<typeof investmentTransactionGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionGet>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionGet<TData = Awaited<ReturnType<typeof investmentTransactionGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionGet>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionGet<TData = Awaited<ReturnType<typeof investmentTransactionGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentTransactionGet<TData = Awaited<ReturnType<typeof investmentTransactionGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentTransactionGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentTransactionLotsResponse200 = {
  data: InvestmentTransactionLots200
  status: 200
}

export type investmentTransactionLotsResponseSuccess = (investmentTransactionLotsResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionLotsResponse = (investmentTransactionLotsResponseSuccess)

export const getInvestmentTransactionLotsUrl = (id: string,) => {




  return `/api/transactions/${id}/lots`
}

export const investmentTransactionLots = async (id: string, options?: RequestInit): Promise<investmentTransactionLotsResponse> => {

  return customMutator<investmentTransactionLotsResponse>(getInvestmentTransactionLotsUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentTransactionLotsQueryKey = (id: string,) => {
    return [
    `/api/transactions/${id}/lots`
    ] as const;
    }


export const getInvestmentTransactionLotsQueryOptions = <TData = Awaited<ReturnType<typeof investmentTransactionLots>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionLots>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentTransactionLotsQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentTransactionLots>>> = ({ signal }) => investmentTransactionLots(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionLots>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentTransactionLotsQueryResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionLots>>>
export type InvestmentTransactionLotsQueryError = unknown


export function useInvestmentTransactionLots<TData = Awaited<ReturnType<typeof investmentTransactionLots>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionLots>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionLots>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionLots>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionLots<TData = Awaited<ReturnType<typeof investmentTransactionLots>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionLots>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionLots>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionLots>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionLots<TData = Awaited<ReturnType<typeof investmentTransactionLots>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionLots>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentTransactionLots<TData = Awaited<ReturnType<typeof investmentTransactionLots>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionLots>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentTransactionLotsQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentTransactionDisposalsResponse200 = {
  data: InvestmentTransactionDisposals200
  status: 200
}

export type investmentTransactionDisposalsResponseSuccess = (investmentTransactionDisposalsResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionDisposalsResponse = (investmentTransactionDisposalsResponseSuccess)

export const getInvestmentTransactionDisposalsUrl = (id: string,) => {




  return `/api/transactions/${id}/disposals`
}

export const investmentTransactionDisposals = async (id: string, options?: RequestInit): Promise<investmentTransactionDisposalsResponse> => {

  return customMutator<investmentTransactionDisposalsResponse>(getInvestmentTransactionDisposalsUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentTransactionDisposalsQueryKey = (id: string,) => {
    return [
    `/api/transactions/${id}/disposals`
    ] as const;
    }


export const getInvestmentTransactionDisposalsQueryOptions = <TData = Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentTransactionDisposalsQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentTransactionDisposals>>> = ({ signal }) => investmentTransactionDisposals(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentTransactionDisposalsQueryResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionDisposals>>>
export type InvestmentTransactionDisposalsQueryError = unknown


export function useInvestmentTransactionDisposals<TData = Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionDisposals>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionDisposals>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionDisposals<TData = Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionDisposals>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionDisposals>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionDisposals<TData = Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentTransactionDisposals<TData = Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionDisposals>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentTransactionDisposalsQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentTransactionFactsResponse200 = {
  data: InvestmentTransactionFacts200
  status: 200
}

export type investmentTransactionFactsResponseSuccess = (investmentTransactionFactsResponse200) & {
  headers: Headers;
};
;

export type investmentTransactionFactsResponse = (investmentTransactionFactsResponseSuccess)

export const getInvestmentTransactionFactsUrl = (id: string,) => {




  return `/api/transactions/${id}/facts`
}

export const investmentTransactionFacts = async (id: string, options?: RequestInit): Promise<investmentTransactionFactsResponse> => {

  return customMutator<investmentTransactionFactsResponse>(getInvestmentTransactionFactsUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentTransactionFactsQueryKey = (id: string,) => {
    return [
    `/api/transactions/${id}/facts`
    ] as const;
    }


export const getInvestmentTransactionFactsQueryOptions = <TData = Awaited<ReturnType<typeof investmentTransactionFacts>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionFacts>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentTransactionFactsQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentTransactionFacts>>> = ({ signal }) => investmentTransactionFacts(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionFacts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentTransactionFactsQueryResult = NonNullable<Awaited<ReturnType<typeof investmentTransactionFacts>>>
export type InvestmentTransactionFactsQueryError = unknown


export function useInvestmentTransactionFacts<TData = Awaited<ReturnType<typeof investmentTransactionFacts>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionFacts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionFacts>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionFacts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionFacts<TData = Awaited<ReturnType<typeof investmentTransactionFacts>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionFacts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentTransactionFacts>>,
          TError,
          Awaited<ReturnType<typeof investmentTransactionFacts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentTransactionFacts<TData = Awaited<ReturnType<typeof investmentTransactionFacts>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionFacts>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentTransactionFacts<TData = Awaited<ReturnType<typeof investmentTransactionFacts>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentTransactionFacts>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentTransactionFactsQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type settlementInstructionsListResponse200 = {
  data: void
  status: 200
}

export type settlementInstructionsListResponseSuccess = (settlementInstructionsListResponse200) & {
  headers: Headers;
};
;

export type settlementInstructionsListResponse = (settlementInstructionsListResponseSuccess)

export const getSettlementInstructionsListUrl = () => {




  return `/api/settlement-instructions`
}

export const settlementInstructionsList = async ( options?: RequestInit): Promise<settlementInstructionsListResponse> => {

  return customMutator<settlementInstructionsListResponse>(getSettlementInstructionsListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSettlementInstructionsListQueryKey = () => {
    return [
    `/api/settlement-instructions`
    ] as const;
    }


export const getSettlementInstructionsListQueryOptions = <TData = Awaited<ReturnType<typeof settlementInstructionsList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSettlementInstructionsListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof settlementInstructionsList>>> = ({ signal }) => settlementInstructionsList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SettlementInstructionsListQueryResult = NonNullable<Awaited<ReturnType<typeof settlementInstructionsList>>>
export type SettlementInstructionsListQueryError = unknown


export function useSettlementInstructionsList<TData = Awaited<ReturnType<typeof settlementInstructionsList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof settlementInstructionsList>>,
          TError,
          Awaited<ReturnType<typeof settlementInstructionsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettlementInstructionsList<TData = Awaited<ReturnType<typeof settlementInstructionsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof settlementInstructionsList>>,
          TError,
          Awaited<ReturnType<typeof settlementInstructionsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettlementInstructionsList<TData = Awaited<ReturnType<typeof settlementInstructionsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useSettlementInstructionsList<TData = Awaited<ReturnType<typeof settlementInstructionsList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSettlementInstructionsListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type settlementInstructionsCreateResponse201 = {
  data: void
  status: 201
}

export type settlementInstructionsCreateResponseSuccess = (settlementInstructionsCreateResponse201) & {
  headers: Headers;
};
;

export type settlementInstructionsCreateResponse = (settlementInstructionsCreateResponseSuccess)

export const getSettlementInstructionsCreateUrl = () => {




  return `/api/settlement-instructions`
}

export const settlementInstructionsCreate = async ( options?: RequestInit): Promise<settlementInstructionsCreateResponse> => {

  return customMutator<settlementInstructionsCreateResponse>(getSettlementInstructionsCreateUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSettlementInstructionsCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsCreate>>, TError,void, TContext> => {

const mutationKey = ['settlementInstructionsCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof settlementInstructionsCreate>>, void> = () => {


          return  settlementInstructionsCreate(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SettlementInstructionsCreateMutationResult = NonNullable<Awaited<ReturnType<typeof settlementInstructionsCreate>>>

    export type SettlementInstructionsCreateMutationError = unknown

    export const useSettlementInstructionsCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof settlementInstructionsCreate>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getSettlementInstructionsCreateMutationOptions(options), queryClient);
    }

export type settlementFailuresListResponse200 = {
  data: void
  status: 200
}

export type settlementFailuresListResponseSuccess = (settlementFailuresListResponse200) & {
  headers: Headers;
};
;

export type settlementFailuresListResponse = (settlementFailuresListResponseSuccess)

export const getSettlementFailuresListUrl = () => {




  return `/api/settlement-instructions/failures`
}

export const settlementFailuresList = async ( options?: RequestInit): Promise<settlementFailuresListResponse> => {

  return customMutator<settlementFailuresListResponse>(getSettlementFailuresListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSettlementFailuresListQueryKey = () => {
    return [
    `/api/settlement-instructions/failures`
    ] as const;
    }


export const getSettlementFailuresListQueryOptions = <TData = Awaited<ReturnType<typeof settlementFailuresList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementFailuresList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSettlementFailuresListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof settlementFailuresList>>> = ({ signal }) => settlementFailuresList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof settlementFailuresList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SettlementFailuresListQueryResult = NonNullable<Awaited<ReturnType<typeof settlementFailuresList>>>
export type SettlementFailuresListQueryError = unknown


export function useSettlementFailuresList<TData = Awaited<ReturnType<typeof settlementFailuresList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementFailuresList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof settlementFailuresList>>,
          TError,
          Awaited<ReturnType<typeof settlementFailuresList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettlementFailuresList<TData = Awaited<ReturnType<typeof settlementFailuresList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementFailuresList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof settlementFailuresList>>,
          TError,
          Awaited<ReturnType<typeof settlementFailuresList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettlementFailuresList<TData = Awaited<ReturnType<typeof settlementFailuresList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementFailuresList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useSettlementFailuresList<TData = Awaited<ReturnType<typeof settlementFailuresList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementFailuresList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSettlementFailuresListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type settlementInstructionsGetResponse200 = {
  data: void
  status: 200
}

export type settlementInstructionsGetResponseSuccess = (settlementInstructionsGetResponse200) & {
  headers: Headers;
};
;

export type settlementInstructionsGetResponse = (settlementInstructionsGetResponseSuccess)

export const getSettlementInstructionsGetUrl = (id: string,) => {




  return `/api/settlement-instructions/${id}`
}

export const settlementInstructionsGet = async (id: string, options?: RequestInit): Promise<settlementInstructionsGetResponse> => {

  return customMutator<settlementInstructionsGetResponse>(getSettlementInstructionsGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSettlementInstructionsGetQueryKey = (id: string,) => {
    return [
    `/api/settlement-instructions/${id}`
    ] as const;
    }


export const getSettlementInstructionsGetQueryOptions = <TData = Awaited<ReturnType<typeof settlementInstructionsGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSettlementInstructionsGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof settlementInstructionsGet>>> = ({ signal }) => settlementInstructionsGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SettlementInstructionsGetQueryResult = NonNullable<Awaited<ReturnType<typeof settlementInstructionsGet>>>
export type SettlementInstructionsGetQueryError = unknown


export function useSettlementInstructionsGet<TData = Awaited<ReturnType<typeof settlementInstructionsGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof settlementInstructionsGet>>,
          TError,
          Awaited<ReturnType<typeof settlementInstructionsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettlementInstructionsGet<TData = Awaited<ReturnType<typeof settlementInstructionsGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof settlementInstructionsGet>>,
          TError,
          Awaited<ReturnType<typeof settlementInstructionsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettlementInstructionsGet<TData = Awaited<ReturnType<typeof settlementInstructionsGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useSettlementInstructionsGet<TData = Awaited<ReturnType<typeof settlementInstructionsGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSettlementInstructionsGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type settlementInstructionsAvailabilityResponse200 = {
  data: void
  status: 200
}

export type settlementInstructionsAvailabilityResponseSuccess = (settlementInstructionsAvailabilityResponse200) & {
  headers: Headers;
};
;

export type settlementInstructionsAvailabilityResponse = (settlementInstructionsAvailabilityResponseSuccess)

export const getSettlementInstructionsAvailabilityUrl = (id: string,) => {




  return `/api/settlement-instructions/${id}/availability`
}

export const settlementInstructionsAvailability = async (id: string, options?: RequestInit): Promise<settlementInstructionsAvailabilityResponse> => {

  return customMutator<settlementInstructionsAvailabilityResponse>(getSettlementInstructionsAvailabilityUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSettlementInstructionsAvailabilityQueryKey = (id: string,) => {
    return [
    `/api/settlement-instructions/${id}/availability`
    ] as const;
    }


export const getSettlementInstructionsAvailabilityQueryOptions = <TData = Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSettlementInstructionsAvailabilityQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof settlementInstructionsAvailability>>> = ({ signal }) => settlementInstructionsAvailability(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SettlementInstructionsAvailabilityQueryResult = NonNullable<Awaited<ReturnType<typeof settlementInstructionsAvailability>>>
export type SettlementInstructionsAvailabilityQueryError = unknown


export function useSettlementInstructionsAvailability<TData = Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof settlementInstructionsAvailability>>,
          TError,
          Awaited<ReturnType<typeof settlementInstructionsAvailability>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettlementInstructionsAvailability<TData = Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof settlementInstructionsAvailability>>,
          TError,
          Awaited<ReturnType<typeof settlementInstructionsAvailability>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSettlementInstructionsAvailability<TData = Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useSettlementInstructionsAvailability<TData = Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof settlementInstructionsAvailability>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSettlementInstructionsAvailabilityQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type settlementInstructionsConfirmResponse200 = {
  data: void
  status: 200
}

export type settlementInstructionsConfirmResponseSuccess = (settlementInstructionsConfirmResponse200) & {
  headers: Headers;
};
;

export type settlementInstructionsConfirmResponse = (settlementInstructionsConfirmResponseSuccess)

export const getSettlementInstructionsConfirmUrl = (id: string,) => {




  return `/api/settlement-instructions/${id}/confirmations`
}

export const settlementInstructionsConfirm = async (id: string, options?: RequestInit): Promise<settlementInstructionsConfirmResponse> => {

  return customMutator<settlementInstructionsConfirmResponse>(getSettlementInstructionsConfirmUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSettlementInstructionsConfirmMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsConfirm>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsConfirm>>, TError,{id: string}, TContext> => {

const mutationKey = ['settlementInstructionsConfirm'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof settlementInstructionsConfirm>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  settlementInstructionsConfirm(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SettlementInstructionsConfirmMutationResult = NonNullable<Awaited<ReturnType<typeof settlementInstructionsConfirm>>>

    export type SettlementInstructionsConfirmMutationError = unknown

    export const useSettlementInstructionsConfirm = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsConfirm>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof settlementInstructionsConfirm>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getSettlementInstructionsConfirmMutationOptions(options), queryClient);
    }

export type settlementInstructionsCorrectResponse201 = {
  data: void
  status: 201
}

export type settlementInstructionsCorrectResponseSuccess = (settlementInstructionsCorrectResponse201) & {
  headers: Headers;
};
;

export type settlementInstructionsCorrectResponse = (settlementInstructionsCorrectResponseSuccess)

export const getSettlementInstructionsCorrectUrl = (id: string,) => {




  return `/api/settlement-instructions/${id}/correct`
}

export const settlementInstructionsCorrect = async (id: string, options?: RequestInit): Promise<settlementInstructionsCorrectResponse> => {

  return customMutator<settlementInstructionsCorrectResponse>(getSettlementInstructionsCorrectUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSettlementInstructionsCorrectMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsCorrect>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsCorrect>>, TError,{id: string}, TContext> => {

const mutationKey = ['settlementInstructionsCorrect'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof settlementInstructionsCorrect>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  settlementInstructionsCorrect(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SettlementInstructionsCorrectMutationResult = NonNullable<Awaited<ReturnType<typeof settlementInstructionsCorrect>>>

    export type SettlementInstructionsCorrectMutationError = unknown

    export const useSettlementInstructionsCorrect = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsCorrect>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof settlementInstructionsCorrect>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getSettlementInstructionsCorrectMutationOptions(options), queryClient);
    }

export type settlementInstructionsCancelResponse201 = {
  data: void
  status: 201
}

export type settlementInstructionsCancelResponseSuccess = (settlementInstructionsCancelResponse201) & {
  headers: Headers;
};
;

export type settlementInstructionsCancelResponse = (settlementInstructionsCancelResponseSuccess)

export const getSettlementInstructionsCancelUrl = (id: string,) => {




  return `/api/settlement-instructions/${id}/cancel`
}

export const settlementInstructionsCancel = async (id: string, options?: RequestInit): Promise<settlementInstructionsCancelResponse> => {

  return customMutator<settlementInstructionsCancelResponse>(getSettlementInstructionsCancelUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSettlementInstructionsCancelMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsCancel>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsCancel>>, TError,{id: string}, TContext> => {

const mutationKey = ['settlementInstructionsCancel'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof settlementInstructionsCancel>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  settlementInstructionsCancel(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SettlementInstructionsCancelMutationResult = NonNullable<Awaited<ReturnType<typeof settlementInstructionsCancel>>>

    export type SettlementInstructionsCancelMutationError = unknown

    export const useSettlementInstructionsCancel = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof settlementInstructionsCancel>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof settlementInstructionsCancel>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getSettlementInstructionsCancelMutationOptions(options), queryClient);
    }

export type investmentPositionTransferListResponse200 = {
  data: void
  status: 200
}

export type investmentPositionTransferListResponseSuccess = (investmentPositionTransferListResponse200) & {
  headers: Headers;
};
;

export type investmentPositionTransferListResponse = (investmentPositionTransferListResponseSuccess)

export const getInvestmentPositionTransferListUrl = () => {




  return `/api/position-transfers`
}

export const investmentPositionTransferList = async ( options?: RequestInit): Promise<investmentPositionTransferListResponse> => {

  return customMutator<investmentPositionTransferListResponse>(getInvestmentPositionTransferListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentPositionTransferListQueryKey = () => {
    return [
    `/api/position-transfers`
    ] as const;
    }


export const getInvestmentPositionTransferListQueryOptions = <TData = Awaited<ReturnType<typeof investmentPositionTransferList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentPositionTransferListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentPositionTransferList>>> = ({ signal }) => investmentPositionTransferList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentPositionTransferListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentPositionTransferList>>>
export type InvestmentPositionTransferListQueryError = unknown


export function useInvestmentPositionTransferList<TData = Awaited<ReturnType<typeof investmentPositionTransferList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPositionTransferList>>,
          TError,
          Awaited<ReturnType<typeof investmentPositionTransferList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPositionTransferList<TData = Awaited<ReturnType<typeof investmentPositionTransferList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPositionTransferList>>,
          TError,
          Awaited<ReturnType<typeof investmentPositionTransferList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPositionTransferList<TData = Awaited<ReturnType<typeof investmentPositionTransferList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentPositionTransferList<TData = Awaited<ReturnType<typeof investmentPositionTransferList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentPositionTransferListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPositionTransferRequestResponse201 = {
  data: void
  status: 201
}

export type investmentPositionTransferRequestResponseSuccess = (investmentPositionTransferRequestResponse201) & {
  headers: Headers;
};
;

export type investmentPositionTransferRequestResponse = (investmentPositionTransferRequestResponseSuccess)

export const getInvestmentPositionTransferRequestUrl = () => {




  return `/api/position-transfers`
}

export const investmentPositionTransferRequest = async ( options?: RequestInit): Promise<investmentPositionTransferRequestResponse> => {

  return customMutator<investmentPositionTransferRequestResponse>(getInvestmentPositionTransferRequestUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getInvestmentPositionTransferRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferRequest>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferRequest>>, TError,void, TContext> => {

const mutationKey = ['investmentPositionTransferRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPositionTransferRequest>>, void> = () => {


          return  investmentPositionTransferRequest(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPositionTransferRequestMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPositionTransferRequest>>>

    export type InvestmentPositionTransferRequestMutationError = unknown

    export const useInvestmentPositionTransferRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferRequest>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPositionTransferRequest>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getInvestmentPositionTransferRequestMutationOptions(options), queryClient);
    }

export type investmentPositionTransferRequestEligibilityResponse200 = {
  data: void
  status: 200
}

export type investmentPositionTransferRequestEligibilityResponseSuccess = (investmentPositionTransferRequestEligibilityResponse200) & {
  headers: Headers;
};
;

export type investmentPositionTransferRequestEligibilityResponse = (investmentPositionTransferRequestEligibilityResponseSuccess)

export const getInvestmentPositionTransferRequestEligibilityUrl = () => {




  return `/api/position-transfers/request-eligibility`
}

export const investmentPositionTransferRequestEligibility = async ( options?: RequestInit): Promise<investmentPositionTransferRequestEligibilityResponse> => {

  return customMutator<investmentPositionTransferRequestEligibilityResponse>(getInvestmentPositionTransferRequestEligibilityUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentPositionTransferRequestEligibilityQueryKey = () => {
    return [
    `/api/position-transfers/request-eligibility`
    ] as const;
    }


export const getInvestmentPositionTransferRequestEligibilityQueryOptions = <TData = Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentPositionTransferRequestEligibilityQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>> = ({ signal }) => investmentPositionTransferRequestEligibility({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentPositionTransferRequestEligibilityQueryResult = NonNullable<Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>>
export type InvestmentPositionTransferRequestEligibilityQueryError = unknown


export function useInvestmentPositionTransferRequestEligibility<TData = Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>,
          TError,
          Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPositionTransferRequestEligibility<TData = Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>,
          TError,
          Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPositionTransferRequestEligibility<TData = Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentPositionTransferRequestEligibility<TData = Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferRequestEligibility>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentPositionTransferRequestEligibilityQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPositionTransferGetResponse200 = {
  data: void
  status: 200
}

export type investmentPositionTransferGetResponseSuccess = (investmentPositionTransferGetResponse200) & {
  headers: Headers;
};
;

export type investmentPositionTransferGetResponse = (investmentPositionTransferGetResponseSuccess)

export const getInvestmentPositionTransferGetUrl = (id: string,) => {




  return `/api/position-transfers/${id}`
}

export const investmentPositionTransferGet = async (id: string, options?: RequestInit): Promise<investmentPositionTransferGetResponse> => {

  return customMutator<investmentPositionTransferGetResponse>(getInvestmentPositionTransferGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentPositionTransferGetQueryKey = (id: string,) => {
    return [
    `/api/position-transfers/${id}`
    ] as const;
    }


export const getInvestmentPositionTransferGetQueryOptions = <TData = Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentPositionTransferGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentPositionTransferGet>>> = ({ signal }) => investmentPositionTransferGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentPositionTransferGetQueryResult = NonNullable<Awaited<ReturnType<typeof investmentPositionTransferGet>>>
export type InvestmentPositionTransferGetQueryError = unknown


export function useInvestmentPositionTransferGet<TData = Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPositionTransferGet>>,
          TError,
          Awaited<ReturnType<typeof investmentPositionTransferGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPositionTransferGet<TData = Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPositionTransferGet>>,
          TError,
          Awaited<ReturnType<typeof investmentPositionTransferGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPositionTransferGet<TData = Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentPositionTransferGet<TData = Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentPositionTransferGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPositionTransferEligibilityResponse200 = {
  data: void
  status: 200
}

export type investmentPositionTransferEligibilityResponseSuccess = (investmentPositionTransferEligibilityResponse200) & {
  headers: Headers;
};
;

export type investmentPositionTransferEligibilityResponse = (investmentPositionTransferEligibilityResponseSuccess)

export const getInvestmentPositionTransferEligibilityUrl = (id: string,) => {




  return `/api/position-transfers/${id}/eligibility`
}

export const investmentPositionTransferEligibility = async (id: string, options?: RequestInit): Promise<investmentPositionTransferEligibilityResponse> => {

  return customMutator<investmentPositionTransferEligibilityResponse>(getInvestmentPositionTransferEligibilityUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentPositionTransferEligibilityQueryKey = (id: string,) => {
    return [
    `/api/position-transfers/${id}/eligibility`
    ] as const;
    }


export const getInvestmentPositionTransferEligibilityQueryOptions = <TData = Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentPositionTransferEligibilityQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentPositionTransferEligibility>>> = ({ signal }) => investmentPositionTransferEligibility(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentPositionTransferEligibilityQueryResult = NonNullable<Awaited<ReturnType<typeof investmentPositionTransferEligibility>>>
export type InvestmentPositionTransferEligibilityQueryError = unknown


export function useInvestmentPositionTransferEligibility<TData = Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPositionTransferEligibility>>,
          TError,
          Awaited<ReturnType<typeof investmentPositionTransferEligibility>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPositionTransferEligibility<TData = Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentPositionTransferEligibility>>,
          TError,
          Awaited<ReturnType<typeof investmentPositionTransferEligibility>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentPositionTransferEligibility<TData = Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentPositionTransferEligibility<TData = Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentPositionTransferEligibility>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentPositionTransferEligibilityQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPositionTransferApproveResponse200 = {
  data: void
  status: 200
}

export type investmentPositionTransferApproveResponseSuccess = (investmentPositionTransferApproveResponse200) & {
  headers: Headers;
};
;

export type investmentPositionTransferApproveResponse = (investmentPositionTransferApproveResponseSuccess)

export const getInvestmentPositionTransferApproveUrl = (id: string,) => {




  return `/api/position-transfers/${id}/approve`
}

export const investmentPositionTransferApprove = async (id: string, options?: RequestInit): Promise<investmentPositionTransferApproveResponse> => {

  return customMutator<investmentPositionTransferApproveResponse>(getInvestmentPositionTransferApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getInvestmentPositionTransferApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['investmentPositionTransferApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPositionTransferApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  investmentPositionTransferApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPositionTransferApproveMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPositionTransferApprove>>>

    export type InvestmentPositionTransferApproveMutationError = unknown

    export const useInvestmentPositionTransferApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPositionTransferApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getInvestmentPositionTransferApproveMutationOptions(options), queryClient);
    }

export type investmentPositionTransferExecuteResponse200 = {
  data: void
  status: 200
}

export type investmentPositionTransferExecuteResponseSuccess = (investmentPositionTransferExecuteResponse200) & {
  headers: Headers;
};
;

export type investmentPositionTransferExecuteResponse = (investmentPositionTransferExecuteResponseSuccess)

export const getInvestmentPositionTransferExecuteUrl = (id: string,) => {




  return `/api/position-transfers/${id}/execute`
}

export const investmentPositionTransferExecute = async (id: string, options?: RequestInit): Promise<investmentPositionTransferExecuteResponse> => {

  return customMutator<investmentPositionTransferExecuteResponse>(getInvestmentPositionTransferExecuteUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getInvestmentPositionTransferExecuteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferExecute>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferExecute>>, TError,{id: string}, TContext> => {

const mutationKey = ['investmentPositionTransferExecute'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPositionTransferExecute>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  investmentPositionTransferExecute(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPositionTransferExecuteMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPositionTransferExecute>>>

    export type InvestmentPositionTransferExecuteMutationError = unknown

    export const useInvestmentPositionTransferExecute = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferExecute>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPositionTransferExecute>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getInvestmentPositionTransferExecuteMutationOptions(options), queryClient);
    }

export type investmentPositionTransferCancelResponse200 = {
  data: void
  status: 200
}

export type investmentPositionTransferCancelResponseSuccess = (investmentPositionTransferCancelResponse200) & {
  headers: Headers;
};
;

export type investmentPositionTransferCancelResponse = (investmentPositionTransferCancelResponseSuccess)

export const getInvestmentPositionTransferCancelUrl = (id: string,) => {




  return `/api/position-transfers/${id}/cancel`
}

export const investmentPositionTransferCancel = async (id: string, options?: RequestInit): Promise<investmentPositionTransferCancelResponse> => {

  return customMutator<investmentPositionTransferCancelResponse>(getInvestmentPositionTransferCancelUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getInvestmentPositionTransferCancelMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferCancel>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferCancel>>, TError,{id: string}, TContext> => {

const mutationKey = ['investmentPositionTransferCancel'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPositionTransferCancel>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  investmentPositionTransferCancel(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPositionTransferCancelMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPositionTransferCancel>>>

    export type InvestmentPositionTransferCancelMutationError = unknown

    export const useInvestmentPositionTransferCancel = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferCancel>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPositionTransferCancel>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getInvestmentPositionTransferCancelMutationOptions(options), queryClient);
    }

export type investmentPositionTransferReverseResponse200 = {
  data: void
  status: 200
}

export type investmentPositionTransferReverseResponseSuccess = (investmentPositionTransferReverseResponse200) & {
  headers: Headers;
};
;

export type investmentPositionTransferReverseResponse = (investmentPositionTransferReverseResponseSuccess)

export const getInvestmentPositionTransferReverseUrl = (id: string,) => {




  return `/api/position-transfers/${id}/reverse`
}

export const investmentPositionTransferReverse = async (id: string, options?: RequestInit): Promise<investmentPositionTransferReverseResponse> => {

  return customMutator<investmentPositionTransferReverseResponse>(getInvestmentPositionTransferReverseUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getInvestmentPositionTransferReverseMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferReverse>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferReverse>>, TError,{id: string}, TContext> => {

const mutationKey = ['investmentPositionTransferReverse'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPositionTransferReverse>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  investmentPositionTransferReverse(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPositionTransferReverseMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPositionTransferReverse>>>

    export type InvestmentPositionTransferReverseMutationError = unknown

    export const useInvestmentPositionTransferReverse = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPositionTransferReverse>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPositionTransferReverse>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getInvestmentPositionTransferReverseMutationOptions(options), queryClient);
    }

export type investmentCorporateActionListResponse200 = {
  data: InvestmentCorporateActionList200
  status: 200
}

export type investmentCorporateActionListResponseSuccess = (investmentCorporateActionListResponse200) & {
  headers: Headers;
};
;

export type investmentCorporateActionListResponse = (investmentCorporateActionListResponseSuccess)

export const getInvestmentCorporateActionListUrl = () => {




  return `/api/corporate-actions`
}

export const investmentCorporateActionList = async ( options?: RequestInit): Promise<investmentCorporateActionListResponse> => {

  return customMutator<investmentCorporateActionListResponse>(getInvestmentCorporateActionListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentCorporateActionListQueryKey = () => {
    return [
    `/api/corporate-actions`
    ] as const;
    }


export const getInvestmentCorporateActionListQueryOptions = <TData = Awaited<ReturnType<typeof investmentCorporateActionList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentCorporateActionListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentCorporateActionList>>> = ({ signal }) => investmentCorporateActionList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentCorporateActionListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionList>>>
export type InvestmentCorporateActionListQueryError = unknown


export function useInvestmentCorporateActionList<TData = Awaited<ReturnType<typeof investmentCorporateActionList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionList>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionList<TData = Awaited<ReturnType<typeof investmentCorporateActionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionList>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionList<TData = Awaited<ReturnType<typeof investmentCorporateActionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentCorporateActionList<TData = Awaited<ReturnType<typeof investmentCorporateActionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentCorporateActionListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentCorporateActionCreateResponse200 = {
  data: void
  status: 200
}

export type investmentCorporateActionCreateResponse201 = {
  data: void
  status: 201
}

export type investmentCorporateActionCreateResponseSuccess = (investmentCorporateActionCreateResponse200 | investmentCorporateActionCreateResponse201) & {
  headers: Headers;
};
;

export type investmentCorporateActionCreateResponse = (investmentCorporateActionCreateResponseSuccess)

export const getInvestmentCorporateActionCreateUrl = () => {




  return `/api/corporate-actions`
}

export const investmentCorporateActionCreate = async (createCorporateActionDto: CreateCorporateActionDto, options?: RequestInit): Promise<investmentCorporateActionCreateResponse> => {

  return customMutator<investmentCorporateActionCreateResponse>(getInvestmentCorporateActionCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createCorporateActionDto)
  }
);}





export const getInvestmentCorporateActionCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionCreate>>, TError,{data: CreateCorporateActionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionCreate>>, TError,{data: CreateCorporateActionDto}, TContext> => {

const mutationKey = ['investmentCorporateActionCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentCorporateActionCreate>>, {data: CreateCorporateActionDto}> = (props) => {
          const {data} = props ?? {};

          return  investmentCorporateActionCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentCorporateActionCreateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionCreate>>>
    export type InvestmentCorporateActionCreateMutationBody = CreateCorporateActionDto
    export type InvestmentCorporateActionCreateMutationError = unknown

    export const useInvestmentCorporateActionCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionCreate>>, TError,{data: CreateCorporateActionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentCorporateActionCreate>>,
        TError,
        {data: CreateCorporateActionDto},
        TContext
      > => {
      return useMutation(getInvestmentCorporateActionCreateMutationOptions(options), queryClient);
    }

export type investmentCorporateActionGetResponse200 = {
  data: InvestmentCorporateActionGet200
  status: 200
}

export type investmentCorporateActionGetResponseSuccess = (investmentCorporateActionGetResponse200) & {
  headers: Headers;
};
;

export type investmentCorporateActionGetResponse = (investmentCorporateActionGetResponseSuccess)

export const getInvestmentCorporateActionGetUrl = (id: string,) => {




  return `/api/corporate-actions/${id}`
}

export const investmentCorporateActionGet = async (id: string, options?: RequestInit): Promise<investmentCorporateActionGetResponse> => {

  return customMutator<investmentCorporateActionGetResponse>(getInvestmentCorporateActionGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentCorporateActionGetQueryKey = (id: string,) => {
    return [
    `/api/corporate-actions/${id}`
    ] as const;
    }


export const getInvestmentCorporateActionGetQueryOptions = <TData = Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentCorporateActionGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentCorporateActionGet>>> = ({ signal }) => investmentCorporateActionGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentCorporateActionGetQueryResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionGet>>>
export type InvestmentCorporateActionGetQueryError = unknown


export function useInvestmentCorporateActionGet<TData = Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionGet>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionGet<TData = Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionGet>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionGet<TData = Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentCorporateActionGet<TData = Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentCorporateActionGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentCorporateActionUpdateResponse200 = {
  data: InvestmentCorporateActionUpdate200
  status: 200
}

export type investmentCorporateActionUpdateResponseSuccess = (investmentCorporateActionUpdateResponse200) & {
  headers: Headers;
};
;

export type investmentCorporateActionUpdateResponse = (investmentCorporateActionUpdateResponseSuccess)

export const getInvestmentCorporateActionUpdateUrl = (id: string,) => {




  return `/api/corporate-actions/${id}`
}

export const investmentCorporateActionUpdate = async (id: string,
    updateCorporateActionDto: UpdateCorporateActionDto, options?: RequestInit): Promise<investmentCorporateActionUpdateResponse> => {

  return customMutator<investmentCorporateActionUpdateResponse>(getInvestmentCorporateActionUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateCorporateActionDto)
  }
);}





export const getInvestmentCorporateActionUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionUpdate>>, TError,{id: string;data: UpdateCorporateActionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionUpdate>>, TError,{id: string;data: UpdateCorporateActionDto}, TContext> => {

const mutationKey = ['investmentCorporateActionUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentCorporateActionUpdate>>, {id: string;data: UpdateCorporateActionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  investmentCorporateActionUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentCorporateActionUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionUpdate>>>
    export type InvestmentCorporateActionUpdateMutationBody = UpdateCorporateActionDto
    export type InvestmentCorporateActionUpdateMutationError = unknown

    export const useInvestmentCorporateActionUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionUpdate>>, TError,{id: string;data: UpdateCorporateActionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentCorporateActionUpdate>>,
        TError,
        {id: string;data: UpdateCorporateActionDto},
        TContext
      > => {
      return useMutation(getInvestmentCorporateActionUpdateMutationOptions(options), queryClient);
    }

export type investmentCorporateActionProcessResponse200 = {
  data: void
  status: 200
}

export type investmentCorporateActionProcessResponse201 = {
  data: void
  status: 201
}

export type investmentCorporateActionProcessResponseSuccess = (investmentCorporateActionProcessResponse200 | investmentCorporateActionProcessResponse201) & {
  headers: Headers;
};
;

export type investmentCorporateActionProcessResponse = (investmentCorporateActionProcessResponseSuccess)

export const getInvestmentCorporateActionProcessUrl = (id: string,) => {




  return `/api/corporate-actions/${id}/process`
}

export const investmentCorporateActionProcess = async (id: string, options?: RequestInit): Promise<investmentCorporateActionProcessResponse> => {

  return customMutator<investmentCorporateActionProcessResponse>(getInvestmentCorporateActionProcessUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getInvestmentCorporateActionProcessMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionProcess>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionProcess>>, TError,{id: string}, TContext> => {

const mutationKey = ['investmentCorporateActionProcess'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentCorporateActionProcess>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  investmentCorporateActionProcess(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentCorporateActionProcessMutationResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionProcess>>>

    export type InvestmentCorporateActionProcessMutationError = unknown

    export const useInvestmentCorporateActionProcess = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionProcess>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentCorporateActionProcess>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getInvestmentCorporateActionProcessMutationOptions(options), queryClient);
    }

export type investmentCorporateActionEntitlementsListResponse200 = {
  data: InvestmentCorporateActionEntitlementsList200
  status: 200
}

export type investmentCorporateActionEntitlementsListResponseSuccess = (investmentCorporateActionEntitlementsListResponse200) & {
  headers: Headers;
};
;

export type investmentCorporateActionEntitlementsListResponse = (investmentCorporateActionEntitlementsListResponseSuccess)

export const getInvestmentCorporateActionEntitlementsListUrl = (id: string,) => {




  return `/api/corporate-actions/${id}/entitlements`
}

export const investmentCorporateActionEntitlementsList = async (id: string, options?: RequestInit): Promise<investmentCorporateActionEntitlementsListResponse> => {

  return customMutator<investmentCorporateActionEntitlementsListResponse>(getInvestmentCorporateActionEntitlementsListUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentCorporateActionEntitlementsListQueryKey = (id: string,) => {
    return [
    `/api/corporate-actions/${id}/entitlements`
    ] as const;
    }


export const getInvestmentCorporateActionEntitlementsListQueryOptions = <TData = Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentCorporateActionEntitlementsListQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>> = ({ signal }) => investmentCorporateActionEntitlementsList(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentCorporateActionEntitlementsListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>>
export type InvestmentCorporateActionEntitlementsListQueryError = unknown


export function useInvestmentCorporateActionEntitlementsList<TData = Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionEntitlementsList<TData = Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionEntitlementsList<TData = Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentCorporateActionEntitlementsList<TData = Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionEntitlementsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentCorporateActionEntitlementsListQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentCorporateActionElectResponse200 = {
  data: void
  status: 200
}

export type investmentCorporateActionElectResponse201 = {
  data: void
  status: 201
}

export type investmentCorporateActionElectResponseSuccess = (investmentCorporateActionElectResponse200 | investmentCorporateActionElectResponse201) & {
  headers: Headers;
};
;

export type investmentCorporateActionElectResponse = (investmentCorporateActionElectResponseSuccess)

export const getInvestmentCorporateActionElectUrl = (id: string,
    accountId: string,) => {




  return `/api/corporate-actions/${id}/entitlements/${accountId}/elect`
}

export const investmentCorporateActionElect = async (id: string,
    accountId: string,
    electionDto: ElectionDto, options?: RequestInit): Promise<investmentCorporateActionElectResponse> => {

  return customMutator<investmentCorporateActionElectResponse>(getInvestmentCorporateActionElectUrl(id,accountId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(electionDto)
  }
);}





export const getInvestmentCorporateActionElectMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionElect>>, TError,{id: string;accountId: string;data: ElectionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionElect>>, TError,{id: string;accountId: string;data: ElectionDto}, TContext> => {

const mutationKey = ['investmentCorporateActionElect'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentCorporateActionElect>>, {id: string;accountId: string;data: ElectionDto}> = (props) => {
          const {id,accountId,data} = props ?? {};

          return  investmentCorporateActionElect(id,accountId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentCorporateActionElectMutationResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionElect>>>
    export type InvestmentCorporateActionElectMutationBody = ElectionDto
    export type InvestmentCorporateActionElectMutationError = unknown

    export const useInvestmentCorporateActionElect = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionElect>>, TError,{id: string;accountId: string;data: ElectionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentCorporateActionElect>>,
        TError,
        {id: string;accountId: string;data: ElectionDto},
        TContext
      > => {
      return useMutation(getInvestmentCorporateActionElectMutationOptions(options), queryClient);
    }

export type investmentCorporateActionAllocateResponse200 = {
  data: void
  status: 200
}

export type investmentCorporateActionAllocateResponse201 = {
  data: void
  status: 201
}

export type investmentCorporateActionAllocateResponseSuccess = (investmentCorporateActionAllocateResponse200 | investmentCorporateActionAllocateResponse201) & {
  headers: Headers;
};
;

export type investmentCorporateActionAllocateResponse = (investmentCorporateActionAllocateResponseSuccess)

export const getInvestmentCorporateActionAllocateUrl = (id: string,
    accountId: string,) => {




  return `/api/corporate-actions/${id}/entitlements/${accountId}/recognize-income`
}

export const investmentCorporateActionAllocate = async (id: string,
    accountId: string,
    incomeRecognitionDto: IncomeRecognitionDto, options?: RequestInit): Promise<investmentCorporateActionAllocateResponse> => {

  return customMutator<investmentCorporateActionAllocateResponse>(getInvestmentCorporateActionAllocateUrl(id,accountId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(incomeRecognitionDto)
  }
);}





export const getInvestmentCorporateActionAllocateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionAllocate>>, TError,{id: string;accountId: string;data: IncomeRecognitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionAllocate>>, TError,{id: string;accountId: string;data: IncomeRecognitionDto}, TContext> => {

const mutationKey = ['investmentCorporateActionAllocate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentCorporateActionAllocate>>, {id: string;accountId: string;data: IncomeRecognitionDto}> = (props) => {
          const {id,accountId,data} = props ?? {};

          return  investmentCorporateActionAllocate(id,accountId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentCorporateActionAllocateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionAllocate>>>
    export type InvestmentCorporateActionAllocateMutationBody = IncomeRecognitionDto
    export type InvestmentCorporateActionAllocateMutationError = unknown

    export const useInvestmentCorporateActionAllocate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionAllocate>>, TError,{id: string;accountId: string;data: IncomeRecognitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentCorporateActionAllocate>>,
        TError,
        {id: string;accountId: string;data: IncomeRecognitionDto},
        TContext
      > => {
      return useMutation(getInvestmentCorporateActionAllocateMutationOptions(options), queryClient);
    }

export type investmentCorporateActionAllocatePostResponse200 = {
  data: void
  status: 200
}

export type investmentCorporateActionAllocatePostResponse201 = {
  data: void
  status: 201
}

export type investmentCorporateActionAllocatePostResponseSuccess = (investmentCorporateActionAllocatePostResponse200 | investmentCorporateActionAllocatePostResponse201) & {
  headers: Headers;
};
;

export type investmentCorporateActionAllocatePostResponse = (investmentCorporateActionAllocatePostResponseSuccess)

export const getInvestmentCorporateActionAllocatePostUrl = (id: string,
    accountId: string,) => {




  return `/api/corporate-actions/${id}/entitlements/${accountId}/receive-cash`
}

export const investmentCorporateActionAllocatePost = async (id: string,
    accountId: string,
    cashReceiptDto: CashReceiptDto, options?: RequestInit): Promise<investmentCorporateActionAllocatePostResponse> => {

  return customMutator<investmentCorporateActionAllocatePostResponse>(getInvestmentCorporateActionAllocatePostUrl(id,accountId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(cashReceiptDto)
  }
);}





export const getInvestmentCorporateActionAllocatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost>>, TError,{id: string;accountId: string;data: CashReceiptDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost>>, TError,{id: string;accountId: string;data: CashReceiptDto}, TContext> => {

const mutationKey = ['investmentCorporateActionAllocatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost>>, {id: string;accountId: string;data: CashReceiptDto}> = (props) => {
          const {id,accountId,data} = props ?? {};

          return  investmentCorporateActionAllocatePost(id,accountId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentCorporateActionAllocatePostMutationResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost>>>
    export type InvestmentCorporateActionAllocatePostMutationBody = CashReceiptDto
    export type InvestmentCorporateActionAllocatePostMutationError = unknown

    export const useInvestmentCorporateActionAllocatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost>>, TError,{id: string;accountId: string;data: CashReceiptDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentCorporateActionAllocatePost>>,
        TError,
        {id: string;accountId: string;data: CashReceiptDto},
        TContext
      > => {
      return useMutation(getInvestmentCorporateActionAllocatePostMutationOptions(options), queryClient);
    }

export type investmentCorporateActionAllocatePost1Response200 = {
  data: void
  status: 200
}

export type investmentCorporateActionAllocatePost1Response201 = {
  data: void
  status: 201
}

export type investmentCorporateActionAllocatePost1ResponseSuccess = (investmentCorporateActionAllocatePost1Response200 | investmentCorporateActionAllocatePost1Response201) & {
  headers: Headers;
};
;

export type investmentCorporateActionAllocatePost1Response = (investmentCorporateActionAllocatePost1ResponseSuccess)

export const getInvestmentCorporateActionAllocatePost1Url = (id: string,) => {




  return `/api/corporate-actions/${id}/allocate`
}

export const investmentCorporateActionAllocatePost1 = async (id: string, options?: RequestInit): Promise<investmentCorporateActionAllocatePost1Response> => {

  return customMutator<investmentCorporateActionAllocatePost1Response>(getInvestmentCorporateActionAllocatePost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getInvestmentCorporateActionAllocatePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['investmentCorporateActionAllocatePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  investmentCorporateActionAllocatePost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentCorporateActionAllocatePost1MutationResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost1>>>

    export type InvestmentCorporateActionAllocatePost1MutationError = unknown

    export const useInvestmentCorporateActionAllocatePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentCorporateActionAllocatePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentCorporateActionAllocatePost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getInvestmentCorporateActionAllocatePost1MutationOptions(options), queryClient);
    }

export type investmentCorporateActionExDateClassifyResponse200 = {
  data: InvestmentCorporateActionExDateClassify200
  status: 200
}

export type investmentCorporateActionExDateClassifyResponseSuccess = (investmentCorporateActionExDateClassifyResponse200) & {
  headers: Headers;
};
;

export type investmentCorporateActionExDateClassifyResponse = (investmentCorporateActionExDateClassifyResponseSuccess)

export const getInvestmentCorporateActionExDateClassifyUrl = (id: string,) => {




  return `/api/corporate-actions/${id}/ex-date-classification`
}

export const investmentCorporateActionExDateClassify = async (id: string, options?: RequestInit): Promise<investmentCorporateActionExDateClassifyResponse> => {

  return customMutator<investmentCorporateActionExDateClassifyResponse>(getInvestmentCorporateActionExDateClassifyUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentCorporateActionExDateClassifyQueryKey = (id: string,) => {
    return [
    `/api/corporate-actions/${id}/ex-date-classification`
    ] as const;
    }


export const getInvestmentCorporateActionExDateClassifyQueryOptions = <TData = Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentCorporateActionExDateClassifyQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>> = ({ signal }) => investmentCorporateActionExDateClassify(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentCorporateActionExDateClassifyQueryResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>>
export type InvestmentCorporateActionExDateClassifyQueryError = unknown


export function useInvestmentCorporateActionExDateClassify<TData = Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionExDateClassify<TData = Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionExDateClassify<TData = Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentCorporateActionExDateClassify<TData = Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateClassify>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentCorporateActionExDateClassifyQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentCorporateActionExDateVerifyResponse200 = {
  data: InvestmentCorporateActionExDateVerify200
  status: 200
}

export type investmentCorporateActionExDateVerifyResponseSuccess = (investmentCorporateActionExDateVerifyResponse200) & {
  headers: Headers;
};
;

export type investmentCorporateActionExDateVerifyResponse = (investmentCorporateActionExDateVerifyResponseSuccess)

export const getInvestmentCorporateActionExDateVerifyUrl = (id: string,) => {




  return `/api/corporate-actions/${id}/ex-date-verification`
}

export const investmentCorporateActionExDateVerify = async (id: string, options?: RequestInit): Promise<investmentCorporateActionExDateVerifyResponse> => {

  return customMutator<investmentCorporateActionExDateVerifyResponse>(getInvestmentCorporateActionExDateVerifyUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentCorporateActionExDateVerifyQueryKey = (id: string,) => {
    return [
    `/api/corporate-actions/${id}/ex-date-verification`
    ] as const;
    }


export const getInvestmentCorporateActionExDateVerifyQueryOptions = <TData = Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentCorporateActionExDateVerifyQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>> = ({ signal }) => investmentCorporateActionExDateVerify(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentCorporateActionExDateVerifyQueryResult = NonNullable<Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>>
export type InvestmentCorporateActionExDateVerifyQueryError = unknown


export function useInvestmentCorporateActionExDateVerify<TData = Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionExDateVerify<TData = Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>,
          TError,
          Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentCorporateActionExDateVerify<TData = Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentCorporateActionExDateVerify<TData = Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentCorporateActionExDateVerify>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentCorporateActionExDateVerifyQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentFactPositionResponse200 = {
  data: InvestmentFactPosition200
  status: 200
}

export type investmentFactPositionResponseSuccess = (investmentFactPositionResponse200) & {
  headers: Headers;
};
;

export type investmentFactPositionResponse = (investmentFactPositionResponseSuccess)

export const getInvestmentFactPositionUrl = (params?: InvestmentFactPositionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/facts/position?${stringifiedParams}` : `/api/facts/position`
}

export const investmentFactPosition = async (params?: InvestmentFactPositionParams, options?: RequestInit): Promise<investmentFactPositionResponse> => {

  return customMutator<investmentFactPositionResponse>(getInvestmentFactPositionUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentFactPositionQueryKey = (params?: InvestmentFactPositionParams,) => {
    return [
    `/api/facts/position`, ...(params ? [params] : [])
    ] as const;
    }


export const getInvestmentFactPositionQueryOptions = <TData = Awaited<ReturnType<typeof investmentFactPosition>>, TError = unknown>(params?: InvestmentFactPositionParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactPosition>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentFactPositionQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentFactPosition>>> = ({ signal }) => investmentFactPosition(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentFactPosition>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentFactPositionQueryResult = NonNullable<Awaited<ReturnType<typeof investmentFactPosition>>>
export type InvestmentFactPositionQueryError = unknown


export function useInvestmentFactPosition<TData = Awaited<ReturnType<typeof investmentFactPosition>>, TError = unknown>(
 params: undefined |  InvestmentFactPositionParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactPosition>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactPosition>>,
          TError,
          Awaited<ReturnType<typeof investmentFactPosition>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactPosition<TData = Awaited<ReturnType<typeof investmentFactPosition>>, TError = unknown>(
 params?: InvestmentFactPositionParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactPosition>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactPosition>>,
          TError,
          Awaited<ReturnType<typeof investmentFactPosition>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactPosition<TData = Awaited<ReturnType<typeof investmentFactPosition>>, TError = unknown>(
 params?: InvestmentFactPositionParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactPosition>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentFactPosition<TData = Awaited<ReturnType<typeof investmentFactPosition>>, TError = unknown>(
 params?: InvestmentFactPositionParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactPosition>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentFactPositionQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentFactValuationResponse200 = {
  data: InvestmentFactValuation200
  status: 200
}

export type investmentFactValuationResponseSuccess = (investmentFactValuationResponse200) & {
  headers: Headers;
};
;

export type investmentFactValuationResponse = (investmentFactValuationResponseSuccess)

export const getInvestmentFactValuationUrl = (params?: InvestmentFactValuationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/facts/valuation?${stringifiedParams}` : `/api/facts/valuation`
}

export const investmentFactValuation = async (params?: InvestmentFactValuationParams, options?: RequestInit): Promise<investmentFactValuationResponse> => {

  return customMutator<investmentFactValuationResponse>(getInvestmentFactValuationUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentFactValuationQueryKey = (params?: InvestmentFactValuationParams,) => {
    return [
    `/api/facts/valuation`, ...(params ? [params] : [])
    ] as const;
    }


export const getInvestmentFactValuationQueryOptions = <TData = Awaited<ReturnType<typeof investmentFactValuation>>, TError = unknown>(params?: InvestmentFactValuationParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactValuation>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentFactValuationQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentFactValuation>>> = ({ signal }) => investmentFactValuation(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentFactValuation>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentFactValuationQueryResult = NonNullable<Awaited<ReturnType<typeof investmentFactValuation>>>
export type InvestmentFactValuationQueryError = unknown


export function useInvestmentFactValuation<TData = Awaited<ReturnType<typeof investmentFactValuation>>, TError = unknown>(
 params: undefined |  InvestmentFactValuationParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactValuation>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactValuation>>,
          TError,
          Awaited<ReturnType<typeof investmentFactValuation>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactValuation<TData = Awaited<ReturnType<typeof investmentFactValuation>>, TError = unknown>(
 params?: InvestmentFactValuationParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactValuation>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactValuation>>,
          TError,
          Awaited<ReturnType<typeof investmentFactValuation>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactValuation<TData = Awaited<ReturnType<typeof investmentFactValuation>>, TError = unknown>(
 params?: InvestmentFactValuationParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactValuation>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentFactValuation<TData = Awaited<ReturnType<typeof investmentFactValuation>>, TError = unknown>(
 params?: InvestmentFactValuationParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactValuation>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentFactValuationQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentFactMovementResponse200 = {
  data: InvestmentFactMovement200
  status: 200
}

export type investmentFactMovementResponseSuccess = (investmentFactMovementResponse200) & {
  headers: Headers;
};
;

export type investmentFactMovementResponse = (investmentFactMovementResponseSuccess)

export const getInvestmentFactMovementUrl = (params?: InvestmentFactMovementParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/facts/movement?${stringifiedParams}` : `/api/facts/movement`
}

export const investmentFactMovement = async (params?: InvestmentFactMovementParams, options?: RequestInit): Promise<investmentFactMovementResponse> => {

  return customMutator<investmentFactMovementResponse>(getInvestmentFactMovementUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentFactMovementQueryKey = (params?: InvestmentFactMovementParams,) => {
    return [
    `/api/facts/movement`, ...(params ? [params] : [])
    ] as const;
    }


export const getInvestmentFactMovementQueryOptions = <TData = Awaited<ReturnType<typeof investmentFactMovement>>, TError = unknown>(params?: InvestmentFactMovementParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactMovement>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentFactMovementQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentFactMovement>>> = ({ signal }) => investmentFactMovement(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentFactMovement>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentFactMovementQueryResult = NonNullable<Awaited<ReturnType<typeof investmentFactMovement>>>
export type InvestmentFactMovementQueryError = unknown


export function useInvestmentFactMovement<TData = Awaited<ReturnType<typeof investmentFactMovement>>, TError = unknown>(
 params: undefined |  InvestmentFactMovementParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactMovement>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactMovement>>,
          TError,
          Awaited<ReturnType<typeof investmentFactMovement>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactMovement<TData = Awaited<ReturnType<typeof investmentFactMovement>>, TError = unknown>(
 params?: InvestmentFactMovementParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactMovement>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactMovement>>,
          TError,
          Awaited<ReturnType<typeof investmentFactMovement>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactMovement<TData = Awaited<ReturnType<typeof investmentFactMovement>>, TError = unknown>(
 params?: InvestmentFactMovementParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactMovement>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentFactMovement<TData = Awaited<ReturnType<typeof investmentFactMovement>>, TError = unknown>(
 params?: InvestmentFactMovementParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactMovement>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentFactMovementQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentFactRealizedResponse200 = {
  data: InvestmentFactRealized200
  status: 200
}

export type investmentFactRealizedResponseSuccess = (investmentFactRealizedResponse200) & {
  headers: Headers;
};
;

export type investmentFactRealizedResponse = (investmentFactRealizedResponseSuccess)

export const getInvestmentFactRealizedUrl = (params?: InvestmentFactRealizedParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/facts/realized?${stringifiedParams}` : `/api/facts/realized`
}

export const investmentFactRealized = async (params?: InvestmentFactRealizedParams, options?: RequestInit): Promise<investmentFactRealizedResponse> => {

  return customMutator<investmentFactRealizedResponse>(getInvestmentFactRealizedUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentFactRealizedQueryKey = (params?: InvestmentFactRealizedParams,) => {
    return [
    `/api/facts/realized`, ...(params ? [params] : [])
    ] as const;
    }


export const getInvestmentFactRealizedQueryOptions = <TData = Awaited<ReturnType<typeof investmentFactRealized>>, TError = unknown>(params?: InvestmentFactRealizedParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactRealized>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentFactRealizedQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentFactRealized>>> = ({ signal }) => investmentFactRealized(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentFactRealized>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentFactRealizedQueryResult = NonNullable<Awaited<ReturnType<typeof investmentFactRealized>>>
export type InvestmentFactRealizedQueryError = unknown


export function useInvestmentFactRealized<TData = Awaited<ReturnType<typeof investmentFactRealized>>, TError = unknown>(
 params: undefined |  InvestmentFactRealizedParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactRealized>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactRealized>>,
          TError,
          Awaited<ReturnType<typeof investmentFactRealized>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactRealized<TData = Awaited<ReturnType<typeof investmentFactRealized>>, TError = unknown>(
 params?: InvestmentFactRealizedParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactRealized>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactRealized>>,
          TError,
          Awaited<ReturnType<typeof investmentFactRealized>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactRealized<TData = Awaited<ReturnType<typeof investmentFactRealized>>, TError = unknown>(
 params?: InvestmentFactRealizedParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactRealized>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentFactRealized<TData = Awaited<ReturnType<typeof investmentFactRealized>>, TError = unknown>(
 params?: InvestmentFactRealizedParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactRealized>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentFactRealizedQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentFactCgtResponse200 = {
  data: InvestmentFactCgt200
  status: 200
}

export type investmentFactCgtResponseSuccess = (investmentFactCgtResponse200) & {
  headers: Headers;
};
;

export type investmentFactCgtResponse = (investmentFactCgtResponseSuccess)

export const getInvestmentFactCgtUrl = (params?: InvestmentFactCgtParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/facts/cgt?${stringifiedParams}` : `/api/facts/cgt`
}

export const investmentFactCgt = async (params?: InvestmentFactCgtParams, options?: RequestInit): Promise<investmentFactCgtResponse> => {

  return customMutator<investmentFactCgtResponse>(getInvestmentFactCgtUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentFactCgtQueryKey = (params?: InvestmentFactCgtParams,) => {
    return [
    `/api/facts/cgt`, ...(params ? [params] : [])
    ] as const;
    }


export const getInvestmentFactCgtQueryOptions = <TData = Awaited<ReturnType<typeof investmentFactCgt>>, TError = unknown>(params?: InvestmentFactCgtParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactCgt>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentFactCgtQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentFactCgt>>> = ({ signal }) => investmentFactCgt(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentFactCgt>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentFactCgtQueryResult = NonNullable<Awaited<ReturnType<typeof investmentFactCgt>>>
export type InvestmentFactCgtQueryError = unknown


export function useInvestmentFactCgt<TData = Awaited<ReturnType<typeof investmentFactCgt>>, TError = unknown>(
 params: undefined |  InvestmentFactCgtParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactCgt>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactCgt>>,
          TError,
          Awaited<ReturnType<typeof investmentFactCgt>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactCgt<TData = Awaited<ReturnType<typeof investmentFactCgt>>, TError = unknown>(
 params?: InvestmentFactCgtParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactCgt>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactCgt>>,
          TError,
          Awaited<ReturnType<typeof investmentFactCgt>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactCgt<TData = Awaited<ReturnType<typeof investmentFactCgt>>, TError = unknown>(
 params?: InvestmentFactCgtParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactCgt>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentFactCgt<TData = Awaited<ReturnType<typeof investmentFactCgt>>, TError = unknown>(
 params?: InvestmentFactCgtParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactCgt>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentFactCgtQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentFactNfrsResponse200 = {
  data: InvestmentFactNfrs200
  status: 200
}

export type investmentFactNfrsResponseSuccess = (investmentFactNfrsResponse200) & {
  headers: Headers;
};
;

export type investmentFactNfrsResponse = (investmentFactNfrsResponseSuccess)

export const getInvestmentFactNfrsUrl = (params?: InvestmentFactNfrsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/facts/nfrs?${stringifiedParams}` : `/api/facts/nfrs`
}

export const investmentFactNfrs = async (params?: InvestmentFactNfrsParams, options?: RequestInit): Promise<investmentFactNfrsResponse> => {

  return customMutator<investmentFactNfrsResponse>(getInvestmentFactNfrsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentFactNfrsQueryKey = (params?: InvestmentFactNfrsParams,) => {
    return [
    `/api/facts/nfrs`, ...(params ? [params] : [])
    ] as const;
    }


export const getInvestmentFactNfrsQueryOptions = <TData = Awaited<ReturnType<typeof investmentFactNfrs>>, TError = unknown>(params?: InvestmentFactNfrsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactNfrs>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentFactNfrsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentFactNfrs>>> = ({ signal }) => investmentFactNfrs(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentFactNfrs>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentFactNfrsQueryResult = NonNullable<Awaited<ReturnType<typeof investmentFactNfrs>>>
export type InvestmentFactNfrsQueryError = unknown


export function useInvestmentFactNfrs<TData = Awaited<ReturnType<typeof investmentFactNfrs>>, TError = unknown>(
 params: undefined |  InvestmentFactNfrsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactNfrs>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactNfrs>>,
          TError,
          Awaited<ReturnType<typeof investmentFactNfrs>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactNfrs<TData = Awaited<ReturnType<typeof investmentFactNfrs>>, TError = unknown>(
 params?: InvestmentFactNfrsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactNfrs>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactNfrs>>,
          TError,
          Awaited<ReturnType<typeof investmentFactNfrs>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactNfrs<TData = Awaited<ReturnType<typeof investmentFactNfrs>>, TError = unknown>(
 params?: InvestmentFactNfrsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactNfrs>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentFactNfrs<TData = Awaited<ReturnType<typeof investmentFactNfrs>>, TError = unknown>(
 params?: InvestmentFactNfrsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactNfrs>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentFactNfrsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentFactCreateResponse200 = {
  data: void
  status: 200
}

export type investmentFactCreateResponse201 = {
  data: InvestmentFactCreate201
  status: 201
}

export type investmentFactCreateResponseSuccess = (investmentFactCreateResponse200 | investmentFactCreateResponse201) & {
  headers: Headers;
};
;

export type investmentFactCreateResponse = (investmentFactCreateResponseSuccess)

export const getInvestmentFactCreateUrl = () => {




  return `/api/facts/generate`
}

export const investmentFactCreate = async (generateFactsDto: GenerateFactsDto, options?: RequestInit): Promise<investmentFactCreateResponse> => {

  return customMutator<investmentFactCreateResponse>(getInvestmentFactCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(generateFactsDto)
  }
);}





export const getInvestmentFactCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentFactCreate>>, TError,{data: GenerateFactsDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentFactCreate>>, TError,{data: GenerateFactsDto}, TContext> => {

const mutationKey = ['investmentFactCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentFactCreate>>, {data: GenerateFactsDto}> = (props) => {
          const {data} = props ?? {};

          return  investmentFactCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentFactCreateMutationResult = NonNullable<Awaited<ReturnType<typeof investmentFactCreate>>>
    export type InvestmentFactCreateMutationBody = GenerateFactsDto
    export type InvestmentFactCreateMutationError = unknown

    export const useInvestmentFactCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentFactCreate>>, TError,{data: GenerateFactsDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentFactCreate>>,
        TError,
        {data: GenerateFactsDto},
        TContext
      > => {
      return useMutation(getInvestmentFactCreateMutationOptions(options), queryClient);
    }

export type investmentFactBatchesListResponse200 = {
  data: InvestmentFactBatchesList200
  status: 200
}

export type investmentFactBatchesListResponseSuccess = (investmentFactBatchesListResponse200) & {
  headers: Headers;
};
;

export type investmentFactBatchesListResponse = (investmentFactBatchesListResponseSuccess)

export const getInvestmentFactBatchesListUrl = () => {




  return `/api/fact-batches`
}

export const investmentFactBatchesList = async ( options?: RequestInit): Promise<investmentFactBatchesListResponse> => {

  return customMutator<investmentFactBatchesListResponse>(getInvestmentFactBatchesListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentFactBatchesListQueryKey = () => {
    return [
    `/api/fact-batches`
    ] as const;
    }


export const getInvestmentFactBatchesListQueryOptions = <TData = Awaited<ReturnType<typeof investmentFactBatchesList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentFactBatchesListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentFactBatchesList>>> = ({ signal }) => investmentFactBatchesList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchesList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentFactBatchesListQueryResult = NonNullable<Awaited<ReturnType<typeof investmentFactBatchesList>>>
export type InvestmentFactBatchesListQueryError = unknown


export function useInvestmentFactBatchesList<TData = Awaited<ReturnType<typeof investmentFactBatchesList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchesList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactBatchesList>>,
          TError,
          Awaited<ReturnType<typeof investmentFactBatchesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactBatchesList<TData = Awaited<ReturnType<typeof investmentFactBatchesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchesList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactBatchesList>>,
          TError,
          Awaited<ReturnType<typeof investmentFactBatchesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactBatchesList<TData = Awaited<ReturnType<typeof investmentFactBatchesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentFactBatchesList<TData = Awaited<ReturnType<typeof investmentFactBatchesList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentFactBatchesListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentFactBatchGetResponse200 = {
  data: InvestmentFactBatchGet200
  status: 200
}

export type investmentFactBatchGetResponseSuccess = (investmentFactBatchGetResponse200) & {
  headers: Headers;
};
;

export type investmentFactBatchGetResponse = (investmentFactBatchGetResponseSuccess)

export const getInvestmentFactBatchGetUrl = (id: string,) => {




  return `/api/fact-batches/${id}`
}

export const investmentFactBatchGet = async (id: string, options?: RequestInit): Promise<investmentFactBatchGetResponse> => {

  return customMutator<investmentFactBatchGetResponse>(getInvestmentFactBatchGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentFactBatchGetQueryKey = (id: string,) => {
    return [
    `/api/fact-batches/${id}`
    ] as const;
    }


export const getInvestmentFactBatchGetQueryOptions = <TData = Awaited<ReturnType<typeof investmentFactBatchGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentFactBatchGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentFactBatchGet>>> = ({ signal }) => investmentFactBatchGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentFactBatchGetQueryResult = NonNullable<Awaited<ReturnType<typeof investmentFactBatchGet>>>
export type InvestmentFactBatchGetQueryError = unknown


export function useInvestmentFactBatchGet<TData = Awaited<ReturnType<typeof investmentFactBatchGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactBatchGet>>,
          TError,
          Awaited<ReturnType<typeof investmentFactBatchGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactBatchGet<TData = Awaited<ReturnType<typeof investmentFactBatchGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactBatchGet>>,
          TError,
          Awaited<ReturnType<typeof investmentFactBatchGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactBatchGet<TData = Awaited<ReturnType<typeof investmentFactBatchGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentFactBatchGet<TData = Awaited<ReturnType<typeof investmentFactBatchGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentFactBatchGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentFactBatchLineageResponse200 = {
  data: InvestmentFactBatchLineage200
  status: 200
}

export type investmentFactBatchLineageResponseSuccess = (investmentFactBatchLineageResponse200) & {
  headers: Headers;
};
;

export type investmentFactBatchLineageResponse = (investmentFactBatchLineageResponseSuccess)

export const getInvestmentFactBatchLineageUrl = (id: string,) => {




  return `/api/fact-batches/${id}/lineage`
}

export const investmentFactBatchLineage = async (id: string, options?: RequestInit): Promise<investmentFactBatchLineageResponse> => {

  return customMutator<investmentFactBatchLineageResponse>(getInvestmentFactBatchLineageUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInvestmentFactBatchLineageQueryKey = (id: string,) => {
    return [
    `/api/fact-batches/${id}/lineage`
    ] as const;
    }


export const getInvestmentFactBatchLineageQueryOptions = <TData = Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInvestmentFactBatchLineageQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof investmentFactBatchLineage>>> = ({ signal }) => investmentFactBatchLineage(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type InvestmentFactBatchLineageQueryResult = NonNullable<Awaited<ReturnType<typeof investmentFactBatchLineage>>>
export type InvestmentFactBatchLineageQueryError = unknown


export function useInvestmentFactBatchLineage<TData = Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactBatchLineage>>,
          TError,
          Awaited<ReturnType<typeof investmentFactBatchLineage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactBatchLineage<TData = Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof investmentFactBatchLineage>>,
          TError,
          Awaited<ReturnType<typeof investmentFactBatchLineage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useInvestmentFactBatchLineage<TData = Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useInvestmentFactBatchLineage<TData = Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof investmentFactBatchLineage>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getInvestmentFactBatchLineageQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reportingValuationRunCreateResponse201 = {
  data: void
  status: 201
}

export type reportingValuationRunCreateResponseSuccess = (reportingValuationRunCreateResponse201) & {
  headers: Headers;
};
;

export type reportingValuationRunCreateResponse = (reportingValuationRunCreateResponseSuccess)

export const getReportingValuationRunCreateUrl = () => {




  return `/api/reporting/valuation-runs`
}

export const reportingValuationRunCreate = async ( options?: RequestInit): Promise<reportingValuationRunCreateResponse> => {

  return customMutator<reportingValuationRunCreateResponse>(getReportingValuationRunCreateUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReportingValuationRunCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunCreate>>, TError,void, TContext> => {

const mutationKey = ['reportingValuationRunCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingValuationRunCreate>>, void> = () => {


          return  reportingValuationRunCreate(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingValuationRunCreateMutationResult = NonNullable<Awaited<ReturnType<typeof reportingValuationRunCreate>>>

    export type ReportingValuationRunCreateMutationError = unknown

    export const useReportingValuationRunCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunCreate>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingValuationRunCreate>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getReportingValuationRunCreateMutationOptions(options), queryClient);
    }

export type reportingValuationRunListResponse200 = {
  data: void
  status: 200
}

export type reportingValuationRunListResponseSuccess = (reportingValuationRunListResponse200) & {
  headers: Headers;
};
;

export type reportingValuationRunListResponse = (reportingValuationRunListResponseSuccess)

export const getReportingValuationRunListUrl = () => {




  return `/api/reporting/valuation-runs`
}

export const reportingValuationRunList = async ( options?: RequestInit): Promise<reportingValuationRunListResponse> => {

  return customMutator<reportingValuationRunListResponse>(getReportingValuationRunListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReportingValuationRunListQueryKey = () => {
    return [
    `/api/reporting/valuation-runs`
    ] as const;
    }


export const getReportingValuationRunListQueryOptions = <TData = Awaited<ReturnType<typeof reportingValuationRunList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingValuationRunList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReportingValuationRunListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reportingValuationRunList>>> = ({ signal }) => reportingValuationRunList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reportingValuationRunList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReportingValuationRunListQueryResult = NonNullable<Awaited<ReturnType<typeof reportingValuationRunList>>>
export type ReportingValuationRunListQueryError = unknown


export function useReportingValuationRunList<TData = Awaited<ReturnType<typeof reportingValuationRunList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingValuationRunList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reportingValuationRunList>>,
          TError,
          Awaited<ReturnType<typeof reportingValuationRunList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReportingValuationRunList<TData = Awaited<ReturnType<typeof reportingValuationRunList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingValuationRunList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reportingValuationRunList>>,
          TError,
          Awaited<ReturnType<typeof reportingValuationRunList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReportingValuationRunList<TData = Awaited<ReturnType<typeof reportingValuationRunList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingValuationRunList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReportingValuationRunList<TData = Awaited<ReturnType<typeof reportingValuationRunList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingValuationRunList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReportingValuationRunListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reportingValuationRunApproveResponse201 = {
  data: void
  status: 201
}

export type reportingValuationRunApproveResponseSuccess = (reportingValuationRunApproveResponse201) & {
  headers: Headers;
};
;

export type reportingValuationRunApproveResponse = (reportingValuationRunApproveResponseSuccess)

export const getReportingValuationRunApproveUrl = (id: string,) => {




  return `/api/reporting/valuation-runs/${id}/approve`
}

export const reportingValuationRunApprove = async (id: string, options?: RequestInit): Promise<reportingValuationRunApproveResponse> => {

  return customMutator<reportingValuationRunApproveResponse>(getReportingValuationRunApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReportingValuationRunApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['reportingValuationRunApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingValuationRunApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  reportingValuationRunApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingValuationRunApproveMutationResult = NonNullable<Awaited<ReturnType<typeof reportingValuationRunApprove>>>

    export type ReportingValuationRunApproveMutationError = unknown

    export const useReportingValuationRunApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingValuationRunApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getReportingValuationRunApproveMutationOptions(options), queryClient);
    }

export type reportingValuationRunPublishResponse201 = {
  data: void
  status: 201
}

export type reportingValuationRunPublishResponseSuccess = (reportingValuationRunPublishResponse201) & {
  headers: Headers;
};
;

export type reportingValuationRunPublishResponse = (reportingValuationRunPublishResponseSuccess)

export const getReportingValuationRunPublishUrl = (id: string,) => {




  return `/api/reporting/valuation-runs/${id}/publish`
}

export const reportingValuationRunPublish = async (id: string, options?: RequestInit): Promise<reportingValuationRunPublishResponse> => {

  return customMutator<reportingValuationRunPublishResponse>(getReportingValuationRunPublishUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReportingValuationRunPublishMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunPublish>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunPublish>>, TError,{id: string}, TContext> => {

const mutationKey = ['reportingValuationRunPublish'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingValuationRunPublish>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  reportingValuationRunPublish(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingValuationRunPublishMutationResult = NonNullable<Awaited<ReturnType<typeof reportingValuationRunPublish>>>

    export type ReportingValuationRunPublishMutationError = unknown

    export const useReportingValuationRunPublish = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunPublish>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingValuationRunPublish>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getReportingValuationRunPublishMutationOptions(options), queryClient);
    }

export type reportingValuationRunReopenRequestResponse201 = {
  data: void
  status: 201
}

export type reportingValuationRunReopenRequestResponseSuccess = (reportingValuationRunReopenRequestResponse201) & {
  headers: Headers;
};
;

export type reportingValuationRunReopenRequestResponse = (reportingValuationRunReopenRequestResponseSuccess)

export const getReportingValuationRunReopenRequestUrl = (id: string,) => {




  return `/api/reporting/valuation-runs/${id}/reopen`
}

export const reportingValuationRunReopenRequest = async (id: string, options?: RequestInit): Promise<reportingValuationRunReopenRequestResponse> => {

  return customMutator<reportingValuationRunReopenRequestResponse>(getReportingValuationRunReopenRequestUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReportingValuationRunReopenRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunReopenRequest>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunReopenRequest>>, TError,{id: string}, TContext> => {

const mutationKey = ['reportingValuationRunReopenRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingValuationRunReopenRequest>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  reportingValuationRunReopenRequest(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingValuationRunReopenRequestMutationResult = NonNullable<Awaited<ReturnType<typeof reportingValuationRunReopenRequest>>>

    export type ReportingValuationRunReopenRequestMutationError = unknown

    export const useReportingValuationRunReopenRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunReopenRequest>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingValuationRunReopenRequest>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getReportingValuationRunReopenRequestMutationOptions(options), queryClient);
    }

export type reportingValuationRunReopenApproveResponse201 = {
  data: void
  status: 201
}

export type reportingValuationRunReopenApproveResponseSuccess = (reportingValuationRunReopenApproveResponse201) & {
  headers: Headers;
};
;

export type reportingValuationRunReopenApproveResponse = (reportingValuationRunReopenApproveResponseSuccess)

export const getReportingValuationRunReopenApproveUrl = (id: string,) => {




  return `/api/reporting/valuation-runs/${id}/reopen/approve`
}

export const reportingValuationRunReopenApprove = async (id: string, options?: RequestInit): Promise<reportingValuationRunReopenApproveResponse> => {

  return customMutator<reportingValuationRunReopenApproveResponse>(getReportingValuationRunReopenApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReportingValuationRunReopenApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunReopenApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunReopenApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['reportingValuationRunReopenApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingValuationRunReopenApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  reportingValuationRunReopenApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingValuationRunReopenApproveMutationResult = NonNullable<Awaited<ReturnType<typeof reportingValuationRunReopenApprove>>>

    export type ReportingValuationRunReopenApproveMutationError = unknown

    export const useReportingValuationRunReopenApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingValuationRunReopenApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingValuationRunReopenApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getReportingValuationRunReopenApproveMutationOptions(options), queryClient);
    }

export type reportingPerformanceReturnCalculateResponse201 = {
  data: void
  status: 201
}

export type reportingPerformanceReturnCalculateResponseSuccess = (reportingPerformanceReturnCalculateResponse201) & {
  headers: Headers;
};
;

export type reportingPerformanceReturnCalculateResponse = (reportingPerformanceReturnCalculateResponseSuccess)

export const getReportingPerformanceReturnCalculateUrl = () => {




  return `/api/reporting/performance-returns/calculate`
}

export const reportingPerformanceReturnCalculate = async (calculatePerformanceReturnDto: CalculatePerformanceReturnDto, options?: RequestInit): Promise<reportingPerformanceReturnCalculateResponse> => {

  return customMutator<reportingPerformanceReturnCalculateResponse>(getReportingPerformanceReturnCalculateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(calculatePerformanceReturnDto)
  }
);}





export const getReportingPerformanceReturnCalculateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCalculate>>, TError,{data: CalculatePerformanceReturnDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCalculate>>, TError,{data: CalculatePerformanceReturnDto}, TContext> => {

const mutationKey = ['reportingPerformanceReturnCalculate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingPerformanceReturnCalculate>>, {data: CalculatePerformanceReturnDto}> = (props) => {
          const {data} = props ?? {};

          return  reportingPerformanceReturnCalculate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingPerformanceReturnCalculateMutationResult = NonNullable<Awaited<ReturnType<typeof reportingPerformanceReturnCalculate>>>
    export type ReportingPerformanceReturnCalculateMutationBody = CalculatePerformanceReturnDto
    export type ReportingPerformanceReturnCalculateMutationError = unknown

    export const useReportingPerformanceReturnCalculate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCalculate>>, TError,{data: CalculatePerformanceReturnDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingPerformanceReturnCalculate>>,
        TError,
        {data: CalculatePerformanceReturnDto},
        TContext
      > => {
      return useMutation(getReportingPerformanceReturnCalculateMutationOptions(options), queryClient);
    }

export type reportingFixedIncomeValuationCalculateResponse201 = {
  data: void
  status: 201
}

export type reportingFixedIncomeValuationCalculateResponseSuccess = (reportingFixedIncomeValuationCalculateResponse201) & {
  headers: Headers;
};
;

export type reportingFixedIncomeValuationCalculateResponse = (reportingFixedIncomeValuationCalculateResponseSuccess)

export const getReportingFixedIncomeValuationCalculateUrl = () => {




  return `/api/reporting/fixed-income-valuations/calculate`
}

export const reportingFixedIncomeValuationCalculate = async (calculateFixedIncomeValuationDto: CalculateFixedIncomeValuationDto, options?: RequestInit): Promise<reportingFixedIncomeValuationCalculateResponse> => {

  return customMutator<reportingFixedIncomeValuationCalculateResponse>(getReportingFixedIncomeValuationCalculateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(calculateFixedIncomeValuationDto)
  }
);}





export const getReportingFixedIncomeValuationCalculateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeValuationCalculate>>, TError,{data: CalculateFixedIncomeValuationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeValuationCalculate>>, TError,{data: CalculateFixedIncomeValuationDto}, TContext> => {

const mutationKey = ['reportingFixedIncomeValuationCalculate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingFixedIncomeValuationCalculate>>, {data: CalculateFixedIncomeValuationDto}> = (props) => {
          const {data} = props ?? {};

          return  reportingFixedIncomeValuationCalculate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingFixedIncomeValuationCalculateMutationResult = NonNullable<Awaited<ReturnType<typeof reportingFixedIncomeValuationCalculate>>>
    export type ReportingFixedIncomeValuationCalculateMutationBody = CalculateFixedIncomeValuationDto
    export type ReportingFixedIncomeValuationCalculateMutationError = unknown

    export const useReportingFixedIncomeValuationCalculate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeValuationCalculate>>, TError,{data: CalculateFixedIncomeValuationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingFixedIncomeValuationCalculate>>,
        TError,
        {data: CalculateFixedIncomeValuationDto},
        TContext
      > => {
      return useMutation(getReportingFixedIncomeValuationCalculateMutationOptions(options), queryClient);
    }

export type reportingFixedIncomeLifecycleScheduleResponse201 = {
  data: void
  status: 201
}

export type reportingFixedIncomeLifecycleScheduleResponseSuccess = (reportingFixedIncomeLifecycleScheduleResponse201) & {
  headers: Headers;
};
;

export type reportingFixedIncomeLifecycleScheduleResponse = (reportingFixedIncomeLifecycleScheduleResponseSuccess)

export const getReportingFixedIncomeLifecycleScheduleUrl = () => {




  return `/api/reporting/fixed-income-lifecycle-events/schedule`
}

export const reportingFixedIncomeLifecycleSchedule = async (scheduleFixedIncomeLifecycleDto: ScheduleFixedIncomeLifecycleDto, options?: RequestInit): Promise<reportingFixedIncomeLifecycleScheduleResponse> => {

  return customMutator<reportingFixedIncomeLifecycleScheduleResponse>(getReportingFixedIncomeLifecycleScheduleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(scheduleFixedIncomeLifecycleDto)
  }
);}





export const getReportingFixedIncomeLifecycleScheduleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleSchedule>>, TError,{data: ScheduleFixedIncomeLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleSchedule>>, TError,{data: ScheduleFixedIncomeLifecycleDto}, TContext> => {

const mutationKey = ['reportingFixedIncomeLifecycleSchedule'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleSchedule>>, {data: ScheduleFixedIncomeLifecycleDto}> = (props) => {
          const {data} = props ?? {};

          return  reportingFixedIncomeLifecycleSchedule(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingFixedIncomeLifecycleScheduleMutationResult = NonNullable<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleSchedule>>>
    export type ReportingFixedIncomeLifecycleScheduleMutationBody = ScheduleFixedIncomeLifecycleDto
    export type ReportingFixedIncomeLifecycleScheduleMutationError = unknown

    export const useReportingFixedIncomeLifecycleSchedule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleSchedule>>, TError,{data: ScheduleFixedIncomeLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingFixedIncomeLifecycleSchedule>>,
        TError,
        {data: ScheduleFixedIncomeLifecycleDto},
        TContext
      > => {
      return useMutation(getReportingFixedIncomeLifecycleScheduleMutationOptions(options), queryClient);
    }

export type reportingFixedIncomeLifecycleApproveResponse201 = {
  data: void
  status: 201
}

export type reportingFixedIncomeLifecycleApproveResponseSuccess = (reportingFixedIncomeLifecycleApproveResponse201) & {
  headers: Headers;
};
;

export type reportingFixedIncomeLifecycleApproveResponse = (reportingFixedIncomeLifecycleApproveResponseSuccess)

export const getReportingFixedIncomeLifecycleApproveUrl = (id: string,) => {




  return `/api/reporting/fixed-income-lifecycle-events/${id}/approve`
}

export const reportingFixedIncomeLifecycleApprove = async (id: string,
    transitionFixedIncomeLifecycleDto: TransitionFixedIncomeLifecycleDto, options?: RequestInit): Promise<reportingFixedIncomeLifecycleApproveResponse> => {

  return customMutator<reportingFixedIncomeLifecycleApproveResponse>(getReportingFixedIncomeLifecycleApproveUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(transitionFixedIncomeLifecycleDto)
  }
);}





export const getReportingFixedIncomeLifecycleApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleApprove>>, TError,{id: string;data: TransitionFixedIncomeLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleApprove>>, TError,{id: string;data: TransitionFixedIncomeLifecycleDto}, TContext> => {

const mutationKey = ['reportingFixedIncomeLifecycleApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleApprove>>, {id: string;data: TransitionFixedIncomeLifecycleDto}> = (props) => {
          const {id,data} = props ?? {};

          return  reportingFixedIncomeLifecycleApprove(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingFixedIncomeLifecycleApproveMutationResult = NonNullable<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleApprove>>>
    export type ReportingFixedIncomeLifecycleApproveMutationBody = TransitionFixedIncomeLifecycleDto
    export type ReportingFixedIncomeLifecycleApproveMutationError = unknown

    export const useReportingFixedIncomeLifecycleApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeLifecycleApprove>>, TError,{id: string;data: TransitionFixedIncomeLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingFixedIncomeLifecycleApprove>>,
        TError,
        {id: string;data: TransitionFixedIncomeLifecycleDto},
        TContext
      > => {
      return useMutation(getReportingFixedIncomeLifecycleApproveMutationOptions(options), queryClient);
    }

export type reportingFixedIncomeLifecyclePostResponse201 = {
  data: void
  status: 201
}

export type reportingFixedIncomeLifecyclePostResponseSuccess = (reportingFixedIncomeLifecyclePostResponse201) & {
  headers: Headers;
};
;

export type reportingFixedIncomeLifecyclePostResponse = (reportingFixedIncomeLifecyclePostResponseSuccess)

export const getReportingFixedIncomeLifecyclePostUrl = (id: string,) => {




  return `/api/reporting/fixed-income-lifecycle-events/${id}/post`
}

export const reportingFixedIncomeLifecyclePost = async (id: string,
    transitionFixedIncomeLifecycleDto: TransitionFixedIncomeLifecycleDto, options?: RequestInit): Promise<reportingFixedIncomeLifecyclePostResponse> => {

  return customMutator<reportingFixedIncomeLifecyclePostResponse>(getReportingFixedIncomeLifecyclePostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(transitionFixedIncomeLifecycleDto)
  }
);}





export const getReportingFixedIncomeLifecyclePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeLifecyclePost>>, TError,{id: string;data: TransitionFixedIncomeLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeLifecyclePost>>, TError,{id: string;data: TransitionFixedIncomeLifecycleDto}, TContext> => {

const mutationKey = ['reportingFixedIncomeLifecyclePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingFixedIncomeLifecyclePost>>, {id: string;data: TransitionFixedIncomeLifecycleDto}> = (props) => {
          const {id,data} = props ?? {};

          return  reportingFixedIncomeLifecyclePost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingFixedIncomeLifecyclePostMutationResult = NonNullable<Awaited<ReturnType<typeof reportingFixedIncomeLifecyclePost>>>
    export type ReportingFixedIncomeLifecyclePostMutationBody = TransitionFixedIncomeLifecycleDto
    export type ReportingFixedIncomeLifecyclePostMutationError = unknown

    export const useReportingFixedIncomeLifecyclePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeLifecyclePost>>, TError,{id: string;data: TransitionFixedIncomeLifecycleDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingFixedIncomeLifecyclePost>>,
        TError,
        {id: string;data: TransitionFixedIncomeLifecycleDto},
        TContext
      > => {
      return useMutation(getReportingFixedIncomeLifecyclePostMutationOptions(options), queryClient);
    }

export type reportingFixedIncomeDebtEventProcessResponse201 = {
  data: void
  status: 201
}

export type reportingFixedIncomeDebtEventProcessResponseSuccess = (reportingFixedIncomeDebtEventProcessResponse201) & {
  headers: Headers;
};
;

export type reportingFixedIncomeDebtEventProcessResponse = (reportingFixedIncomeDebtEventProcessResponseSuccess)

export const getReportingFixedIncomeDebtEventProcessUrl = () => {




  return `/api/reporting/fixed-income-debt-events/process`
}

export const reportingFixedIncomeDebtEventProcess = async (processFixedIncomeDebtEventDto: ProcessFixedIncomeDebtEventDto, options?: RequestInit): Promise<reportingFixedIncomeDebtEventProcessResponse> => {

  return customMutator<reportingFixedIncomeDebtEventProcessResponse>(getReportingFixedIncomeDebtEventProcessUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(processFixedIncomeDebtEventDto)
  }
);}





export const getReportingFixedIncomeDebtEventProcessMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeDebtEventProcess>>, TError,{data: ProcessFixedIncomeDebtEventDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeDebtEventProcess>>, TError,{data: ProcessFixedIncomeDebtEventDto}, TContext> => {

const mutationKey = ['reportingFixedIncomeDebtEventProcess'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingFixedIncomeDebtEventProcess>>, {data: ProcessFixedIncomeDebtEventDto}> = (props) => {
          const {data} = props ?? {};

          return  reportingFixedIncomeDebtEventProcess(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingFixedIncomeDebtEventProcessMutationResult = NonNullable<Awaited<ReturnType<typeof reportingFixedIncomeDebtEventProcess>>>
    export type ReportingFixedIncomeDebtEventProcessMutationBody = ProcessFixedIncomeDebtEventDto
    export type ReportingFixedIncomeDebtEventProcessMutationError = unknown

    export const useReportingFixedIncomeDebtEventProcess = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingFixedIncomeDebtEventProcess>>, TError,{data: ProcessFixedIncomeDebtEventDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingFixedIncomeDebtEventProcess>>,
        TError,
        {data: ProcessFixedIncomeDebtEventDto},
        TContext
      > => {
      return useMutation(getReportingFixedIncomeDebtEventProcessMutationOptions(options), queryClient);
    }

export type reportingPerformanceReturnListResponse200 = {
  data: void
  status: 200
}

export type reportingPerformanceReturnListResponseSuccess = (reportingPerformanceReturnListResponse200) & {
  headers: Headers;
};
;

export type reportingPerformanceReturnListResponse = (reportingPerformanceReturnListResponseSuccess)

export const getReportingPerformanceReturnListUrl = () => {




  return `/api/reporting/performance-returns`
}

export const reportingPerformanceReturnList = async ( options?: RequestInit): Promise<reportingPerformanceReturnListResponse> => {

  return customMutator<reportingPerformanceReturnListResponse>(getReportingPerformanceReturnListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReportingPerformanceReturnListQueryKey = () => {
    return [
    `/api/reporting/performance-returns`
    ] as const;
    }


export const getReportingPerformanceReturnListQueryOptions = <TData = Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReportingPerformanceReturnListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reportingPerformanceReturnList>>> = ({ signal }) => reportingPerformanceReturnList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReportingPerformanceReturnListQueryResult = NonNullable<Awaited<ReturnType<typeof reportingPerformanceReturnList>>>
export type ReportingPerformanceReturnListQueryError = unknown


export function useReportingPerformanceReturnList<TData = Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reportingPerformanceReturnList>>,
          TError,
          Awaited<ReturnType<typeof reportingPerformanceReturnList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReportingPerformanceReturnList<TData = Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reportingPerformanceReturnList>>,
          TError,
          Awaited<ReturnType<typeof reportingPerformanceReturnList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReportingPerformanceReturnList<TData = Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReportingPerformanceReturnList<TData = Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportingPerformanceReturnList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReportingPerformanceReturnListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reportingPerformanceReturnCorrectionRequestResponse201 = {
  data: void
  status: 201
}

export type reportingPerformanceReturnCorrectionRequestResponseSuccess = (reportingPerformanceReturnCorrectionRequestResponse201) & {
  headers: Headers;
};
;

export type reportingPerformanceReturnCorrectionRequestResponse = (reportingPerformanceReturnCorrectionRequestResponseSuccess)

export const getReportingPerformanceReturnCorrectionRequestUrl = (id: string,) => {




  return `/api/reporting/performance-returns/${id}/corrections`
}

export const reportingPerformanceReturnCorrectionRequest = async (id: string,
    requestPerformanceReturnCorrectionDto: RequestPerformanceReturnCorrectionDto, options?: RequestInit): Promise<reportingPerformanceReturnCorrectionRequestResponse> => {

  return customMutator<reportingPerformanceReturnCorrectionRequestResponse>(getReportingPerformanceReturnCorrectionRequestUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(requestPerformanceReturnCorrectionDto)
  }
);}





export const getReportingPerformanceReturnCorrectionRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionRequest>>, TError,{id: string;data: RequestPerformanceReturnCorrectionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionRequest>>, TError,{id: string;data: RequestPerformanceReturnCorrectionDto}, TContext> => {

const mutationKey = ['reportingPerformanceReturnCorrectionRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionRequest>>, {id: string;data: RequestPerformanceReturnCorrectionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  reportingPerformanceReturnCorrectionRequest(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingPerformanceReturnCorrectionRequestMutationResult = NonNullable<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionRequest>>>
    export type ReportingPerformanceReturnCorrectionRequestMutationBody = RequestPerformanceReturnCorrectionDto
    export type ReportingPerformanceReturnCorrectionRequestMutationError = unknown

    export const useReportingPerformanceReturnCorrectionRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionRequest>>, TError,{id: string;data: RequestPerformanceReturnCorrectionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionRequest>>,
        TError,
        {id: string;data: RequestPerformanceReturnCorrectionDto},
        TContext
      > => {
      return useMutation(getReportingPerformanceReturnCorrectionRequestMutationOptions(options), queryClient);
    }

export type reportingPerformanceReturnCorrectionApproveResponse201 = {
  data: void
  status: 201
}

export type reportingPerformanceReturnCorrectionApproveResponseSuccess = (reportingPerformanceReturnCorrectionApproveResponse201) & {
  headers: Headers;
};
;

export type reportingPerformanceReturnCorrectionApproveResponse = (reportingPerformanceReturnCorrectionApproveResponseSuccess)

export const getReportingPerformanceReturnCorrectionApproveUrl = (id: string,) => {




  return `/api/reporting/performance-returns/${id}/corrections/approve`
}

export const reportingPerformanceReturnCorrectionApprove = async (id: string,
    approvePerformanceReturnCorrectionDto: ApprovePerformanceReturnCorrectionDto, options?: RequestInit): Promise<reportingPerformanceReturnCorrectionApproveResponse> => {

  return customMutator<reportingPerformanceReturnCorrectionApproveResponse>(getReportingPerformanceReturnCorrectionApproveUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(approvePerformanceReturnCorrectionDto)
  }
);}





export const getReportingPerformanceReturnCorrectionApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionApprove>>, TError,{id: string;data: ApprovePerformanceReturnCorrectionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionApprove>>, TError,{id: string;data: ApprovePerformanceReturnCorrectionDto}, TContext> => {

const mutationKey = ['reportingPerformanceReturnCorrectionApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionApprove>>, {id: string;data: ApprovePerformanceReturnCorrectionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  reportingPerformanceReturnCorrectionApprove(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingPerformanceReturnCorrectionApproveMutationResult = NonNullable<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionApprove>>>
    export type ReportingPerformanceReturnCorrectionApproveMutationBody = ApprovePerformanceReturnCorrectionDto
    export type ReportingPerformanceReturnCorrectionApproveMutationError = unknown

    export const useReportingPerformanceReturnCorrectionApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionApprove>>, TError,{id: string;data: ApprovePerformanceReturnCorrectionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionApprove>>,
        TError,
        {id: string;data: ApprovePerformanceReturnCorrectionDto},
        TContext
      > => {
      return useMutation(getReportingPerformanceReturnCorrectionApproveMutationOptions(options), queryClient);
    }

export type reportingPerformanceReturnCorrectionFinalizeResponse201 = {
  data: void
  status: 201
}

export type reportingPerformanceReturnCorrectionFinalizeResponseSuccess = (reportingPerformanceReturnCorrectionFinalizeResponse201) & {
  headers: Headers;
};
;

export type reportingPerformanceReturnCorrectionFinalizeResponse = (reportingPerformanceReturnCorrectionFinalizeResponseSuccess)

export const getReportingPerformanceReturnCorrectionFinalizeUrl = (id: string,) => {




  return `/api/reporting/performance-returns/${id}/corrections/finalize`
}

export const reportingPerformanceReturnCorrectionFinalize = async (id: string,
    finalizePerformanceReturnCorrectionDto: FinalizePerformanceReturnCorrectionDto, options?: RequestInit): Promise<reportingPerformanceReturnCorrectionFinalizeResponse> => {

  return customMutator<reportingPerformanceReturnCorrectionFinalizeResponse>(getReportingPerformanceReturnCorrectionFinalizeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(finalizePerformanceReturnCorrectionDto)
  }
);}





export const getReportingPerformanceReturnCorrectionFinalizeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionFinalize>>, TError,{id: string;data: FinalizePerformanceReturnCorrectionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionFinalize>>, TError,{id: string;data: FinalizePerformanceReturnCorrectionDto}, TContext> => {

const mutationKey = ['reportingPerformanceReturnCorrectionFinalize'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionFinalize>>, {id: string;data: FinalizePerformanceReturnCorrectionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  reportingPerformanceReturnCorrectionFinalize(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReportingPerformanceReturnCorrectionFinalizeMutationResult = NonNullable<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionFinalize>>>
    export type ReportingPerformanceReturnCorrectionFinalizeMutationBody = FinalizePerformanceReturnCorrectionDto
    export type ReportingPerformanceReturnCorrectionFinalizeMutationError = unknown

    export const useReportingPerformanceReturnCorrectionFinalize = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionFinalize>>, TError,{id: string;data: FinalizePerformanceReturnCorrectionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reportingPerformanceReturnCorrectionFinalize>>,
        TError,
        {id: string;data: FinalizePerformanceReturnCorrectionDto},
        TContext
      > => {
      return useMutation(getReportingPerformanceReturnCorrectionFinalizeMutationOptions(options), queryClient);
    }

export type reportsViewResponse200 = {
  data: ReportsView200
  status: 200
}

export type reportsViewResponseSuccess = (reportsViewResponse200) & {
  headers: Headers;
};
;

export type reportsViewResponse = (reportsViewResponseSuccess)

export const getReportsViewUrl = () => {




  return `/api/reporting/catalog`
}

export const reportsView = async ( options?: RequestInit): Promise<reportsViewResponse> => {

  return customMutator<reportsViewResponse>(getReportsViewUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReportsViewQueryKey = () => {
    return [
    `/api/reporting/catalog`
    ] as const;
    }


export const getReportsViewQueryOptions = <TData = Awaited<ReturnType<typeof reportsView>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportsView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReportsViewQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reportsView>>> = ({ signal }) => reportsView({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reportsView>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReportsViewQueryResult = NonNullable<Awaited<ReturnType<typeof reportsView>>>
export type ReportsViewQueryError = unknown


export function useReportsView<TData = Awaited<ReturnType<typeof reportsView>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportsView>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reportsView>>,
          TError,
          Awaited<ReturnType<typeof reportsView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReportsView<TData = Awaited<ReturnType<typeof reportsView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportsView>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reportsView>>,
          TError,
          Awaited<ReturnType<typeof reportsView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReportsView<TData = Awaited<ReturnType<typeof reportsView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportsView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReportsView<TData = Awaited<ReturnType<typeof reportsView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reportsView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReportsViewQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type dashboardViewResponse200 = {
  data: DashboardView200
  status: 200
}

export type dashboardViewResponseSuccess = (dashboardViewResponse200) & {
  headers: Headers;
};
;

export type dashboardViewResponse = (dashboardViewResponseSuccess)

export const getDashboardViewUrl = () => {




  return `/api/reporting/dashboard/summary`
}

export const dashboardView = async ( options?: RequestInit): Promise<dashboardViewResponse> => {

  return customMutator<dashboardViewResponse>(getDashboardViewUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDashboardViewQueryKey = () => {
    return [
    `/api/reporting/dashboard/summary`
    ] as const;
    }


export const getDashboardViewQueryOptions = <TData = Awaited<ReturnType<typeof dashboardView>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof dashboardView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDashboardViewQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof dashboardView>>> = ({ signal }) => dashboardView({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof dashboardView>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type DashboardViewQueryResult = NonNullable<Awaited<ReturnType<typeof dashboardView>>>
export type DashboardViewQueryError = unknown


export function useDashboardView<TData = Awaited<ReturnType<typeof dashboardView>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof dashboardView>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof dashboardView>>,
          TError,
          Awaited<ReturnType<typeof dashboardView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDashboardView<TData = Awaited<ReturnType<typeof dashboardView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof dashboardView>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof dashboardView>>,
          TError,
          Awaited<ReturnType<typeof dashboardView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDashboardView<TData = Awaited<ReturnType<typeof dashboardView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof dashboardView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useDashboardView<TData = Awaited<ReturnType<typeof dashboardView>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof dashboardView>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getDashboardViewQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type clientDashboardReadResponse200 = {
  data: ClientDashboardRead200
  status: 200
}

export type clientDashboardReadResponseSuccess = (clientDashboardReadResponse200) & {
  headers: Headers;
};
;

export type clientDashboardReadResponse = (clientDashboardReadResponseSuccess)

export const getClientDashboardReadUrl = () => {




  return `/api/reporting/dashboard/client-summary`
}

export const clientDashboardRead = async ( options?: RequestInit): Promise<clientDashboardReadResponse> => {

  return customMutator<clientDashboardReadResponse>(getClientDashboardReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getClientDashboardReadQueryKey = () => {
    return [
    `/api/reporting/dashboard/client-summary`
    ] as const;
    }


export const getClientDashboardReadQueryOptions = <TData = Awaited<ReturnType<typeof clientDashboardRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientDashboardRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getClientDashboardReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof clientDashboardRead>>> = ({ signal }) => clientDashboardRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof clientDashboardRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ClientDashboardReadQueryResult = NonNullable<Awaited<ReturnType<typeof clientDashboardRead>>>
export type ClientDashboardReadQueryError = unknown


export function useClientDashboardRead<TData = Awaited<ReturnType<typeof clientDashboardRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientDashboardRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientDashboardRead>>,
          TError,
          Awaited<ReturnType<typeof clientDashboardRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientDashboardRead<TData = Awaited<ReturnType<typeof clientDashboardRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientDashboardRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientDashboardRead>>,
          TError,
          Awaited<ReturnType<typeof clientDashboardRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientDashboardRead<TData = Awaited<ReturnType<typeof clientDashboardRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientDashboardRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useClientDashboardRead<TData = Awaited<ReturnType<typeof clientDashboardRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientDashboardRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getClientDashboardReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformDashboardReadResponse200 = {
  data: PlatformDashboardRead200
  status: 200
}

export type platformDashboardReadResponseSuccess = (platformDashboardReadResponse200) & {
  headers: Headers;
};
;

export type platformDashboardReadResponse = (platformDashboardReadResponseSuccess)

export const getPlatformDashboardReadUrl = () => {




  return `/api/reporting/dashboard/platform-summary`
}

export const platformDashboardRead = async ( options?: RequestInit): Promise<platformDashboardReadResponse> => {

  return customMutator<platformDashboardReadResponse>(getPlatformDashboardReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformDashboardReadQueryKey = () => {
    return [
    `/api/reporting/dashboard/platform-summary`
    ] as const;
    }


export const getPlatformDashboardReadQueryOptions = <TData = Awaited<ReturnType<typeof platformDashboardRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDashboardRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformDashboardReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformDashboardRead>>> = ({ signal }) => platformDashboardRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformDashboardRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformDashboardReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformDashboardRead>>>
export type PlatformDashboardReadQueryError = unknown


export function usePlatformDashboardRead<TData = Awaited<ReturnType<typeof platformDashboardRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDashboardRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformDashboardRead>>,
          TError,
          Awaited<ReturnType<typeof platformDashboardRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformDashboardRead<TData = Awaited<ReturnType<typeof platformDashboardRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDashboardRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformDashboardRead>>,
          TError,
          Awaited<ReturnType<typeof platformDashboardRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformDashboardRead<TData = Awaited<ReturnType<typeof platformDashboardRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDashboardRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformDashboardRead<TData = Awaited<ReturnType<typeof platformDashboardRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformDashboardRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformDashboardReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type investmentPerformanceExportResponse200 = {
  data: InvestmentPerformanceExport200
  status: 200
}

export type investmentPerformanceExportResponseSuccess = (investmentPerformanceExportResponse200) & {
  headers: Headers;
};
;

export type investmentPerformanceExportResponse = (investmentPerformanceExportResponseSuccess)

export const getInvestmentPerformanceExportUrl = () => {




  return `/api/portfolio-performance/export`
}

export const investmentPerformanceExport = async ( options?: RequestInit): Promise<investmentPerformanceExportResponse> => {

  return customMutator<investmentPerformanceExportResponse>(getInvestmentPerformanceExportUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getInvestmentPerformanceExportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPerformanceExport>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof investmentPerformanceExport>>, TError,void, TContext> => {

const mutationKey = ['investmentPerformanceExport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof investmentPerformanceExport>>, void> = () => {


          return  investmentPerformanceExport(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InvestmentPerformanceExportMutationResult = NonNullable<Awaited<ReturnType<typeof investmentPerformanceExport>>>

    export type InvestmentPerformanceExportMutationError = unknown

    export const useInvestmentPerformanceExport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof investmentPerformanceExport>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof investmentPerformanceExport>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getInvestmentPerformanceExportMutationOptions(options), queryClient);
    }

export type importUploadResponse201 = {
  data: ImportUpload201
  status: 201
}

export type importUploadResponseSuccess = (importUploadResponse201) & {
  headers: Headers;
};
;

export type importUploadResponse = (importUploadResponseSuccess)

export const getImportUploadUrl = () => {




  return `/api/import/jobs`
}

export const importUpload = async (createImportJobDto: CreateImportJobDto, options?: RequestInit): Promise<importUploadResponse> => {

  return customMutator<importUploadResponse>(getImportUploadUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createImportJobDto)
  }
);}





export const getImportUploadMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importUpload>>, TError,{data: CreateImportJobDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof importUpload>>, TError,{data: CreateImportJobDto}, TContext> => {

const mutationKey = ['importUpload'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importUpload>>, {data: CreateImportJobDto}> = (props) => {
          const {data} = props ?? {};

          return  importUpload(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportUploadMutationResult = NonNullable<Awaited<ReturnType<typeof importUpload>>>
    export type ImportUploadMutationBody = CreateImportJobDto
    export type ImportUploadMutationError = unknown

    export const useImportUpload = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importUpload>>, TError,{data: CreateImportJobDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importUpload>>,
        TError,
        {data: CreateImportJobDto},
        TContext
      > => {
      return useMutation(getImportUploadMutationOptions(options), queryClient);
    }

export type importListResponse200 = {
  data: ImportList200
  status: 200
}

export type importListResponseSuccess = (importListResponse200) & {
  headers: Headers;
};
;

export type importListResponse = (importListResponseSuccess)

export const getImportListUrl = () => {




  return `/api/import/jobs`
}

export const importList = async ( options?: RequestInit): Promise<importListResponse> => {

  return customMutator<importListResponse>(getImportListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getImportListQueryKey = () => {
    return [
    `/api/import/jobs`
    ] as const;
    }


export const getImportListQueryOptions = <TData = Awaited<ReturnType<typeof importList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getImportListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof importList>>> = ({ signal }) => importList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof importList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ImportListQueryResult = NonNullable<Awaited<ReturnType<typeof importList>>>
export type ImportListQueryError = unknown


export function useImportList<TData = Awaited<ReturnType<typeof importList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof importList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof importList>>,
          TError,
          Awaited<ReturnType<typeof importList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useImportList<TData = Awaited<ReturnType<typeof importList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof importList>>,
          TError,
          Awaited<ReturnType<typeof importList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useImportList<TData = Awaited<ReturnType<typeof importList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useImportList<TData = Awaited<ReturnType<typeof importList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getImportListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type importGetResponse200 = {
  data: ImportGet200
  status: 200
}

export type importGetResponseSuccess = (importGetResponse200) & {
  headers: Headers;
};
;

export type importGetResponse = (importGetResponseSuccess)

export const getImportGetUrl = (id: string,) => {




  return `/api/import/jobs/${id}`
}

export const importGet = async (id: string, options?: RequestInit): Promise<importGetResponse> => {

  return customMutator<importGetResponse>(getImportGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getImportGetQueryKey = (id: string,) => {
    return [
    `/api/import/jobs/${id}`
    ] as const;
    }


export const getImportGetQueryOptions = <TData = Awaited<ReturnType<typeof importGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getImportGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof importGet>>> = ({ signal }) => importGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof importGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ImportGetQueryResult = NonNullable<Awaited<ReturnType<typeof importGet>>>
export type ImportGetQueryError = unknown


export function useImportGet<TData = Awaited<ReturnType<typeof importGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof importGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof importGet>>,
          TError,
          Awaited<ReturnType<typeof importGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useImportGet<TData = Awaited<ReturnType<typeof importGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof importGet>>,
          TError,
          Awaited<ReturnType<typeof importGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useImportGet<TData = Awaited<ReturnType<typeof importGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useImportGet<TData = Awaited<ReturnType<typeof importGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getImportGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type importRowsListResponse200 = {
  data: ImportRowsList200
  status: 200
}

export type importRowsListResponseSuccess = (importRowsListResponse200) & {
  headers: Headers;
};
;

export type importRowsListResponse = (importRowsListResponseSuccess)

export const getImportRowsListUrl = (id: string,) => {




  return `/api/import/jobs/${id}/rows`
}

export const importRowsList = async (id: string, options?: RequestInit): Promise<importRowsListResponse> => {

  return customMutator<importRowsListResponse>(getImportRowsListUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getImportRowsListQueryKey = (id: string,) => {
    return [
    `/api/import/jobs/${id}/rows`
    ] as const;
    }


export const getImportRowsListQueryOptions = <TData = Awaited<ReturnType<typeof importRowsList>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importRowsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getImportRowsListQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof importRowsList>>> = ({ signal }) => importRowsList(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof importRowsList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ImportRowsListQueryResult = NonNullable<Awaited<ReturnType<typeof importRowsList>>>
export type ImportRowsListQueryError = unknown


export function useImportRowsList<TData = Awaited<ReturnType<typeof importRowsList>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof importRowsList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof importRowsList>>,
          TError,
          Awaited<ReturnType<typeof importRowsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useImportRowsList<TData = Awaited<ReturnType<typeof importRowsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importRowsList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof importRowsList>>,
          TError,
          Awaited<ReturnType<typeof importRowsList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useImportRowsList<TData = Awaited<ReturnType<typeof importRowsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importRowsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useImportRowsList<TData = Awaited<ReturnType<typeof importRowsList>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof importRowsList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getImportRowsListQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type importValidateResponse200 = {
  data: ImportValidate200
  status: 200
}

export type importValidateResponseSuccess = (importValidateResponse200) & {
  headers: Headers;
};
;

export type importValidateResponse = (importValidateResponseSuccess)

export const getImportValidateUrl = (id: string,) => {




  return `/api/import/jobs/${id}/preview`
}

export const importValidate = async (id: string, options?: RequestInit): Promise<importValidateResponse> => {

  return customMutator<importValidateResponse>(getImportValidateUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getImportValidateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importValidate>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof importValidate>>, TError,{id: string}, TContext> => {

const mutationKey = ['importValidate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importValidate>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  importValidate(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportValidateMutationResult = NonNullable<Awaited<ReturnType<typeof importValidate>>>

    export type ImportValidateMutationError = unknown

    export const useImportValidate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importValidate>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importValidate>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getImportValidateMutationOptions(options), queryClient);
    }

export type importCommitResponse200 = {
  data: ImportCommit200
  status: 200
}

export type importCommitResponseSuccess = (importCommitResponse200) & {
  headers: Headers;
};
;

export type importCommitResponse = (importCommitResponseSuccess)

export const getImportCommitUrl = (id: string,) => {




  return `/api/import/jobs/${id}/commit`
}

export const importCommit = async (id: string, options?: RequestInit): Promise<importCommitResponse> => {

  return customMutator<importCommitResponse>(getImportCommitUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getImportCommitMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importCommit>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof importCommit>>, TError,{id: string}, TContext> => {

const mutationKey = ['importCommit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importCommit>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  importCommit(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportCommitMutationResult = NonNullable<Awaited<ReturnType<typeof importCommit>>>

    export type ImportCommitMutationError = unknown

    export const useImportCommit = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importCommit>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importCommit>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getImportCommitMutationOptions(options), queryClient);
    }

export type importRecoverResponse200 = {
  data: ImportRecover200
  status: 200
}

export type importRecoverResponseSuccess = (importRecoverResponse200) & {
  headers: Headers;
};
;

export type importRecoverResponse = (importRecoverResponseSuccess)

export const getImportRecoverUrl = (id: string,) => {




  return `/api/import/jobs/${id}/recover`
}

export const importRecover = async (id: string, options?: RequestInit): Promise<importRecoverResponse> => {

  return customMutator<importRecoverResponse>(getImportRecoverUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getImportRecoverMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importRecover>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof importRecover>>, TError,{id: string}, TContext> => {

const mutationKey = ['importRecover'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importRecover>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  importRecover(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportRecoverMutationResult = NonNullable<Awaited<ReturnType<typeof importRecover>>>

    export type ImportRecoverMutationError = unknown

    export const useImportRecover = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importRecover>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importRecover>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getImportRecoverMutationOptions(options), queryClient);
    }

export type importCancelResponse200 = {
  data: ImportCancel200
  status: 200
}

export type importCancelResponseSuccess = (importCancelResponse200) & {
  headers: Headers;
};
;

export type importCancelResponse = (importCancelResponseSuccess)

export const getImportCancelUrl = (id: string,) => {




  return `/api/import/jobs/${id}/cancel`
}

export const importCancel = async (id: string, options?: RequestInit): Promise<importCancelResponse> => {

  return customMutator<importCancelResponse>(getImportCancelUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getImportCancelMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importCancel>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof importCancel>>, TError,{id: string}, TContext> => {

const mutationKey = ['importCancel'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importCancel>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  importCancel(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportCancelMutationResult = NonNullable<Awaited<ReturnType<typeof importCancel>>>

    export type ImportCancelMutationError = unknown

    export const useImportCancel = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importCancel>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importCancel>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getImportCancelMutationOptions(options), queryClient);
    }

export type importReconcileResponse200 = {
  data: ImportReconcile200
  status: 200
}

export type importReconcileResponseSuccess = (importReconcileResponse200) & {
  headers: Headers;
};
;

export type importReconcileResponse = (importReconcileResponseSuccess)

export const getImportReconcileUrl = (id: string,) => {




  return `/api/import/jobs/${id}/reconcile`
}

export const importReconcile = async (id: string, options?: RequestInit): Promise<importReconcileResponse> => {

  return customMutator<importReconcileResponse>(getImportReconcileUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getImportReconcileMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importReconcile>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof importReconcile>>, TError,{id: string}, TContext> => {

const mutationKey = ['importReconcile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importReconcile>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  importReconcile(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportReconcileMutationResult = NonNullable<Awaited<ReturnType<typeof importReconcile>>>

    export type ImportReconcileMutationError = unknown

    export const useImportReconcile = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importReconcile>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importReconcile>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getImportReconcileMutationOptions(options), queryClient);
    }

export type reconciliationStatementCreateResponse200 = {
  data: void
  status: 200
}

export type reconciliationStatementCreateResponse201 = {
  data: void
  status: 201
}

export type reconciliationStatementCreateResponseSuccess = (reconciliationStatementCreateResponse200 | reconciliationStatementCreateResponse201) & {
  headers: Headers;
};
;

export type reconciliationStatementCreateResponse = (reconciliationStatementCreateResponseSuccess)

export const getReconciliationStatementCreateUrl = () => {




  return `/api/reconciliation/external-statements`
}

export const reconciliationStatementCreate = async (createStatementDto: CreateStatementDto, options?: RequestInit): Promise<reconciliationStatementCreateResponse> => {

  return customMutator<reconciliationStatementCreateResponse>(getReconciliationStatementCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createStatementDto)
  }
);}





export const getReconciliationStatementCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationStatementCreate>>, TError,{data: CreateStatementDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationStatementCreate>>, TError,{data: CreateStatementDto}, TContext> => {

const mutationKey = ['reconciliationStatementCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationStatementCreate>>, {data: CreateStatementDto}> = (props) => {
          const {data} = props ?? {};

          return  reconciliationStatementCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationStatementCreateMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationStatementCreate>>>
    export type ReconciliationStatementCreateMutationBody = CreateStatementDto
    export type ReconciliationStatementCreateMutationError = unknown

    export const useReconciliationStatementCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationStatementCreate>>, TError,{data: CreateStatementDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationStatementCreate>>,
        TError,
        {data: CreateStatementDto},
        TContext
      > => {
      return useMutation(getReconciliationStatementCreateMutationOptions(options), queryClient);
    }

export type reconciliationStatementListResponse200 = {
  data: ReconciliationStatementList200
  status: 200
}

export type reconciliationStatementListResponseSuccess = (reconciliationStatementListResponse200) & {
  headers: Headers;
};
;

export type reconciliationStatementListResponse = (reconciliationStatementListResponseSuccess)

export const getReconciliationStatementListUrl = () => {




  return `/api/reconciliation/external-statements`
}

export const reconciliationStatementList = async ( options?: RequestInit): Promise<reconciliationStatementListResponse> => {

  return customMutator<reconciliationStatementListResponse>(getReconciliationStatementListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationStatementListQueryKey = () => {
    return [
    `/api/reconciliation/external-statements`
    ] as const;
    }


export const getReconciliationStatementListQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationStatementList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationStatementListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationStatementList>>> = ({ signal }) => reconciliationStatementList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationStatementListQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationStatementList>>>
export type ReconciliationStatementListQueryError = unknown


export function useReconciliationStatementList<TData = Awaited<ReturnType<typeof reconciliationStatementList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationStatementList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationStatementList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationStatementList<TData = Awaited<ReturnType<typeof reconciliationStatementList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationStatementList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationStatementList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationStatementList<TData = Awaited<ReturnType<typeof reconciliationStatementList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationStatementList<TData = Awaited<ReturnType<typeof reconciliationStatementList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationStatementListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationSuspenseCashCreateResponse200 = {
  data: void
  status: 200
}

export type reconciliationSuspenseCashCreateResponse201 = {
  data: void
  status: 201
}

export type reconciliationSuspenseCashCreateResponseSuccess = (reconciliationSuspenseCashCreateResponse200 | reconciliationSuspenseCashCreateResponse201) & {
  headers: Headers;
};
;

export type reconciliationSuspenseCashCreateResponse = (reconciliationSuspenseCashCreateResponseSuccess)

export const getReconciliationSuspenseCashCreateUrl = () => {




  return `/api/reconciliation/suspense-cash/cases`
}

export const reconciliationSuspenseCashCreate = async (createSuspenseCashCaseDto: CreateSuspenseCashCaseDto, options?: RequestInit): Promise<reconciliationSuspenseCashCreateResponse> => {

  return customMutator<reconciliationSuspenseCashCreateResponse>(getReconciliationSuspenseCashCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createSuspenseCashCaseDto)
  }
);}





export const getReconciliationSuspenseCashCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashCreate>>, TError,{data: CreateSuspenseCashCaseDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashCreate>>, TError,{data: CreateSuspenseCashCaseDto}, TContext> => {

const mutationKey = ['reconciliationSuspenseCashCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationSuspenseCashCreate>>, {data: CreateSuspenseCashCaseDto}> = (props) => {
          const {data} = props ?? {};

          return  reconciliationSuspenseCashCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationSuspenseCashCreateMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationSuspenseCashCreate>>>
    export type ReconciliationSuspenseCashCreateMutationBody = CreateSuspenseCashCaseDto
    export type ReconciliationSuspenseCashCreateMutationError = unknown

    export const useReconciliationSuspenseCashCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashCreate>>, TError,{data: CreateSuspenseCashCaseDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationSuspenseCashCreate>>,
        TError,
        {data: CreateSuspenseCashCaseDto},
        TContext
      > => {
      return useMutation(getReconciliationSuspenseCashCreateMutationOptions(options), queryClient);
    }

export type reconciliationSuspenseCashListResponse200 = {
  data: ReconciliationSuspenseCashList200
  status: 200
}

export type reconciliationSuspenseCashListResponseSuccess = (reconciliationSuspenseCashListResponse200) & {
  headers: Headers;
};
;

export type reconciliationSuspenseCashListResponse = (reconciliationSuspenseCashListResponseSuccess)

export const getReconciliationSuspenseCashListUrl = () => {




  return `/api/reconciliation/suspense-cash/cases`
}

export const reconciliationSuspenseCashList = async ( options?: RequestInit): Promise<reconciliationSuspenseCashListResponse> => {

  return customMutator<reconciliationSuspenseCashListResponse>(getReconciliationSuspenseCashListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationSuspenseCashListQueryKey = () => {
    return [
    `/api/reconciliation/suspense-cash/cases`
    ] as const;
    }


export const getReconciliationSuspenseCashListQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationSuspenseCashListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationSuspenseCashList>>> = ({ signal }) => reconciliationSuspenseCashList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationSuspenseCashListQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationSuspenseCashList>>>
export type ReconciliationSuspenseCashListQueryError = unknown


export function useReconciliationSuspenseCashList<TData = Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationSuspenseCashList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationSuspenseCashList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationSuspenseCashList<TData = Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationSuspenseCashList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationSuspenseCashList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationSuspenseCashList<TData = Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationSuspenseCashList<TData = Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationSuspenseCashListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationSuspenseCashResolutionRequestResponse200 = {
  data: void
  status: 200
}

export type reconciliationSuspenseCashResolutionRequestResponse201 = {
  data: void
  status: 201
}

export type reconciliationSuspenseCashResolutionRequestResponseSuccess = (reconciliationSuspenseCashResolutionRequestResponse200 | reconciliationSuspenseCashResolutionRequestResponse201) & {
  headers: Headers;
};
;

export type reconciliationSuspenseCashResolutionRequestResponse = (reconciliationSuspenseCashResolutionRequestResponseSuccess)

export const getReconciliationSuspenseCashResolutionRequestUrl = (caseId: string,) => {




  return `/api/reconciliation/suspense-cash/cases/${caseId}/resolution-requests`
}

export const reconciliationSuspenseCashResolutionRequest = async (caseId: string,
    requestSuspenseCashResolutionDto: RequestSuspenseCashResolutionDto, options?: RequestInit): Promise<reconciliationSuspenseCashResolutionRequestResponse> => {

  return customMutator<reconciliationSuspenseCashResolutionRequestResponse>(getReconciliationSuspenseCashResolutionRequestUrl(caseId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(requestSuspenseCashResolutionDto)
  }
);}





export const getReconciliationSuspenseCashResolutionRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionRequest>>, TError,{caseId: string;data: RequestSuspenseCashResolutionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionRequest>>, TError,{caseId: string;data: RequestSuspenseCashResolutionDto}, TContext> => {

const mutationKey = ['reconciliationSuspenseCashResolutionRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionRequest>>, {caseId: string;data: RequestSuspenseCashResolutionDto}> = (props) => {
          const {caseId,data} = props ?? {};

          return  reconciliationSuspenseCashResolutionRequest(caseId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationSuspenseCashResolutionRequestMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionRequest>>>
    export type ReconciliationSuspenseCashResolutionRequestMutationBody = RequestSuspenseCashResolutionDto
    export type ReconciliationSuspenseCashResolutionRequestMutationError = unknown

    export const useReconciliationSuspenseCashResolutionRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionRequest>>, TError,{caseId: string;data: RequestSuspenseCashResolutionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionRequest>>,
        TError,
        {caseId: string;data: RequestSuspenseCashResolutionDto},
        TContext
      > => {
      return useMutation(getReconciliationSuspenseCashResolutionRequestMutationOptions(options), queryClient);
    }

export type reconciliationSuspenseCashResolutionApproveResponse200 = {
  data: void
  status: 200
}

export type reconciliationSuspenseCashResolutionApproveResponse201 = {
  data: void
  status: 201
}

export type reconciliationSuspenseCashResolutionApproveResponseSuccess = (reconciliationSuspenseCashResolutionApproveResponse200 | reconciliationSuspenseCashResolutionApproveResponse201) & {
  headers: Headers;
};
;

export type reconciliationSuspenseCashResolutionApproveResponse = (reconciliationSuspenseCashResolutionApproveResponseSuccess)

export const getReconciliationSuspenseCashResolutionApproveUrl = (caseId: string,) => {




  return `/api/reconciliation/suspense-cash/cases/${caseId}/resolution-approvals`
}

export const reconciliationSuspenseCashResolutionApprove = async (caseId: string,
    suspenseCashTransitionDto: SuspenseCashTransitionDto, options?: RequestInit): Promise<reconciliationSuspenseCashResolutionApproveResponse> => {

  return customMutator<reconciliationSuspenseCashResolutionApproveResponse>(getReconciliationSuspenseCashResolutionApproveUrl(caseId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(suspenseCashTransitionDto)
  }
);}





export const getReconciliationSuspenseCashResolutionApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionApprove>>, TError,{caseId: string;data: SuspenseCashTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionApprove>>, TError,{caseId: string;data: SuspenseCashTransitionDto}, TContext> => {

const mutationKey = ['reconciliationSuspenseCashResolutionApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionApprove>>, {caseId: string;data: SuspenseCashTransitionDto}> = (props) => {
          const {caseId,data} = props ?? {};

          return  reconciliationSuspenseCashResolutionApprove(caseId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationSuspenseCashResolutionApproveMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionApprove>>>
    export type ReconciliationSuspenseCashResolutionApproveMutationBody = SuspenseCashTransitionDto
    export type ReconciliationSuspenseCashResolutionApproveMutationError = unknown

    export const useReconciliationSuspenseCashResolutionApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionApprove>>, TError,{caseId: string;data: SuspenseCashTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationSuspenseCashResolutionApprove>>,
        TError,
        {caseId: string;data: SuspenseCashTransitionDto},
        TContext
      > => {
      return useMutation(getReconciliationSuspenseCashResolutionApproveMutationOptions(options), queryClient);
    }

export type reconciliationSuspenseCashReopenRequestResponse200 = {
  data: void
  status: 200
}

export type reconciliationSuspenseCashReopenRequestResponse201 = {
  data: void
  status: 201
}

export type reconciliationSuspenseCashReopenRequestResponseSuccess = (reconciliationSuspenseCashReopenRequestResponse200 | reconciliationSuspenseCashReopenRequestResponse201) & {
  headers: Headers;
};
;

export type reconciliationSuspenseCashReopenRequestResponse = (reconciliationSuspenseCashReopenRequestResponseSuccess)

export const getReconciliationSuspenseCashReopenRequestUrl = (caseId: string,) => {




  return `/api/reconciliation/suspense-cash/cases/${caseId}/reopen-requests`
}

export const reconciliationSuspenseCashReopenRequest = async (caseId: string,
    suspenseCashTransitionDto: SuspenseCashTransitionDto, options?: RequestInit): Promise<reconciliationSuspenseCashReopenRequestResponse> => {

  return customMutator<reconciliationSuspenseCashReopenRequestResponse>(getReconciliationSuspenseCashReopenRequestUrl(caseId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(suspenseCashTransitionDto)
  }
);}





export const getReconciliationSuspenseCashReopenRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenRequest>>, TError,{caseId: string;data: SuspenseCashTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenRequest>>, TError,{caseId: string;data: SuspenseCashTransitionDto}, TContext> => {

const mutationKey = ['reconciliationSuspenseCashReopenRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenRequest>>, {caseId: string;data: SuspenseCashTransitionDto}> = (props) => {
          const {caseId,data} = props ?? {};

          return  reconciliationSuspenseCashReopenRequest(caseId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationSuspenseCashReopenRequestMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenRequest>>>
    export type ReconciliationSuspenseCashReopenRequestMutationBody = SuspenseCashTransitionDto
    export type ReconciliationSuspenseCashReopenRequestMutationError = unknown

    export const useReconciliationSuspenseCashReopenRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenRequest>>, TError,{caseId: string;data: SuspenseCashTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationSuspenseCashReopenRequest>>,
        TError,
        {caseId: string;data: SuspenseCashTransitionDto},
        TContext
      > => {
      return useMutation(getReconciliationSuspenseCashReopenRequestMutationOptions(options), queryClient);
    }

export type reconciliationSuspenseCashReopenApproveResponse200 = {
  data: void
  status: 200
}

export type reconciliationSuspenseCashReopenApproveResponse201 = {
  data: void
  status: 201
}

export type reconciliationSuspenseCashReopenApproveResponseSuccess = (reconciliationSuspenseCashReopenApproveResponse200 | reconciliationSuspenseCashReopenApproveResponse201) & {
  headers: Headers;
};
;

export type reconciliationSuspenseCashReopenApproveResponse = (reconciliationSuspenseCashReopenApproveResponseSuccess)

export const getReconciliationSuspenseCashReopenApproveUrl = (caseId: string,) => {




  return `/api/reconciliation/suspense-cash/cases/${caseId}/reopen-approvals`
}

export const reconciliationSuspenseCashReopenApprove = async (caseId: string,
    suspenseCashTransitionDto: SuspenseCashTransitionDto, options?: RequestInit): Promise<reconciliationSuspenseCashReopenApproveResponse> => {

  return customMutator<reconciliationSuspenseCashReopenApproveResponse>(getReconciliationSuspenseCashReopenApproveUrl(caseId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(suspenseCashTransitionDto)
  }
);}





export const getReconciliationSuspenseCashReopenApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenApprove>>, TError,{caseId: string;data: SuspenseCashTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenApprove>>, TError,{caseId: string;data: SuspenseCashTransitionDto}, TContext> => {

const mutationKey = ['reconciliationSuspenseCashReopenApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenApprove>>, {caseId: string;data: SuspenseCashTransitionDto}> = (props) => {
          const {caseId,data} = props ?? {};

          return  reconciliationSuspenseCashReopenApprove(caseId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationSuspenseCashReopenApproveMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenApprove>>>
    export type ReconciliationSuspenseCashReopenApproveMutationBody = SuspenseCashTransitionDto
    export type ReconciliationSuspenseCashReopenApproveMutationError = unknown

    export const useReconciliationSuspenseCashReopenApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationSuspenseCashReopenApprove>>, TError,{caseId: string;data: SuspenseCashTransitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationSuspenseCashReopenApprove>>,
        TError,
        {caseId: string;data: SuspenseCashTransitionDto},
        TContext
      > => {
      return useMutation(getReconciliationSuspenseCashReopenApproveMutationOptions(options), queryClient);
    }

export type reconciliationStatementGetResponse200 = {
  data: ReconciliationStatementGet200
  status: 200
}

export type reconciliationStatementGetResponseSuccess = (reconciliationStatementGetResponse200) & {
  headers: Headers;
};
;

export type reconciliationStatementGetResponse = (reconciliationStatementGetResponseSuccess)

export const getReconciliationStatementGetUrl = (id: string,) => {




  return `/api/reconciliation/external-statements/${id}`
}

export const reconciliationStatementGet = async (id: string, options?: RequestInit): Promise<reconciliationStatementGetResponse> => {

  return customMutator<reconciliationStatementGetResponse>(getReconciliationStatementGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationStatementGetQueryKey = (id: string,) => {
    return [
    `/api/reconciliation/external-statements/${id}`
    ] as const;
    }


export const getReconciliationStatementGetQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationStatementGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationStatementGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationStatementGet>>> = ({ signal }) => reconciliationStatementGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationStatementGetQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationStatementGet>>>
export type ReconciliationStatementGetQueryError = unknown


export function useReconciliationStatementGet<TData = Awaited<ReturnType<typeof reconciliationStatementGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationStatementGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationStatementGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationStatementGet<TData = Awaited<ReturnType<typeof reconciliationStatementGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationStatementGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationStatementGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationStatementGet<TData = Awaited<ReturnType<typeof reconciliationStatementGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationStatementGet<TData = Awaited<ReturnType<typeof reconciliationStatementGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationStatementGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationStatementGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationRunCreateResponse200 = {
  data: void
  status: 200
}

export type reconciliationRunCreateResponse201 = {
  data: void
  status: 201
}

export type reconciliationRunCreateResponseSuccess = (reconciliationRunCreateResponse200 | reconciliationRunCreateResponse201) & {
  headers: Headers;
};
;

export type reconciliationRunCreateResponse = (reconciliationRunCreateResponseSuccess)

export const getReconciliationRunCreateUrl = () => {




  return `/api/reconciliation/runs`
}

export const reconciliationRunCreate = async (startReconciliationDto: StartReconciliationDto, options?: RequestInit): Promise<reconciliationRunCreateResponse> => {

  return customMutator<reconciliationRunCreateResponse>(getReconciliationRunCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(startReconciliationDto)
  }
);}





export const getReconciliationRunCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationRunCreate>>, TError,{data: StartReconciliationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationRunCreate>>, TError,{data: StartReconciliationDto}, TContext> => {

const mutationKey = ['reconciliationRunCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationRunCreate>>, {data: StartReconciliationDto}> = (props) => {
          const {data} = props ?? {};

          return  reconciliationRunCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationRunCreateMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationRunCreate>>>
    export type ReconciliationRunCreateMutationBody = StartReconciliationDto
    export type ReconciliationRunCreateMutationError = unknown

    export const useReconciliationRunCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationRunCreate>>, TError,{data: StartReconciliationDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationRunCreate>>,
        TError,
        {data: StartReconciliationDto},
        TContext
      > => {
      return useMutation(getReconciliationRunCreateMutationOptions(options), queryClient);
    }

export type reconciliationRunListResponse200 = {
  data: ReconciliationRunList200
  status: 200
}

export type reconciliationRunListResponseSuccess = (reconciliationRunListResponse200) & {
  headers: Headers;
};
;

export type reconciliationRunListResponse = (reconciliationRunListResponseSuccess)

export const getReconciliationRunListUrl = () => {




  return `/api/reconciliation/runs`
}

export const reconciliationRunList = async ( options?: RequestInit): Promise<reconciliationRunListResponse> => {

  return customMutator<reconciliationRunListResponse>(getReconciliationRunListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationRunListQueryKey = () => {
    return [
    `/api/reconciliation/runs`
    ] as const;
    }


export const getReconciliationRunListQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationRunList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationRunListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationRunList>>> = ({ signal }) => reconciliationRunList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationRunListQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationRunList>>>
export type ReconciliationRunListQueryError = unknown


export function useReconciliationRunList<TData = Awaited<ReturnType<typeof reconciliationRunList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunList<TData = Awaited<ReturnType<typeof reconciliationRunList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunList<TData = Awaited<ReturnType<typeof reconciliationRunList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationRunList<TData = Awaited<ReturnType<typeof reconciliationRunList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationRunListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationRunCreatePostResponse200 = {
  data: void
  status: 200
}

export type reconciliationRunCreatePostResponse201 = {
  data: void
  status: 201
}

export type reconciliationRunCreatePostResponseSuccess = (reconciliationRunCreatePostResponse200 | reconciliationRunCreatePostResponse201) & {
  headers: Headers;
};
;

export type reconciliationRunCreatePostResponse = (reconciliationRunCreatePostResponseSuccess)

export const getReconciliationRunCreatePostUrl = () => {




  return `/api/reconciliation/cutoff-controls/runs`
}

export const reconciliationRunCreatePost = async (runFinancialControlDto: RunFinancialControlDto, options?: RequestInit): Promise<reconciliationRunCreatePostResponse> => {

  return customMutator<reconciliationRunCreatePostResponse>(getReconciliationRunCreatePostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(runFinancialControlDto)
  }
);}





export const getReconciliationRunCreatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationRunCreatePost>>, TError,{data: RunFinancialControlDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationRunCreatePost>>, TError,{data: RunFinancialControlDto}, TContext> => {

const mutationKey = ['reconciliationRunCreatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationRunCreatePost>>, {data: RunFinancialControlDto}> = (props) => {
          const {data} = props ?? {};

          return  reconciliationRunCreatePost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationRunCreatePostMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationRunCreatePost>>>
    export type ReconciliationRunCreatePostMutationBody = RunFinancialControlDto
    export type ReconciliationRunCreatePostMutationError = unknown

    export const useReconciliationRunCreatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationRunCreatePost>>, TError,{data: RunFinancialControlDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationRunCreatePost>>,
        TError,
        {data: RunFinancialControlDto},
        TContext
      > => {
      return useMutation(getReconciliationRunCreatePostMutationOptions(options), queryClient);
    }

export type reconciliationRunListGetResponse200 = {
  data: ReconciliationRunListGet200
  status: 200
}

export type reconciliationRunListGetResponseSuccess = (reconciliationRunListGetResponse200) & {
  headers: Headers;
};
;

export type reconciliationRunListGetResponse = (reconciliationRunListGetResponseSuccess)

export const getReconciliationRunListGetUrl = () => {




  return `/api/reconciliation/cutoff-controls/runs`
}

export const reconciliationRunListGet = async ( options?: RequestInit): Promise<reconciliationRunListGetResponse> => {

  return customMutator<reconciliationRunListGetResponse>(getReconciliationRunListGetUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationRunListGetQueryKey = () => {
    return [
    `/api/reconciliation/cutoff-controls/runs`
    ] as const;
    }


export const getReconciliationRunListGetQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationRunListGet>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunListGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationRunListGetQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationRunListGet>>> = ({ signal }) => reconciliationRunListGet({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationRunListGetQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationRunListGet>>>
export type ReconciliationRunListGetQueryError = unknown


export function useReconciliationRunListGet<TData = Awaited<ReturnType<typeof reconciliationRunListGet>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunListGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunListGet<TData = Awaited<ReturnType<typeof reconciliationRunListGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunListGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunListGet<TData = Awaited<ReturnType<typeof reconciliationRunListGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunListGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationRunListGet<TData = Awaited<ReturnType<typeof reconciliationRunListGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunListGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationRunListGetQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationRunGetResponse200 = {
  data: ReconciliationRunGet200
  status: 200
}

export type reconciliationRunGetResponseSuccess = (reconciliationRunGetResponse200) & {
  headers: Headers;
};
;

export type reconciliationRunGetResponse = (reconciliationRunGetResponseSuccess)

export const getReconciliationRunGetUrl = (runId: string,) => {




  return `/api/reconciliation/cutoff-controls/runs/${runId}`
}

export const reconciliationRunGet = async (runId: string, options?: RequestInit): Promise<reconciliationRunGetResponse> => {

  return customMutator<reconciliationRunGetResponse>(getReconciliationRunGetUrl(runId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationRunGetQueryKey = (runId: string,) => {
    return [
    `/api/reconciliation/cutoff-controls/runs/${runId}`
    ] as const;
    }


export const getReconciliationRunGetQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationRunGet>>, TError = unknown>(runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationRunGetQueryKey(runId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationRunGet>>> = ({ signal }) => reconciliationRunGet(runId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationRunGetQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationRunGet>>>
export type ReconciliationRunGetQueryError = unknown


export function useReconciliationRunGet<TData = Awaited<ReturnType<typeof reconciliationRunGet>>, TError = unknown>(
 runId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunGet<TData = Awaited<ReturnType<typeof reconciliationRunGet>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunGet<TData = Awaited<ReturnType<typeof reconciliationRunGet>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationRunGet<TData = Awaited<ReturnType<typeof reconciliationRunGet>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationRunGetQueryOptions(runId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationAdjustmentCreateResponse200 = {
  data: void
  status: 200
}

export type reconciliationAdjustmentCreateResponse201 = {
  data: void
  status: 201
}

export type reconciliationAdjustmentCreateResponseSuccess = (reconciliationAdjustmentCreateResponse200 | reconciliationAdjustmentCreateResponse201) & {
  headers: Headers;
};
;

export type reconciliationAdjustmentCreateResponse = (reconciliationAdjustmentCreateResponseSuccess)

export const getReconciliationAdjustmentCreateUrl = (breakId: string,) => {




  return `/api/reconciliation/cutoff-controls/breaks/${breakId}/repairs`
}

export const reconciliationAdjustmentCreate = async (breakId: string,
    createFinancialControlRepairDto: CreateFinancialControlRepairDto, options?: RequestInit): Promise<reconciliationAdjustmentCreateResponse> => {

  return customMutator<reconciliationAdjustmentCreateResponse>(getReconciliationAdjustmentCreateUrl(breakId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createFinancialControlRepairDto)
  }
);}





export const getReconciliationAdjustmentCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentCreate>>, TError,{breakId: string;data: CreateFinancialControlRepairDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentCreate>>, TError,{breakId: string;data: CreateFinancialControlRepairDto}, TContext> => {

const mutationKey = ['reconciliationAdjustmentCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationAdjustmentCreate>>, {breakId: string;data: CreateFinancialControlRepairDto}> = (props) => {
          const {breakId,data} = props ?? {};

          return  reconciliationAdjustmentCreate(breakId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationAdjustmentCreateMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationAdjustmentCreate>>>
    export type ReconciliationAdjustmentCreateMutationBody = CreateFinancialControlRepairDto
    export type ReconciliationAdjustmentCreateMutationError = unknown

    export const useReconciliationAdjustmentCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentCreate>>, TError,{breakId: string;data: CreateFinancialControlRepairDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationAdjustmentCreate>>,
        TError,
        {breakId: string;data: CreateFinancialControlRepairDto},
        TContext
      > => {
      return useMutation(getReconciliationAdjustmentCreateMutationOptions(options), queryClient);
    }

export type reconciliationAdjustmentApproveResponse200 = {
  data: void
  status: 200
}

export type reconciliationAdjustmentApproveResponseSuccess = (reconciliationAdjustmentApproveResponse200) & {
  headers: Headers;
};
;

export type reconciliationAdjustmentApproveResponse = (reconciliationAdjustmentApproveResponseSuccess)

export const getReconciliationAdjustmentApproveUrl = (repairId: string,) => {




  return `/api/reconciliation/cutoff-controls/repairs/${repairId}/approve`
}

export const reconciliationAdjustmentApprove = async (repairId: string,
    financialControlRepairDecisionDto: FinancialControlRepairDecisionDto, options?: RequestInit): Promise<reconciliationAdjustmentApproveResponse> => {

  return customMutator<reconciliationAdjustmentApproveResponse>(getReconciliationAdjustmentApproveUrl(repairId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(financialControlRepairDecisionDto)
  }
);}





export const getReconciliationAdjustmentApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentApprove>>, TError,{repairId: string;data: FinancialControlRepairDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentApprove>>, TError,{repairId: string;data: FinancialControlRepairDecisionDto}, TContext> => {

const mutationKey = ['reconciliationAdjustmentApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationAdjustmentApprove>>, {repairId: string;data: FinancialControlRepairDecisionDto}> = (props) => {
          const {repairId,data} = props ?? {};

          return  reconciliationAdjustmentApprove(repairId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationAdjustmentApproveMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationAdjustmentApprove>>>
    export type ReconciliationAdjustmentApproveMutationBody = FinancialControlRepairDecisionDto
    export type ReconciliationAdjustmentApproveMutationError = unknown

    export const useReconciliationAdjustmentApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentApprove>>, TError,{repairId: string;data: FinancialControlRepairDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationAdjustmentApprove>>,
        TError,
        {repairId: string;data: FinancialControlRepairDecisionDto},
        TContext
      > => {
      return useMutation(getReconciliationAdjustmentApproveMutationOptions(options), queryClient);
    }

export type reconciliationAdjustmentPostResponse200 = {
  data: void
  status: 200
}

export type reconciliationAdjustmentPostResponse201 = {
  data: void
  status: 201
}

export type reconciliationAdjustmentPostResponseSuccess = (reconciliationAdjustmentPostResponse200 | reconciliationAdjustmentPostResponse201) & {
  headers: Headers;
};
;

export type reconciliationAdjustmentPostResponse = (reconciliationAdjustmentPostResponseSuccess)

export const getReconciliationAdjustmentPostUrl = (repairId: string,) => {




  return `/api/reconciliation/cutoff-controls/repairs/${repairId}/post`
}

export const reconciliationAdjustmentPost = async (repairId: string,
    financialControlRepairDecisionDto: FinancialControlRepairDecisionDto, options?: RequestInit): Promise<reconciliationAdjustmentPostResponse> => {

  return customMutator<reconciliationAdjustmentPostResponse>(getReconciliationAdjustmentPostUrl(repairId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(financialControlRepairDecisionDto)
  }
);}





export const getReconciliationAdjustmentPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentPost>>, TError,{repairId: string;data: FinancialControlRepairDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentPost>>, TError,{repairId: string;data: FinancialControlRepairDecisionDto}, TContext> => {

const mutationKey = ['reconciliationAdjustmentPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationAdjustmentPost>>, {repairId: string;data: FinancialControlRepairDecisionDto}> = (props) => {
          const {repairId,data} = props ?? {};

          return  reconciliationAdjustmentPost(repairId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationAdjustmentPostMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationAdjustmentPost>>>
    export type ReconciliationAdjustmentPostMutationBody = FinancialControlRepairDecisionDto
    export type ReconciliationAdjustmentPostMutationError = unknown

    export const useReconciliationAdjustmentPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentPost>>, TError,{repairId: string;data: FinancialControlRepairDecisionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationAdjustmentPost>>,
        TError,
        {repairId: string;data: FinancialControlRepairDecisionDto},
        TContext
      > => {
      return useMutation(getReconciliationAdjustmentPostMutationOptions(options), queryClient);
    }

export type reconciliationRunGetGetResponse200 = {
  data: ReconciliationRunGetGet200
  status: 200
}

export type reconciliationRunGetGetResponseSuccess = (reconciliationRunGetGetResponse200) & {
  headers: Headers;
};
;

export type reconciliationRunGetGetResponse = (reconciliationRunGetGetResponseSuccess)

export const getReconciliationRunGetGetUrl = (runId: string,) => {




  return `/api/reconciliation/runs/${runId}`
}

export const reconciliationRunGetGet = async (runId: string, options?: RequestInit): Promise<reconciliationRunGetGetResponse> => {

  return customMutator<reconciliationRunGetGetResponse>(getReconciliationRunGetGetUrl(runId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationRunGetGetQueryKey = (runId: string,) => {
    return [
    `/api/reconciliation/runs/${runId}`
    ] as const;
    }


export const getReconciliationRunGetGetQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError = unknown>(runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationRunGetGetQueryKey(runId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationRunGetGet>>> = ({ signal }) => reconciliationRunGetGet(runId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationRunGetGetQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationRunGetGet>>>
export type ReconciliationRunGetGetQueryError = unknown


export function useReconciliationRunGetGet<TData = Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError = unknown>(
 runId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunGetGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunGetGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunGetGet<TData = Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunGetGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunGetGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunGetGet<TData = Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationRunGetGet<TData = Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunGetGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationRunGetGetQueryOptions(runId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationRunBreaksListResponse200 = {
  data: ReconciliationRunBreaksList200
  status: 200
}

export type reconciliationRunBreaksListResponseSuccess = (reconciliationRunBreaksListResponse200) & {
  headers: Headers;
};
;

export type reconciliationRunBreaksListResponse = (reconciliationRunBreaksListResponseSuccess)

export const getReconciliationRunBreaksListUrl = (runId: string,) => {




  return `/api/reconciliation/runs/${runId}/breaks`
}

export const reconciliationRunBreaksList = async (runId: string, options?: RequestInit): Promise<reconciliationRunBreaksListResponse> => {

  return customMutator<reconciliationRunBreaksListResponse>(getReconciliationRunBreaksListUrl(runId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationRunBreaksListQueryKey = (runId: string,) => {
    return [
    `/api/reconciliation/runs/${runId}/breaks`
    ] as const;
    }


export const getReconciliationRunBreaksListQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError = unknown>(runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationRunBreaksListQueryKey(runId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationRunBreaksList>>> = ({ signal }) => reconciliationRunBreaksList(runId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationRunBreaksListQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationRunBreaksList>>>
export type ReconciliationRunBreaksListQueryError = unknown


export function useReconciliationRunBreaksList<TData = Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError = unknown>(
 runId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunBreaksList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunBreaksList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunBreaksList<TData = Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunBreaksList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunBreaksList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunBreaksList<TData = Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationRunBreaksList<TData = Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunBreaksList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationRunBreaksListQueryOptions(runId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationRunMatchesListResponse200 = {
  data: ReconciliationRunMatchesList200
  status: 200
}

export type reconciliationRunMatchesListResponseSuccess = (reconciliationRunMatchesListResponse200) & {
  headers: Headers;
};
;

export type reconciliationRunMatchesListResponse = (reconciliationRunMatchesListResponseSuccess)

export const getReconciliationRunMatchesListUrl = (runId: string,) => {




  return `/api/reconciliation/runs/${runId}/matches`
}

export const reconciliationRunMatchesList = async (runId: string, options?: RequestInit): Promise<reconciliationRunMatchesListResponse> => {

  return customMutator<reconciliationRunMatchesListResponse>(getReconciliationRunMatchesListUrl(runId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationRunMatchesListQueryKey = (runId: string,) => {
    return [
    `/api/reconciliation/runs/${runId}/matches`
    ] as const;
    }


export const getReconciliationRunMatchesListQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError = unknown>(runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationRunMatchesListQueryKey(runId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationRunMatchesList>>> = ({ signal }) => reconciliationRunMatchesList(runId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationRunMatchesListQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationRunMatchesList>>>
export type ReconciliationRunMatchesListQueryError = unknown


export function useReconciliationRunMatchesList<TData = Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError = unknown>(
 runId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunMatchesList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunMatchesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunMatchesList<TData = Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationRunMatchesList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationRunMatchesList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationRunMatchesList<TData = Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationRunMatchesList<TData = Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError = unknown>(
 runId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationRunMatchesList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationRunMatchesListQueryOptions(runId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationBreakGetResponse200 = {
  data: ReconciliationBreakGet200
  status: 200
}

export type reconciliationBreakGetResponseSuccess = (reconciliationBreakGetResponse200) & {
  headers: Headers;
};
;

export type reconciliationBreakGetResponse = (reconciliationBreakGetResponseSuccess)

export const getReconciliationBreakGetUrl = (breakId: string,) => {




  return `/api/reconciliation/breaks/${breakId}`
}

export const reconciliationBreakGet = async (breakId: string, options?: RequestInit): Promise<reconciliationBreakGetResponse> => {

  return customMutator<reconciliationBreakGetResponse>(getReconciliationBreakGetUrl(breakId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationBreakGetQueryKey = (breakId: string,) => {
    return [
    `/api/reconciliation/breaks/${breakId}`
    ] as const;
    }


export const getReconciliationBreakGetQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationBreakGet>>, TError = unknown>(breakId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationBreakGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationBreakGetQueryKey(breakId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationBreakGet>>> = ({ signal }) => reconciliationBreakGet(breakId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: breakId !== null && breakId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationBreakGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationBreakGetQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationBreakGet>>>
export type ReconciliationBreakGetQueryError = unknown


export function useReconciliationBreakGet<TData = Awaited<ReturnType<typeof reconciliationBreakGet>>, TError = unknown>(
 breakId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationBreakGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationBreakGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationBreakGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationBreakGet<TData = Awaited<ReturnType<typeof reconciliationBreakGet>>, TError = unknown>(
 breakId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationBreakGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationBreakGet>>,
          TError,
          Awaited<ReturnType<typeof reconciliationBreakGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationBreakGet<TData = Awaited<ReturnType<typeof reconciliationBreakGet>>, TError = unknown>(
 breakId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationBreakGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationBreakGet<TData = Awaited<ReturnType<typeof reconciliationBreakGet>>, TError = unknown>(
 breakId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationBreakGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationBreakGetQueryOptions(breakId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationBreakUpdateResponse200 = {
  data: void
  status: 200
}

export type reconciliationBreakUpdateResponseSuccess = (reconciliationBreakUpdateResponse200) & {
  headers: Headers;
};
;

export type reconciliationBreakUpdateResponse = (reconciliationBreakUpdateResponseSuccess)

export const getReconciliationBreakUpdateUrl = (breakId: string,) => {




  return `/api/reconciliation/breaks/${breakId}`
}

export const reconciliationBreakUpdate = async (breakId: string,
    breakStatusUpdateDto: BreakStatusUpdateDto, options?: RequestInit): Promise<reconciliationBreakUpdateResponse> => {

  return customMutator<reconciliationBreakUpdateResponse>(getReconciliationBreakUpdateUrl(breakId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(breakStatusUpdateDto)
  }
);}





export const getReconciliationBreakUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationBreakUpdate>>, TError,{breakId: string;data: BreakStatusUpdateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationBreakUpdate>>, TError,{breakId: string;data: BreakStatusUpdateDto}, TContext> => {

const mutationKey = ['reconciliationBreakUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationBreakUpdate>>, {breakId: string;data: BreakStatusUpdateDto}> = (props) => {
          const {breakId,data} = props ?? {};

          return  reconciliationBreakUpdate(breakId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationBreakUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationBreakUpdate>>>
    export type ReconciliationBreakUpdateMutationBody = BreakStatusUpdateDto
    export type ReconciliationBreakUpdateMutationError = unknown

    export const useReconciliationBreakUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationBreakUpdate>>, TError,{breakId: string;data: BreakStatusUpdateDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationBreakUpdate>>,
        TError,
        {breakId: string;data: BreakStatusUpdateDto},
        TContext
      > => {
      return useMutation(getReconciliationBreakUpdateMutationOptions(options), queryClient);
    }

export type reconciliationAdjustmentCreatePostResponse200 = {
  data: void
  status: 200
}

export type reconciliationAdjustmentCreatePostResponse201 = {
  data: void
  status: 201
}

export type reconciliationAdjustmentCreatePostResponseSuccess = (reconciliationAdjustmentCreatePostResponse200 | reconciliationAdjustmentCreatePostResponse201) & {
  headers: Headers;
};
;

export type reconciliationAdjustmentCreatePostResponse = (reconciliationAdjustmentCreatePostResponseSuccess)

export const getReconciliationAdjustmentCreatePostUrl = (breakId: string,) => {




  return `/api/reconciliation/breaks/${breakId}/adjustment`
}

export const reconciliationAdjustmentCreatePost = async (breakId: string,
    createAdjustmentDto: CreateAdjustmentDto, options?: RequestInit): Promise<reconciliationAdjustmentCreatePostResponse> => {

  return customMutator<reconciliationAdjustmentCreatePostResponse>(getReconciliationAdjustmentCreatePostUrl(breakId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createAdjustmentDto)
  }
);}





export const getReconciliationAdjustmentCreatePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentCreatePost>>, TError,{breakId: string;data: CreateAdjustmentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentCreatePost>>, TError,{breakId: string;data: CreateAdjustmentDto}, TContext> => {

const mutationKey = ['reconciliationAdjustmentCreatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationAdjustmentCreatePost>>, {breakId: string;data: CreateAdjustmentDto}> = (props) => {
          const {breakId,data} = props ?? {};

          return  reconciliationAdjustmentCreatePost(breakId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationAdjustmentCreatePostMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationAdjustmentCreatePost>>>
    export type ReconciliationAdjustmentCreatePostMutationBody = CreateAdjustmentDto
    export type ReconciliationAdjustmentCreatePostMutationError = unknown

    export const useReconciliationAdjustmentCreatePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentCreatePost>>, TError,{breakId: string;data: CreateAdjustmentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationAdjustmentCreatePost>>,
        TError,
        {breakId: string;data: CreateAdjustmentDto},
        TContext
      > => {
      return useMutation(getReconciliationAdjustmentCreatePostMutationOptions(options), queryClient);
    }

export type reconciliationAgingExecuteResponse200 = {
  data: void
  status: 200
}

export type reconciliationAgingExecuteResponse201 = {
  data: void
  status: 201
}

export type reconciliationAgingExecuteResponseSuccess = (reconciliationAgingExecuteResponse200 | reconciliationAgingExecuteResponse201) & {
  headers: Headers;
};
;

export type reconciliationAgingExecuteResponse = (reconciliationAgingExecuteResponseSuccess)

export const getReconciliationAgingExecuteUrl = () => {




  return `/api/reconciliation/age-breaks`
}

export const reconciliationAgingExecute = async ( options?: RequestInit): Promise<reconciliationAgingExecuteResponse> => {

  return customMutator<reconciliationAgingExecuteResponse>(getReconciliationAgingExecuteUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReconciliationAgingExecuteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAgingExecute>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationAgingExecute>>, TError,void, TContext> => {

const mutationKey = ['reconciliationAgingExecute'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationAgingExecute>>, void> = () => {


          return  reconciliationAgingExecute(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationAgingExecuteMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationAgingExecute>>>

    export type ReconciliationAgingExecuteMutationError = unknown

    export const useReconciliationAgingExecute = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAgingExecute>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationAgingExecute>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getReconciliationAgingExecuteMutationOptions(options), queryClient);
    }

export type reconciliationAdjustmentListResponse200 = {
  data: ReconciliationAdjustmentList200
  status: 200
}

export type reconciliationAdjustmentListResponseSuccess = (reconciliationAdjustmentListResponse200) & {
  headers: Headers;
};
;

export type reconciliationAdjustmentListResponse = (reconciliationAdjustmentListResponseSuccess)

export const getReconciliationAdjustmentListUrl = () => {




  return `/api/reconciliation/adjustments`
}

export const reconciliationAdjustmentList = async ( options?: RequestInit): Promise<reconciliationAdjustmentListResponse> => {

  return customMutator<reconciliationAdjustmentListResponse>(getReconciliationAdjustmentListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReconciliationAdjustmentListQueryKey = () => {
    return [
    `/api/reconciliation/adjustments`
    ] as const;
    }


export const getReconciliationAdjustmentListQueryOptions = <TData = Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReconciliationAdjustmentListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof reconciliationAdjustmentList>>> = ({ signal }) => reconciliationAdjustmentList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReconciliationAdjustmentListQueryResult = NonNullable<Awaited<ReturnType<typeof reconciliationAdjustmentList>>>
export type ReconciliationAdjustmentListQueryError = unknown


export function useReconciliationAdjustmentList<TData = Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationAdjustmentList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationAdjustmentList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationAdjustmentList<TData = Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconciliationAdjustmentList>>,
          TError,
          Awaited<ReturnType<typeof reconciliationAdjustmentList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReconciliationAdjustmentList<TData = Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useReconciliationAdjustmentList<TData = Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof reconciliationAdjustmentList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReconciliationAdjustmentListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type reconciliationAdjustmentApprovePatchResponse200 = {
  data: void
  status: 200
}

export type reconciliationAdjustmentApprovePatchResponseSuccess = (reconciliationAdjustmentApprovePatchResponse200) & {
  headers: Headers;
};
;

export type reconciliationAdjustmentApprovePatchResponse = (reconciliationAdjustmentApprovePatchResponseSuccess)

export const getReconciliationAdjustmentApprovePatchUrl = (adjustmentId: string,) => {




  return `/api/reconciliation/adjustments/${adjustmentId}/approve`
}

export const reconciliationAdjustmentApprovePatch = async (adjustmentId: string,
    adjustmentCommentDto: AdjustmentCommentDto, options?: RequestInit): Promise<reconciliationAdjustmentApprovePatchResponse> => {

  return customMutator<reconciliationAdjustmentApprovePatchResponse>(getReconciliationAdjustmentApprovePatchUrl(adjustmentId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(adjustmentCommentDto)
  }
);}





export const getReconciliationAdjustmentApprovePatchMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentApprovePatch>>, TError,{adjustmentId: string;data: AdjustmentCommentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentApprovePatch>>, TError,{adjustmentId: string;data: AdjustmentCommentDto}, TContext> => {

const mutationKey = ['reconciliationAdjustmentApprovePatch'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationAdjustmentApprovePatch>>, {adjustmentId: string;data: AdjustmentCommentDto}> = (props) => {
          const {adjustmentId,data} = props ?? {};

          return  reconciliationAdjustmentApprovePatch(adjustmentId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationAdjustmentApprovePatchMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationAdjustmentApprovePatch>>>
    export type ReconciliationAdjustmentApprovePatchMutationBody = AdjustmentCommentDto
    export type ReconciliationAdjustmentApprovePatchMutationError = unknown

    export const useReconciliationAdjustmentApprovePatch = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentApprovePatch>>, TError,{adjustmentId: string;data: AdjustmentCommentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationAdjustmentApprovePatch>>,
        TError,
        {adjustmentId: string;data: AdjustmentCommentDto},
        TContext
      > => {
      return useMutation(getReconciliationAdjustmentApprovePatchMutationOptions(options), queryClient);
    }

export type reconciliationAdjustmentRejectResponse200 = {
  data: void
  status: 200
}

export type reconciliationAdjustmentRejectResponseSuccess = (reconciliationAdjustmentRejectResponse200) & {
  headers: Headers;
};
;

export type reconciliationAdjustmentRejectResponse = (reconciliationAdjustmentRejectResponseSuccess)

export const getReconciliationAdjustmentRejectUrl = (adjustmentId: string,) => {




  return `/api/reconciliation/adjustments/${adjustmentId}/reject`
}

export const reconciliationAdjustmentReject = async (adjustmentId: string,
    adjustmentCommentDto: AdjustmentCommentDto, options?: RequestInit): Promise<reconciliationAdjustmentRejectResponse> => {

  return customMutator<reconciliationAdjustmentRejectResponse>(getReconciliationAdjustmentRejectUrl(adjustmentId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(adjustmentCommentDto)
  }
);}





export const getReconciliationAdjustmentRejectMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentReject>>, TError,{adjustmentId: string;data: AdjustmentCommentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentReject>>, TError,{adjustmentId: string;data: AdjustmentCommentDto}, TContext> => {

const mutationKey = ['reconciliationAdjustmentReject'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationAdjustmentReject>>, {adjustmentId: string;data: AdjustmentCommentDto}> = (props) => {
          const {adjustmentId,data} = props ?? {};

          return  reconciliationAdjustmentReject(adjustmentId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationAdjustmentRejectMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationAdjustmentReject>>>
    export type ReconciliationAdjustmentRejectMutationBody = AdjustmentCommentDto
    export type ReconciliationAdjustmentRejectMutationError = unknown

    export const useReconciliationAdjustmentReject = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentReject>>, TError,{adjustmentId: string;data: AdjustmentCommentDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationAdjustmentReject>>,
        TError,
        {adjustmentId: string;data: AdjustmentCommentDto},
        TContext
      > => {
      return useMutation(getReconciliationAdjustmentRejectMutationOptions(options), queryClient);
    }

export type reconciliationAdjustmentPostPostResponse200 = {
  data: void
  status: 200
}

export type reconciliationAdjustmentPostPostResponse201 = {
  data: void
  status: 201
}

export type reconciliationAdjustmentPostPostResponseSuccess = (reconciliationAdjustmentPostPostResponse200 | reconciliationAdjustmentPostPostResponse201) & {
  headers: Headers;
};
;

export type reconciliationAdjustmentPostPostResponse = (reconciliationAdjustmentPostPostResponseSuccess)

export const getReconciliationAdjustmentPostPostUrl = (adjustmentId: string,) => {




  return `/api/reconciliation/adjustments/${adjustmentId}/post`
}

export const reconciliationAdjustmentPostPost = async (adjustmentId: string, options?: RequestInit): Promise<reconciliationAdjustmentPostPostResponse> => {

  return customMutator<reconciliationAdjustmentPostPostResponse>(getReconciliationAdjustmentPostPostUrl(adjustmentId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReconciliationAdjustmentPostPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentPostPost>>, TError,{adjustmentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentPostPost>>, TError,{adjustmentId: string}, TContext> => {

const mutationKey = ['reconciliationAdjustmentPostPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationAdjustmentPostPost>>, {adjustmentId: string}> = (props) => {
          const {adjustmentId} = props ?? {};

          return  reconciliationAdjustmentPostPost(adjustmentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationAdjustmentPostPostMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationAdjustmentPostPost>>>

    export type ReconciliationAdjustmentPostPostMutationError = unknown

    export const useReconciliationAdjustmentPostPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationAdjustmentPostPost>>, TError,{adjustmentId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationAdjustmentPostPost>>,
        TError,
        {adjustmentId: string},
        TContext
      > => {
      return useMutation(getReconciliationAdjustmentPostPostMutationOptions(options), queryClient);
    }

export type reconciliationEscalationExecuteResponse200 = {
  data: void
  status: 200
}

export type reconciliationEscalationExecuteResponse201 = {
  data: void
  status: 201
}

export type reconciliationEscalationExecuteResponseSuccess = (reconciliationEscalationExecuteResponse200 | reconciliationEscalationExecuteResponse201) & {
  headers: Headers;
};
;

export type reconciliationEscalationExecuteResponse = (reconciliationEscalationExecuteResponseSuccess)

export const getReconciliationEscalationExecuteUrl = () => {




  return `/api/reconciliation/escalate-breaks`
}

export const reconciliationEscalationExecute = async ( options?: RequestInit): Promise<reconciliationEscalationExecuteResponse> => {

  return customMutator<reconciliationEscalationExecuteResponse>(getReconciliationEscalationExecuteUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReconciliationEscalationExecuteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationEscalationExecute>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof reconciliationEscalationExecute>>, TError,void, TContext> => {

const mutationKey = ['reconciliationEscalationExecute'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconciliationEscalationExecute>>, void> = () => {


          return  reconciliationEscalationExecute(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconciliationEscalationExecuteMutationResult = NonNullable<Awaited<ReturnType<typeof reconciliationEscalationExecute>>>

    export type ReconciliationEscalationExecuteMutationError = unknown

    export const useReconciliationEscalationExecute = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconciliationEscalationExecute>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reconciliationEscalationExecute>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getReconciliationEscalationExecuteMutationOptions(options), queryClient);
    }

export type regulatoryReportDefinitionCreateResponse201 = {
  data: void
  status: 201
}

export type regulatoryReportDefinitionCreateResponseSuccess = (regulatoryReportDefinitionCreateResponse201) & {
  headers: Headers;
};
;

export type regulatoryReportDefinitionCreateResponse = (regulatoryReportDefinitionCreateResponseSuccess)

export const getRegulatoryReportDefinitionCreateUrl = () => {




  return `/api/regulatory/report-definitions`
}

export const regulatoryReportDefinitionCreate = async (createReportDefinitionDto: CreateReportDefinitionDto, options?: RequestInit): Promise<regulatoryReportDefinitionCreateResponse> => {

  return customMutator<regulatoryReportDefinitionCreateResponse>(getRegulatoryReportDefinitionCreateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createReportDefinitionDto)
  }
);}





export const getRegulatoryReportDefinitionCreateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionCreate>>, TError,{data: CreateReportDefinitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionCreate>>, TError,{data: CreateReportDefinitionDto}, TContext> => {

const mutationKey = ['regulatoryReportDefinitionCreate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryReportDefinitionCreate>>, {data: CreateReportDefinitionDto}> = (props) => {
          const {data} = props ?? {};

          return  regulatoryReportDefinitionCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryReportDefinitionCreateMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportDefinitionCreate>>>
    export type RegulatoryReportDefinitionCreateMutationBody = CreateReportDefinitionDto
    export type RegulatoryReportDefinitionCreateMutationError = unknown

    export const useRegulatoryReportDefinitionCreate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionCreate>>, TError,{data: CreateReportDefinitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryReportDefinitionCreate>>,
        TError,
        {data: CreateReportDefinitionDto},
        TContext
      > => {
      return useMutation(getRegulatoryReportDefinitionCreateMutationOptions(options), queryClient);
    }

export type regulatoryReportDefinitionListResponse200 = {
  data: RegulatoryReportDefinitionList200
  status: 200
}

export type regulatoryReportDefinitionListResponseSuccess = (regulatoryReportDefinitionListResponse200) & {
  headers: Headers;
};
;

export type regulatoryReportDefinitionListResponse = (regulatoryReportDefinitionListResponseSuccess)

export const getRegulatoryReportDefinitionListUrl = () => {




  return `/api/regulatory/report-definitions`
}

export const regulatoryReportDefinitionList = async ( options?: RequestInit): Promise<regulatoryReportDefinitionListResponse> => {

  return customMutator<regulatoryReportDefinitionListResponse>(getRegulatoryReportDefinitionListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getRegulatoryReportDefinitionListQueryKey = () => {
    return [
    `/api/regulatory/report-definitions`
    ] as const;
    }


export const getRegulatoryReportDefinitionListQueryOptions = <TData = Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getRegulatoryReportDefinitionListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof regulatoryReportDefinitionList>>> = ({ signal }) => regulatoryReportDefinitionList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type RegulatoryReportDefinitionListQueryResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportDefinitionList>>>
export type RegulatoryReportDefinitionListQueryError = unknown


export function useRegulatoryReportDefinitionList<TData = Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportDefinitionList>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportDefinitionList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportDefinitionList<TData = Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportDefinitionList>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportDefinitionList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportDefinitionList<TData = Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useRegulatoryReportDefinitionList<TData = Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getRegulatoryReportDefinitionListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type regulatoryReportDefinitionGetResponse200 = {
  data: RegulatoryReportDefinitionGet200
  status: 200
}

export type regulatoryReportDefinitionGetResponseSuccess = (regulatoryReportDefinitionGetResponse200) & {
  headers: Headers;
};
;

export type regulatoryReportDefinitionGetResponse = (regulatoryReportDefinitionGetResponseSuccess)

export const getRegulatoryReportDefinitionGetUrl = (id: string,) => {




  return `/api/regulatory/report-definitions/${id}`
}

export const regulatoryReportDefinitionGet = async (id: string, options?: RequestInit): Promise<regulatoryReportDefinitionGetResponse> => {

  return customMutator<regulatoryReportDefinitionGetResponse>(getRegulatoryReportDefinitionGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getRegulatoryReportDefinitionGetQueryKey = (id: string,) => {
    return [
    `/api/regulatory/report-definitions/${id}`
    ] as const;
    }


export const getRegulatoryReportDefinitionGetQueryOptions = <TData = Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getRegulatoryReportDefinitionGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>> = ({ signal }) => regulatoryReportDefinitionGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type RegulatoryReportDefinitionGetQueryResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>>
export type RegulatoryReportDefinitionGetQueryError = unknown


export function useRegulatoryReportDefinitionGet<TData = Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportDefinitionGet<TData = Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportDefinitionGet<TData = Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useRegulatoryReportDefinitionGet<TData = Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getRegulatoryReportDefinitionGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type regulatoryReportDefinitionUpdateResponse200 = {
  data: RegulatoryReportDefinitionUpdate200
  status: 200
}

export type regulatoryReportDefinitionUpdateResponseSuccess = (regulatoryReportDefinitionUpdateResponse200) & {
  headers: Headers;
};
;

export type regulatoryReportDefinitionUpdateResponse = (regulatoryReportDefinitionUpdateResponseSuccess)

export const getRegulatoryReportDefinitionUpdateUrl = (id: string,) => {




  return `/api/regulatory/report-definitions/${id}`
}

export const regulatoryReportDefinitionUpdate = async (id: string,
    createReportDefinitionDto: CreateReportDefinitionDto, options?: RequestInit): Promise<regulatoryReportDefinitionUpdateResponse> => {

  return customMutator<regulatoryReportDefinitionUpdateResponse>(getRegulatoryReportDefinitionUpdateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createReportDefinitionDto)
  }
);}





export const getRegulatoryReportDefinitionUpdateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionUpdate>>, TError,{id: string;data: CreateReportDefinitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionUpdate>>, TError,{id: string;data: CreateReportDefinitionDto}, TContext> => {

const mutationKey = ['regulatoryReportDefinitionUpdate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryReportDefinitionUpdate>>, {id: string;data: CreateReportDefinitionDto}> = (props) => {
          const {id,data} = props ?? {};

          return  regulatoryReportDefinitionUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryReportDefinitionUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportDefinitionUpdate>>>
    export type RegulatoryReportDefinitionUpdateMutationBody = CreateReportDefinitionDto
    export type RegulatoryReportDefinitionUpdateMutationError = unknown

    export const useRegulatoryReportDefinitionUpdate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionUpdate>>, TError,{id: string;data: CreateReportDefinitionDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryReportDefinitionUpdate>>,
        TError,
        {id: string;data: CreateReportDefinitionDto},
        TContext
      > => {
      return useMutation(getRegulatoryReportDefinitionUpdateMutationOptions(options), queryClient);
    }

export type regulatoryReportDefinitionDisableResponse200 = {
  data: RegulatoryReportDefinitionDisable200
  status: 200
}

export type regulatoryReportDefinitionDisableResponseSuccess = (regulatoryReportDefinitionDisableResponse200) & {
  headers: Headers;
};
;

export type regulatoryReportDefinitionDisableResponse = (regulatoryReportDefinitionDisableResponseSuccess)

export const getRegulatoryReportDefinitionDisableUrl = (id: string,) => {




  return `/api/regulatory/report-definitions/${id}/disable`
}

export const regulatoryReportDefinitionDisable = async (id: string, options?: RequestInit): Promise<regulatoryReportDefinitionDisableResponse> => {

  return customMutator<regulatoryReportDefinitionDisableResponse>(getRegulatoryReportDefinitionDisableUrl(id),
  {
    ...options,
    method: 'PATCH'


  }
);}





export const getRegulatoryReportDefinitionDisableMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionDisable>>, TError,{id: string}, TContext> => {

const mutationKey = ['regulatoryReportDefinitionDisable'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryReportDefinitionDisable>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  regulatoryReportDefinitionDisable(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryReportDefinitionDisableMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportDefinitionDisable>>>

    export type RegulatoryReportDefinitionDisableMutationError = unknown

    export const useRegulatoryReportDefinitionDisable = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportDefinitionDisable>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryReportDefinitionDisable>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getRegulatoryReportDefinitionDisableMutationOptions(options), queryClient);
    }

export type regulatoryReportRunGenerateResponse201 = {
  data: RegulatoryReportRunGenerate201
  status: 201
}

export type regulatoryReportRunGenerateResponseSuccess = (regulatoryReportRunGenerateResponse201) & {
  headers: Headers;
};
;

export type regulatoryReportRunGenerateResponse = (regulatoryReportRunGenerateResponseSuccess)

export const getRegulatoryReportRunGenerateUrl = () => {




  return `/api/regulatory/report-runs`
}

export const regulatoryReportRunGenerate = async (generateReportDto: GenerateReportDto, options?: RequestInit): Promise<regulatoryReportRunGenerateResponse> => {

  return customMutator<regulatoryReportRunGenerateResponse>(getRegulatoryReportRunGenerateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(generateReportDto)
  }
);}





export const getRegulatoryReportRunGenerateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunGenerate>>, TError,{data: GenerateReportDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunGenerate>>, TError,{data: GenerateReportDto}, TContext> => {

const mutationKey = ['regulatoryReportRunGenerate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryReportRunGenerate>>, {data: GenerateReportDto}> = (props) => {
          const {data} = props ?? {};

          return  regulatoryReportRunGenerate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryReportRunGenerateMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportRunGenerate>>>
    export type RegulatoryReportRunGenerateMutationBody = GenerateReportDto
    export type RegulatoryReportRunGenerateMutationError = unknown

    export const useRegulatoryReportRunGenerate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunGenerate>>, TError,{data: GenerateReportDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryReportRunGenerate>>,
        TError,
        {data: GenerateReportDto},
        TContext
      > => {
      return useMutation(getRegulatoryReportRunGenerateMutationOptions(options), queryClient);
    }

export type regulatoryReportRunListResponse200 = {
  data: RegulatoryReportRunList200
  status: 200
}

export type regulatoryReportRunListResponseSuccess = (regulatoryReportRunListResponse200) & {
  headers: Headers;
};
;

export type regulatoryReportRunListResponse = (regulatoryReportRunListResponseSuccess)

export const getRegulatoryReportRunListUrl = () => {




  return `/api/regulatory/report-runs`
}

export const regulatoryReportRunList = async ( options?: RequestInit): Promise<regulatoryReportRunListResponse> => {

  return customMutator<regulatoryReportRunListResponse>(getRegulatoryReportRunListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getRegulatoryReportRunListQueryKey = () => {
    return [
    `/api/regulatory/report-runs`
    ] as const;
    }


export const getRegulatoryReportRunListQueryOptions = <TData = Awaited<ReturnType<typeof regulatoryReportRunList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getRegulatoryReportRunListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof regulatoryReportRunList>>> = ({ signal }) => regulatoryReportRunList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type RegulatoryReportRunListQueryResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportRunList>>>
export type RegulatoryReportRunListQueryError = unknown


export function useRegulatoryReportRunList<TData = Awaited<ReturnType<typeof regulatoryReportRunList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportRunList>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportRunList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportRunList<TData = Awaited<ReturnType<typeof regulatoryReportRunList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportRunList>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportRunList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportRunList<TData = Awaited<ReturnType<typeof regulatoryReportRunList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useRegulatoryReportRunList<TData = Awaited<ReturnType<typeof regulatoryReportRunList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getRegulatoryReportRunListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type regulatorySubmissionListResponse200 = {
  data: RegulatorySubmissionList200
  status: 200
}

export type regulatorySubmissionListResponseSuccess = (regulatorySubmissionListResponse200) & {
  headers: Headers;
};
;

export type regulatorySubmissionListResponse = (regulatorySubmissionListResponseSuccess)

export const getRegulatorySubmissionListUrl = () => {




  return `/api/regulatory/report-runs/submissions`
}

export const regulatorySubmissionList = async ( options?: RequestInit): Promise<regulatorySubmissionListResponse> => {

  return customMutator<regulatorySubmissionListResponse>(getRegulatorySubmissionListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getRegulatorySubmissionListQueryKey = () => {
    return [
    `/api/regulatory/report-runs/submissions`
    ] as const;
    }


export const getRegulatorySubmissionListQueryOptions = <TData = Awaited<ReturnType<typeof regulatorySubmissionList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatorySubmissionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getRegulatorySubmissionListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof regulatorySubmissionList>>> = ({ signal }) => regulatorySubmissionList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof regulatorySubmissionList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type RegulatorySubmissionListQueryResult = NonNullable<Awaited<ReturnType<typeof regulatorySubmissionList>>>
export type RegulatorySubmissionListQueryError = unknown


export function useRegulatorySubmissionList<TData = Awaited<ReturnType<typeof regulatorySubmissionList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatorySubmissionList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatorySubmissionList>>,
          TError,
          Awaited<ReturnType<typeof regulatorySubmissionList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatorySubmissionList<TData = Awaited<ReturnType<typeof regulatorySubmissionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatorySubmissionList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatorySubmissionList>>,
          TError,
          Awaited<ReturnType<typeof regulatorySubmissionList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatorySubmissionList<TData = Awaited<ReturnType<typeof regulatorySubmissionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatorySubmissionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useRegulatorySubmissionList<TData = Awaited<ReturnType<typeof regulatorySubmissionList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatorySubmissionList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getRegulatorySubmissionListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type regulatoryReportRunGetResponse200 = {
  data: RegulatoryReportRunGet200
  status: 200
}

export type regulatoryReportRunGetResponseSuccess = (regulatoryReportRunGetResponse200) & {
  headers: Headers;
};
;

export type regulatoryReportRunGetResponse = (regulatoryReportRunGetResponseSuccess)

export const getRegulatoryReportRunGetUrl = (id: string,) => {




  return `/api/regulatory/report-runs/${id}`
}

export const regulatoryReportRunGet = async (id: string, options?: RequestInit): Promise<regulatoryReportRunGetResponse> => {

  return customMutator<regulatoryReportRunGetResponse>(getRegulatoryReportRunGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getRegulatoryReportRunGetQueryKey = (id: string,) => {
    return [
    `/api/regulatory/report-runs/${id}`
    ] as const;
    }


export const getRegulatoryReportRunGetQueryOptions = <TData = Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getRegulatoryReportRunGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof regulatoryReportRunGet>>> = ({ signal }) => regulatoryReportRunGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type RegulatoryReportRunGetQueryResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportRunGet>>>
export type RegulatoryReportRunGetQueryError = unknown


export function useRegulatoryReportRunGet<TData = Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportRunGet>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportRunGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportRunGet<TData = Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportRunGet>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportRunGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportRunGet<TData = Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useRegulatoryReportRunGet<TData = Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getRegulatoryReportRunGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type regulatoryReportRunArtifactGetResponse200 = {
  data: RegulatoryReportRunArtifactGet200
  status: 200
}

export type regulatoryReportRunArtifactGetResponseSuccess = (regulatoryReportRunArtifactGetResponse200) & {
  headers: Headers;
};
;

export type regulatoryReportRunArtifactGetResponse = (regulatoryReportRunArtifactGetResponseSuccess)

export const getRegulatoryReportRunArtifactGetUrl = (id: string,) => {




  return `/api/regulatory/report-runs/${id}/artifact`
}

export const regulatoryReportRunArtifactGet = async (id: string, options?: RequestInit): Promise<regulatoryReportRunArtifactGetResponse> => {

  return customMutator<regulatoryReportRunArtifactGetResponse>(getRegulatoryReportRunArtifactGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getRegulatoryReportRunArtifactGetQueryKey = (id: string,) => {
    return [
    `/api/regulatory/report-runs/${id}/artifact`
    ] as const;
    }


export const getRegulatoryReportRunArtifactGetQueryOptions = <TData = Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getRegulatoryReportRunArtifactGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>> = ({ signal }) => regulatoryReportRunArtifactGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type RegulatoryReportRunArtifactGetQueryResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>>
export type RegulatoryReportRunArtifactGetQueryError = unknown


export function useRegulatoryReportRunArtifactGet<TData = Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportRunArtifactGet<TData = Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>,
          TError,
          Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryReportRunArtifactGet<TData = Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useRegulatoryReportRunArtifactGet<TData = Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryReportRunArtifactGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getRegulatoryReportRunArtifactGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type regulatoryReportRunExportResponse200 = {
  data: void
  status: 200
}

export type regulatoryReportRunExportResponseSuccess = (regulatoryReportRunExportResponse200) & {
  headers: Headers;
};
;

export type regulatoryReportRunExportResponse = (regulatoryReportRunExportResponseSuccess)

export const getRegulatoryReportRunExportUrl = (id: string,) => {




  return `/api/regulatory/report-runs/${id}/export`
}

export const regulatoryReportRunExport = async (id: string,
    exportReportDto: ExportReportDto, options?: RequestInit): Promise<regulatoryReportRunExportResponse> => {

  return customMutator<regulatoryReportRunExportResponse>(getRegulatoryReportRunExportUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(exportReportDto)
  }
);}





export const getRegulatoryReportRunExportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunExport>>, TError,{id: string;data: ExportReportDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunExport>>, TError,{id: string;data: ExportReportDto}, TContext> => {

const mutationKey = ['regulatoryReportRunExport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryReportRunExport>>, {id: string;data: ExportReportDto}> = (props) => {
          const {id,data} = props ?? {};

          return  regulatoryReportRunExport(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryReportRunExportMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportRunExport>>>
    export type RegulatoryReportRunExportMutationBody = ExportReportDto
    export type RegulatoryReportRunExportMutationError = unknown

    export const useRegulatoryReportRunExport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunExport>>, TError,{id: string;data: ExportReportDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryReportRunExport>>,
        TError,
        {id: string;data: ExportReportDto},
        TContext
      > => {
      return useMutation(getRegulatoryReportRunExportMutationOptions(options), queryClient);
    }

export type regulatoryReportRunValidateResponse201 = {
  data: RegulatoryReportRunValidate201
  status: 201
}

export type regulatoryReportRunValidateResponse400 = {
  data: void
  status: 400
}

export type regulatoryReportRunValidateResponseSuccess = (regulatoryReportRunValidateResponse201) & {
  headers: Headers;
};
export type regulatoryReportRunValidateResponseError = (regulatoryReportRunValidateResponse400) & {
  headers: Headers;
};

export type regulatoryReportRunValidateResponse = (regulatoryReportRunValidateResponseSuccess | regulatoryReportRunValidateResponseError)

export const getRegulatoryReportRunValidateUrl = (id: string,) => {




  return `/api/regulatory/report-runs/${id}/validate`
}

export const regulatoryReportRunValidate = async (id: string, options?: RequestInit): Promise<regulatoryReportRunValidateResponse> => {

  return customMutator<regulatoryReportRunValidateResponse>(getRegulatoryReportRunValidateUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRegulatoryReportRunValidateMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunValidate>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunValidate>>, TError,{id: string}, TContext> => {

const mutationKey = ['regulatoryReportRunValidate'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryReportRunValidate>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  regulatoryReportRunValidate(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryReportRunValidateMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportRunValidate>>>

    export type RegulatoryReportRunValidateMutationError = void

    export const useRegulatoryReportRunValidate = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunValidate>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryReportRunValidate>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getRegulatoryReportRunValidateMutationOptions(options), queryClient);
    }

export type regulatoryReportRunSubmitResponse201 = {
  data: RegulatoryReportRunSubmit201
  status: 201
}

export type regulatoryReportRunSubmitResponseSuccess = (regulatoryReportRunSubmitResponse201) & {
  headers: Headers;
};
;

export type regulatoryReportRunSubmitResponse = (regulatoryReportRunSubmitResponseSuccess)

export const getRegulatoryReportRunSubmitUrl = (id: string,) => {




  return `/api/regulatory/report-runs/${id}/submit`
}

export const regulatoryReportRunSubmit = async (id: string, options?: RequestInit): Promise<regulatoryReportRunSubmitResponse> => {

  return customMutator<regulatoryReportRunSubmitResponse>(getRegulatoryReportRunSubmitUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRegulatoryReportRunSubmitMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunSubmit>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunSubmit>>, TError,{id: string}, TContext> => {

const mutationKey = ['regulatoryReportRunSubmit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryReportRunSubmit>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  regulatoryReportRunSubmit(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryReportRunSubmitMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportRunSubmit>>>

    export type RegulatoryReportRunSubmitMutationError = unknown

    export const useRegulatoryReportRunSubmit = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunSubmit>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryReportRunSubmit>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getRegulatoryReportRunSubmitMutationOptions(options), queryClient);
    }

export type regulatorySubmissionAcknowledgeResponse200 = {
  data: RegulatorySubmissionAcknowledge200
  status: 200
}

export type regulatorySubmissionAcknowledgeResponseSuccess = (regulatorySubmissionAcknowledgeResponse200) & {
  headers: Headers;
};
;

export type regulatorySubmissionAcknowledgeResponse = (regulatorySubmissionAcknowledgeResponseSuccess)

export const getRegulatorySubmissionAcknowledgeUrl = (id: string,) => {




  return `/api/regulatory/report-runs/${id}/acknowledge`
}

export const regulatorySubmissionAcknowledge = async (id: string, options?: RequestInit): Promise<regulatorySubmissionAcknowledgeResponse> => {

  return customMutator<regulatorySubmissionAcknowledgeResponse>(getRegulatorySubmissionAcknowledgeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRegulatorySubmissionAcknowledgeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatorySubmissionAcknowledge>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatorySubmissionAcknowledge>>, TError,{id: string}, TContext> => {

const mutationKey = ['regulatorySubmissionAcknowledge'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatorySubmissionAcknowledge>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  regulatorySubmissionAcknowledge(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatorySubmissionAcknowledgeMutationResult = NonNullable<Awaited<ReturnType<typeof regulatorySubmissionAcknowledge>>>

    export type RegulatorySubmissionAcknowledgeMutationError = unknown

    export const useRegulatorySubmissionAcknowledge = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatorySubmissionAcknowledge>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatorySubmissionAcknowledge>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getRegulatorySubmissionAcknowledgeMutationOptions(options), queryClient);
    }

export type regulatoryReportRunCancelResponse200 = {
  data: RegulatoryReportRunCancel200
  status: 200
}

export type regulatoryReportRunCancelResponseSuccess = (regulatoryReportRunCancelResponse200) & {
  headers: Headers;
};
;

export type regulatoryReportRunCancelResponse = (regulatoryReportRunCancelResponseSuccess)

export const getRegulatoryReportRunCancelUrl = (id: string,) => {




  return `/api/regulatory/report-runs/${id}/cancel`
}

export const regulatoryReportRunCancel = async (id: string,
    reportRunLifecycleCommandDto: ReportRunLifecycleCommandDto, options?: RequestInit): Promise<regulatoryReportRunCancelResponse> => {

  return customMutator<regulatoryReportRunCancelResponse>(getRegulatoryReportRunCancelUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(reportRunLifecycleCommandDto)
  }
);}





export const getRegulatoryReportRunCancelMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunCancel>>, TError,{id: string;data: ReportRunLifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunCancel>>, TError,{id: string;data: ReportRunLifecycleCommandDto}, TContext> => {

const mutationKey = ['regulatoryReportRunCancel'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryReportRunCancel>>, {id: string;data: ReportRunLifecycleCommandDto}> = (props) => {
          const {id,data} = props ?? {};

          return  regulatoryReportRunCancel(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryReportRunCancelMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportRunCancel>>>
    export type RegulatoryReportRunCancelMutationBody = ReportRunLifecycleCommandDto
    export type RegulatoryReportRunCancelMutationError = unknown

    export const useRegulatoryReportRunCancel = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunCancel>>, TError,{id: string;data: ReportRunLifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryReportRunCancel>>,
        TError,
        {id: string;data: ReportRunLifecycleCommandDto},
        TContext
      > => {
      return useMutation(getRegulatoryReportRunCancelMutationOptions(options), queryClient);
    }

export type regulatoryReportRunRetryResponse201 = {
  data: RegulatoryReportRunRetry201
  status: 201
}

export type regulatoryReportRunRetryResponseSuccess = (regulatoryReportRunRetryResponse201) & {
  headers: Headers;
};
;

export type regulatoryReportRunRetryResponse = (regulatoryReportRunRetryResponseSuccess)

export const getRegulatoryReportRunRetryUrl = (id: string,) => {




  return `/api/regulatory/report-runs/${id}/retry`
}

export const regulatoryReportRunRetry = async (id: string,
    reportRunLifecycleCommandDto: ReportRunLifecycleCommandDto, options?: RequestInit): Promise<regulatoryReportRunRetryResponse> => {

  return customMutator<regulatoryReportRunRetryResponse>(getRegulatoryReportRunRetryUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(reportRunLifecycleCommandDto)
  }
);}





export const getRegulatoryReportRunRetryMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunRetry>>, TError,{id: string;data: ReportRunLifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunRetry>>, TError,{id: string;data: ReportRunLifecycleCommandDto}, TContext> => {

const mutationKey = ['regulatoryReportRunRetry'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryReportRunRetry>>, {id: string;data: ReportRunLifecycleCommandDto}> = (props) => {
          const {id,data} = props ?? {};

          return  regulatoryReportRunRetry(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryReportRunRetryMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryReportRunRetry>>>
    export type RegulatoryReportRunRetryMutationBody = ReportRunLifecycleCommandDto
    export type RegulatoryReportRunRetryMutationError = unknown

    export const useRegulatoryReportRunRetry = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryReportRunRetry>>, TError,{id: string;data: ReportRunLifecycleCommandDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryReportRunRetry>>,
        TError,
        {id: string;data: ReportRunLifecycleCommandDto},
        TContext
      > => {
      return useMutation(getRegulatoryReportRunRetryMutationOptions(options), queryClient);
    }

export type regulatoryTaxRemittanceListResponse200 = {
  data: RegulatoryTaxRemittanceList200
  status: 200
}

export type regulatoryTaxRemittanceListResponseSuccess = (regulatoryTaxRemittanceListResponse200) & {
  headers: Headers;
};
;

export type regulatoryTaxRemittanceListResponse = (regulatoryTaxRemittanceListResponseSuccess)

export const getRegulatoryTaxRemittanceListUrl = () => {




  return `/api/regulatory/tax-remittances`
}

export const regulatoryTaxRemittanceList = async ( options?: RequestInit): Promise<regulatoryTaxRemittanceListResponse> => {

  return customMutator<regulatoryTaxRemittanceListResponse>(getRegulatoryTaxRemittanceListUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getRegulatoryTaxRemittanceListQueryKey = () => {
    return [
    `/api/regulatory/tax-remittances`
    ] as const;
    }


export const getRegulatoryTaxRemittanceListQueryOptions = <TData = Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getRegulatoryTaxRemittanceListQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>> = ({ signal }) => regulatoryTaxRemittanceList({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type RegulatoryTaxRemittanceListQueryResult = NonNullable<Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>>
export type RegulatoryTaxRemittanceListQueryError = unknown


export function useRegulatoryTaxRemittanceList<TData = Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>,
          TError,
          Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryTaxRemittanceList<TData = Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>,
          TError,
          Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRegulatoryTaxRemittanceList<TData = Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useRegulatoryTaxRemittanceList<TData = Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof regulatoryTaxRemittanceList>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getRegulatoryTaxRemittanceListQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type regulatoryTaxRemittanceRecordResponse201 = {
  data: RegulatoryTaxRemittanceRecord201
  status: 201
}

export type regulatoryTaxRemittanceRecordResponseSuccess = (regulatoryTaxRemittanceRecordResponse201) & {
  headers: Headers;
};
;

export type regulatoryTaxRemittanceRecordResponse = (regulatoryTaxRemittanceRecordResponseSuccess)

export const getRegulatoryTaxRemittanceRecordUrl = (id: string,) => {




  return `/api/regulatory/report-runs/${id}/tax-remittances`
}

export const regulatoryTaxRemittanceRecord = async (id: string,
    recordTaxRemittanceDto: RecordTaxRemittanceDto, options?: RequestInit): Promise<regulatoryTaxRemittanceRecordResponse> => {

  return customMutator<regulatoryTaxRemittanceRecordResponse>(getRegulatoryTaxRemittanceRecordUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(recordTaxRemittanceDto)
  }
);}





export const getRegulatoryTaxRemittanceRecordMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryTaxRemittanceRecord>>, TError,{id: string;data: RecordTaxRemittanceDto}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof regulatoryTaxRemittanceRecord>>, TError,{id: string;data: RecordTaxRemittanceDto}, TContext> => {

const mutationKey = ['regulatoryTaxRemittanceRecord'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof regulatoryTaxRemittanceRecord>>, {id: string;data: RecordTaxRemittanceDto}> = (props) => {
          const {id,data} = props ?? {};

          return  regulatoryTaxRemittanceRecord(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegulatoryTaxRemittanceRecordMutationResult = NonNullable<Awaited<ReturnType<typeof regulatoryTaxRemittanceRecord>>>
    export type RegulatoryTaxRemittanceRecordMutationBody = RecordTaxRemittanceDto
    export type RegulatoryTaxRemittanceRecordMutationError = unknown

    export const useRegulatoryTaxRemittanceRecord = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof regulatoryTaxRemittanceRecord>>, TError,{id: string;data: RecordTaxRemittanceDto}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof regulatoryTaxRemittanceRecord>>,
        TError,
        {id: string;data: RecordTaxRemittanceDto},
        TContext
      > => {
      return useMutation(getRegulatoryTaxRemittanceRecordMutationOptions(options), queryClient);
    }

export type accessPermissionsReadResponse200 = {
  data: void
  status: 200
}

export type accessPermissionsReadResponseSuccess = (accessPermissionsReadResponse200) & {
  headers: Headers;
};
;

export type accessPermissionsReadResponse = (accessPermissionsReadResponseSuccess)

export const getAccessPermissionsReadUrl = () => {




  return `/api/access/persona-policies`
}

export const accessPermissionsRead = async ( options?: RequestInit): Promise<accessPermissionsReadResponse> => {

  return customMutator<accessPermissionsReadResponse>(getAccessPermissionsReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccessPermissionsReadQueryKey = () => {
    return [
    `/api/access/persona-policies`
    ] as const;
    }


export const getAccessPermissionsReadQueryOptions = <TData = Awaited<ReturnType<typeof accessPermissionsRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccessPermissionsReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accessPermissionsRead>>> = ({ signal }) => accessPermissionsRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccessPermissionsReadQueryResult = NonNullable<Awaited<ReturnType<typeof accessPermissionsRead>>>
export type AccessPermissionsReadQueryError = unknown


export function useAccessPermissionsRead<TData = Awaited<ReturnType<typeof accessPermissionsRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accessPermissionsRead>>,
          TError,
          Awaited<ReturnType<typeof accessPermissionsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccessPermissionsRead<TData = Awaited<ReturnType<typeof accessPermissionsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accessPermissionsRead>>,
          TError,
          Awaited<ReturnType<typeof accessPermissionsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccessPermissionsRead<TData = Awaited<ReturnType<typeof accessPermissionsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccessPermissionsRead<TData = Awaited<ReturnType<typeof accessPermissionsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccessPermissionsReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accessPermissionsReadGetResponse200 = {
  data: void
  status: 200
}

export type accessPermissionsReadGetResponseSuccess = (accessPermissionsReadGetResponse200) & {
  headers: Headers;
};
;

export type accessPermissionsReadGetResponse = (accessPermissionsReadGetResponseSuccess)

export const getAccessPermissionsReadGetUrl = (persona: string,) => {




  return `/api/access/persona-policies/${persona}`
}

export const accessPermissionsReadGet = async (persona: string, options?: RequestInit): Promise<accessPermissionsReadGetResponse> => {

  return customMutator<accessPermissionsReadGetResponse>(getAccessPermissionsReadGetUrl(persona),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccessPermissionsReadGetQueryKey = (persona: string,) => {
    return [
    `/api/access/persona-policies/${persona}`
    ] as const;
    }


export const getAccessPermissionsReadGetQueryOptions = <TData = Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError = unknown>(persona: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccessPermissionsReadGetQueryKey(persona);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accessPermissionsReadGet>>> = ({ signal }) => accessPermissionsReadGet(persona, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: persona !== null && persona !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccessPermissionsReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof accessPermissionsReadGet>>>
export type AccessPermissionsReadGetQueryError = unknown


export function useAccessPermissionsReadGet<TData = Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError = unknown>(
 persona: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accessPermissionsReadGet>>,
          TError,
          Awaited<ReturnType<typeof accessPermissionsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccessPermissionsReadGet<TData = Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError = unknown>(
 persona: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accessPermissionsReadGet>>,
          TError,
          Awaited<ReturnType<typeof accessPermissionsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccessPermissionsReadGet<TData = Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError = unknown>(
 persona: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccessPermissionsReadGet<TData = Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError = unknown>(
 persona: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccessPermissionsReadGetQueryOptions(persona,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accessPermissionsManageResponse201 = {
  data: void
  status: 201
}

export type accessPermissionsManageResponseSuccess = (accessPermissionsManageResponse201) & {
  headers: Headers;
};
;

export type accessPermissionsManageResponse = (accessPermissionsManageResponseSuccess)

export const getAccessPermissionsManageUrl = () => {




  return `/api/access/organization-permission-assignment/validate`
}

export const accessPermissionsManage = async ( options?: RequestInit): Promise<accessPermissionsManageResponse> => {

  return customMutator<accessPermissionsManageResponse>(getAccessPermissionsManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAccessPermissionsManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accessPermissionsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accessPermissionsManage>>, TError,void, TContext> => {

const mutationKey = ['accessPermissionsManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accessPermissionsManage>>, void> = () => {


          return  accessPermissionsManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccessPermissionsManageMutationResult = NonNullable<Awaited<ReturnType<typeof accessPermissionsManage>>>

    export type AccessPermissionsManageMutationError = unknown

    export const useAccessPermissionsManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accessPermissionsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accessPermissionsManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAccessPermissionsManageMutationOptions(options), queryClient);
    }

export type accessPermissionsReadGet1Response200 = {
  data: void
  status: 200
}

export type accessPermissionsReadGet1ResponseSuccess = (accessPermissionsReadGet1Response200) & {
  headers: Headers;
};
;

export type accessPermissionsReadGet1Response = (accessPermissionsReadGet1ResponseSuccess)

export const getAccessPermissionsReadGet1Url = () => {




  return `/api/access/platform-access-inspector`
}

export const accessPermissionsReadGet1 = async ( options?: RequestInit): Promise<accessPermissionsReadGet1Response> => {

  return customMutator<accessPermissionsReadGet1Response>(getAccessPermissionsReadGet1Url(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccessPermissionsReadGet1QueryKey = () => {
    return [
    `/api/access/platform-access-inspector`
    ] as const;
    }


export const getAccessPermissionsReadGet1QueryOptions = <TData = Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccessPermissionsReadGet1QueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accessPermissionsReadGet1>>> = ({ signal }) => accessPermissionsReadGet1({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccessPermissionsReadGet1QueryResult = NonNullable<Awaited<ReturnType<typeof accessPermissionsReadGet1>>>
export type AccessPermissionsReadGet1QueryError = unknown


export function useAccessPermissionsReadGet1<TData = Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accessPermissionsReadGet1>>,
          TError,
          Awaited<ReturnType<typeof accessPermissionsReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccessPermissionsReadGet1<TData = Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accessPermissionsReadGet1>>,
          TError,
          Awaited<ReturnType<typeof accessPermissionsReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccessPermissionsReadGet1<TData = Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccessPermissionsReadGet1<TData = Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accessPermissionsReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccessPermissionsReadGet1QueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type organizationAccessInspectorReadResponse200 = {
  data: void
  status: 200
}

export type organizationAccessInspectorReadResponseSuccess = (organizationAccessInspectorReadResponse200) & {
  headers: Headers;
};
;

export type organizationAccessInspectorReadResponse = (organizationAccessInspectorReadResponseSuccess)

export const getOrganizationAccessInspectorReadUrl = () => {




  return `/api/access/organization-access-inspector`
}

export const organizationAccessInspectorRead = async ( options?: RequestInit): Promise<organizationAccessInspectorReadResponse> => {

  return customMutator<organizationAccessInspectorReadResponse>(getOrganizationAccessInspectorReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOrganizationAccessInspectorReadQueryKey = () => {
    return [
    `/api/access/organization-access-inspector`
    ] as const;
    }


export const getOrganizationAccessInspectorReadQueryOptions = <TData = Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOrganizationAccessInspectorReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof organizationAccessInspectorRead>>> = ({ signal }) => organizationAccessInspectorRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type OrganizationAccessInspectorReadQueryResult = NonNullable<Awaited<ReturnType<typeof organizationAccessInspectorRead>>>
export type OrganizationAccessInspectorReadQueryError = unknown


export function useOrganizationAccessInspectorRead<TData = Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAccessInspectorRead>>,
          TError,
          Awaited<ReturnType<typeof organizationAccessInspectorRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAccessInspectorRead<TData = Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof organizationAccessInspectorRead>>,
          TError,
          Awaited<ReturnType<typeof organizationAccessInspectorRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useOrganizationAccessInspectorRead<TData = Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useOrganizationAccessInspectorRead<TData = Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof organizationAccessInspectorRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getOrganizationAccessInspectorReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformSecurityIncidentsReadResponse200 = {
  data: void
  status: 200
}

export type platformSecurityIncidentsReadResponseSuccess = (platformSecurityIncidentsReadResponse200) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsReadResponse = (platformSecurityIncidentsReadResponseSuccess)

export const getPlatformSecurityIncidentsReadUrl = () => {




  return `/api/platform/security-incidents`
}

export const platformSecurityIncidentsRead = async ( options?: RequestInit): Promise<platformSecurityIncidentsReadResponse> => {

  return customMutator<platformSecurityIncidentsReadResponse>(getPlatformSecurityIncidentsReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformSecurityIncidentsReadQueryKey = () => {
    return [
    `/api/platform/security-incidents`
    ] as const;
    }


export const getPlatformSecurityIncidentsReadQueryOptions = <TData = Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformSecurityIncidentsReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformSecurityIncidentsRead>>> = ({ signal }) => platformSecurityIncidentsRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformSecurityIncidentsReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsRead>>>
export type PlatformSecurityIncidentsReadQueryError = unknown


export function usePlatformSecurityIncidentsRead<TData = Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformSecurityIncidentsRead>>,
          TError,
          Awaited<ReturnType<typeof platformSecurityIncidentsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformSecurityIncidentsRead<TData = Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformSecurityIncidentsRead>>,
          TError,
          Awaited<ReturnType<typeof platformSecurityIncidentsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformSecurityIncidentsRead<TData = Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformSecurityIncidentsRead<TData = Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformSecurityIncidentsReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformSecurityIncidentsManageResponse201 = {
  data: void
  status: 201
}

export type platformSecurityIncidentsManageResponseSuccess = (platformSecurityIncidentsManageResponse201) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsManageResponse = (platformSecurityIncidentsManageResponseSuccess)

export const getPlatformSecurityIncidentsManageUrl = () => {




  return `/api/platform/security-incidents`
}

export const platformSecurityIncidentsManage = async ( options?: RequestInit): Promise<platformSecurityIncidentsManageResponse> => {

  return customMutator<platformSecurityIncidentsManageResponse>(getPlatformSecurityIncidentsManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSecurityIncidentsManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManage>>, TError,void, TContext> => {

const mutationKey = ['platformSecurityIncidentsManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSecurityIncidentsManage>>, void> = () => {


          return  platformSecurityIncidentsManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSecurityIncidentsManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsManage>>>

    export type PlatformSecurityIncidentsManageMutationError = unknown

    export const usePlatformSecurityIncidentsManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSecurityIncidentsManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformSecurityIncidentsManageMutationOptions(options), queryClient);
    }

export type platformSecurityIncidentsReadGetResponse200 = {
  data: void
  status: 200
}

export type platformSecurityIncidentsReadGetResponseSuccess = (platformSecurityIncidentsReadGetResponse200) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsReadGetResponse = (platformSecurityIncidentsReadGetResponseSuccess)

export const getPlatformSecurityIncidentsReadGetUrl = (id: string,) => {




  return `/api/platform/security-incidents/${id}`
}

export const platformSecurityIncidentsReadGet = async (id: string, options?: RequestInit): Promise<platformSecurityIncidentsReadGetResponse> => {

  return customMutator<platformSecurityIncidentsReadGetResponse>(getPlatformSecurityIncidentsReadGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformSecurityIncidentsReadGetQueryKey = (id: string,) => {
    return [
    `/api/platform/security-incidents/${id}`
    ] as const;
    }


export const getPlatformSecurityIncidentsReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformSecurityIncidentsReadGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>> = ({ signal }) => platformSecurityIncidentsReadGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformSecurityIncidentsReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>>
export type PlatformSecurityIncidentsReadGetQueryError = unknown


export function usePlatformSecurityIncidentsReadGet<TData = Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformSecurityIncidentsReadGet<TData = Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformSecurityIncidentsReadGet<TData = Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformSecurityIncidentsReadGet<TData = Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformSecurityIncidentsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformSecurityIncidentsReadGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformSecurityIncidentsManagePostResponse201 = {
  data: void
  status: 201
}

export type platformSecurityIncidentsManagePostResponseSuccess = (platformSecurityIncidentsManagePostResponse201) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsManagePostResponse = (platformSecurityIncidentsManagePostResponseSuccess)

export const getPlatformSecurityIncidentsManagePostUrl = (id: string,) => {




  return `/api/platform/security-incidents/${id}/containment-commands`
}

export const platformSecurityIncidentsManagePost = async (id: string, options?: RequestInit): Promise<platformSecurityIncidentsManagePostResponse> => {

  return customMutator<platformSecurityIncidentsManagePostResponse>(getPlatformSecurityIncidentsManagePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSecurityIncidentsManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformSecurityIncidentsManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformSecurityIncidentsManagePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSecurityIncidentsManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost>>>

    export type PlatformSecurityIncidentsManagePostMutationError = unknown

    export const usePlatformSecurityIncidentsManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSecurityIncidentsManagePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformSecurityIncidentsManagePostMutationOptions(options), queryClient);
    }

export type platformSecurityIncidentsManagePost1Response201 = {
  data: void
  status: 201
}

export type platformSecurityIncidentsManagePost1ResponseSuccess = (platformSecurityIncidentsManagePost1Response201) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsManagePost1Response = (platformSecurityIncidentsManagePost1ResponseSuccess)

export const getPlatformSecurityIncidentsManagePost1Url = (id: string,) => {




  return `/api/platform/security-incidents/${id}/impacts`
}

export const platformSecurityIncidentsManagePost1 = async (id: string, options?: RequestInit): Promise<platformSecurityIncidentsManagePost1Response> => {

  return customMutator<platformSecurityIncidentsManagePost1Response>(getPlatformSecurityIncidentsManagePost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSecurityIncidentsManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformSecurityIncidentsManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformSecurityIncidentsManagePost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSecurityIncidentsManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost1>>>

    export type PlatformSecurityIncidentsManagePost1MutationError = unknown

    export const usePlatformSecurityIncidentsManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSecurityIncidentsManagePost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformSecurityIncidentsManagePost1MutationOptions(options), queryClient);
    }

export type platformSecurityIncidentsManagePost2Response201 = {
  data: void
  status: 201
}

export type platformSecurityIncidentsManagePost2ResponseSuccess = (platformSecurityIncidentsManagePost2Response201) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsManagePost2Response = (platformSecurityIncidentsManagePost2ResponseSuccess)

export const getPlatformSecurityIncidentsManagePost2Url = (id: string,) => {




  return `/api/platform/security-incidents/${id}/notifications`
}

export const platformSecurityIncidentsManagePost2 = async (id: string, options?: RequestInit): Promise<platformSecurityIncidentsManagePost2Response> => {

  return customMutator<platformSecurityIncidentsManagePost2Response>(getPlatformSecurityIncidentsManagePost2Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSecurityIncidentsManagePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost2>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformSecurityIncidentsManagePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost2>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformSecurityIncidentsManagePost2(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSecurityIncidentsManagePost2MutationResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost2>>>

    export type PlatformSecurityIncidentsManagePost2MutationError = unknown

    export const usePlatformSecurityIncidentsManagePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSecurityIncidentsManagePost2>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformSecurityIncidentsManagePost2MutationOptions(options), queryClient);
    }

export type platformSecurityIncidentsManagePost3Response201 = {
  data: void
  status: 201
}

export type platformSecurityIncidentsManagePost3ResponseSuccess = (platformSecurityIncidentsManagePost3Response201) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsManagePost3Response = (platformSecurityIncidentsManagePost3ResponseSuccess)

export const getPlatformSecurityIncidentsManagePost3Url = (id: string,) => {




  return `/api/platform/security-incidents/${id}/recoveries`
}

export const platformSecurityIncidentsManagePost3 = async (id: string, options?: RequestInit): Promise<platformSecurityIncidentsManagePost3Response> => {

  return customMutator<platformSecurityIncidentsManagePost3Response>(getPlatformSecurityIncidentsManagePost3Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSecurityIncidentsManagePost3MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost3>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost3>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformSecurityIncidentsManagePost3'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost3>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformSecurityIncidentsManagePost3(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSecurityIncidentsManagePost3MutationResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost3>>>

    export type PlatformSecurityIncidentsManagePost3MutationError = unknown

    export const usePlatformSecurityIncidentsManagePost3 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost3>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSecurityIncidentsManagePost3>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformSecurityIncidentsManagePost3MutationOptions(options), queryClient);
    }

export type platformSecurityIncidentsManagePost4Response201 = {
  data: void
  status: 201
}

export type platformSecurityIncidentsManagePost4ResponseSuccess = (platformSecurityIncidentsManagePost4Response201) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsManagePost4Response = (platformSecurityIncidentsManagePost4ResponseSuccess)

export const getPlatformSecurityIncidentsManagePost4Url = (id: string,) => {




  return `/api/platform/security-incidents/${id}/false-positive`
}

export const platformSecurityIncidentsManagePost4 = async (id: string, options?: RequestInit): Promise<platformSecurityIncidentsManagePost4Response> => {

  return customMutator<platformSecurityIncidentsManagePost4Response>(getPlatformSecurityIncidentsManagePost4Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSecurityIncidentsManagePost4MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost4>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost4>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformSecurityIncidentsManagePost4'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost4>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformSecurityIncidentsManagePost4(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSecurityIncidentsManagePost4MutationResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost4>>>

    export type PlatformSecurityIncidentsManagePost4MutationError = unknown

    export const usePlatformSecurityIncidentsManagePost4 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost4>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSecurityIncidentsManagePost4>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformSecurityIncidentsManagePost4MutationOptions(options), queryClient);
    }

export type platformSecurityIncidentsManagePost5Response201 = {
  data: void
  status: 201
}

export type platformSecurityIncidentsManagePost5ResponseSuccess = (platformSecurityIncidentsManagePost5Response201) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsManagePost5Response = (platformSecurityIncidentsManagePost5ResponseSuccess)

export const getPlatformSecurityIncidentsManagePost5Url = (id: string,) => {




  return `/api/platform/security-incidents/${id}/postmortem-actions`
}

export const platformSecurityIncidentsManagePost5 = async (id: string, options?: RequestInit): Promise<platformSecurityIncidentsManagePost5Response> => {

  return customMutator<platformSecurityIncidentsManagePost5Response>(getPlatformSecurityIncidentsManagePost5Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSecurityIncidentsManagePost5MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost5>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost5>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformSecurityIncidentsManagePost5'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost5>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformSecurityIncidentsManagePost5(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSecurityIncidentsManagePost5MutationResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost5>>>

    export type PlatformSecurityIncidentsManagePost5MutationError = unknown

    export const usePlatformSecurityIncidentsManagePost5 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost5>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSecurityIncidentsManagePost5>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformSecurityIncidentsManagePost5MutationOptions(options), queryClient);
    }

export type platformSecurityIncidentsManagePost6Response201 = {
  data: void
  status: 201
}

export type platformSecurityIncidentsManagePost6ResponseSuccess = (platformSecurityIncidentsManagePost6Response201) & {
  headers: Headers;
};
;

export type platformSecurityIncidentsManagePost6Response = (platformSecurityIncidentsManagePost6ResponseSuccess)

export const getPlatformSecurityIncidentsManagePost6Url = (id: string,) => {




  return `/api/platform/security-incidents/${id}/close`
}

export const platformSecurityIncidentsManagePost6 = async (id: string, options?: RequestInit): Promise<platformSecurityIncidentsManagePost6Response> => {

  return customMutator<platformSecurityIncidentsManagePost6Response>(getPlatformSecurityIncidentsManagePost6Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformSecurityIncidentsManagePost6MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost6>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost6>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformSecurityIncidentsManagePost6'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost6>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformSecurityIncidentsManagePost6(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformSecurityIncidentsManagePost6MutationResult = NonNullable<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost6>>>

    export type PlatformSecurityIncidentsManagePost6MutationError = unknown

    export const usePlatformSecurityIncidentsManagePost6 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformSecurityIncidentsManagePost6>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformSecurityIncidentsManagePost6>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformSecurityIncidentsManagePost6MutationOptions(options), queryClient);
    }

export type platformCryptographicKeysReadResponse200 = {
  data: void
  status: 200
}

export type platformCryptographicKeysReadResponseSuccess = (platformCryptographicKeysReadResponse200) & {
  headers: Headers;
};
;

export type platformCryptographicKeysReadResponse = (platformCryptographicKeysReadResponseSuccess)

export const getPlatformCryptographicKeysReadUrl = () => {




  return `/api/platform/cryptographic-keys`
}

export const platformCryptographicKeysRead = async ( options?: RequestInit): Promise<platformCryptographicKeysReadResponse> => {

  return customMutator<platformCryptographicKeysReadResponse>(getPlatformCryptographicKeysReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformCryptographicKeysReadQueryKey = () => {
    return [
    `/api/platform/cryptographic-keys`
    ] as const;
    }


export const getPlatformCryptographicKeysReadQueryOptions = <TData = Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformCryptographicKeysReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformCryptographicKeysRead>>> = ({ signal }) => platformCryptographicKeysRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformCryptographicKeysReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformCryptographicKeysRead>>>
export type PlatformCryptographicKeysReadQueryError = unknown


export function usePlatformCryptographicKeysRead<TData = Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformCryptographicKeysRead>>,
          TError,
          Awaited<ReturnType<typeof platformCryptographicKeysRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformCryptographicKeysRead<TData = Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformCryptographicKeysRead>>,
          TError,
          Awaited<ReturnType<typeof platformCryptographicKeysRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformCryptographicKeysRead<TData = Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformCryptographicKeysRead<TData = Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformCryptographicKeysRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformCryptographicKeysReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformCryptographicKeysManageResponse201 = {
  data: void
  status: 201
}

export type platformCryptographicKeysManageResponseSuccess = (platformCryptographicKeysManageResponse201) & {
  headers: Headers;
};
;

export type platformCryptographicKeysManageResponse = (platformCryptographicKeysManageResponseSuccess)

export const getPlatformCryptographicKeysManageUrl = () => {




  return `/api/platform/cryptographic-keys/rotate`
}

export const platformCryptographicKeysManage = async ( options?: RequestInit): Promise<platformCryptographicKeysManageResponse> => {

  return customMutator<platformCryptographicKeysManageResponse>(getPlatformCryptographicKeysManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformCryptographicKeysManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManage>>, TError,void, TContext> => {

const mutationKey = ['platformCryptographicKeysManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformCryptographicKeysManage>>, void> = () => {


          return  platformCryptographicKeysManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformCryptographicKeysManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformCryptographicKeysManage>>>

    export type PlatformCryptographicKeysManageMutationError = unknown

    export const usePlatformCryptographicKeysManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformCryptographicKeysManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformCryptographicKeysManageMutationOptions(options), queryClient);
    }

export type platformCryptographicKeysManagePostResponse201 = {
  data: void
  status: 201
}

export type platformCryptographicKeysManagePostResponseSuccess = (platformCryptographicKeysManagePostResponse201) & {
  headers: Headers;
};
;

export type platformCryptographicKeysManagePostResponse = (platformCryptographicKeysManagePostResponseSuccess)

export const getPlatformCryptographicKeysManagePostUrl = (id: string,) => {




  return `/api/platform/cryptographic-keys/runs/${id}/progress`
}

export const platformCryptographicKeysManagePost = async (id: string, options?: RequestInit): Promise<platformCryptographicKeysManagePostResponse> => {

  return customMutator<platformCryptographicKeysManagePostResponse>(getPlatformCryptographicKeysManagePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformCryptographicKeysManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManagePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformCryptographicKeysManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformCryptographicKeysManagePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformCryptographicKeysManagePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformCryptographicKeysManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformCryptographicKeysManagePost>>>

    export type PlatformCryptographicKeysManagePostMutationError = unknown

    export const usePlatformCryptographicKeysManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformCryptographicKeysManagePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformCryptographicKeysManagePostMutationOptions(options), queryClient);
    }

export type platformCryptographicKeysManagePost1Response201 = {
  data: void
  status: 201
}

export type platformCryptographicKeysManagePost1ResponseSuccess = (platformCryptographicKeysManagePost1Response201) & {
  headers: Headers;
};
;

export type platformCryptographicKeysManagePost1Response = (platformCryptographicKeysManagePost1ResponseSuccess)

export const getPlatformCryptographicKeysManagePost1Url = (id: string,) => {




  return `/api/platform/cryptographic-keys/runs/${id}/resume`
}

export const platformCryptographicKeysManagePost1 = async (id: string, options?: RequestInit): Promise<platformCryptographicKeysManagePost1Response> => {

  return customMutator<platformCryptographicKeysManagePost1Response>(getPlatformCryptographicKeysManagePost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformCryptographicKeysManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManagePost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformCryptographicKeysManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformCryptographicKeysManagePost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformCryptographicKeysManagePost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformCryptographicKeysManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformCryptographicKeysManagePost1>>>

    export type PlatformCryptographicKeysManagePost1MutationError = unknown

    export const usePlatformCryptographicKeysManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformCryptographicKeysManagePost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformCryptographicKeysManagePost1MutationOptions(options), queryClient);
    }

export type platformCryptographicKeysManagePost2Response201 = {
  data: void
  status: 201
}

export type platformCryptographicKeysManagePost2ResponseSuccess = (platformCryptographicKeysManagePost2Response201) & {
  headers: Headers;
};
;

export type platformCryptographicKeysManagePost2Response = (platformCryptographicKeysManagePost2ResponseSuccess)

export const getPlatformCryptographicKeysManagePost2Url = (id: string,) => {




  return `/api/platform/cryptographic-keys/${id}/revoke`
}

export const platformCryptographicKeysManagePost2 = async (id: string, options?: RequestInit): Promise<platformCryptographicKeysManagePost2Response> => {

  return customMutator<platformCryptographicKeysManagePost2Response>(getPlatformCryptographicKeysManagePost2Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformCryptographicKeysManagePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManagePost2>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformCryptographicKeysManagePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformCryptographicKeysManagePost2>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformCryptographicKeysManagePost2(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformCryptographicKeysManagePost2MutationResult = NonNullable<Awaited<ReturnType<typeof platformCryptographicKeysManagePost2>>>

    export type PlatformCryptographicKeysManagePost2MutationError = unknown

    export const usePlatformCryptographicKeysManagePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformCryptographicKeysManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformCryptographicKeysManagePost2>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformCryptographicKeysManagePost2MutationOptions(options), queryClient);
    }

export type platformScheduledControlsReadResponse200 = {
  data: void
  status: 200
}

export type platformScheduledControlsReadResponseSuccess = (platformScheduledControlsReadResponse200) & {
  headers: Headers;
};
;

export type platformScheduledControlsReadResponse = (platformScheduledControlsReadResponseSuccess)

export const getPlatformScheduledControlsReadUrl = () => {




  return `/api/platform/scheduled-controls`
}

export const platformScheduledControlsRead = async ( options?: RequestInit): Promise<platformScheduledControlsReadResponse> => {

  return customMutator<platformScheduledControlsReadResponse>(getPlatformScheduledControlsReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformScheduledControlsReadQueryKey = () => {
    return [
    `/api/platform/scheduled-controls`
    ] as const;
    }


export const getPlatformScheduledControlsReadQueryOptions = <TData = Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformScheduledControlsReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformScheduledControlsRead>>> = ({ signal }) => platformScheduledControlsRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformScheduledControlsReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformScheduledControlsRead>>>
export type PlatformScheduledControlsReadQueryError = unknown


export function usePlatformScheduledControlsRead<TData = Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformScheduledControlsRead>>,
          TError,
          Awaited<ReturnType<typeof platformScheduledControlsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformScheduledControlsRead<TData = Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformScheduledControlsRead>>,
          TError,
          Awaited<ReturnType<typeof platformScheduledControlsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformScheduledControlsRead<TData = Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformScheduledControlsRead<TData = Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformScheduledControlsReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformScheduledControlsReadGetResponse200 = {
  data: void
  status: 200
}

export type platformScheduledControlsReadGetResponseSuccess = (platformScheduledControlsReadGetResponse200) & {
  headers: Headers;
};
;

export type platformScheduledControlsReadGetResponse = (platformScheduledControlsReadGetResponseSuccess)

export const getPlatformScheduledControlsReadGetUrl = (controlKey: string,) => {




  return `/api/platform/scheduled-controls/${controlKey}`
}

export const platformScheduledControlsReadGet = async (controlKey: string, options?: RequestInit): Promise<platformScheduledControlsReadGetResponse> => {

  return customMutator<platformScheduledControlsReadGetResponse>(getPlatformScheduledControlsReadGetUrl(controlKey),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformScheduledControlsReadGetQueryKey = (controlKey: string,) => {
    return [
    `/api/platform/scheduled-controls/${controlKey}`
    ] as const;
    }


export const getPlatformScheduledControlsReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError = unknown>(controlKey: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformScheduledControlsReadGetQueryKey(controlKey);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformScheduledControlsReadGet>>> = ({ signal }) => platformScheduledControlsReadGet(controlKey, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: controlKey !== null && controlKey !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformScheduledControlsReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformScheduledControlsReadGet>>>
export type PlatformScheduledControlsReadGetQueryError = unknown


export function usePlatformScheduledControlsReadGet<TData = Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError = unknown>(
 controlKey: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformScheduledControlsReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformScheduledControlsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformScheduledControlsReadGet<TData = Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError = unknown>(
 controlKey: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformScheduledControlsReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformScheduledControlsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformScheduledControlsReadGet<TData = Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError = unknown>(
 controlKey: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformScheduledControlsReadGet<TData = Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError = unknown>(
 controlKey: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformScheduledControlsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformScheduledControlsReadGetQueryOptions(controlKey,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformScheduledControlsManageResponse201 = {
  data: void
  status: 201
}

export type platformScheduledControlsManageResponseSuccess = (platformScheduledControlsManageResponse201) & {
  headers: Headers;
};
;

export type platformScheduledControlsManageResponse = (platformScheduledControlsManageResponseSuccess)

export const getPlatformScheduledControlsManageUrl = () => {




  return `/api/platform/scheduled-controls/definitions`
}

export const platformScheduledControlsManage = async ( options?: RequestInit): Promise<platformScheduledControlsManageResponse> => {

  return customMutator<platformScheduledControlsManageResponse>(getPlatformScheduledControlsManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformScheduledControlsManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsManage>>, TError,void, TContext> => {

const mutationKey = ['platformScheduledControlsManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformScheduledControlsManage>>, void> = () => {


          return  platformScheduledControlsManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformScheduledControlsManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformScheduledControlsManage>>>

    export type PlatformScheduledControlsManageMutationError = unknown

    export const usePlatformScheduledControlsManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformScheduledControlsManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformScheduledControlsManageMutationOptions(options), queryClient);
    }

export type platformScheduledControlsManagePostResponse201 = {
  data: void
  status: 201
}

export type platformScheduledControlsManagePostResponseSuccess = (platformScheduledControlsManagePostResponse201) & {
  headers: Headers;
};
;

export type platformScheduledControlsManagePostResponse = (platformScheduledControlsManagePostResponseSuccess)

export const getPlatformScheduledControlsManagePostUrl = (controlKey: string,) => {




  return `/api/platform/scheduled-controls/${controlKey}/approvals`
}

export const platformScheduledControlsManagePost = async (controlKey: string, options?: RequestInit): Promise<platformScheduledControlsManagePostResponse> => {

  return customMutator<platformScheduledControlsManagePostResponse>(getPlatformScheduledControlsManagePostUrl(controlKey),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformScheduledControlsManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsManagePost>>, TError,{controlKey: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsManagePost>>, TError,{controlKey: string}, TContext> => {

const mutationKey = ['platformScheduledControlsManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformScheduledControlsManagePost>>, {controlKey: string}> = (props) => {
          const {controlKey} = props ?? {};

          return  platformScheduledControlsManagePost(controlKey,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformScheduledControlsManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformScheduledControlsManagePost>>>

    export type PlatformScheduledControlsManagePostMutationError = unknown

    export const usePlatformScheduledControlsManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsManagePost>>, TError,{controlKey: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformScheduledControlsManagePost>>,
        TError,
        {controlKey: string},
        TContext
      > => {
      return useMutation(getPlatformScheduledControlsManagePostMutationOptions(options), queryClient);
    }

export type platformScheduledControlsManagePost1Response201 = {
  data: void
  status: 201
}

export type platformScheduledControlsManagePost1ResponseSuccess = (platformScheduledControlsManagePost1Response201) & {
  headers: Headers;
};
;

export type platformScheduledControlsManagePost1Response = (platformScheduledControlsManagePost1ResponseSuccess)

export const getPlatformScheduledControlsManagePost1Url = (controlKey: string,
    businessDate: string,) => {




  return `/api/platform/scheduled-controls/${controlKey}/runs/${businessDate}/execute`
}

export const platformScheduledControlsManagePost1 = async (controlKey: string,
    businessDate: string, options?: RequestInit): Promise<platformScheduledControlsManagePost1Response> => {

  return customMutator<platformScheduledControlsManagePost1Response>(getPlatformScheduledControlsManagePost1Url(controlKey,businessDate),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformScheduledControlsManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsManagePost1>>, TError,{controlKey: string;businessDate: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsManagePost1>>, TError,{controlKey: string;businessDate: string}, TContext> => {

const mutationKey = ['platformScheduledControlsManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformScheduledControlsManagePost1>>, {controlKey: string;businessDate: string}> = (props) => {
          const {controlKey,businessDate} = props ?? {};

          return  platformScheduledControlsManagePost1(controlKey,businessDate,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformScheduledControlsManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformScheduledControlsManagePost1>>>

    export type PlatformScheduledControlsManagePost1MutationError = unknown

    export const usePlatformScheduledControlsManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsManagePost1>>, TError,{controlKey: string;businessDate: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformScheduledControlsManagePost1>>,
        TError,
        {controlKey: string;businessDate: string},
        TContext
      > => {
      return useMutation(getPlatformScheduledControlsManagePost1MutationOptions(options), queryClient);
    }

export type platformScheduledControlsApproveResponse201 = {
  data: void
  status: 201
}

export type platformScheduledControlsApproveResponseSuccess = (platformScheduledControlsApproveResponse201) & {
  headers: Headers;
};
;

export type platformScheduledControlsApproveResponse = (platformScheduledControlsApproveResponseSuccess)

export const getPlatformScheduledControlsApproveUrl = (approvalId: string,) => {




  return `/api/platform/scheduled-controls/approvals/${approvalId}/approve`
}

export const platformScheduledControlsApprove = async (approvalId: string, options?: RequestInit): Promise<platformScheduledControlsApproveResponse> => {

  return customMutator<platformScheduledControlsApproveResponse>(getPlatformScheduledControlsApproveUrl(approvalId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformScheduledControlsApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsApprove>>, TError,{approvalId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsApprove>>, TError,{approvalId: string}, TContext> => {

const mutationKey = ['platformScheduledControlsApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformScheduledControlsApprove>>, {approvalId: string}> = (props) => {
          const {approvalId} = props ?? {};

          return  platformScheduledControlsApprove(approvalId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformScheduledControlsApproveMutationResult = NonNullable<Awaited<ReturnType<typeof platformScheduledControlsApprove>>>

    export type PlatformScheduledControlsApproveMutationError = unknown

    export const usePlatformScheduledControlsApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsApprove>>, TError,{approvalId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformScheduledControlsApprove>>,
        TError,
        {approvalId: string},
        TContext
      > => {
      return useMutation(getPlatformScheduledControlsApproveMutationOptions(options), queryClient);
    }

export type platformScheduledControlsApprovePostResponse201 = {
  data: void
  status: 201
}

export type platformScheduledControlsApprovePostResponseSuccess = (platformScheduledControlsApprovePostResponse201) & {
  headers: Headers;
};
;

export type platformScheduledControlsApprovePostResponse = (platformScheduledControlsApprovePostResponseSuccess)

export const getPlatformScheduledControlsApprovePostUrl = (approvalId: string,) => {




  return `/api/platform/scheduled-controls/approvals/${approvalId}/reject`
}

export const platformScheduledControlsApprovePost = async (approvalId: string, options?: RequestInit): Promise<platformScheduledControlsApprovePostResponse> => {

  return customMutator<platformScheduledControlsApprovePostResponse>(getPlatformScheduledControlsApprovePostUrl(approvalId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformScheduledControlsApprovePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsApprovePost>>, TError,{approvalId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsApprovePost>>, TError,{approvalId: string}, TContext> => {

const mutationKey = ['platformScheduledControlsApprovePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformScheduledControlsApprovePost>>, {approvalId: string}> = (props) => {
          const {approvalId} = props ?? {};

          return  platformScheduledControlsApprovePost(approvalId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformScheduledControlsApprovePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformScheduledControlsApprovePost>>>

    export type PlatformScheduledControlsApprovePostMutationError = unknown

    export const usePlatformScheduledControlsApprovePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformScheduledControlsApprovePost>>, TError,{approvalId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformScheduledControlsApprovePost>>,
        TError,
        {approvalId: string},
        TContext
      > => {
      return useMutation(getPlatformScheduledControlsApprovePostMutationOptions(options), queryClient);
    }

export type platformMarketDataQualityReadResponse200 = {
  data: void
  status: 200
}

export type platformMarketDataQualityReadResponseSuccess = (platformMarketDataQualityReadResponse200) & {
  headers: Headers;
};
;

export type platformMarketDataQualityReadResponse = (platformMarketDataQualityReadResponseSuccess)

export const getPlatformMarketDataQualityReadUrl = () => {




  return `/api/platform/market-data-quality`
}

export const platformMarketDataQualityRead = async ( options?: RequestInit): Promise<platformMarketDataQualityReadResponse> => {

  return customMutator<platformMarketDataQualityReadResponse>(getPlatformMarketDataQualityReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformMarketDataQualityReadQueryKey = () => {
    return [
    `/api/platform/market-data-quality`
    ] as const;
    }


export const getPlatformMarketDataQualityReadQueryOptions = <TData = Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformMarketDataQualityReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformMarketDataQualityRead>>> = ({ signal }) => platformMarketDataQualityRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformMarketDataQualityReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformMarketDataQualityRead>>>
export type PlatformMarketDataQualityReadQueryError = unknown


export function usePlatformMarketDataQualityRead<TData = Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformMarketDataQualityRead>>,
          TError,
          Awaited<ReturnType<typeof platformMarketDataQualityRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformMarketDataQualityRead<TData = Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformMarketDataQualityRead>>,
          TError,
          Awaited<ReturnType<typeof platformMarketDataQualityRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformMarketDataQualityRead<TData = Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformMarketDataQualityRead<TData = Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformMarketDataQualityReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformMarketDataQualityManageResponse201 = {
  data: void
  status: 201
}

export type platformMarketDataQualityManageResponseSuccess = (platformMarketDataQualityManageResponse201) & {
  headers: Headers;
};
;

export type platformMarketDataQualityManageResponse = (platformMarketDataQualityManageResponseSuccess)

export const getPlatformMarketDataQualityManageUrl = () => {




  return `/api/platform/market-data-quality`
}

export const platformMarketDataQualityManage = async ( options?: RequestInit): Promise<platformMarketDataQualityManageResponse> => {

  return customMutator<platformMarketDataQualityManageResponse>(getPlatformMarketDataQualityManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformMarketDataQualityManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityManage>>, TError,void, TContext> => {

const mutationKey = ['platformMarketDataQualityManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformMarketDataQualityManage>>, void> = () => {


          return  platformMarketDataQualityManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformMarketDataQualityManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformMarketDataQualityManage>>>

    export type PlatformMarketDataQualityManageMutationError = unknown

    export const usePlatformMarketDataQualityManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformMarketDataQualityManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformMarketDataQualityManageMutationOptions(options), queryClient);
    }

export type platformMarketDataQualityReadGetResponse200 = {
  data: void
  status: 200
}

export type platformMarketDataQualityReadGetResponseSuccess = (platformMarketDataQualityReadGetResponse200) & {
  headers: Headers;
};
;

export type platformMarketDataQualityReadGetResponse = (platformMarketDataQualityReadGetResponseSuccess)

export const getPlatformMarketDataQualityReadGetUrl = (id: string,) => {




  return `/api/platform/market-data-quality/${id}`
}

export const platformMarketDataQualityReadGet = async (id: string, options?: RequestInit): Promise<platformMarketDataQualityReadGetResponse> => {

  return customMutator<platformMarketDataQualityReadGetResponse>(getPlatformMarketDataQualityReadGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformMarketDataQualityReadGetQueryKey = (id: string,) => {
    return [
    `/api/platform/market-data-quality/${id}`
    ] as const;
    }


export const getPlatformMarketDataQualityReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformMarketDataQualityReadGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>> = ({ signal }) => platformMarketDataQualityReadGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformMarketDataQualityReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>>
export type PlatformMarketDataQualityReadGetQueryError = unknown


export function usePlatformMarketDataQualityReadGet<TData = Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformMarketDataQualityReadGet<TData = Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformMarketDataQualityReadGet<TData = Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformMarketDataQualityReadGet<TData = Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMarketDataQualityReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformMarketDataQualityReadGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformMarketDataQualityManagePostResponse201 = {
  data: void
  status: 201
}

export type platformMarketDataQualityManagePostResponseSuccess = (platformMarketDataQualityManagePostResponse201) & {
  headers: Headers;
};
;

export type platformMarketDataQualityManagePostResponse = (platformMarketDataQualityManagePostResponseSuccess)

export const getPlatformMarketDataQualityManagePostUrl = (id: string,) => {




  return `/api/platform/market-data-quality/${id}/investigate`
}

export const platformMarketDataQualityManagePost = async (id: string, options?: RequestInit): Promise<platformMarketDataQualityManagePostResponse> => {

  return customMutator<platformMarketDataQualityManagePostResponse>(getPlatformMarketDataQualityManagePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformMarketDataQualityManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityManagePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformMarketDataQualityManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformMarketDataQualityManagePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformMarketDataQualityManagePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformMarketDataQualityManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformMarketDataQualityManagePost>>>

    export type PlatformMarketDataQualityManagePostMutationError = unknown

    export const usePlatformMarketDataQualityManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformMarketDataQualityManagePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformMarketDataQualityManagePostMutationOptions(options), queryClient);
    }

export type platformMarketDataQualityManagePost1Response201 = {
  data: void
  status: 201
}

export type platformMarketDataQualityManagePost1ResponseSuccess = (platformMarketDataQualityManagePost1Response201) & {
  headers: Headers;
};
;

export type platformMarketDataQualityManagePost1Response = (platformMarketDataQualityManagePost1ResponseSuccess)

export const getPlatformMarketDataQualityManagePost1Url = (id: string,) => {




  return `/api/platform/market-data-quality/${id}/correct`
}

export const platformMarketDataQualityManagePost1 = async (id: string, options?: RequestInit): Promise<platformMarketDataQualityManagePost1Response> => {

  return customMutator<platformMarketDataQualityManagePost1Response>(getPlatformMarketDataQualityManagePost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformMarketDataQualityManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityManagePost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformMarketDataQualityManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformMarketDataQualityManagePost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformMarketDataQualityManagePost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformMarketDataQualityManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformMarketDataQualityManagePost1>>>

    export type PlatformMarketDataQualityManagePost1MutationError = unknown

    export const usePlatformMarketDataQualityManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformMarketDataQualityManagePost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformMarketDataQualityManagePost1MutationOptions(options), queryClient);
    }

export type platformMarketDataQualityApproveResponse201 = {
  data: void
  status: 201
}

export type platformMarketDataQualityApproveResponseSuccess = (platformMarketDataQualityApproveResponse201) & {
  headers: Headers;
};
;

export type platformMarketDataQualityApproveResponse = (platformMarketDataQualityApproveResponseSuccess)

export const getPlatformMarketDataQualityApproveUrl = (id: string,) => {




  return `/api/platform/market-data-quality/${id}/approve`
}

export const platformMarketDataQualityApprove = async (id: string, options?: RequestInit): Promise<platformMarketDataQualityApproveResponse> => {

  return customMutator<platformMarketDataQualityApproveResponse>(getPlatformMarketDataQualityApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformMarketDataQualityApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformMarketDataQualityApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformMarketDataQualityApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformMarketDataQualityApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformMarketDataQualityApproveMutationResult = NonNullable<Awaited<ReturnType<typeof platformMarketDataQualityApprove>>>

    export type PlatformMarketDataQualityApproveMutationError = unknown

    export const usePlatformMarketDataQualityApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMarketDataQualityApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformMarketDataQualityApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformMarketDataQualityApproveMutationOptions(options), queryClient);
    }

export type platformProviderIngestionReadResponse200 = {
  data: void
  status: 200
}

export type platformProviderIngestionReadResponseSuccess = (platformProviderIngestionReadResponse200) & {
  headers: Headers;
};
;

export type platformProviderIngestionReadResponse = (platformProviderIngestionReadResponseSuccess)

export const getPlatformProviderIngestionReadUrl = () => {




  return `/api/platform/provider-ingestion/providers`
}

export const platformProviderIngestionRead = async ( options?: RequestInit): Promise<platformProviderIngestionReadResponse> => {

  return customMutator<platformProviderIngestionReadResponse>(getPlatformProviderIngestionReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformProviderIngestionReadQueryKey = () => {
    return [
    `/api/platform/provider-ingestion/providers`
    ] as const;
    }


export const getPlatformProviderIngestionReadQueryOptions = <TData = Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformProviderIngestionReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformProviderIngestionRead>>> = ({ signal }) => platformProviderIngestionRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformProviderIngestionReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformProviderIngestionRead>>>
export type PlatformProviderIngestionReadQueryError = unknown


export function usePlatformProviderIngestionRead<TData = Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformProviderIngestionRead>>,
          TError,
          Awaited<ReturnType<typeof platformProviderIngestionRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformProviderIngestionRead<TData = Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformProviderIngestionRead>>,
          TError,
          Awaited<ReturnType<typeof platformProviderIngestionRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformProviderIngestionRead<TData = Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformProviderIngestionRead<TData = Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformProviderIngestionReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformProviderIngestionManageResponse201 = {
  data: void
  status: 201
}

export type platformProviderIngestionManageResponseSuccess = (platformProviderIngestionManageResponse201) & {
  headers: Headers;
};
;

export type platformProviderIngestionManageResponse = (platformProviderIngestionManageResponseSuccess)

export const getPlatformProviderIngestionManageUrl = () => {




  return `/api/platform/provider-ingestion/providers`
}

export const platformProviderIngestionManage = async ( options?: RequestInit): Promise<platformProviderIngestionManageResponse> => {

  return customMutator<platformProviderIngestionManageResponse>(getPlatformProviderIngestionManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformProviderIngestionManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManage>>, TError,void, TContext> => {

const mutationKey = ['platformProviderIngestionManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformProviderIngestionManage>>, void> = () => {


          return  platformProviderIngestionManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformProviderIngestionManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformProviderIngestionManage>>>

    export type PlatformProviderIngestionManageMutationError = unknown

    export const usePlatformProviderIngestionManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformProviderIngestionManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformProviderIngestionManageMutationOptions(options), queryClient);
    }

export type platformProviderIngestionReadGetResponse200 = {
  data: void
  status: 200
}

export type platformProviderIngestionReadGetResponseSuccess = (platformProviderIngestionReadGetResponse200) & {
  headers: Headers;
};
;

export type platformProviderIngestionReadGetResponse = (platformProviderIngestionReadGetResponseSuccess)

export const getPlatformProviderIngestionReadGetUrl = () => {




  return `/api/platform/provider-ingestion/checkpoints`
}

export const platformProviderIngestionReadGet = async ( options?: RequestInit): Promise<platformProviderIngestionReadGetResponse> => {

  return customMutator<platformProviderIngestionReadGetResponse>(getPlatformProviderIngestionReadGetUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformProviderIngestionReadGetQueryKey = () => {
    return [
    `/api/platform/provider-ingestion/checkpoints`
    ] as const;
    }


export const getPlatformProviderIngestionReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformProviderIngestionReadGetQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformProviderIngestionReadGet>>> = ({ signal }) => platformProviderIngestionReadGet({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformProviderIngestionReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformProviderIngestionReadGet>>>
export type PlatformProviderIngestionReadGetQueryError = unknown


export function usePlatformProviderIngestionReadGet<TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformProviderIngestionReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformProviderIngestionReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformProviderIngestionReadGet<TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformProviderIngestionReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformProviderIngestionReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformProviderIngestionReadGet<TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformProviderIngestionReadGet<TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformProviderIngestionReadGetQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformProviderIngestionReadGet1Response200 = {
  data: void
  status: 200
}

export type platformProviderIngestionReadGet1ResponseSuccess = (platformProviderIngestionReadGet1Response200) & {
  headers: Headers;
};
;

export type platformProviderIngestionReadGet1Response = (platformProviderIngestionReadGet1ResponseSuccess)

export const getPlatformProviderIngestionReadGet1Url = () => {




  return `/api/platform/provider-ingestion/gaps`
}

export const platformProviderIngestionReadGet1 = async ( options?: RequestInit): Promise<platformProviderIngestionReadGet1Response> => {

  return customMutator<platformProviderIngestionReadGet1Response>(getPlatformProviderIngestionReadGet1Url(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformProviderIngestionReadGet1QueryKey = () => {
    return [
    `/api/platform/provider-ingestion/gaps`
    ] as const;
    }


export const getPlatformProviderIngestionReadGet1QueryOptions = <TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformProviderIngestionReadGet1QueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>> = ({ signal }) => platformProviderIngestionReadGet1({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformProviderIngestionReadGet1QueryResult = NonNullable<Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>>
export type PlatformProviderIngestionReadGet1QueryError = unknown


export function usePlatformProviderIngestionReadGet1<TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>,
          TError,
          Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformProviderIngestionReadGet1<TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>,
          TError,
          Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformProviderIngestionReadGet1<TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformProviderIngestionReadGet1<TData = Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformProviderIngestionReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformProviderIngestionReadGet1QueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformProviderIngestionManagePostResponse201 = {
  data: void
  status: 201
}

export type platformProviderIngestionManagePostResponseSuccess = (platformProviderIngestionManagePostResponse201) & {
  headers: Headers;
};
;

export type platformProviderIngestionManagePostResponse = (platformProviderIngestionManagePostResponseSuccess)

export const getPlatformProviderIngestionManagePostUrl = () => {




  return `/api/platform/provider-ingestion/events`
}

export const platformProviderIngestionManagePost = async ( options?: RequestInit): Promise<platformProviderIngestionManagePostResponse> => {

  return customMutator<platformProviderIngestionManagePostResponse>(getPlatformProviderIngestionManagePostUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformProviderIngestionManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost>>, TError,void, TContext> => {

const mutationKey = ['platformProviderIngestionManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformProviderIngestionManagePost>>, void> = () => {


          return  platformProviderIngestionManagePost(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformProviderIngestionManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformProviderIngestionManagePost>>>

    export type PlatformProviderIngestionManagePostMutationError = unknown

    export const usePlatformProviderIngestionManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformProviderIngestionManagePost>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformProviderIngestionManagePostMutationOptions(options), queryClient);
    }

export type platformProviderIngestionManagePost1Response201 = {
  data: void
  status: 201
}

export type platformProviderIngestionManagePost1ResponseSuccess = (platformProviderIngestionManagePost1Response201) & {
  headers: Headers;
};
;

export type platformProviderIngestionManagePost1Response = (platformProviderIngestionManagePost1ResponseSuccess)

export const getPlatformProviderIngestionManagePost1Url = (providerCode: string,) => {




  return `/api/platform/provider-ingestion/failover/${providerCode}`
}

export const platformProviderIngestionManagePost1 = async (providerCode: string, options?: RequestInit): Promise<platformProviderIngestionManagePost1Response> => {

  return customMutator<platformProviderIngestionManagePost1Response>(getPlatformProviderIngestionManagePost1Url(providerCode),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformProviderIngestionManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost1>>, TError,{providerCode: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost1>>, TError,{providerCode: string}, TContext> => {

const mutationKey = ['platformProviderIngestionManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformProviderIngestionManagePost1>>, {providerCode: string}> = (props) => {
          const {providerCode} = props ?? {};

          return  platformProviderIngestionManagePost1(providerCode,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformProviderIngestionManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformProviderIngestionManagePost1>>>

    export type PlatformProviderIngestionManagePost1MutationError = unknown

    export const usePlatformProviderIngestionManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost1>>, TError,{providerCode: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformProviderIngestionManagePost1>>,
        TError,
        {providerCode: string},
        TContext
      > => {
      return useMutation(getPlatformProviderIngestionManagePost1MutationOptions(options), queryClient);
    }

export type platformProviderIngestionManagePost2Response201 = {
  data: void
  status: 201
}

export type platformProviderIngestionManagePost2ResponseSuccess = (platformProviderIngestionManagePost2Response201) & {
  headers: Headers;
};
;

export type platformProviderIngestionManagePost2Response = (platformProviderIngestionManagePost2ResponseSuccess)

export const getPlatformProviderIngestionManagePost2Url = (providerCode: string,) => {




  return `/api/platform/provider-ingestion/recover/${providerCode}`
}

export const platformProviderIngestionManagePost2 = async (providerCode: string, options?: RequestInit): Promise<platformProviderIngestionManagePost2Response> => {

  return customMutator<platformProviderIngestionManagePost2Response>(getPlatformProviderIngestionManagePost2Url(providerCode),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformProviderIngestionManagePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost2>>, TError,{providerCode: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost2>>, TError,{providerCode: string}, TContext> => {

const mutationKey = ['platformProviderIngestionManagePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformProviderIngestionManagePost2>>, {providerCode: string}> = (props) => {
          const {providerCode} = props ?? {};

          return  platformProviderIngestionManagePost2(providerCode,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformProviderIngestionManagePost2MutationResult = NonNullable<Awaited<ReturnType<typeof platformProviderIngestionManagePost2>>>

    export type PlatformProviderIngestionManagePost2MutationError = unknown

    export const usePlatformProviderIngestionManagePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost2>>, TError,{providerCode: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformProviderIngestionManagePost2>>,
        TError,
        {providerCode: string},
        TContext
      > => {
      return useMutation(getPlatformProviderIngestionManagePost2MutationOptions(options), queryClient);
    }

export type platformProviderIngestionManagePost3Response201 = {
  data: void
  status: 201
}

export type platformProviderIngestionManagePost3ResponseSuccess = (platformProviderIngestionManagePost3Response201) & {
  headers: Headers;
};
;

export type platformProviderIngestionManagePost3Response = (platformProviderIngestionManagePost3ResponseSuccess)

export const getPlatformProviderIngestionManagePost3Url = () => {




  return `/api/platform/provider-ingestion/replays`
}

export const platformProviderIngestionManagePost3 = async ( options?: RequestInit): Promise<platformProviderIngestionManagePost3Response> => {

  return customMutator<platformProviderIngestionManagePost3Response>(getPlatformProviderIngestionManagePost3Url(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformProviderIngestionManagePost3MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost3>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost3>>, TError,void, TContext> => {

const mutationKey = ['platformProviderIngestionManagePost3'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformProviderIngestionManagePost3>>, void> = () => {


          return  platformProviderIngestionManagePost3(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformProviderIngestionManagePost3MutationResult = NonNullable<Awaited<ReturnType<typeof platformProviderIngestionManagePost3>>>

    export type PlatformProviderIngestionManagePost3MutationError = unknown

    export const usePlatformProviderIngestionManagePost3 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformProviderIngestionManagePost3>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformProviderIngestionManagePost3>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformProviderIngestionManagePost3MutationOptions(options), queryClient);
    }

export type platformIssuerEventsReadResponse200 = {
  data: void
  status: 200
}

export type platformIssuerEventsReadResponseSuccess = (platformIssuerEventsReadResponse200) & {
  headers: Headers;
};
;

export type platformIssuerEventsReadResponse = (platformIssuerEventsReadResponseSuccess)

export const getPlatformIssuerEventsReadUrl = () => {




  return `/api/platform/issuer-events`
}

export const platformIssuerEventsRead = async ( options?: RequestInit): Promise<platformIssuerEventsReadResponse> => {

  return customMutator<platformIssuerEventsReadResponse>(getPlatformIssuerEventsReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformIssuerEventsReadQueryKey = () => {
    return [
    `/api/platform/issuer-events`
    ] as const;
    }


export const getPlatformIssuerEventsReadQueryOptions = <TData = Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformIssuerEventsReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformIssuerEventsRead>>> = ({ signal }) => platformIssuerEventsRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformIssuerEventsReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformIssuerEventsRead>>>
export type PlatformIssuerEventsReadQueryError = unknown


export function usePlatformIssuerEventsRead<TData = Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformIssuerEventsRead>>,
          TError,
          Awaited<ReturnType<typeof platformIssuerEventsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformIssuerEventsRead<TData = Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformIssuerEventsRead>>,
          TError,
          Awaited<ReturnType<typeof platformIssuerEventsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformIssuerEventsRead<TData = Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformIssuerEventsRead<TData = Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformIssuerEventsReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformIssuerEventsManageResponse201 = {
  data: void
  status: 201
}

export type platformIssuerEventsManageResponseSuccess = (platformIssuerEventsManageResponse201) & {
  headers: Headers;
};
;

export type platformIssuerEventsManageResponse = (platformIssuerEventsManageResponseSuccess)

export const getPlatformIssuerEventsManageUrl = () => {




  return `/api/platform/issuer-events`
}

export const platformIssuerEventsManage = async ( options?: RequestInit): Promise<platformIssuerEventsManageResponse> => {

  return customMutator<platformIssuerEventsManageResponse>(getPlatformIssuerEventsManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformIssuerEventsManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManage>>, TError,void, TContext> => {

const mutationKey = ['platformIssuerEventsManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformIssuerEventsManage>>, void> = () => {


          return  platformIssuerEventsManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformIssuerEventsManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformIssuerEventsManage>>>

    export type PlatformIssuerEventsManageMutationError = unknown

    export const usePlatformIssuerEventsManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformIssuerEventsManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformIssuerEventsManageMutationOptions(options), queryClient);
    }

export type platformIssuerEventsReadGetResponse200 = {
  data: void
  status: 200
}

export type platformIssuerEventsReadGetResponseSuccess = (platformIssuerEventsReadGetResponse200) & {
  headers: Headers;
};
;

export type platformIssuerEventsReadGetResponse = (platformIssuerEventsReadGetResponseSuccess)

export const getPlatformIssuerEventsReadGetUrl = (id: string,) => {




  return `/api/platform/issuer-events/${id}`
}

export const platformIssuerEventsReadGet = async (id: string, options?: RequestInit): Promise<platformIssuerEventsReadGetResponse> => {

  return customMutator<platformIssuerEventsReadGetResponse>(getPlatformIssuerEventsReadGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformIssuerEventsReadGetQueryKey = (id: string,) => {
    return [
    `/api/platform/issuer-events/${id}`
    ] as const;
    }


export const getPlatformIssuerEventsReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformIssuerEventsReadGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformIssuerEventsReadGet>>> = ({ signal }) => platformIssuerEventsReadGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformIssuerEventsReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformIssuerEventsReadGet>>>
export type PlatformIssuerEventsReadGetQueryError = unknown


export function usePlatformIssuerEventsReadGet<TData = Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformIssuerEventsReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformIssuerEventsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformIssuerEventsReadGet<TData = Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformIssuerEventsReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformIssuerEventsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformIssuerEventsReadGet<TData = Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformIssuerEventsReadGet<TData = Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformIssuerEventsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformIssuerEventsReadGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformIssuerEventsManagePostResponse201 = {
  data: void
  status: 201
}

export type platformIssuerEventsManagePostResponseSuccess = (platformIssuerEventsManagePostResponse201) & {
  headers: Headers;
};
;

export type platformIssuerEventsManagePostResponse = (platformIssuerEventsManagePostResponseSuccess)

export const getPlatformIssuerEventsManagePostUrl = (id: string,
    versionId: string,) => {




  return `/api/platform/issuer-events/${id}/conflicts/${versionId}/resolve`
}

export const platformIssuerEventsManagePost = async (id: string,
    versionId: string, options?: RequestInit): Promise<platformIssuerEventsManagePostResponse> => {

  return customMutator<platformIssuerEventsManagePostResponse>(getPlatformIssuerEventsManagePostUrl(id,versionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformIssuerEventsManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManagePost>>, TError,{id: string;versionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManagePost>>, TError,{id: string;versionId: string}, TContext> => {

const mutationKey = ['platformIssuerEventsManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformIssuerEventsManagePost>>, {id: string;versionId: string}> = (props) => {
          const {id,versionId} = props ?? {};

          return  platformIssuerEventsManagePost(id,versionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformIssuerEventsManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformIssuerEventsManagePost>>>

    export type PlatformIssuerEventsManagePostMutationError = unknown

    export const usePlatformIssuerEventsManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManagePost>>, TError,{id: string;versionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformIssuerEventsManagePost>>,
        TError,
        {id: string;versionId: string},
        TContext
      > => {
      return useMutation(getPlatformIssuerEventsManagePostMutationOptions(options), queryClient);
    }

export type platformIssuerEventsManagePost1Response201 = {
  data: void
  status: 201
}

export type platformIssuerEventsManagePost1ResponseSuccess = (platformIssuerEventsManagePost1Response201) & {
  headers: Headers;
};
;

export type platformIssuerEventsManagePost1Response = (platformIssuerEventsManagePost1ResponseSuccess)

export const getPlatformIssuerEventsManagePost1Url = (id: string,) => {




  return `/api/platform/issuer-events/${id}/revisions`
}

export const platformIssuerEventsManagePost1 = async (id: string, options?: RequestInit): Promise<platformIssuerEventsManagePost1Response> => {

  return customMutator<platformIssuerEventsManagePost1Response>(getPlatformIssuerEventsManagePost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformIssuerEventsManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManagePost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformIssuerEventsManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformIssuerEventsManagePost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformIssuerEventsManagePost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformIssuerEventsManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformIssuerEventsManagePost1>>>

    export type PlatformIssuerEventsManagePost1MutationError = unknown

    export const usePlatformIssuerEventsManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformIssuerEventsManagePost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformIssuerEventsManagePost1MutationOptions(options), queryClient);
    }

export type platformIssuerEventsManagePost2Response201 = {
  data: void
  status: 201
}

export type platformIssuerEventsManagePost2ResponseSuccess = (platformIssuerEventsManagePost2Response201) & {
  headers: Headers;
};
;

export type platformIssuerEventsManagePost2Response = (platformIssuerEventsManagePost2ResponseSuccess)

export const getPlatformIssuerEventsManagePost2Url = (id: string,) => {




  return `/api/platform/issuer-events/${id}/cancellations`
}

export const platformIssuerEventsManagePost2 = async (id: string, options?: RequestInit): Promise<platformIssuerEventsManagePost2Response> => {

  return customMutator<platformIssuerEventsManagePost2Response>(getPlatformIssuerEventsManagePost2Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformIssuerEventsManagePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManagePost2>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformIssuerEventsManagePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformIssuerEventsManagePost2>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformIssuerEventsManagePost2(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformIssuerEventsManagePost2MutationResult = NonNullable<Awaited<ReturnType<typeof platformIssuerEventsManagePost2>>>

    export type PlatformIssuerEventsManagePost2MutationError = unknown

    export const usePlatformIssuerEventsManagePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformIssuerEventsManagePost2>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformIssuerEventsManagePost2MutationOptions(options), queryClient);
    }

export type platformIssuerEventsApproveResponse201 = {
  data: void
  status: 201
}

export type platformIssuerEventsApproveResponseSuccess = (platformIssuerEventsApproveResponse201) & {
  headers: Headers;
};
;

export type platformIssuerEventsApproveResponse = (platformIssuerEventsApproveResponseSuccess)

export const getPlatformIssuerEventsApproveUrl = (id: string,
    versionId: string,) => {




  return `/api/platform/issuer-events/${id}/versions/${versionId}/approve`
}

export const platformIssuerEventsApprove = async (id: string,
    versionId: string, options?: RequestInit): Promise<platformIssuerEventsApproveResponse> => {

  return customMutator<platformIssuerEventsApproveResponse>(getPlatformIssuerEventsApproveUrl(id,versionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformIssuerEventsApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsApprove>>, TError,{id: string;versionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsApprove>>, TError,{id: string;versionId: string}, TContext> => {

const mutationKey = ['platformIssuerEventsApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformIssuerEventsApprove>>, {id: string;versionId: string}> = (props) => {
          const {id,versionId} = props ?? {};

          return  platformIssuerEventsApprove(id,versionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformIssuerEventsApproveMutationResult = NonNullable<Awaited<ReturnType<typeof platformIssuerEventsApprove>>>

    export type PlatformIssuerEventsApproveMutationError = unknown

    export const usePlatformIssuerEventsApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformIssuerEventsApprove>>, TError,{id: string;versionId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformIssuerEventsApprove>>,
        TError,
        {id: string;versionId: string},
        TContext
      > => {
      return useMutation(getPlatformIssuerEventsApproveMutationOptions(options), queryClient);
    }

export type platformPermissionCatalogMigrationsReadResponse200 = {
  data: void
  status: 200
}

export type platformPermissionCatalogMigrationsReadResponseSuccess = (platformPermissionCatalogMigrationsReadResponse200) & {
  headers: Headers;
};
;

export type platformPermissionCatalogMigrationsReadResponse = (platformPermissionCatalogMigrationsReadResponseSuccess)

export const getPlatformPermissionCatalogMigrationsReadUrl = () => {




  return `/api/platform/permission-catalog-migrations`
}

export const platformPermissionCatalogMigrationsRead = async ( options?: RequestInit): Promise<platformPermissionCatalogMigrationsReadResponse> => {

  return customMutator<platformPermissionCatalogMigrationsReadResponse>(getPlatformPermissionCatalogMigrationsReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformPermissionCatalogMigrationsReadQueryKey = () => {
    return [
    `/api/platform/permission-catalog-migrations`
    ] as const;
    }


export const getPlatformPermissionCatalogMigrationsReadQueryOptions = <TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformPermissionCatalogMigrationsReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>> = ({ signal }) => platformPermissionCatalogMigrationsRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformPermissionCatalogMigrationsReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>>
export type PlatformPermissionCatalogMigrationsReadQueryError = unknown


export function usePlatformPermissionCatalogMigrationsRead<TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>,
          TError,
          Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformPermissionCatalogMigrationsRead<TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>,
          TError,
          Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformPermissionCatalogMigrationsRead<TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformPermissionCatalogMigrationsRead<TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformPermissionCatalogMigrationsReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformPermissionCatalogMigrationsReadGetResponse200 = {
  data: void
  status: 200
}

export type platformPermissionCatalogMigrationsReadGetResponseSuccess = (platformPermissionCatalogMigrationsReadGetResponse200) & {
  headers: Headers;
};
;

export type platformPermissionCatalogMigrationsReadGetResponse = (platformPermissionCatalogMigrationsReadGetResponseSuccess)

export const getPlatformPermissionCatalogMigrationsReadGetUrl = (id: string,) => {




  return `/api/platform/permission-catalog-migrations/${id}`
}

export const platformPermissionCatalogMigrationsReadGet = async (id: string, options?: RequestInit): Promise<platformPermissionCatalogMigrationsReadGetResponse> => {

  return customMutator<platformPermissionCatalogMigrationsReadGetResponse>(getPlatformPermissionCatalogMigrationsReadGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformPermissionCatalogMigrationsReadGetQueryKey = (id: string,) => {
    return [
    `/api/platform/permission-catalog-migrations/${id}`
    ] as const;
    }


export const getPlatformPermissionCatalogMigrationsReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformPermissionCatalogMigrationsReadGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>> = ({ signal }) => platformPermissionCatalogMigrationsReadGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformPermissionCatalogMigrationsReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>>
export type PlatformPermissionCatalogMigrationsReadGetQueryError = unknown


export function usePlatformPermissionCatalogMigrationsReadGet<TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformPermissionCatalogMigrationsReadGet<TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformPermissionCatalogMigrationsReadGet<TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformPermissionCatalogMigrationsReadGet<TData = Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformPermissionCatalogMigrationsReadGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformPermissionCatalogMigrationsManageResponse201 = {
  data: void
  status: 201
}

export type platformPermissionCatalogMigrationsManageResponseSuccess = (platformPermissionCatalogMigrationsManageResponse201) & {
  headers: Headers;
};
;

export type platformPermissionCatalogMigrationsManageResponse = (platformPermissionCatalogMigrationsManageResponseSuccess)

export const getPlatformPermissionCatalogMigrationsManageUrl = () => {




  return `/api/platform/permission-catalog-migrations/preview`
}

export const platformPermissionCatalogMigrationsManage = async ( options?: RequestInit): Promise<platformPermissionCatalogMigrationsManageResponse> => {

  return customMutator<platformPermissionCatalogMigrationsManageResponse>(getPlatformPermissionCatalogMigrationsManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformPermissionCatalogMigrationsManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManage>>, TError,void, TContext> => {

const mutationKey = ['platformPermissionCatalogMigrationsManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManage>>, void> = () => {


          return  platformPermissionCatalogMigrationsManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformPermissionCatalogMigrationsManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManage>>>

    export type PlatformPermissionCatalogMigrationsManageMutationError = unknown

    export const usePlatformPermissionCatalogMigrationsManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformPermissionCatalogMigrationsManageMutationOptions(options), queryClient);
    }

export type platformPermissionCatalogMigrationsManagePostResponse201 = {
  data: void
  status: 201
}

export type platformPermissionCatalogMigrationsManagePostResponseSuccess = (platformPermissionCatalogMigrationsManagePostResponse201) & {
  headers: Headers;
};
;

export type platformPermissionCatalogMigrationsManagePostResponse = (platformPermissionCatalogMigrationsManagePostResponseSuccess)

export const getPlatformPermissionCatalogMigrationsManagePostUrl = (id: string,) => {




  return `/api/platform/permission-catalog-migrations/${id}/activate`
}

export const platformPermissionCatalogMigrationsManagePost = async (id: string, options?: RequestInit): Promise<platformPermissionCatalogMigrationsManagePostResponse> => {

  return customMutator<platformPermissionCatalogMigrationsManagePostResponse>(getPlatformPermissionCatalogMigrationsManagePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformPermissionCatalogMigrationsManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformPermissionCatalogMigrationsManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformPermissionCatalogMigrationsManagePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformPermissionCatalogMigrationsManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost>>>

    export type PlatformPermissionCatalogMigrationsManagePostMutationError = unknown

    export const usePlatformPermissionCatalogMigrationsManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformPermissionCatalogMigrationsManagePostMutationOptions(options), queryClient);
    }

export type platformPermissionCatalogMigrationsManagePost1Response201 = {
  data: void
  status: 201
}

export type platformPermissionCatalogMigrationsManagePost1ResponseSuccess = (platformPermissionCatalogMigrationsManagePost1Response201) & {
  headers: Headers;
};
;

export type platformPermissionCatalogMigrationsManagePost1Response = (platformPermissionCatalogMigrationsManagePost1ResponseSuccess)

export const getPlatformPermissionCatalogMigrationsManagePost1Url = (id: string,) => {




  return `/api/platform/permission-catalog-migrations/${id}/rollback`
}

export const platformPermissionCatalogMigrationsManagePost1 = async (id: string, options?: RequestInit): Promise<platformPermissionCatalogMigrationsManagePost1Response> => {

  return customMutator<platformPermissionCatalogMigrationsManagePost1Response>(getPlatformPermissionCatalogMigrationsManagePost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformPermissionCatalogMigrationsManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformPermissionCatalogMigrationsManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformPermissionCatalogMigrationsManagePost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformPermissionCatalogMigrationsManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost1>>>

    export type PlatformPermissionCatalogMigrationsManagePost1MutationError = unknown

    export const usePlatformPermissionCatalogMigrationsManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformPermissionCatalogMigrationsManagePost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformPermissionCatalogMigrationsManagePost1MutationOptions(options), queryClient);
    }

export type platformFederationReadResponse200 = {
  data: void
  status: 200
}

export type platformFederationReadResponseSuccess = (platformFederationReadResponse200) & {
  headers: Headers;
};
;

export type platformFederationReadResponse = (platformFederationReadResponseSuccess)

export const getPlatformFederationReadUrl = () => {




  return `/api/platform/federation`
}

export const platformFederationRead = async ( options?: RequestInit): Promise<platformFederationReadResponse> => {

  return customMutator<platformFederationReadResponse>(getPlatformFederationReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformFederationReadQueryKey = () => {
    return [
    `/api/platform/federation`
    ] as const;
    }


export const getPlatformFederationReadQueryOptions = <TData = Awaited<ReturnType<typeof platformFederationRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformFederationRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformFederationReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformFederationRead>>> = ({ signal }) => platformFederationRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformFederationRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformFederationReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformFederationRead>>>
export type PlatformFederationReadQueryError = unknown


export function usePlatformFederationRead<TData = Awaited<ReturnType<typeof platformFederationRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformFederationRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformFederationRead>>,
          TError,
          Awaited<ReturnType<typeof platformFederationRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformFederationRead<TData = Awaited<ReturnType<typeof platformFederationRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformFederationRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformFederationRead>>,
          TError,
          Awaited<ReturnType<typeof platformFederationRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformFederationRead<TData = Awaited<ReturnType<typeof platformFederationRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformFederationRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformFederationRead<TData = Awaited<ReturnType<typeof platformFederationRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformFederationRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformFederationReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformFederationManageResponse201 = {
  data: void
  status: 201
}

export type platformFederationManageResponseSuccess = (platformFederationManageResponse201) & {
  headers: Headers;
};
;

export type platformFederationManageResponse = (platformFederationManageResponseSuccess)

export const getPlatformFederationManageUrl = () => {




  return `/api/platform/federation/providers`
}

export const platformFederationManage = async ( options?: RequestInit): Promise<platformFederationManageResponse> => {

  return customMutator<platformFederationManageResponse>(getPlatformFederationManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformFederationManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformFederationManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformFederationManage>>, TError,void, TContext> => {

const mutationKey = ['platformFederationManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformFederationManage>>, void> = () => {


          return  platformFederationManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformFederationManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformFederationManage>>>

    export type PlatformFederationManageMutationError = unknown

    export const usePlatformFederationManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformFederationManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformFederationManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformFederationManageMutationOptions(options), queryClient);
    }

export type platformFederationManagePostResponse201 = {
  data: void
  status: 201
}

export type platformFederationManagePostResponseSuccess = (platformFederationManagePostResponse201) & {
  headers: Headers;
};
;

export type platformFederationManagePostResponse = (platformFederationManagePostResponseSuccess)

export const getPlatformFederationManagePostUrl = (code: string,) => {




  return `/api/platform/federation/providers/${code}/deprovision`
}

export const platformFederationManagePost = async (code: string, options?: RequestInit): Promise<platformFederationManagePostResponse> => {

  return customMutator<platformFederationManagePostResponse>(getPlatformFederationManagePostUrl(code),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformFederationManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformFederationManagePost>>, TError,{code: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformFederationManagePost>>, TError,{code: string}, TContext> => {

const mutationKey = ['platformFederationManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformFederationManagePost>>, {code: string}> = (props) => {
          const {code} = props ?? {};

          return  platformFederationManagePost(code,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformFederationManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformFederationManagePost>>>

    export type PlatformFederationManagePostMutationError = unknown

    export const usePlatformFederationManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformFederationManagePost>>, TError,{code: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformFederationManagePost>>,
        TError,
        {code: string},
        TContext
      > => {
      return useMutation(getPlatformFederationManagePostMutationOptions(options), queryClient);
    }

export type platformFederationManagePost1Response201 = {
  data: void
  status: 201
}

export type platformFederationManagePost1ResponseSuccess = (platformFederationManagePost1Response201) & {
  headers: Headers;
};
;

export type platformFederationManagePost1Response = (platformFederationManagePost1ResponseSuccess)

export const getPlatformFederationManagePost1Url = (code: string,) => {




  return `/api/platform/federation/providers/${code}/remove`
}

export const platformFederationManagePost1 = async (code: string, options?: RequestInit): Promise<platformFederationManagePost1Response> => {

  return customMutator<platformFederationManagePost1Response>(getPlatformFederationManagePost1Url(code),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformFederationManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformFederationManagePost1>>, TError,{code: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformFederationManagePost1>>, TError,{code: string}, TContext> => {

const mutationKey = ['platformFederationManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformFederationManagePost1>>, {code: string}> = (props) => {
          const {code} = props ?? {};

          return  platformFederationManagePost1(code,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformFederationManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformFederationManagePost1>>>

    export type PlatformFederationManagePost1MutationError = unknown

    export const usePlatformFederationManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformFederationManagePost1>>, TError,{code: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformFederationManagePost1>>,
        TError,
        {code: string},
        TContext
      > => {
      return useMutation(getPlatformFederationManagePost1MutationOptions(options), queryClient);
    }

export type federationControllerAssertResponse201 = {
  data: void
  status: 201
}

export type federationControllerAssertResponseSuccess = (federationControllerAssertResponse201) & {
  headers: Headers;
};
;

export type federationControllerAssertResponse = (federationControllerAssertResponseSuccess)

export const getFederationControllerAssertUrl = () => {




  return `/api/platform/federation/assertions`
}

export const federationControllerAssert = async ( options?: RequestInit): Promise<federationControllerAssertResponse> => {

  return customMutator<federationControllerAssertResponse>(getFederationControllerAssertUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getFederationControllerAssertMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof federationControllerAssert>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof federationControllerAssert>>, TError,void, TContext> => {

const mutationKey = ['federationControllerAssert'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof federationControllerAssert>>, void> = () => {


          return  federationControllerAssert(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type FederationControllerAssertMutationResult = NonNullable<Awaited<ReturnType<typeof federationControllerAssert>>>

    export type FederationControllerAssertMutationError = unknown

    export const useFederationControllerAssert = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof federationControllerAssert>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof federationControllerAssert>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getFederationControllerAssertMutationOptions(options), queryClient);
    }

export type platformServiceAccountsReadResponse200 = {
  data: void
  status: 200
}

export type platformServiceAccountsReadResponseSuccess = (platformServiceAccountsReadResponse200) & {
  headers: Headers;
};
;

export type platformServiceAccountsReadResponse = (platformServiceAccountsReadResponseSuccess)

export const getPlatformServiceAccountsReadUrl = () => {




  return `/api/platform/service-accounts`
}

export const platformServiceAccountsRead = async ( options?: RequestInit): Promise<platformServiceAccountsReadResponse> => {

  return customMutator<platformServiceAccountsReadResponse>(getPlatformServiceAccountsReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformServiceAccountsReadQueryKey = () => {
    return [
    `/api/platform/service-accounts`
    ] as const;
    }


export const getPlatformServiceAccountsReadQueryOptions = <TData = Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformServiceAccountsReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformServiceAccountsRead>>> = ({ signal }) => platformServiceAccountsRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformServiceAccountsReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformServiceAccountsRead>>>
export type PlatformServiceAccountsReadQueryError = unknown


export function usePlatformServiceAccountsRead<TData = Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformServiceAccountsRead>>,
          TError,
          Awaited<ReturnType<typeof platformServiceAccountsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformServiceAccountsRead<TData = Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformServiceAccountsRead>>,
          TError,
          Awaited<ReturnType<typeof platformServiceAccountsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformServiceAccountsRead<TData = Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformServiceAccountsRead<TData = Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformServiceAccountsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformServiceAccountsReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformServiceAccountsManageResponse201 = {
  data: void
  status: 201
}

export type platformServiceAccountsManageResponseSuccess = (platformServiceAccountsManageResponse201) & {
  headers: Headers;
};
;

export type platformServiceAccountsManageResponse = (platformServiceAccountsManageResponseSuccess)

export const getPlatformServiceAccountsManageUrl = () => {




  return `/api/platform/service-accounts`
}

export const platformServiceAccountsManage = async ( options?: RequestInit): Promise<platformServiceAccountsManageResponse> => {

  return customMutator<platformServiceAccountsManageResponse>(getPlatformServiceAccountsManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformServiceAccountsManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManage>>, TError,void, TContext> => {

const mutationKey = ['platformServiceAccountsManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformServiceAccountsManage>>, void> = () => {


          return  platformServiceAccountsManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformServiceAccountsManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformServiceAccountsManage>>>

    export type PlatformServiceAccountsManageMutationError = unknown

    export const usePlatformServiceAccountsManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformServiceAccountsManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformServiceAccountsManageMutationOptions(options), queryClient);
    }

export type platformServiceAccountsManagePostResponse201 = {
  data: void
  status: 201
}

export type platformServiceAccountsManagePostResponseSuccess = (platformServiceAccountsManagePostResponse201) & {
  headers: Headers;
};
;

export type platformServiceAccountsManagePostResponse = (platformServiceAccountsManagePostResponseSuccess)

export const getPlatformServiceAccountsManagePostUrl = (code: string,) => {




  return `/api/platform/service-accounts/${code}/credentials`
}

export const platformServiceAccountsManagePost = async (code: string, options?: RequestInit): Promise<platformServiceAccountsManagePostResponse> => {

  return customMutator<platformServiceAccountsManagePostResponse>(getPlatformServiceAccountsManagePostUrl(code),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformServiceAccountsManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManagePost>>, TError,{code: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManagePost>>, TError,{code: string}, TContext> => {

const mutationKey = ['platformServiceAccountsManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformServiceAccountsManagePost>>, {code: string}> = (props) => {
          const {code} = props ?? {};

          return  platformServiceAccountsManagePost(code,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformServiceAccountsManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformServiceAccountsManagePost>>>

    export type PlatformServiceAccountsManagePostMutationError = unknown

    export const usePlatformServiceAccountsManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManagePost>>, TError,{code: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformServiceAccountsManagePost>>,
        TError,
        {code: string},
        TContext
      > => {
      return useMutation(getPlatformServiceAccountsManagePostMutationOptions(options), queryClient);
    }

export type platformServiceAccountsManagePost1Response201 = {
  data: void
  status: 201
}

export type platformServiceAccountsManagePost1ResponseSuccess = (platformServiceAccountsManagePost1Response201) & {
  headers: Headers;
};
;

export type platformServiceAccountsManagePost1Response = (platformServiceAccountsManagePost1ResponseSuccess)

export const getPlatformServiceAccountsManagePost1Url = (code: string,
    id: string,) => {




  return `/api/platform/service-accounts/${code}/credentials/${id}/rotate`
}

export const platformServiceAccountsManagePost1 = async (code: string,
    id: string, options?: RequestInit): Promise<platformServiceAccountsManagePost1Response> => {

  return customMutator<platformServiceAccountsManagePost1Response>(getPlatformServiceAccountsManagePost1Url(code,id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformServiceAccountsManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManagePost1>>, TError,{code: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManagePost1>>, TError,{code: string;id: string}, TContext> => {

const mutationKey = ['platformServiceAccountsManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformServiceAccountsManagePost1>>, {code: string;id: string}> = (props) => {
          const {code,id} = props ?? {};

          return  platformServiceAccountsManagePost1(code,id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformServiceAccountsManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformServiceAccountsManagePost1>>>

    export type PlatformServiceAccountsManagePost1MutationError = unknown

    export const usePlatformServiceAccountsManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManagePost1>>, TError,{code: string;id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformServiceAccountsManagePost1>>,
        TError,
        {code: string;id: string},
        TContext
      > => {
      return useMutation(getPlatformServiceAccountsManagePost1MutationOptions(options), queryClient);
    }

export type platformServiceAccountsManagePost2Response201 = {
  data: void
  status: 201
}

export type platformServiceAccountsManagePost2ResponseSuccess = (platformServiceAccountsManagePost2Response201) & {
  headers: Headers;
};
;

export type platformServiceAccountsManagePost2Response = (platformServiceAccountsManagePost2ResponseSuccess)

export const getPlatformServiceAccountsManagePost2Url = (id: string,) => {




  return `/api/platform/service-accounts/credentials/${id}/revoke`
}

export const platformServiceAccountsManagePost2 = async (id: string, options?: RequestInit): Promise<platformServiceAccountsManagePost2Response> => {

  return customMutator<platformServiceAccountsManagePost2Response>(getPlatformServiceAccountsManagePost2Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformServiceAccountsManagePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManagePost2>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformServiceAccountsManagePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformServiceAccountsManagePost2>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformServiceAccountsManagePost2(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformServiceAccountsManagePost2MutationResult = NonNullable<Awaited<ReturnType<typeof platformServiceAccountsManagePost2>>>

    export type PlatformServiceAccountsManagePost2MutationError = unknown

    export const usePlatformServiceAccountsManagePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformServiceAccountsManagePost2>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformServiceAccountsManagePost2>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformServiceAccountsManagePost2MutationOptions(options), queryClient);
    }

export type serviceAccountsControllerUseResponse201 = {
  data: void
  status: 201
}

export type serviceAccountsControllerUseResponseSuccess = (serviceAccountsControllerUseResponse201) & {
  headers: Headers;
};
;

export type serviceAccountsControllerUseResponse = (serviceAccountsControllerUseResponseSuccess)

export const getServiceAccountsControllerUseUrl = () => {




  return `/api/platform/service-accounts/machine/use`
}

export const serviceAccountsControllerUse = async ( options?: RequestInit): Promise<serviceAccountsControllerUseResponse> => {

  return customMutator<serviceAccountsControllerUseResponse>(getServiceAccountsControllerUseUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getServiceAccountsControllerUseMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof serviceAccountsControllerUse>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof serviceAccountsControllerUse>>, TError,void, TContext> => {

const mutationKey = ['serviceAccountsControllerUse'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof serviceAccountsControllerUse>>, void> = () => {


          return  serviceAccountsControllerUse(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ServiceAccountsControllerUseMutationResult = NonNullable<Awaited<ReturnType<typeof serviceAccountsControllerUse>>>

    export type ServiceAccountsControllerUseMutationError = unknown

    export const useServiceAccountsControllerUse = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof serviceAccountsControllerUse>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof serviceAccountsControllerUse>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getServiceAccountsControllerUseMutationOptions(options), queryClient);
    }

export type platformAccessReviewsReadResponse200 = {
  data: void
  status: 200
}

export type platformAccessReviewsReadResponseSuccess = (platformAccessReviewsReadResponse200) & {
  headers: Headers;
};
;

export type platformAccessReviewsReadResponse = (platformAccessReviewsReadResponseSuccess)

export const getPlatformAccessReviewsReadUrl = () => {




  return `/api/platform/access-reviews`
}

export const platformAccessReviewsRead = async ( options?: RequestInit): Promise<platformAccessReviewsReadResponse> => {

  return customMutator<platformAccessReviewsReadResponse>(getPlatformAccessReviewsReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformAccessReviewsReadQueryKey = () => {
    return [
    `/api/platform/access-reviews`
    ] as const;
    }


export const getPlatformAccessReviewsReadQueryOptions = <TData = Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformAccessReviewsReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformAccessReviewsRead>>> = ({ signal }) => platformAccessReviewsRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformAccessReviewsReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformAccessReviewsRead>>>
export type PlatformAccessReviewsReadQueryError = unknown


export function usePlatformAccessReviewsRead<TData = Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformAccessReviewsRead>>,
          TError,
          Awaited<ReturnType<typeof platformAccessReviewsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformAccessReviewsRead<TData = Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformAccessReviewsRead>>,
          TError,
          Awaited<ReturnType<typeof platformAccessReviewsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformAccessReviewsRead<TData = Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformAccessReviewsRead<TData = Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformAccessReviewsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformAccessReviewsReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformAccessReviewsManageResponse201 = {
  data: void
  status: 201
}

export type platformAccessReviewsManageResponseSuccess = (platformAccessReviewsManageResponse201) & {
  headers: Headers;
};
;

export type platformAccessReviewsManageResponse = (platformAccessReviewsManageResponseSuccess)

export const getPlatformAccessReviewsManageUrl = () => {




  return `/api/platform/access-reviews`
}

export const platformAccessReviewsManage = async ( options?: RequestInit): Promise<platformAccessReviewsManageResponse> => {

  return customMutator<platformAccessReviewsManageResponse>(getPlatformAccessReviewsManageUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformAccessReviewsManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformAccessReviewsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformAccessReviewsManage>>, TError,void, TContext> => {

const mutationKey = ['platformAccessReviewsManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformAccessReviewsManage>>, void> = () => {


          return  platformAccessReviewsManage(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformAccessReviewsManageMutationResult = NonNullable<Awaited<ReturnType<typeof platformAccessReviewsManage>>>

    export type PlatformAccessReviewsManageMutationError = unknown

    export const usePlatformAccessReviewsManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformAccessReviewsManage>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformAccessReviewsManage>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformAccessReviewsManageMutationOptions(options), queryClient);
    }

export type platformAccessReviewsManagePostResponse201 = {
  data: void
  status: 201
}

export type platformAccessReviewsManagePostResponseSuccess = (platformAccessReviewsManagePostResponse201) & {
  headers: Headers;
};
;

export type platformAccessReviewsManagePostResponse = (platformAccessReviewsManagePostResponseSuccess)

export const getPlatformAccessReviewsManagePostUrl = (id: string,) => {




  return `/api/platform/access-reviews/items/${id}/decision`
}

export const platformAccessReviewsManagePost = async (id: string, options?: RequestInit): Promise<platformAccessReviewsManagePostResponse> => {

  return customMutator<platformAccessReviewsManagePostResponse>(getPlatformAccessReviewsManagePostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformAccessReviewsManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformAccessReviewsManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformAccessReviewsManagePost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformAccessReviewsManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformAccessReviewsManagePost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformAccessReviewsManagePost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformAccessReviewsManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformAccessReviewsManagePost>>>

    export type PlatformAccessReviewsManagePostMutationError = unknown

    export const usePlatformAccessReviewsManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformAccessReviewsManagePost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformAccessReviewsManagePost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformAccessReviewsManagePostMutationOptions(options), queryClient);
    }

export type platformAccessReviewsManagePost1Response201 = {
  data: void
  status: 201
}

export type platformAccessReviewsManagePost1ResponseSuccess = (platformAccessReviewsManagePost1Response201) & {
  headers: Headers;
};
;

export type platformAccessReviewsManagePost1Response = (platformAccessReviewsManagePost1ResponseSuccess)

export const getPlatformAccessReviewsManagePost1Url = (id: string,) => {




  return `/api/platform/access-reviews/${id}/escalate`
}

export const platformAccessReviewsManagePost1 = async (id: string, options?: RequestInit): Promise<platformAccessReviewsManagePost1Response> => {

  return customMutator<platformAccessReviewsManagePost1Response>(getPlatformAccessReviewsManagePost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformAccessReviewsManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformAccessReviewsManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformAccessReviewsManagePost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformAccessReviewsManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformAccessReviewsManagePost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformAccessReviewsManagePost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformAccessReviewsManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformAccessReviewsManagePost1>>>

    export type PlatformAccessReviewsManagePost1MutationError = unknown

    export const usePlatformAccessReviewsManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformAccessReviewsManagePost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformAccessReviewsManagePost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformAccessReviewsManagePost1MutationOptions(options), queryClient);
    }

export type platformOrganizationOwnershipReadResponse200 = {
  data: void
  status: 200
}

export type platformOrganizationOwnershipReadResponseSuccess = (platformOrganizationOwnershipReadResponse200) & {
  headers: Headers;
};
;

export type platformOrganizationOwnershipReadResponse = (platformOrganizationOwnershipReadResponseSuccess)

export const getPlatformOrganizationOwnershipReadUrl = () => {




  return `/api/platform/organization-ownership-transitions`
}

export const platformOrganizationOwnershipRead = async ( options?: RequestInit): Promise<platformOrganizationOwnershipReadResponse> => {

  return customMutator<platformOrganizationOwnershipReadResponse>(getPlatformOrganizationOwnershipReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformOrganizationOwnershipReadQueryKey = () => {
    return [
    `/api/platform/organization-ownership-transitions`
    ] as const;
    }


export const getPlatformOrganizationOwnershipReadQueryOptions = <TData = Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformOrganizationOwnershipReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>> = ({ signal }) => platformOrganizationOwnershipRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformOrganizationOwnershipReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>>
export type PlatformOrganizationOwnershipReadQueryError = unknown


export function usePlatformOrganizationOwnershipRead<TData = Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationOwnershipRead<TData = Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationOwnershipRead<TData = Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformOrganizationOwnershipRead<TData = Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformOrganizationOwnershipReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformOrganizationOwnershipRequestResponse201 = {
  data: void
  status: 201
}

export type platformOrganizationOwnershipRequestResponseSuccess = (platformOrganizationOwnershipRequestResponse201) & {
  headers: Headers;
};
;

export type platformOrganizationOwnershipRequestResponse = (platformOrganizationOwnershipRequestResponseSuccess)

export const getPlatformOrganizationOwnershipRequestUrl = () => {




  return `/api/platform/organization-ownership-transitions`
}

export const platformOrganizationOwnershipRequest = async ( options?: RequestInit): Promise<platformOrganizationOwnershipRequestResponse> => {

  return customMutator<platformOrganizationOwnershipRequestResponse>(getPlatformOrganizationOwnershipRequestUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationOwnershipRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRequest>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRequest>>, TError,void, TContext> => {

const mutationKey = ['platformOrganizationOwnershipRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationOwnershipRequest>>, void> = () => {


          return  platformOrganizationOwnershipRequest(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationOwnershipRequestMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationOwnershipRequest>>>

    export type PlatformOrganizationOwnershipRequestMutationError = unknown

    export const usePlatformOrganizationOwnershipRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRequest>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationOwnershipRequest>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformOrganizationOwnershipRequestMutationOptions(options), queryClient);
    }

export type platformOrganizationOwnershipReadGetResponse200 = {
  data: void
  status: 200
}

export type platformOrganizationOwnershipReadGetResponseSuccess = (platformOrganizationOwnershipReadGetResponse200) & {
  headers: Headers;
};
;

export type platformOrganizationOwnershipReadGetResponse = (platformOrganizationOwnershipReadGetResponseSuccess)

export const getPlatformOrganizationOwnershipReadGetUrl = (id: string,) => {




  return `/api/platform/organization-ownership-transitions/${id}`
}

export const platformOrganizationOwnershipReadGet = async (id: string, options?: RequestInit): Promise<platformOrganizationOwnershipReadGetResponse> => {

  return customMutator<platformOrganizationOwnershipReadGetResponse>(getPlatformOrganizationOwnershipReadGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformOrganizationOwnershipReadGetQueryKey = (id: string,) => {
    return [
    `/api/platform/organization-ownership-transitions/${id}`
    ] as const;
    }


export const getPlatformOrganizationOwnershipReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformOrganizationOwnershipReadGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>> = ({ signal }) => platformOrganizationOwnershipReadGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformOrganizationOwnershipReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>>
export type PlatformOrganizationOwnershipReadGetQueryError = unknown


export function usePlatformOrganizationOwnershipReadGet<TData = Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationOwnershipReadGet<TData = Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformOrganizationOwnershipReadGet<TData = Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformOrganizationOwnershipReadGet<TData = Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformOrganizationOwnershipReadGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformOrganizationOwnershipRequestPostResponse201 = {
  data: void
  status: 201
}

export type platformOrganizationOwnershipRequestPostResponseSuccess = (platformOrganizationOwnershipRequestPostResponse201) & {
  headers: Headers;
};
;

export type platformOrganizationOwnershipRequestPostResponse = (platformOrganizationOwnershipRequestPostResponseSuccess)

export const getPlatformOrganizationOwnershipRequestPostUrl = (id: string,) => {




  return `/api/platform/organization-ownership-transitions/${id}/verifications`
}

export const platformOrganizationOwnershipRequestPost = async (id: string, options?: RequestInit): Promise<platformOrganizationOwnershipRequestPostResponse> => {

  return customMutator<platformOrganizationOwnershipRequestPostResponse>(getPlatformOrganizationOwnershipRequestPostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationOwnershipRequestPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformOrganizationOwnershipRequestPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformOrganizationOwnershipRequestPost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationOwnershipRequestPostMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost>>>

    export type PlatformOrganizationOwnershipRequestPostMutationError = unknown

    export const usePlatformOrganizationOwnershipRequestPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformOrganizationOwnershipRequestPostMutationOptions(options), queryClient);
    }

export type platformOrganizationOwnershipRequestPost1Response201 = {
  data: void
  status: 201
}

export type platformOrganizationOwnershipRequestPost1ResponseSuccess = (platformOrganizationOwnershipRequestPost1Response201) & {
  headers: Headers;
};
;

export type platformOrganizationOwnershipRequestPost1Response = (platformOrganizationOwnershipRequestPost1ResponseSuccess)

export const getPlatformOrganizationOwnershipRequestPost1Url = (id: string,) => {




  return `/api/platform/organization-ownership-transitions/${id}/disputes`
}

export const platformOrganizationOwnershipRequestPost1 = async (id: string, options?: RequestInit): Promise<platformOrganizationOwnershipRequestPost1Response> => {

  return customMutator<platformOrganizationOwnershipRequestPost1Response>(getPlatformOrganizationOwnershipRequestPost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationOwnershipRequestPost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformOrganizationOwnershipRequestPost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformOrganizationOwnershipRequestPost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationOwnershipRequestPost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost1>>>

    export type PlatformOrganizationOwnershipRequestPost1MutationError = unknown

    export const usePlatformOrganizationOwnershipRequestPost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationOwnershipRequestPost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformOrganizationOwnershipRequestPost1MutationOptions(options), queryClient);
    }

export type platformOrganizationOwnershipApproveResponse201 = {
  data: void
  status: 201
}

export type platformOrganizationOwnershipApproveResponseSuccess = (platformOrganizationOwnershipApproveResponse201) & {
  headers: Headers;
};
;

export type platformOrganizationOwnershipApproveResponse = (platformOrganizationOwnershipApproveResponseSuccess)

export const getPlatformOrganizationOwnershipApproveUrl = (id: string,) => {




  return `/api/platform/organization-ownership-transitions/${id}/approvals`
}

export const platformOrganizationOwnershipApprove = async (id: string, options?: RequestInit): Promise<platformOrganizationOwnershipApproveResponse> => {

  return customMutator<platformOrganizationOwnershipApproveResponse>(getPlatformOrganizationOwnershipApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformOrganizationOwnershipApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformOrganizationOwnershipApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformOrganizationOwnershipApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformOrganizationOwnershipApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformOrganizationOwnershipApproveMutationResult = NonNullable<Awaited<ReturnType<typeof platformOrganizationOwnershipApprove>>>

    export type PlatformOrganizationOwnershipApproveMutationError = unknown

    export const usePlatformOrganizationOwnershipApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformOrganizationOwnershipApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformOrganizationOwnershipApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformOrganizationOwnershipApproveMutationOptions(options), queryClient);
    }

export type platformMigrationGovernanceReadResponse200 = {
  data: void
  status: 200
}

export type platformMigrationGovernanceReadResponseSuccess = (platformMigrationGovernanceReadResponse200) & {
  headers: Headers;
};
;

export type platformMigrationGovernanceReadResponse = (platformMigrationGovernanceReadResponseSuccess)

export const getPlatformMigrationGovernanceReadUrl = () => {




  return `/api/platform/migration-governance`
}

export const platformMigrationGovernanceRead = async ( options?: RequestInit): Promise<platformMigrationGovernanceReadResponse> => {

  return customMutator<platformMigrationGovernanceReadResponse>(getPlatformMigrationGovernanceReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformMigrationGovernanceReadQueryKey = () => {
    return [
    `/api/platform/migration-governance`
    ] as const;
    }


export const getPlatformMigrationGovernanceReadQueryOptions = <TData = Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformMigrationGovernanceReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformMigrationGovernanceRead>>> = ({ signal }) => platformMigrationGovernanceRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformMigrationGovernanceReadQueryResult = NonNullable<Awaited<ReturnType<typeof platformMigrationGovernanceRead>>>
export type PlatformMigrationGovernanceReadQueryError = unknown


export function usePlatformMigrationGovernanceRead<TData = Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformMigrationGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof platformMigrationGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformMigrationGovernanceRead<TData = Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformMigrationGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof platformMigrationGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformMigrationGovernanceRead<TData = Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformMigrationGovernanceRead<TData = Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformMigrationGovernanceReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformMigrationGovernanceReadGetResponse200 = {
  data: void
  status: 200
}

export type platformMigrationGovernanceReadGetResponseSuccess = (platformMigrationGovernanceReadGetResponse200) & {
  headers: Headers;
};
;

export type platformMigrationGovernanceReadGetResponse = (platformMigrationGovernanceReadGetResponseSuccess)

export const getPlatformMigrationGovernanceReadGetUrl = (id: string,) => {




  return `/api/platform/migration-governance/${id}`
}

export const platformMigrationGovernanceReadGet = async (id: string, options?: RequestInit): Promise<platformMigrationGovernanceReadGetResponse> => {

  return customMutator<platformMigrationGovernanceReadGetResponse>(getPlatformMigrationGovernanceReadGetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPlatformMigrationGovernanceReadGetQueryKey = (id: string,) => {
    return [
    `/api/platform/migration-governance/${id}`
    ] as const;
    }


export const getPlatformMigrationGovernanceReadGetQueryOptions = <TData = Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPlatformMigrationGovernanceReadGetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>> = ({ signal }) => platformMigrationGovernanceReadGet(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PlatformMigrationGovernanceReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>>
export type PlatformMigrationGovernanceReadGetQueryError = unknown


export function usePlatformMigrationGovernanceReadGet<TData = Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformMigrationGovernanceReadGet<TData = Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>,
          TError,
          Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePlatformMigrationGovernanceReadGet<TData = Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePlatformMigrationGovernanceReadGet<TData = Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof platformMigrationGovernanceReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPlatformMigrationGovernanceReadGetQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type platformMigrationGovernanceRequestResponse201 = {
  data: void
  status: 201
}

export type platformMigrationGovernanceRequestResponseSuccess = (platformMigrationGovernanceRequestResponse201) & {
  headers: Headers;
};
;

export type platformMigrationGovernanceRequestResponse = (platformMigrationGovernanceRequestResponseSuccess)

export const getPlatformMigrationGovernanceRequestUrl = () => {




  return `/api/platform/migration-governance/preview`
}

export const platformMigrationGovernanceRequest = async ( options?: RequestInit): Promise<platformMigrationGovernanceRequestResponse> => {

  return customMutator<platformMigrationGovernanceRequestResponse>(getPlatformMigrationGovernanceRequestUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformMigrationGovernanceRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRequest>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRequest>>, TError,void, TContext> => {

const mutationKey = ['platformMigrationGovernanceRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformMigrationGovernanceRequest>>, void> = () => {


          return  platformMigrationGovernanceRequest(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformMigrationGovernanceRequestMutationResult = NonNullable<Awaited<ReturnType<typeof platformMigrationGovernanceRequest>>>

    export type PlatformMigrationGovernanceRequestMutationError = unknown

    export const usePlatformMigrationGovernanceRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRequest>>, TError,void, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformMigrationGovernanceRequest>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPlatformMigrationGovernanceRequestMutationOptions(options), queryClient);
    }

export type platformMigrationGovernanceApproveResponse201 = {
  data: void
  status: 201
}

export type platformMigrationGovernanceApproveResponseSuccess = (platformMigrationGovernanceApproveResponse201) & {
  headers: Headers;
};
;

export type platformMigrationGovernanceApproveResponse = (platformMigrationGovernanceApproveResponseSuccess)

export const getPlatformMigrationGovernanceApproveUrl = (id: string,) => {




  return `/api/platform/migration-governance/${id}/approvals`
}

export const platformMigrationGovernanceApprove = async (id: string, options?: RequestInit): Promise<platformMigrationGovernanceApproveResponse> => {

  return customMutator<platformMigrationGovernanceApproveResponse>(getPlatformMigrationGovernanceApproveUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformMigrationGovernanceApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceApprove>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformMigrationGovernanceApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformMigrationGovernanceApprove>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformMigrationGovernanceApprove(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformMigrationGovernanceApproveMutationResult = NonNullable<Awaited<ReturnType<typeof platformMigrationGovernanceApprove>>>

    export type PlatformMigrationGovernanceApproveMutationError = unknown

    export const usePlatformMigrationGovernanceApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceApprove>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformMigrationGovernanceApprove>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformMigrationGovernanceApproveMutationOptions(options), queryClient);
    }

export type platformMigrationGovernanceRequestPostResponse201 = {
  data: void
  status: 201
}

export type platformMigrationGovernanceRequestPostResponseSuccess = (platformMigrationGovernanceRequestPostResponse201) & {
  headers: Headers;
};
;

export type platformMigrationGovernanceRequestPostResponse = (platformMigrationGovernanceRequestPostResponseSuccess)

export const getPlatformMigrationGovernanceRequestPostUrl = (id: string,
    ordinal: string,) => {




  return `/api/platform/migration-governance/${id}/cohorts/${ordinal}/run`
}

export const platformMigrationGovernanceRequestPost = async (id: string,
    ordinal: string, options?: RequestInit): Promise<platformMigrationGovernanceRequestPostResponse> => {

  return customMutator<platformMigrationGovernanceRequestPostResponse>(getPlatformMigrationGovernanceRequestPostUrl(id,ordinal),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformMigrationGovernanceRequestPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost>>, TError,{id: string;ordinal: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost>>, TError,{id: string;ordinal: string}, TContext> => {

const mutationKey = ['platformMigrationGovernanceRequestPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost>>, {id: string;ordinal: string}> = (props) => {
          const {id,ordinal} = props ?? {};

          return  platformMigrationGovernanceRequestPost(id,ordinal,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformMigrationGovernanceRequestPostMutationResult = NonNullable<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost>>>

    export type PlatformMigrationGovernanceRequestPostMutationError = unknown

    export const usePlatformMigrationGovernanceRequestPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost>>, TError,{id: string;ordinal: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost>>,
        TError,
        {id: string;ordinal: string},
        TContext
      > => {
      return useMutation(getPlatformMigrationGovernanceRequestPostMutationOptions(options), queryClient);
    }

export type platformMigrationGovernanceRequestPost1Response201 = {
  data: void
  status: 201
}

export type platformMigrationGovernanceRequestPost1ResponseSuccess = (platformMigrationGovernanceRequestPost1Response201) & {
  headers: Headers;
};
;

export type platformMigrationGovernanceRequestPost1Response = (platformMigrationGovernanceRequestPost1ResponseSuccess)

export const getPlatformMigrationGovernanceRequestPost1Url = (id: string,) => {




  return `/api/platform/migration-governance/${id}/rollbacks`
}

export const platformMigrationGovernanceRequestPost1 = async (id: string, options?: RequestInit): Promise<platformMigrationGovernanceRequestPost1Response> => {

  return customMutator<platformMigrationGovernanceRequestPost1Response>(getPlatformMigrationGovernanceRequestPost1Url(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformMigrationGovernanceRequestPost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost1>>, TError,{id: string}, TContext> => {

const mutationKey = ['platformMigrationGovernanceRequestPost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost1>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  platformMigrationGovernanceRequestPost1(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformMigrationGovernanceRequestPost1MutationResult = NonNullable<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost1>>>

    export type PlatformMigrationGovernanceRequestPost1MutationError = unknown

    export const usePlatformMigrationGovernanceRequestPost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost1>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformMigrationGovernanceRequestPost1>>,
        TError,
        {id: string},
        TContext
      > => {
      return useMutation(getPlatformMigrationGovernanceRequestPost1MutationOptions(options), queryClient);
    }

export type platformMigrationGovernanceApprovePostResponse201 = {
  data: void
  status: 201
}

export type platformMigrationGovernanceApprovePostResponseSuccess = (platformMigrationGovernanceApprovePostResponse201) & {
  headers: Headers;
};
;

export type platformMigrationGovernanceApprovePostResponse = (platformMigrationGovernanceApprovePostResponseSuccess)

export const getPlatformMigrationGovernanceApprovePostUrl = (id: string,
    rollbackId: string,) => {




  return `/api/platform/migration-governance/${id}/rollbacks/${rollbackId}/approvals`
}

export const platformMigrationGovernanceApprovePost = async (id: string,
    rollbackId: string, options?: RequestInit): Promise<platformMigrationGovernanceApprovePostResponse> => {

  return customMutator<platformMigrationGovernanceApprovePostResponse>(getPlatformMigrationGovernanceApprovePostUrl(id,rollbackId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPlatformMigrationGovernanceApprovePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceApprovePost>>, TError,{id: string;rollbackId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceApprovePost>>, TError,{id: string;rollbackId: string}, TContext> => {

const mutationKey = ['platformMigrationGovernanceApprovePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof platformMigrationGovernanceApprovePost>>, {id: string;rollbackId: string}> = (props) => {
          const {id,rollbackId} = props ?? {};

          return  platformMigrationGovernanceApprovePost(id,rollbackId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PlatformMigrationGovernanceApprovePostMutationResult = NonNullable<Awaited<ReturnType<typeof platformMigrationGovernanceApprovePost>>>

    export type PlatformMigrationGovernanceApprovePostMutationError = unknown

    export const usePlatformMigrationGovernanceApprovePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof platformMigrationGovernanceApprovePost>>, TError,{id: string;rollbackId: string}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof platformMigrationGovernanceApprovePost>>,
        TError,
        {id: string;rollbackId: string},
        TContext
      > => {
      return useMutation(getPlatformMigrationGovernanceApprovePostMutationOptions(options), queryClient);
    }

export type accountingGovernanceReadResponse200 = {
  data: void
  status: 200
}

export type accountingGovernanceReadResponseSuccess = (accountingGovernanceReadResponse200) & {
  headers: Headers;
};
;

export type accountingGovernanceReadResponse = (accountingGovernanceReadResponseSuccess)

export const getAccountingGovernanceReadUrl = () => {




  return `/api/organization-governance/accounting/results`
}

export const accountingGovernanceRead = async ( options?: RequestInit): Promise<accountingGovernanceReadResponse> => {

  return customMutator<accountingGovernanceReadResponse>(getAccountingGovernanceReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAccountingGovernanceReadQueryKey = () => {
    return [
    `/api/organization-governance/accounting/results`
    ] as const;
    }


export const getAccountingGovernanceReadQueryOptions = <TData = Awaited<ReturnType<typeof accountingGovernanceRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAccountingGovernanceReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof accountingGovernanceRead>>> = ({ signal }) => accountingGovernanceRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof accountingGovernanceRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AccountingGovernanceReadQueryResult = NonNullable<Awaited<ReturnType<typeof accountingGovernanceRead>>>
export type AccountingGovernanceReadQueryError = unknown


export function useAccountingGovernanceRead<TData = Awaited<ReturnType<typeof accountingGovernanceRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingGovernanceRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof accountingGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingGovernanceRead<TData = Awaited<ReturnType<typeof accountingGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingGovernanceRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountingGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof accountingGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAccountingGovernanceRead<TData = Awaited<ReturnType<typeof accountingGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useAccountingGovernanceRead<TData = Awaited<ReturnType<typeof accountingGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof accountingGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAccountingGovernanceReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type accountingGovernanceRecordResponse201 = {
  data: void
  status: 201
}

export type accountingGovernanceRecordResponseSuccess = (accountingGovernanceRecordResponse201) & {
  headers: Headers;
};
;

export type accountingGovernanceRecordResponse = (accountingGovernanceRecordResponseSuccess)

export const getAccountingGovernanceRecordUrl = () => {




  return `/api/organization-governance/accounting/classifications`
}

export const accountingGovernanceRecord = async (accountingGovernanceRecordBody: AccountingGovernanceRecordBody, options?: RequestInit): Promise<accountingGovernanceRecordResponse> => {

  return customMutator<accountingGovernanceRecordResponse>(getAccountingGovernanceRecordUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(accountingGovernanceRecordBody)
  }
);}





export const getAccountingGovernanceRecordMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecord>>, TError,{data: AccountingGovernanceRecordBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecord>>, TError,{data: AccountingGovernanceRecordBody}, TContext> => {

const mutationKey = ['accountingGovernanceRecord'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingGovernanceRecord>>, {data: AccountingGovernanceRecordBody}> = (props) => {
          const {data} = props ?? {};

          return  accountingGovernanceRecord(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingGovernanceRecordMutationResult = NonNullable<Awaited<ReturnType<typeof accountingGovernanceRecord>>>
    export type AccountingGovernanceRecordMutationBody = AccountingGovernanceRecordBody
    export type AccountingGovernanceRecordMutationError = unknown

    export const useAccountingGovernanceRecord = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecord>>, TError,{data: AccountingGovernanceRecordBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingGovernanceRecord>>,
        TError,
        {data: AccountingGovernanceRecordBody},
        TContext
      > => {
      return useMutation(getAccountingGovernanceRecordMutationOptions(options), queryClient);
    }

export type accountingGovernanceRecordPostResponse201 = {
  data: void
  status: 201
}

export type accountingGovernanceRecordPostResponseSuccess = (accountingGovernanceRecordPostResponse201) & {
  headers: Headers;
};
;

export type accountingGovernanceRecordPostResponse = (accountingGovernanceRecordPostResponseSuccess)

export const getAccountingGovernanceRecordPostUrl = () => {




  return `/api/organization-governance/accounting/expected-credit-loss-runs`
}

export const accountingGovernanceRecordPost = async (accountingGovernanceRecordPostBody: AccountingGovernanceRecordPostBody, options?: RequestInit): Promise<accountingGovernanceRecordPostResponse> => {

  return customMutator<accountingGovernanceRecordPostResponse>(getAccountingGovernanceRecordPostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(accountingGovernanceRecordPostBody)
  }
);}





export const getAccountingGovernanceRecordPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecordPost>>, TError,{data: AccountingGovernanceRecordPostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecordPost>>, TError,{data: AccountingGovernanceRecordPostBody}, TContext> => {

const mutationKey = ['accountingGovernanceRecordPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingGovernanceRecordPost>>, {data: AccountingGovernanceRecordPostBody}> = (props) => {
          const {data} = props ?? {};

          return  accountingGovernanceRecordPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingGovernanceRecordPostMutationResult = NonNullable<Awaited<ReturnType<typeof accountingGovernanceRecordPost>>>
    export type AccountingGovernanceRecordPostMutationBody = AccountingGovernanceRecordPostBody
    export type AccountingGovernanceRecordPostMutationError = unknown

    export const useAccountingGovernanceRecordPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecordPost>>, TError,{data: AccountingGovernanceRecordPostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingGovernanceRecordPost>>,
        TError,
        {data: AccountingGovernanceRecordPostBody},
        TContext
      > => {
      return useMutation(getAccountingGovernanceRecordPostMutationOptions(options), queryClient);
    }

export type accountingGovernanceRecordPost1Response201 = {
  data: void
  status: 201
}

export type accountingGovernanceRecordPost1ResponseSuccess = (accountingGovernanceRecordPost1Response201) & {
  headers: Headers;
};
;

export type accountingGovernanceRecordPost1Response = (accountingGovernanceRecordPost1ResponseSuccess)

export const getAccountingGovernanceRecordPost1Url = () => {




  return `/api/organization-governance/accounting/fair-value-measurements`
}

export const accountingGovernanceRecordPost1 = async (accountingGovernanceRecordPost1Body: AccountingGovernanceRecordPost1Body, options?: RequestInit): Promise<accountingGovernanceRecordPost1Response> => {

  return customMutator<accountingGovernanceRecordPost1Response>(getAccountingGovernanceRecordPost1Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(accountingGovernanceRecordPost1Body)
  }
);}





export const getAccountingGovernanceRecordPost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecordPost1>>, TError,{data: AccountingGovernanceRecordPost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecordPost1>>, TError,{data: AccountingGovernanceRecordPost1Body}, TContext> => {

const mutationKey = ['accountingGovernanceRecordPost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingGovernanceRecordPost1>>, {data: AccountingGovernanceRecordPost1Body}> = (props) => {
          const {data} = props ?? {};

          return  accountingGovernanceRecordPost1(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingGovernanceRecordPost1MutationResult = NonNullable<Awaited<ReturnType<typeof accountingGovernanceRecordPost1>>>
    export type AccountingGovernanceRecordPost1MutationBody = AccountingGovernanceRecordPost1Body
    export type AccountingGovernanceRecordPost1MutationError = unknown

    export const useAccountingGovernanceRecordPost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecordPost1>>, TError,{data: AccountingGovernanceRecordPost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingGovernanceRecordPost1>>,
        TError,
        {data: AccountingGovernanceRecordPost1Body},
        TContext
      > => {
      return useMutation(getAccountingGovernanceRecordPost1MutationOptions(options), queryClient);
    }

export type accountingGovernanceRecordPost2Response201 = {
  data: void
  status: 201
}

export type accountingGovernanceRecordPost2ResponseSuccess = (accountingGovernanceRecordPost2Response201) & {
  headers: Headers;
};
;

export type accountingGovernanceRecordPost2Response = (accountingGovernanceRecordPost2ResponseSuccess)

export const getAccountingGovernanceRecordPost2Url = () => {




  return `/api/organization-governance/accounting/disclosures`
}

export const accountingGovernanceRecordPost2 = async (accountingGovernanceRecordPost2Body: AccountingGovernanceRecordPost2Body, options?: RequestInit): Promise<accountingGovernanceRecordPost2Response> => {

  return customMutator<accountingGovernanceRecordPost2Response>(getAccountingGovernanceRecordPost2Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(accountingGovernanceRecordPost2Body)
  }
);}





export const getAccountingGovernanceRecordPost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecordPost2>>, TError,{data: AccountingGovernanceRecordPost2Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecordPost2>>, TError,{data: AccountingGovernanceRecordPost2Body}, TContext> => {

const mutationKey = ['accountingGovernanceRecordPost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof accountingGovernanceRecordPost2>>, {data: AccountingGovernanceRecordPost2Body}> = (props) => {
          const {data} = props ?? {};

          return  accountingGovernanceRecordPost2(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AccountingGovernanceRecordPost2MutationResult = NonNullable<Awaited<ReturnType<typeof accountingGovernanceRecordPost2>>>
    export type AccountingGovernanceRecordPost2MutationBody = AccountingGovernanceRecordPost2Body
    export type AccountingGovernanceRecordPost2MutationError = unknown

    export const useAccountingGovernanceRecordPost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof accountingGovernanceRecordPost2>>, TError,{data: AccountingGovernanceRecordPost2Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof accountingGovernanceRecordPost2>>,
        TError,
        {data: AccountingGovernanceRecordPost2Body},
        TContext
      > => {
      return useMutation(getAccountingGovernanceRecordPost2MutationOptions(options), queryClient);
    }

export type complianceGovernanceReadResponse200 = {
  data: void
  status: 200
}

export type complianceGovernanceReadResponseSuccess = (complianceGovernanceReadResponse200) & {
  headers: Headers;
};
;

export type complianceGovernanceReadResponse = (complianceGovernanceReadResponseSuccess)

export const getComplianceGovernanceReadUrl = () => {




  return `/api/organization-governance/compliance/related-parties`
}

export const complianceGovernanceRead = async ( options?: RequestInit): Promise<complianceGovernanceReadResponse> => {

  return customMutator<complianceGovernanceReadResponse>(getComplianceGovernanceReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getComplianceGovernanceReadQueryKey = () => {
    return [
    `/api/organization-governance/compliance/related-parties`
    ] as const;
    }


export const getComplianceGovernanceReadQueryOptions = <TData = Awaited<ReturnType<typeof complianceGovernanceRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getComplianceGovernanceReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof complianceGovernanceRead>>> = ({ signal }) => complianceGovernanceRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof complianceGovernanceRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ComplianceGovernanceReadQueryResult = NonNullable<Awaited<ReturnType<typeof complianceGovernanceRead>>>
export type ComplianceGovernanceReadQueryError = unknown


export function useComplianceGovernanceRead<TData = Awaited<ReturnType<typeof complianceGovernanceRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceGovernanceRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof complianceGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof complianceGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useComplianceGovernanceRead<TData = Awaited<ReturnType<typeof complianceGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceGovernanceRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof complianceGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof complianceGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useComplianceGovernanceRead<TData = Awaited<ReturnType<typeof complianceGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useComplianceGovernanceRead<TData = Awaited<ReturnType<typeof complianceGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof complianceGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getComplianceGovernanceReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type complianceGovernanceRecordResponse201 = {
  data: void
  status: 201
}

export type complianceGovernanceRecordResponseSuccess = (complianceGovernanceRecordResponse201) & {
  headers: Headers;
};
;

export type complianceGovernanceRecordResponse = (complianceGovernanceRecordResponseSuccess)

export const getComplianceGovernanceRecordUrl = () => {




  return `/api/organization-governance/compliance/related-parties`
}

export const complianceGovernanceRecord = async (complianceGovernanceRecordBody: ComplianceGovernanceRecordBody, options?: RequestInit): Promise<complianceGovernanceRecordResponse> => {

  return customMutator<complianceGovernanceRecordResponse>(getComplianceGovernanceRecordUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(complianceGovernanceRecordBody)
  }
);}





export const getComplianceGovernanceRecordMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceRecord>>, TError,{data: ComplianceGovernanceRecordBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceRecord>>, TError,{data: ComplianceGovernanceRecordBody}, TContext> => {

const mutationKey = ['complianceGovernanceRecord'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceGovernanceRecord>>, {data: ComplianceGovernanceRecordBody}> = (props) => {
          const {data} = props ?? {};

          return  complianceGovernanceRecord(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceGovernanceRecordMutationResult = NonNullable<Awaited<ReturnType<typeof complianceGovernanceRecord>>>
    export type ComplianceGovernanceRecordMutationBody = ComplianceGovernanceRecordBody
    export type ComplianceGovernanceRecordMutationError = unknown

    export const useComplianceGovernanceRecord = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceRecord>>, TError,{data: ComplianceGovernanceRecordBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceGovernanceRecord>>,
        TError,
        {data: ComplianceGovernanceRecordBody},
        TContext
      > => {
      return useMutation(getComplianceGovernanceRecordMutationOptions(options), queryClient);
    }

export type complianceGovernanceApproveResponse201 = {
  data: void
  status: 201
}

export type complianceGovernanceApproveResponseSuccess = (complianceGovernanceApproveResponse201) & {
  headers: Headers;
};
;

export type complianceGovernanceApproveResponse = (complianceGovernanceApproveResponseSuccess)

export const getComplianceGovernanceApproveUrl = () => {




  return `/api/organization-governance/compliance/conflict-decisions`
}

export const complianceGovernanceApprove = async (complianceGovernanceApproveBody: ComplianceGovernanceApproveBody, options?: RequestInit): Promise<complianceGovernanceApproveResponse> => {

  return customMutator<complianceGovernanceApproveResponse>(getComplianceGovernanceApproveUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(complianceGovernanceApproveBody)
  }
);}





export const getComplianceGovernanceApproveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceApprove>>, TError,{data: ComplianceGovernanceApproveBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceApprove>>, TError,{data: ComplianceGovernanceApproveBody}, TContext> => {

const mutationKey = ['complianceGovernanceApprove'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceGovernanceApprove>>, {data: ComplianceGovernanceApproveBody}> = (props) => {
          const {data} = props ?? {};

          return  complianceGovernanceApprove(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceGovernanceApproveMutationResult = NonNullable<Awaited<ReturnType<typeof complianceGovernanceApprove>>>
    export type ComplianceGovernanceApproveMutationBody = ComplianceGovernanceApproveBody
    export type ComplianceGovernanceApproveMutationError = unknown

    export const useComplianceGovernanceApprove = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceApprove>>, TError,{data: ComplianceGovernanceApproveBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceGovernanceApprove>>,
        TError,
        {data: ComplianceGovernanceApproveBody},
        TContext
      > => {
      return useMutation(getComplianceGovernanceApproveMutationOptions(options), queryClient);
    }

export type complianceGovernanceRecordPostResponse201 = {
  data: void
  status: 201
}

export type complianceGovernanceRecordPostResponseSuccess = (complianceGovernanceRecordPostResponse201) & {
  headers: Headers;
};
;

export type complianceGovernanceRecordPostResponse = (complianceGovernanceRecordPostResponseSuccess)

export const getComplianceGovernanceRecordPostUrl = () => {




  return `/api/organization-governance/compliance/screening-list-snapshots`
}

export const complianceGovernanceRecordPost = async (complianceGovernanceRecordPostBody: ComplianceGovernanceRecordPostBody, options?: RequestInit): Promise<complianceGovernanceRecordPostResponse> => {

  return customMutator<complianceGovernanceRecordPostResponse>(getComplianceGovernanceRecordPostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(complianceGovernanceRecordPostBody)
  }
);}





export const getComplianceGovernanceRecordPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceRecordPost>>, TError,{data: ComplianceGovernanceRecordPostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceRecordPost>>, TError,{data: ComplianceGovernanceRecordPostBody}, TContext> => {

const mutationKey = ['complianceGovernanceRecordPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceGovernanceRecordPost>>, {data: ComplianceGovernanceRecordPostBody}> = (props) => {
          const {data} = props ?? {};

          return  complianceGovernanceRecordPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceGovernanceRecordPostMutationResult = NonNullable<Awaited<ReturnType<typeof complianceGovernanceRecordPost>>>
    export type ComplianceGovernanceRecordPostMutationBody = ComplianceGovernanceRecordPostBody
    export type ComplianceGovernanceRecordPostMutationError = unknown

    export const useComplianceGovernanceRecordPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceRecordPost>>, TError,{data: ComplianceGovernanceRecordPostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceGovernanceRecordPost>>,
        TError,
        {data: ComplianceGovernanceRecordPostBody},
        TContext
      > => {
      return useMutation(getComplianceGovernanceRecordPostMutationOptions(options), queryClient);
    }

export type complianceGovernanceApprovePostResponse201 = {
  data: void
  status: 201
}

export type complianceGovernanceApprovePostResponseSuccess = (complianceGovernanceApprovePostResponse201) & {
  headers: Headers;
};
;

export type complianceGovernanceApprovePostResponse = (complianceGovernanceApprovePostResponseSuccess)

export const getComplianceGovernanceApprovePostUrl = () => {




  return `/api/organization-governance/compliance/screening-decisions`
}

export const complianceGovernanceApprovePost = async (complianceGovernanceApprovePostBody: ComplianceGovernanceApprovePostBody, options?: RequestInit): Promise<complianceGovernanceApprovePostResponse> => {

  return customMutator<complianceGovernanceApprovePostResponse>(getComplianceGovernanceApprovePostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(complianceGovernanceApprovePostBody)
  }
);}





export const getComplianceGovernanceApprovePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceApprovePost>>, TError,{data: ComplianceGovernanceApprovePostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceApprovePost>>, TError,{data: ComplianceGovernanceApprovePostBody}, TContext> => {

const mutationKey = ['complianceGovernanceApprovePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceGovernanceApprovePost>>, {data: ComplianceGovernanceApprovePostBody}> = (props) => {
          const {data} = props ?? {};

          return  complianceGovernanceApprovePost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceGovernanceApprovePostMutationResult = NonNullable<Awaited<ReturnType<typeof complianceGovernanceApprovePost>>>
    export type ComplianceGovernanceApprovePostMutationBody = ComplianceGovernanceApprovePostBody
    export type ComplianceGovernanceApprovePostMutationError = unknown

    export const useComplianceGovernanceApprovePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceApprovePost>>, TError,{data: ComplianceGovernanceApprovePostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceGovernanceApprovePost>>,
        TError,
        {data: ComplianceGovernanceApprovePostBody},
        TContext
      > => {
      return useMutation(getComplianceGovernanceApprovePostMutationOptions(options), queryClient);
    }

export type complianceGovernanceRecordPost1Response201 = {
  data: void
  status: 201
}

export type complianceGovernanceRecordPost1ResponseSuccess = (complianceGovernanceRecordPost1Response201) & {
  headers: Headers;
};
;

export type complianceGovernanceRecordPost1Response = (complianceGovernanceRecordPost1ResponseSuccess)

export const getComplianceGovernanceRecordPost1Url = () => {




  return `/api/organization-governance/compliance/regulatory-reports`
}

export const complianceGovernanceRecordPost1 = async (complianceGovernanceRecordPost1Body: ComplianceGovernanceRecordPost1Body, options?: RequestInit): Promise<complianceGovernanceRecordPost1Response> => {

  return customMutator<complianceGovernanceRecordPost1Response>(getComplianceGovernanceRecordPost1Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(complianceGovernanceRecordPost1Body)
  }
);}





export const getComplianceGovernanceRecordPost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceRecordPost1>>, TError,{data: ComplianceGovernanceRecordPost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceRecordPost1>>, TError,{data: ComplianceGovernanceRecordPost1Body}, TContext> => {

const mutationKey = ['complianceGovernanceRecordPost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceGovernanceRecordPost1>>, {data: ComplianceGovernanceRecordPost1Body}> = (props) => {
          const {data} = props ?? {};

          return  complianceGovernanceRecordPost1(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceGovernanceRecordPost1MutationResult = NonNullable<Awaited<ReturnType<typeof complianceGovernanceRecordPost1>>>
    export type ComplianceGovernanceRecordPost1MutationBody = ComplianceGovernanceRecordPost1Body
    export type ComplianceGovernanceRecordPost1MutationError = unknown

    export const useComplianceGovernanceRecordPost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceRecordPost1>>, TError,{data: ComplianceGovernanceRecordPost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceGovernanceRecordPost1>>,
        TError,
        {data: ComplianceGovernanceRecordPost1Body},
        TContext
      > => {
      return useMutation(getComplianceGovernanceRecordPost1MutationOptions(options), queryClient);
    }

export type complianceGovernanceApprovePatch1Response200 = {
  data: void
  status: 200
}

export type complianceGovernanceApprovePatch1ResponseSuccess = (complianceGovernanceApprovePatch1Response200) & {
  headers: Headers;
};
;

export type complianceGovernanceApprovePatch1Response = (complianceGovernanceApprovePatch1ResponseSuccess)

export const getComplianceGovernanceApprovePatch1Url = (reportId: string,) => {




  return `/api/organization-governance/compliance/regulatory-reports/${reportId}`
}

export const complianceGovernanceApprovePatch1 = async (reportId: string,
    complianceGovernanceApprovePatch1Body: ComplianceGovernanceApprovePatch1Body, options?: RequestInit): Promise<complianceGovernanceApprovePatch1Response> => {

  return customMutator<complianceGovernanceApprovePatch1Response>(getComplianceGovernanceApprovePatch1Url(reportId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(complianceGovernanceApprovePatch1Body)
  }
);}





export const getComplianceGovernanceApprovePatch1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceApprovePatch1>>, TError,{reportId: string;data: ComplianceGovernanceApprovePatch1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceApprovePatch1>>, TError,{reportId: string;data: ComplianceGovernanceApprovePatch1Body}, TContext> => {

const mutationKey = ['complianceGovernanceApprovePatch1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof complianceGovernanceApprovePatch1>>, {reportId: string;data: ComplianceGovernanceApprovePatch1Body}> = (props) => {
          const {reportId,data} = props ?? {};

          return  complianceGovernanceApprovePatch1(reportId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ComplianceGovernanceApprovePatch1MutationResult = NonNullable<Awaited<ReturnType<typeof complianceGovernanceApprovePatch1>>>
    export type ComplianceGovernanceApprovePatch1MutationBody = ComplianceGovernanceApprovePatch1Body
    export type ComplianceGovernanceApprovePatch1MutationError = unknown

    export const useComplianceGovernanceApprovePatch1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof complianceGovernanceApprovePatch1>>, TError,{reportId: string;data: ComplianceGovernanceApprovePatch1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof complianceGovernanceApprovePatch1>>,
        TError,
        {reportId: string;data: ComplianceGovernanceApprovePatch1Body},
        TContext
      > => {
      return useMutation(getComplianceGovernanceApprovePatch1MutationOptions(options), queryClient);
    }

export type clientsGovernanceReadResponse200 = {
  data: void
  status: 200
}

export type clientsGovernanceReadResponseSuccess = (clientsGovernanceReadResponse200) & {
  headers: Headers;
};
;

export type clientsGovernanceReadResponse = (clientsGovernanceReadResponseSuccess)

export const getClientsGovernanceReadUrl = () => {




  return `/api/organization-governance/clients/agreements`
}

export const clientsGovernanceRead = async ( options?: RequestInit): Promise<clientsGovernanceReadResponse> => {

  return customMutator<clientsGovernanceReadResponse>(getClientsGovernanceReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getClientsGovernanceReadQueryKey = () => {
    return [
    `/api/organization-governance/clients/agreements`
    ] as const;
    }


export const getClientsGovernanceReadQueryOptions = <TData = Awaited<ReturnType<typeof clientsGovernanceRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getClientsGovernanceReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof clientsGovernanceRead>>> = ({ signal }) => clientsGovernanceRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ClientsGovernanceReadQueryResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRead>>>
export type ClientsGovernanceReadQueryError = unknown


export function useClientsGovernanceRead<TData = Awaited<ReturnType<typeof clientsGovernanceRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientsGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof clientsGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientsGovernanceRead<TData = Awaited<ReturnType<typeof clientsGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientsGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof clientsGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientsGovernanceRead<TData = Awaited<ReturnType<typeof clientsGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useClientsGovernanceRead<TData = Awaited<ReturnType<typeof clientsGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getClientsGovernanceReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type clientsGovernanceRecordResponse201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordResponseSuccess = (clientsGovernanceRecordResponse201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordResponse = (clientsGovernanceRecordResponseSuccess)

export const getClientsGovernanceRecordUrl = () => {




  return `/api/organization-governance/clients/agreements`
}

export const clientsGovernanceRecord = async (clientsGovernanceRecordBody: ClientsGovernanceRecordBody, options?: RequestInit): Promise<clientsGovernanceRecordResponse> => {

  return customMutator<clientsGovernanceRecordResponse>(getClientsGovernanceRecordUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordBody)
  }
);}





export const getClientsGovernanceRecordMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecord>>, TError,{data: ClientsGovernanceRecordBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecord>>, TError,{data: ClientsGovernanceRecordBody}, TContext> => {

const mutationKey = ['clientsGovernanceRecord'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecord>>, {data: ClientsGovernanceRecordBody}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecord(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordMutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecord>>>
    export type ClientsGovernanceRecordMutationBody = ClientsGovernanceRecordBody
    export type ClientsGovernanceRecordMutationError = unknown

    export const useClientsGovernanceRecord = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecord>>, TError,{data: ClientsGovernanceRecordBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecord>>,
        TError,
        {data: ClientsGovernanceRecordBody},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordMutationOptions(options), queryClient);
    }

export type clientsGovernanceRecordPostResponse201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPostResponseSuccess = (clientsGovernanceRecordPostResponse201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPostResponse = (clientsGovernanceRecordPostResponseSuccess)

export const getClientsGovernanceRecordPostUrl = () => {




  return `/api/organization-governance/clients/agreements/deliveries`
}

export const clientsGovernanceRecordPost = async (clientsGovernanceRecordPostBody: ClientsGovernanceRecordPostBody, options?: RequestInit): Promise<clientsGovernanceRecordPostResponse> => {

  return customMutator<clientsGovernanceRecordPostResponse>(getClientsGovernanceRecordPostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPostBody)
  }
);}





export const getClientsGovernanceRecordPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost>>, TError,{data: ClientsGovernanceRecordPostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost>>, TError,{data: ClientsGovernanceRecordPostBody}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost>>, {data: ClientsGovernanceRecordPostBody}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecordPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPostMutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost>>>
    export type ClientsGovernanceRecordPostMutationBody = ClientsGovernanceRecordPostBody
    export type ClientsGovernanceRecordPostMutationError = unknown

    export const useClientsGovernanceRecordPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost>>, TError,{data: ClientsGovernanceRecordPostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost>>,
        TError,
        {data: ClientsGovernanceRecordPostBody},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPostMutationOptions(options), queryClient);
    }

export type clientsGovernanceRecordPost1Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost1ResponseSuccess = (clientsGovernanceRecordPost1Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost1Response = (clientsGovernanceRecordPost1ResponseSuccess)

export const getClientsGovernanceRecordPost1Url = () => {




  return `/api/organization-governance/clients/agreements/decisions`
}

export const clientsGovernanceRecordPost1 = async (clientsGovernanceRecordPost1Body: ClientsGovernanceRecordPost1Body, options?: RequestInit): Promise<clientsGovernanceRecordPost1Response> => {

  return customMutator<clientsGovernanceRecordPost1Response>(getClientsGovernanceRecordPost1Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost1Body)
  }
);}





export const getClientsGovernanceRecordPost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost1>>, TError,{data: ClientsGovernanceRecordPost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost1>>, TError,{data: ClientsGovernanceRecordPost1Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost1>>, {data: ClientsGovernanceRecordPost1Body}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecordPost1(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost1MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost1>>>
    export type ClientsGovernanceRecordPost1MutationBody = ClientsGovernanceRecordPost1Body
    export type ClientsGovernanceRecordPost1MutationError = unknown

    export const useClientsGovernanceRecordPost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost1>>, TError,{data: ClientsGovernanceRecordPost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost1>>,
        TError,
        {data: ClientsGovernanceRecordPost1Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost1MutationOptions(options), queryClient);
    }

export type clientsGovernanceRecordPost2Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost2ResponseSuccess = (clientsGovernanceRecordPost2Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost2Response = (clientsGovernanceRecordPost2ResponseSuccess)

export const getClientsGovernanceRecordPost2Url = () => {




  return `/api/organization-governance/clients/agreements/amendments`
}

export const clientsGovernanceRecordPost2 = async (clientsGovernanceRecordPost2Body: ClientsGovernanceRecordPost2Body, options?: RequestInit): Promise<clientsGovernanceRecordPost2Response> => {

  return customMutator<clientsGovernanceRecordPost2Response>(getClientsGovernanceRecordPost2Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost2Body)
  }
);}





export const getClientsGovernanceRecordPost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost2>>, TError,{data: ClientsGovernanceRecordPost2Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost2>>, TError,{data: ClientsGovernanceRecordPost2Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost2>>, {data: ClientsGovernanceRecordPost2Body}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecordPost2(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost2MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost2>>>
    export type ClientsGovernanceRecordPost2MutationBody = ClientsGovernanceRecordPost2Body
    export type ClientsGovernanceRecordPost2MutationError = unknown

    export const useClientsGovernanceRecordPost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost2>>, TError,{data: ClientsGovernanceRecordPost2Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost2>>,
        TError,
        {data: ClientsGovernanceRecordPost2Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost2MutationOptions(options), queryClient);
    }

export type clientsGovernanceRecordPost3Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost3ResponseSuccess = (clientsGovernanceRecordPost3Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost3Response = (clientsGovernanceRecordPost3ResponseSuccess)

export const getClientsGovernanceRecordPost3Url = () => {




  return `/api/organization-governance/clients/statements`
}

export const clientsGovernanceRecordPost3 = async (clientsGovernanceRecordPost3Body: ClientsGovernanceRecordPost3Body, options?: RequestInit): Promise<clientsGovernanceRecordPost3Response> => {

  return customMutator<clientsGovernanceRecordPost3Response>(getClientsGovernanceRecordPost3Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost3Body)
  }
);}





export const getClientsGovernanceRecordPost3MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost3>>, TError,{data: ClientsGovernanceRecordPost3Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost3>>, TError,{data: ClientsGovernanceRecordPost3Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost3'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost3>>, {data: ClientsGovernanceRecordPost3Body}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecordPost3(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost3MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost3>>>
    export type ClientsGovernanceRecordPost3MutationBody = ClientsGovernanceRecordPost3Body
    export type ClientsGovernanceRecordPost3MutationError = unknown

    export const useClientsGovernanceRecordPost3 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost3>>, TError,{data: ClientsGovernanceRecordPost3Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost3>>,
        TError,
        {data: ClientsGovernanceRecordPost3Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost3MutationOptions(options), queryClient);
    }

export type clientsGovernanceReadGetResponse200 = {
  data: void
  status: 200
}

export type clientsGovernanceReadGetResponseSuccess = (clientsGovernanceReadGetResponse200) & {
  headers: Headers;
};
;

export type clientsGovernanceReadGetResponse = (clientsGovernanceReadGetResponseSuccess)

export const getClientsGovernanceReadGetUrl = () => {




  return `/api/organization-governance/clients/statements`
}

export const clientsGovernanceReadGet = async ( options?: RequestInit): Promise<clientsGovernanceReadGetResponse> => {

  return customMutator<clientsGovernanceReadGetResponse>(getClientsGovernanceReadGetUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getClientsGovernanceReadGetQueryKey = () => {
    return [
    `/api/organization-governance/clients/statements`
    ] as const;
    }


export const getClientsGovernanceReadGetQueryOptions = <TData = Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getClientsGovernanceReadGetQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof clientsGovernanceReadGet>>> = ({ signal }) => clientsGovernanceReadGet({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ClientsGovernanceReadGetQueryResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceReadGet>>>
export type ClientsGovernanceReadGetQueryError = unknown


export function useClientsGovernanceReadGet<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientsGovernanceReadGet>>,
          TError,
          Awaited<ReturnType<typeof clientsGovernanceReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientsGovernanceReadGet<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientsGovernanceReadGet>>,
          TError,
          Awaited<ReturnType<typeof clientsGovernanceReadGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientsGovernanceReadGet<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useClientsGovernanceReadGet<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getClientsGovernanceReadGetQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type clientsGovernanceReadGet1Response200 = {
  data: void
  status: 200
}

export type clientsGovernanceReadGet1ResponseSuccess = (clientsGovernanceReadGet1Response200) & {
  headers: Headers;
};
;

export type clientsGovernanceReadGet1Response = (clientsGovernanceReadGet1ResponseSuccess)

export const getClientsGovernanceReadGet1Url = () => {




  return `/api/organization-governance/clients/statement-schedules`
}

export const clientsGovernanceReadGet1 = async ( options?: RequestInit): Promise<clientsGovernanceReadGet1Response> => {

  return customMutator<clientsGovernanceReadGet1Response>(getClientsGovernanceReadGet1Url(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getClientsGovernanceReadGet1QueryKey = () => {
    return [
    `/api/organization-governance/clients/statement-schedules`
    ] as const;
    }


export const getClientsGovernanceReadGet1QueryOptions = <TData = Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getClientsGovernanceReadGet1QueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof clientsGovernanceReadGet1>>> = ({ signal }) => clientsGovernanceReadGet1({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ClientsGovernanceReadGet1QueryResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceReadGet1>>>
export type ClientsGovernanceReadGet1QueryError = unknown


export function useClientsGovernanceReadGet1<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientsGovernanceReadGet1>>,
          TError,
          Awaited<ReturnType<typeof clientsGovernanceReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientsGovernanceReadGet1<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientsGovernanceReadGet1>>,
          TError,
          Awaited<ReturnType<typeof clientsGovernanceReadGet1>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientsGovernanceReadGet1<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useClientsGovernanceReadGet1<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet1>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getClientsGovernanceReadGet1QueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type clientsGovernanceRecordPost4Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost4ResponseSuccess = (clientsGovernanceRecordPost4Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost4Response = (clientsGovernanceRecordPost4ResponseSuccess)

export const getClientsGovernanceRecordPost4Url = () => {




  return `/api/organization-governance/clients/statement-schedules`
}

export const clientsGovernanceRecordPost4 = async (clientsGovernanceRecordPost4Body: ClientsGovernanceRecordPost4Body, options?: RequestInit): Promise<clientsGovernanceRecordPost4Response> => {

  return customMutator<clientsGovernanceRecordPost4Response>(getClientsGovernanceRecordPost4Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost4Body)
  }
);}





export const getClientsGovernanceRecordPost4MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost4>>, TError,{data: ClientsGovernanceRecordPost4Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost4>>, TError,{data: ClientsGovernanceRecordPost4Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost4'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost4>>, {data: ClientsGovernanceRecordPost4Body}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecordPost4(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost4MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost4>>>
    export type ClientsGovernanceRecordPost4MutationBody = ClientsGovernanceRecordPost4Body
    export type ClientsGovernanceRecordPost4MutationError = unknown

    export const useClientsGovernanceRecordPost4 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost4>>, TError,{data: ClientsGovernanceRecordPost4Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost4>>,
        TError,
        {data: ClientsGovernanceRecordPost4Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost4MutationOptions(options), queryClient);
    }

export type clientsGovernanceRecordPost5Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost5ResponseSuccess = (clientsGovernanceRecordPost5Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost5Response = (clientsGovernanceRecordPost5ResponseSuccess)

export const getClientsGovernanceRecordPost5Url = (scheduleId: string,) => {




  return `/api/organization-governance/clients/statement-schedules/${scheduleId}/approvals`
}

export const clientsGovernanceRecordPost5 = async (scheduleId: string,
    clientsGovernanceRecordPost5Body: ClientsGovernanceRecordPost5Body, options?: RequestInit): Promise<clientsGovernanceRecordPost5Response> => {

  return customMutator<clientsGovernanceRecordPost5Response>(getClientsGovernanceRecordPost5Url(scheduleId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost5Body)
  }
);}





export const getClientsGovernanceRecordPost5MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost5>>, TError,{scheduleId: string;data: ClientsGovernanceRecordPost5Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost5>>, TError,{scheduleId: string;data: ClientsGovernanceRecordPost5Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost5'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost5>>, {scheduleId: string;data: ClientsGovernanceRecordPost5Body}> = (props) => {
          const {scheduleId,data} = props ?? {};

          return  clientsGovernanceRecordPost5(scheduleId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost5MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost5>>>
    export type ClientsGovernanceRecordPost5MutationBody = ClientsGovernanceRecordPost5Body
    export type ClientsGovernanceRecordPost5MutationError = unknown

    export const useClientsGovernanceRecordPost5 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost5>>, TError,{scheduleId: string;data: ClientsGovernanceRecordPost5Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost5>>,
        TError,
        {scheduleId: string;data: ClientsGovernanceRecordPost5Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost5MutationOptions(options), queryClient);
    }

export type clientsGovernanceRecordPost6Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost6ResponseSuccess = (clientsGovernanceRecordPost6Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost6Response = (clientsGovernanceRecordPost6ResponseSuccess)

export const getClientsGovernanceRecordPost6Url = (scheduleId: string,) => {




  return `/api/organization-governance/clients/statement-schedules/${scheduleId}/cancellations`
}

export const clientsGovernanceRecordPost6 = async (scheduleId: string,
    clientsGovernanceRecordPost6Body: ClientsGovernanceRecordPost6Body, options?: RequestInit): Promise<clientsGovernanceRecordPost6Response> => {

  return customMutator<clientsGovernanceRecordPost6Response>(getClientsGovernanceRecordPost6Url(scheduleId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost6Body)
  }
);}





export const getClientsGovernanceRecordPost6MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost6>>, TError,{scheduleId: string;data: ClientsGovernanceRecordPost6Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost6>>, TError,{scheduleId: string;data: ClientsGovernanceRecordPost6Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost6'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost6>>, {scheduleId: string;data: ClientsGovernanceRecordPost6Body}> = (props) => {
          const {scheduleId,data} = props ?? {};

          return  clientsGovernanceRecordPost6(scheduleId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost6MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost6>>>
    export type ClientsGovernanceRecordPost6MutationBody = ClientsGovernanceRecordPost6Body
    export type ClientsGovernanceRecordPost6MutationError = unknown

    export const useClientsGovernanceRecordPost6 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost6>>, TError,{scheduleId: string;data: ClientsGovernanceRecordPost6Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost6>>,
        TError,
        {scheduleId: string;data: ClientsGovernanceRecordPost6Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost6MutationOptions(options), queryClient);
    }

export type clientsGovernanceRecordPost7Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost7ResponseSuccess = (clientsGovernanceRecordPost7Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost7Response = (clientsGovernanceRecordPost7ResponseSuccess)

export const getClientsGovernanceRecordPost7Url = () => {




  return `/api/organization-governance/clients/statements/generations`
}

export const clientsGovernanceRecordPost7 = async (clientsGovernanceRecordPost7Body: ClientsGovernanceRecordPost7Body, options?: RequestInit): Promise<clientsGovernanceRecordPost7Response> => {

  return customMutator<clientsGovernanceRecordPost7Response>(getClientsGovernanceRecordPost7Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost7Body)
  }
);}





export const getClientsGovernanceRecordPost7MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost7>>, TError,{data: ClientsGovernanceRecordPost7Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost7>>, TError,{data: ClientsGovernanceRecordPost7Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost7'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost7>>, {data: ClientsGovernanceRecordPost7Body}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecordPost7(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost7MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost7>>>
    export type ClientsGovernanceRecordPost7MutationBody = ClientsGovernanceRecordPost7Body
    export type ClientsGovernanceRecordPost7MutationError = unknown

    export const useClientsGovernanceRecordPost7 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost7>>, TError,{data: ClientsGovernanceRecordPost7Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost7>>,
        TError,
        {data: ClientsGovernanceRecordPost7Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost7MutationOptions(options), queryClient);
    }

export type clientsGovernanceRecordPost8Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost8ResponseSuccess = (clientsGovernanceRecordPost8Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost8Response = (clientsGovernanceRecordPost8ResponseSuccess)

export const getClientsGovernanceRecordPost8Url = () => {




  return `/api/organization-governance/clients/statements/delivery-attempts`
}

export const clientsGovernanceRecordPost8 = async (clientsGovernanceRecordPost8Body: ClientsGovernanceRecordPost8Body, options?: RequestInit): Promise<clientsGovernanceRecordPost8Response> => {

  return customMutator<clientsGovernanceRecordPost8Response>(getClientsGovernanceRecordPost8Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost8Body)
  }
);}





export const getClientsGovernanceRecordPost8MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost8>>, TError,{data: ClientsGovernanceRecordPost8Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost8>>, TError,{data: ClientsGovernanceRecordPost8Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost8'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost8>>, {data: ClientsGovernanceRecordPost8Body}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecordPost8(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost8MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost8>>>
    export type ClientsGovernanceRecordPost8MutationBody = ClientsGovernanceRecordPost8Body
    export type ClientsGovernanceRecordPost8MutationError = unknown

    export const useClientsGovernanceRecordPost8 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost8>>, TError,{data: ClientsGovernanceRecordPost8Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost8>>,
        TError,
        {data: ClientsGovernanceRecordPost8Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost8MutationOptions(options), queryClient);
    }

export type clientsGovernanceRecordPost9Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost9ResponseSuccess = (clientsGovernanceRecordPost9Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost9Response = (clientsGovernanceRecordPost9ResponseSuccess)

export const getClientsGovernanceRecordPost9Url = () => {




  return `/api/organization-governance/clients/complaints`
}

export const clientsGovernanceRecordPost9 = async (clientsGovernanceRecordPost9Body: ClientsGovernanceRecordPost9Body, options?: RequestInit): Promise<clientsGovernanceRecordPost9Response> => {

  return customMutator<clientsGovernanceRecordPost9Response>(getClientsGovernanceRecordPost9Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost9Body)
  }
);}





export const getClientsGovernanceRecordPost9MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost9>>, TError,{data: ClientsGovernanceRecordPost9Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost9>>, TError,{data: ClientsGovernanceRecordPost9Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost9'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost9>>, {data: ClientsGovernanceRecordPost9Body}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecordPost9(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost9MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost9>>>
    export type ClientsGovernanceRecordPost9MutationBody = ClientsGovernanceRecordPost9Body
    export type ClientsGovernanceRecordPost9MutationError = unknown

    export const useClientsGovernanceRecordPost9 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost9>>, TError,{data: ClientsGovernanceRecordPost9Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost9>>,
        TError,
        {data: ClientsGovernanceRecordPost9Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost9MutationOptions(options), queryClient);
    }

export type clientsGovernanceReadGet2Response200 = {
  data: void
  status: 200
}

export type clientsGovernanceReadGet2ResponseSuccess = (clientsGovernanceReadGet2Response200) & {
  headers: Headers;
};
;

export type clientsGovernanceReadGet2Response = (clientsGovernanceReadGet2ResponseSuccess)

export const getClientsGovernanceReadGet2Url = () => {




  return `/api/organization-governance/clients/complaints`
}

export const clientsGovernanceReadGet2 = async ( options?: RequestInit): Promise<clientsGovernanceReadGet2Response> => {

  return customMutator<clientsGovernanceReadGet2Response>(getClientsGovernanceReadGet2Url(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getClientsGovernanceReadGet2QueryKey = () => {
    return [
    `/api/organization-governance/clients/complaints`
    ] as const;
    }


export const getClientsGovernanceReadGet2QueryOptions = <TData = Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getClientsGovernanceReadGet2QueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof clientsGovernanceReadGet2>>> = ({ signal }) => clientsGovernanceReadGet2({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ClientsGovernanceReadGet2QueryResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceReadGet2>>>
export type ClientsGovernanceReadGet2QueryError = unknown


export function useClientsGovernanceReadGet2<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientsGovernanceReadGet2>>,
          TError,
          Awaited<ReturnType<typeof clientsGovernanceReadGet2>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientsGovernanceReadGet2<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof clientsGovernanceReadGet2>>,
          TError,
          Awaited<ReturnType<typeof clientsGovernanceReadGet2>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useClientsGovernanceReadGet2<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useClientsGovernanceReadGet2<TData = Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof clientsGovernanceReadGet2>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getClientsGovernanceReadGet2QueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type clientsGovernanceRecordPost10Response201 = {
  data: void
  status: 201
}

export type clientsGovernanceRecordPost10ResponseSuccess = (clientsGovernanceRecordPost10Response201) & {
  headers: Headers;
};
;

export type clientsGovernanceRecordPost10Response = (clientsGovernanceRecordPost10ResponseSuccess)

export const getClientsGovernanceRecordPost10Url = () => {




  return `/api/organization-governance/clients/complaints/actions`
}

export const clientsGovernanceRecordPost10 = async (clientsGovernanceRecordPost10Body: ClientsGovernanceRecordPost10Body, options?: RequestInit): Promise<clientsGovernanceRecordPost10Response> => {

  return customMutator<clientsGovernanceRecordPost10Response>(getClientsGovernanceRecordPost10Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clientsGovernanceRecordPost10Body)
  }
);}





export const getClientsGovernanceRecordPost10MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost10>>, TError,{data: ClientsGovernanceRecordPost10Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost10>>, TError,{data: ClientsGovernanceRecordPost10Body}, TContext> => {

const mutationKey = ['clientsGovernanceRecordPost10'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clientsGovernanceRecordPost10>>, {data: ClientsGovernanceRecordPost10Body}> = (props) => {
          const {data} = props ?? {};

          return  clientsGovernanceRecordPost10(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClientsGovernanceRecordPost10MutationResult = NonNullable<Awaited<ReturnType<typeof clientsGovernanceRecordPost10>>>
    export type ClientsGovernanceRecordPost10MutationBody = ClientsGovernanceRecordPost10Body
    export type ClientsGovernanceRecordPost10MutationError = unknown

    export const useClientsGovernanceRecordPost10 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clientsGovernanceRecordPost10>>, TError,{data: ClientsGovernanceRecordPost10Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clientsGovernanceRecordPost10>>,
        TError,
        {data: ClientsGovernanceRecordPost10Body},
        TContext
      > => {
      return useMutation(getClientsGovernanceRecordPost10MutationOptions(options), queryClient);
    }

export type privacyRightsReadResponse200 = {
  data: void
  status: 200
}

export type privacyRightsReadResponseSuccess = (privacyRightsReadResponse200) & {
  headers: Headers;
};
;

export type privacyRightsReadResponse = (privacyRightsReadResponseSuccess)

export const getPrivacyRightsReadUrl = () => {




  return `/api/organization-governance/privacy/requests`
}

export const privacyRightsRead = async ( options?: RequestInit): Promise<privacyRightsReadResponse> => {

  return customMutator<privacyRightsReadResponse>(getPrivacyRightsReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPrivacyRightsReadQueryKey = () => {
    return [
    `/api/organization-governance/privacy/requests`
    ] as const;
    }


export const getPrivacyRightsReadQueryOptions = <TData = Awaited<ReturnType<typeof privacyRightsRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof privacyRightsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPrivacyRightsReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof privacyRightsRead>>> = ({ signal }) => privacyRightsRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof privacyRightsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PrivacyRightsReadQueryResult = NonNullable<Awaited<ReturnType<typeof privacyRightsRead>>>
export type PrivacyRightsReadQueryError = unknown


export function usePrivacyRightsRead<TData = Awaited<ReturnType<typeof privacyRightsRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof privacyRightsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof privacyRightsRead>>,
          TError,
          Awaited<ReturnType<typeof privacyRightsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePrivacyRightsRead<TData = Awaited<ReturnType<typeof privacyRightsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof privacyRightsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof privacyRightsRead>>,
          TError,
          Awaited<ReturnType<typeof privacyRightsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePrivacyRightsRead<TData = Awaited<ReturnType<typeof privacyRightsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof privacyRightsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function usePrivacyRightsRead<TData = Awaited<ReturnType<typeof privacyRightsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof privacyRightsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPrivacyRightsReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type privacyRightsManageResponse201 = {
  data: void
  status: 201
}

export type privacyRightsManageResponseSuccess = (privacyRightsManageResponse201) & {
  headers: Headers;
};
;

export type privacyRightsManageResponse = (privacyRightsManageResponseSuccess)

export const getPrivacyRightsManageUrl = () => {




  return `/api/organization-governance/privacy/requests`
}

export const privacyRightsManage = async (privacyRightsManageBody: PrivacyRightsManageBody, options?: RequestInit): Promise<privacyRightsManageResponse> => {

  return customMutator<privacyRightsManageResponse>(getPrivacyRightsManageUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(privacyRightsManageBody)
  }
);}





export const getPrivacyRightsManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof privacyRightsManage>>, TError,{data: PrivacyRightsManageBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof privacyRightsManage>>, TError,{data: PrivacyRightsManageBody}, TContext> => {

const mutationKey = ['privacyRightsManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof privacyRightsManage>>, {data: PrivacyRightsManageBody}> = (props) => {
          const {data} = props ?? {};

          return  privacyRightsManage(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PrivacyRightsManageMutationResult = NonNullable<Awaited<ReturnType<typeof privacyRightsManage>>>
    export type PrivacyRightsManageMutationBody = PrivacyRightsManageBody
    export type PrivacyRightsManageMutationError = unknown

    export const usePrivacyRightsManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof privacyRightsManage>>, TError,{data: PrivacyRightsManageBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof privacyRightsManage>>,
        TError,
        {data: PrivacyRightsManageBody},
        TContext
      > => {
      return useMutation(getPrivacyRightsManageMutationOptions(options), queryClient);
    }

export type privacyRightsManagePostResponse201 = {
  data: void
  status: 201
}

export type privacyRightsManagePostResponseSuccess = (privacyRightsManagePostResponse201) & {
  headers: Headers;
};
;

export type privacyRightsManagePostResponse = (privacyRightsManagePostResponseSuccess)

export const getPrivacyRightsManagePostUrl = () => {




  return `/api/organization-governance/privacy/decisions`
}

export const privacyRightsManagePost = async (privacyRightsManagePostBody: PrivacyRightsManagePostBody, options?: RequestInit): Promise<privacyRightsManagePostResponse> => {

  return customMutator<privacyRightsManagePostResponse>(getPrivacyRightsManagePostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(privacyRightsManagePostBody)
  }
);}





export const getPrivacyRightsManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof privacyRightsManagePost>>, TError,{data: PrivacyRightsManagePostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof privacyRightsManagePost>>, TError,{data: PrivacyRightsManagePostBody}, TContext> => {

const mutationKey = ['privacyRightsManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof privacyRightsManagePost>>, {data: PrivacyRightsManagePostBody}> = (props) => {
          const {data} = props ?? {};

          return  privacyRightsManagePost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PrivacyRightsManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof privacyRightsManagePost>>>
    export type PrivacyRightsManagePostMutationBody = PrivacyRightsManagePostBody
    export type PrivacyRightsManagePostMutationError = unknown

    export const usePrivacyRightsManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof privacyRightsManagePost>>, TError,{data: PrivacyRightsManagePostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof privacyRightsManagePost>>,
        TError,
        {data: PrivacyRightsManagePostBody},
        TContext
      > => {
      return useMutation(getPrivacyRightsManagePostMutationOptions(options), queryClient);
    }

export type integrationsGovernanceReadResponse200 = {
  data: void
  status: 200
}

export type integrationsGovernanceReadResponseSuccess = (integrationsGovernanceReadResponse200) & {
  headers: Headers;
};
;

export type integrationsGovernanceReadResponse = (integrationsGovernanceReadResponseSuccess)

export const getIntegrationsGovernanceReadUrl = () => {




  return `/api/organization-governance/integrations`
}

export const integrationsGovernanceRead = async ( options?: RequestInit): Promise<integrationsGovernanceReadResponse> => {

  return customMutator<integrationsGovernanceReadResponse>(getIntegrationsGovernanceReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getIntegrationsGovernanceReadQueryKey = () => {
    return [
    `/api/organization-governance/integrations`
    ] as const;
    }


export const getIntegrationsGovernanceReadQueryOptions = <TData = Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getIntegrationsGovernanceReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof integrationsGovernanceRead>>> = ({ signal }) => integrationsGovernanceRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type IntegrationsGovernanceReadQueryResult = NonNullable<Awaited<ReturnType<typeof integrationsGovernanceRead>>>
export type IntegrationsGovernanceReadQueryError = unknown


export function useIntegrationsGovernanceRead<TData = Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof integrationsGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof integrationsGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useIntegrationsGovernanceRead<TData = Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof integrationsGovernanceRead>>,
          TError,
          Awaited<ReturnType<typeof integrationsGovernanceRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useIntegrationsGovernanceRead<TData = Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useIntegrationsGovernanceRead<TData = Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof integrationsGovernanceRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getIntegrationsGovernanceReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type integrationsGovernanceManageResponse201 = {
  data: void
  status: 201
}

export type integrationsGovernanceManageResponseSuccess = (integrationsGovernanceManageResponse201) & {
  headers: Headers;
};
;

export type integrationsGovernanceManageResponse = (integrationsGovernanceManageResponseSuccess)

export const getIntegrationsGovernanceManageUrl = () => {




  return `/api/organization-governance/integrations`
}

export const integrationsGovernanceManage = async (integrationsGovernanceManageBody: IntegrationsGovernanceManageBody, options?: RequestInit): Promise<integrationsGovernanceManageResponse> => {

  return customMutator<integrationsGovernanceManageResponse>(getIntegrationsGovernanceManageUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(integrationsGovernanceManageBody)
  }
);}





export const getIntegrationsGovernanceManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManage>>, TError,{data: IntegrationsGovernanceManageBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManage>>, TError,{data: IntegrationsGovernanceManageBody}, TContext> => {

const mutationKey = ['integrationsGovernanceManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof integrationsGovernanceManage>>, {data: IntegrationsGovernanceManageBody}> = (props) => {
          const {data} = props ?? {};

          return  integrationsGovernanceManage(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type IntegrationsGovernanceManageMutationResult = NonNullable<Awaited<ReturnType<typeof integrationsGovernanceManage>>>
    export type IntegrationsGovernanceManageMutationBody = IntegrationsGovernanceManageBody
    export type IntegrationsGovernanceManageMutationError = unknown

    export const useIntegrationsGovernanceManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManage>>, TError,{data: IntegrationsGovernanceManageBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof integrationsGovernanceManage>>,
        TError,
        {data: IntegrationsGovernanceManageBody},
        TContext
      > => {
      return useMutation(getIntegrationsGovernanceManageMutationOptions(options), queryClient);
    }

export type integrationsGovernanceManagePostResponse201 = {
  data: void
  status: 201
}

export type integrationsGovernanceManagePostResponseSuccess = (integrationsGovernanceManagePostResponse201) & {
  headers: Headers;
};
;

export type integrationsGovernanceManagePostResponse = (integrationsGovernanceManagePostResponseSuccess)

export const getIntegrationsGovernanceManagePostUrl = (connectionId: string,) => {




  return `/api/organization-governance/integrations/${connectionId}/credential-rotations`
}

export const integrationsGovernanceManagePost = async (connectionId: string,
    integrationsGovernanceManagePostBody: IntegrationsGovernanceManagePostBody, options?: RequestInit): Promise<integrationsGovernanceManagePostResponse> => {

  return customMutator<integrationsGovernanceManagePostResponse>(getIntegrationsGovernanceManagePostUrl(connectionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(integrationsGovernanceManagePostBody)
  }
);}





export const getIntegrationsGovernanceManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost>>, TError,{connectionId: string;data: IntegrationsGovernanceManagePostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost>>, TError,{connectionId: string;data: IntegrationsGovernanceManagePostBody}, TContext> => {

const mutationKey = ['integrationsGovernanceManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof integrationsGovernanceManagePost>>, {connectionId: string;data: IntegrationsGovernanceManagePostBody}> = (props) => {
          const {connectionId,data} = props ?? {};

          return  integrationsGovernanceManagePost(connectionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type IntegrationsGovernanceManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof integrationsGovernanceManagePost>>>
    export type IntegrationsGovernanceManagePostMutationBody = IntegrationsGovernanceManagePostBody
    export type IntegrationsGovernanceManagePostMutationError = unknown

    export const useIntegrationsGovernanceManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost>>, TError,{connectionId: string;data: IntegrationsGovernanceManagePostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof integrationsGovernanceManagePost>>,
        TError,
        {connectionId: string;data: IntegrationsGovernanceManagePostBody},
        TContext
      > => {
      return useMutation(getIntegrationsGovernanceManagePostMutationOptions(options), queryClient);
    }

export type integrationsGovernanceManagePost1Response201 = {
  data: void
  status: 201
}

export type integrationsGovernanceManagePost1ResponseSuccess = (integrationsGovernanceManagePost1Response201) & {
  headers: Headers;
};
;

export type integrationsGovernanceManagePost1Response = (integrationsGovernanceManagePost1ResponseSuccess)

export const getIntegrationsGovernanceManagePost1Url = (connectionId: string,
    credentialId: string,) => {




  return `/api/organization-governance/integrations/${connectionId}/credentials/${credentialId}/approvals`
}

export const integrationsGovernanceManagePost1 = async (connectionId: string,
    credentialId: string,
    integrationsGovernanceManagePost1Body: IntegrationsGovernanceManagePost1Body, options?: RequestInit): Promise<integrationsGovernanceManagePost1Response> => {

  return customMutator<integrationsGovernanceManagePost1Response>(getIntegrationsGovernanceManagePost1Url(connectionId,credentialId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(integrationsGovernanceManagePost1Body)
  }
);}





export const getIntegrationsGovernanceManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost1>>, TError,{connectionId: string;credentialId: string;data: IntegrationsGovernanceManagePost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost1>>, TError,{connectionId: string;credentialId: string;data: IntegrationsGovernanceManagePost1Body}, TContext> => {

const mutationKey = ['integrationsGovernanceManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof integrationsGovernanceManagePost1>>, {connectionId: string;credentialId: string;data: IntegrationsGovernanceManagePost1Body}> = (props) => {
          const {connectionId,credentialId,data} = props ?? {};

          return  integrationsGovernanceManagePost1(connectionId,credentialId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type IntegrationsGovernanceManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof integrationsGovernanceManagePost1>>>
    export type IntegrationsGovernanceManagePost1MutationBody = IntegrationsGovernanceManagePost1Body
    export type IntegrationsGovernanceManagePost1MutationError = unknown

    export const useIntegrationsGovernanceManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost1>>, TError,{connectionId: string;credentialId: string;data: IntegrationsGovernanceManagePost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof integrationsGovernanceManagePost1>>,
        TError,
        {connectionId: string;credentialId: string;data: IntegrationsGovernanceManagePost1Body},
        TContext
      > => {
      return useMutation(getIntegrationsGovernanceManagePost1MutationOptions(options), queryClient);
    }

export type integrationsGovernanceManagePost2Response201 = {
  data: void
  status: 201
}

export type integrationsGovernanceManagePost2ResponseSuccess = (integrationsGovernanceManagePost2Response201) & {
  headers: Headers;
};
;

export type integrationsGovernanceManagePost2Response = (integrationsGovernanceManagePost2ResponseSuccess)

export const getIntegrationsGovernanceManagePost2Url = () => {




  return `/api/organization-governance/integrations/messages`
}

export const integrationsGovernanceManagePost2 = async (integrationsGovernanceManagePost2Body: IntegrationsGovernanceManagePost2Body, options?: RequestInit): Promise<integrationsGovernanceManagePost2Response> => {

  return customMutator<integrationsGovernanceManagePost2Response>(getIntegrationsGovernanceManagePost2Url(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(integrationsGovernanceManagePost2Body)
  }
);}





export const getIntegrationsGovernanceManagePost2MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost2>>, TError,{data: IntegrationsGovernanceManagePost2Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost2>>, TError,{data: IntegrationsGovernanceManagePost2Body}, TContext> => {

const mutationKey = ['integrationsGovernanceManagePost2'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof integrationsGovernanceManagePost2>>, {data: IntegrationsGovernanceManagePost2Body}> = (props) => {
          const {data} = props ?? {};

          return  integrationsGovernanceManagePost2(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type IntegrationsGovernanceManagePost2MutationResult = NonNullable<Awaited<ReturnType<typeof integrationsGovernanceManagePost2>>>
    export type IntegrationsGovernanceManagePost2MutationBody = IntegrationsGovernanceManagePost2Body
    export type IntegrationsGovernanceManagePost2MutationError = unknown

    export const useIntegrationsGovernanceManagePost2 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost2>>, TError,{data: IntegrationsGovernanceManagePost2Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof integrationsGovernanceManagePost2>>,
        TError,
        {data: IntegrationsGovernanceManagePost2Body},
        TContext
      > => {
      return useMutation(getIntegrationsGovernanceManagePost2MutationOptions(options), queryClient);
    }

export type integrationsGovernanceManagePost3Response201 = {
  data: void
  status: 201
}

export type integrationsGovernanceManagePost3ResponseSuccess = (integrationsGovernanceManagePost3Response201) & {
  headers: Headers;
};
;

export type integrationsGovernanceManagePost3Response = (integrationsGovernanceManagePost3ResponseSuccess)

export const getIntegrationsGovernanceManagePost3Url = (receiptId: string,) => {




  return `/api/organization-governance/integrations/messages/${receiptId}/commits`
}

export const integrationsGovernanceManagePost3 = async (receiptId: string,
    integrationsGovernanceManagePost3Body: IntegrationsGovernanceManagePost3Body, options?: RequestInit): Promise<integrationsGovernanceManagePost3Response> => {

  return customMutator<integrationsGovernanceManagePost3Response>(getIntegrationsGovernanceManagePost3Url(receiptId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(integrationsGovernanceManagePost3Body)
  }
);}





export const getIntegrationsGovernanceManagePost3MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost3>>, TError,{receiptId: string;data: IntegrationsGovernanceManagePost3Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost3>>, TError,{receiptId: string;data: IntegrationsGovernanceManagePost3Body}, TContext> => {

const mutationKey = ['integrationsGovernanceManagePost3'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof integrationsGovernanceManagePost3>>, {receiptId: string;data: IntegrationsGovernanceManagePost3Body}> = (props) => {
          const {receiptId,data} = props ?? {};

          return  integrationsGovernanceManagePost3(receiptId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type IntegrationsGovernanceManagePost3MutationResult = NonNullable<Awaited<ReturnType<typeof integrationsGovernanceManagePost3>>>
    export type IntegrationsGovernanceManagePost3MutationBody = IntegrationsGovernanceManagePost3Body
    export type IntegrationsGovernanceManagePost3MutationError = unknown

    export const useIntegrationsGovernanceManagePost3 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof integrationsGovernanceManagePost3>>, TError,{receiptId: string;data: IntegrationsGovernanceManagePost3Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof integrationsGovernanceManagePost3>>,
        TError,
        {receiptId: string;data: IntegrationsGovernanceManagePost3Body},
        TContext
      > => {
      return useMutation(getIntegrationsGovernanceManagePost3MutationOptions(options), queryClient);
    }

export type monetaryApprovalReadResponse200 = {
  data: void
  status: 200
}

export type monetaryApprovalReadResponseSuccess = (monetaryApprovalReadResponse200) & {
  headers: Headers;
};
;

export type monetaryApprovalReadResponse = (monetaryApprovalReadResponseSuccess)

export const getMonetaryApprovalReadUrl = () => {




  return `/api/organization-governance/monetary-approvals`
}

export const monetaryApprovalRead = async ( options?: RequestInit): Promise<monetaryApprovalReadResponse> => {

  return customMutator<monetaryApprovalReadResponse>(getMonetaryApprovalReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getMonetaryApprovalReadQueryKey = () => {
    return [
    `/api/organization-governance/monetary-approvals`
    ] as const;
    }


export const getMonetaryApprovalReadQueryOptions = <TData = Awaited<ReturnType<typeof monetaryApprovalRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof monetaryApprovalRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getMonetaryApprovalReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof monetaryApprovalRead>>> = ({ signal }) => monetaryApprovalRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof monetaryApprovalRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type MonetaryApprovalReadQueryResult = NonNullable<Awaited<ReturnType<typeof monetaryApprovalRead>>>
export type MonetaryApprovalReadQueryError = unknown


export function useMonetaryApprovalRead<TData = Awaited<ReturnType<typeof monetaryApprovalRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof monetaryApprovalRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof monetaryApprovalRead>>,
          TError,
          Awaited<ReturnType<typeof monetaryApprovalRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useMonetaryApprovalRead<TData = Awaited<ReturnType<typeof monetaryApprovalRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof monetaryApprovalRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof monetaryApprovalRead>>,
          TError,
          Awaited<ReturnType<typeof monetaryApprovalRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useMonetaryApprovalRead<TData = Awaited<ReturnType<typeof monetaryApprovalRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof monetaryApprovalRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useMonetaryApprovalRead<TData = Awaited<ReturnType<typeof monetaryApprovalRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof monetaryApprovalRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getMonetaryApprovalReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type monetaryApprovalManageResponse201 = {
  data: void
  status: 201
}

export type monetaryApprovalManageResponseSuccess = (monetaryApprovalManageResponse201) & {
  headers: Headers;
};
;

export type monetaryApprovalManageResponse = (monetaryApprovalManageResponseSuccess)

export const getMonetaryApprovalManageUrl = () => {




  return `/api/organization-governance/monetary-approvals/authorities`
}

export const monetaryApprovalManage = async (monetaryApprovalManageBody: MonetaryApprovalManageBody, options?: RequestInit): Promise<monetaryApprovalManageResponse> => {

  return customMutator<monetaryApprovalManageResponse>(getMonetaryApprovalManageUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(monetaryApprovalManageBody)
  }
);}





export const getMonetaryApprovalManageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalManage>>, TError,{data: MonetaryApprovalManageBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalManage>>, TError,{data: MonetaryApprovalManageBody}, TContext> => {

const mutationKey = ['monetaryApprovalManage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof monetaryApprovalManage>>, {data: MonetaryApprovalManageBody}> = (props) => {
          const {data} = props ?? {};

          return  monetaryApprovalManage(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MonetaryApprovalManageMutationResult = NonNullable<Awaited<ReturnType<typeof monetaryApprovalManage>>>
    export type MonetaryApprovalManageMutationBody = MonetaryApprovalManageBody
    export type MonetaryApprovalManageMutationError = unknown

    export const useMonetaryApprovalManage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalManage>>, TError,{data: MonetaryApprovalManageBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof monetaryApprovalManage>>,
        TError,
        {data: MonetaryApprovalManageBody},
        TContext
      > => {
      return useMutation(getMonetaryApprovalManageMutationOptions(options), queryClient);
    }

export type monetaryApprovalManagePostResponse201 = {
  data: void
  status: 201
}

export type monetaryApprovalManagePostResponseSuccess = (monetaryApprovalManagePostResponse201) & {
  headers: Headers;
};
;

export type monetaryApprovalManagePostResponse = (monetaryApprovalManagePostResponseSuccess)

export const getMonetaryApprovalManagePostUrl = (authorityId: string,) => {




  return `/api/organization-governance/monetary-approvals/authorities/${authorityId}/approvals`
}

export const monetaryApprovalManagePost = async (authorityId: string,
    monetaryApprovalManagePostBody: MonetaryApprovalManagePostBody, options?: RequestInit): Promise<monetaryApprovalManagePostResponse> => {

  return customMutator<monetaryApprovalManagePostResponse>(getMonetaryApprovalManagePostUrl(authorityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(monetaryApprovalManagePostBody)
  }
);}





export const getMonetaryApprovalManagePostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalManagePost>>, TError,{authorityId: string;data: MonetaryApprovalManagePostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalManagePost>>, TError,{authorityId: string;data: MonetaryApprovalManagePostBody}, TContext> => {

const mutationKey = ['monetaryApprovalManagePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof monetaryApprovalManagePost>>, {authorityId: string;data: MonetaryApprovalManagePostBody}> = (props) => {
          const {authorityId,data} = props ?? {};

          return  monetaryApprovalManagePost(authorityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MonetaryApprovalManagePostMutationResult = NonNullable<Awaited<ReturnType<typeof monetaryApprovalManagePost>>>
    export type MonetaryApprovalManagePostMutationBody = MonetaryApprovalManagePostBody
    export type MonetaryApprovalManagePostMutationError = unknown

    export const useMonetaryApprovalManagePost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalManagePost>>, TError,{authorityId: string;data: MonetaryApprovalManagePostBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof monetaryApprovalManagePost>>,
        TError,
        {authorityId: string;data: MonetaryApprovalManagePostBody},
        TContext
      > => {
      return useMutation(getMonetaryApprovalManagePostMutationOptions(options), queryClient);
    }

export type monetaryApprovalManagePost1Response201 = {
  data: void
  status: 201
}

export type monetaryApprovalManagePost1ResponseSuccess = (monetaryApprovalManagePost1Response201) & {
  headers: Headers;
};
;

export type monetaryApprovalManagePost1Response = (monetaryApprovalManagePost1ResponseSuccess)

export const getMonetaryApprovalManagePost1Url = (authorityId: string,) => {




  return `/api/organization-governance/monetary-approvals/authorities/${authorityId}/revocations`
}

export const monetaryApprovalManagePost1 = async (authorityId: string,
    monetaryApprovalManagePost1Body: MonetaryApprovalManagePost1Body, options?: RequestInit): Promise<monetaryApprovalManagePost1Response> => {

  return customMutator<monetaryApprovalManagePost1Response>(getMonetaryApprovalManagePost1Url(authorityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(monetaryApprovalManagePost1Body)
  }
);}





export const getMonetaryApprovalManagePost1MutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalManagePost1>>, TError,{authorityId: string;data: MonetaryApprovalManagePost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalManagePost1>>, TError,{authorityId: string;data: MonetaryApprovalManagePost1Body}, TContext> => {

const mutationKey = ['monetaryApprovalManagePost1'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof monetaryApprovalManagePost1>>, {authorityId: string;data: MonetaryApprovalManagePost1Body}> = (props) => {
          const {authorityId,data} = props ?? {};

          return  monetaryApprovalManagePost1(authorityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MonetaryApprovalManagePost1MutationResult = NonNullable<Awaited<ReturnType<typeof monetaryApprovalManagePost1>>>
    export type MonetaryApprovalManagePost1MutationBody = MonetaryApprovalManagePost1Body
    export type MonetaryApprovalManagePost1MutationError = unknown

    export const useMonetaryApprovalManagePost1 = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalManagePost1>>, TError,{authorityId: string;data: MonetaryApprovalManagePost1Body}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof monetaryApprovalManagePost1>>,
        TError,
        {authorityId: string;data: MonetaryApprovalManagePost1Body},
        TContext
      > => {
      return useMutation(getMonetaryApprovalManagePost1MutationOptions(options), queryClient);
    }

export type monetaryApprovalDecideResponse201 = {
  data: void
  status: 201
}

export type monetaryApprovalDecideResponseSuccess = (monetaryApprovalDecideResponse201) & {
  headers: Headers;
};
;

export type monetaryApprovalDecideResponse = (monetaryApprovalDecideResponseSuccess)

export const getMonetaryApprovalDecideUrl = () => {




  return `/api/organization-governance/monetary-approvals/decisions`
}

export const monetaryApprovalDecide = async (monetaryApprovalDecideBody: MonetaryApprovalDecideBody, options?: RequestInit): Promise<monetaryApprovalDecideResponse> => {

  return customMutator<monetaryApprovalDecideResponse>(getMonetaryApprovalDecideUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(monetaryApprovalDecideBody)
  }
);}





export const getMonetaryApprovalDecideMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalDecide>>, TError,{data: MonetaryApprovalDecideBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalDecide>>, TError,{data: MonetaryApprovalDecideBody}, TContext> => {

const mutationKey = ['monetaryApprovalDecide'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof monetaryApprovalDecide>>, {data: MonetaryApprovalDecideBody}> = (props) => {
          const {data} = props ?? {};

          return  monetaryApprovalDecide(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MonetaryApprovalDecideMutationResult = NonNullable<Awaited<ReturnType<typeof monetaryApprovalDecide>>>
    export type MonetaryApprovalDecideMutationBody = MonetaryApprovalDecideBody
    export type MonetaryApprovalDecideMutationError = unknown

    export const useMonetaryApprovalDecide = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof monetaryApprovalDecide>>, TError,{data: MonetaryApprovalDecideBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof monetaryApprovalDecide>>,
        TError,
        {data: MonetaryApprovalDecideBody},
        TContext
      > => {
      return useMutation(getMonetaryApprovalDecideMutationOptions(options), queryClient);
    }

export type riskAnalyticsReadResponse200 = {
  data: void
  status: 200
}

export type riskAnalyticsReadResponseSuccess = (riskAnalyticsReadResponse200) & {
  headers: Headers;
};
;

export type riskAnalyticsReadResponse = (riskAnalyticsReadResponseSuccess)

export const getRiskAnalyticsReadUrl = () => {




  return `/api/organization-governance/risk/runs`
}

export const riskAnalyticsRead = async ( options?: RequestInit): Promise<riskAnalyticsReadResponse> => {

  return customMutator<riskAnalyticsReadResponse>(getRiskAnalyticsReadUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getRiskAnalyticsReadQueryKey = () => {
    return [
    `/api/organization-governance/risk/runs`
    ] as const;
    }


export const getRiskAnalyticsReadQueryOptions = <TData = Awaited<ReturnType<typeof riskAnalyticsRead>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof riskAnalyticsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getRiskAnalyticsReadQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof riskAnalyticsRead>>> = ({ signal }) => riskAnalyticsRead({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof riskAnalyticsRead>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type RiskAnalyticsReadQueryResult = NonNullable<Awaited<ReturnType<typeof riskAnalyticsRead>>>
export type RiskAnalyticsReadQueryError = unknown


export function useRiskAnalyticsRead<TData = Awaited<ReturnType<typeof riskAnalyticsRead>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof riskAnalyticsRead>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof riskAnalyticsRead>>,
          TError,
          Awaited<ReturnType<typeof riskAnalyticsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRiskAnalyticsRead<TData = Awaited<ReturnType<typeof riskAnalyticsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof riskAnalyticsRead>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof riskAnalyticsRead>>,
          TError,
          Awaited<ReturnType<typeof riskAnalyticsRead>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useRiskAnalyticsRead<TData = Awaited<ReturnType<typeof riskAnalyticsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof riskAnalyticsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useRiskAnalyticsRead<TData = Awaited<ReturnType<typeof riskAnalyticsRead>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof riskAnalyticsRead>>, TError, TData>>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getRiskAnalyticsReadQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type riskAnalyticsRunResponse201 = {
  data: void
  status: 201
}

export type riskAnalyticsRunResponseSuccess = (riskAnalyticsRunResponse201) & {
  headers: Headers;
};
;

export type riskAnalyticsRunResponse = (riskAnalyticsRunResponseSuccess)

export const getRiskAnalyticsRunUrl = () => {




  return `/api/organization-governance/risk/runs`
}

export const riskAnalyticsRun = async (riskAnalyticsRunBody: RiskAnalyticsRunBody, options?: RequestInit): Promise<riskAnalyticsRunResponse> => {

  return customMutator<riskAnalyticsRunResponse>(getRiskAnalyticsRunUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(riskAnalyticsRunBody)
  }
);}





export const getRiskAnalyticsRunMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof riskAnalyticsRun>>, TError,{data: RiskAnalyticsRunBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof riskAnalyticsRun>>, TError,{data: RiskAnalyticsRunBody}, TContext> => {

const mutationKey = ['riskAnalyticsRun'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof riskAnalyticsRun>>, {data: RiskAnalyticsRunBody}> = (props) => {
          const {data} = props ?? {};

          return  riskAnalyticsRun(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RiskAnalyticsRunMutationResult = NonNullable<Awaited<ReturnType<typeof riskAnalyticsRun>>>
    export type RiskAnalyticsRunMutationBody = RiskAnalyticsRunBody
    export type RiskAnalyticsRunMutationError = unknown

    export const useRiskAnalyticsRun = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof riskAnalyticsRun>>, TError,{data: RiskAnalyticsRunBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof riskAnalyticsRun>>,
        TError,
        {data: RiskAnalyticsRunBody},
        TContext
      > => {
      return useMutation(getRiskAnalyticsRunMutationOptions(options), queryClient);
    }

export type riskAnalyticsResolveResponse201 = {
  data: void
  status: 201
}

export type riskAnalyticsResolveResponseSuccess = (riskAnalyticsResolveResponse201) & {
  headers: Headers;
};
;

export type riskAnalyticsResolveResponse = (riskAnalyticsResolveResponseSuccess)

export const getRiskAnalyticsResolveUrl = (breachId: string,) => {




  return `/api/organization-governance/risk/breaches/${breachId}/transitions`
}

export const riskAnalyticsResolve = async (breachId: string,
    riskAnalyticsResolveBody: RiskAnalyticsResolveBody, options?: RequestInit): Promise<riskAnalyticsResolveResponse> => {

  return customMutator<riskAnalyticsResolveResponse>(getRiskAnalyticsResolveUrl(breachId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(riskAnalyticsResolveBody)
  }
);}





export const getRiskAnalyticsResolveMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof riskAnalyticsResolve>>, TError,{breachId: string;data: RiskAnalyticsResolveBody}, TContext>, request?: SecondParameter<typeof customMutator>}
): UseMutationOptions<Awaited<ReturnType<typeof riskAnalyticsResolve>>, TError,{breachId: string;data: RiskAnalyticsResolveBody}, TContext> => {

const mutationKey = ['riskAnalyticsResolve'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof riskAnalyticsResolve>>, {breachId: string;data: RiskAnalyticsResolveBody}> = (props) => {
          const {breachId,data} = props ?? {};

          return  riskAnalyticsResolve(breachId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RiskAnalyticsResolveMutationResult = NonNullable<Awaited<ReturnType<typeof riskAnalyticsResolve>>>
    export type RiskAnalyticsResolveMutationBody = RiskAnalyticsResolveBody
    export type RiskAnalyticsResolveMutationError = unknown

    export const useRiskAnalyticsResolve = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof riskAnalyticsResolve>>, TError,{breachId: string;data: RiskAnalyticsResolveBody}, TContext>, request?: SecondParameter<typeof customMutator>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof riskAnalyticsResolve>>,
        TError,
        {breachId: string;data: RiskAnalyticsResolveBody},
        TContext
      > => {
      return useMutation(getRiskAnalyticsResolveMutationOptions(options), queryClient);
    }
